const GOVERNED_SOURCE_DIGEST_ID = 'approved_formula_evaluator' as const;
void GOVERNED_SOURCE_DIGEST_ID;
import {
  CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF,
  CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  COMMERCIAL_EFFECTIVENESS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  COST_TO_SERVE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  ESG_EMISSIONS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
  FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
  INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF,
  ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  PORTFOLIO_CAPACITY_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  PRICING_DISCOUNT_POLICY_AUTHORITY_SOURCE_SYSTEM_REF,
  PRICING_DISCOUNT_TRANSACTION_SNAPSHOT_SOURCE_SYSTEM_REF,
  PRICING_REALIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  REALIZATION_MONITORING_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  REALIZATION_COMPONENT_VARIANCE_SOURCE_SYSTEM_REF,
  ATTRIBUTION_COUNTERFACTUAL_EVIDENCE_SOURCE_SYSTEM_REF,
  ATTRIBUTION_DECONFLICTION_RECEIPT_SOURCE_SYSTEM_REF,
  ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
  ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION,
  ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
  RISK_CLAIMS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_SOURCE_SYSTEM_REF,
  SEGMENT_MARGIN_CLEANUP_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF,
  getApprovedSourceSystemDefinition,
} from './approved-source-systems';
import {
  ASK_HELIX_CARRIER_TRANSPORTATION_COST_DATA_LAKE_CONTEXT_KIND,
  ASK_HELIX_PRICING_DISCOUNT_POLICY_AUTHORITY_SCHEMA_VERSION,
  ASK_HELIX_PRICING_DISCOUNT_TRANSACTION_SCHEMA_VERSION,
  ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_CONTEXT_KIND,
  ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_SCHEMA_VERSION,
} from './ask-helix';
import {
  GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
  type MetricQuerySpec,
} from './metric-registry';
import {
  buildVarianceDecompositionMetricQuerySpec,
  buildVarianceDecompositionTotalQuerySpec,
  validateVarianceSpecCompilation,
} from './variance-query-validation';
import {
  SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS,
} from './contracts/segmentMarginCleanupModuleEvidenceContract';
import { capitalAllocationFormulaDefinitions } from './capital-allocation-formulas';
import { CARRIER_TRANSPORTATION_APPROVED_FORMULA_DEFINITIONS } from './carrier-transportation-approved-formulas';
import { CURRENT_FY_ESTIMATE_APPROVED_FORMULA_DEFINITIONS } from './current-fy-estimate-fact';
import { valueDigestInput } from './approved-formula-value-seal';
import { governedSourceDigest } from './governed-source-digests';
import { createFinanceIncomeMarginTargetDefinition, executeFinanceIncomeMarginTarget } from './finance-income-margin-target-formula';
export * from './finance-income-margin-target-formula';
import {
  configureFinanceEbitdaTargetResolve,
  createFinanceEbitdaTargetResolveDefinition,
  executeFinanceEbitdaTargetResolve,
} from './finance-ebitda-target-resolve-formula';
import { ORG_ANALYSIS_APPROVED_FORMULA_DEFINITIONS } from './org-analysis-approved-formulas';
import { ORG_PATTERN_COST_APPROVED_FORMULA_DEFINITIONS } from './org-pattern-cost-approved-formula';
import { createApprovedFormulaRegistry } from './approved-formula-registry';
import { GL_SCOPED_REDUCTION_APPROVED_FORMULA_DEFINITION } from './gl-cost-scenario';
import {
  calculateMaDealGrossSynergyByBand,
  isMaDealBand,
  MA_DEAL_GROSS_SYNERGY_BY_BAND_FORMULA_ID,
  MA_DEAL_GROSS_SYNERGY_BY_BAND_FORMULA_VERSION,
  type MaDealGrossSynergyByBandInput,
  type MaDealGrossSynergyByBandOutput,
} from './ma-deal-gross-synergy-by-band-formula';
export {
  FINANCE_EBITDA_TARGET_TYPES,
  isFinanceEbitdaTargetType,
  type FinanceEbitdaTargetResolveInput,
  type FinanceEbitdaTargetResolveOutput,
  type FinanceEbitdaTargetType,
} from './finance-ebitda-target-resolve-formula';
import { createHash } from 'crypto';
import {
  computeCounterfactualAttribution,
  getAttributionCounterfactualImplementationDigestInputs,
  type AttributedOutcome,
  type ComputeCounterfactualAttributionInput,
} from './attribution-engine';
import {
  composeAttributionRollup,
  getAttributionRollupImplementationDigestInputs,
  type AttributionRollup,
} from './attribution-rollup';
import type { AttributionLedgerEntry } from './attribution-ledger';
import {
  validateOverlapDimensionKeys,
  type ApprovedOverlapDimensionKey,
  type TargetMetricFamily,
} from './ask-helix/contracts/objective-value-bridge';
import type {
  AllocationLensExclusionMemberUniverseProvenance,
  ImpactClaim,
} from './ask-helix/contracts/governed-tool-output-envelope';
import {
  DECISION_AGENT_SOURCE_CLASSES,
  type DecisionAgentSourceClass,
} from './contracts/sourceClasses';
import type { ApprovedFormulaBlockedReasonCode } from './approved-formula-reason-codes';
export {
  APPROVED_FORMULA_BLOCKED_REASON_CODES,
  type ApprovedFormulaBlockedReasonCode,
} from './approved-formula-reason-codes';
export const APPROVED_FORMULAS_SCHEMA_VERSION = 'approved_formulas_registry_v1';
export const APPROVED_FORMULA_CANONICALIZER_VERSION = 'approved_formula_canonicalizer_v1';

export type ApprovedFormulaLifecycle = 'code_defined';

export type ApprovedFormulaStatusBand = 'sufficient' | 'constrained' | 'critical';

export function canonicalizeApprovedFormulaJson(value: unknown): unknown {
  return canonicalizeJson(value);
}

export function canonicalizeApprovedFormulaJsonBytes(value: unknown): string {
  return JSON.stringify(canonicalizeJson(value));
}

export type ApprovedFormulaBlockedResult = Readonly<{
  status: 'blocked';
  reasonCode: ApprovedFormulaBlockedReasonCode;
  message: string;
}>;

export type ApprovedFormulaOkResult<TOutput> = Readonly<{
  status: 'ok';
  output: Readonly<TOutput>;
}>;

export type ApprovedFormulaResult<TOutput> =
  | ApprovedFormulaOkResult<TOutput>
  | ApprovedFormulaBlockedResult;

export type ApprovedFormulaGoldenTrace<TInput, TOutput> = Readonly<{
  traceId: string;
  input: Readonly<TInput>;
  expected: ApprovedFormulaResult<TOutput>;
}>;

export type ApprovedFormulaEvidenceArtifact = Readonly<{
  artifactId: string;
  tenantId?: string;
  scopeRootNodeId?: string | null;
  contextKind?: string;
  sourceClass: string;
  sourceSystemRef?: string;
  schemaVersion: string;
  sourceRef: string | null;
  ingestJobId: string;
  contentJson: unknown;
  contentDigest?: string;
  reportingCutAsOf?: string;
}>;

export type ApprovedFormulaEvidenceValidationBlock = Readonly<{
  reasonCode:
    | 'missing_governed_evidence'
    | 'unapproved_source_system'
    | 'invalid_formula_input'
    | 'formula_reconciliation_failure';
  message: string;
}>;

export const APPROVED_FORMULA_RECONCILIATION_RULE_KINDS = Object.freeze([
  'input_output_evidence_reconciliation',
  'aggregate_baseline_totals_reconciliation',
  'derived_risk_score_reconciliation',
  'threshold_warning_level_reconciliation',
  'monitoring_drift_reconciliation',
] as const);

export type ApprovedFormulaReconciliationRuleKind =
  (typeof APPROVED_FORMULA_RECONCILIATION_RULE_KINDS)[number];

export type ApprovedFormulaReconciliationRule<TInput = unknown, TOutput = unknown> = Readonly<{
  kind: ApprovedFormulaReconciliationRuleKind;
  ruleId: string;
  description: string;
  validate(
    input: Readonly<TInput>,
    output: Readonly<TOutput>,
    artifacts: readonly ApprovedFormulaEvidenceArtifact[],
  ): ApprovedFormulaEvidenceValidationBlock | null;
}>;

export type ApprovedFormulaImplementationDigestInput = Readonly<{
  name: string;
  source: string;
}>;

export type ApprovedFormulaDefinition<TInput = unknown, TOutput = unknown> = Readonly<{
  formulaId: string;
  version: string;
  displayName: string;
  description: string;
  lifecycle: ApprovedFormulaLifecycle;
  formulaRuntimeVersion: string;
  canonicalizerVersion: string;
  inputSchemaVersion: string;
  outputSchemaVersion: string;
  expectedContextKind?: string;
  additionalExpectedContextKinds?: readonly string[];
  approvedSourceSystemRefs: readonly string[];
  implementationDigestInputs: readonly ApprovedFormulaImplementationDigestInput[];
  goldenTraces: readonly ApprovedFormulaGoldenTrace<TInput, TOutput>[];
  validateInputAgainstEvidence?(
    input: Readonly<TInput>,
    artifacts: readonly ApprovedFormulaEvidenceArtifact[],
  ): ApprovedFormulaEvidenceValidationBlock | null;
  reconciliationRule?: ApprovedFormulaReconciliationRule<TInput, TOutput>;
  execute(input: TInput): ApprovedFormulaResult<TOutput>;
}>;

export type PortfolioCapacityBalanceInput = Readonly<{
  capabilityId: string;
  committedFte: number;
  availableFte: number;
  sourceAsOf: string;
}>;

export type PortfolioCapacityBalanceOutput = Readonly<{
  capabilityId: string;
  committedFte: number;
  availableFte: number;
  capacityGapFte: number;
  utilizationPct: number | null;
}>;

export type PortfolioValueDensityInput = Readonly<{
  initiativeId: string;
  expectedValueUsd: number;
  requiredFte: number;
  sourceAsOf: string;
}>;

export type PortfolioValueDensityOutput = Readonly<{
  initiativeId: string;
  expectedValueUsd: number;
  requiredFte: number;
  valueDensityUsdPerFte: number;
}>;

export type PortfolioDependencyBlockScoreInput = Readonly<{
  initiativeId: string;
  unmetDependencyCount: number;
  totalDependencyCount: number;
  sourceAsOf: string;
}>;

export type PortfolioDependencyBlockScoreOutput = Readonly<{
  initiativeId: string;
  unmetDependencyCount: number;
  totalDependencyCount: number;
  dependencyBlocked: boolean;
}>;

export type PortfolioConstraintBindingInput = Readonly<{
  constraintId: string;
  demandHours: number;
  supplyHours: number;
  policyAllocatedPct: number;
  policyCapPct: number;
  kernelScarcityPriceUsdPerHour: number;
  sourceAsOf: string;
}>;

export type PortfolioConstraintBindingOutput = Readonly<{
  constraintId: string;
  capacityShortfallHours: number;
  capacityBinding: boolean;
  policyBinding: boolean;
  binding: boolean;
  kernelScarcityPriceUsdPerHour: number;
}>;

export type PortfolioPlanActualsReconcileInput = Readonly<{
  portfolioScopeId: string;
  financeScopeId: string;
  planSpendUsd: number;
  financeActualUsd: number;
  reconciliationControlGapUsd: number;
  mappedCostPoolCount: number;
  unmappedCostPoolCount: number;
  sourceAsOf: string;
}>;

export type PortfolioPlanActualsReconcileOutput = Readonly<{
  portfolioScopeId: string;
  planSpendUsd: number;
  financeActualUsd: number;
  planActualsGapUsd: number;
  residualAfterReconciliationUsd: number;
  mappedCostPoolCount: number;
}>;

export type PortfolioReallocationValueDeltaInput = Readonly<{
  baselineCapturedValueUsd: number;
  scenarioCapturedValueUsd: number;
  firstOrderValueDeltaUsd: number;
  targetBucketBinding: boolean;
  tolerancePct: number;
  addedInitiativeIds: readonly string[];
  removedInitiativeIds: readonly string[];
  sourceAsOf: string;
}>;

export type PortfolioReallocationValueDeltaOutput = Readonly<{
  capturedValueDeltaUsd: number;
  firstOrderValueDeltaUsd: number;
  reconciliationResidualUsd: number;
  withinTolerance: boolean;
  targetBucketBinding: boolean;
  addedInitiativeIds: readonly string[];
  removedInitiativeIds: readonly string[];
}>;

export type PortfolioCapacityCutRow = Readonly<{
  initiativeId: string;
  valueUsd: number;
  valueDensityUsdPerFte: number;
}>;

export type PortfolioCapacityCutRankingInput = Readonly<{
  cutPct: number;
  baselineSelected: readonly PortfolioCapacityCutRow[];
  scenarioSelectedInitiativeIds: readonly string[];
  sourceAsOf: string;
}>;

export type PortfolioCapacityCutRankingOutput = Readonly<{
  cutPct: number;
  belowLine: readonly PortfolioCapacityCutRow[];
  valueAtRiskUsd: number;
}>;

export type PortfolioDependencyEdge = Readonly<{
  fromInitiativeId: string;
  toInitiativeId: string;
}>;

export type PortfolioKernelForecastRow = Readonly<{
  initiativeId: string;
  p50CompletionWeek: string;
  p80CompletionWeek: string;
  selected: boolean;
}>;

export type PortfolioDependencySequencingInput = Readonly<{
  requestedInitiativeIds: readonly string[];
  kernelForecastRows: readonly PortfolioKernelForecastRow[];
  dependencyEdges: readonly PortfolioDependencyEdge[];
  sourceAsOf: string;
}>;

export type PortfolioDependencySequencingOutput = Readonly<{
  orderedInitiativeIds: readonly string[];
  forecastRows: readonly PortfolioKernelForecastRow[];
  dependencyViolationCount: number;
  capacityViolationCount: number;
  publishable: boolean;
}>;

export type CashConversionCycleDaysInput = Readonly<{
  daysSalesOutstandingDays: number;
  daysInventoryOutstandingDays: number;
  daysPayablesOutstandingDays: number;
  reportingCutAsOf?: string;
}>;

export type CashConversionCycleDaysOutput = Readonly<{
  daysSalesOutstandingDays: number;
  daysInventoryOutstandingDays: number;
  daysPayablesOutstandingDays: number;
  cashConversionCycleDays: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type DaysSalesOutstandingInput = Readonly<{
  accountsReceivableUsd: number;
  revenueUsd: number;
  reportingCutAsOf?: string;
}>;

export type DaysSalesOutstandingOutput = Readonly<{
  accountsReceivableUsd: number;
  revenueUsd: number;
  dsoDays: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type DaysInventoryOutstandingDioDaysInput = Readonly<{
  inventoryUsd: number;
  costOfGoodsSoldUsd: number;
  reportingCutAsOf?: string;
}>;

export type DaysInventoryOutstandingDioDaysOutput = Readonly<{
  inventoryUsd: number;
  costOfGoodsSoldUsd: number;
  dioDays: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type DaysPayablesOutstandingInput = Readonly<{
  accountsPayableUsd: number;
  costOfGoodsSoldUsd: number;
  reportingCutAsOf?: string;
}>;

export type DaysPayablesOutstandingOutput = Readonly<{
  accountsPayableUsd: number;
  costOfGoodsSoldUsd: number;
  dpoDays: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type WorkingCapitalDeltaCashInput = Readonly<{
  priorAccountsReceivableUsd: number;
  priorInventoryUsd: number;
  priorAccountsPayableUsd: number;
  currentAccountsReceivableUsd: number;
  currentInventoryUsd: number;
  currentAccountsPayableUsd: number;
  reportingCutAsOf?: string;
}>;

export type WorkingCapitalDeltaCashOutput = Readonly<{
  priorNetWorkingCapitalUsd: number;
  currentNetWorkingCapitalUsd: number;
  netWorkingCapitalDeltaUsd: number;
  cashImpactUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type StrategicExecutionCapacityUtilizationInput = Readonly<{
  requiredCapacityFte: number;
  availableCapacityFte: number;
  reportingCutAsOf?: string;
}>;

export type StrategicExecutionCapacityUtilizationOutput = Readonly<{
  requiredCapacityFte: number;
  availableCapacityFte: number;
  capacityGapFte: number;
  utilizationRatio: number;
  utilizationPct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type StrategicExecutionCapacityFormulaRecord = Readonly<{
  initiativeId: string;
  capacityRequiredFte: number;
  capacityAvailableFte: number;
  currentStatus?: string | null;
  blockedReason?: string | null;
}>;

export type StrategicExecutionCapacityBaselineTotalsInput = Readonly<{
  records: readonly StrategicExecutionCapacityFormulaRecord[];
  reportingCutAsOf?: string;
}>;

export type StrategicExecutionCapacityBaselineTotalsOutput = Readonly<{
  initiativeCount: number;
  totalCapacityRequiredFte: number;
  totalCapacityAvailableFte: number;
  capacityGapFte: number;
  capacityGapPct: number;
}>;

export type StrategicExecutionCapacityRiskScoreInput = Readonly<{
  records: readonly StrategicExecutionCapacityFormulaRecord[];
  capacityGapPct: number;
  reportingCutAsOf?: string;
}>;

export type StrategicExecutionCapacityRiskScoreOutput = Readonly<{
  initiativesAtRiskCount: number;
  blockedInitiativeCount: number;
  strategicExecutionCapacityRiskScore: number;
}>;

export type StrategicExecutionCapacityWarningLevel =
  | 'none'
  | 'watch'
  | 'decision_required';

export type StrategicExecutionCapacityWarningInput = Readonly<{
  records: readonly StrategicExecutionCapacityFormulaRecord[];
  capacityGapFte: number;
  capacityGapPct: number;
  blockedInitiativeCount: number;
  strategicExecutionCapacityRiskScore: number;
  reportingCutAsOf?: string;
}>;

export type StrategicExecutionCapacityWarningOutput = Readonly<{
  warningLevel: StrategicExecutionCapacityWarningLevel;
  triggeredRuleIds: readonly string[];
}>;

export type StrategicExecutionCapacityDriftStatus =
  | 'on_track'
  | 'watch'
  | 'drifting'
  | 'amendment_required';

export type StrategicExecutionCapacityDriftDirection = 'increase' | 'decrease';

export type StrategicExecutionCapacityDriftDetectionInput = Readonly<{
  metricKey: string;
  baselineValue: number;
  targetValue: number;
  actualValue: number;
  baselineAsOf: string;
  actualAsOf: string;
  targetAt: string;
  direction: StrategicExecutionCapacityDriftDirection;
  warningThresholdRatio?: number;
  driftThresholdRatio?: number;
  amendmentThresholdRatio?: number;
  reportingCutAsOf?: string;
}>;

export type StrategicExecutionCapacityDriftDetectionOutput = Readonly<{
  metricKey: string;
  baselineValue: number;
  targetValue: number;
  actualValue: number;
  expectedValue: number;
  deltaFromExpected: number;
  behindRatio: number;
  driftStatus: StrategicExecutionCapacityDriftStatus;
  driftStatusCode: number;
  triggeredRuleId: string;
}>;

export type RealizationProjectedMissDirection = 'increase' | 'decrease';

export type RealizationProjectedMissInput = Readonly<{
  metricKey: string;
  expectedValue: number;
  actualValue: number;
  direction: RealizationProjectedMissDirection;
  valuePerUnit: number;
  annualizationFactor?: number;
  reportingCutAsOf?: string;
}>;

export type RealizationProjectedMissOutput = Readonly<{
  metricKey: string;
  expectedValue: number;
  actualValue: number;
  direction: RealizationProjectedMissDirection;
  behindUnits: number;
  valuePerUnit: number;
  annualizationFactor: number;
  projectedMissUsd: number;
}>;

export type RealizationPaceReceiptDirection = 'increase' | 'decrease';

export type RealizationPaceReceiptInput = Readonly<{
  metricKey: string;
  direction: RealizationPaceReceiptDirection;
  baselineValue: number;
  targetValue: number;
  expectedValue: number;
  actualValue: number;
  reportingCutAsOf: string;
  targetAt: string;
  observationWindow?: Readonly<{
    policyId: string;
    fromAsOf: string;
    toAsOf: string;
    firstActualValue: number;
    latestActualValue: number;
  }>;
}>;

export type RealizationPaceReceiptOutput = Readonly<{
  metricKey: string;
  direction: RealizationPaceReceiptDirection;
  baselineValue: number;
  targetValue: number;
  expectedValue: number;
  actualValue: number;
  reportingCutAsOf: string;
  targetAt: string;
  behindUnits: number;
  actualPacePerWeek: number | null;
  requiredTotalPacePerWeek: number | null;
  requiredCatchUpIncrementPerWeek: number | null;
  paceCoverageRatio: number | null;
  projectedLandAt: string | null;
  weeksUntilCommittedMiss: number | null;
  gapCodes: readonly string[];
}>;

export type PublishedRealizationPaceReceipt = Readonly<{
  artifactId: string;
  schemaVersion: 'realization_pace_receipt_v1';
  tenantId: string;
  strategyId: string;
  decisionId: string;
  commitmentArtifactId: string;
  formulaId: 'realization_pace_receipt_v1';
  formulaVersion: '1.0.0';
  metricKey: string;
  unit: string;
  scopeKind: string;
  scopeId: string;
  direction: RealizationPaceReceiptDirection;
  reportingCutAsOf: string;
  executionReceiptId: string;
  requiredTotalPacePerWeek: number | null;
  requiredCatchUpIncrementPerWeek: number | null;
  inputDigest: string;
  outputDigest: string;
  evidenceRefs: readonly string[];
}>;

/**
 * Realization proportion on named bases.
 *
 * A realized figure and one or more candidate denominators are not an answer to a
 * proportion; the proportion is. This formula resolves the ratio ONCE PER NAMED BASIS
 * so parallel measures can never be published in place of the share they imply. Each
 * basis is resolved on its own: a basis whose denominator is absent or non-positive is
 * withheld with a machine-named reason while every other basis still resolves, so a
 * single missing denominator can never silently drop the whole answer. Bases are never
 * summed with each other, and the realized figure is never added into any denominator.
 *
 * When EVERY basis is withheld the formula still returns each basis's OWN reason rather
 * than collapsing them into one refusal. A caller can then report why each denominator
 * failed instead of restating one basis's cause as another's; the input itself is the
 * only thing this formula ever blocks on.
 */
export type RealizationProportionWithheldReasonCode =
  | 'denominator_not_supplied'
  | 'denominator_not_positive';

export type RealizationProportionBasisInput = Readonly<{
  basisId: string;
  /** A denominator already carried as one governed figure. */
  denominatorUsd?: number | null;
  /**
   * A denominator assembled from governed component figures. The formula performs the
   * addition so the composed base is engine-bound, never assembled in caller prose.
   */
  denominatorComponentsUsd?: readonly number[] | null;
}>;

export type RealizationProportionInput = Readonly<{
  realizedUsd?: number | null;
  bases?: readonly RealizationProportionBasisInput[];
  reportingCutAsOf?: string;
}>;

export type RealizationProportionBasisOutput = Readonly<{
  basisId: string;
  denominatorUsd: number | null;
  denominatorComponentsUsd: readonly number[] | null;
  realizationSharePct: number | null;
  unrealizedUsd: number | null;
  withheldReasonCode: RealizationProportionWithheldReasonCode | null;
}>;

export type RealizationProportionOutput = Readonly<{
  realizedUsd: number;
  bases: readonly RealizationProportionBasisOutput[];
  resolvedBasisCount: number;
  withheldBasisCount: number;
}>;

export type DelayeringNetEbitdaOpportunityInput = Readonly<{
  baselineRunRateCostUsd: number;
  scenarioRunRateCostUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd: number;
  reportingCutAsOf?: string;
}>;

export type DelayeringNetEbitdaOpportunityOutput = Readonly<{
  grossRunRateSavingsUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd: number;
  delayeringNetEbitdaOpportunityUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type NetEbitdaOpportunityInput = Readonly<{
  grossRunRateSavingsUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd?: number;
}>;

export type NetEbitdaOpportunityOutput = Readonly<{
  grossRunRateSavingsUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd: number;
  netEbitdaOpportunityUsd: number;
}>;

export type WorkforceReductionNetEbitdaOpportunityInput = Readonly<{
  grossRunRateSavingsUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd: number;
  sourceRunId: string;
  scenarioCaseId: string;
  reportingCutAsOf: string;
}>;

export type WorkforceReductionNetEbitdaOpportunityOutput = Readonly<{
  grossRunRateSavingsUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd: number;
  workforceReductionNetEbitdaOpportunityUsd: number;
}>;

export const WORKFORCE_REDUCTION_FORMULA_SCENARIO_RUN_SCHEMA_VERSION =
  'workforce_reduction_formula_scenario_run_v1';

export type WorkforceReductionFormulaScenarioCase = Readonly<{
  scenarioCaseId: string;
  grossRunRateSavingsUsd: number;
  oneTimeSeveranceUsd: number;
  capacityCostAtRiskUsd: number;
  workforceReductionNetEbitdaOpportunityUsd: number;
}>;

export type WorkforceReductionFormulaScenarioRunEvidence = Readonly<{
  schemaVersion: typeof WORKFORCE_REDUCTION_FORMULA_SCENARIO_RUN_SCHEMA_VERSION;
  sourceRunId: string;
  reportingCutAsOf: string;
  cases: readonly WorkforceReductionFormulaScenarioCase[];
}>;

export type DelayeringOrgHealthWarningInput = Readonly<{
  maxDepth: number;
  targetMaxDepth: number;
  averageManagerSpan: number;
  targetMinimumManagerSpan: number;
  thinSpanManagerCount: number;
  managerCount: number;
  maxThinSpanManagerRatio: number;
  reportingCutAsOf?: string;
}>;

export type DelayeringOrgHealthWarningOutput = Readonly<{
  warningLevel: StrategicExecutionCapacityWarningLevel;
  warningLevelCode: number;
  triggeredRuleIds: readonly string[];
}>;

export type CarrierTransportationCostOpportunityInput = Readonly<{
  baselineGrossMarginUsd: number;
  belowFloorRecoveryUsd: number;
  contractRateRecoveryUsd: number;
  carrierRateDeltaRecoveryUsd: number;
  carrier?: string | null;
  rateDeltaPct?: number | null;
  reportingCutAsOf?: string;
}>;

export type CarrierTransportationCostOpportunityOutput = Readonly<{
  baselineGrossMarginUsd: number;
  belowFloorRecoveryUsd: number;
  contractRateRecoveryUsd: number;
  carrierRateDeltaRecoveryUsd: number;
  carrierCostOpportunityUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CarrierTransportationCostOpportunityScenarioInput = Readonly<{
  baselineGrossMarginUsd: number;
  belowFloorRecoveryUsd: number;
  contractRateRecoveryUsd: number;
  carrierRateDeltaBaseSpendUsd: number;
  scenarioRateDeltaPct: number;
  expectedBaselineOpportunityUsd: number;
  carrier: string;
  reportingCutAsOf?: string;
}>;

export type CarrierTransportationCostOpportunityScenarioOutput = Readonly<{
  baselineGrossMarginUsd: number;
  belowFloorRecoveryUsd: number;
  contractRateRecoveryUsd: number;
  carrierRateDeltaBaseSpendUsd: number;
  scenarioRateDeltaPct: number;
  baselineCarrierCostOpportunityUsd: number;
  baselineOpportunityVarianceUsd: number;
  scenarioCarrierRateDeltaRecoveryUsd: number;
  scenarioCarrierCostOpportunityUsd: number;
  operatingIncomeDeltaUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceEbitdaDeriveInput = Readonly<{
  operatingIncomeUsd: number;
  depreciationAmortizationUsd: number;
  approvedAdjustmentsUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceEbitdaDeriveOutput = Readonly<{
  ebitdaUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceOperatingIncomeDeriveInput = Readonly<{
  revenueUsd: number;
  costOfRevenueUsd: number;
  operatingExpenseUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceOperatingIncomeDeriveOutput = Readonly<{
  operatingIncomeUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceEbitdaFromOperatingIncomeInput = Readonly<{
  operatingIncomeUsd: number;
  depreciationAmortizationUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceEbitdaFromOperatingIncomeOutput = Readonly<{
  ebitdaUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceGrossMarginInput = Readonly<{
  grossProfitUsd: number;
  revenueUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceGrossMarginOutput = Readonly<{
  grossMarginPct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceNetRevenueMarginInput = Readonly<{
  adjustedGrossProfitUsd: number;
  revenueUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceNetRevenueMarginOutput = Readonly<{
  netRevenueMarginPct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FreightCostRepricingInput = Readonly<{
  baselinePurchasedTransportationUsd: number;
  admittedSensitivity: number;
  freightRateDeltaPct: number;
  reportingCutAsOf?: string;
}>;

export type FreightCostRepricingOutput = Readonly<{
  baselinePurchasedTransportationUsd: number;
  admittedSensitivity: number;
  freightRateDeltaPct: number;
  purchasedTransportationDeltaUsd: number;
  repricedPurchasedTransportationUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type DemandRevenueRepricingInput = Readonly<{
  baselineGrossRevenueUsd: number;
  admittedSensitivity: number;
  demandVolumeDeltaPct: number;
  reportingCutAsOf?: string;
}>;

export type DemandRevenueRepricingOutput = Readonly<{
  baselineGrossRevenueUsd: number;
  admittedSensitivity: number;
  demandVolumeDeltaPct: number;
  grossRevenueDeltaUsd: number;
  repricedGrossRevenueUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type OperationalRevenuePerLoadInput = Readonly<{
  revenueUsd: number;
  loadCount: number;
  reportingCutAsOf?: string;
}>;

export type OperationalRevenuePerLoadOutput = Readonly<{
  revenueUsd: number;
  loadCount: number;
  revenuePerLoadUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type OperationalCostPerLoadInput = Readonly<{
  costUsd: number;
  loadCount: number;
  reportingCutAsOf?: string;
}>;

export type OperationalCostPerLoadOutput = Readonly<{
  costUsd: number;
  loadCount: number;
  costPerLoadUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceOperatingMarginInput = Readonly<{
  adjustedOperatingIncomeUsd: number;
  adjustedGrossProfitUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceOperatingMarginOutput = Readonly<{
  operatingMarginPct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceEbitdaMarginInput = Readonly<{
  ebitdaUsd: number;
  revenueUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceEbitdaMarginOutput = Readonly<{
  ebitdaMarginPct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceOperatingExpensePerHeadInput = Readonly<{
  operatingExpenseUsd: number;
  reportingHeadcount: number;
  reportingCutAsOf?: string;
}>;

export type FinanceOperatingExpensePerHeadOutput = Readonly<{
  operatingExpensePerHeadUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceEbitdaGapInput = Readonly<{
  ebitdaUsd: number;
  ebitdaTargetUsd: number;
  reportingCutAsOf?: string;
}>;

export type FinanceEbitdaGapOutput = Readonly<{
  ebitdaGapUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type FinanceObjectiveValueGapInput = Readonly<{
  objectiveMetricId: string;
  baselineValue: number;
  targetValue: number;
  reportingCutAsOf?: string;
}>;

export type FinanceObjectiveValueGapOutput = Readonly<{
  objectiveMetricId: string;
  baselineValue: number;
  targetValue: number;
  gapValue: number;
  gapPct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type BenchmarkMetricDirection = 'higher_is_better' | 'lower_is_leaner';

export type BenchmarkQuartilePosition =
  | 'below_p25'
  | 'p25_to_median'
  | 'median_to_p75'
  | 'above_p75';

export type BenchmarkReferenceRangeInput = Readonly<{
  min: number;
  p25: number;
  median: number;
  p75: number;
  max: number;
}>;

export type BenchmarkDollarizationInput =
  | Readonly<{
      basis: 'percent_of_revenue';
      revenueUsd: number;
    }>
  | Readonly<{
      basis: 'per_revenue_billion_unit';
      revenueUsd: number;
      unitCostUsd: number;
    }>;

export type BenchmarkGapToMedianDollarizedInput = Readonly<{
  metricId: string;
  tenantMetricValue: number;
  direction: BenchmarkMetricDirection;
  referenceRange: BenchmarkReferenceRangeInput;
  dollarization: BenchmarkDollarizationInput;
  reportingCutAsOf?: string;
}>;

export type BenchmarkGapToMedianDollarizedOutput = Readonly<{
  metricId: string;
  tenantMetricValue: number;
  referenceMedian: number;
  quartilePosition: BenchmarkQuartilePosition;
  quartileIndex: 1 | 2 | 3 | 4;
  rawDeltaToMedian: number;
  opportunityGapToMedian: number;
  favorableToMedian: boolean;
  dollarizationBasis: BenchmarkDollarizationInput['basis'];
  valuePerMetricUnitUsd: number;
  gapToMedianDollarizedUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type ScenarioInvestmentRoiInput = Readonly<{
  riskAdjustedValueUsd?: number;
  totalInvestmentUsd?: number;
  reportingCutAsOf?: string;
}>;

export type ScenarioInvestmentRoiOutput = Readonly<{
  riskAdjustedValueUsd: number;
  totalInvestmentUsd: number;
  roi: number;
}>;

export type PricingRealizationNetRevenueDeltaInput = Readonly<{
  baselineRevenueUsd: number;
  plannedSellRateIncreasePct: number;
  expectedNetRevenueRetentionPct: number;
  reportingCutAsOf?: string;
}>;

export type PricingRealizationNetRevenueDeltaOutput = Readonly<{
  netRevenueDeltaUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PricingRealizationElasticityGuardInput = Readonly<{
  expectedRevenueGrowthPct: number;
  flatRevenueTolerancePct: number;
  expectedVolumeResponsePct?: number;
  reportingCutAsOf?: string;
}>;

export type PricingRealizationElasticityGuardOutput = Readonly<{
  flatRevenueEligible: boolean;
  expectedRevenueGrowthPct: number;
  flatRevenueTolerancePct: number;
  expectedVolumeResponsePct: number | null;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PricingRealizationAgpContributionInput = Readonly<{
  netRevenueDeltaUsd: number;
  agpFlowThroughPct: number;
  reportingCutAsOf?: string;
}>;

export type PricingRealizationAgpContributionOutput = Readonly<{
  agpContributionUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PricingRealizationOiContributionInput = Readonly<{
  agpContributionUsd: number;
  executionCostUsd: number;
  reportingCutAsOf?: string;
}>;

export type PricingRealizationOiContributionOutput = Readonly<{
  operatingIncomeContributionUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PricingQuoteInvoiceLeakageInput = Readonly<{
  customerCohort?: string | null;
  segment?: string | null;
  serviceLaneScope?: string | null;
  pricingActionId?: string | null;
  quotedRevenueUsd?: number | null;
  invoicedRevenueUsd?: number | null;
  eligibleRevenueUsd?: number | null;
  reportingCutAsOf?: string;
}>;

export type PricingQuoteInvoiceLeakageOutput = Readonly<{
  quoteInvoiceLeakageUsd: number;
  eligibleRevenueUsd: number;
  leakageRatePct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeAccountFormulaInput = Readonly<{
  accountId: string;
  netRevenueUsd: number;
  fullyLoadedCostToServeUsd: number;
}>;

export type CostToServeActivityAllocationInput = Readonly<{
  accountAllocatedCostsUsd: readonly number[];
  approvedCostPoolUsd: number;
  reportingCutAsOf?: string;
}>;

export type CostToServeActivityAllocationOutput = Readonly<{
  fullyLoadedCostToServeUsd: number;
  activityAllocatedCostUsd: number;
  approvedCostPoolUsd: number;
  allocatedCostPoolSharePct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeMarginInput = Readonly<{
  accounts: readonly CostToServeAccountFormulaInput[];
  reportingCutAsOf?: string;
}>;

export type CostToServeMarginOutput = Readonly<{
  ctsMarginUsd: number;
  lossMakingAccountCount: number;
  negativeMarginPoolUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeServiceTierInput = Readonly<{
  serviceTierId: string;
  shipmentCount: number;
  totalServiceTierCostUsd: number;
}>;

export type CostToServeServiceTierCostInput = Readonly<{
  serviceTiers: readonly CostToServeServiceTierInput[];
  reportingCutAsOf?: string;
}>;

export type CostToServeServiceTierCostOutput = Readonly<{
  serviceTierCostPerShipmentUsd: number;
  shipmentCount: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeConcentrationHhiInput = Readonly<{
  accounts: readonly CostToServeAccountFormulaInput[];
  reportingCutAsOf?: string;
}>;

export type CostToServeConcentrationHhiOutput = Readonly<{
  accountProfitHhi: number;
  positiveProfitPoolUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeRevenueConcentrationAccountInput = Readonly<{
  accountId: string;
  netRevenueUsd: number;
}>;

export type CostToServeTopNRevenueShareInput = Readonly<{
  accounts: readonly CostToServeRevenueConcentrationAccountInput[];
  topN: number;
  reportingCutAsOf?: string;
}>;

export type CostToServeTopNRevenueShareOutput = Readonly<{
  topNRevenueSharePct: number;
  topNRevenueUsd: number;
  totalRevenueUsd: number;
  topNAccountIds: readonly string[];
  accountCount: number;
  topN: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeBenchmarkInput = Readonly<{
  accountNetRevenueUsd: number;
  fullyLoadedCostToServeUsd: number;
  benchmarkCostToServeRatioPct: number;
  watchCostToServeRatioPct: number;
  decisionCostToServeRatioPct: number;
  reportingCutAsOf?: string;
}>;

export type CostToServeBenchmarkOutput = Readonly<{
  costToServeRatioPct: number;
  ctsBenchmarkGapPct: number;
  warningLevel: 'none' | 'watch' | 'decision_required';
  warningLevelCode: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeRecoveryOpportunityInput = Readonly<{
  negativeMarginPoolUsd: number;
  recoveryFactorPct: number;
  approvedFinanceRecoveryUsd: number;
  financeOperatingIncomeUsd: number;
  reportingCutAsOf?: string;
}>;

export type CostToServeRecoveryOpportunityOutput = Readonly<{
  ctsRecoveryOpportunityUsd: number;
  modeledRecoveryUsd: number;
  approvedFinanceRecoveryUsd: number;
  ctsFinanceReconciliationResidualUsd: number;
  recoveryShareOfOperatingIncomePct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeRepriceAccountInput = CostToServeAccountFormulaInput &
  Readonly<{
    customerPriceElasticityFactor: number;
  }>;

export type CostToServeRepriceDeltaInput = Readonly<{
  accounts: readonly CostToServeRepriceAccountInput[];
  priceLiftPct: number;
  reportingCutAsOf?: string;
}>;

export type CostToServeRepriceDeltaOutput = Readonly<{
  ctsRepriceDeltaUsd: number;
  affectedAccountCount: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type CostToServeServiceRedesignInput = Readonly<{
  currentServiceTierCostPerShipmentUsd: number;
  targetServiceTierCostPerShipmentUsd: number;
  migratableAccountCount: number;
  attritionRiskUsd: number;
  reportingCutAsOf?: string;
}>;

export type CostToServeServiceRedesignOutput = Readonly<{
  ctsServiceRedesignDeltaUsd: number;
  serviceCostDeltaPerShipmentUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PricingDiscountAuthorityBandInput = Readonly<{
  bandId: string;
  maxDiscountPctOfList: number;
}>;

export type PricingDiscountTransactionInput = Readonly<{
  rowId: string;
  segment: string;
  rep: string;
  authorityBandId: string;
  listRevenueUsd: number;
  netRevenueUsd: number;
  discountUsd?: number | null;
  offPolicyLeakageUsd?: number | null;
}>;

export type PricingDiscountLeakageInput = Readonly<{
  authorityBands: readonly PricingDiscountAuthorityBandInput[];
  transactions: readonly PricingDiscountTransactionInput[];
  reportingCutAsOf?: string;
}>;

export type PricingDiscountLeakageOutput = Readonly<{
  eligibleListRevenueUsd: number;
  totalNetRevenueUsd: number;
  totalDiscountUsd: number;
  withinPolicyDiscountUsd: number;
  offPolicyDiscountUsd: number;
  discountLeakageUsd: number;
  discountLeakageRatePct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type RealizationRateInput = Readonly<{
  segment?: string | null;
  quotedRevenueUsd?: number | null;
  invoicedRevenueUsd?: number | null;
  eligibleRevenueUsd?: number | null;
  reportingCutAsOf?: string;
}>;

export type RealizationRateOutput = Readonly<{
  quotedRevenueUsd: number;
  invoicedRevenueUsd: number;
  eligibleRevenueUsd: number;
  realizationGapUsd: number;
  realizationRatePct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PriceVolumeMixBridgeInput = Readonly<{
  baselineRevenueUsd: number;
  plannedSellRateIncreasePct: number;
  expectedVolumeResponsePct: number;
  expectedRevenueGrowthPct: number;
  expectedNetRevenueRetentionPct: number;
  agpFlowThroughPct: number;
  executionCostUsd: number;
  reportingCutAsOf?: string;
}>;

export type PriceVolumeMixBridgeOutput = Readonly<{
  priceImpactUsd: number;
  volumeImpactUsd: number;
  mixImpactUsd: number;
  pvmNetRevenueDeltaUsd: number;
  pvmAgpContributionUsd: number;
  pvmOperatingIncomeContributionUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type PricingStructureMixShiftMarginContributionInput = Readonly<{
  period: string;
  segment: string;
  totalNetRevenueUsd: number;
  baselineSpotMixPct: number;
  currentSpotMixPct: number;
  baselineContractMixPct: number;
  currentContractMixPct: number;
  spotNetRevenueMarginPct: number;
  contractNetRevenueMarginPct: number;
  reportingCutAsOf?: string;
}>;

export type PricingStructureMixShiftMarginContributionOutput = Readonly<{
  spotToContractMixShiftPct: number;
  marginSpreadPct: number;
  mixShiftContributionUsd: number;
  netRevenueMarginContributionBps: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

export type VarianceDecompositionCellProvenance = Readonly<{
  factIds: readonly string[];
  sourceClasses: readonly string[];
  sourceSystemRefs: readonly string[];
  evidenceRefIds: readonly string[];
  artifactIds: readonly string[];
  ingestJobIds: readonly string[];
  asOf: readonly string[];
  approvedAt: readonly string[];
}>;

export type VarianceDecompositionCell = Readonly<{
  metricKey: string;
  periodType: string;
  periodKey: string;
  dimensions: Readonly<Record<string, string>>;
  valueNumeric: string | number;
  unit: string;
  provenance: VarianceDecompositionCellProvenance;
}>;

export type VarianceDecompositionInput = Readonly<{
  tenantId: string;
  metricKey: string;
  basisMetricKey: string;
  dimension: string;
  memberKeys: readonly string[];
  periodType: string;
  baselinePeriodKey: string;
  comparisonPeriodKey: string;
  grainRollup: string;
  cells: readonly VarianceDecompositionCell[];
  totalMetricCells?: readonly VarianceDecompositionCell[];
  dimensionMembersByDimension?: Readonly<Record<string, readonly string[]>>;
  asOfPolicy?: MetricQuerySpec['asOfPolicy'];
  maxCells?: number;
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type VarianceDecompositionProvenance = Readonly<{
  sourceClass: string | null;
  sourceClasses: readonly string[];
  sourceSystemRefs: readonly string[];
  evidenceRefIds: readonly string[];
  factIds: readonly string[];
  artifactIds: readonly string[];
  ingestJobIds: readonly string[];
  asOf: readonly string[];
  approvedAt: readonly string[];
}>;

export type VarianceDecompositionMemberContribution = Readonly<{
  memberKey: string;
  baselineValue: number;
  comparisonValue: number;
  basisBaselineValue: number;
  basisComparisonValue: number;
  baselineRate: number;
  comparisonRate: number;
  withinLineContributionDelta: number;
  mixContributionDelta: number;
  totalContributionDelta: number;
  provenance: VarianceDecompositionProvenance;
}>;

export type VarianceDecompositionOutput = Readonly<{
  metricKey: string;
  basisMetricKey: string;
  dimension: string;
  periodType: string;
  baselinePeriodKey: string;
  comparisonPeriodKey: string;
  totalBaselineValue: number;
  totalComparisonValue: number;
  totalChange: number;
  basisBaselineTotal: number;
  basisComparisonTotal: number;
  withinLineEffect: number;
  mixEffect: number;
  memberContributions: readonly VarianceDecompositionMemberContribution[];
  reconciliation: Readonly<{
    totalChange: number;
    withinLineEffect: number;
    mixEffect: number;
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  provenance: VarianceDecompositionProvenance;
  sourceClass: string | null;
  sourceClasses: readonly string[];
  statusBand: ApprovedFormulaStatusBand;
  reportingCutAsOf?: string;
}>;

export type PvmDecompositionItemInput = Readonly<{
  itemKey: string;
  baselinePrice: number;
  baselineQuantity: number;
  comparisonPrice: number;
  comparisonQuantity: number;
}>;

export type PvmDecompositionInput = Readonly<{
  items: readonly PvmDecompositionItemInput[];
  governedTotalChange?: number;
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type PvmDecompositionItemContribution = Readonly<{
  itemKey: string;
  baselinePrice: number;
  comparisonPrice: number;
  baselineQuantity: number;
  comparisonQuantity: number;
  baselineShare: number;
  comparisonShare: number;
  priceChange: number;
  priceContribution: number;
  volumeContribution: number;
  mixContribution: number;
  totalValueChange: number;
  residual: number;
}>;

export type PvmDecompositionOutput = Readonly<{
  baselineValue: number;
  comparisonValue: number;
  baselineQuantity: number;
  comparisonQuantity: number;
  baselineBlendedPrice: number;
  comparisonBlendedPrice: number;
  blendedPriceChange: number;
  totalChange: number;
  priceEffect: number;
  volumeEffect: number;
  mixEffect: number;
  residual: number;
  residualLabel: 'unexplained variance';
  itemContributions: readonly PvmDecompositionItemContribution[];
  reconciliation: Readonly<{
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  reportingCutAsOf?: string;
}>;

export type CostRateVolumeInput = Readonly<{
  baselineRate: number;
  baselineActivity: number;
  comparisonRate: number;
  comparisonActivity: number;
  efficiencyStandard?: Readonly<{
    standardInputPerOutput: number;
    baselineOutput: number;
    comparisonOutput: number;
  }>;
  governedTotalCostChange?: number;
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type CostRateVolumeOutput = Readonly<{
  rawCostChange: number;
  totalOiFavorabilityChange: number;
  rateEffect: number;
  volumeEffect: number;
  pureVolumeEffect: number | null;
  efficiencyEffect: number | null;
  residual: number;
  residualLabel: 'unexplained cost variance';
  missingEfficiencyStandard: boolean;
  missingEfficiencyStandardLabel: 'efficiency standard not governed' | null;
  reconciliation: Readonly<{
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  reportingCutAsOf?: string;
}>;

export type FxIsolationLineInput = Readonly<{
  lineKey: string;
  baselineReportedValue: number;
  comparisonReportedValue: number;
  comparisonLocalValue: number;
  baselineReportingRate?: number;
  comparisonReportingRate?: number;
}>;

export type FxIsolationInput = Readonly<{
  lines: readonly FxIsolationLineInput[];
  governedTotalReportedChange?: number;
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type FxIsolationOutput = Readonly<{
  totalReportedChange: number;
  fxEffect: number;
  constantCurrencyChange: number;
  residual: number;
  residualLabel: 'unexplained FX variance';
  reconciliation: Readonly<{
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  reportingCutAsOf?: string;
}>;

export type OneoffIsolationLineInput = Readonly<{
  lineKey: string;
  baselineValue: number;
  comparisonValue: number;
  nonrecurring?: boolean;
}>;

export type OneoffIsolationInput = Readonly<{
  lines: readonly OneoffIsolationLineInput[];
  governedTotalChange?: number;
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type OneoffIsolationOutput = Readonly<{
  totalChange: number;
  underlyingChange: number;
  oneoffChange: number;
  residual: number;
  residualLabel: 'unexplained one-off variance';
  unclassifiedLineKeys: readonly string[];
  reconciliation: Readonly<{
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  reportingCutAsOf?: string;
}>;

export type OiBridgeNodeInput = Readonly<{
  nodeKey: string;
  reportedDelta: number;
  revenueSideEffect: number;
  costSideEffect: number;
  children?: readonly OiBridgeNodeInput[];
}>;

export type OiBridgeComposeInput = Readonly<{
  root: OiBridgeNodeInput;
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type OiBridgeNodeOutput = Readonly<{
  nodeKey: string;
  reportedDelta: number;
  revenueSideEffect: number;
  costSideEffect: number;
  contribution: number;
  residual: number;
  children: readonly OiBridgeNodeOutput[];
}>;

export type OiBridgeComposeOutput = Readonly<{
  totalChange: number;
  residual: number;
  residualLabel: 'unexplained bridge variance';
  root: OiBridgeNodeOutput;
  reconciliation: Readonly<{
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  reportingCutAsOf?: string;
}>;

export type VarianceTracebackTermInput = Readonly<{
  termKey: string;
  value: number;
}>;

export type VarianceTracebackInput = Readonly<{
  totalMiss: number;
  explainedTerms: readonly VarianceTracebackTermInput[];
  tolerance?: number;
  reportingCutAsOf?: string;
}>;

export type VarianceTracebackOutput = Readonly<{
  totalMiss: number;
  explainedVariance: number;
  unexplainedResidual: number;
  residualLabel: 'unexplained (no governed driver)';
  explainedTerms: readonly VarianceTracebackTermInput[];
  reconciliation: Readonly<{
    partsTotal: number;
    residual: number;
    tolerance: number;
    reconciles: boolean;
  }>;
  reportingCutAsOf?: string;
}>;
export type PricingIncreaseElasticityScenarioInput = Readonly<{
  baselineRevenueUsd: number;
  priceChangePct: number;
  baselinePlannedSellRateIncreasePct: number;
  baselineExpectedVolumeResponsePct: number;
  expectedNetRevenueRetentionPct: number;
  agpFlowThroughPct: number;
  executionCostUsd: number;
  reportingCutAsOf?: string;
}>;

export type PricingIncreaseElasticityScenarioOutput = Readonly<{
  priceChangePct: number;
  impliedElasticity: number;
  expectedVolumeResponsePct: number;
  expectedRevenueGrowthPct: number;
  netRevenueDeltaUsd: number;
  agpContributionUsd: number;
  operatingIncomeContributionUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

const STRATEGIC_EXECUTION_CAPACITY_SOURCE_REFS = Object.freeze([
  'ask_helix_memory:strategic_execution_capacity_evidence_v1',
]);
const STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KIND = 'portfolio_planner';
const ORG_COST_OPTIMIZATION_SOURCE_REFS = Object.freeze([
  ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const ORG_COST_OPTIMIZATION_CONTEXT_KIND = 'decision_agent:org_analysis_optimization';
const CARRIER_TRANSPORTATION_COST_SOURCE_REFS = Object.freeze([
  CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const CARRIER_TRANSPORTATION_COST_CONTEXT_KIND =
  ASK_HELIX_CARRIER_TRANSPORTATION_COST_DATA_LAKE_CONTEXT_KIND;
export const PORTFOLIO_CAPACITY_ALLOCATION_EVIDENCE_SCHEMA_VERSION =
  'portfolio_capacity_allocation_governed_snapshot_v1';
export const PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS = Object.freeze([
  'decision_agent:portfolio_capacity_allocation:portfolio_plan_snapshot',
  'decision_agent:portfolio_capacity_allocation:capacity_actuals_snapshot',
  'decision_agent:portfolio_capacity_allocation:dependency_constraint_snapshot',
  'decision_agent:portfolio_capacity_allocation:allocation_policy',
  'decision_agent:portfolio_capacity_allocation:finance_actuals_snapshot',
] as const);
const PORTFOLIO_CAPACITY_ALLOCATION_SOURCE_REFS = Object.freeze([
  PORTFOLIO_CAPACITY_ALLOCATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS = Object.freeze([
  FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
  FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
]);
const FINANCE_CONTEXT_KIND = 'decision_agent:finance';
const REALIZATION_COMPONENT_VARIANCE_CONTEXT_KIND =
  'decision_agent:realization:component_variance';
const REALIZATION_COMPONENT_VARIANCE_SOURCE_REFS = Object.freeze([
  REALIZATION_COMPONENT_VARIANCE_SOURCE_SYSTEM_REF,
]);
const INDUSTRY_BENCHMARK_SOURCE_REFS = Object.freeze([
  INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF,
]);
const INDUSTRY_BENCHMARK_CONTEXT_KIND =
  'decision_agent:industry_benchmark_reference_ranges';
const PRICING_REALIZATION_SOURCE_REFS = Object.freeze([
  PRICING_REALIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const PRICING_REALIZATION_CONTEXT_KIND =
  ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_CONTEXT_KIND;
const COST_TO_SERVE_SOURCE_REFS = Object.freeze([
  COST_TO_SERVE_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const ESG_EMISSIONS_SOURCE_REFS = Object.freeze([
  ESG_EMISSIONS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const COST_TO_SERVE_CONTEXT_KIND =
  'decision_agent:cost_to_serve:governed_snapshot';
const CAPITAL_CASH_SOURCE_REFS = Object.freeze([
  CAPITAL_CASH_BALANCE_SHEET_PUBLIC_FILING_SOURCE_SYSTEM_REF,
]);
const CAPITAL_CASH_CONTEXT_KIND =
  'decision_agent:capital_cash:balance_sheet_snapshot';
const WORKFORCE_REDUCTION_EVIDENCE_PACK_CONTEXT_KIND =
  'decision_agent:workforce_reduction:evidence_pack';
const PRICING_DISCOUNT_LEAKAGE_SOURCE_REFS = Object.freeze([
  PRICING_DISCOUNT_POLICY_AUTHORITY_SOURCE_SYSTEM_REF,
  PRICING_DISCOUNT_TRANSACTION_SNAPSHOT_SOURCE_SYSTEM_REF,
]);
const NUMBER_TOLERANCE = 0.000001;

/**
 * Capture the implementation source RAW (#4008, sol re-review of PR #4094).
 *
 * This used to collapse whitespace here, at capture time. That is upstream of the
 * digest, and it silently destroyed the source: a `//` line comment survives the
 * collapse while the newline that ENDED it does not, so every character after the
 * first line comment becomes commented-out and the captured text no longer parses.
 * Under an emitter that keeps comments (ts-jest) that made
 * `carrier_transportation_cost_opportunity@1.0.3` uncanonicalizable, so its identity
 * fell back to raw text and diverged from the product's.
 *
 */
function implementationDigestInput(
  name: string,
  _implementation: unknown,
): ApprovedFormulaImplementationDigestInput {
  return Object.freeze({
    name,
    source: governedSourceDigest('approved_formula_evaluator'),
  });
}

function implementationDigestInputs(
  entries: readonly (readonly [string, unknown])[],
): readonly ApprovedFormulaImplementationDigestInput[] {
  return Object.freeze(entries.map(([name, implementation]) =>
    implementationDigestInput(name, implementation),
  ));
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function requireFiniteUsd(value: unknown, name: string): number {
  if (!isFiniteNumber(value)) {
    throw new Error(`${name} must be a finite USD value`);
  }
  return Math.round(value);
}

export function computeNetEbitdaOpportunityUsd(
  input: NetEbitdaOpportunityInput,
): NetEbitdaOpportunityOutput {
  const grossRunRateSavingsUsd = requireFiniteUsd(
    input.grossRunRateSavingsUsd,
    'grossRunRateSavingsUsd',
  );
  const oneTimeSeveranceUsd = requireFiniteUsd(
    input.oneTimeSeveranceUsd,
    'oneTimeSeveranceUsd',
  );
  const capacityCostAtRiskUsd = requireFiniteUsd(
    input.capacityCostAtRiskUsd ?? 0,
    'capacityCostAtRiskUsd',
  );

  return Object.freeze({
    grossRunRateSavingsUsd,
    oneTimeSeveranceUsd,
    capacityCostAtRiskUsd,
    netEbitdaOpportunityUsd:
      grossRunRateSavingsUsd - oneTimeSeveranceUsd - capacityCostAtRiskUsd,
  });
}

function executeWorkforceReductionNetEbitdaOpportunity(
  input: WorkforceReductionNetEbitdaOpportunityInput,
): ApprovedFormulaResult<WorkforceReductionNetEbitdaOpportunityOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  if (
    !isFiniteNumber(input.grossRunRateSavingsUsd) ||
    !isFiniteNumber(input.oneTimeSeveranceUsd) ||
    !isFiniteNumber(input.capacityCostAtRiskUsd) ||
    input.grossRunRateSavingsUsd < 0 ||
    input.oneTimeSeveranceUsd < 0 ||
    input.capacityCostAtRiskUsd < 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Workforce net EBITDA inputs must be finite non-negative USD values',
    };
  }
  if (
    typeof input.sourceRunId !== 'string' ||
    !input.sourceRunId.trim() ||
    typeof input.scenarioCaseId !== 'string' ||
    !input.scenarioCaseId.trim() ||
    typeof input.reportingCutAsOf !== 'string' ||
    !Number.isFinite(Date.parse(input.reportingCutAsOf))
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'sourceRunId, scenarioCaseId, and a valid reportingCutAsOf are required',
    };
  }

  const netEbitda = computeNetEbitdaOpportunityUsd(input);
  return {
    status: 'ok',
    output: Object.freeze({
      grossRunRateSavingsUsd: netEbitda.grossRunRateSavingsUsd,
      oneTimeSeveranceUsd: netEbitda.oneTimeSeveranceUsd,
      capacityCostAtRiskUsd: netEbitda.capacityCostAtRiskUsd,
      workforceReductionNetEbitdaOpportunityUsd:
        netEbitda.netEbitdaOpportunityUsd,
    }),
  };
}

function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function capacityStatusBand(utilizationRatio: number): ApprovedFormulaStatusBand {
  if (utilizationRatio >= 1) {
    return 'sufficient';
  }

  if (utilizationRatio >= 0.75) {
    return 'constrained';
  }

  return 'critical';
}

function ebitdaOpportunityStatusBand(netOpportunityUsd: number): ApprovedFormulaStatusBand {
  if (netOpportunityUsd > 0) return 'sufficient';
  if (netOpportunityUsd === 0) return 'constrained';
  return 'critical';
}

function opportunityStatusBand(opportunityUsd: number): ApprovedFormulaStatusBand {
  if (opportunityUsd > 0) return 'sufficient';
  if (opportunityUsd === 0) return 'constrained';
  return 'critical';
}

function workingCapitalDaysStatusBand(days: number): ApprovedFormulaStatusBand {
  if (days <= 30) return 'sufficient';
  if (days <= 60) return 'constrained';
  return 'critical';
}

function workingCapitalCashImpactStatusBand(cashImpactUsd: number): ApprovedFormulaStatusBand {
  if (cashImpactUsd > 0) return 'sufficient';
  if (cashImpactUsd === 0) return 'constrained';
  return 'critical';
}

function cashConversionCycleStatusBand(cashConversionCycleDays: number): ApprovedFormulaStatusBand {
  if (cashConversionCycleDays <= 30) return 'sufficient';
  if (cashConversionCycleDays <= 60) return 'constrained';
  return 'critical';
}

function executeDaysSalesOutstanding(
  input: DaysSalesOutstandingInput,
): ApprovedFormulaResult<DaysSalesOutstandingOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const accountsReceivableUsd = record.accountsReceivableUsd;
  const revenueUsd = record.revenueUsd;
  if (!isFiniteNumber(accountsReceivableUsd) || !isFiniteNumber(revenueUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'Accounts receivable and revenue are required for DSO.',
    };
  }
  if (accountsReceivableUsd < 0 || revenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Accounts receivable must be non-negative and revenue must be positive.',
    };
  }

  const dsoDays = roundTo((accountsReceivableUsd / revenueUsd) * 365, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      accountsReceivableUsd: roundTo(accountsReceivableUsd, 2),
      revenueUsd: roundTo(revenueUsd, 2),
      dsoDays,
      statusBand: workingCapitalDaysStatusBand(dsoDays),
    }),
  };
}

function executeDaysInventoryOutstandingDioDays(
  input: DaysInventoryOutstandingDioDaysInput,
): ApprovedFormulaResult<DaysInventoryOutstandingDioDaysOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const inventoryUsd = record.inventoryUsd;
  const costOfGoodsSoldUsd = record.costOfGoodsSoldUsd;
  if (!isFiniteNumber(inventoryUsd) || !isFiniteNumber(costOfGoodsSoldUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'Inventory and COGS are required for DIO.',
    };
  }
  if (inventoryUsd < 0 || costOfGoodsSoldUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Inventory must be non-negative and COGS must be positive.',
    };
  }

  const dioDays = roundTo((inventoryUsd / costOfGoodsSoldUsd) * 365, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      inventoryUsd: roundTo(inventoryUsd, 2),
      costOfGoodsSoldUsd: roundTo(costOfGoodsSoldUsd, 2),
      dioDays,
      statusBand: workingCapitalDaysStatusBand(dioDays),
    }),
  };
}

function executeDaysPayablesOutstanding(
  input: DaysPayablesOutstandingInput,
): ApprovedFormulaResult<DaysPayablesOutstandingOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const accountsPayableUsd = record.accountsPayableUsd;
  const costOfGoodsSoldUsd = record.costOfGoodsSoldUsd;
  if (!isFiniteNumber(accountsPayableUsd) || !isFiniteNumber(costOfGoodsSoldUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'Accounts payable and COGS are required for DPO.',
    };
  }
  if (accountsPayableUsd < 0 || costOfGoodsSoldUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Accounts payable must be non-negative and COGS must be positive.',
    };
  }

  const dpoDays = roundTo((accountsPayableUsd / costOfGoodsSoldUsd) * 365, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      accountsPayableUsd: roundTo(accountsPayableUsd, 2),
      costOfGoodsSoldUsd: roundTo(costOfGoodsSoldUsd, 2),
      dpoDays,
      statusBand: workingCapitalDaysStatusBand(dpoDays),
    }),
  };
}

function executeWorkingCapitalDeltaCash(
  input: WorkingCapitalDeltaCashInput,
): ApprovedFormulaResult<WorkingCapitalDeltaCashOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const priorAccountsReceivableUsd = finiteNumberOrNull(record.priorAccountsReceivableUsd);
  const priorInventoryUsd = finiteNumberOrNull(record.priorInventoryUsd);
  const priorAccountsPayableUsd = finiteNumberOrNull(record.priorAccountsPayableUsd);
  const currentAccountsReceivableUsd = finiteNumberOrNull(record.currentAccountsReceivableUsd);
  const currentInventoryUsd = finiteNumberOrNull(record.currentInventoryUsd);
  const currentAccountsPayableUsd = finiteNumberOrNull(record.currentAccountsPayableUsd);
  if (
    priorAccountsReceivableUsd === null ||
    priorInventoryUsd === null ||
    priorAccountsPayableUsd === null ||
    currentAccountsReceivableUsd === null ||
    currentInventoryUsd === null ||
    currentAccountsPayableUsd === null
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'Prior and current AR, inventory, and AP are required for NWC cash impact.',
    };
  }
  if (
    [
      priorAccountsReceivableUsd,
      priorInventoryUsd,
      priorAccountsPayableUsd,
      currentAccountsReceivableUsd,
      currentInventoryUsd,
      currentAccountsPayableUsd,
    ].some((value) => value < 0)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'AR, inventory, and AP inputs must be non-negative USD values.',
    };
  }

  const priorNetWorkingCapitalUsd = roundTo(
    priorAccountsReceivableUsd + priorInventoryUsd - priorAccountsPayableUsd,
    2,
  );
  const currentNetWorkingCapitalUsd = roundTo(
    currentAccountsReceivableUsd + currentInventoryUsd - currentAccountsPayableUsd,
    2,
  );
  const netWorkingCapitalDeltaUsd = roundTo(
    currentNetWorkingCapitalUsd - priorNetWorkingCapitalUsd,
    2,
  );
  const cashImpactUsd = roundTo(-netWorkingCapitalDeltaUsd, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      priorNetWorkingCapitalUsd,
      currentNetWorkingCapitalUsd,
      netWorkingCapitalDeltaUsd,
      cashImpactUsd,
      statusBand: workingCapitalCashImpactStatusBand(cashImpactUsd),
    }),
  };
}

function executeCashConversionCycleDays(
  input: CashConversionCycleDaysInput,
): ApprovedFormulaResult<CashConversionCycleDaysOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const dso = record.daysSalesOutstandingDays;
  const dio = record.daysInventoryOutstandingDays;
  const dpo = record.daysPayablesOutstandingDays;
  if (!isFiniteNumber(dso) || !isFiniteNumber(dio) || !isFiniteNumber(dpo)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'DSO, DIO, and DPO are required day-count inputs.',
    };
  }
  if (dso < 0 || dio < 0 || dpo < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'DSO, DIO, and DPO must be non-negative day counts.',
    };
  }

  const cashConversionCycleDays = roundTo(dso + dio - dpo, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      daysSalesOutstandingDays: roundTo(dso, 2),
      daysInventoryOutstandingDays: roundTo(dio, 2),
      daysPayablesOutstandingDays: roundTo(dpo, 2),
      cashConversionCycleDays,
      statusBand: cashConversionCycleStatusBand(cashConversionCycleDays),
    }),
  };
}

function validateCashConversionCycleInputAgainstEvidence(
  input: Readonly<CashConversionCycleDaysInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBinding = readCashConversionCycleInputBinding(input);
  if ('reasonCode' in inputBinding) return inputBinding;
  return validateCashConversionCycleEvidenceBinding(inputBinding, artifacts);
}

function validateCashConversionCycleReconciliation(
  input: Readonly<CashConversionCycleDaysInput>,
  output: Readonly<CashConversionCycleDaysOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBinding = readCashConversionCycleInputBinding(input);
  if ('reasonCode' in inputBinding) return inputBinding;

  const outputRecord = asRecord(output);
  if (
    !outputRecord ||
    !isFiniteNumber(outputRecord.daysSalesOutstandingDays) ||
    !isFiniteNumber(outputRecord.daysInventoryOutstandingDays) ||
    !isFiniteNumber(outputRecord.daysPayablesOutstandingDays) ||
    !isFiniteNumber(outputRecord.cashConversionCycleDays)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Cash conversion cycle output must carry DSO, DIO, DPO, and CCC values.',
    };
  }

  const expectedCashConversionCycleDays = roundTo(
    inputBinding.daysSalesOutstandingDays +
      inputBinding.daysInventoryOutstandingDays -
      inputBinding.daysPayablesOutstandingDays,
    2,
  );
  if (
    !sameNumber(
      outputRecord.daysSalesOutstandingDays,
      roundTo(inputBinding.daysSalesOutstandingDays, 2),
    ) ||
    !sameNumber(
      outputRecord.daysInventoryOutstandingDays,
      roundTo(inputBinding.daysInventoryOutstandingDays, 2),
    ) ||
    !sameNumber(
      outputRecord.daysPayablesOutstandingDays,
      roundTo(inputBinding.daysPayablesOutstandingDays, 2),
    ) ||
    !sameNumber(outputRecord.cashConversionCycleDays, expectedCashConversionCycleDays)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Cash conversion cycle output must reconcile to DSO + DIO - DPO.',
    };
  }

  return validateCashConversionCycleEvidenceBinding(inputBinding, artifacts);
}

type CashConversionCycleEvidenceBinding = Readonly<{
  daysSalesOutstandingDays: number;
  daysInventoryOutstandingDays: number;
  daysPayablesOutstandingDays: number;
  reportingCutAsOf: string | null;
}>;

function readCashConversionCycleInputBinding(
  input: Readonly<CashConversionCycleDaysInput>,
): CashConversionCycleEvidenceBinding | ApprovedFormulaEvidenceValidationBlock {
  const record = asRecord(input);
  if (!record) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Cash conversion cycle input must be an object.',
    };
  }
  if (
    !isFiniteNumber(record.daysSalesOutstandingDays) ||
    !isFiniteNumber(record.daysInventoryOutstandingDays) ||
    !isFiniteNumber(record.daysPayablesOutstandingDays)
  ) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Cash conversion cycle input must carry DSO, DIO, and DPO.',
    };
  }

  return {
    daysSalesOutstandingDays: record.daysSalesOutstandingDays,
    daysInventoryOutstandingDays: record.daysInventoryOutstandingDays,
    daysPayablesOutstandingDays: record.daysPayablesOutstandingDays,
    reportingCutAsOf: stringOrNull(record.reportingCutAsOf),
  };
}

function validateCashConversionCycleEvidenceBinding(
  input: CashConversionCycleEvidenceBinding,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const hasApprovedArtifact = artifacts.some((artifact) => {
    const content = asRecord(artifact.contentJson);
    const source = asRecord(content?.source);
    const components = Array.isArray(content?.workingCapitalComponents)
      ? content.workingCapitalComponents
      : [];
    const reportingCutAsOf =
      stringOrNull(artifact.reportingCutAsOf) ?? stringOrNull(source?.sourceAsOf);
    if (content?.fixtureOnly === true || !reportingCutAsOf) return false;
    if (input.reportingCutAsOf && !sameIsoDate(input.reportingCutAsOf, reportingCutAsOf)) {
      return false;
    }

    return (
      artifact.sourceClass === 'public-filing' &&
      artifact.schemaVersion === 'capital_cash_balance_sheet_snapshot_v1' &&
      content?.schemaVersion === 'capital_cash_balance_sheet_snapshot_v1' &&
      hasMatchingCashConversionCycleComponent(
        components,
        'days_sales_outstanding_days',
        input.daysSalesOutstandingDays,
        reportingCutAsOf,
      ) &&
      hasMatchingCashConversionCycleComponent(
        components,
        'days_inventory_outstanding_days',
        input.daysInventoryOutstandingDays,
        reportingCutAsOf,
      ) &&
      hasMatchingCashConversionCycleComponent(
        components,
        'days_payables_outstanding_days',
        input.daysPayablesOutstandingDays,
        reportingCutAsOf,
      )
    );
  });

  if (!hasApprovedArtifact) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Cash conversion cycle requires approved balance-sheet and working-capital component evidence.',
    };
  }

  return null;
}

function hasMatchingCashConversionCycleComponent(
  components: readonly unknown[],
  metricKey: string,
  expectedValue: number,
  reportingCutAsOf: string,
): boolean {
  return components.some((component) => {
    const record = asRecord(component);
    if (!record) return false;
    const sourceAsOf = stringOrNull(record?.sourceAsOf);
    return (
      record.metricKey === metricKey &&
      record.unit === 'days' &&
      isFiniteNumber(record.value) &&
      sameNumber(record.value, expectedValue) &&
      sourceAsOf !== null &&
      sameIsoDate(sourceAsOf, reportingCutAsOf)
    );
  });
}

function marginStatusBand(marginPct: number): ApprovedFormulaStatusBand {
  if (marginPct >= 15) return 'sufficient';
  if (marginPct >= 5) return 'constrained';
  return 'critical';
}

function positiveUsdStatusBand(valueUsd: number): ApprovedFormulaStatusBand {
  if (valueUsd > 0) return 'sufficient';
  if (valueUsd === 0) return 'constrained';
  return 'critical';
}

function financeGapStatusBand(gapUsd: number): ApprovedFormulaStatusBand {
  if (gapUsd <= 0) return 'sufficient';
  return 'constrained';
}

function executeFinanceEbitdaDerive(
  input: FinanceEbitdaDeriveInput,
): ApprovedFormulaResult<FinanceEbitdaDeriveOutput> {
  if (
    !isFiniteNumber(input?.operatingIncomeUsd) ||
    !isFiniteNumber(input?.depreciationAmortizationUsd) ||
    !isFiniteNumber(input?.approvedAdjustmentsUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'operatingIncomeUsd, depreciationAmortizationUsd, and approvedAdjustmentsUsd must be finite USD values',
    };
  }

  const ebitdaUsd = roundTo(
    input.operatingIncomeUsd +
      input.depreciationAmortizationUsd +
      input.approvedAdjustmentsUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      ebitdaUsd,
      statusBand: positiveUsdStatusBand(ebitdaUsd),
    }),
  };
}

function executeFinanceOperatingIncomeDerive(
  input: FinanceOperatingIncomeDeriveInput,
): ApprovedFormulaResult<FinanceOperatingIncomeDeriveOutput> {
  if (
    !isFiniteNumber(input?.revenueUsd) ||
    !isFiniteNumber(input?.costOfRevenueUsd) ||
    !isFiniteNumber(input?.operatingExpenseUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'revenueUsd, costOfRevenueUsd, and operatingExpenseUsd must be finite USD values',
    };
  }

  const operatingIncomeUsd = roundTo(
    input.revenueUsd - input.costOfRevenueUsd - input.operatingExpenseUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      operatingIncomeUsd,
      statusBand: positiveUsdStatusBand(operatingIncomeUsd),
    }),
  };
}

function executeFinanceEbitdaFromOperatingIncome(
  input: FinanceEbitdaFromOperatingIncomeInput,
): ApprovedFormulaResult<FinanceEbitdaFromOperatingIncomeOutput> {
  if (
    !isFiniteNumber(input?.operatingIncomeUsd) ||
    !isFiniteNumber(input?.depreciationAmortizationUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'operatingIncomeUsd and depreciationAmortizationUsd must be finite USD values',
    };
  }

  const ebitdaUsd = roundTo(
    input.operatingIncomeUsd + input.depreciationAmortizationUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      ebitdaUsd,
      statusBand: positiveUsdStatusBand(ebitdaUsd),
    }),
  };
}

function executeFinanceGrossMargin(
  input: FinanceGrossMarginInput,
): ApprovedFormulaResult<FinanceGrossMarginOutput> {
  if (!isFiniteNumber(input?.grossProfitUsd) || !isFiniteNumber(input?.revenueUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'grossProfitUsd and revenueUsd must be finite USD values',
    };
  }
  if (input.revenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'revenueUsd must be greater than zero',
    };
  }

  const grossMarginPct = roundTo((input.grossProfitUsd / input.revenueUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      grossMarginPct,
      statusBand: marginStatusBand(grossMarginPct),
    }),
  };
}

function executeFinanceNetRevenueMargin(
  input: FinanceNetRevenueMarginInput,
): ApprovedFormulaResult<FinanceNetRevenueMarginOutput> {
  if (
    !isFiniteNumber(input?.adjustedGrossProfitUsd) ||
    !isFiniteNumber(input?.revenueUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'adjustedGrossProfitUsd and revenueUsd must be finite USD values',
    };
  }
  if (input.revenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'revenueUsd must be greater than zero',
    };
  }

  const netRevenueMarginPct = roundTo(
    (input.adjustedGrossProfitUsd / input.revenueUsd) * 100,
    4,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      netRevenueMarginPct,
      statusBand: marginStatusBand(netRevenueMarginPct),
    }),
  };
}

function executeFreightCostRepricing(
  input: FreightCostRepricingInput,
): ApprovedFormulaResult<FreightCostRepricingOutput> {
  if (
    !isFiniteNumber(input?.baselinePurchasedTransportationUsd) ||
    !isFiniteNumber(input?.admittedSensitivity) ||
    !isFiniteNumber(input?.freightRateDeltaPct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'baselinePurchasedTransportationUsd, admittedSensitivity, and freightRateDeltaPct must be finite values',
    };
  }
  if (
    input.baselinePurchasedTransportationUsd < 0 ||
    input.admittedSensitivity < 0 ||
    input.admittedSensitivity > 1 ||
    input.freightRateDeltaPct <= -100 ||
    Math.abs(input.freightRateDeltaPct) > 50
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'freight repricing requires non-negative baseline USD, sensitivity in [0,1], and freightRateDeltaPct in [-50,50]',
    };
  }

  const purchasedTransportationDeltaUsd = roundTo(
    input.baselinePurchasedTransportationUsd *
      input.admittedSensitivity *
      (input.freightRateDeltaPct / 100),
    2,
  );
  const repricedPurchasedTransportationUsd = roundTo(
    input.baselinePurchasedTransportationUsd + purchasedTransportationDeltaUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      baselinePurchasedTransportationUsd: roundTo(
        input.baselinePurchasedTransportationUsd,
        2,
      ),
      admittedSensitivity: roundTo(input.admittedSensitivity, 4),
      freightRateDeltaPct: roundTo(input.freightRateDeltaPct, 4),
      purchasedTransportationDeltaUsd,
      repricedPurchasedTransportationUsd,
      statusBand: positiveUsdStatusBand(repricedPurchasedTransportationUsd),
    }),
  };
}

function executeDemandRevenueRepricing(
  input: DemandRevenueRepricingInput,
): ApprovedFormulaResult<DemandRevenueRepricingOutput> {
  if (
    !isFiniteNumber(input?.baselineGrossRevenueUsd) ||
    !isFiniteNumber(input?.admittedSensitivity) ||
    !isFiniteNumber(input?.demandVolumeDeltaPct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'baselineGrossRevenueUsd, admittedSensitivity, and demandVolumeDeltaPct must be finite values',
    };
  }
  if (
    input.baselineGrossRevenueUsd < 0 ||
    input.admittedSensitivity < 0 ||
    input.admittedSensitivity > 1 ||
    input.demandVolumeDeltaPct <= -100 ||
    Math.abs(input.demandVolumeDeltaPct) > 50
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'demand revenue repricing requires non-negative baseline USD, sensitivity in [0,1], and demandVolumeDeltaPct in [-50,50]',
    };
  }

  const grossRevenueDeltaUsd = roundTo(
    input.baselineGrossRevenueUsd *
      input.admittedSensitivity *
      (input.demandVolumeDeltaPct / 100),
    2,
  );
  const repricedGrossRevenueUsd = roundTo(
    input.baselineGrossRevenueUsd + grossRevenueDeltaUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      baselineGrossRevenueUsd: roundTo(input.baselineGrossRevenueUsd, 2),
      admittedSensitivity: roundTo(input.admittedSensitivity, 4),
      demandVolumeDeltaPct: roundTo(input.demandVolumeDeltaPct, 4),
      grossRevenueDeltaUsd,
      repricedGrossRevenueUsd,
      statusBand: positiveUsdStatusBand(repricedGrossRevenueUsd),
    }),
  };
}

function executeOperationalRevenuePerLoad(
  input: OperationalRevenuePerLoadInput,
): ApprovedFormulaResult<OperationalRevenuePerLoadOutput> {
  if (!isFiniteNumber(input?.revenueUsd) || !isFiniteNumber(input?.loadCount)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'revenueUsd and loadCount must be finite values',
    };
  }
  if (input.revenueUsd < 0 || input.loadCount <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'revenueUsd must be non-negative and loadCount must be greater than zero',
    };
  }

  const revenuePerLoadUsd = roundTo(input.revenueUsd / input.loadCount, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      revenueUsd: roundTo(input.revenueUsd, 2),
      loadCount: roundTo(input.loadCount, 4),
      revenuePerLoadUsd,
      statusBand: positiveUsdStatusBand(revenuePerLoadUsd),
    }),
  };
}

function executeOperationalCostPerLoad(
  input: OperationalCostPerLoadInput,
): ApprovedFormulaResult<OperationalCostPerLoadOutput> {
  if (!isFiniteNumber(input?.costUsd) || !isFiniteNumber(input?.loadCount)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'costUsd and loadCount must be finite values',
    };
  }
  if (input.costUsd < 0 || input.loadCount <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'costUsd must be non-negative and loadCount must be greater than zero',
    };
  }

  const costPerLoadUsd = roundTo(input.costUsd / input.loadCount, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      costUsd: roundTo(input.costUsd, 2),
      loadCount: roundTo(input.loadCount, 4),
      costPerLoadUsd,
      statusBand: positiveUsdStatusBand(costPerLoadUsd),
    }),
  };
}

function executeFinanceOperatingMargin(
  input: FinanceOperatingMarginInput,
): ApprovedFormulaResult<FinanceOperatingMarginOutput> {
  if (
    !isFiniteNumber(input?.adjustedOperatingIncomeUsd) ||
    !isFiniteNumber(input?.adjustedGrossProfitUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'adjustedOperatingIncomeUsd and adjustedGrossProfitUsd must be finite USD values',
    };
  }
  if (input.adjustedGrossProfitUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'adjustedGrossProfitUsd must be greater than zero',
    };
  }

  const operatingMarginPct = roundTo(
    (input.adjustedOperatingIncomeUsd / input.adjustedGrossProfitUsd) * 100,
    4,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      operatingMarginPct,
      statusBand: marginStatusBand(operatingMarginPct),
    }),
  };
}

function executeFinanceEbitdaMargin(
  input: FinanceEbitdaMarginInput,
): ApprovedFormulaResult<FinanceEbitdaMarginOutput> {
  if (!isFiniteNumber(input?.ebitdaUsd) || !isFiniteNumber(input?.revenueUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'ebitdaUsd and revenueUsd must be finite USD values',
    };
  }
  if (input.revenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'revenueUsd must be greater than zero',
    };
  }

  const ebitdaMarginPct = roundTo((input.ebitdaUsd / input.revenueUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      ebitdaMarginPct,
      statusBand: marginStatusBand(ebitdaMarginPct),
    }),
  };
}

function executeFinanceOperatingExpensePerHead(
  input: FinanceOperatingExpensePerHeadInput,
): ApprovedFormulaResult<FinanceOperatingExpensePerHeadOutput> {
  if (
    !isFiniteNumber(input?.operatingExpenseUsd) ||
    !isFiniteNumber(input?.reportingHeadcount)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'operatingExpenseUsd and reportingHeadcount must be finite values',
    };
  }
  if (input.reportingHeadcount <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'reportingHeadcount must be greater than zero',
    };
  }

  const operatingExpensePerHeadUsd = roundTo(
    input.operatingExpenseUsd / input.reportingHeadcount,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      operatingExpensePerHeadUsd,
      statusBand: positiveUsdStatusBand(operatingExpensePerHeadUsd),
    }),
  };
}

configureFinanceEbitdaTargetResolve({
  isFiniteNumber,
  roundTo,
  marginStatusBand,
  positiveUsdStatusBand,
});

function executeFinanceEbitdaGap(
  input: FinanceEbitdaGapInput,
): ApprovedFormulaResult<FinanceEbitdaGapOutput> {
  if (!isFiniteNumber(input?.ebitdaUsd) || !isFiniteNumber(input?.ebitdaTargetUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'ebitdaUsd and ebitdaTargetUsd must be finite USD values',
    };
  }

  const ebitdaGapUsd = roundTo(input.ebitdaTargetUsd - input.ebitdaUsd, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      ebitdaGapUsd,
      statusBand: financeGapStatusBand(ebitdaGapUsd),
    }),
  };
}

function executeFinanceObjectiveValueGap(
  input: FinanceObjectiveValueGapInput,
): ApprovedFormulaResult<FinanceObjectiveValueGapOutput> {
  if (
    typeof input?.objectiveMetricId !== 'string' ||
    !/^[a-z][a-z0-9_]*$/.test(input.objectiveMetricId.trim()) ||
    !isFiniteNumber(input.baselineValue) ||
    !isFiniteNumber(input.targetValue)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'objectiveMetricId, baselineValue, and targetValue must be finite governed objective-gap inputs',
    };
  }
  if (input.baselineValue === 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'baselineValue must be non-zero to compute gapPct',
    };
  }

  const baselineValue = roundTo(input.baselineValue, 2);
  const targetValue = roundTo(input.targetValue, 2);
  const gapValue = roundTo(targetValue - baselineValue, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      objectiveMetricId: input.objectiveMetricId.trim(),
      baselineValue,
      targetValue,
      gapValue,
      gapPct: roundTo((gapValue / baselineValue) * 100, 4),
      statusBand: financeGapStatusBand(gapValue),
    }),
  };
}

function executeBenchmarkGapToMedianDollarized(
  input: BenchmarkGapToMedianDollarizedInput,
): ApprovedFormulaResult<BenchmarkGapToMedianDollarizedOutput> {
  const metricId = typeof input?.metricId === 'string' ? input.metricId.trim() : '';
  const range = input?.referenceRange;
  const direction = input?.direction;
  const tenantMetricValue = input?.tenantMetricValue;

  if (!metricId) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'metricId is required',
    };
  }

  if (
    !isFiniteNumber(tenantMetricValue) ||
    !isBenchmarkMetricDirection(direction) ||
    !range ||
    !isFiniteNumber(range.min) ||
    !isFiniteNumber(range.p25) ||
    !isFiniteNumber(range.median) ||
    !isFiniteNumber(range.p75) ||
    !isFiniteNumber(range.max)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Benchmark input requires a finite tenantMetricValue, valid direction, and finite reference range.',
    };
  }

  if (!isBenchmarkReferenceRangeMonotonic(range)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Benchmark reference range must be monotonic from min through max.',
    };
  }

  const dollarization = benchmarkDollarValuePerMetricUnit(input.dollarization);
  if (dollarization.status === 'blocked') {
    return {
      status: 'blocked',
      reasonCode: dollarization.reasonCode,
      message: dollarization.message,
    };
  }

  const quartilePosition = benchmarkQuartilePosition(tenantMetricValue, range);
  const rawDelta = tenantMetricValue - range.median;
  const opportunityGap =
    direction === 'lower_is_leaner' ? Math.max(0, rawDelta) : Math.max(0, -rawDelta);
  const favorableToMedian = opportunityGap <= NUMBER_TOLERANCE;
  const gapToMedianDollarizedUsd = roundTo(
    opportunityGap * dollarization.output.valuePerMetricUnitUsd,
    2,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      metricId,
      tenantMetricValue: roundTo(tenantMetricValue, 4),
      referenceMedian: roundTo(range.median, 6),
      quartilePosition,
      quartileIndex: benchmarkQuartileIndex(quartilePosition),
      rawDeltaToMedian: roundTo(rawDelta, 4),
      opportunityGapToMedian: roundTo(opportunityGap, 4),
      favorableToMedian,
      dollarizationBasis: dollarization.output.basis,
      valuePerMetricUnitUsd: dollarization.output.valuePerMetricUnitUsd,
      gapToMedianDollarizedUsd,
      statusBand: benchmarkGapStatusBand(direction, quartilePosition, favorableToMedian),
    }),
  };
}

function executeScenarioInvestmentRoi(
  input: ScenarioInvestmentRoiInput,
): ApprovedFormulaResult<ScenarioInvestmentRoiOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const riskAdjustedValueUsd = record.riskAdjustedValueUsd;
  const totalInvestmentUsd = record.totalInvestmentUsd;
  if (!isFiniteNumber(riskAdjustedValueUsd) || !isFiniteNumber(totalInvestmentUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'riskAdjustedValueUsd and totalInvestmentUsd are required',
    };
  }
  if (totalInvestmentUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'totalInvestmentUsd must be greater than zero; incomplete requirements must stay named instead of returning infinite ROI',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      riskAdjustedValueUsd: roundTo(riskAdjustedValueUsd, 2),
      totalInvestmentUsd: roundTo(totalInvestmentUsd, 2),
      roi: roundTo(riskAdjustedValueUsd / totalInvestmentUsd, 4),
    }),
  };
}

function isBenchmarkMetricDirection(value: unknown): value is BenchmarkMetricDirection {
  return value === 'higher_is_better' || value === 'lower_is_leaner';
}

function isBenchmarkReferenceRangeMonotonic(
  range: BenchmarkReferenceRangeInput,
): boolean {
  return (
    range.min <= range.p25 &&
    range.p25 <= range.median &&
    range.median <= range.p75 &&
    range.p75 <= range.max
  );
}

function benchmarkQuartilePosition(
  value: number,
  range: BenchmarkReferenceRangeInput,
): BenchmarkQuartilePosition {
  if (value <= range.p25) return 'below_p25';
  if (value <= range.median) return 'p25_to_median';
  if (value <= range.p75) return 'median_to_p75';
  return 'above_p75';
}

function benchmarkQuartileIndex(position: BenchmarkQuartilePosition): 1 | 2 | 3 | 4 {
  if (position === 'below_p25') return 1;
  if (position === 'p25_to_median') return 2;
  if (position === 'median_to_p75') return 3;
  return 4;
}

function benchmarkDollarValuePerMetricUnit(
  input: BenchmarkDollarizationInput | undefined,
): ApprovedFormulaResult<{
  basis: BenchmarkDollarizationInput['basis'];
  valuePerMetricUnitUsd: number;
}> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Benchmark dollarization basis is required.',
    };
  }

  if (input.basis === 'percent_of_revenue') {
    if (!isFiniteNumber(input.revenueUsd) || input.revenueUsd <= 0) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'percent_of_revenue dollarization requires positive revenueUsd.',
      };
    }
    return {
      status: 'ok',
      output: Object.freeze({
        basis: input.basis,
        valuePerMetricUnitUsd: roundTo(input.revenueUsd / 100, 2),
      }),
    };
  }

  if (input.basis === 'per_revenue_billion_unit') {
    if (
      !isFiniteNumber(input.revenueUsd) ||
      input.revenueUsd <= 0 ||
      !isFiniteNumber(input.unitCostUsd) ||
      input.unitCostUsd < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'per_revenue_billion_unit dollarization requires positive revenueUsd and non-negative unitCostUsd.',
      };
    }
    return {
      status: 'ok',
      output: Object.freeze({
        basis: input.basis,
        valuePerMetricUnitUsd: roundTo((input.revenueUsd / 1_000_000_000) * input.unitCostUsd, 2),
      }),
    };
  }

  return {
    status: 'blocked',
    reasonCode: 'invalid_formula_input',
    message: 'Unsupported benchmark dollarization basis.',
  };
}

function benchmarkGapStatusBand(
  direction: BenchmarkMetricDirection,
  quartilePosition: BenchmarkQuartilePosition,
  favorableToMedian: boolean,
): ApprovedFormulaStatusBand {
  if (favorableToMedian) return 'sufficient';
  if (direction === 'lower_is_leaner' && quartilePosition === 'above_p75') {
    return 'critical';
  }
  if (direction === 'higher_is_better' && quartilePosition === 'below_p25') {
    return 'critical';
  }
  return 'constrained';
}

function validateDelayeringOrgHealthNumbers(
  input: DelayeringOrgHealthWarningInput,
): boolean {
  return (
    !isFiniteNumber(input.maxDepth) ||
    !isFiniteNumber(input.targetMaxDepth) ||
    !isFiniteNumber(input.averageManagerSpan) ||
    !isFiniteNumber(input.targetMinimumManagerSpan) ||
    !isFiniteNumber(input.thinSpanManagerCount) ||
    !isFiniteNumber(input.managerCount) ||
    !isFiniteNumber(input.maxThinSpanManagerRatio) ||
    input.maxDepth < 0 ||
    input.targetMaxDepth < 0 ||
    input.averageManagerSpan < 0 ||
    input.targetMinimumManagerSpan < 0 ||
    input.thinSpanManagerCount < 0 ||
    input.managerCount <= 0 ||
    input.maxThinSpanManagerRatio < 0 ||
    input.maxThinSpanManagerRatio > 1
  );
}

function deriveDelayeringOrgHealthWarning(
  input: DelayeringOrgHealthWarningInput,
): DelayeringOrgHealthWarningOutput {
  const triggeredRuleIds: string[] = [];
  if (input.maxDepth > input.targetMaxDepth) {
    triggeredRuleIds.push('delayering_org_health:max_depth_above_target');
  }
  if (input.averageManagerSpan < input.targetMinimumManagerSpan) {
    triggeredRuleIds.push('delayering_org_health:average_span_below_target');
  }
  const thinSpanManagerRatio = roundTo(input.thinSpanManagerCount / input.managerCount, 4);
  if (thinSpanManagerRatio > input.maxThinSpanManagerRatio) {
    triggeredRuleIds.push('delayering_org_health:thin_span_ratio_above_target');
  }

  const warningLevel: StrategicExecutionCapacityWarningLevel =
    triggeredRuleIds.length === 0
      ? 'none'
      : triggeredRuleIds.length === 1
        ? 'watch'
        : 'decision_required';

  return {
    warningLevel,
    warningLevelCode:
      warningLevel === 'none' ? 0 : warningLevel === 'watch' ? 1 : 2,
    triggeredRuleIds: Object.freeze(triggeredRuleIds),
  };
}

function executePricingRealizationNetRevenueDelta(
  input: PricingRealizationNetRevenueDeltaInput,
): ApprovedFormulaResult<PricingRealizationNetRevenueDeltaOutput> {
  if (
    !Number.isFinite(input.baselineRevenueUsd) ||
    !Number.isFinite(input.plannedSellRateIncreasePct) ||
    !Number.isFinite(input.expectedNetRevenueRetentionPct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Pricing realization net-revenue delta requires baseline revenue, planned sell-rate increase, and expected net-revenue retention.',
    };
  }
  if (
    input.baselineRevenueUsd < 0 ||
    input.expectedNetRevenueRetentionPct < 0 ||
    input.expectedNetRevenueRetentionPct > 1
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Pricing realization net-revenue delta requires non-negative baseline revenue and retention between 0 and 1.',
    };
  }
  const netRevenueDeltaUsd = roundTo(
    input.baselineRevenueUsd *
      input.plannedSellRateIncreasePct *
      input.expectedNetRevenueRetentionPct,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      netRevenueDeltaUsd,
      statusBand: netRevenueDeltaUsd >= 0 ? 'sufficient' : 'critical',
    }),
  };
}

function executePricingRealizationElasticityGuard(
  input: PricingRealizationElasticityGuardInput,
): ApprovedFormulaResult<PricingRealizationElasticityGuardOutput> {
  if (
    !Number.isFinite(input.expectedRevenueGrowthPct) ||
    !Number.isFinite(input.flatRevenueTolerancePct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Pricing realization flat-revenue guard requires expected revenue growth and a flat-revenue tolerance.',
    };
  }
  if (input.flatRevenueTolerancePct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Pricing realization flat-revenue tolerance must be non-negative.',
    };
  }
  const flatRevenueEligible =
    Math.abs(input.expectedRevenueGrowthPct) <= input.flatRevenueTolerancePct;
  return {
    status: 'ok',
    output: Object.freeze({
      flatRevenueEligible,
      expectedRevenueGrowthPct: roundTo(input.expectedRevenueGrowthPct, 6),
      flatRevenueTolerancePct: roundTo(input.flatRevenueTolerancePct, 6),
      expectedVolumeResponsePct: Number.isFinite(input.expectedVolumeResponsePct)
        ? roundTo(input.expectedVolumeResponsePct ?? 0, 6)
        : null,
      statusBand: flatRevenueEligible ? 'sufficient' : 'critical',
    }),
  };
}

function executePricingRealizationAgpContribution(
  input: PricingRealizationAgpContributionInput,
): ApprovedFormulaResult<PricingRealizationAgpContributionOutput> {
  if (
    !Number.isFinite(input.netRevenueDeltaUsd) ||
    !Number.isFinite(input.agpFlowThroughPct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Pricing realization AGP contribution requires net-revenue delta and AGP flow-through.',
    };
  }
  if (input.agpFlowThroughPct < 0 || input.agpFlowThroughPct > 1) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Pricing realization AGP flow-through must be between 0 and 1.',
    };
  }
  const agpContributionUsd = roundTo(input.netRevenueDeltaUsd * input.agpFlowThroughPct, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      agpContributionUsd,
      statusBand: agpContributionUsd >= 0 ? 'sufficient' : 'critical',
    }),
  };
}

function executePricingRealizationOiContribution(
  input: PricingRealizationOiContributionInput,
): ApprovedFormulaResult<PricingRealizationOiContributionOutput> {
  if (
    !Number.isFinite(input.agpContributionUsd) ||
    !Number.isFinite(input.executionCostUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Pricing realization OI contribution requires AGP contribution and execution cost.',
    };
  }
  if (input.executionCostUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Pricing realization execution cost must be non-negative.',
    };
  }
  const operatingIncomeContributionUsd = roundTo(
    input.agpContributionUsd - input.executionCostUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      operatingIncomeContributionUsd,
      statusBand:
        operatingIncomeContributionUsd > 0
          ? 'sufficient'
          : operatingIncomeContributionUsd === 0
            ? 'constrained'
            : 'critical',
    }),
  };
}

const PRICING_QUOTE_INVOICE_LEAKAGE_REQUIRED_INPUTS = Object.freeze([
  'customerCohort',
  'segment',
  'serviceLaneScope',
  'pricingActionId',
  'quotedRevenueUsd',
  'invoicedRevenueUsd',
  'eligibleRevenueUsd',
] as const);

function executePricingQuoteInvoiceLeakage(
  input: PricingQuoteInvoiceLeakageInput,
): ApprovedFormulaResult<PricingQuoteInvoiceLeakageOutput> {
  const missingInputs = missingPricingQuoteInvoiceLeakageInputs(input);
  if (missingInputs.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `missing_formula_input: ${missingInputs.join(', ')}`,
    };
  }
  const quotedRevenueUsd = input.quotedRevenueUsd!;
  const invoicedRevenueUsd = input.invoicedRevenueUsd!;
  const eligibleRevenueUsd = input.eligibleRevenueUsd!;
  if (quotedRevenueUsd < 0 || invoicedRevenueUsd < 0 || eligibleRevenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Pricing quote-to-invoice leakage requires non-negative quote/invoice revenue and positive eligible revenue.',
    };
  }

  const quoteInvoiceLeakageUsd = roundTo(
    Math.max(0, quotedRevenueUsd - invoicedRevenueUsd),
    2,
  );
  const leakageRatePct = roundTo((quoteInvoiceLeakageUsd / eligibleRevenueUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      quoteInvoiceLeakageUsd,
      eligibleRevenueUsd: roundTo(eligibleRevenueUsd, 2),
      leakageRatePct,
      statusBand:
        leakageRatePct === 0
          ? 'sufficient'
          : leakageRatePct <= 5
            ? 'constrained'
            : 'critical',
    }),
  };
}

function executeCostToServeActivityAllocation(
  input: CostToServeActivityAllocationInput,
): ApprovedFormulaResult<CostToServeActivityAllocationOutput> {
  const costs = Array.isArray(input?.accountAllocatedCostsUsd)
    ? input.accountAllocatedCostsUsd
    : [];
  if (
    costs.length === 0 ||
    costs.some((cost) => !isFiniteNumber(cost) || cost < 0) ||
    !isFiniteNumber(input?.approvedCostPoolUsd) ||
    input.approvedCostPoolUsd <= 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve activity allocation requires non-negative account costs and a positive approved cost pool.',
    };
  }

  const activityAllocatedCostUsd = roundTo(
    costs.reduce((sum, cost) => sum + cost, 0),
    2,
  );
  const allocatedCostPoolSharePct = roundTo(
    (activityAllocatedCostUsd / input.approvedCostPoolUsd) * 100,
    4,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      fullyLoadedCostToServeUsd: activityAllocatedCostUsd,
      activityAllocatedCostUsd,
      approvedCostPoolUsd: roundTo(input.approvedCostPoolUsd, 2),
      allocatedCostPoolSharePct,
      statusBand:
        allocatedCostPoolSharePct <= 100 ? 'sufficient' : 'constrained',
    }),
  };
}

function executeCostToServeMargin(
  input: CostToServeMarginInput,
): ApprovedFormulaResult<CostToServeMarginOutput> {
  const accounts = Array.isArray(input?.accounts) ? input.accounts : [];
  if (!costToServeAccountsAreValid(accounts)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve margin requires account revenue and fully loaded cost for at least one account.',
    };
  }

  let ctsMarginUsd = 0;
  let lossMakingAccountCount = 0;
  let negativeMarginPoolUsd = 0;
  for (const account of accounts) {
    const marginUsd = account.netRevenueUsd - account.fullyLoadedCostToServeUsd;
    ctsMarginUsd += marginUsd;
    if (marginUsd < 0) {
      lossMakingAccountCount += 1;
      negativeMarginPoolUsd += -marginUsd;
    }
  }

  return {
    status: 'ok',
    output: Object.freeze({
      ctsMarginUsd: roundTo(ctsMarginUsd, 2),
      lossMakingAccountCount,
      negativeMarginPoolUsd: roundTo(negativeMarginPoolUsd, 2),
      statusBand: positiveUsdStatusBand(ctsMarginUsd),
    }),
  };
}

function executeCostToServeServiceTierCost(
  input: CostToServeServiceTierCostInput,
): ApprovedFormulaResult<CostToServeServiceTierCostOutput> {
  const serviceTiers = Array.isArray(input?.serviceTiers) ? input.serviceTiers : [];
  if (
    serviceTiers.length === 0 ||
    serviceTiers.some(
      (tier) =>
        !tier ||
        typeof tier.serviceTierId !== 'string' ||
        !tier.serviceTierId.trim() ||
        !isFiniteNumber(tier.shipmentCount) ||
        tier.shipmentCount <= 0 ||
        !isFiniteNumber(tier.totalServiceTierCostUsd) ||
        tier.totalServiceTierCostUsd < 0,
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve service-tier cost requires positive shipment counts and non-negative service-tier costs.',
    };
  }

  const shipmentCount = serviceTiers.reduce((sum, tier) => sum + tier.shipmentCount, 0);
  const totalServiceTierCostUsd = serviceTiers.reduce(
    (sum, tier) => sum + tier.totalServiceTierCostUsd,
    0,
  );
  const serviceTierCostPerShipmentUsd = roundTo(totalServiceTierCostUsd / shipmentCount, 4);

  return {
    status: 'ok',
    output: Object.freeze({
      serviceTierCostPerShipmentUsd,
      shipmentCount: roundTo(shipmentCount, 2),
      statusBand: positiveUsdStatusBand(serviceTierCostPerShipmentUsd),
    }),
  };
}

function executeCostToServeConcentrationHhi(
  input: CostToServeConcentrationHhiInput,
): ApprovedFormulaResult<CostToServeConcentrationHhiOutput> {
  const accounts = Array.isArray(input?.accounts) ? input.accounts : [];
  if (!costToServeAccountsAreValid(accounts)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve concentration HHI requires account revenue and fully loaded cost for at least one account.',
    };
  }

  const positiveProfits = accounts.map((account) =>
    Math.max(0, account.netRevenueUsd - account.fullyLoadedCostToServeUsd),
  );
  const positiveProfitPoolUsd = positiveProfits.reduce((sum, value) => sum + value, 0);
  if (positiveProfitPoolUsd <= 0) {
    return {
      status: 'ok',
      output: Object.freeze({
        accountProfitHhi: 0,
        positiveProfitPoolUsd: 0,
        statusBand: 'critical',
      }),
    };
  }

  const accountProfitHhi = roundTo(
    positiveProfits.reduce((sum, profit) => {
      const share = profit / positiveProfitPoolUsd;
      return sum + share * share;
    }, 0),
    6,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      accountProfitHhi,
      positiveProfitPoolUsd: roundTo(positiveProfitPoolUsd, 2),
      statusBand:
        accountProfitHhi <= 0.25
          ? 'sufficient'
          : accountProfitHhi <= 0.4
            ? 'constrained'
            : 'critical',
    }),
  };
}

function executeCostToServeTopNRevenueShare(
  input: CostToServeTopNRevenueShareInput,
): ApprovedFormulaResult<CostToServeTopNRevenueShareOutput> {
  const accounts = Array.isArray(input?.accounts) ? input.accounts : [];
  const topN = input?.topN;
  if (!Number.isInteger(topN) || topN <= 0 || accounts.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve top-N revenue share requires at least one account and a positive integer topN.',
    };
  }
  if (
    accounts.some(
      (account) =>
        !account ||
        typeof account.accountId !== 'string' ||
        !account.accountId.trim() ||
        !isFiniteNumber(account.netRevenueUsd) ||
        account.netRevenueUsd < 0,
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve top-N revenue share requires non-negative revenue and account ids for every account.',
    };
  }
  const accountIds = accounts.map((account) => account.accountId.trim());
  if (new Set(accountIds).size !== accountIds.length) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Cost-to-serve top-N revenue share requires unique account ids.',
    };
  }

  const totalRevenueUsd = accounts.reduce((sum, account) => sum + account.netRevenueUsd, 0);
  if (totalRevenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Cost-to-serve top-N revenue share cannot divide by zero total revenue.',
    };
  }

  const topAccounts = [...accounts]
    .sort((left, right) => {
      const revenueDelta = right.netRevenueUsd - left.netRevenueUsd;
      if (revenueDelta !== 0) return revenueDelta;
      return left.accountId.localeCompare(right.accountId);
    })
    .slice(0, topN);
  const topNRevenueUsd = topAccounts.reduce((sum, account) => sum + account.netRevenueUsd, 0);
  const topNRevenueSharePct = roundTo((topNRevenueUsd / totalRevenueUsd) * 100, 4);

  return {
    status: 'ok',
    output: Object.freeze({
      topNRevenueSharePct,
      topNRevenueUsd: roundTo(topNRevenueUsd, 2),
      totalRevenueUsd: roundTo(totalRevenueUsd, 2),
      topNAccountIds: Object.freeze(topAccounts.map((account) => account.accountId)),
      accountCount: accounts.length,
      topN,
      statusBand:
        topNRevenueSharePct <= 40
          ? 'sufficient'
          : topNRevenueSharePct <= 65
            ? 'constrained'
            : 'critical',
    }),
  };
}

function executeCostToServeBenchmark(
  input: CostToServeBenchmarkInput,
): ApprovedFormulaResult<CostToServeBenchmarkOutput> {
  if (
    !isFiniteNumber(input?.accountNetRevenueUsd) ||
    input.accountNetRevenueUsd <= 0 ||
    !isFiniteNumber(input?.fullyLoadedCostToServeUsd) ||
    input.fullyLoadedCostToServeUsd < 0 ||
    !isFiniteNumber(input?.benchmarkCostToServeRatioPct) ||
    !isFiniteNumber(input?.watchCostToServeRatioPct) ||
    !isFiniteNumber(input?.decisionCostToServeRatioPct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve benchmark requires revenue, cost, benchmark, watch, and decision threshold inputs.',
    };
  }
  if (input.watchCostToServeRatioPct > input.decisionCostToServeRatioPct) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Cost-to-serve benchmark watch threshold must not exceed decision threshold.',
    };
  }

  const costToServeRatioPct = roundTo(
    (input.fullyLoadedCostToServeUsd / input.accountNetRevenueUsd) * 100,
    4,
  );
  const ctsBenchmarkGapPct = roundTo(
    costToServeRatioPct - input.benchmarkCostToServeRatioPct,
    4,
  );
  const warningLevel =
    costToServeRatioPct >= input.decisionCostToServeRatioPct
      ? 'decision_required'
      : costToServeRatioPct >= input.watchCostToServeRatioPct
        ? 'watch'
        : 'none';

  return {
    status: 'ok',
    output: Object.freeze({
      costToServeRatioPct,
      ctsBenchmarkGapPct,
      warningLevel,
      warningLevelCode:
        warningLevel === 'none' ? 0 : warningLevel === 'watch' ? 1 : 2,
      statusBand:
        warningLevel === 'none'
          ? 'sufficient'
          : warningLevel === 'watch'
            ? 'constrained'
            : 'critical',
    }),
  };
}

function executeCostToServeRecoveryOpportunity(
  input: CostToServeRecoveryOpportunityInput,
): ApprovedFormulaResult<CostToServeRecoveryOpportunityOutput> {
  if (
    !isFiniteNumber(input?.negativeMarginPoolUsd) ||
    input.negativeMarginPoolUsd < 0 ||
    !isFiniteNumber(input?.recoveryFactorPct) ||
    input.recoveryFactorPct < 0 ||
    input.recoveryFactorPct > 100 ||
    !isFiniteNumber(input?.approvedFinanceRecoveryUsd) ||
    input.approvedFinanceRecoveryUsd < 0 ||
    !isFiniteNumber(input?.financeOperatingIncomeUsd) ||
    input.financeOperatingIncomeUsd <= 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve recovery requires negative margin pool, recovery factor, finance recovery, and positive operating income inputs.',
    };
  }

  const modeledRecoveryUsd = roundTo(
    input.negativeMarginPoolUsd * (input.recoveryFactorPct / 100),
    2,
  );
  const ctsFinanceReconciliationResidualUsd = roundTo(
    modeledRecoveryUsd - input.approvedFinanceRecoveryUsd,
    2,
  );
  const recoveryShareOfOperatingIncomePct = roundTo(
    (modeledRecoveryUsd / input.financeOperatingIncomeUsd) * 100,
    4,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      ctsRecoveryOpportunityUsd: modeledRecoveryUsd,
      modeledRecoveryUsd,
      approvedFinanceRecoveryUsd: roundTo(input.approvedFinanceRecoveryUsd, 2),
      ctsFinanceReconciliationResidualUsd,
      recoveryShareOfOperatingIncomePct,
      statusBand:
        Math.abs(ctsFinanceReconciliationResidualUsd) <= NUMBER_TOLERANCE
          ? 'sufficient'
          : 'constrained',
    }),
  };
}

function executeCostToServeRepriceDelta(
  input: CostToServeRepriceDeltaInput,
): ApprovedFormulaResult<CostToServeRepriceDeltaOutput> {
  const accounts = Array.isArray(input?.accounts) ? input.accounts : [];
  if (
    !costToServeAccountsAreValid(accounts) ||
    accounts.some(
      (account) =>
        !isFiniteNumber(account.customerPriceElasticityFactor) ||
        account.customerPriceElasticityFactor < 0 ||
        account.customerPriceElasticityFactor > 1,
    ) ||
    !isFiniteNumber(input?.priceLiftPct) ||
    input.priceLiftPct < 0 ||
    input.priceLiftPct > 100
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve repricing requires account margins, elasticity factors, and a price lift between 0 and 100 percent.',
    };
  }

  let ctsRepriceDeltaUsd = 0;
  let affectedAccountCount = 0;
  for (const account of accounts) {
    const marginUsd = account.netRevenueUsd - account.fullyLoadedCostToServeUsd;
    if (marginUsd >= 0) continue;
    affectedAccountCount += 1;
    ctsRepriceDeltaUsd +=
      account.netRevenueUsd *
      (input.priceLiftPct / 100) *
      account.customerPriceElasticityFactor;
  }

  return {
    status: 'ok',
    output: Object.freeze({
      ctsRepriceDeltaUsd: roundTo(ctsRepriceDeltaUsd, 2),
      affectedAccountCount,
      statusBand: positiveUsdStatusBand(ctsRepriceDeltaUsd),
    }),
  };
}

function executeCostToServeServiceRedesign(
  input: CostToServeServiceRedesignInput,
): ApprovedFormulaResult<CostToServeServiceRedesignOutput> {
  if (
    !isFiniteNumber(input?.currentServiceTierCostPerShipmentUsd) ||
    input.currentServiceTierCostPerShipmentUsd < 0 ||
    !isFiniteNumber(input?.targetServiceTierCostPerShipmentUsd) ||
    input.targetServiceTierCostPerShipmentUsd < 0 ||
    !isFiniteNumber(input?.migratableAccountCount) ||
    input.migratableAccountCount < 0 ||
    !isFiniteNumber(input?.attritionRiskUsd) ||
    input.attritionRiskUsd < 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Cost-to-serve service redesign requires current and target service costs, migratable account count, and attrition risk.',
    };
  }

  const serviceCostDeltaPerShipmentUsd = roundTo(
    Math.max(
      0,
      input.currentServiceTierCostPerShipmentUsd -
        input.targetServiceTierCostPerShipmentUsd,
    ),
    4,
  );
  const ctsServiceRedesignDeltaUsd = roundTo(
    serviceCostDeltaPerShipmentUsd * input.migratableAccountCount -
      input.attritionRiskUsd,
    2,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      ctsServiceRedesignDeltaUsd,
      serviceCostDeltaPerShipmentUsd,
      statusBand: positiveUsdStatusBand(ctsServiceRedesignDeltaUsd),
    }),
  };
}

function costToServeAccountsAreValid(
  accounts: readonly CostToServeAccountFormulaInput[],
): boolean {
  return (
    accounts.length > 0 &&
    accounts.every(
      (account) =>
        !!account &&
        typeof account.accountId === 'string' &&
        account.accountId.trim().length > 0 &&
        isFiniteNumber(account.netRevenueUsd) &&
        account.netRevenueUsd >= 0 &&
        isFiniteNumber(account.fullyLoadedCostToServeUsd) &&
        account.fullyLoadedCostToServeUsd >= 0,
    )
  );
}

function executePricingDiscountLeakage(
  input: PricingDiscountLeakageInput,
): ApprovedFormulaResult<PricingDiscountLeakageOutput> {
  if (!Array.isArray(input.authorityBands) || input.authorityBands.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'missing_formula_input: authorityBands',
    };
  }
  if (!Array.isArray(input.transactions) || input.transactions.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'missing_formula_input: transactions',
    };
  }

  const bands = new Map<string, PricingDiscountAuthorityBandInput>();
  for (const band of input.authorityBands) {
    if (
      typeof band.bandId !== 'string' ||
      !band.bandId.trim() ||
      !Number.isFinite(band.maxDiscountPctOfList) ||
      band.maxDiscountPctOfList < 0 ||
      band.maxDiscountPctOfList > 1
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Pricing discount leakage requires authority bands with ids and max discount percentages between 0 and 1.',
      };
    }
    bands.set(band.bandId, band);
  }

  let eligibleListRevenueUsd = 0;
  let totalNetRevenueUsd = 0;
  let totalDiscountUsd = 0;
  let withinPolicyDiscountUsd = 0;
  let offPolicyDiscountUsd = 0;

  for (const row of input.transactions) {
    const band = bands.get(row.authorityBandId);
    if (!band) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `Pricing discount leakage row ${row.rowId} references an unknown authority band.`,
      };
    }
    if (
      typeof row.rowId !== 'string' ||
      !row.rowId.trim() ||
      typeof row.segment !== 'string' ||
      !row.segment.trim() ||
      typeof row.rep !== 'string' ||
      !row.rep.trim() ||
      !Number.isFinite(row.listRevenueUsd) ||
      !Number.isFinite(row.netRevenueUsd) ||
      row.listRevenueUsd <= 0 ||
      row.netRevenueUsd < 0 ||
      row.netRevenueUsd > row.listRevenueUsd
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Pricing discount leakage requires row id, segment, rep, positive list revenue, and non-negative net revenue not above list revenue.',
      };
    }

    const actualDiscountUsd = roundTo(
      Number.isFinite(row.discountUsd ?? Number.NaN)
        ? Number(row.discountUsd)
        : row.listRevenueUsd - row.netRevenueUsd,
      2,
    );
    if (actualDiscountUsd < 0) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `Pricing discount leakage row ${row.rowId} has negative actual discount.`,
      };
    }
    const identityDiscountUsd = roundTo(row.listRevenueUsd - row.netRevenueUsd, 2);
    if (!sameNumber(actualDiscountUsd, identityDiscountUsd)) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: `Pricing discount leakage row ${row.rowId} does not reconcile list, net, and discount.`,
      };
    }

    const authorizedDiscountUsd = roundTo(
      row.listRevenueUsd * band.maxDiscountPctOfList,
      2,
    );
    const rowOffPolicyUsd = roundTo(
      Math.max(0, actualDiscountUsd - authorizedDiscountUsd),
      2,
    );
    if (
      Number.isFinite(row.offPolicyLeakageUsd ?? Number.NaN) &&
      !sameNumber(rowOffPolicyUsd, Number(row.offPolicyLeakageUsd))
    ) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: `Pricing discount leakage row ${row.rowId} does not reconcile off-policy leakage.`,
      };
    }

    eligibleListRevenueUsd = roundTo(eligibleListRevenueUsd + row.listRevenueUsd, 2);
    totalNetRevenueUsd = roundTo(totalNetRevenueUsd + row.netRevenueUsd, 2);
    totalDiscountUsd = roundTo(totalDiscountUsd + actualDiscountUsd, 2);
    offPolicyDiscountUsd = roundTo(offPolicyDiscountUsd + rowOffPolicyUsd, 2);
    withinPolicyDiscountUsd = roundTo(
      withinPolicyDiscountUsd + (actualDiscountUsd - rowOffPolicyUsd),
      2,
    );
  }

  if (
    !sameNumber(roundTo(eligibleListRevenueUsd - totalDiscountUsd, 2), totalNetRevenueUsd) ||
    !sameNumber(
      roundTo(withinPolicyDiscountUsd + offPolicyDiscountUsd, 2),
      totalDiscountUsd,
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing discount leakage aggregate identities do not reconcile list, net, total discount, within-policy, and off-policy amounts.',
    };
  }

  const discountLeakageRatePct = roundTo(
    (offPolicyDiscountUsd / eligibleListRevenueUsd) * 100,
    4,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      eligibleListRevenueUsd,
      totalNetRevenueUsd,
      totalDiscountUsd,
      withinPolicyDiscountUsd,
      offPolicyDiscountUsd,
      discountLeakageUsd: offPolicyDiscountUsd,
      discountLeakageRatePct,
      statusBand:
        discountLeakageRatePct === 0
          ? 'sufficient'
          : discountLeakageRatePct <= 1
            ? 'constrained'
            : 'critical',
    }),
  };
}

const REALIZATION_RATE_REQUIRED_INPUTS = Object.freeze([
  'segment',
  'quotedRevenueUsd',
  'invoicedRevenueUsd',
  'eligibleRevenueUsd',
] as const);

function executeRealizationRate(
  input: RealizationRateInput,
): ApprovedFormulaResult<RealizationRateOutput> {
  const missingInputs = missingRealizationRateInputs(input);
  if (missingInputs.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `missing_formula_input: ${missingInputs.join(', ')}`,
    };
  }
  const quotedRevenueUsd = input.quotedRevenueUsd!;
  const invoicedRevenueUsd = input.invoicedRevenueUsd!;
  const eligibleRevenueUsd = input.eligibleRevenueUsd!;
  if (quotedRevenueUsd <= 0 || invoicedRevenueUsd < 0 || eligibleRevenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Realization rate requires positive quoted and eligible revenue, and non-negative invoiced revenue.',
    };
  }

  const realizationGapUsd = roundTo(Math.max(0, quotedRevenueUsd - invoicedRevenueUsd), 2);
  const realizationRatePct = roundTo((invoicedRevenueUsd / quotedRevenueUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      quotedRevenueUsd: roundTo(quotedRevenueUsd, 2),
      invoicedRevenueUsd: roundTo(invoicedRevenueUsd, 2),
      eligibleRevenueUsd: roundTo(eligibleRevenueUsd, 2),
      realizationGapUsd,
      realizationRatePct,
      statusBand:
        realizationRatePct >= 98
          ? 'sufficient'
          : realizationRatePct >= 95
            ? 'constrained'
            : 'critical',
    }),
  };
}

function executePriceVolumeMixBridge(
  input: PriceVolumeMixBridgeInput,
): ApprovedFormulaResult<PriceVolumeMixBridgeOutput> {
  const required = [
    'baselineRevenueUsd',
    'plannedSellRateIncreasePct',
    'expectedVolumeResponsePct',
    'expectedRevenueGrowthPct',
    'expectedNetRevenueRetentionPct',
    'agpFlowThroughPct',
    'executionCostUsd',
  ] as const;
  const missing = required.filter((field) => !Number.isFinite(input[field]));
  if (missing.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `Price/volume/mix bridge requires ${missing.join(', ')}.`,
    };
  }
  if (input.baselineRevenueUsd < 0 || input.executionCostUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Price/volume/mix bridge requires non-negative baseline revenue and execution cost.',
    };
  }
  if (
    input.expectedNetRevenueRetentionPct < 0 ||
    input.expectedNetRevenueRetentionPct > 1 ||
    input.agpFlowThroughPct < 0 ||
    input.agpFlowThroughPct > 1
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Price/volume/mix bridge requires retention and AGP flow-through between 0 and 1.',
    };
  }

  const retainedBaselineRevenue =
    input.baselineRevenueUsd * input.expectedNetRevenueRetentionPct;
  const priceImpactUsd = roundTo(
    retainedBaselineRevenue * input.plannedSellRateIncreasePct,
    2,
  );
  const volumeImpactUsd = roundTo(
    retainedBaselineRevenue * input.expectedVolumeResponsePct,
    2,
  );
  const pvmNetRevenueDeltaUsd = roundTo(
    retainedBaselineRevenue * input.expectedRevenueGrowthPct,
    2,
  );
  const mixImpactUsd = roundTo(
    pvmNetRevenueDeltaUsd - priceImpactUsd - volumeImpactUsd,
    2,
  );
  const pvmAgpContributionUsd = roundTo(
    pvmNetRevenueDeltaUsd * input.agpFlowThroughPct,
    2,
  );
  const pvmOperatingIncomeContributionUsd = roundTo(
    pvmAgpContributionUsd - input.executionCostUsd,
    2,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      priceImpactUsd,
      volumeImpactUsd,
      mixImpactUsd,
      pvmNetRevenueDeltaUsd,
      pvmAgpContributionUsd,
      pvmOperatingIncomeContributionUsd,
      statusBand:
        pvmOperatingIncomeContributionUsd > 0
          ? 'sufficient'
          : pvmOperatingIncomeContributionUsd === 0
            ? 'constrained'
            : 'critical',
    }),
  };
}

function executePricingStructureMixShiftMarginContribution(
  input: PricingStructureMixShiftMarginContributionInput,
): ApprovedFormulaResult<PricingStructureMixShiftMarginContributionOutput> {
  const required = [
    'totalNetRevenueUsd',
    'baselineSpotMixPct',
    'currentSpotMixPct',
    'baselineContractMixPct',
    'currentContractMixPct',
    'spotNetRevenueMarginPct',
    'contractNetRevenueMarginPct',
  ] as const;
  const missing = required.filter((field) => !Number.isFinite(input[field]));
  if (!input.period || !input.segment || missing.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        `Pricing structure mix-shift contribution requires period, segment, ` +
        `${required.join(', ')}.`,
    };
  }
  if (
    input.totalNetRevenueUsd <= 0 ||
    input.baselineSpotMixPct < 0 ||
    input.currentSpotMixPct < 0 ||
    input.baselineContractMixPct < 0 ||
    input.currentContractMixPct < 0 ||
    input.spotNetRevenueMarginPct < 0 ||
    input.contractNetRevenueMarginPct < 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Pricing structure mix-shift contribution requires positive net revenue and non-negative mix and margin inputs.',
    };
  }
  if (
    !sameNumber(roundTo(input.baselineSpotMixPct + input.baselineContractMixPct, 4), 100) ||
    !sameNumber(roundTo(input.currentSpotMixPct + input.currentContractMixPct, 4), 100)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing structure mix-shift contribution requires baseline and current spot/contract mix to reconcile to 100%.',
    };
  }

  const spotToContractMixShiftPct = roundTo(
    input.currentContractMixPct - input.baselineContractMixPct,
    4,
  );
  const marginSpreadPct = roundTo(
    input.contractNetRevenueMarginPct - input.spotNetRevenueMarginPct,
    4,
  );
  const mixShiftContributionUsd = roundTo(
    input.totalNetRevenueUsd *
      (spotToContractMixShiftPct / 100) *
      (marginSpreadPct / 100),
    2,
  );
  const netRevenueMarginContributionBps = roundTo(
    (mixShiftContributionUsd / input.totalNetRevenueUsd) * 10000,
    4,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      spotToContractMixShiftPct,
      marginSpreadPct,
      mixShiftContributionUsd,
      netRevenueMarginContributionBps,
      statusBand:
        mixShiftContributionUsd > 0
          ? 'sufficient'
          : mixShiftContributionUsd === 0
            ? 'constrained'
            : 'critical',
    }),
  };
}

const VARIANCE_DECOMPOSITION_TOLERANCE = NUMBER_TOLERANCE;
const VARIANCE_DECOMPOSITION_DECIMALS = 6;

function normalizeVarianceResidual(value: number, tolerance: number): number {
  const rounded = roundTo(value, VARIANCE_DECOMPOSITION_DECIMALS);
  return Math.abs(rounded) <= tolerance ? 0 : rounded;
}

function roundVarianceValue(value: number): number {
  return roundTo(value, VARIANCE_DECOMPOSITION_DECIMALS);
}

function varianceCellMember(cell: VarianceDecompositionCell, dimension: string): string {
  return cell.dimensions[dimension] ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY;
}

function matchingVarianceCells(
  cells: readonly VarianceDecompositionCell[],
  metricKey: string,
  periodType: string,
  periodKey: string,
  dimension: string,
  memberKey: string
): readonly VarianceDecompositionCell[] {
  return cells.filter(
    (cell) =>
      cell.metricKey === metricKey &&
      cell.periodType === periodType &&
      cell.periodKey === periodKey &&
      varianceCellMember(cell, dimension) === memberKey
  );
}

function selectVarianceCell(
  cells: readonly VarianceDecompositionCell[],
  metricKey: string,
  periodType: string,
  periodKey: string,
  dimension: string,
  memberKey: string
): VarianceDecompositionCell | ApprovedFormulaBlockedResult {
  const matches = matchingVarianceCells(
    cells,
    metricKey,
    periodType,
    periodKey,
    dimension,
    memberKey
  );
  if (matches.length === 1) return matches[0];
  if (matches.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_period_cells',
      message: `Variance decomposition is missing ${metricKey} for ${memberKey} in ${periodKey}.`,
    };
  }
  return {
    status: 'blocked',
    reasonCode: 'invalid_formula_input',
    message: `Variance decomposition found duplicate ${metricKey} cells for ${memberKey} in ${periodKey}.`,
  };
}

function parseVarianceCellValue(
  cell: VarianceDecompositionCell
): number | ApprovedFormulaBlockedResult {
  const value =
    typeof cell.valueNumeric === 'number' ? cell.valueNumeric : Number(cell.valueNumeric);
  if (!Number.isFinite(value)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `Variance decomposition cell ${cell.metricKey}:${cell.periodKey} has a non-finite value.`,
    };
  }
  return value;
}

function uniqueVarianceStrings(values: readonly string[]): readonly string[] {
  return Object.freeze([...new Set(values.filter((value) => value.trim().length > 0))].sort());
}

function aggregateVarianceProvenance(
  cells: readonly VarianceDecompositionCell[]
): VarianceDecompositionProvenance {
  const sourceClasses = uniqueVarianceStrings(
    cells.flatMap((cell) => [...cell.provenance.sourceClasses])
  );
  return Object.freeze({
    sourceClass: sourceClasses.length === 1 ? sourceClasses[0] : null,
    sourceClasses,
    sourceSystemRefs: uniqueVarianceStrings(
      cells.flatMap((cell) => [...cell.provenance.sourceSystemRefs])
    ),
    evidenceRefIds: uniqueVarianceStrings(
      cells.flatMap((cell) => [...cell.provenance.evidenceRefIds])
    ),
    factIds: uniqueVarianceStrings(cells.flatMap((cell) => [...cell.provenance.factIds])),
    artifactIds: uniqueVarianceStrings(cells.flatMap((cell) => [...cell.provenance.artifactIds])),
    ingestJobIds: uniqueVarianceStrings(cells.flatMap((cell) => [...cell.provenance.ingestJobIds])),
    asOf: uniqueVarianceStrings(cells.flatMap((cell) => [...cell.provenance.asOf])),
    approvedAt: uniqueVarianceStrings(cells.flatMap((cell) => [...cell.provenance.approvedAt])),
  });
}

function varianceMissingEvidenceBlock(): ApprovedFormulaBlockedResult {
  return {
    status: 'blocked',
    reasonCode: 'missing_governed_evidence',
    message: 'Variance decomposition requires fact-level provenance and sourceClass on every cell.',
  };
}

function assertVarianceProvenancePresent(
  provenance: VarianceDecompositionProvenance
): ApprovedFormulaBlockedResult | null {
  if (provenance.factIds.length === 0 || provenance.sourceClasses.length === 0) {
    return varianceMissingEvidenceBlock();
  }
  return null;
}

function varianceStatusBand(totalChange: number): ApprovedFormulaStatusBand {
  return positiveUsdStatusBand(totalChange);
}

function executeVarianceDecomposition(
  input: VarianceDecompositionInput
): ApprovedFormulaResult<VarianceDecompositionOutput> {
  const requiredText = [
    'tenantId',
    'metricKey',
    'basisMetricKey',
    'dimension',
    'periodType',
    'baselinePeriodKey',
    'comparisonPeriodKey',
    'grainRollup',
  ] as const;
  const missingText = requiredText.filter((field) => {
    const value = input[field];
    return typeof value !== 'string' || value.trim().length === 0;
  });
  if (missingText.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `Variance decomposition requires ${missingText.join(', ')}.`,
    };
  }

  const metricSpecBlock = validateVarianceSpecCompilation(
    input,
    buildVarianceDecompositionMetricQuerySpec(input)
  );
  if (metricSpecBlock) return metricSpecBlock;

  const totalCells = input.totalMetricCells ?? [];
  if (totalCells.length > 0) {
    const totalSpecBlock = validateVarianceSpecCompilation(
      input,
      buildVarianceDecompositionTotalQuerySpec(input)
    );
    if (totalSpecBlock) return totalSpecBlock;
  }

  if (input.memberKeys.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Variance decomposition requires at least one dimension member after metric fences pass.',
    };
  }

  const tolerance = input.tolerance ?? VARIANCE_DECOMPOSITION_TOLERANCE;
  const memberContributions: VarianceDecompositionMemberContribution[] = [];
  const provenanceCells: VarianceDecompositionCell[] = [];

  for (const memberKey of input.memberKeys) {
    const metricBaselineCell = selectVarianceCell(
      input.cells,
      input.metricKey,
      input.periodType,
      input.baselinePeriodKey,
      input.dimension,
      memberKey
    );
    if ('status' in metricBaselineCell) return metricBaselineCell;
    const metricComparisonCell = selectVarianceCell(
      input.cells,
      input.metricKey,
      input.periodType,
      input.comparisonPeriodKey,
      input.dimension,
      memberKey
    );
    if ('status' in metricComparisonCell) return metricComparisonCell;
    const basisBaselineCell = selectVarianceCell(
      input.cells,
      input.basisMetricKey,
      input.periodType,
      input.baselinePeriodKey,
      input.dimension,
      memberKey
    );
    if ('status' in basisBaselineCell) return basisBaselineCell;
    const basisComparisonCell = selectVarianceCell(
      input.cells,
      input.basisMetricKey,
      input.periodType,
      input.comparisonPeriodKey,
      input.dimension,
      memberKey
    );
    if ('status' in basisComparisonCell) return basisComparisonCell;

    const metricBaselineValue = parseVarianceCellValue(metricBaselineCell);
    if (typeof metricBaselineValue !== 'number') return metricBaselineValue;
    const metricComparisonValue = parseVarianceCellValue(metricComparisonCell);
    if (typeof metricComparisonValue !== 'number') return metricComparisonValue;
    const basisBaselineValue = parseVarianceCellValue(basisBaselineCell);
    if (typeof basisBaselineValue !== 'number') return basisBaselineValue;
    const basisComparisonValue = parseVarianceCellValue(basisComparisonCell);
    if (typeof basisComparisonValue !== 'number') return basisComparisonValue;

    if (basisBaselineValue <= 0 || basisComparisonValue <= 0) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `Variance decomposition requires positive basis cells for ${memberKey}.`,
      };
    }

    const baselineRate = metricBaselineValue / basisBaselineValue;
    const comparisonRate = metricComparisonValue / basisComparisonValue;
    const withinLineContributionDelta = roundVarianceValue(
      ((basisBaselineValue + basisComparisonValue) / 2) * (comparisonRate - baselineRate)
    );
    const mixContributionDelta = roundVarianceValue(
      ((baselineRate + comparisonRate) / 2) * (basisComparisonValue - basisBaselineValue)
    );
    const totalContributionDelta = roundVarianceValue(metricComparisonValue - metricBaselineValue);
    const memberResidual = normalizeVarianceResidual(
      totalContributionDelta - withinLineContributionDelta - mixContributionDelta,
      tolerance
    );
    if (memberResidual !== 0) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: `Variance decomposition member ${memberKey} does not reconcile; residual=${memberResidual}.`,
      };
    }

    const memberCells = [
      metricBaselineCell,
      metricComparisonCell,
      basisBaselineCell,
      basisComparisonCell,
    ];
    const memberProvenance = aggregateVarianceProvenance(memberCells);
    const memberProvenanceBlock = assertVarianceProvenancePresent(memberProvenance);
    if (memberProvenanceBlock) return memberProvenanceBlock;

    provenanceCells.push(...memberCells);
    memberContributions.push(
      Object.freeze({
        memberKey,
        baselineValue: roundVarianceValue(metricBaselineValue),
        comparisonValue: roundVarianceValue(metricComparisonValue),
        basisBaselineValue: roundVarianceValue(basisBaselineValue),
        basisComparisonValue: roundVarianceValue(basisComparisonValue),
        baselineRate: roundVarianceValue(baselineRate),
        comparisonRate: roundVarianceValue(comparisonRate),
        withinLineContributionDelta,
        mixContributionDelta,
        totalContributionDelta,
        provenance: memberProvenance,
      })
    );
  }

  let totalBaselineValue = roundVarianceValue(
    memberContributions.reduce((sum, member) => sum + member.baselineValue, 0)
  );
  let totalComparisonValue = roundVarianceValue(
    memberContributions.reduce((sum, member) => sum + member.comparisonValue, 0)
  );

  if (totalCells.length > 0) {
    const totalBaselineCell = selectVarianceCell(
      totalCells,
      input.metricKey,
      input.periodType,
      input.baselinePeriodKey,
      input.dimension,
      GOVERNED_METRIC_FACT_ALL_MEMBER_KEY
    );
    if ('status' in totalBaselineCell) return totalBaselineCell;
    const totalComparisonCell = selectVarianceCell(
      totalCells,
      input.metricKey,
      input.periodType,
      input.comparisonPeriodKey,
      input.dimension,
      GOVERNED_METRIC_FACT_ALL_MEMBER_KEY
    );
    if ('status' in totalComparisonCell) return totalComparisonCell;

    const totalBaselineParsed = parseVarianceCellValue(totalBaselineCell);
    if (typeof totalBaselineParsed !== 'number') return totalBaselineParsed;
    const totalComparisonParsed = parseVarianceCellValue(totalComparisonCell);
    if (typeof totalComparisonParsed !== 'number') return totalComparisonParsed;
    totalBaselineValue = roundVarianceValue(totalBaselineParsed);
    totalComparisonValue = roundVarianceValue(totalComparisonParsed);
    provenanceCells.push(totalBaselineCell, totalComparisonCell);
  }

  const totalChange = roundVarianceValue(totalComparisonValue - totalBaselineValue);
  const withinLineEffect = roundVarianceValue(
    memberContributions.reduce((sum, member) => sum + member.withinLineContributionDelta, 0)
  );
  const mixEffect = roundVarianceValue(
    memberContributions.reduce((sum, member) => sum + member.mixContributionDelta, 0)
  );
  const partsTotal = roundVarianceValue(withinLineEffect + mixEffect);
  const residual = normalizeVarianceResidual(totalChange - partsTotal, tolerance);

  if (residual !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `Variance decomposition does not reconcile; residual=${residual}.`,
    };
  }

  const provenance = aggregateVarianceProvenance(provenanceCells);
  const provenanceBlock = assertVarianceProvenancePresent(provenance);
  if (provenanceBlock) return provenanceBlock;

  return {
    status: 'ok',
    output: Object.freeze({
      metricKey: input.metricKey,
      basisMetricKey: input.basisMetricKey,
      dimension: input.dimension,
      periodType: input.periodType,
      baselinePeriodKey: input.baselinePeriodKey,
      comparisonPeriodKey: input.comparisonPeriodKey,
      totalBaselineValue,
      totalComparisonValue,
      totalChange,
      basisBaselineTotal: roundVarianceValue(
        memberContributions.reduce((sum, member) => sum + member.basisBaselineValue, 0)
      ),
      basisComparisonTotal: roundVarianceValue(
        memberContributions.reduce((sum, member) => sum + member.basisComparisonValue, 0)
      ),
      withinLineEffect,
      mixEffect,
      memberContributions: Object.freeze(memberContributions),
      reconciliation: Object.freeze({
        totalChange,
        withinLineEffect,
        mixEffect,
        partsTotal,
        residual,
        tolerance,
        reconciles: residual === 0,
      }),
      provenance,
      sourceClass: provenance.sourceClass,
      sourceClasses: provenance.sourceClasses,
      statusBand: varianceStatusBand(totalChange),
      reportingCutAsOf: input.reportingCutAsOf,
    }),
  };
}

function validateVarianceDecompositionInputAgainstEvidence(
  input: VarianceDecompositionInput,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[]
): ApprovedFormulaEvidenceValidationBlock | null {
  if (!isVarianceInputLike(input)) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Variance decomposition runtime input is malformed.',
    };
  }
  const realizationArtifacts = artifacts.filter(
    (artifact) =>
      artifact.sourceSystemRef === REALIZATION_COMPONENT_VARIANCE_SOURCE_SYSTEM_REF ||
      artifact.schemaVersion === 'realization_component_variance_v1'
  );
  if (realizationArtifacts.length === 0) return null;
  if (realizationArtifacts.length !== 1) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Variance decomposition requires exactly one approved component variance artifact.',
    };
  }

  const artifact = realizationArtifacts[0];
  const content = asRecord(artifact.contentJson);
  const artifactInput = content?.varianceInput as VarianceDecompositionInput;
  if (!isVarianceInputLike(artifactInput)) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Variance decomposition requires an approved component variance artifact input.',
    };
  }

  if (!varianceInputsMatchApprovedArtifact(input, artifactInput)) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Variance decomposition input does not match the approved component variance artifact.',
    };
  }

  if (!varianceInputProvenanceMatchesArtifact(input, artifact)) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Variance decomposition input provenance does not match approved runtime evidence.',
    };
  }

  return null;
}

function isVarianceInputLike(value: unknown): value is VarianceDecompositionInput {
  const record = asRecord(value);
  return Boolean(
    record &&
      typeof record.tenantId === 'string' &&
      typeof record.metricKey === 'string' &&
      typeof record.basisMetricKey === 'string' &&
      typeof record.dimension === 'string' &&
      typeof record.periodType === 'string' &&
      typeof record.baselinePeriodKey === 'string' &&
      typeof record.comparisonPeriodKey === 'string' &&
      typeof record.grainRollup === 'string' &&
      Array.isArray(record.memberKeys) &&
      Array.isArray(record.cells)
  );
}

function varianceInputsMatchApprovedArtifact(
  input: VarianceDecompositionInput,
  artifactInput: VarianceDecompositionInput
): boolean {
  return (
    input.tenantId === artifactInput.tenantId &&
    input.metricKey === artifactInput.metricKey &&
    input.basisMetricKey === artifactInput.basisMetricKey &&
    input.dimension === artifactInput.dimension &&
    input.periodType === artifactInput.periodType &&
    input.baselinePeriodKey === artifactInput.baselinePeriodKey &&
    input.comparisonPeriodKey === artifactInput.comparisonPeriodKey &&
    input.grainRollup === artifactInput.grainRollup &&
    sameStringArray([...input.memberKeys], [...artifactInput.memberKeys]) &&
    sameVarianceReportingCut(input.reportingCutAsOf, artifactInput.reportingCutAsOf) &&
    sameVarianceCellSet(input.cells, artifactInput.cells, input.dimension) &&
    sameVarianceCellSet(
      input.totalMetricCells ?? [],
      artifactInput.totalMetricCells ?? [],
      input.dimension
    )
  );
}

function sameVarianceReportingCut(
  left: string | undefined,
  right: string | undefined
): boolean {
  if (!left || !right) return left === right;
  return sameIsoDate(left, right);
}

function sameVarianceCellSet(
  left: readonly VarianceDecompositionCell[],
  right: readonly VarianceDecompositionCell[],
  dimension: string
): boolean {
  const leftSignatures = left.map((cell) => varianceCellEvidenceSignature(cell, dimension)).sort();
  const rightSignatures = right
    .map((cell) => varianceCellEvidenceSignature(cell, dimension))
    .sort();
  return sameStringArray(leftSignatures, rightSignatures);
}

function varianceCellEvidenceSignature(
  cell: VarianceDecompositionCell,
  dimension: string
): string {
  return [
    cell.metricKey,
    cell.periodType,
    cell.periodKey,
    cell.dimensions[dimension] ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY,
    String(cell.valueNumeric),
    cell.unit,
  ].join('|');
}

function varianceInputProvenanceMatchesArtifact(
  input: VarianceDecompositionInput,
  artifact: ApprovedFormulaEvidenceArtifact
): boolean {
  const allCells = [...input.cells, ...(input.totalMetricCells ?? [])];
  return allCells.every((cell) => {
    const sourceSystemRef =
      artifact.sourceSystemRef ?? REALIZATION_COMPONENT_VARIANCE_SOURCE_SYSTEM_REF;
    return (
      cell.provenance.artifactIds.includes(artifact.artifactId) &&
      cell.provenance.ingestJobIds.includes(artifact.ingestJobId) &&
      cell.provenance.sourceClasses.includes(artifact.sourceClass) &&
      cell.provenance.sourceSystemRefs.includes(sourceSystemRef) &&
      (!artifact.reportingCutAsOf ||
        cell.provenance.asOf.some((asOf) => sameIsoDate(asOf, artifact.reportingCutAsOf!)))
    );
  });
}

function varianceFamilyTolerance(value: number | undefined): number | null {
  if (value === undefined) return VARIANCE_DECOMPOSITION_TOLERANCE;
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function varianceFamilyFiniteValues(values: readonly number[]): boolean {
  return values.every((value) => Number.isFinite(value));
}

const PVM_PUBLISHED_DECIMALS = 12;

function roundVarianceFamilyPublishedValue(value: number): number {
  return roundTo(value, PVM_PUBLISHED_DECIMALS);
}

function sumVarianceFamilyValues(values: readonly number[]): number {
  let sum = 0;
  let compensation = 0;
  for (const value of values) {
    const adjusted = value - compensation;
    const next = sum + adjusted;
    compensation = next - sum - adjusted;
    sum = next;
  }
  return sum;
}

function varianceFamilyNonFiniteIntermediateBlock(
  formulaLabel: string
): ApprovedFormulaBlockedResult {
  return {
    status: 'blocked',
    reasonCode: 'invalid_formula_input',
    message: `${formulaLabel} produced a non-finite intermediate result.`,
  };
}

function varianceFamilyReconciles(
  total: number,
  partsTotal: number,
  residual: number,
  tolerance: number
): boolean {
  const canonicalDiscrepancy = normalizeVarianceResidual(
    total - partsTotal - residual,
    tolerance
  );
  return Number.isFinite(canonicalDiscrepancy) && canonicalDiscrepancy === 0;
}

function executePvmDecomposition(
  input: PvmDecompositionInput
): ApprovedFormulaResult<PvmDecompositionOutput> {
  const tolerance = varianceFamilyTolerance(input.tolerance);
  if (tolerance === null || !Array.isArray(input.items) || input.items.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'PVM decomposition requires at least one priced item and a valid tolerance.',
    };
  }
  const itemKeys = new Set<string>();
  for (const item of input.items) {
    if (
      typeof item.itemKey !== 'string' ||
      item.itemKey.trim().length === 0 ||
      itemKeys.has(item.itemKey) ||
      !varianceFamilyFiniteValues([
        item.baselinePrice,
        item.baselineQuantity,
        item.comparisonPrice,
        item.comparisonQuantity,
      ]) ||
      item.baselineQuantity < 0 ||
      item.comparisonQuantity < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'PVM decomposition requires unique item keys, finite prices, and non-negative quantities.',
      };
    }
    itemKeys.add(item.itemKey);
  }
  const baselineQuantity = sumVarianceFamilyValues(
    input.items.map((item) => item.baselineQuantity)
  );
  const comparisonQuantity = sumVarianceFamilyValues(
    input.items.map((item) => item.comparisonQuantity)
  );
  if (!varianceFamilyFiniteValues([baselineQuantity, comparisonQuantity])) {
    return varianceFamilyNonFiniteIntermediateBlock('PVM decomposition');
  }
  if (baselineQuantity <= 0 || comparisonQuantity <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'PVM decomposition requires positive total quantity in both periods.',
    };
  }
  const rawBaselineValue = sumVarianceFamilyValues(
    input.items.map((item) => item.baselinePrice * item.baselineQuantity)
  );
  const rawComparisonValue = sumVarianceFamilyValues(
    input.items.map((item) => item.comparisonPrice * item.comparisonQuantity)
  );
  const rawTotalChange = rawComparisonValue - rawBaselineValue;
  if (!varianceFamilyFiniteValues([rawBaselineValue, rawComparisonValue, rawTotalChange])) {
    return varianceFamilyNonFiniteIntermediateBlock('PVM decomposition');
  }
  if (
    input.governedTotalChange !== undefined &&
    (!Number.isFinite(input.governedTotalChange) ||
      Math.abs(rawTotalChange - input.governedTotalChange) > tolerance)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `PVM priced-item movement does not reconcile to the governed total; computed=${rawTotalChange}, governed=${input.governedTotalChange}.`,
    };
  }
  const rawBaselineBlendedPrice = rawBaselineValue / baselineQuantity;
  const rawComparisonBlendedPrice = rawComparisonValue / comparisonQuantity;
  const baselineBlendedPrice = roundVarianceValue(rawBaselineBlendedPrice);
  const comparisonBlendedPrice = roundVarianceValue(rawComparisonBlendedPrice);
  const midpointTotalQuantity = (baselineQuantity + comparisonQuantity) / 2;
  const totalQuantityChange = comparisonQuantity - baselineQuantity;
  if (
    !varianceFamilyFiniteValues([
      rawBaselineBlendedPrice,
      rawComparisonBlendedPrice,
      midpointTotalQuantity,
      totalQuantityChange,
    ])
  ) {
    return varianceFamilyNonFiniteIntermediateBlock('PVM decomposition');
  }
  const rawItemContributions = input.items.map((item) => {
      const baselineShare = item.baselineQuantity / baselineQuantity;
      const comparisonShare = item.comparisonQuantity / comparisonQuantity;
      const midpointShare = (baselineShare + comparisonShare) / 2;
      const shareChange = comparisonShare - baselineShare;
      const midpointItemPrice = (item.baselinePrice + item.comparisonPrice) / 2;
      const priceContribution =
        ((item.baselineQuantity + item.comparisonQuantity) / 2) *
        (item.comparisonPrice - item.baselinePrice);
      const priceChange = item.comparisonPrice - item.baselinePrice;
      const volumeContribution = totalQuantityChange * midpointItemPrice * midpointShare;
      const mixContribution = midpointTotalQuantity * midpointItemPrice * shareChange;
      const totalValueChange =
        item.comparisonPrice * item.comparisonQuantity -
        item.baselinePrice * item.baselineQuantity;
      const intermediateValues = [
        baselineShare,
        comparisonShare,
        midpointShare,
        shareChange,
        midpointItemPrice,
        priceChange,
        priceContribution,
        volumeContribution,
        mixContribution,
        totalValueChange,
      ];
      const residual = varianceFamilyFiniteValues(intermediateValues)
        ? normalizeVarianceResidual(
            totalValueChange - priceContribution - volumeContribution - mixContribution,
            tolerance
          )
        : Number.NaN;
      return {
        item,
        itemKey: item.itemKey,
        baselineShare,
        comparisonShare,
        priceChange,
        priceContribution,
        volumeContribution,
        mixContribution,
        totalValueChange,
        residual,
      };
    }
  );
  if (rawItemContributions.some((item) => !Number.isFinite(item.residual))) {
    return varianceFamilyNonFiniteIntermediateBlock('PVM decomposition');
  }
  const nonReconcilingItem = rawItemContributions.find((item) => item.residual !== 0);
  if (nonReconcilingItem) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `PVM item ${nonReconcilingItem.itemKey} does not reconcile; residual=${nonReconcilingItem.residual}.`,
    };
  }
  const rawPriceEffect = sumVarianceFamilyValues(
    rawItemContributions.map((item) => item.priceContribution)
  );
  const rawVolumeEffect = sumVarianceFamilyValues(
    rawItemContributions.map((item) => item.volumeContribution)
  );
  const rawMixEffect = sumVarianceFamilyValues(
    rawItemContributions.map((item) => item.mixContribution)
  );
  const rawPartsTotal = sumVarianceFamilyValues([
    rawPriceEffect,
    rawVolumeEffect,
    rawMixEffect,
  ]);
  if (!varianceFamilyFiniteValues([rawPriceEffect, rawVolumeEffect, rawMixEffect, rawPartsTotal])) {
    return varianceFamilyNonFiniteIntermediateBlock('PVM decomposition');
  }
  const residual = normalizeVarianceResidual(rawTotalChange - rawPartsTotal, tolerance);
  if (residual !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `PVM decomposition does not reconcile; residual=${residual}.`,
    };
  }
  const itemContributions: readonly PvmDecompositionItemContribution[] = Object.freeze(
    rawItemContributions.map((rawItem) =>
      Object.freeze({
        itemKey: rawItem.itemKey,
        baselinePrice: roundVarianceFamilyPublishedValue(rawItem.item.baselinePrice),
        comparisonPrice: roundVarianceFamilyPublishedValue(rawItem.item.comparisonPrice),
        baselineQuantity: roundVarianceFamilyPublishedValue(rawItem.item.baselineQuantity),
        comparisonQuantity: roundVarianceFamilyPublishedValue(rawItem.item.comparisonQuantity),
        baselineShare: roundVarianceFamilyPublishedValue(rawItem.baselineShare),
        comparisonShare: roundVarianceFamilyPublishedValue(rawItem.comparisonShare),
        priceChange: roundVarianceFamilyPublishedValue(rawItem.priceChange),
        priceContribution: roundVarianceFamilyPublishedValue(rawItem.priceContribution),
        volumeContribution: roundVarianceFamilyPublishedValue(rawItem.volumeContribution),
        mixContribution: roundVarianceFamilyPublishedValue(rawItem.mixContribution),
        totalValueChange: roundVarianceFamilyPublishedValue(rawItem.totalValueChange),
        residual: rawItem.residual,
      })
    )
  );
  const baselineValue = roundVarianceFamilyPublishedValue(rawBaselineValue);
  const comparisonValue = roundVarianceFamilyPublishedValue(rawComparisonValue);
  const totalChange = roundVarianceFamilyPublishedValue(rawTotalChange);
  const priceEffect = roundVarianceFamilyPublishedValue(rawPriceEffect);
  const volumeEffect = roundVarianceFamilyPublishedValue(rawVolumeEffect);
  const mixEffect = roundVarianceFamilyPublishedValue(rawMixEffect);
  const partsTotal = roundVarianceFamilyPublishedValue(rawPartsTotal);
  const publishedItemValues = itemContributions.flatMap((item) => [
    item.baselinePrice,
    item.comparisonPrice,
    item.baselineQuantity,
    item.comparisonQuantity,
    item.baselineShare,
    item.comparisonShare,
    item.priceChange,
    item.priceContribution,
    item.volumeContribution,
    item.mixContribution,
    item.totalValueChange,
    item.residual,
  ]);
  if (
    !varianceFamilyFiniteValues([
      baselineValue,
      comparisonValue,
      baselineQuantity,
      comparisonQuantity,
      baselineBlendedPrice,
      comparisonBlendedPrice,
      totalChange,
      priceEffect,
      volumeEffect,
      mixEffect,
      partsTotal,
      residual,
      ...publishedItemValues,
    ])
  ) {
    return varianceFamilyNonFiniteIntermediateBlock('PVM decomposition');
  }
  const reconciles = varianceFamilyReconciles(totalChange, partsTotal, residual, tolerance);
  if (!reconciles) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'PVM decomposition published values do not reconcile.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      baselineValue,
      comparisonValue,
      baselineQuantity: roundVarianceFamilyPublishedValue(baselineQuantity),
      comparisonQuantity: roundVarianceFamilyPublishedValue(comparisonQuantity),
      baselineBlendedPrice,
      comparisonBlendedPrice,
      blendedPriceChange: roundVarianceValue(comparisonBlendedPrice - baselineBlendedPrice),
      totalChange,
      priceEffect,
      volumeEffect,
      mixEffect,
      residual,
      residualLabel: 'unexplained variance' as const,
      itemContributions,
      reconciliation: Object.freeze({
        partsTotal,
        residual,
        tolerance,
        reconciles,
      }),
      ...(input.reportingCutAsOf ? { reportingCutAsOf: input.reportingCutAsOf } : {}),
    }),
  };
}

function executeCostRateVolume(
  input: CostRateVolumeInput
): ApprovedFormulaResult<CostRateVolumeOutput> {
  const tolerance = varianceFamilyTolerance(input.tolerance);
  if (
    tolerance === null ||
    !varianceFamilyFiniteValues([
      input.baselineRate,
      input.baselineActivity,
      input.comparisonRate,
      input.comparisonActivity,
    ]) ||
    input.baselineActivity < 0 ||
    input.comparisonActivity < 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Cost rate/volume decomposition requires finite rates and non-negative activity.',
    };
  }
  const efficiencyStandard = input.efficiencyStandard;
  if (
    efficiencyStandard &&
    (!varianceFamilyFiniteValues([
      efficiencyStandard.standardInputPerOutput,
      efficiencyStandard.baselineOutput,
      efficiencyStandard.comparisonOutput,
    ]) ||
      efficiencyStandard.standardInputPerOutput < 0 ||
      efficiencyStandard.baselineOutput < 0 ||
      efficiencyStandard.comparisonOutput < 0)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Cost efficiency decomposition requires a non-negative governed input-per-output standard and output quantities.',
    };
  }
  const rawCostChange = roundVarianceValue(
    input.comparisonRate * input.comparisonActivity - input.baselineRate * input.baselineActivity
  );
  if (!Number.isFinite(rawCostChange)) {
    return varianceFamilyNonFiniteIntermediateBlock('Cost rate/volume decomposition');
  }
  if (
    input.governedTotalCostChange !== undefined &&
    (!Number.isFinite(input.governedTotalCostChange) ||
      Math.abs(rawCostChange - input.governedTotalCostChange) > tolerance)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `Cost movement does not reconcile to the governed total; computed=${rawCostChange}, governed=${input.governedTotalCostChange}.`,
    };
  }
  const rateEffect = roundVarianceValue(
    -((input.baselineActivity + input.comparisonActivity) / 2) *
      (input.comparisonRate - input.baselineRate)
  );
  const volumeEffect = roundVarianceValue(
    -((input.baselineRate + input.comparisonRate) / 2) *
      (input.comparisonActivity - input.baselineActivity)
  );
  const averageRate = (input.baselineRate + input.comparisonRate) / 2;
  const pureVolumeEffect = efficiencyStandard
    ? roundVarianceValue(
        -averageRate *
          efficiencyStandard.standardInputPerOutput *
          (efficiencyStandard.comparisonOutput - efficiencyStandard.baselineOutput)
      )
    : null;
  const efficiencyEffect = efficiencyStandard
    ? roundVarianceValue(
        -averageRate *
          ((input.comparisonActivity -
            efficiencyStandard.standardInputPerOutput * efficiencyStandard.comparisonOutput) -
            (input.baselineActivity -
              efficiencyStandard.standardInputPerOutput * efficiencyStandard.baselineOutput))
      )
    : null;
  const totalOiFavorabilityChange = roundVarianceValue(-rawCostChange);
  const partsTotal = roundVarianceValue(
    rateEffect +
      (pureVolumeEffect === null || efficiencyEffect === null
        ? volumeEffect
        : pureVolumeEffect + efficiencyEffect)
  );
  const derivedCostValues = [
    rateEffect,
    volumeEffect,
    totalOiFavorabilityChange,
    partsTotal,
    ...(pureVolumeEffect === null ? [] : [pureVolumeEffect]),
    ...(efficiencyEffect === null ? [] : [efficiencyEffect]),
  ];
  if (!varianceFamilyFiniteValues(derivedCostValues)) {
    return varianceFamilyNonFiniteIntermediateBlock('Cost rate/volume decomposition');
  }
  const residual = normalizeVarianceResidual(totalOiFavorabilityChange - partsTotal, tolerance);
  if (!Number.isFinite(residual)) {
    return varianceFamilyNonFiniteIntermediateBlock('Cost rate/volume decomposition');
  }
  if (residual !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `Cost rate/volume decomposition does not reconcile; residual=${residual}.`,
    };
  }
  const reconciles = varianceFamilyReconciles(
    totalOiFavorabilityChange,
    partsTotal,
    residual,
    tolerance
  );
  if (!reconciles) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'Cost rate/volume published values do not reconcile.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      rawCostChange,
      totalOiFavorabilityChange,
      rateEffect,
      volumeEffect,
      pureVolumeEffect,
      efficiencyEffect,
      residual,
      residualLabel: 'unexplained cost variance' as const,
      missingEfficiencyStandard: efficiencyStandard === undefined,
      missingEfficiencyStandardLabel:
        efficiencyStandard === undefined ? ('efficiency standard not governed' as const) : null,
      reconciliation: Object.freeze({
        partsTotal,
        residual,
        tolerance,
        reconciles,
      }),
      ...(input.reportingCutAsOf ? { reportingCutAsOf: input.reportingCutAsOf } : {}),
    }),
  };
}

function executeFxIsolation(input: FxIsolationInput): ApprovedFormulaResult<FxIsolationOutput> {
  const tolerance = varianceFamilyTolerance(input.tolerance);
  if (tolerance === null || !Array.isArray(input.lines) || input.lines.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'FX isolation requires at least one governed currency line.',
    };
  }
  let totalReportedChange = 0;
  let fxEffect = 0;
  for (const line of input.lines) {
    const baselineRate = line.baselineReportingRate;
    const comparisonRate = line.comparisonReportingRate;
    if (baselineRate === undefined || comparisonRate === undefined) {
      return {
        status: 'blocked',
        reasonCode: 'missing_fx_rates',
        message: `FX isolation is missing governed reporting rates for ${line.lineKey}.`,
      };
    }
    if (
      typeof line.lineKey !== 'string' ||
      line.lineKey.trim().length === 0 ||
      !varianceFamilyFiniteValues([
        line.baselineReportedValue,
        line.comparisonReportedValue,
        line.comparisonLocalValue,
        baselineRate,
        comparisonRate,
      ])
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'FX isolation requires named lines with finite reported values, local values, and rates.',
      };
    }
    totalReportedChange += line.comparisonReportedValue - line.baselineReportedValue;
    fxEffect += line.comparisonLocalValue * (comparisonRate - baselineRate);
  }
  if (!varianceFamilyFiniteValues([totalReportedChange, fxEffect])) {
    return varianceFamilyNonFiniteIntermediateBlock('FX isolation');
  }
  totalReportedChange = roundVarianceValue(totalReportedChange);
  fxEffect = roundVarianceValue(fxEffect);
  if (
    input.governedTotalReportedChange !== undefined &&
    (!Number.isFinite(input.governedTotalReportedChange) ||
      Math.abs(totalReportedChange - input.governedTotalReportedChange) > tolerance)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `FX reported movement does not reconcile to the governed total; computed=${totalReportedChange}, governed=${input.governedTotalReportedChange}.`,
    };
  }
  const constantCurrencyChange = roundVarianceValue(totalReportedChange - fxEffect);
  const partsTotal = roundVarianceValue(fxEffect + constantCurrencyChange);
  if (!varianceFamilyFiniteValues([constantCurrencyChange, partsTotal])) {
    return varianceFamilyNonFiniteIntermediateBlock('FX isolation');
  }
  const residual = normalizeVarianceResidual(totalReportedChange - partsTotal, tolerance);
  if (!Number.isFinite(residual)) {
    return varianceFamilyNonFiniteIntermediateBlock('FX isolation');
  }
  if (residual !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `FX isolation does not reconcile; residual=${residual}.`,
    };
  }
  const reconciles = varianceFamilyReconciles(
    totalReportedChange,
    partsTotal,
    residual,
    tolerance
  );
  if (!reconciles) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'FX isolation published values do not reconcile.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalReportedChange,
      fxEffect,
      constantCurrencyChange,
      residual,
      residualLabel: 'unexplained FX variance' as const,
      reconciliation: Object.freeze({
        partsTotal,
        residual,
        tolerance,
        reconciles,
      }),
      ...(input.reportingCutAsOf ? { reportingCutAsOf: input.reportingCutAsOf } : {}),
    }),
  };
}

function executeOneoffIsolation(
  input: OneoffIsolationInput
): ApprovedFormulaResult<OneoffIsolationOutput> {
  const tolerance = varianceFamilyTolerance(input.tolerance);
  if (tolerance === null || !Array.isArray(input.lines) || input.lines.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'One-off isolation requires at least one governed line.',
    };
  }
  const lineKeys = new Set<string>();
  for (const line of input.lines) {
    if (
      typeof line.lineKey !== 'string' ||
      line.lineKey.trim().length === 0 ||
      lineKeys.has(line.lineKey) ||
      !varianceFamilyFiniteValues([line.baselineValue, line.comparisonValue])
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'One-off isolation requires unique named lines with finite period values.',
      };
    }
    lineKeys.add(line.lineKey);
  }
  const totalChange = roundVarianceValue(
    input.lines.reduce((sum, line) => sum + line.comparisonValue - line.baselineValue, 0)
  );
  if (!Number.isFinite(totalChange)) {
    return varianceFamilyNonFiniteIntermediateBlock('One-off isolation');
  }
  if (
    input.governedTotalChange !== undefined &&
    (!Number.isFinite(input.governedTotalChange) ||
      Math.abs(totalChange - input.governedTotalChange) > tolerance)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `One-off movement does not reconcile to the governed total; computed=${totalChange}, governed=${input.governedTotalChange}.`,
    };
  }
  const underlyingChange = roundVarianceValue(
    input.lines
      .filter((line) => line.nonrecurring !== true)
      .reduce((sum, line) => sum + line.comparisonValue - line.baselineValue, 0)
  );
  const oneoffChange = roundVarianceValue(
    input.lines
      .filter((line) => line.nonrecurring === true)
      .reduce((sum, line) => sum + line.comparisonValue - line.baselineValue, 0)
  );
  const partsTotal = roundVarianceValue(underlyingChange + oneoffChange);
  if (!varianceFamilyFiniteValues([underlyingChange, oneoffChange, partsTotal])) {
    return varianceFamilyNonFiniteIntermediateBlock('One-off isolation');
  }
  const residual = normalizeVarianceResidual(totalChange - partsTotal, tolerance);
  if (!Number.isFinite(residual)) {
    return varianceFamilyNonFiniteIntermediateBlock('One-off isolation');
  }
  if (residual !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `One-off isolation does not reconcile; residual=${residual}.`,
    };
  }
  const reconciles = varianceFamilyReconciles(totalChange, partsTotal, residual, tolerance);
  if (!reconciles) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'One-off isolation published values do not reconcile.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalChange,
      underlyingChange,
      oneoffChange,
      residual,
      residualLabel: 'unexplained one-off variance' as const,
      unclassifiedLineKeys: Object.freeze(
        input.lines.filter((line) => line.nonrecurring === undefined).map((line) => line.lineKey)
      ),
      reconciliation: Object.freeze({
        partsTotal,
        residual,
        tolerance,
        reconciles,
      }),
      ...(input.reportingCutAsOf ? { reportingCutAsOf: input.reportingCutAsOf } : {}),
    }),
  };
}

type OiBridgeNodeExecution =
  | Readonly<{ ok: true; output: OiBridgeNodeOutput }>
  | Readonly<{ ok: false; block: ApprovedFormulaBlockedResult }>;

function executeOiBridgeNode(node: OiBridgeNodeInput, tolerance: number): OiBridgeNodeExecution {
  if (
    typeof node.nodeKey !== 'string' ||
    node.nodeKey.trim().length === 0 ||
    !varianceFamilyFiniteValues([
      node.reportedDelta,
      node.revenueSideEffect,
      node.costSideEffect,
    ])
  ) {
    return {
      ok: false,
      block: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'OI bridge nodes require a key and finite reported, revenue, and cost values.',
      },
    };
  }
  const contribution = roundVarianceValue(node.revenueSideEffect - node.costSideEffect);
  if (!Number.isFinite(contribution)) {
    return {
      ok: false,
      block: varianceFamilyNonFiniteIntermediateBlock('OI bridge composition'),
    };
  }
  const residual = normalizeVarianceResidual(node.reportedDelta - contribution, tolerance);
  if (!Number.isFinite(residual)) {
    return {
      ok: false,
      block: varianceFamilyNonFiniteIntermediateBlock('OI bridge composition'),
    };
  }
  if (residual !== 0) {
    return {
      ok: false,
      block: {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: `OI bridge node ${node.nodeKey} does not reconcile revenue minus cost to its reported delta; residual=${residual}.`,
      },
    };
  }
  const children: OiBridgeNodeOutput[] = [];
  for (const child of node.children ?? []) {
    const childResult = executeOiBridgeNode(child, tolerance);
    if (!childResult.ok) return childResult;
    children.push(childResult.output);
  }
  if (children.length > 0) {
    const childTotal = roundVarianceValue(
      children.reduce((sum, child) => sum + child.reportedDelta, 0)
    );
    if (!Number.isFinite(childTotal)) {
      return {
        ok: false,
        block: varianceFamilyNonFiniteIntermediateBlock('OI bridge composition'),
      };
    }
    const childResidual = normalizeVarianceResidual(node.reportedDelta - childTotal, tolerance);
    if (!Number.isFinite(childResidual)) {
      return {
        ok: false,
        block: varianceFamilyNonFiniteIntermediateBlock('OI bridge composition'),
      };
    }
    if (childResidual !== 0) {
      return {
        ok: false,
        block: {
          status: 'blocked',
          reasonCode: 'formula_reconciliation_failure',
          message: `OI bridge node ${node.nodeKey} does not reconcile to its children; residual=${childResidual}.`,
        },
      };
    }
  }
  return {
    ok: true,
    output: Object.freeze({
      nodeKey: node.nodeKey,
      reportedDelta: roundVarianceValue(node.reportedDelta),
      revenueSideEffect: roundVarianceValue(node.revenueSideEffect),
      costSideEffect: roundVarianceValue(node.costSideEffect),
      contribution,
      residual,
      children: Object.freeze(children),
    }),
  };
}

function executeOiBridgeCompose(
  input: OiBridgeComposeInput
): ApprovedFormulaResult<OiBridgeComposeOutput> {
  const tolerance = varianceFamilyTolerance(input.tolerance);
  if (tolerance === null || !input.root) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'OI bridge composition requires a root node and valid tolerance.',
    };
  }
  const rootResult = executeOiBridgeNode(input.root, tolerance);
  if (!rootResult.ok) return rootResult.block;
  const residual = rootResult.output.residual;
  const reconciles = varianceFamilyReconciles(
    rootResult.output.reportedDelta,
    rootResult.output.contribution,
    residual,
    tolerance
  );
  if (!reconciles) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'OI bridge published values do not reconcile.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalChange: rootResult.output.reportedDelta,
      residual,
      residualLabel: 'unexplained bridge variance' as const,
      root: rootResult.output,
      reconciliation: Object.freeze({
        partsTotal: rootResult.output.contribution,
        residual,
        tolerance,
        reconciles,
      }),
      ...(input.reportingCutAsOf ? { reportingCutAsOf: input.reportingCutAsOf } : {}),
    }),
  };
}

function executeVarianceTraceback(
  input: VarianceTracebackInput
): ApprovedFormulaResult<VarianceTracebackOutput> {
  const tolerance = varianceFamilyTolerance(input.tolerance);
  if (
    tolerance === null ||
    !Number.isFinite(input.totalMiss) ||
    !Array.isArray(input.explainedTerms)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Variance traceback requires a finite total miss and explained term list.',
    };
  }
  const termKeys = new Set<string>();
  for (const term of input.explainedTerms) {
    if (
      typeof term.termKey !== 'string' ||
      term.termKey.trim().length === 0 ||
      termKeys.has(term.termKey) ||
      !Number.isFinite(term.value)
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'Variance traceback requires unique named terms with finite values.',
      };
    }
    termKeys.add(term.termKey);
  }
  const rawExplainedVariance = sumVarianceFamilyValues(
    input.explainedTerms.map((term) => term.value)
  );
  const rawUnexplainedResidual = input.totalMiss - rawExplainedVariance;
  if (!varianceFamilyFiniteValues([rawExplainedVariance, rawUnexplainedResidual])) {
    return varianceFamilyNonFiniteIntermediateBlock('Variance traceback');
  }
  const explainedTerms = Object.freeze(
    input.explainedTerms.map((term) =>
      Object.freeze({
        termKey: term.termKey,
        value: roundVarianceFamilyPublishedValue(term.value),
      })
    )
  );
  const totalMiss = roundVarianceFamilyPublishedValue(input.totalMiss);
  const explainedVariance = roundVarianceFamilyPublishedValue(
    sumVarianceFamilyValues(explainedTerms.map((term) => term.value))
  );
  const unexplainedResidual = roundVarianceFamilyPublishedValue(
    totalMiss - explainedVariance
  );
  const partsTotal = roundVarianceFamilyPublishedValue(
    explainedVariance + unexplainedResidual
  );
  if (
    !varianceFamilyFiniteValues([
      totalMiss,
      explainedVariance,
      unexplainedResidual,
      partsTotal,
    ])
  ) {
    return varianceFamilyNonFiniteIntermediateBlock('Variance traceback');
  }
  const reconciliationResidual = normalizeVarianceResidual(totalMiss - partsTotal, tolerance);
  if (!Number.isFinite(reconciliationResidual)) {
    return varianceFamilyNonFiniteIntermediateBlock('Variance traceback');
  }
  if (reconciliationResidual !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: `Variance traceback does not reconcile; residual=${reconciliationResidual}.`,
    };
  }
  const reconciles = varianceFamilyReconciles(
    totalMiss,
    partsTotal,
    reconciliationResidual,
    tolerance
  );
  if (!reconciles) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'Variance traceback published values do not reconcile.',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalMiss,
      explainedVariance,
      unexplainedResidual,
      residualLabel: 'unexplained (no governed driver)' as const,
      explainedTerms,
      reconciliation: Object.freeze({
        partsTotal,
        residual: reconciliationResidual,
        tolerance,
        reconciles,
      }),
      ...(input.reportingCutAsOf ? { reportingCutAsOf: input.reportingCutAsOf } : {}),
    }),
  };
}

function executePricingIncreaseElasticityScenario(
  input: PricingIncreaseElasticityScenarioInput,
): ApprovedFormulaResult<PricingIncreaseElasticityScenarioOutput> {
  const required = [
    'baselineRevenueUsd',
    'priceChangePct',
    'baselinePlannedSellRateIncreasePct',
    'baselineExpectedVolumeResponsePct',
    'expectedNetRevenueRetentionPct',
    'agpFlowThroughPct',
    'executionCostUsd',
  ] as const;
  const missing = required.filter((field) => !Number.isFinite(input[field]));
  if (missing.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `Pricing increase elasticity scenario requires ${missing.join(', ')}.`,
    };
  }
  if (input.baselineRevenueUsd < 0 || input.executionCostUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Pricing increase elasticity scenario requires non-negative baseline revenue and execution cost.',
    };
  }
  if (
    input.expectedNetRevenueRetentionPct < 0 ||
    input.expectedNetRevenueRetentionPct > 1 ||
    input.agpFlowThroughPct < 0 ||
    input.agpFlowThroughPct > 1
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Pricing increase elasticity scenario requires retention and AGP flow-through between 0 and 1.',
    };
  }
  if (input.baselinePlannedSellRateIncreasePct === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'Pricing increase elasticity scenario requires a non-zero governed planned sell-rate increase to derive implied elasticity.',
    };
  }

  const impliedElasticity = roundTo(
    input.baselineExpectedVolumeResponsePct / input.baselinePlannedSellRateIncreasePct,
    6,
  );
  const expectedVolumeResponsePct = roundTo(input.priceChangePct * impliedElasticity, 6);
  const expectedRevenueGrowthPct = roundTo(
    input.priceChangePct + expectedVolumeResponsePct,
    6,
  );
  const retainedBaselineRevenue =
    input.baselineRevenueUsd * input.expectedNetRevenueRetentionPct;
  const netRevenueDeltaUsd = roundTo(
    retainedBaselineRevenue * (input.priceChangePct + expectedVolumeResponsePct),
    2,
  );
  const agpContributionUsd = roundTo(netRevenueDeltaUsd * input.agpFlowThroughPct, 2);
  const operatingIncomeContributionUsd = roundTo(
    agpContributionUsd - input.executionCostUsd,
    2,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      priceChangePct: roundTo(input.priceChangePct, 6),
      impliedElasticity,
      expectedVolumeResponsePct,
      expectedRevenueGrowthPct,
      netRevenueDeltaUsd,
      agpContributionUsd,
      operatingIncomeContributionUsd,
      statusBand:
        operatingIncomeContributionUsd > 0
          ? 'sufficient'
          : operatingIncomeContributionUsd === 0
            ? 'constrained'
            : 'critical',
    }),
  };
}

function missingPricingQuoteInvoiceLeakageInputs(
  input: PricingQuoteInvoiceLeakageInput | null | undefined,
): string[] {
  if (!input || typeof input !== 'object') {
    return [...PRICING_QUOTE_INVOICE_LEAKAGE_REQUIRED_INPUTS];
  }
  return PRICING_QUOTE_INVOICE_LEAKAGE_REQUIRED_INPUTS.filter((field) => {
    const value = input[field];
    if (
      field === 'customerCohort' ||
      field === 'segment' ||
      field === 'serviceLaneScope' ||
      field === 'pricingActionId'
    ) {
      return typeof value !== 'string' || value.trim().length === 0;
    }
    return !Number.isFinite(value);
  });
}

function missingRealizationRateInputs(
  input: RealizationRateInput | null | undefined,
): string[] {
  if (!input || typeof input !== 'object') {
    return [...REALIZATION_RATE_REQUIRED_INPUTS];
  }
  return REALIZATION_RATE_REQUIRED_INPUTS.filter((field) => {
    const value = input[field];
    if (field === 'segment') {
      return typeof value !== 'string' || value.trim().length === 0;
    }
    return !Number.isFinite(value);
  });
}

function validatePricingQuoteInvoiceLeakageInputAgainstEvidence(
  input: Readonly<PricingQuoteInvoiceLeakageInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const missingInputs = missingPricingQuoteInvoiceLeakageInputs(input);
  if (missingInputs.length > 0) {
    return {
      reasonCode: 'invalid_formula_input',
      message: `missing_formula_input: ${missingInputs.join(', ')}`,
    };
  }
  if (artifacts.length !== 1) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing quote-to-invoice leakage requires exactly one approved pricing realization artifact.',
    };
  }
  const evidenceFacts = parsePricingQuoteInvoiceLeakageEvidenceFacts(
    artifacts[0].contentJson,
  );
  if (!evidenceFacts) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Approved pricing evidence is missing raw quote-to-invoice formula inputs.',
    };
  }
  if (
    evidenceFacts.customerCohort !== normalizeOptionalText(input.customerCohort) ||
    evidenceFacts.segment !== normalizeOptionalText(input.segment) ||
    evidenceFacts.serviceLaneScope !== normalizeOptionalText(input.serviceLaneScope) ||
    evidenceFacts.pricingActionId !== normalizeOptionalText(input.pricingActionId) ||
    !sameNumber(evidenceFacts.quotedRevenueUsd, input.quotedRevenueUsd!) ||
    !sameNumber(evidenceFacts.invoicedRevenueUsd, input.invoicedRevenueUsd!) ||
    !sameNumber(evidenceFacts.eligibleRevenueUsd, input.eligibleRevenueUsd!)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing quote-to-invoice leakage formula inputs do not reconcile to approved pricing evidence.',
    };
  }
  const evidenceReportingCut =
    stringOrNull(artifacts[0].reportingCutAsOf) ??
    stringOrNull(asRecord(artifacts[0].contentJson)?.reportingCutAsOf);
  if (
    input.reportingCutAsOf &&
    evidenceReportingCut &&
    !sameIsoDate(input.reportingCutAsOf, evidenceReportingCut)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing quote-to-invoice leakage reporting cut does not reconcile to approved pricing evidence.',
    };
  }
  return null;
}

function validateRealizationRateInputAgainstEvidence(
  input: Readonly<RealizationRateInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const missingInputs = missingRealizationRateInputs(input);
  if (missingInputs.length > 0) {
    return {
      reasonCode: 'invalid_formula_input',
      message: `missing_formula_input: ${missingInputs.join(', ')}`,
    };
  }
  if (artifacts.length !== 1) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Realization rate requires exactly one approved pricing realization artifact.',
    };
  }
  const evidenceFacts = parsePricingQuoteInvoiceLeakageEvidenceFacts(
    artifacts[0].contentJson,
  );
  if (!evidenceFacts) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Approved pricing evidence is missing raw realization-rate inputs.',
    };
  }
  if (
    evidenceFacts.segment !== normalizeOptionalText(input.segment) ||
    !sameNumber(evidenceFacts.quotedRevenueUsd, input.quotedRevenueUsd!) ||
    !sameNumber(evidenceFacts.invoicedRevenueUsd, input.invoicedRevenueUsd!) ||
    !sameNumber(evidenceFacts.eligibleRevenueUsd, input.eligibleRevenueUsd!)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Realization rate formula inputs do not reconcile to approved pricing evidence.',
    };
  }
  const evidenceReportingCut =
    stringOrNull(artifacts[0].reportingCutAsOf) ??
    stringOrNull(asRecord(artifacts[0].contentJson)?.reportingCutAsOf);
  if (
    input.reportingCutAsOf &&
    evidenceReportingCut &&
    !sameIsoDate(input.reportingCutAsOf, evidenceReportingCut)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Realization rate reporting cut does not reconcile to approved pricing evidence.',
    };
  }
  return null;
}

function validatePricingDiscountLeakageInputAgainstEvidence(
  input: Readonly<PricingDiscountLeakageInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const evidenceFacts = parsePricingDiscountLeakageEvidenceFacts(artifacts);
  if (!evidenceFacts) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Pricing discount leakage requires one approved authority matrix and one approved transaction snapshot artifact.',
    };
  }
  if (
    !sameDiscountBands(input.authorityBands, evidenceFacts.authorityBands) ||
    !sameDiscountTransactions(input.transactions, evidenceFacts.transactions)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing discount leakage formula inputs do not reconcile to approved discount artifacts.',
    };
  }
  if (
    input.reportingCutAsOf &&
    evidenceFacts.reportingCutAsOf &&
    !sameIsoDate(input.reportingCutAsOf, evidenceFacts.reportingCutAsOf)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing discount leakage reporting cut does not reconcile to approved discount artifacts.',
    };
  }
  return null;
}

function validatePricingStructureMixShiftInputAgainstEvidence(
  input: Readonly<PricingStructureMixShiftMarginContributionInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const evidenceFacts = parsePricingStructureMixShiftEvidenceFacts(artifacts);
  if (!evidenceFacts) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Pricing structure mix-shift contribution requires certified observed-feed pricing structure evidence.',
    };
  }
  const matchingRow = evidenceFacts?.rows.find(
    (row) => row.period === input.period && row.segment === input.segment,
  );
  if (!matchingRow) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Pricing structure mix-shift contribution requires an approved pricing structure evidence row.',
    };
  }
  if (
    !sameNumber(input.totalNetRevenueUsd, matchingRow.totalNetRevenueUsd) ||
    !sameNumber(input.baselineSpotMixPct, matchingRow.baselineSpotMixPct) ||
    !sameNumber(input.currentSpotMixPct, matchingRow.currentSpotMixPct) ||
    !sameNumber(input.baselineContractMixPct, matchingRow.baselineContractMixPct) ||
    !sameNumber(input.currentContractMixPct, matchingRow.currentContractMixPct) ||
    !sameNumber(input.spotNetRevenueMarginPct, matchingRow.spotNetRevenueMarginPct) ||
    !sameNumber(input.contractNetRevenueMarginPct, matchingRow.contractNetRevenueMarginPct)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing structure mix-shift formula inputs do not reconcile to approved pricing structure evidence.',
    };
  }
  if (
    input.reportingCutAsOf &&
    matchingRow.reportingCutAsOf &&
    !sameIsoDate(input.reportingCutAsOf, matchingRow.reportingCutAsOf)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing structure mix-shift reporting cut does not reconcile to approved pricing structure evidence.',
    };
  }
  return null;
}

function validatePricingQuoteInvoiceLeakageOutputAgainstEvidence(
  input: Readonly<PricingQuoteInvoiceLeakageInput>,
  output: Readonly<PricingQuoteInvoiceLeakageOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validatePricingQuoteInvoiceLeakageInputAgainstEvidence(
    input,
    artifacts,
  );
  if (inputBlock) return inputBlock;
  const recomputed = executePricingQuoteInvoiceLeakage(input);
  if (recomputed.status === 'blocked') {
    return {
      reasonCode: normalizePricingEvidenceValidationReasonCode(recomputed.reasonCode),
      message: recomputed.message,
    };
  }
  if (
    !sameNumber(
      output.quoteInvoiceLeakageUsd,
      recomputed.output.quoteInvoiceLeakageUsd,
    ) ||
    !sameNumber(output.eligibleRevenueUsd, recomputed.output.eligibleRevenueUsd) ||
    !sameNumber(output.leakageRatePct, recomputed.output.leakageRatePct)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing quote-to-invoice leakage output does not reconcile to approved formula execution.',
    };
  }
  return null;
}

function validatePricingDiscountLeakageOutputAgainstEvidence(
  input: Readonly<PricingDiscountLeakageInput>,
  output: Readonly<PricingDiscountLeakageOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validatePricingDiscountLeakageInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;
  const recomputed = executePricingDiscountLeakage(input);
  if (recomputed.status === 'blocked') {
    return {
      reasonCode: normalizePricingEvidenceValidationReasonCode(recomputed.reasonCode),
      message: recomputed.message,
    };
  }
  const expected = recomputed.output;
  const evidenceFacts = parsePricingDiscountLeakageEvidenceFacts(artifacts);
  if (!evidenceFacts) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Approved pricing discount leakage evidence is missing.',
    };
  }
  if (
    !sameNumber(output.eligibleListRevenueUsd, expected.eligibleListRevenueUsd) ||
    !sameNumber(output.totalNetRevenueUsd, expected.totalNetRevenueUsd) ||
    !sameNumber(output.totalDiscountUsd, expected.totalDiscountUsd) ||
    !sameNumber(output.withinPolicyDiscountUsd, expected.withinPolicyDiscountUsd) ||
    !sameNumber(output.offPolicyDiscountUsd, expected.offPolicyDiscountUsd) ||
    !sameNumber(output.discountLeakageUsd, expected.discountLeakageUsd) ||
    !sameNumber(output.discountLeakageRatePct, expected.discountLeakageRatePct)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing discount leakage output does not reconcile to approved formula execution.',
    };
  }
  const controlTotals = evidenceFacts.controlTotals;
  if (
    controlTotals &&
    (!sameNumber(output.eligibleListRevenueUsd, controlTotals.totalListRevenueUsd) ||
      !sameNumber(output.totalNetRevenueUsd, controlTotals.totalNetRevenueUsd) ||
      !sameNumber(output.totalDiscountUsd, controlTotals.totalDiscountUsd) ||
      !sameNumber(output.withinPolicyDiscountUsd, controlTotals.totalWithinPolicyDiscountUsd) ||
      !sameNumber(output.offPolicyDiscountUsd, controlTotals.totalOffPolicyLeakageUsd) ||
      !sameNumber(output.discountLeakageRatePct, controlTotals.offPolicyLeakageRatePct))
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing discount leakage output does not reconcile to approved transaction control totals.',
    };
  }
  return null;
}

function validatePricingStructureMixShiftOutputAgainstEvidence(
  input: Readonly<PricingStructureMixShiftMarginContributionInput>,
  output: Readonly<PricingStructureMixShiftMarginContributionOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validatePricingStructureMixShiftInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;
  const recomputed = executePricingStructureMixShiftMarginContribution(input);
  if (recomputed.status === 'blocked') {
    return {
      reasonCode: normalizePricingEvidenceValidationReasonCode(recomputed.reasonCode),
      message: recomputed.message,
    };
  }
  if (
    !sameNumber(output.spotToContractMixShiftPct, recomputed.output.spotToContractMixShiftPct) ||
    !sameNumber(output.marginSpreadPct, recomputed.output.marginSpreadPct) ||
    !sameNumber(output.mixShiftContributionUsd, recomputed.output.mixShiftContributionUsd) ||
    !sameNumber(
      output.netRevenueMarginContributionBps,
      recomputed.output.netRevenueMarginContributionBps,
    )
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Pricing structure mix-shift output does not reconcile to approved formula execution.',
    };
  }
  return null;
}

function validateRealizationRateOutputAgainstEvidence(
  input: Readonly<RealizationRateInput>,
  output: Readonly<RealizationRateOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateRealizationRateInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;
  const recomputed = executeRealizationRate(input);
  if (recomputed.status === 'blocked') {
    return {
      reasonCode: normalizePricingEvidenceValidationReasonCode(recomputed.reasonCode),
      message: recomputed.message,
    };
  }
  if (
    !sameNumber(output.quotedRevenueUsd, recomputed.output.quotedRevenueUsd) ||
    !sameNumber(output.invoicedRevenueUsd, recomputed.output.invoicedRevenueUsd) ||
    !sameNumber(output.eligibleRevenueUsd, recomputed.output.eligibleRevenueUsd) ||
    !sameNumber(output.realizationGapUsd, recomputed.output.realizationGapUsd) ||
    !sameNumber(output.realizationRatePct, recomputed.output.realizationRatePct)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Realization rate output does not reconcile to approved formula execution.',
    };
  }
  return null;
}

function normalizePricingEvidenceValidationReasonCode(
  reasonCode: ApprovedFormulaBlockedReasonCode,
): ApprovedFormulaEvidenceValidationBlock['reasonCode'] {
  if (
    reasonCode === 'missing_governed_evidence' ||
    reasonCode === 'unapproved_source_system' ||
    reasonCode === 'formula_reconciliation_failure'
  ) {
    return reasonCode;
  }
  return 'invalid_formula_input';
}

type PricingQuoteInvoiceLeakageEvidenceFacts = Readonly<{
  customerCohort: string;
  segment: string;
  serviceLaneScope: string;
  pricingActionId: string;
  quotedRevenueUsd: number;
  invoicedRevenueUsd: number;
  eligibleRevenueUsd: number;
}>;

type PricingDiscountLeakageEvidenceFacts = Readonly<{
  authorityBands: readonly PricingDiscountAuthorityBandInput[];
  transactions: readonly PricingDiscountTransactionInput[];
  reportingCutAsOf: string | null;
  controlTotals: Readonly<{
    totalListRevenueUsd: number;
    totalNetRevenueUsd: number;
    totalDiscountUsd: number;
    totalWithinPolicyDiscountUsd: number;
    totalOffPolicyLeakageUsd: number;
    offPolicyLeakageRatePct: number;
  }> | null;
}>;

type PricingStructureMixShiftEvidenceFacts = Readonly<{
  rows: readonly PricingStructureMixShiftMarginContributionInput[];
}>;

function parsePricingQuoteInvoiceLeakageEvidenceFacts(
  raw: unknown,
): PricingQuoteInvoiceLeakageEvidenceFacts | null {
  const content = asRecord(raw);
  if (!content) return null;
  const optional = asRecord(content.optionalFields);
  const customerCohort = stringOrNull(optional?.customerCohort ?? content.customerCohort);
  const segment = stringOrNull(optional?.segment ?? content.segment);
  const serviceLaneScope = stringOrNull(
    optional?.serviceLaneScope ?? content.serviceLaneScope,
  );
  const pricingActionId = stringOrNull(optional?.pricingActionId ?? content.pricingActionId);
  const quotedRevenueUsd = finiteNumberOrNull(
    optional?.quotedRevenueUsd ??
      optional?.quoted_revenue_usd ??
      content.quotedRevenueUsd ??
      content.quoted_revenue_usd,
  );
  const invoicedRevenueUsd = finiteNumberOrNull(
    optional?.invoicedRevenueUsd ??
      optional?.invoiced_revenue_usd ??
      content.invoicedRevenueUsd ??
      content.invoiced_revenue_usd,
  );
  const eligibleRevenueUsd = finiteNumberOrNull(
    optional?.eligibleRevenueUsd ??
      optional?.eligible_revenue_usd ??
      content.eligibleRevenueUsd ??
      content.eligible_revenue_usd,
  );
  if (
    !customerCohort ||
    !segment ||
    !serviceLaneScope ||
    !pricingActionId ||
    quotedRevenueUsd === null ||
    invoicedRevenueUsd === null ||
    eligibleRevenueUsd === null
  ) {
    return null;
  }
  return {
    customerCohort,
    segment,
    serviceLaneScope,
    pricingActionId,
    quotedRevenueUsd,
    invoicedRevenueUsd,
    eligibleRevenueUsd,
  };
}

function parsePricingStructureMixShiftEvidenceFacts(
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): PricingStructureMixShiftEvidenceFacts | null {
  const artifact = artifacts.find(
    (candidate) =>
      candidate.schemaVersion === ASK_HELIX_PRICING_REALIZATION_DATA_LAKE_SCHEMA_VERSION,
  );
  const content = asRecord(artifact?.contentJson);
  const pricingStructureMix = asRecord(content?.pricingStructureMix);
  const pricingStructureSourceRef = stringOrNull(pricingStructureMix?.sourceRef);
  if (
    !artifact ||
    artifact.sourceClass !== 'connector-derived' ||
    stringOrNull(pricingStructureMix?.evidenceLabel) !== 'observed_feed' ||
    stringOrNull(pricingStructureMix?.sourceSystemRef) !==
      PRICING_REALIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF ||
    !pricingStructureSourceRef ||
    pricingStructureSourceRef !== stringOrNull(artifact.sourceRef)
  ) {
    return null;
  }
  const rowsRaw = pricingStructureMix?.rows;
  if (!Array.isArray(rowsRaw)) return null;
  const rows: PricingStructureMixShiftMarginContributionInput[] = [];
  for (const raw of rowsRaw) {
    const row = asRecord(raw);
    if (!row) return null;
    const period = stringOrNull(row.period);
    const segment = stringOrNull(row.segment);
    const totalNetRevenueUsd = finiteNumberOrNull(row.totalNetRevenueUsd);
    const baselineSpotMixPct = finiteNumberOrNull(row.baselineSpotMixPct);
    const currentSpotMixPct = finiteNumberOrNull(row.currentSpotMixPct);
    const baselineContractMixPct = finiteNumberOrNull(row.baselineContractMixPct);
    const currentContractMixPct = finiteNumberOrNull(row.currentContractMixPct);
    const spotNetRevenueMarginPct = finiteNumberOrNull(row.spotNetRevenueMarginPct);
    const contractNetRevenueMarginPct = finiteNumberOrNull(row.contractNetRevenueMarginPct);
    if (
      !period ||
      !segment ||
      totalNetRevenueUsd === null ||
      baselineSpotMixPct === null ||
      currentSpotMixPct === null ||
      baselineContractMixPct === null ||
      currentContractMixPct === null ||
      spotNetRevenueMarginPct === null ||
      contractNetRevenueMarginPct === null
    ) {
      return null;
    }
    rows.push({
      period,
      segment,
      totalNetRevenueUsd,
      baselineSpotMixPct,
      currentSpotMixPct,
      baselineContractMixPct,
      currentContractMixPct,
      spotNetRevenueMarginPct,
      contractNetRevenueMarginPct,
      reportingCutAsOf: artifact?.reportingCutAsOf,
    });
  }
  return { rows };
}

function parsePricingDiscountLeakageEvidenceFacts(
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): PricingDiscountLeakageEvidenceFacts | null {
  if (artifacts.length !== 2) return null;
  const policyArtifact = artifacts.find(
    (artifact) =>
      artifact.schemaVersion ===
      ASK_HELIX_PRICING_DISCOUNT_POLICY_AUTHORITY_SCHEMA_VERSION,
  );
  const transactionArtifact = artifacts.find(
    (artifact) =>
      artifact.schemaVersion ===
      ASK_HELIX_PRICING_DISCOUNT_TRANSACTION_SCHEMA_VERSION,
  );
  if (!policyArtifact || !transactionArtifact) return null;
  const policyContent = discountArtifactPayload(policyArtifact.contentJson);
  const transactionContent = discountArtifactPayload(transactionArtifact.contentJson);
  const authorityBands = parseDiscountAuthorityBands(policyContent?.bands);
  const transactions = parseDiscountTransactions(transactionContent?.transactions);
  if (!authorityBands || !transactions) return null;
  return {
    authorityBands,
    transactions,
    reportingCutAsOf:
      stringOrNull(transactionContent?.reportingCutAsOf) ??
      stringOrNull(policyContent?.reportingCutAsOf) ??
      stringOrNull(transactionArtifact.reportingCutAsOf) ??
      stringOrNull(policyArtifact.reportingCutAsOf),
    controlTotals: parseDiscountControlTotals(transactionContent?.controlTotals),
  };
}

function discountArtifactPayload(raw: unknown): Record<string, unknown> | null {
  const content = asRecord(raw);
  if (!content) return null;
  return asRecord(content.content) ?? content;
}

function parseDiscountAuthorityBands(
  value: unknown,
): PricingDiscountAuthorityBandInput[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const rows: PricingDiscountAuthorityBandInput[] = [];
  for (const raw of value) {
    const row = asRecord(raw);
    const bandId = stringOrNull(row?.bandId);
    const maxDiscountPctOfList = finiteNumberOrNull(row?.maxDiscountPctOfList);
    if (!bandId || maxDiscountPctOfList === null) return null;
    rows.push({ bandId, maxDiscountPctOfList });
  }
  return rows;
}

function parseDiscountTransactions(
  value: unknown,
): PricingDiscountTransactionInput[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const rows: PricingDiscountTransactionInput[] = [];
  for (const raw of value) {
    const row = asRecord(raw);
    const rowId = stringOrNull(row?.rowId);
    const segment = stringOrNull(row?.segment);
    const rep = stringOrNull(row?.rep);
    const authorityBandId = stringOrNull(row?.authorityBandId);
    const listRevenueUsd = finiteNumberOrNull(row?.listRevenueUsd);
    const netRevenueUsd = finiteNumberOrNull(row?.netRevenueUsd);
    if (
      !rowId ||
      !segment ||
      !rep ||
      !authorityBandId ||
      listRevenueUsd === null ||
      netRevenueUsd === null
    ) {
      return null;
    }
    rows.push({
      rowId,
      segment,
      rep,
      authorityBandId,
      listRevenueUsd,
      netRevenueUsd,
      discountUsd: finiteNumberOrNull(row?.discountUsd),
      offPolicyLeakageUsd: finiteNumberOrNull(row?.offPolicyLeakageUsd),
    });
  }
  return rows;
}

function parseDiscountControlTotals(
  value: unknown,
): PricingDiscountLeakageEvidenceFacts['controlTotals'] {
  const raw = asRecord(value);
  if (!raw) return null;
  const totalListRevenueUsd = finiteNumberOrNull(raw.totalListRevenueUsd);
  const totalNetRevenueUsd = finiteNumberOrNull(raw.totalNetRevenueUsd);
  const totalDiscountUsd = finiteNumberOrNull(raw.totalDiscountUsd);
  const totalWithinPolicyDiscountUsd = finiteNumberOrNull(
    raw.totalWithinPolicyDiscountUsd,
  );
  const totalOffPolicyLeakageUsd = finiteNumberOrNull(
    raw.totalOffPolicyLeakageUsd,
  );
  const offPolicyLeakageRatePct = finiteNumberOrNull(raw.offPolicyLeakageRatePct);
  if (
    totalListRevenueUsd === null ||
    totalNetRevenueUsd === null ||
    totalDiscountUsd === null ||
    totalWithinPolicyDiscountUsd === null ||
    totalOffPolicyLeakageUsd === null ||
    offPolicyLeakageRatePct === null
  ) {
    return null;
  }
  return {
    totalListRevenueUsd,
    totalNetRevenueUsd,
    totalDiscountUsd,
    totalWithinPolicyDiscountUsd,
    totalOffPolicyLeakageUsd,
    offPolicyLeakageRatePct,
  };
}

function sameDiscountBands(
  inputBands: readonly PricingDiscountAuthorityBandInput[],
  evidenceBands: readonly PricingDiscountAuthorityBandInput[],
): boolean {
  if (inputBands.length !== evidenceBands.length) return false;
  const evidenceById = new Map(evidenceBands.map((band) => [band.bandId, band]));
  return inputBands.every((band) => {
    const evidence = evidenceById.get(band.bandId);
    return !!evidence && sameNumber(band.maxDiscountPctOfList, evidence.maxDiscountPctOfList);
  });
}

function sameDiscountTransactions(
  inputRows: readonly PricingDiscountTransactionInput[],
  evidenceRows: readonly PricingDiscountTransactionInput[],
): boolean {
  if (inputRows.length !== evidenceRows.length) return false;
  const evidenceById = new Map(evidenceRows.map((row) => [row.rowId, row]));
  return inputRows.every((row) => {
    const evidence = evidenceById.get(row.rowId);
    return (
      !!evidence &&
      row.segment === evidence.segment &&
      row.rep === evidence.rep &&
      row.authorityBandId === evidence.authorityBandId &&
      sameNumber(row.listRevenueUsd, evidence.listRevenueUsd) &&
      sameNumber(row.netRevenueUsd, evidence.netRevenueUsd) &&
      sameNullableNumber(row.discountUsd, evidence.discountUsd) &&
      sameNullableNumber(row.offPolicyLeakageUsd, evidence.offPolicyLeakageUsd)
    );
  });
}

function sameNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
): boolean {
  const left = Number.isFinite(a ?? Number.NaN) ? Number(a) : null;
  const right = Number.isFinite(b ?? Number.NaN) ? Number(b) : null;
  if (left === null || right === null) return left === right;
  return sameNumber(left, right);
}

function executeStrategicExecutionCapacityUtilization(
  input: StrategicExecutionCapacityUtilizationInput,
): ApprovedFormulaResult<StrategicExecutionCapacityUtilizationOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const record = input as Partial<StrategicExecutionCapacityUtilizationInput>;

  if (!isFiniteNumber(record.requiredCapacityFte)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'requiredCapacityFte is required',
    };
  }

  if (!isFiniteNumber(record.availableCapacityFte)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'availableCapacityFte is required',
    };
  }

  if (record.requiredCapacityFte <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'requiredCapacityFte must be greater than zero',
    };
  }

  if (record.availableCapacityFte < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'availableCapacityFte must be zero or greater',
    };
  }

  const utilizationRatio = roundTo(record.availableCapacityFte / record.requiredCapacityFte, 4);
  const utilizationPct = roundTo(utilizationRatio * 100, 2);
  const capacityGapFte = roundTo(Math.max(record.requiredCapacityFte - record.availableCapacityFte, 0), 2);

  return {
    status: 'ok',
    output: Object.freeze({
      requiredCapacityFte: record.requiredCapacityFte,
      availableCapacityFte: record.availableCapacityFte,
      capacityGapFte,
      utilizationRatio,
      utilizationPct,
      statusBand: capacityStatusBand(utilizationRatio),
    }),
  };
}

function executeStrategicExecutionCapacityBaselineTotals(
  input: StrategicExecutionCapacityBaselineTotalsInput,
): ApprovedFormulaResult<StrategicExecutionCapacityBaselineTotalsOutput> {
  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') return recordsResult;

  return {
    status: 'ok',
    output: Object.freeze(deriveStrategicExecutionCapacityBaselineTotals(recordsResult.records)),
  };
}

function executeStrategicExecutionCapacityRiskScore(
  input: StrategicExecutionCapacityRiskScoreInput,
): ApprovedFormulaResult<StrategicExecutionCapacityRiskScoreOutput> {
  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') return recordsResult;

  if (!isFiniteNumber(input.capacityGapPct) || input.capacityGapPct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'capacityGapPct must be a finite non-negative number',
    };
  }

  return {
    status: 'ok',
    output: Object.freeze(deriveStrategicExecutionCapacityRiskScore(input)),
  };
}

function executeStrategicExecutionCapacityWarning(
  input: StrategicExecutionCapacityWarningInput,
): ApprovedFormulaResult<StrategicExecutionCapacityWarningOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') return recordsResult;

  const requiredNumbers = [
    input.capacityGapFte,
    input.capacityGapPct,
    input.blockedInitiativeCount,
    input.strategicExecutionCapacityRiskScore,
  ];
  if (requiredNumbers.some((value) => !Number.isFinite(value) || value < 0)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Warning inputs must be finite non-negative numbers',
    };
  }

  return {
    status: 'ok',
    output: Object.freeze(deriveStrategicExecutionCapacityWarning(input)),
  };
}

function executeStrategicExecutionCapacityDriftDetection(
  input: StrategicExecutionCapacityDriftDetectionInput,
): ApprovedFormulaResult<StrategicExecutionCapacityDriftDetectionOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const record = input as Partial<StrategicExecutionCapacityDriftDetectionInput>;
  if (!record.metricKey || typeof record.metricKey !== 'string') {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'metricKey is required',
    };
  }
  if (!isFiniteNumber(record.baselineValue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'baselineValue is required',
    };
  }
  if (!isFiniteNumber(record.targetValue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'targetValue is required',
    };
  }
  if (!isFiniteNumber(record.actualValue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'actualValue is required',
    };
  }
  if (record.direction !== 'increase' && record.direction !== 'decrease') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'direction must be increase or decrease',
    };
  }

  const baselineMs = parseIsoDateMs(record.baselineAsOf);
  const actualMs = parseIsoDateMs(record.actualAsOf);
  const targetMs = parseIsoDateMs(record.targetAt);
  if (baselineMs === null || actualMs === null || targetMs === null) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'baselineAsOf, actualAsOf, and targetAt must be valid ISO dates',
    };
  }
  if (targetMs <= baselineMs) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'targetAt must be after baselineAsOf',
    };
  }

  const warningThresholdRatio = normalizeThresholdRatio(
    record.warningThresholdRatio,
    0.05,
  );
  const driftThresholdRatio = normalizeThresholdRatio(
    record.driftThresholdRatio,
    0.1,
  );
  const amendmentThresholdRatio = normalizeThresholdRatio(
    record.amendmentThresholdRatio,
    0.2,
  );
  if (
    warningThresholdRatio === null ||
    driftThresholdRatio === null ||
    amendmentThresholdRatio === null ||
    !(warningThresholdRatio <= driftThresholdRatio) ||
    !(driftThresholdRatio <= amendmentThresholdRatio)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'threshold ratios must be finite non-negative values in warning <= drift <= amendment order',
    };
  }

  const elapsedRatio = Math.max(
    0,
    Math.min(1, (actualMs - baselineMs) / (targetMs - baselineMs)),
  );
  const expectedValue = roundTo(
    record.baselineValue + (record.targetValue - record.baselineValue) * elapsedRatio,
    4,
  );
  const modeledDelta = Math.abs(record.targetValue - record.baselineValue);
  const normalizationFloor = Math.max(
    modeledDelta,
    Math.abs(expectedValue),
    Math.abs(record.targetValue),
    Math.abs(record.baselineValue),
    1,
  );
  const behindAmount =
    record.direction === 'increase'
      ? expectedValue - record.actualValue
      : record.actualValue - expectedValue;
  const behindRatio = roundTo(
    behindAmount > 0 ? behindAmount / normalizationFloor : 0,
    4,
  );
  const driftStatus = classifyStrategicExecutionCapacityDrift(
    behindRatio,
    warningThresholdRatio,
    driftThresholdRatio,
    amendmentThresholdRatio,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      metricKey: record.metricKey.trim(),
      baselineValue: record.baselineValue,
      targetValue: record.targetValue,
      actualValue: record.actualValue,
      expectedValue,
      deltaFromExpected: roundTo(record.actualValue - expectedValue, 4),
      behindRatio,
      driftStatus,
      driftStatusCode: strategicExecutionCapacityDriftStatusCode(driftStatus),
      triggeredRuleId: strategicExecutionCapacityDriftRuleId(driftStatus),
    }),
  };
}

function executeRealizationProjectedMiss(
  input: RealizationProjectedMissInput,
): ApprovedFormulaResult<RealizationProjectedMissOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const record = input as Partial<RealizationProjectedMissInput>;
  if (!record.metricKey || typeof record.metricKey !== 'string') {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'metricKey is required',
    };
  }
  if (!isFiniteNumber(record.expectedValue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'expectedValue is required',
    };
  }
  if (!isFiniteNumber(record.actualValue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'actualValue is required',
    };
  }
  if (record.direction !== 'increase' && record.direction !== 'decrease') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'direction must be increase or decrease',
    };
  }
  if (!isFiniteNumber(record.valuePerUnit) || record.valuePerUnit <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'valuePerUnit is required',
    };
  }

  const annualizationFactor = record.annualizationFactor ?? 1;
  if (!isFiniteNumber(annualizationFactor) || annualizationFactor <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'annualizationFactor must be a finite positive number',
    };
  }

  const rawBehindUnits =
    record.direction === 'increase'
      ? record.expectedValue - record.actualValue
      : record.actualValue - record.expectedValue;
  const behindUnits = roundTo(Math.max(0, rawBehindUnits), 4);
  return {
    status: 'ok',
    output: Object.freeze({
      metricKey: record.metricKey.trim(),
      expectedValue: record.expectedValue,
      actualValue: record.actualValue,
      direction: record.direction,
      behindUnits,
      valuePerUnit: record.valuePerUnit,
      annualizationFactor,
      projectedMissUsd: roundTo(behindUnits * record.valuePerUnit * annualizationFactor, 2),
    }),
  };
}

function executeRealizationPaceReceipt(
  input: RealizationPaceReceiptInput,
): ApprovedFormulaResult<RealizationPaceReceiptOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const record = input as Partial<RealizationPaceReceiptInput>;
  const metricKey = record.metricKey;
  if (!metricKey || typeof metricKey !== 'string') {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'metricKey is required',
    };
  }
  const direction = record.direction;
  if (direction !== 'increase' && direction !== 'decrease') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'direction must be increase or decrease',
    };
  }

  const baselineValue = record.baselineValue;
  const targetValue = record.targetValue;
  const expectedValue = record.expectedValue;
  const actualValue = record.actualValue;
  if (
    !isFiniteNumber(baselineValue) ||
    !isFiniteNumber(targetValue) ||
    !isFiniteNumber(expectedValue) ||
    !isFiniteNumber(actualValue)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'baselineValue, targetValue, expectedValue, and actualValue are required',
    };
  }

  const reportingCutAsOf = record.reportingCutAsOf;
  const targetAt = record.targetAt;
  const reportingCutAsOfMs = Date.parse(reportingCutAsOf ?? '');
  const targetAtMs = Date.parse(targetAt ?? '');
  if (
    typeof reportingCutAsOf !== 'string' ||
    typeof targetAt !== 'string' ||
    !Number.isFinite(reportingCutAsOfMs) ||
    !Number.isFinite(targetAtMs)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'reportingCutAsOf and targetAt must be valid dates',
    };
  }

  const expectedDirection =
    direction === 'increase'
      ? targetValue > baselineValue
      : targetValue < baselineValue;
  if (!expectedDirection) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'baselineValue and targetValue must move in the declared direction',
    };
  }

  const directionSign = direction === 'increase' ? 1 : -1;
  const remainingWeeks = (targetAtMs - reportingCutAsOfMs) / (7 * 24 * 60 * 60 * 1000);
  const remainingMovement = Math.max(0, directionSign * (targetValue - actualValue));
  const behindUnits = Math.max(0, directionSign * (expectedValue - actualValue));
  const commitmentOverdue = targetAtMs <= reportingCutAsOfMs;
  const requiredTotalPacePerWeek = remainingMovement / remainingWeeks;
  const requiredCatchUpIncrementPerWeek = behindUnits / remainingWeeks;

  const observationWindow = record.observationWindow;
  if (!observationWindow) {
    return {
      status: 'ok',
      output: Object.freeze({
        metricKey: metricKey.trim(),
        direction,
        baselineValue,
        targetValue,
        expectedValue,
        actualValue,
        reportingCutAsOf,
        targetAt,
        behindUnits: roundTo(behindUnits, 4),
        actualPacePerWeek: null,
        requiredTotalPacePerWeek:
          commitmentOverdue ? null : roundTo(requiredTotalPacePerWeek, 4),
        requiredCatchUpIncrementPerWeek:
          commitmentOverdue ? null : roundTo(requiredCatchUpIncrementPerWeek, 4),
        paceCoverageRatio: null,
        projectedLandAt: null,
        weeksUntilCommittedMiss: null,
        gapCodes: Object.freeze(
          commitmentOverdue
            ? ['actual_pace_unavailable', 'commitment_overdue']
            : ['actual_pace_unavailable', 'projected_landing_unavailable'],
        ),
      }),
    };
  }
  if (typeof observationWindow !== 'object' || !observationWindow.policyId?.trim()) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'observationWindow with policyId is required',
    };
  }
  if (
    !isFiniteNumber(observationWindow.firstActualValue) ||
    !isFiniteNumber(observationWindow.latestActualValue)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'observationWindow actual values are required',
    };
  }

  const observationFrom = Date.parse(observationWindow.fromAsOf);
  const observationTo = Date.parse(observationWindow.toAsOf);
  if (
    !Number.isFinite(observationFrom) ||
    !Number.isFinite(observationTo) ||
    observationTo <= observationFrom ||
    observationTo !== reportingCutAsOfMs
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'observationWindow must end at reportingCutAsOf after its start',
    };
  }
  if (observationWindow.latestActualValue !== actualValue) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'observationWindow latest actual must equal actualValue',
    };
  }

  const elapsedWeeks = (observationTo - observationFrom) / (7 * 24 * 60 * 60 * 1000);
  const actualPacePerWeek =
    (directionSign * (observationWindow.latestActualValue - observationWindow.firstActualValue)) /
    elapsedWeeks;
  if (commitmentOverdue) {
    return {
      status: 'ok',
      output: Object.freeze({
        metricKey: metricKey.trim(),
        direction,
        baselineValue,
        targetValue,
        expectedValue,
        actualValue,
        reportingCutAsOf,
        targetAt,
        behindUnits: roundTo(behindUnits, 4),
        actualPacePerWeek: actualPacePerWeek === 0 ? 0 : roundTo(actualPacePerWeek, 4),
        requiredTotalPacePerWeek: null,
        requiredCatchUpIncrementPerWeek: null,
        paceCoverageRatio: null,
        projectedLandAt: null,
        weeksUntilCommittedMiss: null,
        gapCodes: Object.freeze(['commitment_overdue']),
      }),
    };
  }
  if (remainingMovement === 0) {
    return {
      status: 'ok',
      output: Object.freeze({
        metricKey: metricKey.trim(),
        direction,
        baselineValue,
        targetValue,
        expectedValue,
        actualValue,
        reportingCutAsOf,
        targetAt,
        behindUnits: roundTo(behindUnits, 4),
        actualPacePerWeek: actualPacePerWeek === 0 ? 0 : roundTo(actualPacePerWeek, 4),
        requiredTotalPacePerWeek: 0,
        requiredCatchUpIncrementPerWeek: 0,
        paceCoverageRatio: null,
        projectedLandAt: null,
        weeksUntilCommittedMiss: null,
        gapCodes: Object.freeze(['target_already_achieved']),
      }),
    };
  }
  const paceCoverageRatio = actualPacePerWeek / requiredTotalPacePerWeek;
  const projectedLandAt =
    actualPacePerWeek > 0
      ? new Date(reportingCutAsOfMs + (remainingMovement / actualPacePerWeek) * 7 * 24 * 60 * 60 * 1000).toISOString()
      : null;
  const projectedLandAtMs = projectedLandAt ? Date.parse(projectedLandAt) : NaN;
  const gapCodes = Object.freeze(
    actualPacePerWeek > 0 ? [] : ['projected_landing_unavailable'],
  );

  return {
    status: 'ok',
    output: Object.freeze({
      metricKey: metricKey.trim(),
      direction,
      baselineValue,
      targetValue,
      expectedValue,
      actualValue,
      reportingCutAsOf,
      targetAt,
      behindUnits: roundTo(behindUnits, 4),
      actualPacePerWeek: actualPacePerWeek === 0 ? 0 : roundTo(actualPacePerWeek, 4),
      requiredTotalPacePerWeek: roundTo(requiredTotalPacePerWeek, 4),
      requiredCatchUpIncrementPerWeek: roundTo(requiredCatchUpIncrementPerWeek, 4),
      paceCoverageRatio: roundTo(paceCoverageRatio, 4),
      projectedLandAt,
      weeksUntilCommittedMiss:
        Number.isFinite(projectedLandAtMs) && projectedLandAtMs > targetAtMs
          ? roundTo(remainingWeeks, 4)
          : null,
      gapCodes,
    }),
  };
}

function resolveRealizationProportionBasis(
  realizedUsd: number,
  basis: RealizationProportionBasisInput,
): RealizationProportionBasisOutput {
  const basisId = basis.basisId.trim();
  const components = basis.denominatorComponentsUsd ?? null;
  const frozenComponents = components
    ? Object.freeze(components.map((component) => roundTo(component, 2)))
    : null;
  // ONE rounding law, both sides of the ratio: every operand this basis publishes is
  // resolved to cents FIRST, and the share and remainder are computed from exactly those
  // published cents — never from the raw inputs behind them. A composed base totals the
  // SAME rounded components it publishes, so [0.004, 0.004] renders [0, 0] against a base
  // of 0 rather than beside a total of 0.01 no reader can add up. A supplied base is
  // rounded before it is judged and divided, so 0.006 realized against 0.01 publishes
  // 0.01 of 0.01 at 100% instead of a 60% share against operands that read 100%, and a
  // sub-cent base of 0.004 rounds away into the non-positive withhold instead of carrying
  // a 150% share against a published denominator of 0. Cent-precision inputs are
  // unaffected — rounding an already-cent figure returns it unchanged — so every governed
  // figure this formula already resolves is unmoved.
  const rawDenominator = frozenComponents
    ? frozenComponents.reduce((total, component) => total + component, 0)
    : basis.denominatorUsd;
  if (rawDenominator === null || rawDenominator === undefined) {
    return Object.freeze({
      basisId,
      denominatorUsd: null,
      denominatorComponentsUsd: frozenComponents,
      realizationSharePct: null,
      unrealizedUsd: null,
      withheldReasonCode: 'denominator_not_supplied' as const,
    });
  }
  if (!isFiniteNumber(rawDenominator)) {
    return Object.freeze({
      basisId,
      denominatorUsd: null,
      denominatorComponentsUsd: frozenComponents,
      realizationSharePct: null,
      unrealizedUsd: null,
      withheldReasonCode: 'denominator_not_positive' as const,
    });
  }
  const denominatorUsd = roundTo(rawDenominator, 2);
  if (denominatorUsd <= 0) {
    return Object.freeze({
      basisId,
      denominatorUsd,
      denominatorComponentsUsd: frozenComponents,
      realizationSharePct: null,
      unrealizedUsd: null,
      withheldReasonCode: 'denominator_not_positive' as const,
    });
  }
  return Object.freeze({
    basisId,
    denominatorUsd,
    denominatorComponentsUsd: frozenComponents,
    realizationSharePct: roundTo((realizedUsd / denominatorUsd) * 100, 4),
    unrealizedUsd: roundTo(Math.max(0, denominatorUsd - realizedUsd), 2),
    withheldReasonCode: null,
  });
}

function executeRealizationProportion(
  input: RealizationProportionInput,
): ApprovedFormulaResult<RealizationProportionOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const realizedUsd = input.realizedUsd;
  if (!isFiniteNumber(realizedUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'realizedUsd is required',
    };
  }
  if (realizedUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'realizedUsd must not be negative',
    };
  }
  const bases = input.bases;
  if (!Array.isArray(bases) || bases.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'at least one named basis is required',
    };
  }
  const basisIds: string[] = [];
  for (const basis of bases) {
    if (!basis || typeof basis !== 'object' || typeof basis.basisId !== 'string') {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'every basis must declare a basisId',
      };
    }
    const basisId = basis.basisId.trim();
    if (!basisId) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'every basis must declare a basisId',
      };
    }
    if (basisIds.includes(basisId)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `basisId ${basisId} is declared more than once`,
      };
    }
    const components = basis.denominatorComponentsUsd;
    if (components !== null && components !== undefined) {
      if (basis.denominatorUsd !== null && basis.denominatorUsd !== undefined) {
        return {
          status: 'blocked',
          reasonCode: 'invalid_formula_input',
          message: `basis ${basisId} must declare denominatorUsd or denominatorComponentsUsd, never both`,
        };
      }
      if (
        !Array.isArray(components) ||
        components.length === 0 ||
        !components.every((component) => isFiniteNumber(component))
      ) {
        return {
          status: 'blocked',
          reasonCode: 'invalid_formula_input',
          message: `basis ${basisId} declared denominatorComponentsUsd that are not all finite numbers`,
        };
      }
    }
    basisIds.push(basisId);
  }

  // The numerator is resolved to cents ONCE, here, and that single published figure is
  // what every basis divides — so the realized dollars a reader sees are the realized
  // dollars each share was computed from.
  const publishedRealizedUsd = roundTo(realizedUsd, 2);
  const resolved = bases.map((basis) =>
    resolveRealizationProportionBasis(publishedRealizedUsd, basis),
  );
  const withheldBasisCount = resolved.filter(
    (basis) => basis.withheldReasonCode !== null,
  ).length;
  // Every basis withheld is still a resolved-per-basis answer, not a failed execution:
  // each basis keeps the reason its own denominator failed. Collapsing them here would
  // force the caller to invent one, and an invented reason can contradict the input.
  return {
    status: 'ok',
    output: Object.freeze({
      realizedUsd: publishedRealizedUsd,
      bases: Object.freeze(resolved),
      resolvedBasisCount: resolved.length - withheldBasisCount,
      withheldBasisCount,
    }),
  };
}

function executeMaDealGrossSynergyByBand(
  input: MaDealGrossSynergyByBandInput,
): ApprovedFormulaResult<MaDealGrossSynergyByBandOutput> {
  if (!input || typeof input !== 'object' || !Array.isArray(input.commitments)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'commitments are required',
    };
  }
  if (!isMaDealBand(input.band)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'band must be conservative, base, or stretch',
    };
  }
  try {
    return {
      status: 'ok',
      output: calculateMaDealGrossSynergyByBand(input),
    };
  } catch (error) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        error instanceof Error ? error.message : 'Deal commitment bands are invalid',
    };
  }
}

function executeDelayeringNetEbitdaOpportunity(
  input: DelayeringNetEbitdaOpportunityInput,
): ApprovedFormulaResult<DelayeringNetEbitdaOpportunityOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const required = [
    input.baselineRunRateCostUsd,
    input.scenarioRunRateCostUsd,
    input.oneTimeSeveranceUsd,
    input.capacityCostAtRiskUsd,
  ];
  if (required.some((value) => !isFiniteNumber(value) || value < 0)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'baselineRunRateCostUsd, scenarioRunRateCostUsd, oneTimeSeveranceUsd, and capacityCostAtRiskUsd must be finite non-negative USD values',
    };
  }

  const netEbitda = computeNetEbitdaOpportunityUsd({
    grossRunRateSavingsUsd:
      input.baselineRunRateCostUsd - input.scenarioRunRateCostUsd,
    oneTimeSeveranceUsd: input.oneTimeSeveranceUsd,
    capacityCostAtRiskUsd: input.capacityCostAtRiskUsd,
  });

  return {
    status: 'ok',
    output: Object.freeze({
      grossRunRateSavingsUsd: netEbitda.grossRunRateSavingsUsd,
      oneTimeSeveranceUsd: netEbitda.oneTimeSeveranceUsd,
      capacityCostAtRiskUsd: netEbitda.capacityCostAtRiskUsd,
      delayeringNetEbitdaOpportunityUsd: netEbitda.netEbitdaOpportunityUsd,
      statusBand: ebitdaOpportunityStatusBand(
        netEbitda.netEbitdaOpportunityUsd,
      ),
    }),
  };
}

function executeCarrierTransportationCostOpportunity(
  input: CarrierTransportationCostOpportunityInput,
): ApprovedFormulaResult<CarrierTransportationCostOpportunityOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  if (
    !isFiniteNumber(input.baselineGrossMarginUsd) ||
    !isFiniteNumber(input.belowFloorRecoveryUsd) ||
    !isFiniteNumber(input.contractRateRecoveryUsd) ||
    !isFiniteNumber(input.carrierRateDeltaRecoveryUsd) ||
    input.belowFloorRecoveryUsd < 0 ||
    input.contractRateRecoveryUsd < 0 ||
    input.carrierRateDeltaRecoveryUsd < 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Transportation opportunity inputs must include finite baseline margin and non-negative recovery components',
    };
  }

  const belowFloorRecoveryUsd = roundTo(input.belowFloorRecoveryUsd, 2);
  const contractRateRecoveryUsd = roundTo(input.contractRateRecoveryUsd, 2);
  const carrierRateDeltaRecoveryUsd = roundTo(input.carrierRateDeltaRecoveryUsd, 2);
  const carrierCostOpportunityUsd =
    roundTo(belowFloorRecoveryUsd + contractRateRecoveryUsd + carrierRateDeltaRecoveryUsd, 2);

  return {
    status: 'ok',
    output: Object.freeze({
      baselineGrossMarginUsd: roundTo(input.baselineGrossMarginUsd, 2),
      belowFloorRecoveryUsd,
      contractRateRecoveryUsd,
      carrierRateDeltaRecoveryUsd,
      carrierCostOpportunityUsd,
      statusBand: opportunityStatusBand(carrierCostOpportunityUsd),
    }),
  };
}

function executeCarrierTransportationCostOpportunityScenario(
  input: CarrierTransportationCostOpportunityScenarioInput,
): ApprovedFormulaResult<CarrierTransportationCostOpportunityScenarioOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  if (
    !isFiniteNumber(input.baselineGrossMarginUsd) ||
    !isFiniteNumber(input.belowFloorRecoveryUsd) ||
    !isFiniteNumber(input.contractRateRecoveryUsd) ||
    !isFiniteNumber(input.carrierRateDeltaBaseSpendUsd) ||
    !isFiniteNumber(input.scenarioRateDeltaPct) ||
    !isFiniteNumber(input.expectedBaselineOpportunityUsd) ||
    input.belowFloorRecoveryUsd < 0 ||
    input.contractRateRecoveryUsd < 0 ||
    input.carrierRateDeltaBaseSpendUsd <= 0 ||
    input.expectedBaselineOpportunityUsd < 0 ||
    !stringOrNull(input.carrier)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Carrier scenario inputs require finite governed baseline values, a positive carrier-spend driver, a non-negative expected baseline, and a carrier',
    };
  }
  if (input.scenarioRateDeltaPct <= 0 || input.scenarioRateDeltaPct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'scenarioRateDeltaPct must be greater than zero and at most 100',
    };
  }

  const belowFloorRecoveryUsd = roundTo(input.belowFloorRecoveryUsd, 2);
  const contractRateRecoveryUsd = roundTo(input.contractRateRecoveryUsd, 2);
  const carrierRateDeltaBaseSpendUsd = roundTo(input.carrierRateDeltaBaseSpendUsd, 2);
  const baselineCarrierCostOpportunityUsd = roundTo(
    belowFloorRecoveryUsd + contractRateRecoveryUsd,
    2,
  );
  const scenarioCarrierRateDeltaRecoveryUsd = roundTo(
    carrierRateDeltaBaseSpendUsd * (input.scenarioRateDeltaPct / 100),
    2,
  );
  const scenarioCarrierCostOpportunityUsd = roundTo(
    baselineCarrierCostOpportunityUsd + scenarioCarrierRateDeltaRecoveryUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      baselineGrossMarginUsd: roundTo(input.baselineGrossMarginUsd, 2),
      belowFloorRecoveryUsd,
      contractRateRecoveryUsd,
      carrierRateDeltaBaseSpendUsd,
      scenarioRateDeltaPct: roundTo(input.scenarioRateDeltaPct, 4),
      baselineCarrierCostOpportunityUsd,
      baselineOpportunityVarianceUsd: roundTo(
        Math.abs(
          baselineCarrierCostOpportunityUsd - input.expectedBaselineOpportunityUsd,
        ),
        2,
      ),
      scenarioCarrierRateDeltaRecoveryUsd,
      scenarioCarrierCostOpportunityUsd,
      operatingIncomeDeltaUsd: scenarioCarrierRateDeltaRecoveryUsd,
      statusBand: opportunityStatusBand(scenarioCarrierCostOpportunityUsd),
    }),
  };
}

function executeDelayeringOrgHealthWarningLevel(
  input: DelayeringOrgHealthWarningInput,
): ApprovedFormulaResult<DelayeringOrgHealthWarningOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  if (validateDelayeringOrgHealthNumbers(input)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'Org health inputs and targets must be finite non-negative values, with managerCount greater than zero and maxThinSpanManagerRatio between zero and one',
    };
  }

  return {
    status: 'ok',
    output: Object.freeze(deriveDelayeringOrgHealthWarning(input)),
  };
}

function parseStrategicExecutionCapacityFormulaRecords(
  input: { records?: readonly StrategicExecutionCapacityFormulaRecord[] } | null | undefined,
):
  | Readonly<{ status: 'ok'; records: readonly StrategicExecutionCapacityFormulaRecord[] }>
  | ApprovedFormulaBlockedResult {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const records = Array.isArray(input.records) ? input.records : null;
  if (!records || records.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'records are required',
    };
  }

  const seen = new Set<string>();
  for (const record of records) {
    if (
      !record ||
      typeof record !== 'object' ||
      typeof record.initiativeId !== 'string' ||
      record.initiativeId.trim().length === 0 ||
      seen.has(record.initiativeId) ||
      !isFiniteNumber(record.capacityRequiredFte) ||
      !isFiniteNumber(record.capacityAvailableFte) ||
      record.capacityRequiredFte < 0 ||
      record.capacityAvailableFte < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'records must contain unique initiatives with finite non-negative capacity values',
      };
    }
    seen.add(record.initiativeId);
  }

  return { status: 'ok', records };
}

function isStrategicExecutionAtRiskRecord(
  record: StrategicExecutionCapacityFormulaRecord,
): boolean {
  const status = (record.currentStatus ?? '').toLowerCase();
  if (status && status !== 'on_track' && status !== 'complete') return true;
  return Boolean(record.blockedReason && record.blockedReason.trim());
}

function isStrategicExecutionBlockedRecord(
  record: StrategicExecutionCapacityFormulaRecord,
): boolean {
  return (
    (record.currentStatus ?? '').toLowerCase() === 'blocked' ||
    Boolean(record.blockedReason && record.blockedReason.trim())
  );
}

function deriveStrategicExecutionCapacityBaselineTotals(
  records: readonly StrategicExecutionCapacityFormulaRecord[],
): StrategicExecutionCapacityBaselineTotalsOutput {
  const totalCapacityRequiredFte = roundTo(
    records.reduce((sum, record) => sum + record.capacityRequiredFte, 0),
    1,
  );
  const totalCapacityAvailableFte = roundTo(
    records.reduce((sum, record) => sum + record.capacityAvailableFte, 0),
    1,
  );
  const capacityGapFte = roundTo(
    Math.max(0, totalCapacityRequiredFte - totalCapacityAvailableFte),
    1,
  );
  const capacityGapPct = roundTo(
    totalCapacityRequiredFte > 0 ? (capacityGapFte / totalCapacityRequiredFte) * 100 : 0,
    1,
  );

  return {
    initiativeCount: records.length,
    totalCapacityRequiredFte,
    totalCapacityAvailableFte,
    capacityGapFte,
    capacityGapPct,
  };
}

function deriveStrategicExecutionCapacityRiskScore(
  input: StrategicExecutionCapacityRiskScoreInput,
): StrategicExecutionCapacityRiskScoreOutput {
  const initiativesAtRiskCount = input.records.filter(isStrategicExecutionAtRiskRecord).length;
  const blockedInitiativeCount = input.records.filter(isStrategicExecutionBlockedRecord).length;
  const strategicExecutionCapacityRiskScore = roundTo(
    Math.min(
      100,
      Math.max(0, input.capacityGapPct * 1.6 + initiativesAtRiskCount * 8 + blockedInitiativeCount * 6),
    ),
    1,
  );

  return {
    initiativesAtRiskCount,
    blockedInitiativeCount,
    strategicExecutionCapacityRiskScore,
  };
}

function deriveStrategicExecutionCapacityWarning(
  input: StrategicExecutionCapacityWarningInput,
): StrategicExecutionCapacityWarningOutput {
  const triggeredRuleIds: string[] = [];
  if (input.capacityGapFte > 0 || input.strategicExecutionCapacityRiskScore >= 50) {
    triggeredRuleIds.push('strategic_execution_capacity_watch');
  }
  if (input.capacityGapPct >= 15 || input.strategicExecutionCapacityRiskScore >= 70) {
    triggeredRuleIds.push('strategic_execution_capacity_decision_required');
  }
  if (input.blockedInitiativeCount > 0) {
    triggeredRuleIds.push('strategic_execution_blocker_watch');
  }

  const warningLevel =
    input.capacityGapPct >= 15 ||
    input.blockedInitiativeCount > 0 ||
    input.strategicExecutionCapacityRiskScore >= 70
      ? 'decision_required'
      : triggeredRuleIds.length > 0
        ? 'watch'
        : 'none';

  return {
    warningLevel,
    triggeredRuleIds: Object.freeze(triggeredRuleIds),
  };
}

function classifyStrategicExecutionCapacityDrift(
  behindRatio: number,
  warningThresholdRatio: number,
  driftThresholdRatio: number,
  amendmentThresholdRatio: number,
): StrategicExecutionCapacityDriftStatus {
  if (behindRatio >= amendmentThresholdRatio) return 'amendment_required';
  if (behindRatio >= driftThresholdRatio) return 'drifting';
  if (behindRatio >= warningThresholdRatio) return 'watch';
  return 'on_track';
}

function strategicExecutionCapacityDriftStatusCode(
  status: StrategicExecutionCapacityDriftStatus,
): number {
  if (status === 'watch') return 1;
  if (status === 'drifting') return 2;
  if (status === 'amendment_required') return 3;
  return 0;
}

function strategicExecutionCapacityDriftRuleId(
  status: StrategicExecutionCapacityDriftStatus,
): string {
  if (status === 'on_track') return 'strategic_execution_capacity_drift_on_track';
  return `strategic_execution_capacity_drift_${status}`;
}

function normalizeThresholdRatio(value: number | undefined, fallback: number): number | null {
  const raw = value ?? fallback;
  if (!isFiniteNumber(raw) || raw < 0 || raw > 1) return null;
  return raw;
}

function parseIsoDateMs(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  if (
    date.getUTCFullYear() !== Number(match[1]) ||
    date.getUTCMonth() + 1 !== Number(match[2]) ||
    date.getUTCDate() !== Number(match[3])
  ) {
    return null;
  }
  return date.getTime();
}

const strategicExecutionCapacityUtilizationReconciliationRule: ApprovedFormulaReconciliationRule<
  StrategicExecutionCapacityUtilizationInput,
  StrategicExecutionCapacityUtilizationOutput
> = Object.freeze({
  kind: 'input_output_evidence_reconciliation',
  ruleId: 'strategic_execution_capacity_utilization_reconciles_to_evidence_v1',
  description:
    'Formula input and output must reconcile to approved strategic execution capacity evidence before a numeric answer is citable.',
  validate: validateStrategicExecutionCapacityUtilizationOutputAgainstEvidence,
});

const strategicExecutionCapacityBaselineTotalsReconciliationRule: ApprovedFormulaReconciliationRule<
  StrategicExecutionCapacityBaselineTotalsInput,
  StrategicExecutionCapacityBaselineTotalsOutput
> = Object.freeze({
  kind: 'aggregate_baseline_totals_reconciliation',
  ruleId: 'strategic_execution_capacity_baseline_totals_reconciles_to_evidence_v1',
  description:
    'Baseline totals output must reconcile to approved strategic execution records before it can support a numeric answer.',
  validate: validateStrategicExecutionCapacityBaselineTotalsOutputAgainstEvidence,
});

const strategicExecutionCapacityRiskScoreReconciliationRule: ApprovedFormulaReconciliationRule<
  StrategicExecutionCapacityRiskScoreInput,
  StrategicExecutionCapacityRiskScoreOutput
> = Object.freeze({
  kind: 'derived_risk_score_reconciliation',
  ruleId: 'strategic_execution_capacity_risk_score_reconciles_to_evidence_v1',
  description:
    'Risk score output must reconcile to approved strategic execution record status, blocker, and capacity inputs.',
  validate: validateStrategicExecutionCapacityRiskOutputAgainstEvidence,
});

const strategicExecutionCapacityWarningReconciliationRule: ApprovedFormulaReconciliationRule<
  StrategicExecutionCapacityWarningInput,
  StrategicExecutionCapacityWarningOutput
> = Object.freeze({
  kind: 'threshold_warning_level_reconciliation',
  ruleId: 'strategic_execution_capacity_warning_level_reconciles_to_evidence_v1',
  description:
    'Warning level output must reconcile to approved capacity gap, blocker, and risk score inputs.',
  validate: validateStrategicExecutionCapacityWarningOutputAgainstEvidence,
});

const strategicExecutionCapacityDriftReconciliationRule: ApprovedFormulaReconciliationRule<
  StrategicExecutionCapacityDriftDetectionInput,
  StrategicExecutionCapacityDriftDetectionOutput
> = Object.freeze({
  kind: 'monitoring_drift_reconciliation',
  ruleId: 'strategic_execution_capacity_drift_detection_reconciles_to_evidence_v1',
  description:
    'Drift detection output must reconcile actual values to approved strategic execution capacity evidence and approved target math.',
  validate: validateStrategicExecutionCapacityDriftOutputAgainstEvidence,
});

const delayeringNetEbitdaOpportunityReconciliationRule: ApprovedFormulaReconciliationRule<
  DelayeringNetEbitdaOpportunityInput,
  DelayeringNetEbitdaOpportunityOutput
> = Object.freeze({
  kind: 'input_output_evidence_reconciliation',
  ruleId: 'delayering_net_ebitda_opportunity_reconciles_to_evidence_v1',
  description:
    'Net EBITDA opportunity must reconcile to approved org-cost baseline, scenario, severance, and capacity-at-risk evidence before it is citable.',
  validate: validateDelayeringNetEbitdaOpportunityOutputAgainstEvidence,
});

const workforceReductionNetEbitdaOpportunityReconciliationRule: ApprovedFormulaReconciliationRule<
  WorkforceReductionNetEbitdaOpportunityInput,
  WorkforceReductionNetEbitdaOpportunityOutput
> = Object.freeze({
  kind: 'input_output_evidence_reconciliation',
  ruleId:
    'workforce_reduction_net_ebitda_opportunity_reconciles_to_evidence_v1',
  description:
    'Workforce net EBITDA must reconcile to the exact approved run, scenario case, run-rate savings, one-time severance, and capacity-at-risk evidence.',
  validate: (input, output, artifacts) => {
    const inputBlock =
      validateWorkforceReductionNetEbitdaInputAgainstEvidence(
        input,
        artifacts,
    );
    if (inputBlock) return inputBlock;
    const expected = executeWorkforceReductionNetEbitdaOpportunity(input);
    const scenarioRun = parseWorkforceReductionFormulaScenarioRunEvidence(
      artifacts[0]?.contentJson,
    );
    const approvedCase = scenarioRun?.cases.find(
      (candidate) => candidate.scenarioCaseId === input.scenarioCaseId,
    );
    if (
      expected.status === 'ok' &&
      approvedCase !== undefined &&
      sameNumber(
        output.grossRunRateSavingsUsd,
        expected.output.grossRunRateSavingsUsd,
      ) &&
      sameNumber(
        output.oneTimeSeveranceUsd,
        expected.output.oneTimeSeveranceUsd,
      ) &&
      sameNumber(
        output.capacityCostAtRiskUsd,
        expected.output.capacityCostAtRiskUsd,
      ) &&
      sameNumber(
        output.workforceReductionNetEbitdaOpportunityUsd,
        expected.output.workforceReductionNetEbitdaOpportunityUsd,
      ) &&
      sameNumber(
        output.workforceReductionNetEbitdaOpportunityUsd,
        approvedCase.workforceReductionNetEbitdaOpportunityUsd,
      )
    ) {
      return null;
    }
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Workforce net EBITDA output does not reconcile to its governed scenario inputs.',
    };
  },
});

const delayeringOrgHealthWarningReconciliationRule: ApprovedFormulaReconciliationRule<
  DelayeringOrgHealthWarningInput,
  DelayeringOrgHealthWarningOutput
> = Object.freeze({
  kind: 'threshold_warning_level_reconciliation',
  ruleId: 'delayering_org_health_warning_level_reconciles_to_evidence_v1',
  description:
    'Org health warning level must reconcile to approved span/layer metrics and governed target thresholds.',
  validate: validateDelayeringOrgHealthWarningOutputAgainstEvidence,
});

const carrierTransportationCostOpportunityReconciliationRule: ApprovedFormulaReconciliationRule<
  CarrierTransportationCostOpportunityInput,
  CarrierTransportationCostOpportunityOutput
> = Object.freeze({
  kind: 'input_output_evidence_reconciliation',
  ruleId: 'carrier_transportation_cost_opportunity_reconciles_to_evidence_v1',
  description:
    'Transportation opportunity output must reconcile to approved shipment lane spend, carrier rate-card, contract-window, and governed margin-floor evidence.',
  validate: validateCarrierTransportationCostOpportunityOutputAgainstEvidence,
});

const carrierTransportationCostOpportunityScenarioReconciliationRule: ApprovedFormulaReconciliationRule<
  CarrierTransportationCostOpportunityScenarioInput,
  CarrierTransportationCostOpportunityScenarioOutput
> = Object.freeze({
  kind: 'input_output_evidence_reconciliation',
  ruleId: 'carrier_transportation_cost_opportunity_scenario_reconciles_to_evidence_v1_1',
  description:
    'Each carrier scenario must reconcile the observed transportation baseline and carrier-spend driver before applying its labeled rate-reduction case.',
  validate: validateCarrierTransportationCostOpportunityScenarioOutputAgainstEvidence,
});

const STRATEGIC_EXECUTION_CAPACITY_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['roundTo', roundTo],
  ['capacityStatusBand', capacityStatusBand],
  ['executeStrategicExecutionCapacityUtilization', executeStrategicExecutionCapacityUtilization],
  ['executeStrategicExecutionCapacityBaselineTotals', executeStrategicExecutionCapacityBaselineTotals],
  ['executeStrategicExecutionCapacityRiskScore', executeStrategicExecutionCapacityRiskScore],
  ['executeStrategicExecutionCapacityWarning', executeStrategicExecutionCapacityWarning],
  ['parseStrategicExecutionCapacityFormulaRecords', parseStrategicExecutionCapacityFormulaRecords],
  ['isStrategicExecutionAtRiskRecord', isStrategicExecutionAtRiskRecord],
  ['isStrategicExecutionBlockedRecord', isStrategicExecutionBlockedRecord],
  ['deriveStrategicExecutionCapacityBaselineTotals', deriveStrategicExecutionCapacityBaselineTotals],
  ['deriveStrategicExecutionCapacityRiskScore', deriveStrategicExecutionCapacityRiskScore],
  ['deriveStrategicExecutionCapacityWarning', deriveStrategicExecutionCapacityWarning],
  [
    'validateStrategicExecutionCapacityUtilizationInputAgainstEvidence',
    validateStrategicExecutionCapacityUtilizationInputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityUtilizationOutputAgainstEvidence',
    validateStrategicExecutionCapacityUtilizationOutputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityRecordsAgainstEvidence',
    validateStrategicExecutionCapacityRecordsAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityRiskInputAgainstEvidence',
    validateStrategicExecutionCapacityRiskInputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityWarningInputAgainstEvidence',
    validateStrategicExecutionCapacityWarningInputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityBaselineTotalsOutputAgainstEvidence',
    validateStrategicExecutionCapacityBaselineTotalsOutputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityRiskOutputAgainstEvidence',
    validateStrategicExecutionCapacityRiskOutputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityWarningOutputAgainstEvidence',
    validateStrategicExecutionCapacityWarningOutputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityRecordDetailsAgainstEvidence',
    validateStrategicExecutionCapacityRecordDetailsAgainstEvidence,
  ],
  ['validateStrategicExecutionCapacityWarningNumbers', validateStrategicExecutionCapacityWarningNumbers],
  ['parseStrategicExecutionCapacityEvidenceSummary', parseStrategicExecutionCapacityEvidenceSummary],
  ['parseStrategicExecutionCapacityEvidenceRecords', parseStrategicExecutionCapacityEvidenceRecords],
  ['asRecord', asRecord],
  ['finiteNumberOrNull', finiteNumberOrNull],
  ['stringOrNull', stringOrNull],
  ['sameNumber', sameNumber],
  ['sameIsoDate', sameIsoDate],
  ['sameStringArray', sameStringArray],
  ['normalizeStatus', normalizeStatus],
  ['normalizeOptionalText', normalizeOptionalText],
  ['toIsoDate', toIsoDate],
]);

const DELAYERING_ORG_COST_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['computeNetEbitdaOpportunityUsd', computeNetEbitdaOpportunityUsd],
  ['roundTo', roundTo],
  ['ebitdaOpportunityStatusBand', ebitdaOpportunityStatusBand],
  ['validateDelayeringOrgHealthNumbers', validateDelayeringOrgHealthNumbers],
  ['deriveDelayeringOrgHealthWarning', deriveDelayeringOrgHealthWarning],
  ['executeDelayeringNetEbitdaOpportunity', executeDelayeringNetEbitdaOpportunity],
  ['executeDelayeringOrgHealthWarningLevel', executeDelayeringOrgHealthWarningLevel],
  [
    'validateDelayeringNetEbitdaOpportunityInputAgainstEvidence',
    validateDelayeringNetEbitdaOpportunityInputAgainstEvidence,
  ],
  [
    'validateDelayeringNetEbitdaOpportunityOutputAgainstEvidence',
    validateDelayeringNetEbitdaOpportunityOutputAgainstEvidence,
  ],
  [
    'validateDelayeringOrgHealthWarningInputAgainstEvidence',
    validateDelayeringOrgHealthWarningInputAgainstEvidence,
  ],
  [
    'validateDelayeringOrgHealthWarningOutputAgainstEvidence',
    validateDelayeringOrgHealthWarningOutputAgainstEvidence,
  ],
  ['parseDelayeringEvidenceSummary', parseDelayeringEvidenceSummary],
  ['asRecord', asRecord],
  ['finiteNumberOrNull', finiteNumberOrNull],
  ['sameNumber', sameNumber],
]);

const WORKFORCE_REDUCTION_NET_EBITDA_IMPLEMENTATION_DIGEST_INPUTS =
  implementationDigestInputs([
    ['isFiniteNumber', isFiniteNumber],
    ['requireFiniteUsd', requireFiniteUsd],
    ['computeNetEbitdaOpportunityUsd', computeNetEbitdaOpportunityUsd],
    [
      'executeWorkforceReductionNetEbitdaOpportunity',
      executeWorkforceReductionNetEbitdaOpportunity,
    ],
    [
      'validateWorkforceReductionNetEbitdaInputAgainstEvidence',
      validateWorkforceReductionNetEbitdaInputAgainstEvidence,
    ],
    [
      'parseWorkforceReductionFormulaScenarioRunEvidence',
      parseWorkforceReductionFormulaScenarioRunEvidence,
    ],
    ['asRecord', asRecord],
    ['finiteNumberOrNull', finiteNumberOrNull],
    ['stringOrNull', stringOrNull],
    ['sameNumber', sameNumber],
  ]);

const CARRIER_TRANSPORTATION_COST_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['roundTo', roundTo],
  ['opportunityStatusBand', opportunityStatusBand],
  ['executeCarrierTransportationCostOpportunity', executeCarrierTransportationCostOpportunity],
  [
    'validateCarrierTransportationCostOpportunityInputAgainstEvidence',
    validateCarrierTransportationCostOpportunityInputAgainstEvidence,
  ],
  [
    'validateCarrierTransportationCostOpportunityOutputAgainstEvidence',
    validateCarrierTransportationCostOpportunityOutputAgainstEvidence,
  ],
  ['exceedsEvidenceBound', exceedsEvidenceBound],
  [
    'resolveCarrierTransportationCostEvidenceSummary',
    resolveCarrierTransportationCostEvidenceSummary,
  ],
  ['latestPeriodTransportationShipments', latestPeriodTransportationShipments],
  ['parseCarrierTransportationCostEvidenceShipments', parseCarrierTransportationCostEvidenceShipments],
  [
    'deriveCarrierTransportationCostOpportunitySummary',
    deriveCarrierTransportationCostOpportunitySummary,
  ],
  ['groupTransportationShipmentsByLane', groupTransportationShipmentsByLane],
  ['belowFloorRecoveryForLane', belowFloorRecoveryForLane],
  ['carrierRateDeltaRecovery', carrierRateDeltaRecovery],
  ['transportationEvidenceReportingCut', transportationEvidenceReportingCut],
  ['transportationContractWindowMatchesShipment', transportationContractWindowMatchesShipment],
  ['dateOnlyTimestamp', dateOnlyTimestamp],
  ['asRecord', asRecord],
  ['finiteNumberOrNull', finiteNumberOrNull],
  ['stringOrNull', stringOrNull],
  ['sameNumber', sameNumber],
]);

const CARRIER_TRANSPORTATION_COST_SCENARIO_IMPLEMENTATION_DIGEST_INPUTS =
  implementationDigestInputs([
    ['isFiniteNumber', isFiniteNumber],
    ['roundTo', roundTo],
    ['opportunityStatusBand', opportunityStatusBand],
    [
      'executeCarrierTransportationCostOpportunityScenario',
      executeCarrierTransportationCostOpportunityScenario,
    ],
    [
      'validateCarrierTransportationCostOpportunityScenarioInputAgainstEvidence',
      validateCarrierTransportationCostOpportunityScenarioInputAgainstEvidence,
    ],
    [
      'validateCarrierTransportationCostOpportunityScenarioOutputAgainstEvidence',
      validateCarrierTransportationCostOpportunityScenarioOutputAgainstEvidence,
    ],
    [
      'resolveCarrierTransportationCostScenarioEvidenceSummary',
      resolveCarrierTransportationCostScenarioEvidenceSummary,
    ],
    ['carrierScenarioRateDeltaBaseSpend', carrierScenarioRateDeltaBaseSpend],
    ['latestPeriodTransportationShipments', latestPeriodTransportationShipments],
    ['parseCarrierTransportationCostEvidenceShipments', parseCarrierTransportationCostEvidenceShipments],
    ['transportationContractWindowMatchesShipment', transportationContractWindowMatchesShipment],
    ['stringOrNull', stringOrNull],
    ['sameNumber', sameNumber],
  ]);

const STRATEGIC_EXECUTION_CAPACITY_DRIFT_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['roundTo', roundTo],
  ['executeStrategicExecutionCapacityDriftDetection', executeStrategicExecutionCapacityDriftDetection],
  ['classifyStrategicExecutionCapacityDrift', classifyStrategicExecutionCapacityDrift],
  ['strategicExecutionCapacityDriftStatusCode', strategicExecutionCapacityDriftStatusCode],
  ['strategicExecutionCapacityDriftRuleId', strategicExecutionCapacityDriftRuleId],
  [
    'validateStrategicExecutionCapacityDriftInputAgainstEvidence',
    validateStrategicExecutionCapacityDriftInputAgainstEvidence,
  ],
  [
    'validateStrategicExecutionCapacityDriftOutputAgainstEvidence',
    validateStrategicExecutionCapacityDriftOutputAgainstEvidence,
  ],
  [
    'strategicExecutionCapacityEvidenceMetricValue',
    strategicExecutionCapacityEvidenceMetricValue,
  ],
  ['deriveStrategicExecutionCapacityBaselineTotals', deriveStrategicExecutionCapacityBaselineTotals],
  ['deriveStrategicExecutionCapacityRiskScore', deriveStrategicExecutionCapacityRiskScore],
  ['parseStrategicExecutionCapacityEvidenceRecords', parseStrategicExecutionCapacityEvidenceRecords],
  ['parseStrategicExecutionCapacityFormulaRecords', parseStrategicExecutionCapacityFormulaRecords],
  ['isStrategicExecutionAtRiskRecord', isStrategicExecutionAtRiskRecord],
  ['isStrategicExecutionBlockedRecord', isStrategicExecutionBlockedRecord],
  ['asRecord', asRecord],
  ['finiteNumberOrNull', finiteNumberOrNull],
  ['stringOrNull', stringOrNull],
  ['sameNumber', sameNumber],
]);

const REALIZATION_PROJECTED_MISS_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['roundTo', roundTo],
  ['executeRealizationProjectedMiss', executeRealizationProjectedMiss],
]);

const REALIZATION_PACE_RECEIPT_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['roundTo', roundTo],
  ['executeRealizationPaceReceipt', executeRealizationPaceReceipt],
]);

const REALIZATION_PROPORTION_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['isFiniteNumber', isFiniteNumber],
  ['roundTo', roundTo],
  ['resolveRealizationProportionBasis', resolveRealizationProportionBasis],
  ['executeRealizationProportion', executeRealizationProportion],
]);

const strategicExecutionCapacityUtilizationDefinition: ApprovedFormulaDefinition<
  StrategicExecutionCapacityUtilizationInput,
  StrategicExecutionCapacityUtilizationOutput
> = Object.freeze({
  formulaId: 'strategic_execution_capacity_utilization',
  version: '1.0.0',
  displayName: 'Strategic execution capacity utilization',
  description:
    'Computes capacity gap and utilization from approved strategic execution capacity evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'strategic_execution_capacity_utilization_input_v1',
  outputSchemaVersion: 'strategic_execution_capacity_utilization_output_v1',
  expectedContextKind: STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KIND,
  approvedSourceSystemRefs: STRATEGIC_EXECUTION_CAPACITY_SOURCE_REFS,
  implementationDigestInputs: STRATEGIC_EXECUTION_CAPACITY_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'strategic_execution_capacity_utilization:pe-model:constrained:v1',
      input: Object.freeze({
        requiredCapacityFte: 10,
        availableCapacityFte: 8,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          requiredCapacityFte: 10,
          availableCapacityFte: 8,
          capacityGapFte: 2,
          utilizationRatio: 0.8,
          utilizationPct: 80,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_utilization:pe-model:sufficient:v1',
      input: Object.freeze({
        requiredCapacityFte: 12,
        availableCapacityFte: 12,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          requiredCapacityFte: 12,
          availableCapacityFte: 12,
          capacityGapFte: 0,
          utilizationRatio: 1,
          utilizationPct: 100,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_utilization:pe-model:critical:v1',
      input: Object.freeze({
        requiredCapacityFte: 10,
        availableCapacityFte: 4,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          requiredCapacityFte: 10,
          availableCapacityFte: 4,
          capacityGapFte: 6,
          utilizationRatio: 0.4,
          utilizationPct: 40,
          statusBand: 'critical' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_utilization:blocked:invalid-required:v1',
      input: Object.freeze({
        requiredCapacityFte: 0,
        availableCapacityFte: 4,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'requiredCapacityFte must be greater than zero',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateStrategicExecutionCapacityUtilizationInputAgainstEvidence,
  reconciliationRule: strategicExecutionCapacityUtilizationReconciliationRule,
  execute: executeStrategicExecutionCapacityUtilization,
});

const strategicExecutionCapacityBaselineTotalsDefinition: ApprovedFormulaDefinition<
  StrategicExecutionCapacityBaselineTotalsInput,
  StrategicExecutionCapacityBaselineTotalsOutput
> = Object.freeze({
  formulaId: 'strategic_execution_capacity_baseline_totals',
  version: '1.0.0',
  displayName: 'Strategic execution capacity baseline totals',
  description:
    'Computes initiative count, required capacity, available capacity, and capacity gap from approved strategic execution records.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'strategic_execution_capacity_baseline_totals_input_v1',
  outputSchemaVersion: 'strategic_execution_capacity_baseline_totals_output_v1',
  expectedContextKind: STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KIND,
  approvedSourceSystemRefs: STRATEGIC_EXECUTION_CAPACITY_SOURCE_REFS,
  implementationDigestInputs: STRATEGIC_EXECUTION_CAPACITY_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'strategic_execution_capacity_baseline_totals:pe-model:gap:v1',
      input: Object.freeze({
        records: Object.freeze([
          formulaRecord('init-1', 12, 9, 'watch', 'integration lead unavailable'),
          formulaRecord('init-2', 18, 10, 'blocked', 'finance owner missing'),
          formulaRecord('init-3', 12, 11, 'on_track', null),
        ]),
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          initiativeCount: 3,
          totalCapacityRequiredFte: 42,
          totalCapacityAvailableFte: 30,
          capacityGapFte: 12,
          capacityGapPct: 28.6,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_baseline_totals:pe-model:no-gap:v1',
      input: Object.freeze({
        records: Object.freeze([
          formulaRecord('init-1', 5, 5, 'on_track', null),
          formulaRecord('init-2', 7, 8, 'complete', null),
        ]),
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          initiativeCount: 2,
          totalCapacityRequiredFte: 12,
          totalCapacityAvailableFte: 13,
          capacityGapFte: 0,
          capacityGapPct: 0,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_baseline_totals:blocked:missing-records:v1',
      input: Object.freeze({
        records: Object.freeze([]),
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'records are required',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateStrategicExecutionCapacityRecordsAgainstEvidence,
  reconciliationRule: strategicExecutionCapacityBaselineTotalsReconciliationRule,
  execute: executeStrategicExecutionCapacityBaselineTotals,
});

const strategicExecutionCapacityRiskScoreDefinition: ApprovedFormulaDefinition<
  StrategicExecutionCapacityRiskScoreInput,
  StrategicExecutionCapacityRiskScoreOutput
> = Object.freeze({
  formulaId: 'strategic_execution_capacity_risk_score',
  version: '1.0.0',
  displayName: 'Strategic execution capacity risk score',
  description:
    'Computes at-risk initiatives, blocked initiatives, and the approved risk score from governed strategic execution records.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'strategic_execution_capacity_risk_score_input_v1',
  outputSchemaVersion: 'strategic_execution_capacity_risk_score_output_v1',
  expectedContextKind: STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KIND,
  approvedSourceSystemRefs: STRATEGIC_EXECUTION_CAPACITY_SOURCE_REFS,
  implementationDigestInputs: STRATEGIC_EXECUTION_CAPACITY_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'strategic_execution_capacity_risk_score:pe-model:decision-required:v1',
      input: Object.freeze({
        records: Object.freeze([
          formulaRecord('init-1', 12, 9, 'watch', 'integration lead unavailable'),
          formulaRecord('init-2', 18, 10, 'blocked', 'finance owner missing'),
          formulaRecord('init-3', 12, 11, 'on_track', null),
        ]),
        capacityGapPct: 28.6,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          initiativesAtRiskCount: 2,
          blockedInitiativeCount: 2,
          strategicExecutionCapacityRiskScore: 73.8,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_risk_score:pe-model:clean:v1',
      input: Object.freeze({
        records: Object.freeze([
          formulaRecord('init-1', 5, 5, 'on_track', null),
          formulaRecord('init-2', 7, 8, 'complete', null),
        ]),
        capacityGapPct: 0,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          initiativesAtRiskCount: 0,
          blockedInitiativeCount: 0,
          strategicExecutionCapacityRiskScore: 0,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_risk_score:blocked:negative-gap:v1',
      input: Object.freeze({
        records: Object.freeze([formulaRecord('init-1', 5, 5, 'on_track', null)]),
        capacityGapPct: -1,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'capacityGapPct must be a finite non-negative number',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateStrategicExecutionCapacityRiskInputAgainstEvidence,
  reconciliationRule: strategicExecutionCapacityRiskScoreReconciliationRule,
  execute: executeStrategicExecutionCapacityRiskScore,
});

const strategicExecutionCapacityWarningDefinition: ApprovedFormulaDefinition<
  StrategicExecutionCapacityWarningInput,
  StrategicExecutionCapacityWarningOutput
> = Object.freeze({
  formulaId: 'strategic_execution_capacity_warning_level',
  version: '1.0.0',
  displayName: 'Strategic execution capacity warning level',
  description:
    'Classifies strategic execution capacity warning level from approved capacity gap, risk score, and blocker metrics.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'strategic_execution_capacity_warning_level_input_v1',
  outputSchemaVersion: 'strategic_execution_capacity_warning_level_output_v1',
  expectedContextKind: STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KIND,
  approvedSourceSystemRefs: STRATEGIC_EXECUTION_CAPACITY_SOURCE_REFS,
  implementationDigestInputs: STRATEGIC_EXECUTION_CAPACITY_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'strategic_execution_capacity_warning_level:pe-model:decision-required:v1',
      input: Object.freeze({
        records: Object.freeze([
          formulaRecord('init-1', 12, 9, 'watch', 'integration lead unavailable'),
          formulaRecord('init-2', 18, 10, 'blocked', 'finance owner missing'),
          formulaRecord('init-3', 12, 11, 'on_track', null),
        ]),
        capacityGapFte: 12,
        capacityGapPct: 28.6,
        blockedInitiativeCount: 2,
        strategicExecutionCapacityRiskScore: 73.8,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          warningLevel: 'decision_required' as const,
          triggeredRuleIds: Object.freeze([
            'strategic_execution_capacity_watch',
            'strategic_execution_capacity_decision_required',
            'strategic_execution_blocker_watch',
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_warning_level:pe-model:watch:v1',
      input: Object.freeze({
        records: Object.freeze([
          formulaRecord('init-1', 5, 3, 'watch', null),
          formulaRecord('init-2', 5, 5, 'watch', null),
          formulaRecord('init-3', 5, 5, 'watch', null),
          formulaRecord('init-4', 5, 5, 'watch', null),
          formulaRecord('init-5', 5, 5, 'watch', null),
        ]),
        capacityGapFte: 2,
        capacityGapPct: 8,
        blockedInitiativeCount: 0,
        strategicExecutionCapacityRiskScore: 52.8,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          warningLevel: 'watch' as const,
          triggeredRuleIds: Object.freeze(['strategic_execution_capacity_watch']),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_warning_level:blocked:negative-score:v1',
      input: Object.freeze({
        records: Object.freeze([formulaRecord('init-1', 5, 5, 'on_track', null)]),
        capacityGapFte: 0,
        capacityGapPct: 0,
        blockedInitiativeCount: 0,
        strategicExecutionCapacityRiskScore: -1,
        reportingCutAsOf: '2026-05-23',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'Warning inputs must be finite non-negative numbers',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateStrategicExecutionCapacityWarningInputAgainstEvidence,
  reconciliationRule: strategicExecutionCapacityWarningReconciliationRule,
  execute: executeStrategicExecutionCapacityWarning,
});

const strategicExecutionCapacityDriftDefinition: ApprovedFormulaDefinition<
  StrategicExecutionCapacityDriftDetectionInput,
  StrategicExecutionCapacityDriftDetectionOutput
> = Object.freeze({
  formulaId: 'strategic_execution_capacity_drift_detection',
  version: '1.0.0',
  displayName: 'Strategic execution capacity drift detection',
  description:
    'Compares approved baseline, target, and actual execution capacity values against time-phased expected progress.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'strategic_execution_capacity_drift_detection_input_v1',
  outputSchemaVersion: 'strategic_execution_capacity_drift_detection_output_v1',
  expectedContextKind: STRATEGIC_EXECUTION_CAPACITY_CONTEXT_KIND,
  approvedSourceSystemRefs: STRATEGIC_EXECUTION_CAPACITY_SOURCE_REFS,
  implementationDigestInputs: STRATEGIC_EXECUTION_CAPACITY_DRIFT_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'strategic_execution_capacity_drift_detection:pe-model:on-track:v1',
      input: Object.freeze({
        metricKey: 'capacity_gap_fte',
        baselineValue: 12,
        targetValue: 0,
        actualValue: 4,
        baselineAsOf: '2026-05-20',
        actualAsOf: '2026-06-03',
        targetAt: '2026-06-17',
        direction: 'decrease' as const,
        warningThresholdRatio: 0.05,
        driftThresholdRatio: 0.1,
        amendmentThresholdRatio: 0.2,
        reportingCutAsOf: '2026-06-03',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'capacity_gap_fte',
          baselineValue: 12,
          targetValue: 0,
          actualValue: 4,
          expectedValue: 6,
          deltaFromExpected: -2,
          behindRatio: 0,
          driftStatus: 'on_track' as const,
          driftStatusCode: 0,
          triggeredRuleId: 'strategic_execution_capacity_drift_on_track',
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_drift_detection:pe-model:drifting:v1',
      input: Object.freeze({
        metricKey: 'capacity_gap_fte',
        baselineValue: 12,
        targetValue: 0,
        actualValue: 8,
        baselineAsOf: '2026-05-20',
        actualAsOf: '2026-06-03',
        targetAt: '2026-06-17',
        direction: 'decrease' as const,
        warningThresholdRatio: 0.05,
        driftThresholdRatio: 0.1,
        amendmentThresholdRatio: 0.2,
        reportingCutAsOf: '2026-06-03',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'capacity_gap_fte',
          baselineValue: 12,
          targetValue: 0,
          actualValue: 8,
          expectedValue: 6,
          deltaFromExpected: 2,
          behindRatio: 0.1667,
          driftStatus: 'drifting' as const,
          driftStatusCode: 2,
          triggeredRuleId: 'strategic_execution_capacity_drift_drifting',
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_drift_detection:pe-model:amendment-required:v1',
      input: Object.freeze({
        metricKey: 'capacity_gap_fte',
        baselineValue: 12,
        targetValue: 0,
        actualValue: 12,
        baselineAsOf: '2026-05-20',
        actualAsOf: '2026-06-03',
        targetAt: '2026-06-17',
        direction: 'decrease' as const,
        warningThresholdRatio: 0.05,
        driftThresholdRatio: 0.1,
        amendmentThresholdRatio: 0.2,
        reportingCutAsOf: '2026-06-03',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'capacity_gap_fte',
          baselineValue: 12,
          targetValue: 0,
          actualValue: 12,
          expectedValue: 6,
          deltaFromExpected: 6,
          behindRatio: 0.5,
          driftStatus: 'amendment_required' as const,
          driftStatusCode: 3,
          triggeredRuleId: 'strategic_execution_capacity_drift_amendment_required',
        }),
      }),
    }),
    Object.freeze({
      traceId: 'strategic_execution_capacity_drift_detection:blocked:missing-target:v1',
      input: Object.freeze({
        metricKey: 'capacity_gap_fte',
        baselineValue: 12,
        actualValue: 12,
        baselineAsOf: '2026-05-20',
        actualAsOf: '2026-06-03',
        targetAt: '2026-06-17',
        direction: 'decrease' as const,
      }) as StrategicExecutionCapacityDriftDetectionInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'targetValue is required',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateStrategicExecutionCapacityDriftInputAgainstEvidence,
  reconciliationRule: strategicExecutionCapacityDriftReconciliationRule,
  execute: executeStrategicExecutionCapacityDriftDetection,
});

const realizationProjectedMissDefinition: ApprovedFormulaDefinition<
  RealizationProjectedMissInput,
  RealizationProjectedMissOutput
> = Object.freeze({
  formulaId: 'realization_projected_miss_v1',
  version: '1.0.0',
  displayName: 'Realization projected miss',
  description:
    'Converts behind-plan realization units into projected miss dollars from a governed commitment curve and approved value binding.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'realization_projected_miss_input_v1',
  outputSchemaVersion: 'realization_projected_miss_output_v1',
  expectedContextKind: 'decision_ledger:realization_commitment',
  approvedSourceSystemRefs: [WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF],
  implementationDigestInputs: REALIZATION_PROJECTED_MISS_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'realization_projected_miss_v1:on-track:increase:v1',
      input: Object.freeze({
        metricKey: 'benefit_realized_usd',
        expectedValue: 1000000,
        actualValue: 1000000,
        direction: 'increase' as const,
        valuePerUnit: 1,
        annualizationFactor: 1,
        reportingCutAsOf: '2026-07-01',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'benefit_realized_usd',
          expectedValue: 1000000,
          actualValue: 1000000,
          direction: 'increase' as const,
          behindUnits: 0,
          valuePerUnit: 1,
          annualizationFactor: 1,
          projectedMissUsd: 0,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'realization_projected_miss_v1:behind:decrease:v1',
      input: Object.freeze({
        metricKey: 'labor_cost_run_rate_usd',
        expectedValue: 9200000,
        actualValue: 9800000,
        direction: 'decrease' as const,
        valuePerUnit: 1,
        annualizationFactor: 1,
        reportingCutAsOf: '2026-07-01',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'labor_cost_run_rate_usd',
          expectedValue: 9200000,
          actualValue: 9800000,
          direction: 'decrease' as const,
          behindUnits: 600000,
          valuePerUnit: 1,
          annualizationFactor: 1,
          projectedMissUsd: 600000,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'realization_projected_miss_v1:blocked:missing-value-binding:v1',
      input: Object.freeze({
        metricKey: 'seats_removed',
        expectedValue: 50,
        actualValue: 40,
        direction: 'increase' as const,
        annualizationFactor: 1,
      }) as RealizationProjectedMissInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'valuePerUnit is required',
      }),
    }),
  ]),
  execute: executeRealizationProjectedMiss,
});

const realizationPaceReceiptDefinition: ApprovedFormulaDefinition<
  RealizationPaceReceiptInput,
  RealizationPaceReceiptOutput
> = Object.freeze({
  formulaId: 'realization_pace_receipt_v1',
  version: '1.0.0',
  displayName: 'Realization pace receipt',
  description:
    'Publishes the source-owned total required pace, catch-up increment, evidenced actual pace, and projected landing for an approved realization commitment.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'realization_pace_receipt_input_v1',
  outputSchemaVersion: 'realization_pace_receipt_output_v1',
  expectedContextKind: 'decision_ledger:realization_commitment',
  approvedSourceSystemRefs: [REALIZATION_MONITORING_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF],
  implementationDigestInputs: REALIZATION_PACE_RECEIPT_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'realization_pace_receipt_v1:behind:decrease:v1',
      input: Object.freeze({
        metricKey: 'active_headcount',
        direction: 'decrease' as const,
        baselineValue: 100,
        targetValue: 60,
        expectedValue: 80,
        actualValue: 90,
        reportingCutAsOf: '2026-07-01T00:00:00.000Z',
        targetAt: '2026-07-29T00:00:00.000Z',
        observationWindow: Object.freeze({
          policyId: 'weekly_governed_actuals_v1',
          fromAsOf: '2026-06-24T00:00:00.000Z',
          toAsOf: '2026-07-01T00:00:00.000Z',
          firstActualValue: 95,
          latestActualValue: 90,
        }),
      }) as RealizationPaceReceiptInput,
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'active_headcount',
          direction: 'decrease' as const,
          baselineValue: 100,
          targetValue: 60,
          expectedValue: 80,
          actualValue: 90,
          reportingCutAsOf: '2026-07-01T00:00:00.000Z',
          targetAt: '2026-07-29T00:00:00.000Z',
          behindUnits: 10,
          actualPacePerWeek: 5,
          requiredTotalPacePerWeek: 7.5,
          requiredCatchUpIncrementPerWeek: 2.5,
          paceCoverageRatio: 0.6667,
          projectedLandAt: '2026-08-12T00:00:00.000Z',
          weeksUntilCommittedMiss: 4,
          gapCodes: Object.freeze([]),
        }),
      }),
    }),
  ]),
  execute: executeRealizationPaceReceipt,
});

const realizationProportionDefinition: ApprovedFormulaDefinition<
  RealizationProportionInput,
  RealizationProportionOutput
> = Object.freeze({
  formulaId: 'realization_proportion_v1',
  version: '1.0.0',
  displayName: 'Realization proportion on named bases',
  description:
    'Resolves a realized figure into a governed share of every named candidate denominator, one basis at a time, withholding only the bases whose denominator is absent or non-positive.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'realization_proportion_input_v1',
  outputSchemaVersion: 'realization_proportion_output_v1',
  expectedContextKind: 'decision_ledger:realization_commitment',
  approvedSourceSystemRefs: [REALIZATION_MONITORING_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF],
  implementationDigestInputs: REALIZATION_PROPORTION_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'realization_proportion_v1:two-named-bases:v1',
      input: Object.freeze({
        realizedUsd: 1184000,
        bases: Object.freeze([
          Object.freeze({ basisId: 'committed_to_date', denominatorUsd: 2621880 }),
          Object.freeze({
            basisId: 'underwritten_plus_committed',
            denominatorComponentsUsd: Object.freeze([5733585, 2621880]),
          }),
        ]),
        reportingCutAsOf: '2026-07-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          realizedUsd: 1184000,
          bases: Object.freeze([
            Object.freeze({
              basisId: 'committed_to_date',
              denominatorUsd: 2621880,
              denominatorComponentsUsd: null,
              realizationSharePct: 45.1584,
              unrealizedUsd: 1437880,
              withheldReasonCode: null,
            }),
            Object.freeze({
              basisId: 'underwritten_plus_committed',
              denominatorUsd: 8355465,
              denominatorComponentsUsd: Object.freeze([5733585, 2621880]),
              realizationSharePct: 14.1704,
              unrealizedUsd: 7171465,
              withheldReasonCode: null,
            }),
          ]),
          resolvedBasisCount: 2,
          withheldBasisCount: 0,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'realization_proportion_v1:one-basis-withheld:v1',
      input: Object.freeze({
        realizedUsd: 1184000,
        bases: Object.freeze([
          Object.freeze({ basisId: 'committed_to_date', denominatorUsd: 2621880 }),
          Object.freeze({ basisId: 'underwritten_plus_committed', denominatorUsd: null }),
        ]),
        reportingCutAsOf: '2026-07-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          realizedUsd: 1184000,
          bases: Object.freeze([
            Object.freeze({
              basisId: 'committed_to_date',
              denominatorUsd: 2621880,
              denominatorComponentsUsd: null,
              realizationSharePct: 45.1584,
              unrealizedUsd: 1437880,
              withheldReasonCode: null,
            }),
            Object.freeze({
              basisId: 'underwritten_plus_committed',
              denominatorUsd: null,
              denominatorComponentsUsd: null,
              realizationSharePct: null,
              unrealizedUsd: null,
              withheldReasonCode: 'denominator_not_supplied' as const,
            }),
          ]),
          resolvedBasisCount: 1,
          withheldBasisCount: 1,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'realization_proportion_v1:blocked:missing-realized:v1',
      input: Object.freeze({
        bases: Object.freeze([
          Object.freeze({ basisId: 'committed_to_date', denominatorUsd: 2621880 }),
        ]),
        reportingCutAsOf: '2026-07-31',
      }) as RealizationProportionInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'realizedUsd is required',
      }),
    }),
    // Every basis withheld, for DIFFERENT reasons: a supplied non-positive denominator
    // and an unsupplied one. Both reasons survive, so a caller reporting them cannot
    // describe the supplied-but-zero base as one that was never supplied.
    Object.freeze({
      traceId: 'realization_proportion_v1:all-bases-withheld:v1',
      input: Object.freeze({
        realizedUsd: 1184000,
        bases: Object.freeze([
          Object.freeze({ basisId: 'committed_to_date', denominatorUsd: 0 }),
          Object.freeze({ basisId: 'underwritten_plus_committed', denominatorUsd: null }),
        ]),
        reportingCutAsOf: '2026-07-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          realizedUsd: 1184000,
          bases: Object.freeze([
            Object.freeze({
              basisId: 'committed_to_date',
              denominatorUsd: 0,
              denominatorComponentsUsd: null,
              realizationSharePct: null,
              unrealizedUsd: null,
              withheldReasonCode: 'denominator_not_positive' as const,
            }),
            Object.freeze({
              basisId: 'underwritten_plus_committed',
              denominatorUsd: null,
              denominatorComponentsUsd: null,
              realizationSharePct: null,
              unrealizedUsd: null,
              withheldReasonCode: 'denominator_not_supplied' as const,
            }),
          ]),
          resolvedBasisCount: 0,
          withheldBasisCount: 2,
        }),
      }),
    }),
    // Fractional-cent components, pinned because a composed base must RECONCILE to the
    // components published beside it. The first basis totals its rounded components
    // (0.01 + 0 = 0.01), so the receipt adds up; totalling the raw inputs would have
    // declared 0.02 against components a reader can only add to 0.01. The second is the
    // degenerate case: components that all round away leave no base at all, so the rule
    // that already withholds a non-positive denominator withholds it, rather than
    // publishing a share against a cent no component set contains.
    Object.freeze({
      traceId: 'realization_proportion_v1:fractional-cent-components:v1',
      input: Object.freeze({
        realizedUsd: 0.01,
        bases: Object.freeze([
          Object.freeze({
            basisId: 'committed_to_date',
            denominatorComponentsUsd: Object.freeze([0.014, 0.004]),
          }),
          Object.freeze({
            basisId: 'underwritten_plus_committed',
            denominatorComponentsUsd: Object.freeze([0.004, 0.004]),
          }),
        ]),
        reportingCutAsOf: '2026-07-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          realizedUsd: 0.01,
          bases: Object.freeze([
            Object.freeze({
              basisId: 'committed_to_date',
              denominatorUsd: 0.01,
              denominatorComponentsUsd: Object.freeze([0.01, 0]),
              realizationSharePct: 100,
              unrealizedUsd: 0,
              withheldReasonCode: null,
            }),
            Object.freeze({
              basisId: 'underwritten_plus_committed',
              denominatorUsd: 0,
              denominatorComponentsUsd: Object.freeze([0, 0]),
              realizationSharePct: null,
              unrealizedUsd: null,
              withheldReasonCode: 'denominator_not_positive' as const,
            }),
          ]),
          resolvedBasisCount: 1,
          withheldBasisCount: 1,
        }),
      }),
    }),
    // The same law on the OTHER side of the ratio: a fractional-cent numerator, and a
    // supplied base that is itself sub-cent. Realized 0.006 publishes as 0.01, so the
    // share must read 0.01 of 0.01 at 100% — dividing the raw 0.006 by the raw 0.01
    // would print 60% beside operands a reader can only read as 100%. The second basis
    // is the supplied-denominator twin of the degenerate component case: 0.004 is not a
    // cent, so the base rounds away and the rule that already withholds a non-positive
    // denominator withholds it, rather than publishing a 150% share against a published
    // denominator of 0.
    Object.freeze({
      traceId: 'realization_proportion_v1:fractional-cent-numerator:v1',
      input: Object.freeze({
        realizedUsd: 0.006,
        bases: Object.freeze([
          Object.freeze({ basisId: 'committed_to_date', denominatorUsd: 0.01 }),
          Object.freeze({
            basisId: 'underwritten_plus_committed',
            denominatorUsd: 0.004,
          }),
        ]),
        reportingCutAsOf: '2026-07-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          realizedUsd: 0.01,
          bases: Object.freeze([
            Object.freeze({
              basisId: 'committed_to_date',
              denominatorUsd: 0.01,
              denominatorComponentsUsd: null,
              realizationSharePct: 100,
              unrealizedUsd: 0,
              withheldReasonCode: null,
            }),
            Object.freeze({
              basisId: 'underwritten_plus_committed',
              denominatorUsd: 0,
              denominatorComponentsUsd: null,
              realizationSharePct: null,
              unrealizedUsd: null,
              withheldReasonCode: 'denominator_not_positive' as const,
            }),
          ]),
          resolvedBasisCount: 1,
          withheldBasisCount: 1,
        }),
      }),
    }),
  ]),
  execute: executeRealizationProportion,
});

const maDealGrossSynergyByBandDefinition: ApprovedFormulaDefinition<
  MaDealGrossSynergyByBandInput,
  MaDealGrossSynergyByBandOutput
> = Object.freeze({
  formulaId: MA_DEAL_GROSS_SYNERGY_BY_BAND_FORMULA_ID,
  version: MA_DEAL_GROSS_SYNERGY_BY_BAND_FORMULA_VERSION,
  displayName: 'M&A gross synergy by scenario band',
  description:
    'Sums one coherent conservative, base, or stretch target band across admitted deal commitments.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'ma_deal_gross_synergy_by_band_input_v1',
  outputSchemaVersion: 'ma_deal_gross_synergy_by_band_output_v1',
  approvedSourceSystemRefs: Object.freeze([]),
  implementationDigestInputs: implementationDigestInputs([
    ['calculateMaDealGrossSynergyByBand', calculateMaDealGrossSynergyByBand],
    ['isMaDealBand', isMaDealBand],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'ma_deal_gross_synergy_by_band_v1:base:v1',
      input: Object.freeze({
        commitments: Object.freeze([
          Object.freeze({
            commitmentRef: 'commitment:one',
            displayLabel: 'One',
            targetClaim: Object.freeze({
              bands: Object.freeze({ conservative: 10, base: 20, stretch: 30 }),
            }),
          }),
          Object.freeze({
            commitmentRef: 'commitment:two',
            displayLabel: 'Two',
            targetClaim: Object.freeze({
              bands: Object.freeze({ conservative: 5, base: 15, stretch: 25 }),
            }),
          }),
        ]),
        band: 'base' as const,
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          band: 'base' as const,
          value: 35,
          components: Object.freeze([
            Object.freeze({ key: 'commitment:one', label: 'One', value: 20 }),
            Object.freeze({ key: 'commitment:two', label: 'Two', value: 15 }),
          ]),
        }),
      }),
    }),
  ]),
  execute: executeMaDealGrossSynergyByBand,
});

const delayeringNetEbitdaOpportunityDefinition: ApprovedFormulaDefinition<
  DelayeringNetEbitdaOpportunityInput,
  DelayeringNetEbitdaOpportunityOutput
> = Object.freeze({
  formulaId: 'delayering_net_ebitda_opportunity',
  version: '1.0.0',
  displayName: 'Delayering net EBITDA opportunity',
  description:
    'Computes governed org-cost EBITDA contribution from baseline cost, scenario cost, severance, and capacity-at-risk inputs.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'delayering_net_ebitda_opportunity_input_v1',
  outputSchemaVersion: 'delayering_net_ebitda_opportunity_output_v1',
  expectedContextKind: ORG_COST_OPTIMIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: ORG_COST_OPTIMIZATION_SOURCE_REFS,
  implementationDigestInputs: DELAYERING_ORG_COST_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'delayering_net_ebitda_opportunity:pe-model:positive:v1',
      input: Object.freeze({
        baselineRunRateCostUsd: 100000000,
        scenarioRunRateCostUsd: 90000000,
        oneTimeSeveranceUsd: 2500000,
        capacityCostAtRiskUsd: 1000000,
        reportingCutAsOf: '2026-05-26',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          grossRunRateSavingsUsd: 10000000,
          oneTimeSeveranceUsd: 2500000,
          capacityCostAtRiskUsd: 1000000,
          delayeringNetEbitdaOpportunityUsd: 6500000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'delayering_net_ebitda_opportunity:pe-model:budget-overrun:v1',
      input: Object.freeze({
        baselineRunRateCostUsd: 100000000,
        scenarioRunRateCostUsd: 130000000,
        oneTimeSeveranceUsd: 0,
        capacityCostAtRiskUsd: 0,
        reportingCutAsOf: '2026-05-26',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          grossRunRateSavingsUsd: -30000000,
          oneTimeSeveranceUsd: 0,
          capacityCostAtRiskUsd: 0,
          delayeringNetEbitdaOpportunityUsd: -30000000,
          statusBand: 'critical' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'delayering_net_ebitda_opportunity:blocked:missing-capacity-risk:v1',
      input: Object.freeze({
        baselineRunRateCostUsd: 100000000,
        scenarioRunRateCostUsd: 90000000,
        oneTimeSeveranceUsd: 2500000,
      }) as DelayeringNetEbitdaOpportunityInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'baselineRunRateCostUsd, scenarioRunRateCostUsd, oneTimeSeveranceUsd, and capacityCostAtRiskUsd must be finite non-negative USD values',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateDelayeringNetEbitdaOpportunityInputAgainstEvidence,
  reconciliationRule: delayeringNetEbitdaOpportunityReconciliationRule,
  execute: executeDelayeringNetEbitdaOpportunity,
});

const workforceReductionNetEbitdaOpportunityDefinition: ApprovedFormulaDefinition<
  WorkforceReductionNetEbitdaOpportunityInput,
  WorkforceReductionNetEbitdaOpportunityOutput
> = Object.freeze({
  formulaId: 'workforce_reduction_net_ebitda_opportunity',
  version: '1.0.0',
  displayName: 'Workforce reduction net EBITDA opportunity',
  description:
    'Computes net EBITDA for one governed workforce scenario case from run-rate savings, one-time severance, and capacity cost at risk.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion:
    'workforce_reduction_net_ebitda_opportunity_input_v1',
  outputSchemaVersion:
    'workforce_reduction_net_ebitda_opportunity_output_v1',
  expectedContextKind: WORKFORCE_REDUCTION_EVIDENCE_PACK_CONTEXT_KIND,
  approvedSourceSystemRefs: [
    WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF,
  ],
  implementationDigestInputs:
    WORKFORCE_REDUCTION_NET_EBITDA_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'workforce_reduction_net_ebitda_opportunity:base:v1',
      input: Object.freeze({
        grossRunRateSavingsUsd: 120000,
        oneTimeSeveranceUsd: 30000,
        capacityCostAtRiskUsd: 5000,
        sourceRunId: 'workforce-run-1',
        scenarioCaseId: 'structural',
        reportingCutAsOf: '2026-06-04T00:20:00.000Z',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          grossRunRateSavingsUsd: 120000,
          oneTimeSeveranceUsd: 30000,
          capacityCostAtRiskUsd: 5000,
          workforceReductionNetEbitdaOpportunityUsd: 85000,
        }),
      }),
    }),
    Object.freeze({
      traceId:
        'workforce_reduction_net_ebitda_opportunity:blocked:negative-component:v1',
      input: Object.freeze({
        grossRunRateSavingsUsd: 120000,
        oneTimeSeveranceUsd: -1,
        capacityCostAtRiskUsd: 0,
        sourceRunId: 'workforce-run-1',
        scenarioCaseId: 'structural',
        reportingCutAsOf: '2026-06-04T00:20:00.000Z',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'Workforce net EBITDA inputs must be finite non-negative USD values',
      }),
    }),
  ]),
  validateInputAgainstEvidence:
    validateWorkforceReductionNetEbitdaInputAgainstEvidence,
  reconciliationRule: workforceReductionNetEbitdaOpportunityReconciliationRule,
  execute: executeWorkforceReductionNetEbitdaOpportunity,
});

const carrierTransportationCostOpportunityDefinition: ApprovedFormulaDefinition<
  CarrierTransportationCostOpportunityInput,
  CarrierTransportationCostOpportunityOutput
> = Object.freeze({
  formulaId: 'carrier_transportation_cost_opportunity',
  version: '1.0.3',
  displayName: 'Carrier transportation cost opportunity',
  description:
    'Computes governed transportation opportunity from below-floor lane recovery, contract-rate adherence, and explicit carrier-rate delta components.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'carrier_transportation_cost_opportunity_input_v1',
  outputSchemaVersion: 'carrier_transportation_cost_opportunity_output_v1',
  expectedContextKind: CARRIER_TRANSPORTATION_COST_CONTEXT_KIND,
  approvedSourceSystemRefs: CARRIER_TRANSPORTATION_COST_SOURCE_REFS,
  implementationDigestInputs: CARRIER_TRANSPORTATION_COST_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'carrier_transportation_cost_opportunity:pe-model:positive:v1',
      input: Object.freeze({
        baselineGrossMarginUsd: 1250,
        belowFloorRecoveryUsd: 300,
        contractRateRecoveryUsd: 225,
        carrierRateDeltaRecoveryUsd: 0,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselineGrossMarginUsd: 1250,
          belowFloorRecoveryUsd: 300,
          contractRateRecoveryUsd: 225,
          carrierRateDeltaRecoveryUsd: 0,
          carrierCostOpportunityUsd: 525,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'carrier_transportation_cost_opportunity:pe-model:no-opportunity:v1',
      input: Object.freeze({
        baselineGrossMarginUsd: 2200,
        belowFloorRecoveryUsd: 0,
        contractRateRecoveryUsd: 0,
        carrierRateDeltaRecoveryUsd: 0,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselineGrossMarginUsd: 2200,
          belowFloorRecoveryUsd: 0,
          contractRateRecoveryUsd: 0,
          carrierRateDeltaRecoveryUsd: 0,
          carrierCostOpportunityUsd: 0,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'carrier_transportation_cost_opportunity:blocked:negative-component:v1',
      input: Object.freeze({
        baselineGrossMarginUsd: 1250,
        belowFloorRecoveryUsd: -1,
        contractRateRecoveryUsd: 225,
        carrierRateDeltaRecoveryUsd: 0,
      }) as CarrierTransportationCostOpportunityInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'Transportation opportunity inputs must include finite baseline margin and non-negative recovery components',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateCarrierTransportationCostOpportunityInputAgainstEvidence,
  reconciliationRule: carrierTransportationCostOpportunityReconciliationRule,
  execute: executeCarrierTransportationCostOpportunity,
});

const carrierTransportationCostOpportunityScenarioDefinition: ApprovedFormulaDefinition<
  CarrierTransportationCostOpportunityScenarioInput,
  CarrierTransportationCostOpportunityScenarioOutput
> = Object.freeze({
  formulaId: 'carrier_transportation_cost_opportunity',
  version: '1.1.0',
  displayName: 'Carrier transportation cost opportunity scenario',
  description:
    'Reconciles the observed carrier opportunity baseline, then applies one labeled buy-rate reduction to the governed carrier-spend driver.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'carrier_transportation_cost_opportunity_input_v1_1',
  outputSchemaVersion: 'carrier_transportation_cost_opportunity_output_v1_1',
  expectedContextKind: CARRIER_TRANSPORTATION_COST_CONTEXT_KIND,
  approvedSourceSystemRefs: CARRIER_TRANSPORTATION_COST_SOURCE_REFS,
  implementationDigestInputs:
    CARRIER_TRANSPORTATION_COST_SCENARIO_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'carrier_transportation_cost_opportunity:scenario:conservative:v1_1',
      input: Object.freeze({
        baselineGrossMarginUsd: 500,
        belowFloorRecoveryUsd: 50,
        contractRateRecoveryUsd: 100,
        carrierRateDeltaBaseSpendUsd: 3100,
        scenarioRateDeltaPct: 2,
        expectedBaselineOpportunityUsd: 150,
        carrier: 'Carrier A',
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselineGrossMarginUsd: 500,
          belowFloorRecoveryUsd: 50,
          contractRateRecoveryUsd: 100,
          carrierRateDeltaBaseSpendUsd: 3100,
          scenarioRateDeltaPct: 2,
          baselineCarrierCostOpportunityUsd: 150,
          baselineOpportunityVarianceUsd: 0,
          scenarioCarrierRateDeltaRecoveryUsd: 62,
          scenarioCarrierCostOpportunityUsd: 212,
          operatingIncomeDeltaUsd: 62,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'carrier_transportation_cost_opportunity:scenario:blocked:zero-driver:v1_1',
      input: Object.freeze({
        baselineGrossMarginUsd: 500,
        belowFloorRecoveryUsd: 50,
        contractRateRecoveryUsd: 100,
        carrierRateDeltaBaseSpendUsd: 0,
        scenarioRateDeltaPct: 2,
        expectedBaselineOpportunityUsd: 150,
        carrier: 'Carrier A',
      }) as CarrierTransportationCostOpportunityScenarioInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'Carrier scenario inputs require finite governed baseline values, a positive carrier-spend driver, a non-negative expected baseline, and a carrier',
      }),
    }),
    Object.freeze({
      traceId: 'carrier_transportation_cost_opportunity:scenario:blocked:rate:v1_1',
      input: Object.freeze({
        baselineGrossMarginUsd: 500,
        belowFloorRecoveryUsd: 50,
        contractRateRecoveryUsd: 100,
        carrierRateDeltaBaseSpendUsd: 3100,
        scenarioRateDeltaPct: 0,
        expectedBaselineOpportunityUsd: 150,
        carrier: 'Carrier A',
      }) as CarrierTransportationCostOpportunityScenarioInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'scenarioRateDeltaPct must be greater than zero and at most 100',
      }),
    }),
  ]),
  validateInputAgainstEvidence:
    validateCarrierTransportationCostOpportunityScenarioInputAgainstEvidence,
  reconciliationRule: carrierTransportationCostOpportunityScenarioReconciliationRule,
  execute: executeCarrierTransportationCostOpportunityScenario,
});

const delayeringOrgHealthWarningDefinition: ApprovedFormulaDefinition<
  DelayeringOrgHealthWarningInput,
  DelayeringOrgHealthWarningOutput
> = Object.freeze({
  formulaId: 'delayering_org_health_warning_level',
  version: '1.0.0',
  displayName: 'Delayering org health warning level',
  description:
    'Classifies org-health warning level from approved span, layer, thin-span, and governed target threshold inputs.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'delayering_org_health_warning_level_input_v1',
  outputSchemaVersion: 'delayering_org_health_warning_level_output_v1',
  expectedContextKind: ORG_COST_OPTIMIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: ORG_COST_OPTIMIZATION_SOURCE_REFS,
  implementationDigestInputs: DELAYERING_ORG_COST_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'delayering_org_health_warning_level:watch:v1',
      input: Object.freeze({
        maxDepth: 8,
        targetMaxDepth: 7,
        averageManagerSpan: 5,
        targetMinimumManagerSpan: 4,
        thinSpanManagerCount: 10,
        managerCount: 100,
        maxThinSpanManagerRatio: 0.15,
        reportingCutAsOf: '2026-05-26',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          warningLevel: 'watch' as const,
          warningLevelCode: 1,
          triggeredRuleIds: Object.freeze([
            'delayering_org_health:max_depth_above_target',
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'delayering_org_health_warning_level:decision-required:v1',
      input: Object.freeze({
        maxDepth: 9,
        targetMaxDepth: 7,
        averageManagerSpan: 2,
        targetMinimumManagerSpan: 4,
        thinSpanManagerCount: 30,
        managerCount: 100,
        maxThinSpanManagerRatio: 0.15,
        reportingCutAsOf: '2026-05-26',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          warningLevel: 'decision_required' as const,
          warningLevelCode: 2,
          triggeredRuleIds: Object.freeze([
            'delayering_org_health:max_depth_above_target',
            'delayering_org_health:average_span_below_target',
            'delayering_org_health:thin_span_ratio_above_target',
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'delayering_org_health_warning_level:blocked:missing-target:v1',
      input: Object.freeze({
        maxDepth: 8,
        averageManagerSpan: 5,
        targetMinimumManagerSpan: 4,
        thinSpanManagerCount: 10,
        managerCount: 100,
        maxThinSpanManagerRatio: 0.15,
      }) as DelayeringOrgHealthWarningInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'Org health inputs and targets must be finite non-negative values, with managerCount greater than zero and maxThinSpanManagerRatio between zero and one',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateDelayeringOrgHealthWarningInputAgainstEvidence,
  reconciliationRule: delayeringOrgHealthWarningReconciliationRule,
  execute: executeDelayeringOrgHealthWarningLevel,
});

const FINANCE_OPERATING_INCOME_DERIVE_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceOperatingIncomeDerive', executeFinanceOperatingIncomeDerive],
] as const);
const FINANCE_EBITDA_FROM_OPERATING_INCOME_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceEbitdaFromOperatingIncome', executeFinanceEbitdaFromOperatingIncome],
] as const);
const FINANCE_NET_REVENUE_MARGIN_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceNetRevenueMargin', executeFinanceNetRevenueMargin],
] as const);
const FREIGHT_COST_REPRICING_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFreightCostRepricing', executeFreightCostRepricing],
  ['roundTo', roundTo],
] as const);
const DEMAND_REVENUE_REPRICING_DIGEST_INPUTS = implementationDigestInputs([
  ['executeDemandRevenueRepricing', executeDemandRevenueRepricing],
  ['roundTo', roundTo],
] as const);
const OPERATIONAL_REVENUE_PER_LOAD_DIGEST_INPUTS = implementationDigestInputs([
  ['executeOperationalRevenuePerLoad', executeOperationalRevenuePerLoad],
  ['roundTo', roundTo],
  ['positiveUsdStatusBand', positiveUsdStatusBand],
] as const);
const OPERATIONAL_COST_PER_LOAD_DIGEST_INPUTS = implementationDigestInputs([
  ['executeOperationalCostPerLoad', executeOperationalCostPerLoad],
  ['roundTo', roundTo],
  ['positiveUsdStatusBand', positiveUsdStatusBand],
] as const);
const FINANCE_OPERATING_MARGIN_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceOperatingMargin', executeFinanceOperatingMargin],
] as const);
const FINANCE_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceEbitdaDerive', executeFinanceEbitdaDerive],
  ['executeFinanceGrossMargin', executeFinanceGrossMargin],
  ['executeFinanceEbitdaMargin', executeFinanceEbitdaMargin],
  ['executeFinanceOperatingExpensePerHead', executeFinanceOperatingExpensePerHead],
  ['executeFinanceEbitdaTargetResolve', executeFinanceEbitdaTargetResolve],
  ['executeFinanceEbitdaGap', executeFinanceEbitdaGap],
] as const);
const FINANCE_OBJECTIVE_VALUE_GAP_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceObjectiveValueGap', executeFinanceObjectiveValueGap],
] as const);
const BENCHMARK_GAP_TO_MEDIAN_DOLLARIZED_DIGEST_INPUTS = implementationDigestInputs([
  ['executeBenchmarkGapToMedianDollarized', executeBenchmarkGapToMedianDollarized],
  ['benchmarkQuartilePosition', benchmarkQuartilePosition],
  ['benchmarkDollarValuePerMetricUnit', benchmarkDollarValuePerMetricUnit],
] as const);
const SCENARIO_INVESTMENT_ROI_DIGEST_INPUTS = implementationDigestInputs([
  ['executeScenarioInvestmentRoi', executeScenarioInvestmentRoi],
  ['roundTo', roundTo],
  ['isFiniteNumber', isFiniteNumber],
  ['asRecord', asRecord],
] as const);
const FINANCE_PUBLIC_FILING_EBITDA_MARGIN_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceEbitdaMargin', executeFinanceEbitdaMargin],
] as const);
const FINANCE_PUBLIC_FILING_OPERATING_EXPENSE_PER_HEAD_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceOperatingExpensePerHead', executeFinanceOperatingExpensePerHead],
] as const);

const financeOperatingIncomeDeriveDefinition: ApprovedFormulaDefinition<
  FinanceOperatingIncomeDeriveInput,
  FinanceOperatingIncomeDeriveOutput
> = Object.freeze({
  formulaId: 'operating_income_derive_v1',
  version: '1.0.0',
  displayName: 'Finance operating income derive',
  description:
    'Derives operating income from approved revenue, cost of revenue, and operating expense lines.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_operating_income_derive_input_v1',
  outputSchemaVersion: 'finance_operating_income_derive_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_OPERATING_INCOME_DERIVE_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_operating_income_derive:chrw-2025:ok:v1',
      input: Object.freeze({
        revenueUsd: 16232763000,
        costOfRevenueUsd: 13503353000,
        operatingExpenseUsd: 1934449000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          operatingIncomeUsd: 794961000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceOperatingIncomeDerive,
});

const financeEbitdaFromOperatingIncomeDefinition: ApprovedFormulaDefinition<
  FinanceEbitdaFromOperatingIncomeInput,
  FinanceEbitdaFromOperatingIncomeOutput
> = Object.freeze({
  formulaId: 'ebitda_from_operating_income_v1',
  version: '1.0.0',
  displayName: 'Finance EBITDA from operating income',
  description:
    'Derives EBITDA from approved operating income plus depreciation and amortization.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_ebitda_from_operating_income_input_v1',
  outputSchemaVersion: 'finance_ebitda_from_operating_income_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_EBITDA_FROM_OPERATING_INCOME_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_ebitda_from_operating_income:chrw-2025:ok:v1',
      input: Object.freeze({
        operatingIncomeUsd: 794961000,
        depreciationAmortizationUsd: 102818000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ebitdaUsd: 897779000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceEbitdaFromOperatingIncome,
});

const financeNetRevenueMarginDefinition: ApprovedFormulaDefinition<
  FinanceNetRevenueMarginInput,
  FinanceNetRevenueMarginOutput
> = Object.freeze({
  formulaId: 'net_revenue_margin_v1',
  version: '1.0.0',
  displayName: 'Finance net revenue margin',
  description:
    'Computes net revenue margin from approved adjusted gross profit divided by revenue.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_net_revenue_margin_input_v1',
  outputSchemaVersion: 'finance_net_revenue_margin_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_NET_REVENUE_MARGIN_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_net_revenue_margin:chrw-2025:ok:v1',
      input: Object.freeze({
        adjustedGrossProfitUsd: 2729410000,
        revenueUsd: 16232763000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          netRevenueMarginPct: 16.8142,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_net_revenue_margin:blocked:zero-revenue:v1',
      input: Object.freeze({
        adjustedGrossProfitUsd: 2729410,
        revenueUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'revenueUsd must be greater than zero',
      }),
    }),
  ]),
  execute: executeFinanceNetRevenueMargin,
});

const freightCostRepricingDefinition: ApprovedFormulaDefinition<
  FreightCostRepricingInput,
  FreightCostRepricingOutput
> = Object.freeze({
  formulaId: 'freight_cost_repricing_v1',
  version: '1.0.0',
  displayName: 'Freight cost repricing',
  description:
    'Reprices purchased transportation by applying an admitted market-rate sensitivity to a governed purchased-transportation baseline.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'freight_cost_repricing_input_v1',
  outputSchemaVersion: 'freight_cost_repricing_output_v1',
  expectedContextKind: 'decision_agent:scenario_chain:freight_cost_repricing',
  approvedSourceSystemRefs: Object.freeze([
    CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
    INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: FREIGHT_COST_REPRICING_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'freight_cost_repricing_v1:fixture:softens-5:v1',
      input: Object.freeze({
        baselinePurchasedTransportationUsd: 1_000_000,
        admittedSensitivity: 0.4,
        freightRateDeltaPct: -5,
        reportingCutAsOf: '2026-07-09',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselinePurchasedTransportationUsd: 1_000_000,
          admittedSensitivity: 0.4,
          freightRateDeltaPct: -5,
          purchasedTransportationDeltaUsd: -20_000,
          repricedPurchasedTransportationUsd: 980_000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'freight_cost_repricing_v1:blocked:bad-sensitivity:v1',
      input: Object.freeze({
        baselinePurchasedTransportationUsd: 1_000_000,
        admittedSensitivity: 1.25,
        freightRateDeltaPct: -5,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'freight repricing requires non-negative baseline USD, sensitivity in [0,1], and freightRateDeltaPct in [-50,50]',
      }),
    }),
  ]),
  execute: executeFreightCostRepricing,
});

const demandRevenueRepricingDefinition: ApprovedFormulaDefinition<
  DemandRevenueRepricingInput,
  DemandRevenueRepricingOutput
> = Object.freeze({
  formulaId: 'demand_revenue_repricing_v1',
  version: '1.0.0',
  displayName: 'Demand revenue repricing',
  description:
    'Reprices gross revenue by applying a tenant-admitted brokerage-volume sensitivity to a governed gross-revenue baseline.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'demand_revenue_repricing_input_v1',
  outputSchemaVersion: 'demand_revenue_repricing_output_v1',
  expectedContextKind:
    'decision_agent:scenario_chain:demand_revenue_repricing',
  approvedSourceSystemRefs: Object.freeze([
    FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
    FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
    INDUSTRY_BENCHMARKS_PUBLIC_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: DEMAND_REVENUE_REPRICING_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'demand_revenue_repricing_v1:fixture:softens-5:v1',
      input: Object.freeze({
        baselineGrossRevenueUsd: 16_000_000_000,
        admittedSensitivity: 0.6,
        demandVolumeDeltaPct: -5,
        reportingCutAsOf: '2026-07-09',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselineGrossRevenueUsd: 16_000_000_000,
          admittedSensitivity: 0.6,
          demandVolumeDeltaPct: -5,
          grossRevenueDeltaUsd: -480_000_000,
          repricedGrossRevenueUsd: 15_520_000_000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'demand_revenue_repricing_v1:blocked:bad-sensitivity:v1',
      input: Object.freeze({
        baselineGrossRevenueUsd: 16_000_000_000,
        admittedSensitivity: 1.01,
        demandVolumeDeltaPct: -5,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'demand revenue repricing requires non-negative baseline USD, sensitivity in [0,1], and demandVolumeDeltaPct in [-50,50]',
      }),
    }),
  ]),
  execute: executeDemandRevenueRepricing,
});

const operationalRevenuePerLoadDefinition: ApprovedFormulaDefinition<
  OperationalRevenuePerLoadInput,
  OperationalRevenuePerLoadOutput
> = Object.freeze({
  formulaId: 'operational_revenue_per_load_v1',
  version: '1.0.0',
  displayName: 'Operational revenue per load',
  description:
    'Computes revenue per load from governed revenue and fixture-labeled or source-owned load count evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'operational_revenue_per_load_input_v1',
  outputSchemaVersion: 'operational_revenue_per_load_output_v1',
  expectedContextKind: 'decision_agent:operational_drivers:fixture_loader',
  approvedSourceSystemRefs: Object.freeze([
    CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: OPERATIONAL_REVENUE_PER_LOAD_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'operational_revenue_per_load:fixture:truckload-dec-2025:v1',
      input: Object.freeze({
        revenueUsd: 300_000,
        loadCount: 120,
        reportingCutAsOf: '2026-01-05',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          revenueUsd: 300_000,
          loadCount: 120,
          revenuePerLoadUsd: 2500,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'operational_revenue_per_load:blocked:zero-loads:v1',
      input: Object.freeze({
        revenueUsd: 300_000,
        loadCount: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'revenueUsd must be non-negative and loadCount must be greater than zero',
      }),
    }),
  ]),
  execute: executeOperationalRevenuePerLoad,
});

const operationalCostPerLoadDefinition: ApprovedFormulaDefinition<
  OperationalCostPerLoadInput,
  OperationalCostPerLoadOutput
> = Object.freeze({
  formulaId: 'operational_cost_per_load_v1',
  version: '1.0.0',
  displayName: 'Operational cost per load',
  description:
    'Computes cost per load from governed transportation cost and fixture-labeled or source-owned load count evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'operational_cost_per_load_input_v1',
  outputSchemaVersion: 'operational_cost_per_load_output_v1',
  expectedContextKind: 'decision_agent:operational_drivers:fixture_loader',
  approvedSourceSystemRefs: Object.freeze([
    CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    FINANCE_PNL_MANUAL_ADMIN_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: OPERATIONAL_COST_PER_LOAD_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'operational_cost_per_load:fixture:truckload-dec-2025:v1',
      input: Object.freeze({
        costUsd: 240_000,
        loadCount: 120,
        reportingCutAsOf: '2026-01-05',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          costUsd: 240_000,
          loadCount: 120,
          costPerLoadUsd: 2000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'operational_cost_per_load:blocked:zero-loads:v1',
      input: Object.freeze({
        costUsd: 240_000,
        loadCount: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'costUsd must be non-negative and loadCount must be greater than zero',
      }),
    }),
  ]),
  execute: executeOperationalCostPerLoad,
});

const financeOperatingMarginDefinition: ApprovedFormulaDefinition<
  FinanceOperatingMarginInput,
  FinanceOperatingMarginOutput
> = Object.freeze({
  formulaId: 'operating_margin_v1',
  version: '1.0.0',
  displayName: 'Finance operating margin',
  description:
    'Computes operating margin from approved adjusted operating income divided by adjusted gross profit.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_operating_margin_input_v1',
  outputSchemaVersion: 'finance_operating_margin_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_OPERATING_MARGIN_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_operating_margin:chrw-2025:ok:v1',
      input: Object.freeze({
        adjustedOperatingIncomeUsd: 833733000,
        adjustedGrossProfitUsd: 2729410000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          operatingMarginPct: 30.5463,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_operating_margin:blocked:zero-adjusted-gross-profit:v1',
      input: Object.freeze({
        adjustedOperatingIncomeUsd: 833733000,
        adjustedGrossProfitUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'adjustedGrossProfitUsd must be greater than zero',
      }),
    }),
  ]),
  execute: executeFinanceOperatingMargin,
});

const financeEbitdaDeriveDefinition: ApprovedFormulaDefinition<
  FinanceEbitdaDeriveInput,
  FinanceEbitdaDeriveOutput
> = Object.freeze({
  formulaId: 'ebitda_derive_v1',
  version: '1.0.0',
  displayName: 'Finance EBITDA derive',
  description:
    'Derives EBITDA from approved operating income, D&A, and approved adjustments under the finance profile.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_ebitda_derive_input_v1',
  outputSchemaVersion: 'finance_ebitda_derive_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_ebitda_derive:ok:v1',
      input: Object.freeze({
        operatingIncomeUsd: 90000,
        depreciationAmortizationUsd: 15000,
        approvedAdjustmentsUsd: 5000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ebitdaUsd: 110000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceEbitdaDerive,
});

const financeGrossMarginDefinition: ApprovedFormulaDefinition<
  FinanceGrossMarginInput,
  FinanceGrossMarginOutput
> = Object.freeze({
  formulaId: 'gross_margin_v1',
  version: '1.0.0',
  displayName: 'Finance gross margin',
  description:
    'Computes gross margin from approved gross profit divided by revenue for the selected finance profile.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_gross_margin_input_v1',
  outputSchemaVersion: 'finance_gross_margin_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_gross_margin:ok:v1',
      input: Object.freeze({
        grossProfitUsd: 280000,
        revenueUsd: 1000000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          grossMarginPct: 28,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_gross_margin:blocked:zero-revenue:v1',
      input: Object.freeze({
        grossProfitUsd: 280000,
        revenueUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'revenueUsd must be greater than zero',
      }),
    }),
  ]),
  execute: executeFinanceGrossMargin,
});

const financeEbitdaMarginDefinition: ApprovedFormulaDefinition<
  FinanceEbitdaMarginInput,
  FinanceEbitdaMarginOutput
> = Object.freeze({
  formulaId: 'ebitda_margin_v1',
  version: '1.0.0',
  displayName: 'Finance EBITDA margin',
  description:
    'Computes EBITDA margin from approved derived EBITDA divided by approved revenue.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_ebitda_margin_input_v1',
  outputSchemaVersion: 'finance_ebitda_margin_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_ebitda_margin:ok:v1',
      input: Object.freeze({
        ebitdaUsd: 110000,
        revenueUsd: 1000000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ebitdaMarginPct: 11,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceEbitdaMargin,
});

const financeOperatingExpensePerHeadDefinition: ApprovedFormulaDefinition<
  FinanceOperatingExpensePerHeadInput,
  FinanceOperatingExpensePerHeadOutput
> = Object.freeze({
  formulaId: 'operating_expense_per_head_v1',
  version: '1.0.0',
  displayName: 'Finance operating expense per head',
  description:
    'Computes operating expense per reporting headcount from approved finance artifact values.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_operating_expense_per_head_input_v1',
  outputSchemaVersion: 'finance_operating_expense_per_head_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_operating_expense_per_head:ok:v1',
      input: Object.freeze({
        operatingExpenseUsd: 190000,
        reportingHeadcount: 120,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          operatingExpensePerHeadUsd: 1583.33,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceOperatingExpensePerHead,
});

const financePublicFilingEbitdaMarginDefinition: ApprovedFormulaDefinition<
  FinanceEbitdaMarginInput,
  FinanceEbitdaMarginOutput
> = Object.freeze({
  formulaId: 'public_filing_ebitda_margin_v1',
  version: '1.0.0',
  displayName: 'Finance public-filing EBITDA margin',
  description:
    'Computes EBITDA margin from the public-filing OI-to-EBITDA bridge divided by approved revenue.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_ebitda_margin_input_v1',
  outputSchemaVersion: 'finance_ebitda_margin_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_PUBLIC_FILING_EBITDA_MARGIN_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_public_filing_ebitda_margin:chrw-2025:ok:v1',
      input: Object.freeze({
        ebitdaUsd: 897779000,
        revenueUsd: 16232763000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ebitdaMarginPct: 5.5307,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceEbitdaMargin,
});

const financePublicFilingOperatingExpensePerHeadDefinition: ApprovedFormulaDefinition<
  FinanceOperatingExpensePerHeadInput,
  FinanceOperatingExpensePerHeadOutput
> = Object.freeze({
  formulaId: 'public_filing_operating_expense_per_head_v1',
  version: '1.0.0',
  displayName: 'Finance public-filing operating expense per head',
  description:
    'Computes operating expense per reporting headcount from approved public-filing finance values.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_operating_expense_per_head_input_v1',
  outputSchemaVersion: 'finance_operating_expense_per_head_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_PUBLIC_FILING_OPERATING_EXPENSE_PER_HEAD_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_public_filing_operating_expense_per_head:chrw-2025:ok:v1',
      input: Object.freeze({
        operatingExpenseUsd: 1934449000,
        reportingHeadcount: 12733,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          operatingExpensePerHeadUsd: 151924.06,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceOperatingExpensePerHead,
});

const financeEbitdaTargetResolveDefinition = createFinanceEbitdaTargetResolveDefinition({
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_IMPLEMENTATION_DIGEST_INPUTS,
  execute: executeFinanceEbitdaTargetResolve,
});

const financeEbitdaGapDefinition: ApprovedFormulaDefinition<
  FinanceEbitdaGapInput,
  FinanceEbitdaGapOutput
> = Object.freeze({
  formulaId: 'ebitda_gap_v1',
  version: '1.0.0',
  displayName: 'Finance EBITDA gap',
  description:
    'Computes target EBITDA minus baseline EBITDA as the finance value-gap anchor for composite consumers.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_ebitda_gap_input_v1',
  outputSchemaVersion: 'finance_ebitda_gap_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  // #4604 data-unlock 2026-08-15: same repair as ebitda_target_resolve_v1 — the
  // legacy refs admitted no live source system; admit the sanctioned finance
  // P&L systems. Digest changes; relifecycle via provision-finance-objective-uat.ts.
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_ebitda_gap:ok:v1',
      input: Object.freeze({
        ebitdaUsd: 110000,
        ebitdaTargetUsd: 150000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ebitdaGapUsd: 40000,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceEbitdaGap,
});

const financeObjectiveValueGapDefinition: ApprovedFormulaDefinition<
  FinanceObjectiveValueGapInput,
  FinanceObjectiveValueGapOutput
> = Object.freeze({
  formulaId: 'objective_value_gap_v1',
  version: '1.0.1',
  displayName: 'Finance objective value gap',
  description:
    'Computes target minus baseline for the configured finance objective metric as the finance-owned value-gap anchor.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_objective_value_gap_input_v1',
  outputSchemaVersion: 'finance_objective_value_gap_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_OBJECTIVE_VALUE_GAP_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_objective_value_gap:operating-income:v1',
      input: Object.freeze({
        objectiveMetricId: 'operating_income',
        baselineValue: 794961000,
        targetValue: 1002500000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          objectiveMetricId: 'operating_income',
          baselineValue: 794961000,
          targetValue: 1002500000,
          gapValue: 207539000,
          gapPct: 26.1068,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
  ]),
  execute: executeFinanceObjectiveValueGap,
});

const benchmarkGapToMedianDollarizedDefinition: ApprovedFormulaDefinition<
  BenchmarkGapToMedianDollarizedInput,
  BenchmarkGapToMedianDollarizedOutput
> = Object.freeze({
  formulaId: 'benchmark_gap_to_median_dollarized_v1',
  version: '1.0.0',
  displayName: 'Industry benchmark gap to median dollarized',
  description:
    'Classifies tenant metric position against a cited public industry reference range and dollarizes only the unfavorable gap to the median.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'benchmark_gap_to_median_dollarized_input_v1',
  outputSchemaVersion: 'benchmark_gap_to_median_dollarized_output_v1',
  expectedContextKind: INDUSTRY_BENCHMARK_CONTEXT_KIND,
  approvedSourceSystemRefs: INDUSTRY_BENCHMARK_SOURCE_REFS,
  implementationDigestInputs: BENCHMARK_GAP_TO_MEDIAN_DOLLARIZED_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'benchmark_gap_to_median:sga-percent:unfavorable:v1',
      input: Object.freeze({
        metricId: 'sga_percent_of_revenue',
        tenantMetricValue: 6.2,
        direction: 'lower_is_leaner' as const,
        referenceRange: Object.freeze({
          min: 2.881063,
          p25: 3.327451,
          median: 4.168137,
          p75: 7.267451,
          max: 14.489725,
        }),
        dollarization: Object.freeze({
          basis: 'percent_of_revenue' as const,
          revenueUsd: 16232763000,
        }),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricId: 'sga_percent_of_revenue',
          tenantMetricValue: 6.2,
          referenceMedian: 4.168137,
          quartilePosition: 'median_to_p75' as const,
          quartileIndex: 3 as const,
          rawDeltaToMedian: 2.0319,
          opportunityGapToMedian: 2.0319,
          favorableToMedian: false,
          dollarizationBasis: 'percent_of_revenue' as const,
          valuePerMetricUnitUsd: 162327630,
          gapToMedianDollarizedUsd: 329827505.27,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'benchmark_gap_to_median:operating-margin:favorable:v1',
      input: Object.freeze({
        metricId: 'operating_margin_percent',
        tenantMetricValue: 4.897262,
        direction: 'higher_is_better' as const,
        referenceRange: Object.freeze({
          min: -1.375827,
          p25: 3.285199,
          median: 4.226091,
          p75: 7.255945,
          max: 9.508945,
        }),
        dollarization: Object.freeze({
          basis: 'percent_of_revenue' as const,
          revenueUsd: 16232763000,
        }),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricId: 'operating_margin_percent',
          tenantMetricValue: 4.8973,
          referenceMedian: 4.226091,
          quartilePosition: 'median_to_p75' as const,
          quartileIndex: 3 as const,
          rawDeltaToMedian: 0.6712,
          opportunityGapToMedian: 0,
          favorableToMedian: true,
          dollarizationBasis: 'percent_of_revenue' as const,
          valuePerMetricUnitUsd: 162327630,
          gapToMedianDollarizedUsd: 0,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'benchmark_gap_to_median:headcount-per-revenue:critical:v1',
      input: Object.freeze({
        metricId: 'headcount_per_billion_revenue',
        tenantMetricValue: 1800,
        direction: 'lower_is_leaner' as const,
        referenceRange: Object.freeze({
          min: 290.486871,
          p25: 848.414055,
          median: 1421.221628,
          p75: 1765.066472,
          max: 4535.981366,
        }),
        dollarization: Object.freeze({
          basis: 'per_revenue_billion_unit' as const,
          revenueUsd: 16232763000,
          unitCostUsd: 100000,
        }),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricId: 'headcount_per_billion_revenue',
          tenantMetricValue: 1800,
          referenceMedian: 1421.221628,
          quartilePosition: 'above_p75' as const,
          quartileIndex: 4 as const,
          rawDeltaToMedian: 378.7784,
          opportunityGapToMedian: 378.7784,
          favorableToMedian: false,
          dollarizationBasis: 'per_revenue_billion_unit' as const,
          valuePerMetricUnitUsd: 1623276.3,
          gapToMedianDollarizedUsd: 614861954.22,
          statusBand: 'critical' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'benchmark_gap_to_median:tech-spend-percent-of-revenue:modeled:v1',
      input: Object.freeze({
        metricId: 'tech_spend_percent_of_revenue',
        tenantMetricValue: 2.2,
        direction: 'lower_is_leaner' as const,
        referenceRange: Object.freeze({
          min: 1.0,
          p25: 1.6,
          median: 2.2,
          p75: 3.1,
          max: 4.5,
        }),
        dollarization: Object.freeze({
          basis: 'percent_of_revenue' as const,
          revenueUsd: 16232763000,
        }),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricId: 'tech_spend_percent_of_revenue',
          tenantMetricValue: 2.2,
          referenceMedian: 2.2,
          quartilePosition: 'p25_to_median' as const,
          quartileIndex: 2 as const,
          rawDeltaToMedian: 0,
          opportunityGapToMedian: 0,
          favorableToMedian: true,
          dollarizationBasis: 'percent_of_revenue' as const,
          valuePerMetricUnitUsd: 162327630,
          gapToMedianDollarizedUsd: 0,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'benchmark_gap_to_median:capex-percent-of-revenue:favorable:v1',
      input: Object.freeze({
        metricId: 'capex_percent_of_revenue',
        tenantMetricValue: 0.120916,
        direction: 'lower_is_leaner' as const,
        referenceRange: Object.freeze({
          min: 0.120916,
          p25: 0.479727,
          median: 1.027517,
          p75: 1.288443,
          max: 8.054432,
        }),
        dollarization: Object.freeze({
          basis: 'percent_of_revenue' as const,
          revenueUsd: 16232763000,
        }),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricId: 'capex_percent_of_revenue',
          tenantMetricValue: 0.1209,
          referenceMedian: 1.027517,
          quartilePosition: 'below_p25' as const,
          quartileIndex: 1 as const,
          rawDeltaToMedian: -0.9066,
          opportunityGapToMedian: 0,
          favorableToMedian: true,
          dollarizationBasis: 'percent_of_revenue' as const,
          valuePerMetricUnitUsd: 162327630,
          gapToMedianDollarizedUsd: 0,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'benchmark_gap_to_median:personnel-expense-percent-of-revenue:critical:v1',
      input: Object.freeze({
        metricId: 'personnel_expense_percent_of_revenue',
        tenantMetricValue: 30,
        direction: 'lower_is_leaner' as const,
        referenceRange: Object.freeze({
          min: 8.440695,
          p25: 13.08471,
          median: 15.970845,
          p75: 23.4757855,
          max: 41.976217,
        }),
        dollarization: Object.freeze({
          basis: 'percent_of_revenue' as const,
          revenueUsd: 16232763000,
        }),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricId: 'personnel_expense_percent_of_revenue',
          tenantMetricValue: 30,
          referenceMedian: 15.970845,
          quartilePosition: 'above_p75' as const,
          quartileIndex: 4 as const,
          rawDeltaToMedian: 14.0292,
          opportunityGapToMedian: 14.0292,
          favorableToMedian: false,
          dollarizationBasis: 'percent_of_revenue' as const,
          valuePerMetricUnitUsd: 162327630,
          gapToMedianDollarizedUsd: 2277319482.05,
          statusBand: 'critical' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'benchmark_gap_to_median:blocked:zero-revenue:v1',
      input: Object.freeze({
        metricId: 'sga_percent_of_revenue',
        tenantMetricValue: 6.2,
        direction: 'lower_is_leaner' as const,
        referenceRange: Object.freeze({
          min: 2.881063,
          p25: 3.327451,
          median: 4.168137,
          p75: 7.267451,
          max: 14.489725,
        }),
        dollarization: Object.freeze({
          basis: 'percent_of_revenue' as const,
          revenueUsd: 0,
        }),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'percent_of_revenue dollarization requires positive revenueUsd.',
      }),
    }),
  ]),
  execute: executeBenchmarkGapToMedianDollarized,
});

const scenarioInvestmentRoiDefinition: ApprovedFormulaDefinition<
  ScenarioInvestmentRoiInput,
  ScenarioInvestmentRoiOutput
> = Object.freeze({
  formulaId: 'scenario_investment_roi_v1',
  version: '1.0.0',
  displayName: 'Scenario investment ROI',
  description:
    'Ranks scenario options by risk-adjusted value divided by governed total investment; incomplete requirements remain named gaps and do not produce ROI.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'scenario_investment_roi_input_v1',
  outputSchemaVersion: 'scenario_investment_roi_output_v1',
  expectedContextKind: 'decision_agent:workforce_reduction:investment_requirements',
  approvedSourceSystemRefs: [
    WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF,
    ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  ],
  implementationDigestInputs: SCENARIO_INVESTMENT_ROI_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'scenario_investment_roi_v1:workforce-option:base:v1',
      input: Object.freeze({
        riskAdjustedValueUsd: 500000,
        totalInvestmentUsd: 250000,
        reportingCutAsOf: '2026-07-06',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          riskAdjustedValueUsd: 500000,
          totalInvestmentUsd: 250000,
          roi: 2,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'scenario_investment_roi_v1:blocked:zero-investment:v1',
      input: Object.freeze({
        riskAdjustedValueUsd: 500000,
        totalInvestmentUsd: 0,
        reportingCutAsOf: '2026-07-06',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'totalInvestmentUsd must be greater than zero; incomplete requirements must stay named instead of returning infinite ROI',
      }),
    }),
    Object.freeze({
      traceId: 'scenario_investment_roi_v1:blocked:missing-value:v1',
      input: Object.freeze({
        totalInvestmentUsd: 250000,
        reportingCutAsOf: '2026-07-06',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'riskAdjustedValueUsd and totalInvestmentUsd are required',
      }),
    }),
  ]),
  execute: executeScenarioInvestmentRoi,
});

const pricingRealizationNetRevenueDeltaDefinition: ApprovedFormulaDefinition<
  PricingRealizationNetRevenueDeltaInput,
  PricingRealizationNetRevenueDeltaOutput
> = Object.freeze({
  formulaId: 'pricing_realization_net_revenue_delta_v1',
  version: '1.0.0',
  displayName: 'Pricing realization net-revenue delta',
  description:
    'Computes net-revenue delta from governed baseline revenue, planned sell-rate action, and expected net-revenue retention.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_realization_net_revenue_delta_input_v1',
  outputSchemaVersion: 'pricing_realization_net_revenue_delta_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingRealizationNetRevenueDelta', executePricingRealizationNetRevenueDelta],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_realization_net_revenue_delta:base:v1',
      input: Object.freeze({
        baselineRevenueUsd: 100000000,
        plannedSellRateIncreasePct: 0.02,
        expectedNetRevenueRetentionPct: 0.95,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          netRevenueDeltaUsd: 1900000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executePricingRealizationNetRevenueDelta,
});

const pricingRealizationElasticityGuardDefinition: ApprovedFormulaDefinition<
  PricingRealizationElasticityGuardInput,
  PricingRealizationElasticityGuardOutput
> = Object.freeze({
  formulaId: 'pricing_realization_elasticity_guard_v1',
  version: '1.0.0',
  displayName: 'Pricing realization flat-revenue guard',
  description:
    'Checks whether a pricing action stays inside the configured flat-revenue tolerance before it can support the no-top-line-growth bridge.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_realization_elasticity_guard_input_v1',
  outputSchemaVersion: 'pricing_realization_elasticity_guard_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingRealizationElasticityGuard', executePricingRealizationElasticityGuard],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_realization_elasticity_guard:flat:v1',
      input: Object.freeze({
        expectedRevenueGrowthPct: 0.004,
        expectedVolumeResponsePct: -0.005,
        flatRevenueTolerancePct: 0.01,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          flatRevenueEligible: true,
          expectedRevenueGrowthPct: 0.004,
          flatRevenueTolerancePct: 0.01,
          expectedVolumeResponsePct: -0.005,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executePricingRealizationElasticityGuard,
});

const pricingRealizationAgpContributionDefinition: ApprovedFormulaDefinition<
  PricingRealizationAgpContributionInput,
  PricingRealizationAgpContributionOutput
> = Object.freeze({
  formulaId: 'pricing_realization_agp_contribution_v1',
  version: '1.0.0',
  displayName: 'Pricing realization AGP contribution',
  description:
    'Converts net-revenue delta to AGP contribution through the configured 3PL flow-through basis.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_realization_agp_contribution_input_v1',
  outputSchemaVersion: 'pricing_realization_agp_contribution_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingRealizationAgpContribution', executePricingRealizationAgpContribution],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_realization_agp_contribution:flow-through:v1',
      input: Object.freeze({
        netRevenueDeltaUsd: 1900000,
        agpFlowThroughPct: 1,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          agpContributionUsd: 1900000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executePricingRealizationAgpContribution,
});

const pricingRealizationOiContributionDefinition: ApprovedFormulaDefinition<
  PricingRealizationOiContributionInput,
  PricingRealizationOiContributionOutput
> = Object.freeze({
  formulaId: 'pricing_realization_oi_contribution_v1',
  version: '1.0.0',
  displayName: 'Pricing realization OI contribution',
  description:
    'Computes operating-income contribution by netting governed execution cost from AGP contribution.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_realization_oi_contribution_input_v1',
  outputSchemaVersion: 'pricing_realization_oi_contribution_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingRealizationOiContribution', executePricingRealizationOiContribution],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_realization_oi_contribution:net-of-execution-cost:v1',
      input: Object.freeze({
        agpContributionUsd: 1900000,
        executionCostUsd: 800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          operatingIncomeContributionUsd: 1100000,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executePricingRealizationOiContribution,
});

const pricingQuoteInvoiceLeakageDefinition: ApprovedFormulaDefinition<
  PricingQuoteInvoiceLeakageInput,
  PricingQuoteInvoiceLeakageOutput
> = Object.freeze({
  formulaId: 'pricing_quote_invoice_leakage_v1',
  version: '1.0.0',
  displayName: 'Pricing quote-to-invoice leakage',
  description:
    'Computes quote-to-invoice leakage from governed quoted revenue, invoiced revenue, eligible revenue, and pricing action dimensions.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_quote_invoice_leakage_input_v1',
  outputSchemaVersion: 'pricing_quote_invoice_leakage_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingQuoteInvoiceLeakage', executePricingQuoteInvoiceLeakage],
    [
      'validatePricingQuoteInvoiceLeakageInputAgainstEvidence',
      validatePricingQuoteInvoiceLeakageInputAgainstEvidence,
    ],
    [
      'validatePricingQuoteInvoiceLeakageOutputAgainstEvidence',
      validatePricingQuoteInvoiceLeakageOutputAgainstEvidence,
    ],
    [
      'parsePricingQuoteInvoiceLeakageEvidenceFacts',
      parsePricingQuoteInvoiceLeakageEvidenceFacts,
    ],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_quote_invoice_leakage:base:v1',
      input: Object.freeze({
        customerCohort: 'enterprise-contract',
        segment: 'NAST',
        serviceLaneScope: 'contract-truckload',
        pricingActionId: 'pricing-action-42',
        quotedRevenueUsd: 8800000,
        invoicedRevenueUsd: 8380000,
        eligibleRevenueUsd: 8800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          quoteInvoiceLeakageUsd: 420000,
          eligibleRevenueUsd: 8800000,
          leakageRatePct: 4.7727,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'pricing_quote_invoice_leakage:blocked:missing-raw-inputs:v1',
      input: Object.freeze({
        customerCohort: 'enterprise-contract',
        segment: 'NAST',
        serviceLaneScope: 'contract-truckload',
        pricingActionId: 'pricing-action-42',
        eligibleRevenueUsd: 8800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'missing_formula_input: quotedRevenueUsd, invoicedRevenueUsd',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validatePricingQuoteInvoiceLeakageInputAgainstEvidence,
  reconciliationRule: Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: 'pricing_quote_invoice_leakage_reconciles_to_evidence_v1',
    description:
      'Quote-to-invoice leakage inputs and outputs must reconcile to approved raw quote/invoice pricing evidence.',
    validate: validatePricingQuoteInvoiceLeakageOutputAgainstEvidence,
  }),
  execute: executePricingQuoteInvoiceLeakage,
});

const costToServeActivityAllocationDefinition: ApprovedFormulaDefinition<
  CostToServeActivityAllocationInput,
  CostToServeActivityAllocationOutput
> = Object.freeze({
  formulaId: 'cts_activity_allocation_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve activity allocation',
  description:
    'Sums governed account activity-cost allocations and reports the allocated share of the approved cost pool.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_activity_allocation_input_v1',
  outputSchemaVersion: 'cts_activity_allocation_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeActivityAllocation', executeCostToServeActivityAllocation],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_activity_allocation:base:v1',
      input: Object.freeze({
        accountAllocatedCostsUsd: Object.freeze([120, 80, 50]),
        approvedCostPoolUsd: 500,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          fullyLoadedCostToServeUsd: 250,
          activityAllocatedCostUsd: 250,
          approvedCostPoolUsd: 500,
          allocatedCostPoolSharePct: 50,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeActivityAllocation,
});

const costToServeMarginDefinition: ApprovedFormulaDefinition<
  CostToServeMarginInput,
  CostToServeMarginOutput
> = Object.freeze({
  formulaId: 'cost_to_serve_margin_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve margin',
  description:
    'Computes account-level cost-to-serve margin, loss-making account count, and the negative margin pool from governed revenue and fully loaded cost.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cost_to_serve_margin_input_v1',
  outputSchemaVersion: 'cost_to_serve_margin_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeMargin', executeCostToServeMargin],
    ['costToServeAccountsAreValid', costToServeAccountsAreValid],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cost_to_serve_margin:base:v1',
      input: Object.freeze({
        accounts: Object.freeze([
          Object.freeze({
            accountId: 'acct-a',
            netRevenueUsd: 100,
            fullyLoadedCostToServeUsd: 70,
          }),
          Object.freeze({
            accountId: 'acct-b',
            netRevenueUsd: 80,
            fullyLoadedCostToServeUsd: 95,
          }),
        ]),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ctsMarginUsd: 15,
          lossMakingAccountCount: 1,
          negativeMarginPoolUsd: 15,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeMargin,
});

const costToServeServiceTierCostDefinition: ApprovedFormulaDefinition<
  CostToServeServiceTierCostInput,
  CostToServeServiceTierCostOutput
> = Object.freeze({
  formulaId: 'cts_service_tier_cost_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve service-tier cost',
  description:
    'Computes weighted service-tier cost per shipment from governed tier cost and shipment counts.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_service_tier_cost_input_v1',
  outputSchemaVersion: 'cts_service_tier_cost_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeServiceTierCost', executeCostToServeServiceTierCost],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_service_tier_cost:base:v1',
      input: Object.freeze({
        serviceTiers: Object.freeze([
          Object.freeze({
            serviceTierId: 'standard',
            shipmentCount: 100,
            totalServiceTierCostUsd: 1000,
          }),
          Object.freeze({
            serviceTierId: 'expedited',
            shipmentCount: 50,
            totalServiceTierCostUsd: 750,
          }),
        ]),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          serviceTierCostPerShipmentUsd: 11.6667,
          shipmentCount: 150,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeServiceTierCost,
});

const costToServeConcentrationHhiDefinition: ApprovedFormulaDefinition<
  CostToServeConcentrationHhiInput,
  CostToServeConcentrationHhiOutput
> = Object.freeze({
  formulaId: 'cts_concentration_hhi_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve profit concentration HHI',
  description:
    'Computes positive profit-pool concentration across governed customer accounts.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_concentration_hhi_input_v1',
  outputSchemaVersion: 'cts_concentration_hhi_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeConcentrationHhi', executeCostToServeConcentrationHhi],
    ['costToServeAccountsAreValid', costToServeAccountsAreValid],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_concentration_hhi:base:v1',
      input: Object.freeze({
        accounts: Object.freeze([
          Object.freeze({
            accountId: 'acct-a',
            netRevenueUsd: 100,
            fullyLoadedCostToServeUsd: 70,
          }),
          Object.freeze({
            accountId: 'acct-b',
            netRevenueUsd: 80,
            fullyLoadedCostToServeUsd: 65,
          }),
          Object.freeze({
            accountId: 'acct-c',
            netRevenueUsd: 40,
            fullyLoadedCostToServeUsd: 45,
          }),
        ]),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          accountProfitHhi: 0.555556,
          positiveProfitPoolUsd: 45,
          statusBand: 'critical' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeConcentrationHhi,
});

const costToServeTopNRevenueShareDefinition: ApprovedFormulaDefinition<
  CostToServeTopNRevenueShareInput,
  CostToServeTopNRevenueShareOutput
> = Object.freeze({
  formulaId: 'cts_top_n_revenue_share_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve top-N revenue share',
  description:
    'Computes the revenue concentration share carried by the highest-revenue governed customer accounts.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_top_n_revenue_share_input_v1',
  outputSchemaVersion: 'cts_top_n_revenue_share_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeTopNRevenueShare', executeCostToServeTopNRevenueShare],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_top_n_revenue_share:base:v1',
      input: Object.freeze({
        accounts: Object.freeze([
          Object.freeze({
            accountId: 'acct-a',
            netRevenueUsd: 500,
          }),
          Object.freeze({
            accountId: 'acct-b',
            netRevenueUsd: 300,
          }),
          Object.freeze({
            accountId: 'acct-c',
            netRevenueUsd: 200,
          }),
        ]),
        topN: 2,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          topNRevenueSharePct: 80,
          topNRevenueUsd: 800,
          totalRevenueUsd: 1000,
          topNAccountIds: Object.freeze(['acct-a', 'acct-b']),
          accountCount: 3,
          topN: 2,
          statusBand: 'critical' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeTopNRevenueShare,
});

const costToServeBenchmarkDefinition: ApprovedFormulaDefinition<
  CostToServeBenchmarkInput,
  CostToServeBenchmarkOutput
> = Object.freeze({
  formulaId: 'cts_benchmark_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve benchmark gap',
  description:
    'Compares fully loaded cost-to-serve ratio with the approved industry profile benchmark and warning thresholds.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_benchmark_input_v1',
  outputSchemaVersion: 'cts_benchmark_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeBenchmark', executeCostToServeBenchmark],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_benchmark:watch:v1',
      input: Object.freeze({
        accountNetRevenueUsd: 100,
        fullyLoadedCostToServeUsd: 83,
        benchmarkCostToServeRatioPct: 78,
        watchCostToServeRatioPct: 82,
        decisionCostToServeRatioPct: 90,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          costToServeRatioPct: 83,
          ctsBenchmarkGapPct: 5,
          warningLevel: 'watch' as const,
          warningLevelCode: 1,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeBenchmark,
});

const costToServeRecoveryOpportunityDefinition: ApprovedFormulaDefinition<
  CostToServeRecoveryOpportunityInput,
  CostToServeRecoveryOpportunityOutput
> = Object.freeze({
  formulaId: 'cts_recovery_opportunity_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve recovery opportunity',
  description:
    'Computes recoverable negative margin using the approved recovery factor and exposes the residual against finance-approved recovery.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_recovery_opportunity_input_v1',
  outputSchemaVersion: 'cts_recovery_opportunity_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeRecoveryOpportunity', executeCostToServeRecoveryOpportunity],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_recovery_opportunity:reconciled:v1',
      input: Object.freeze({
        negativeMarginPoolUsd: 100,
        recoveryFactorPct: 60,
        approvedFinanceRecoveryUsd: 60,
        financeOperatingIncomeUsd: 1000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ctsRecoveryOpportunityUsd: 60,
          modeledRecoveryUsd: 60,
          approvedFinanceRecoveryUsd: 60,
          ctsFinanceReconciliationResidualUsd: 0,
          recoveryShareOfOperatingIncomePct: 6,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeRecoveryOpportunity,
});

const costToServeRepriceDeltaDefinition: ApprovedFormulaDefinition<
  CostToServeRepriceDeltaInput,
  CostToServeRepriceDeltaOutput
> = Object.freeze({
  formulaId: 'cts_reprice_delta_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve repricing delta',
  description:
    'Computes repricing margin recovery on loss-making accounts using governed revenue and price elasticity factors.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_reprice_delta_input_v1',
  outputSchemaVersion: 'cts_reprice_delta_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeRepriceDelta', executeCostToServeRepriceDelta],
    ['costToServeAccountsAreValid', costToServeAccountsAreValid],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_reprice_delta:loss-accounts:v1',
      input: Object.freeze({
        accounts: Object.freeze([
          Object.freeze({
            accountId: 'acct-a',
            netRevenueUsd: 100,
            fullyLoadedCostToServeUsd: 120,
            customerPriceElasticityFactor: 0.5,
          }),
          Object.freeze({
            accountId: 'acct-b',
            netRevenueUsd: 80,
            fullyLoadedCostToServeUsd: 70,
            customerPriceElasticityFactor: 0.5,
          }),
        ]),
        priceLiftPct: 5,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ctsRepriceDeltaUsd: 2.5,
          affectedAccountCount: 1,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeRepriceDelta,
});

const costToServeServiceRedesignDefinition: ApprovedFormulaDefinition<
  CostToServeServiceRedesignInput,
  CostToServeServiceRedesignOutput
> = Object.freeze({
  formulaId: 'cts_service_redesign_v1',
  version: '1.0.0',
  displayName: 'Cost-to-serve service redesign delta',
  description:
    'Computes service-redesign delta from current versus target service-tier cost and explicit attrition risk.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cts_service_redesign_input_v1',
  outputSchemaVersion: 'cts_service_redesign_output_v1',
  expectedContextKind: COST_TO_SERVE_CONTEXT_KIND,
  approvedSourceSystemRefs: COST_TO_SERVE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostToServeServiceRedesign', executeCostToServeServiceRedesign],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cts_service_redesign:net-of-attrition:v1',
      input: Object.freeze({
        currentServiceTierCostPerShipmentUsd: 12,
        targetServiceTierCostPerShipmentUsd: 8,
        migratableAccountCount: 100,
        attritionRiskUsd: 50,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          ctsServiceRedesignDeltaUsd: 350,
          serviceCostDeltaPerShipmentUsd: 4,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  execute: executeCostToServeServiceRedesign,
});

const pricingDiscountLeakageDefinition: ApprovedFormulaDefinition<
  PricingDiscountLeakageInput,
  PricingDiscountLeakageOutput
> = Object.freeze({
  formulaId: 'discount_leakage_v1',
  version: '1.0.0',
  displayName: 'Pricing discount policy leakage',
  description:
    'Computes off-policy discount leakage from approved discount authority bands and transaction-level list/net/discount evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_discount_leakage_input_v1',
  outputSchemaVersion: 'pricing_discount_leakage_output_v1',
  approvedSourceSystemRefs: PRICING_DISCOUNT_LEAKAGE_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingDiscountLeakage', executePricingDiscountLeakage],
    [
      'validatePricingDiscountLeakageInputAgainstEvidence',
      validatePricingDiscountLeakageInputAgainstEvidence,
    ],
    [
      'validatePricingDiscountLeakageOutputAgainstEvidence',
      validatePricingDiscountLeakageOutputAgainstEvidence,
    ],
    [
      'parsePricingDiscountLeakageEvidenceFacts',
      parsePricingDiscountLeakageEvidenceFacts,
    ],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'discount_leakage:chrw-3pl-governed-demo:v1',
      input: Object.freeze({
        authorityBands: Object.freeze([
          Object.freeze({ bandId: 'standard', maxDiscountPctOfList: 0.07 }),
        ]),
        transactions: Object.freeze([
          Object.freeze({
            rowId: 'TX01',
            segment: 'Retail contract',
            rep: 'Pricing A',
            authorityBandId: 'standard',
            listRevenueUsd: 1000000,
            netRevenueUsd: 900000,
            discountUsd: 100000,
            offPolicyLeakageUsd: 30000,
          }),
        ]),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          eligibleListRevenueUsd: 1000000,
          totalNetRevenueUsd: 900000,
          totalDiscountUsd: 100000,
          withinPolicyDiscountUsd: 70000,
          offPolicyDiscountUsd: 30000,
          discountLeakageUsd: 30000,
          discountLeakageRatePct: 3,
          statusBand: 'critical' as const,
        }),
      }),
    }),
  ]),
  validateInputAgainstEvidence: validatePricingDiscountLeakageInputAgainstEvidence,
  reconciliationRule: Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: 'discount_leakage_reconciles_to_policy_and_transactions_v1',
    description:
      'Discount leakage inputs and outputs must reconcile to approved discount policy authority and transaction evidence.',
    validate: validatePricingDiscountLeakageOutputAgainstEvidence,
  }),
  execute: executePricingDiscountLeakage,
});

const realizationRateDefinition: ApprovedFormulaDefinition<
  RealizationRateInput,
  RealizationRateOutput
> = Object.freeze({
  formulaId: 'realization_rate_v1',
  version: '1.0.0',
  displayName: 'Pricing segment realization rate',
  description:
    'Computes realized invoiced revenue as a percentage of governed quoted revenue for a pricing segment scope.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'realization_rate_input_v1',
  outputSchemaVersion: 'realization_rate_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeRealizationRate', executeRealizationRate],
    ['validateRealizationRateInputAgainstEvidence', validateRealizationRateInputAgainstEvidence],
    [
      'validateRealizationRateOutputAgainstEvidence',
      validateRealizationRateOutputAgainstEvidence,
    ],
    [
      'parsePricingQuoteInvoiceLeakageEvidenceFacts',
      parsePricingQuoteInvoiceLeakageEvidenceFacts,
    ],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'realization_rate:segment:base:v1',
      input: Object.freeze({
        segment: 'NAST',
        quotedRevenueUsd: 8800000,
        invoicedRevenueUsd: 8380000,
        eligibleRevenueUsd: 8800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          quotedRevenueUsd: 8800000,
          invoicedRevenueUsd: 8380000,
          eligibleRevenueUsd: 8800000,
          realizationGapUsd: 420000,
          realizationRatePct: 95.2273,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'realization_rate:blocked:missing-revenue:v1',
      input: Object.freeze({
        segment: 'NAST',
        eligibleRevenueUsd: 8800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'missing_formula_input: quotedRevenueUsd, invoicedRevenueUsd',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateRealizationRateInputAgainstEvidence,
  reconciliationRule: Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: 'realization_rate_reconciles_to_pricing_evidence_v1',
    description:
      'Realization-rate inputs and outputs must reconcile to approved raw quote/invoice pricing evidence.',
    validate: validateRealizationRateOutputAgainstEvidence,
  }),
  execute: executeRealizationRate,
});

const priceVolumeMixBridgeDefinition: ApprovedFormulaDefinition<
  PriceVolumeMixBridgeInput,
  PriceVolumeMixBridgeOutput
> = Object.freeze({
  formulaId: 'price_volume_mix_bridge_v1',
  version: '1.0.0',
  displayName: 'Pricing price/volume/mix bridge',
  description:
    'Decomposes governed pricing revenue movement into price, volume, and residual mix effects, then translates the net movement to AGP and operating income.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'price_volume_mix_bridge_input_v1',
  outputSchemaVersion: 'price_volume_mix_bridge_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePriceVolumeMixBridge', executePriceVolumeMixBridge],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'price_volume_mix_bridge:chrw-3pl:v1',
      input: Object.freeze({
        baselineRevenueUsd: 100000000,
        plannedSellRateIncreasePct: 0.02,
        expectedVolumeResponsePct: -0.005,
        expectedRevenueGrowthPct: 0.004,
        expectedNetRevenueRetentionPct: 0.95,
        agpFlowThroughPct: 1,
        executionCostUsd: 800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          priceImpactUsd: 1900000,
          volumeImpactUsd: -475000,
          mixImpactUsd: -1045000,
          pvmNetRevenueDeltaUsd: 380000,
          pvmAgpContributionUsd: 380000,
          pvmOperatingIncomeContributionUsd: -420000,
          statusBand: 'critical' as const,
        }),
      }),
    }),
  ]),
  execute: executePriceVolumeMixBridge,
});

const pricingStructureMixShiftMarginContributionDefinition: ApprovedFormulaDefinition<
  PricingStructureMixShiftMarginContributionInput,
  PricingStructureMixShiftMarginContributionOutput
> = Object.freeze({
  formulaId: 'pricing_structure_mix_shift_margin_contribution_v1',
  version: '1.0.0',
  displayName: 'Pricing structure mix-shift margin contribution',
  description:
    'Computes net-revenue margin contribution from shifting 3PL pricing mix from transactional spot loads toward contract lanes using governed period and segment mix evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_structure_mix_shift_margin_contribution_input_v1',
  outputSchemaVersion: 'pricing_structure_mix_shift_margin_contribution_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    [
      'executePricingStructureMixShiftMarginContribution',
      executePricingStructureMixShiftMarginContribution,
    ],
    [
      'validatePricingStructureMixShiftInputAgainstEvidence',
      validatePricingStructureMixShiftInputAgainstEvidence,
    ],
    [
      'validatePricingStructureMixShiftOutputAgainstEvidence',
      validatePricingStructureMixShiftOutputAgainstEvidence,
    ],
    [
      'parsePricingStructureMixShiftEvidenceFacts',
      parsePricingStructureMixShiftEvidenceFacts,
    ],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_structure_mix_shift_margin_contribution:spot-to-contract:v1',
      input: Object.freeze({
        period: '2026-Q2',
        segment: 'Retail truckload',
        totalNetRevenueUsd: 10000000,
        baselineSpotMixPct: 55,
        currentSpotMixPct: 40,
        baselineContractMixPct: 45,
        currentContractMixPct: 60,
        spotNetRevenueMarginPct: 16,
        contractNetRevenueMarginPct: 24,
        reportingCutAsOf: '2026-06-30T00:00:00.000Z',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          spotToContractMixShiftPct: 15,
          marginSpreadPct: 8,
          mixShiftContributionUsd: 120000,
          netRevenueMarginContributionBps: 120,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
  ]),
  validateInputAgainstEvidence: validatePricingStructureMixShiftInputAgainstEvidence,
  reconciliationRule: Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: 'pricing_structure_mix_shift_margin_contribution_reconciles_to_evidence_v1',
    description:
      'Pricing structure mix-shift formula inputs and outputs must reconcile to approved spot-versus-contract pricing structure evidence.',
    validate: validatePricingStructureMixShiftOutputAgainstEvidence,
  }),
  execute: executePricingStructureMixShiftMarginContribution,
});

function varianceDecompositionGoldenProvenance(id: string): VarianceDecompositionCellProvenance {
  return Object.freeze({
    factIds: Object.freeze([id]),
    sourceClasses: Object.freeze(['public-filing']),
    sourceSystemRefs: Object.freeze(['finance-pnl-governed-snapshot']),
    evidenceRefIds: Object.freeze([`evidence:${id}`]),
    artifactIds: Object.freeze(['chrw-finance-pnl-governed-snapshot-v1']),
    ingestJobIds: Object.freeze(['chrw-finance-pnl-promotion-v1']),
    asOf: Object.freeze(['2026-02-14T00:00:00.000Z']),
    approvedAt: Object.freeze(['2026-07-08T00:00:00.000Z']),
  });
}

function varianceDecompositionGoldenCell(
  input: Readonly<{
    metricKey: string;
    memberKey?: string;
    periodKey: string;
    valueNumeric: number;
  }>
): VarianceDecompositionCell {
  const memberKey = input.memberKey ?? GOVERNED_METRIC_FACT_ALL_MEMBER_KEY;
  return Object.freeze({
    metricKey: input.metricKey,
    periodType: 'fiscal_year',
    periodKey: input.periodKey,
    dimensions:
      memberKey === GOVERNED_METRIC_FACT_ALL_MEMBER_KEY
        ? Object.freeze({})
        : Object.freeze({ business_line: memberKey }),
    valueNumeric: String(input.valueNumeric),
    unit: 'USD',
    provenance: varianceDecompositionGoldenProvenance(
      `chrw:${input.metricKey}:${memberKey}:${input.periodKey}`
    ),
  });
}

const VARIANCE_DECOMPOSITION_CHRW_GOLDEN_INPUT: VarianceDecompositionInput = Object.freeze({
  tenantId: 'chrw-uat',
  metricKey: 'income_from_operations_usd',
  basisMetricKey: 'gross_revenue_usd',
  dimension: 'business_line',
  memberKeys: Object.freeze(['nast', 'global_forwarding', 'all_other_corporate']),
  periodType: 'fiscal_year',
  baselinePeriodKey: '2024',
  comparisonPeriodKey: '2025',
  grainRollup: 'reported_segment',
  cells: Object.freeze([
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      memberKey: 'nast',
      periodKey: '2024',
      valueNumeric: 531292000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'gross_revenue_usd',
      memberKey: 'nast',
      periodKey: '2024',
      valueNumeric: 11727539000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      memberKey: 'global_forwarding',
      periodKey: '2024',
      valueNumeric: 212476000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'gross_revenue_usd',
      memberKey: 'global_forwarding',
      periodKey: '2024',
      valueNumeric: 3805018000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      memberKey: 'all_other_corporate',
      periodKey: '2024',
      valueNumeric: -74627000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'gross_revenue_usd',
      memberKey: 'all_other_corporate',
      periodKey: '2024',
      valueNumeric: 2192399000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      memberKey: 'nast',
      periodKey: '2025',
      valueNumeric: 621836000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'gross_revenue_usd',
      memberKey: 'nast',
      periodKey: '2025',
      valueNumeric: 11562714000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      memberKey: 'global_forwarding',
      periodKey: '2025',
      valueNumeric: 183783000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'gross_revenue_usd',
      memberKey: 'global_forwarding',
      periodKey: '2025',
      valueNumeric: 3090018000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      memberKey: 'all_other_corporate',
      periodKey: '2025',
      valueNumeric: -10658000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'gross_revenue_usd',
      memberKey: 'all_other_corporate',
      periodKey: '2025',
      valueNumeric: 1580031000,
    }),
  ]),
  totalMetricCells: Object.freeze([
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      periodKey: '2024',
      valueNumeric: 669141000,
    }),
    varianceDecompositionGoldenCell({
      metricKey: 'income_from_operations_usd',
      periodKey: '2025',
      valueNumeric: 794961000,
    }),
  ]),
  reportingCutAsOf: '2026-02-14',
});

const varianceDecompositionDefinition: ApprovedFormulaDefinition<
  VarianceDecompositionInput,
  VarianceDecompositionOutput
> = Object.freeze({
  formulaId: 'variance_decomposition_v1',
  version: '1.0.0',
  displayName: 'Variance decomposition',
  description:
    'Decomposes a governed metric movement across one dimension into within-line rate effect and mix/basis effect using compiler-validated GovernedMetricFact cells.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'variance_decomposition_input_v1',
  outputSchemaVersion: 'variance_decomposition_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  additionalExpectedContextKinds: Object.freeze([REALIZATION_COMPONENT_VARIANCE_CONTEXT_KIND]),
  approvedSourceSystemRefs: Object.freeze([
    ...FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
    ...REALIZATION_COMPONENT_VARIANCE_SOURCE_REFS,
  ]),
  implementationDigestInputs: implementationDigestInputs([
    ['executeVarianceDecomposition', executeVarianceDecomposition],
    [
      'validateVarianceDecompositionInputAgainstEvidence',
      validateVarianceDecompositionInputAgainstEvidence,
    ],
    ['buildVarianceDecompositionMetricQuerySpec', buildVarianceDecompositionMetricQuerySpec],
    ['buildVarianceDecompositionTotalQuerySpec', buildVarianceDecompositionTotalQuerySpec],
    ['normalizeVarianceResidual', normalizeVarianceResidual],
    ['roundVarianceValue', roundVarianceValue],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'variance_decomposition_v1:chrw-oi-business-line:fy2024-fy2025',
      input: VARIANCE_DECOMPOSITION_CHRW_GOLDEN_INPUT,
      expected: executeVarianceDecomposition(VARIANCE_DECOMPOSITION_CHRW_GOLDEN_INPUT),
    }),
    Object.freeze({
      traceId: 'variance_decomposition_v1:blocked:missing-period-cells:v1',
      input: Object.freeze({
        ...VARIANCE_DECOMPOSITION_CHRW_GOLDEN_INPUT,
        cells: Object.freeze(VARIANCE_DECOMPOSITION_CHRW_GOLDEN_INPUT.cells.slice(0, -1)),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_period_cells' as const,
        message:
          'Variance decomposition is missing gross_revenue_usd for all_other_corporate in 2025.',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateVarianceDecompositionInputAgainstEvidence,
  execute: executeVarianceDecomposition,
});

const pvmGoldenInput: PvmDecompositionInput = Object.freeze({
  items: Object.freeze([
    Object.freeze({ itemKey: 'ocean', baselinePrice: 100, baselineQuantity: 60, comparisonPrice: 110, comparisonQuantity: 40 }),
    Object.freeze({ itemKey: 'air', baselinePrice: 200, baselineQuantity: 40, comparisonPrice: 190, comparisonQuantity: 70 }),
  ]),
  governedTotalChange: 3700,
});

const pvmDecompositionDefinition: ApprovedFormulaDefinition<
  PvmDecompositionInput,
  PvmDecompositionOutput
> = Object.freeze({
  formulaId: 'pvm_decomposition_v2',
  version: '1.0.0',
  displayName: 'Retrospective price volume mix decomposition',
  description: 'Decomposes governed price-times-quantity movement into symmetric-midpoint price, volume, mix, and a named residual.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pvm_decomposition_input_v2',
  outputSchemaVersion: 'pvm_decomposition_output_v2',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  // #4788 single-sourcing: the segment-margin-cleanup context kind and snapshot
  // source ref are imported from their canonical homes (module evidence contract,
  // approved-source-systems), never retyped, so a rename there cannot silently
  // strand this formula while loaders emit under the new identity.
  additionalExpectedContextKinds: Object.freeze([
    SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS.governedSnapshot,
  ]),
  approvedSourceSystemRefs: Object.freeze([
    ...FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
    SEGMENT_MARGIN_CLEANUP_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: implementationDigestInputs([
    ['executePvmDecomposition', executePvmDecomposition],
    ['varianceFamilyTolerance', varianceFamilyTolerance],
    ['varianceFamilyFiniteValues', varianceFamilyFiniteValues],
    ['normalizeVarianceResidual', normalizeVarianceResidual],
    ['roundVarianceFamilyPublishedValue', roundVarianceFamilyPublishedValue],
    ['sumVarianceFamilyValues', sumVarianceFamilyValues],
    ['varianceFamilyNonFiniteIntermediateBlock', varianceFamilyNonFiniteIntermediateBlock],
    ['varianceFamilyReconciles', varianceFamilyReconciles],
    ['roundVarianceValue', roundVarianceValue],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pvm_decomposition_v2:midpoint:priced-lines:v1',
      input: pvmGoldenInput,
      expected: executePvmDecomposition(pvmGoldenInput),
    }),
  ]),
  execute: executePvmDecomposition,
});

const costRateVolumeGoldenInput: CostRateVolumeInput = Object.freeze({
  baselineRate: 10,
  baselineActivity: 100,
  comparisonRate: 8,
  comparisonActivity: 120,
});

const costRateVolumeEfficiencyGoldenInput: CostRateVolumeInput = Object.freeze({
  ...costRateVolumeGoldenInput,
  efficiencyStandard: Object.freeze({
    standardInputPerOutput: 2,
    baselineOutput: 50,
    comparisonOutput: 55,
  }),
});

const costRateVolumeDefinition: ApprovedFormulaDefinition<
  CostRateVolumeInput,
  CostRateVolumeOutput
> = Object.freeze({
  formulaId: 'cost_rate_volume_v1',
  version: '1.0.0',
  displayName: 'Cost rate and volume decomposition',
  description: 'Decomposes governed cost movement with midpoint rate and volume effects expressed in operating-income favorability signs.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cost_rate_volume_input_v1',
  outputSchemaVersion: 'cost_rate_volume_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCostRateVolume', executeCostRateVolume],
    ['varianceFamilyTolerance', varianceFamilyTolerance],
    ['varianceFamilyFiniteValues', varianceFamilyFiniteValues],
    ['normalizeVarianceResidual', normalizeVarianceResidual],
    ['varianceFamilyNonFiniteIntermediateBlock', varianceFamilyNonFiniteIntermediateBlock],
    ['varianceFamilyReconciles', varianceFamilyReconciles],
    ['roundVarianceValue', roundVarianceValue],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cost_rate_volume_v1:oi-favorability:midpoint:v1',
      input: costRateVolumeGoldenInput,
      expected: executeCostRateVolume(costRateVolumeGoldenInput),
    }),
    Object.freeze({
      traceId: 'cost_rate_volume_v1:governed-efficiency-standard:v1',
      input: costRateVolumeEfficiencyGoldenInput,
      expected: executeCostRateVolume(costRateVolumeEfficiencyGoldenInput),
    }),
  ]),
  execute: executeCostRateVolume,
});

const fxIsolationGoldenInput: FxIsolationInput = Object.freeze({
  lines: Object.freeze([
    Object.freeze({
      lineKey: 'eur',
      baselineReportedValue: 100,
      comparisonReportedValue: 126,
      comparisonLocalValue: 100,
      baselineReportingRate: 1,
      comparisonReportingRate: 1.2,
    }),
  ]),
});

const fxIsolationDefinition: ApprovedFormulaDefinition<FxIsolationInput, FxIsolationOutput> =
  Object.freeze({
    formulaId: 'fx_isolation_v1',
    version: '1.0.0',
    displayName: 'FX translation isolation',
    description: 'Separates governed translation-rate movement from the constant-currency change and names the residual.',
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: 'fx_isolation_input_v1',
    outputSchemaVersion: 'fx_isolation_output_v1',
    expectedContextKind: FINANCE_CONTEXT_KIND,
    approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs([
      ['executeFxIsolation', executeFxIsolation],
      ['varianceFamilyTolerance', varianceFamilyTolerance],
      ['varianceFamilyFiniteValues', varianceFamilyFiniteValues],
      ['normalizeVarianceResidual', normalizeVarianceResidual],
      ['varianceFamilyNonFiniteIntermediateBlock', varianceFamilyNonFiniteIntermediateBlock],
      ['varianceFamilyReconciles', varianceFamilyReconciles],
      ['roundVarianceValue', roundVarianceValue],
    ]),
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: 'fx_isolation_v1:governed-rates:v1',
        input: fxIsolationGoldenInput,
        expected: executeFxIsolation(fxIsolationGoldenInput),
      }),
    ]),
    execute: executeFxIsolation,
  });

const oneoffIsolationGoldenInput: OneoffIsolationInput = Object.freeze({
  lines: Object.freeze([
    Object.freeze({ lineKey: 'operations', baselineValue: 100, comparisonValue: 120, nonrecurring: false }),
    Object.freeze({ lineKey: 'settlement', baselineValue: 0, comparisonValue: -5, nonrecurring: true }),
    Object.freeze({ lineKey: 'unmapped', baselineValue: 2, comparisonValue: 3 }),
  ]),
});

const oneoffIsolationDefinition: ApprovedFormulaDefinition<
  OneoffIsolationInput,
  OneoffIsolationOutput
> = Object.freeze({
  formulaId: 'oneoff_isolation_v1',
  version: '1.0.0',
  displayName: 'Underlying and one-off isolation',
  description: 'Partitions governed nonrecurring movement while retaining and naming unclassified lines in underlying performance.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'oneoff_isolation_input_v1',
  outputSchemaVersion: 'oneoff_isolation_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeOneoffIsolation', executeOneoffIsolation],
    ['varianceFamilyTolerance', varianceFamilyTolerance],
    ['varianceFamilyFiniteValues', varianceFamilyFiniteValues],
    ['normalizeVarianceResidual', normalizeVarianceResidual],
    ['varianceFamilyNonFiniteIntermediateBlock', varianceFamilyNonFiniteIntermediateBlock],
    ['varianceFamilyReconciles', varianceFamilyReconciles],
    ['roundVarianceValue', roundVarianceValue],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'oneoff_isolation_v1:unclassified-remains-underlying:v1',
      input: oneoffIsolationGoldenInput,
      expected: executeOneoffIsolation(oneoffIsolationGoldenInput),
    }),
  ]),
  execute: executeOneoffIsolation,
});

const oiBridgeGoldenInput: OiBridgeComposeInput = Object.freeze({
  root: Object.freeze({
    nodeKey: 'enterprise',
    reportedDelta: 30,
    revenueSideEffect: 50,
    costSideEffect: 20,
    children: Object.freeze([
      Object.freeze({ nodeKey: 'segment-a', reportedDelta: 20, revenueSideEffect: 35, costSideEffect: 15 }),
      Object.freeze({ nodeKey: 'segment-b', reportedDelta: 10, revenueSideEffect: 15, costSideEffect: 5 }),
    ]),
  }),
});

const oiBridgeComposeDefinition: ApprovedFormulaDefinition<
  OiBridgeComposeInput,
  OiBridgeComposeOutput
> = Object.freeze({
  formulaId: 'oi_bridge_compose_v1',
  version: '1.0.0',
  displayName: 'Nested operating-income bridge composition',
  description: 'Composes consolidated-to-account operating-income bridges and blocks any parent, child, revenue, or cost tie-out failure.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'oi_bridge_compose_input_v1',
  outputSchemaVersion: 'oi_bridge_compose_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeOiBridgeCompose', executeOiBridgeCompose],
    ['executeOiBridgeNode', executeOiBridgeNode],
    ['varianceFamilyTolerance', varianceFamilyTolerance],
    ['varianceFamilyFiniteValues', varianceFamilyFiniteValues],
    ['normalizeVarianceResidual', normalizeVarianceResidual],
    ['varianceFamilyNonFiniteIntermediateBlock', varianceFamilyNonFiniteIntermediateBlock],
    ['varianceFamilyReconciles', varianceFamilyReconciles],
    ['roundVarianceValue', roundVarianceValue],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'oi_bridge_compose_v1:nested-must-sum:v1',
      input: oiBridgeGoldenInput,
      expected: executeOiBridgeCompose(oiBridgeGoldenInput),
    }),
  ]),
  execute: executeOiBridgeCompose,
});

const varianceTracebackGoldenInput: VarianceTracebackInput = Object.freeze({
  totalMiss: -32,
  explainedTerms: Object.freeze([
    Object.freeze({ termKey: 'fx', value: -3 }),
    Object.freeze({ termKey: 'oneoff', value: -5 }),
    Object.freeze({ termKey: 'operating', value: -21 }),
  ]),
});

const varianceTracebackDefinition: ApprovedFormulaDefinition<
  VarianceTracebackInput,
  VarianceTracebackOutput
> = Object.freeze({
  formulaId: 'variance_traceback_v1',
  version: '1.0.0',
  displayName: 'Variance traceback reconciliation',
  description: 'Closes governed variance terms to the total miss while preserving a first-class unexplained residual for unlinked drivers.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'variance_traceback_input_v1',
  outputSchemaVersion: 'variance_traceback_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeVarianceTraceback', executeVarianceTraceback],
    ['varianceFamilyTolerance', varianceFamilyTolerance],
    ['varianceFamilyFiniteValues', varianceFamilyFiniteValues],
    ['normalizeVarianceResidual', normalizeVarianceResidual],
    ['roundVarianceFamilyPublishedValue', roundVarianceFamilyPublishedValue],
    ['sumVarianceFamilyValues', sumVarianceFamilyValues],
    ['varianceFamilyNonFiniteIntermediateBlock', varianceFamilyNonFiniteIntermediateBlock],
    ['varianceFamilyReconciles', varianceFamilyReconciles],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'variance_traceback_v1:named-nonzero-residual:v1',
      input: varianceTracebackGoldenInput,
      expected: executeVarianceTraceback(varianceTracebackGoldenInput),
    }),
  ]),
  execute: executeVarianceTraceback,
});
const pricingIncreaseElasticityScenarioDefinition: ApprovedFormulaDefinition<
  PricingIncreaseElasticityScenarioInput,
  PricingIncreaseElasticityScenarioOutput
> = Object.freeze({
  formulaId: 'pricing_increase_elasticity_scenario_v1',
  version: '1.0.0',
  displayName: 'Pricing increase elasticity scenario',
  description:
    'Computes expected volume response, net revenue, AGP, and operating-income effect for a user-supplied sell-rate movement using the approved baseline pricing response prior.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'pricing_increase_elasticity_scenario_input_v1',
  outputSchemaVersion: 'pricing_increase_elasticity_scenario_output_v1',
  expectedContextKind: PRICING_REALIZATION_CONTEXT_KIND,
  approvedSourceSystemRefs: PRICING_REALIZATION_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executePricingIncreaseElasticityScenario', executePricingIncreaseElasticityScenario],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'pricing_increase_elasticity_scenario:chrw-3pl:3pct:v1',
      input: Object.freeze({
        baselineRevenueUsd: 100000000,
        priceChangePct: 0.03,
        baselinePlannedSellRateIncreasePct: 0.02,
        baselineExpectedVolumeResponsePct: -0.005,
        expectedNetRevenueRetentionPct: 0.95,
        agpFlowThroughPct: 1,
        executionCostUsd: 800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          priceChangePct: 0.03,
          impliedElasticity: -0.25,
          expectedVolumeResponsePct: -0.0075,
          expectedRevenueGrowthPct: 0.0225,
          netRevenueDeltaUsd: 2137500,
          agpContributionUsd: 2137500,
          operatingIncomeContributionUsd: 1337500,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'pricing_increase_elasticity_scenario:blocked:no-baseline-prior:v1',
      input: Object.freeze({
        baselineRevenueUsd: 100000000,
        priceChangePct: 0.03,
        baselinePlannedSellRateIncreasePct: 0,
        baselineExpectedVolumeResponsePct: 0,
        expectedNetRevenueRetentionPct: 0.95,
        agpFlowThroughPct: 1,
        executionCostUsd: 800000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message:
          'Pricing increase elasticity scenario requires a non-zero governed planned sell-rate increase to derive implied elasticity.',
      }),
    }),
  ]),
  execute: executePricingIncreaseElasticityScenario,
});


// ---------------------------------------------------------------------------
// Productivity / Lean-AI realization formulas (canonical layer; module productivity-lean-ai)
// ---------------------------------------------------------------------------

const PRODUCTIVITY_LEAN_AI_APPROVED_SOURCE_REFS = Object.freeze([
  'ask_helix_memory:productivity_lean_ai_governed_snapshot_v1',
]);
const PRODUCTIVITY_LEAN_AI_CONTEXT_KIND =
  'decision_agent:productivity_lean_ai:governed_snapshot';

export type ProductivityThroughputDeltaInput = Readonly<{
  baselineUnitsPerFte: number;
  currentUnitsPerFte: number;
  reportingCutAsOf?: string;
}>;

export type ProductivityThroughputDeltaOutput = Readonly<{
  throughputDeltaPct: number;
}>;

function executeProductivityThroughputDelta(
  input: ProductivityThroughputDeltaInput,
): ApprovedFormulaResult<ProductivityThroughputDeltaOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const baseline = record.baselineUnitsPerFte;
  const current = record.currentUnitsPerFte;
  if (!isFiniteNumber(baseline) || !isFiniteNumber(current)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'baselineUnitsPerFte and currentUnitsPerFte are required',
    };
  }
  if (baseline <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'baselineUnitsPerFte must be greater than zero',
    };
  }
  if (current < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'currentUnitsPerFte must be non-negative',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      throughputDeltaPct: roundTo(((current - baseline) / baseline) * 100, 4),
    }),
  };
}

export type ProductivityOiConversionInput = Readonly<{
  automationEligibleCostUsd: number;
  throughputDeltaPct: number;
  costReleaseFactorPct: number;
  aiInvestmentToDateUsd: number;
  reportingCutAsOf?: string;
}>;

export type ProductivityOiConversionOutput = Readonly<{
  productivityOpportunityUsd: number;
  aiInvestmentConversionPct: number;
}>;

function executeProductivityOiConversion(
  input: ProductivityOiConversionInput,
): ApprovedFormulaResult<ProductivityOiConversionOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const eligible = record.automationEligibleCostUsd;
  const deltaPct = record.throughputDeltaPct;
  const releasePct = record.costReleaseFactorPct;
  const investment = record.aiInvestmentToDateUsd;
  if (
    !isFiniteNumber(eligible) ||
    !isFiniteNumber(deltaPct) ||
    !isFiniteNumber(releasePct) ||
    !isFiniteNumber(investment)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'automationEligibleCostUsd, throughputDeltaPct, costReleaseFactorPct, and aiInvestmentToDateUsd are required',
    };
  }
  if (eligible < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'automationEligibleCostUsd must be non-negative',
    };
  }
  if (releasePct <= 0 || releasePct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'costReleaseFactorPct must be greater than zero and at most 100',
    };
  }
  if (investment <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'aiInvestmentToDateUsd must be greater than zero; zero investment is blocked, never infinite conversion',
    };
  }
  const realizedDeltaPct = Math.max(0, deltaPct);
  const productivityOpportunityUsd = roundTo(
    (eligible * realizedDeltaPct * releasePct) / 10000,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      productivityOpportunityUsd,
      aiInvestmentConversionPct: roundTo((productivityOpportunityUsd / investment) * 100, 4),
    }),
  };
}

export type ProductivityOiConversionV110Input = Readonly<{
  automationEligibleCostUsd: number;
  baselineThroughputDeltaPct: number;
  costReleaseFactorPct: number;
  aiInvestmentToDateUsd: number;
  scenarioRealizationFactorPct: number;
  expectedBaselineOpportunityUsd: number;
  reportingCutAsOf?: string;
}>;

export type ProductivityOiConversionV110Output = Readonly<{
  scenarioRealizationFactorPct: number;
  baselineProductivityOpportunityUsd: number;
  baselineOpportunityVarianceUsd: number;
  scenarioThroughputDeltaPct: number;
  scenarioProductivityOpportunityUsd: number;
  scenarioAiInvestmentConversionPct: number;
  operatingIncomeDeltaUsd: number;
}>;

function executeProductivityOiConversionV110(
  input: ProductivityOiConversionV110Input,
): ApprovedFormulaResult<ProductivityOiConversionV110Output> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const eligible = record.automationEligibleCostUsd;
  const baselineDeltaPct = record.baselineThroughputDeltaPct;
  const releasePct = record.costReleaseFactorPct;
  const investment = record.aiInvestmentToDateUsd;
  const realizationFactorPct = record.scenarioRealizationFactorPct;
  const expectedBaselineOpportunityUsd = record.expectedBaselineOpportunityUsd;
  if (
    !isFiniteNumber(eligible) ||
    !isFiniteNumber(baselineDeltaPct) ||
    !isFiniteNumber(releasePct) ||
    !isFiniteNumber(investment) ||
    !isFiniteNumber(realizationFactorPct) ||
    !isFiniteNumber(expectedBaselineOpportunityUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'automationEligibleCostUsd, baselineThroughputDeltaPct, costReleaseFactorPct, aiInvestmentToDateUsd, scenarioRealizationFactorPct, and expectedBaselineOpportunityUsd are required',
    };
  }
  if (eligible < 0 || expectedBaselineOpportunityUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'automationEligibleCostUsd and expectedBaselineOpportunityUsd must be non-negative',
    };
  }
  if (baselineDeltaPct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'baselineThroughputDeltaPct must be non-negative; negative realization cannot be silently normalized',
    };
  }
  if (releasePct <= 0 || releasePct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'costReleaseFactorPct must be greater than zero and at most 100',
    };
  }
  if (investment <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'aiInvestmentToDateUsd must be greater than zero; zero investment is blocked, never infinite conversion',
    };
  }
  if (realizationFactorPct <= 0 || realizationFactorPct > 200) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'scenarioRealizationFactorPct must be greater than zero and at most 200',
    };
  }

  const realizedBaselineDeltaPct = baselineDeltaPct;
  const baselineProductivityOpportunityUsd = roundTo(
    (eligible * realizedBaselineDeltaPct * releasePct) / 10000,
    2,
  );
  const scenarioThroughputDeltaPct = roundTo(
    (realizedBaselineDeltaPct * realizationFactorPct) / 100,
    4,
  );
  const scenarioProductivityOpportunityUsd = roundTo(
    (eligible * scenarioThroughputDeltaPct * releasePct) / 10000,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      scenarioRealizationFactorPct: realizationFactorPct,
      baselineProductivityOpportunityUsd,
      baselineOpportunityVarianceUsd: roundTo(
        Math.abs(baselineProductivityOpportunityUsd - expectedBaselineOpportunityUsd),
        2,
      ),
      scenarioThroughputDeltaPct,
      scenarioProductivityOpportunityUsd,
      scenarioAiInvestmentConversionPct: roundTo(
        (scenarioProductivityOpportunityUsd / investment) * 100,
        4,
      ),
      operatingIncomeDeltaUsd: roundTo(
        scenarioProductivityOpportunityUsd - expectedBaselineOpportunityUsd,
        2,
      ),
    }),
  };
}

export type ProductivityRealizationWarningInput = Readonly<{
  aiInvestmentConversionPct: number;
  throughputDeltaPct: number;
  decisionRequiredFloorPct?: number;
  watchFloorPct?: number;
}>;

export type ProductivityRealizationWarningOutput = Readonly<{
  warningLevel: 'none' | 'watch' | 'decision_required';
}>;

function executeProductivityRealizationWarningLevel(
  input: ProductivityRealizationWarningInput,
): ApprovedFormulaResult<ProductivityRealizationWarningOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const conversionPct = record.aiInvestmentConversionPct;
  const deltaPct = record.throughputDeltaPct;
  if (!isFiniteNumber(conversionPct) || !isFiniteNumber(deltaPct)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'aiInvestmentConversionPct and throughputDeltaPct are required',
    };
  }
  if (conversionPct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'aiInvestmentConversionPct must be non-negative',
    };
  }
  const decisionFloor = isFiniteNumber(record.decisionRequiredFloorPct)
    ? record.decisionRequiredFloorPct
    : 5;
  const watchFloor = isFiniteNumber(record.watchFloorPct) ? record.watchFloorPct : 15;
  if (decisionFloor < 0 || watchFloor < decisionFloor) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'warning floors must satisfy 0 <= decision_required <= watch',
    };
  }
  const warningLevel =
    conversionPct < decisionFloor
      ? 'decision_required'
      : conversionPct < watchFloor || deltaPct <= 0
        ? 'watch'
        : 'none';
  return {
    status: 'ok',
    output: Object.freeze({ warningLevel }),
  };
}

const PRODUCTIVITY_LEAN_AI_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeProductivityThroughputDelta', executeProductivityThroughputDelta],
  ['executeProductivityOiConversion', executeProductivityOiConversion],
  ['executeProductivityRealizationWarningLevel', executeProductivityRealizationWarningLevel],
]);

const productivityThroughputDeltaDefinition: ApprovedFormulaDefinition<
  ProductivityThroughputDeltaInput,
  ProductivityThroughputDeltaOutput
> = Object.freeze({
  formulaId: 'productivity_throughput_delta',
  version: '1.0.0',
  displayName: 'Productivity throughput delta',
  description:
    'Computes realized change in work units per FTE against the approved pre-automation baseline.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'productivity_throughput_delta_input_v1',
  outputSchemaVersion: 'productivity_throughput_delta_output_v1',
  expectedContextKind: PRODUCTIVITY_LEAN_AI_CONTEXT_KIND,
  approvedSourceSystemRefs: PRODUCTIVITY_LEAN_AI_APPROVED_SOURCE_REFS,
  implementationDigestInputs: PRODUCTIVITY_LEAN_AI_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'productivity_throughput_delta:ok:v1',
      input: Object.freeze({
        baselineUnitsPerFte: 1200,
        currentUnitsPerFte: 1320,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ throughputDeltaPct: 10 }),
      }),
    }),
    Object.freeze({
      traceId: 'productivity_throughput_delta:blocked:zero-baseline:v1',
      input: Object.freeze({ baselineUnitsPerFte: 0, currentUnitsPerFte: 1320 }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'baselineUnitsPerFte must be greater than zero',
      }),
    }),
  ]),
  execute: executeProductivityThroughputDelta,
});

const productivityOiConversionDefinition: ApprovedFormulaDefinition<
  ProductivityOiConversionInput,
  ProductivityOiConversionOutput
> = Object.freeze({
  formulaId: 'productivity_oi_conversion',
  version: '1.0.0',
  displayName: 'Productivity OI conversion',
  description:
    'Converts realized throughput improvement on automation-eligible cost to operating income at the approved release factor, and reports realized run-rate as a share of cumulative AI investment.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'productivity_oi_conversion_input_v1',
  outputSchemaVersion: 'productivity_oi_conversion_output_v1',
  expectedContextKind: PRODUCTIVITY_LEAN_AI_CONTEXT_KIND,
  approvedSourceSystemRefs: PRODUCTIVITY_LEAN_AI_APPROVED_SOURCE_REFS,
  implementationDigestInputs: PRODUCTIVITY_LEAN_AI_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'productivity_oi_conversion:ok:v1',
      input: Object.freeze({
        automationEligibleCostUsd: 400000000,
        throughputDeltaPct: 10,
        costReleaseFactorPct: 40,
        aiInvestmentToDateUsd: 120000000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          productivityOpportunityUsd: 16000000,
          aiInvestmentConversionPct: 13.3333,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'productivity_oi_conversion:blocked:zero-investment:v1',
      input: Object.freeze({
        automationEligibleCostUsd: 400000000,
        throughputDeltaPct: 10,
        costReleaseFactorPct: 40,
        aiInvestmentToDateUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'aiInvestmentToDateUsd must be greater than zero; zero investment is blocked, never infinite conversion',
      }),
    }),
  ]),
  execute: executeProductivityOiConversion,
});

const productivityOiConversionV110Definition: ApprovedFormulaDefinition<
  ProductivityOiConversionV110Input,
  ProductivityOiConversionV110Output
> = Object.freeze({
  formulaId: 'productivity_oi_conversion',
  version: '1.1.0',
  displayName: 'Productivity OI conversion scenario',
  description:
    'Reconciles the approved productivity baseline, then applies one labeled realization posture to throughput, operating-income capture, and AI-investment conversion in lockstep.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'productivity_oi_conversion_input_v1_1',
  outputSchemaVersion: 'productivity_oi_conversion_output_v1_1',
  expectedContextKind: PRODUCTIVITY_LEAN_AI_CONTEXT_KIND,
  approvedSourceSystemRefs: PRODUCTIVITY_LEAN_AI_APPROVED_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeProductivityOiConversionV110', executeProductivityOiConversionV110],
    ['roundTo', roundTo],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'productivity_oi_conversion:scenario:conservative:v1_1',
      input: Object.freeze({
        automationEligibleCostUsd: 10000000,
        baselineThroughputDeltaPct: 15,
        costReleaseFactorPct: 50,
        aiInvestmentToDateUsd: 1000000,
        scenarioRealizationFactorPct: 75,
        expectedBaselineOpportunityUsd: 750000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          scenarioRealizationFactorPct: 75,
          baselineProductivityOpportunityUsd: 750000,
          baselineOpportunityVarianceUsd: 0,
          scenarioThroughputDeltaPct: 11.25,
          scenarioProductivityOpportunityUsd: 562500,
          scenarioAiInvestmentConversionPct: 56.25,
          operatingIncomeDeltaUsd: -187500,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'productivity_oi_conversion:scenario:blocked:zero-factor:v1_1',
      input: Object.freeze({
        automationEligibleCostUsd: 10000000,
        baselineThroughputDeltaPct: 15,
        costReleaseFactorPct: 50,
        aiInvestmentToDateUsd: 1000000,
        scenarioRealizationFactorPct: 0,
        expectedBaselineOpportunityUsd: 750000,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'scenarioRealizationFactorPct must be greater than zero and at most 200',
      }),
    }),
    Object.freeze({
      traceId:
        'productivity_oi_conversion:scenario:blocked:negative-baseline:v1_1',
      input: Object.freeze({
        automationEligibleCostUsd: 10000000,
        baselineThroughputDeltaPct: -1,
        costReleaseFactorPct: 50,
        aiInvestmentToDateUsd: 1000000,
        scenarioRealizationFactorPct: 75,
        expectedBaselineOpportunityUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'baselineThroughputDeltaPct must be non-negative; negative realization cannot be silently normalized',
      }),
    }),
  ]),
  execute: executeProductivityOiConversionV110,
});

const productivityRealizationWarningDefinition: ApprovedFormulaDefinition<
  ProductivityRealizationWarningInput,
  ProductivityRealizationWarningOutput
> = Object.freeze({
  formulaId: 'productivity_realization_warning_level',
  version: '1.0.0',
  displayName: 'Productivity realization warning level',
  description:
    'Classifies AI-investment realization posture from conversion share and throughput delta using externalized floors; low conversion on material investment is the AI-theater detector.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'productivity_realization_warning_input_v1',
  outputSchemaVersion: 'productivity_realization_warning_output_v1',
  expectedContextKind: PRODUCTIVITY_LEAN_AI_CONTEXT_KIND,
  approvedSourceSystemRefs: PRODUCTIVITY_LEAN_AI_APPROVED_SOURCE_REFS,
  implementationDigestInputs: PRODUCTIVITY_LEAN_AI_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'productivity_realization_warning_level:ok:watch:v1',
      input: Object.freeze({
        aiInvestmentConversionPct: 13.3333,
        throughputDeltaPct: 10,
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ warningLevel: 'watch' as const }),
      }),
    }),
    Object.freeze({
      traceId: 'productivity_realization_warning_level:blocked:floors:v1',
      input: Object.freeze({
        aiInvestmentConversionPct: 13.3333,
        throughputDeltaPct: 10,
        decisionRequiredFloorPct: 20,
        watchFloorPct: 10,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'warning floors must satisfy 0 <= decision_required <= watch',
      }),
    }),
  ]),
  execute: executeProductivityRealizationWarningLevel,
});


// ---------------------------------------------------------------------------
// Travel & expense leakage formulas (canonical layer; module travel-expense)
// ---------------------------------------------------------------------------

const TRAVEL_EXPENSE_APPROVED_SOURCE_REFS = Object.freeze([
  'ask_helix_memory:travel_expense_governed_snapshot_v1',
]);

const TRAVEL_EXPENSE_CONTEXT_KIND =
  'decision_agent:travel_expense:governed_snapshot';

export type TnePolicyExceptionRateInput = Readonly<{
  policyExceptionSpendUsd: number;
  totalTneSpendUsd: number;
  reportingCutAsOf?: string;
}>;

export type TnePolicyExceptionRateOutput = Readonly<{
  policyExceptionRatePct: number;
}>;

function executeTnePolicyExceptionRate(
  input: TnePolicyExceptionRateInput,
): ApprovedFormulaResult<TnePolicyExceptionRateOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const exception = record.policyExceptionSpendUsd;
  const total = record.totalTneSpendUsd;
  if (!isFiniteNumber(exception) || !isFiniteNumber(total)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'policyExceptionSpendUsd and totalTneSpendUsd are required',
    };
  }
  if (total <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'totalTneSpendUsd must be greater than zero',
    };
  }
  if (exception < 0 || exception > total) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'policyExceptionSpendUsd must be between zero and totalTneSpendUsd',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      policyExceptionRatePct: roundTo((exception / total) * 100, 4),
    }),
  };
}

export type TneLeakageOpportunityInput = Readonly<{
  totalTneSpendUsd: number;
  leakageRatePct: number;
  recoveryFactorPct: number;
  programCostUsd: number;
  reportingCutAsOf?: string;
}>;

export type TneLeakageOpportunityOutput = Readonly<{
  tneOpportunityUsd: number;
  grossRecoveryUsd: number;
}>;

function executeTneLeakageOpportunity(
  input: TneLeakageOpportunityInput,
): ApprovedFormulaResult<TneLeakageOpportunityOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const total = record.totalTneSpendUsd;
  const leakage = record.leakageRatePct;
  const recovery = record.recoveryFactorPct;
  const programCost = record.programCostUsd;
  if (
    !isFiniteNumber(total) ||
    !isFiniteNumber(leakage) ||
    !isFiniteNumber(recovery) ||
    !isFiniteNumber(programCost)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'totalTneSpendUsd, leakageRatePct, recoveryFactorPct, and programCostUsd are required',
    };
  }
  if (total < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'totalTneSpendUsd must be non-negative',
    };
  }
  if (leakage < 0 || leakage > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'leakageRatePct must be between 0 and 100',
    };
  }
  if (recovery <= 0 || recovery > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'recoveryFactorPct must be greater than zero and at most 100',
    };
  }
  if (programCost < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'programCostUsd must be non-negative',
    };
  }
  const grossRecoveryUsd = roundTo((total * leakage * recovery) / 10000, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      grossRecoveryUsd,
      tneOpportunityUsd: roundTo(grossRecoveryUsd - programCost, 2),
    }),
  };
}

export type TneWarningInput = Readonly<{
  policyExceptionRatePct: number;
  watchFloorPct?: number;
  decisionRequiredFloorPct?: number;
}>;

export type TneWarningOutput = Readonly<{
  warningLevel: 'none' | 'watch' | 'decision_required';
}>;

function executeTneWarningLevel(
  input: TneWarningInput,
): ApprovedFormulaResult<TneWarningOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const ratePct = record.policyExceptionRatePct;
  if (!isFiniteNumber(ratePct) || ratePct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'policyExceptionRatePct is required and must be non-negative',
    };
  }
  const watchFloor = isFiniteNumber(record.watchFloorPct) ? record.watchFloorPct : 10;
  const decisionFloor = isFiniteNumber(record.decisionRequiredFloorPct)
    ? record.decisionRequiredFloorPct
    : 20;
  if (watchFloor < 0 || decisionFloor < watchFloor) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'warning floors must satisfy 0 <= watch <= decision_required',
    };
  }
  const warningLevel =
    ratePct >= decisionFloor
      ? 'decision_required'
      : ratePct >= watchFloor
        ? 'watch'
        : 'none';
  return {
    status: 'ok',
    output: Object.freeze({ warningLevel }),
  };
}

const TRAVEL_EXPENSE_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeTnePolicyExceptionRate', executeTnePolicyExceptionRate],
  ['executeTneLeakageOpportunity', executeTneLeakageOpportunity],
  ['executeTneWarningLevel', executeTneWarningLevel],
]);

const tnePolicyExceptionRateDefinition: ApprovedFormulaDefinition<
  TnePolicyExceptionRateInput,
  TnePolicyExceptionRateOutput
> = Object.freeze({
  formulaId: 'tne_policy_exception_rate',
  version: '1.0.0',
  displayName: 'T&E policy exception rate',
  description:
    'Computes the share of travel-and-expense spend booked outside approved policy.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'tne_policy_exception_rate_input_v1',
  outputSchemaVersion: 'tne_policy_exception_rate_output_v1',
  expectedContextKind: TRAVEL_EXPENSE_CONTEXT_KIND,
  approvedSourceSystemRefs: TRAVEL_EXPENSE_APPROVED_SOURCE_REFS,
  implementationDigestInputs: TRAVEL_EXPENSE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'tne_policy_exception_rate:ok:v1',
      input: Object.freeze({
        policyExceptionSpendUsd: 8640000,
        totalTneSpendUsd: 48000000,
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ policyExceptionRatePct: 18 }),
      }),
    }),
    Object.freeze({
      traceId: 'tne_policy_exception_rate:blocked:zero-total:v1',
      input: Object.freeze({ policyExceptionSpendUsd: 8640000, totalTneSpendUsd: 0 }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'totalTneSpendUsd must be greater than zero',
      }),
    }),
  ]),
  execute: executeTnePolicyExceptionRate,
});

const tneLeakageOpportunityDefinition: ApprovedFormulaDefinition<
  TneLeakageOpportunityInput,
  TneLeakageOpportunityOutput
> = Object.freeze({
  formulaId: 'tne_leakage_opportunity',
  version: '1.0.0',
  displayName: 'T&E leakage opportunity',
  description:
    'Converts approved travel-and-expense leakage at the approved recovery factor into an operating-income contribution net of program cost.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'tne_leakage_opportunity_input_v1',
  outputSchemaVersion: 'tne_leakage_opportunity_output_v1',
  expectedContextKind: TRAVEL_EXPENSE_CONTEXT_KIND,
  approvedSourceSystemRefs: TRAVEL_EXPENSE_APPROVED_SOURCE_REFS,
  implementationDigestInputs: TRAVEL_EXPENSE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'tne_leakage_opportunity:ok:v1',
      input: Object.freeze({
        totalTneSpendUsd: 48000000,
        leakageRatePct: 18,
        recoveryFactorPct: 70,
        programCostUsd: 348000,
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          grossRecoveryUsd: 6048000,
          tneOpportunityUsd: 5700000,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'tne_leakage_opportunity:blocked:bad-recovery:v1',
      input: Object.freeze({
        totalTneSpendUsd: 48000000,
        leakageRatePct: 18,
        recoveryFactorPct: 0,
        programCostUsd: 348000,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'recoveryFactorPct must be greater than zero and at most 100',
      }),
    }),
  ]),
  execute: executeTneLeakageOpportunity,
});

const tneWarningDefinition: ApprovedFormulaDefinition<
  TneWarningInput,
  TneWarningOutput
> = Object.freeze({
  formulaId: 'tne_warning_level',
  version: '1.0.0',
  displayName: 'T&E warning level',
  description:
    'Classifies travel-and-expense control posture from the policy-exception rate against externalized floors; a high exception rate is the discipline detector.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'tne_warning_input_v1',
  outputSchemaVersion: 'tne_warning_output_v1',
  expectedContextKind: TRAVEL_EXPENSE_CONTEXT_KIND,
  approvedSourceSystemRefs: TRAVEL_EXPENSE_APPROVED_SOURCE_REFS,
  implementationDigestInputs: TRAVEL_EXPENSE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'tne_warning_level:ok:watch:v1',
      input: Object.freeze({ policyExceptionRatePct: 18 }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ warningLevel: 'watch' as const }),
      }),
    }),
    Object.freeze({
      traceId: 'tne_warning_level:blocked:floors:v1',
      input: Object.freeze({
        policyExceptionRatePct: 18,
        watchFloorPct: 25,
        decisionRequiredFloorPct: 10,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'warning floors must satisfy 0 <= watch <= decision_required',
      }),
    }),
  ]),
  execute: executeTneWarningLevel,
});

// ---------------------------------------------------------------------------
// Indirect procurement formulas (canonical layer; module indirect-procurement)
// ---------------------------------------------------------------------------

const INDIRECT_PROCUREMENT_APPROVED_SOURCE_REFS = Object.freeze([
  'ask_helix_memory:indirect_procurement_governed_snapshot_v1',
]);

const INDIRECT_PROCUREMENT_CONTEXT_KIND =
  'decision_agent:indirect_procurement:governed_snapshot';

export type IndirectAddressableShareInput = Readonly<{
  addressableSpendUsd: number;
  totalIndirectSpendUsd: number;
  reportingCutAsOf?: string;
}>;

export type IndirectAddressableShareOutput = Readonly<{
  addressableSharePct: number;
}>;

function executeIndirectAddressableShare(
  input: IndirectAddressableShareInput,
): ApprovedFormulaResult<IndirectAddressableShareOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const addressable = record.addressableSpendUsd;
  const total = record.totalIndirectSpendUsd;
  if (!isFiniteNumber(addressable) || !isFiniteNumber(total)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'addressableSpendUsd and totalIndirectSpendUsd are required',
    };
  }
  if (total <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'totalIndirectSpendUsd must be greater than zero',
    };
  }
  if (addressable < 0 || addressable > total) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'addressableSpendUsd must be between zero and totalIndirectSpendUsd',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      addressableSharePct: roundTo((addressable / total) * 100, 4),
    }),
  };
}

export type IndirectProcurementOpportunityInput = Readonly<{
  totalIndirectSpendUsd: number;
  savingsRatePct: number;
  recoveryFactorPct: number;
  programCostUsd: number;
  reportingCutAsOf?: string;
}>;

export type IndirectProcurementOpportunityOutput = Readonly<{
  indirectProcurementOpportunityUsd: number;
  grossSavingsUsd: number;
}>;

function executeIndirectProcurementOpportunity(
  input: IndirectProcurementOpportunityInput,
): ApprovedFormulaResult<IndirectProcurementOpportunityOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const total = record.totalIndirectSpendUsd;
  const savings = record.savingsRatePct;
  const recovery = record.recoveryFactorPct;
  const programCost = record.programCostUsd;
  if (
    !isFiniteNumber(total) ||
    !isFiniteNumber(savings) ||
    !isFiniteNumber(recovery) ||
    !isFiniteNumber(programCost)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'totalIndirectSpendUsd, savingsRatePct, recoveryFactorPct, and programCostUsd are required',
    };
  }
  if (total < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'totalIndirectSpendUsd must be non-negative',
    };
  }
  if (savings < 0 || savings > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'savingsRatePct must be between 0 and 100',
    };
  }
  if (recovery <= 0 || recovery > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'recoveryFactorPct must be greater than zero and at most 100',
    };
  }
  if (programCost < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'programCostUsd must be non-negative',
    };
  }
  const grossSavingsUsd = roundTo((total * savings * recovery) / 10000, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      grossSavingsUsd,
      indirectProcurementOpportunityUsd: roundTo(grossSavingsUsd - programCost, 2),
    }),
  };
}

export type IndirectProcurementWarningInput = Readonly<{
  addressableSharePct: number;
  watchFloorPct?: number;
  decisionRequiredFloorPct?: number;
}>;

export type IndirectProcurementWarningOutput = Readonly<{
  warningLevel: 'none' | 'watch' | 'decision_required';
}>;

function executeIndirectProcurementWarningLevel(
  input: IndirectProcurementWarningInput,
): ApprovedFormulaResult<IndirectProcurementWarningOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const sharePct = record.addressableSharePct;
  if (!isFiniteNumber(sharePct) || sharePct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'addressableSharePct is required and must be non-negative',
    };
  }
  const watchFloor = isFiniteNumber(record.watchFloorPct) ? record.watchFloorPct : 40;
  const decisionFloor = isFiniteNumber(record.decisionRequiredFloorPct)
    ? record.decisionRequiredFloorPct
    : 60;
  if (watchFloor < 0 || decisionFloor < watchFloor) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'warning floors must satisfy 0 <= watch <= decision_required',
    };
  }
  const warningLevel =
    sharePct >= decisionFloor
      ? 'decision_required'
      : sharePct >= watchFloor
        ? 'watch'
        : 'none';
  return {
    status: 'ok',
    output: Object.freeze({ warningLevel }),
  };
}

const INDIRECT_PROCUREMENT_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeIndirectAddressableShare', executeIndirectAddressableShare],
  ['executeIndirectProcurementOpportunity', executeIndirectProcurementOpportunity],
  ['executeIndirectProcurementWarningLevel', executeIndirectProcurementWarningLevel],
]);

const indirectAddressableShareDefinition: ApprovedFormulaDefinition<
  IndirectAddressableShareInput,
  IndirectAddressableShareOutput
> = Object.freeze({
  formulaId: 'indirect_addressable_share',
  version: '1.0.0',
  displayName: 'Indirect addressable spend share',
  description:
    'Computes the share of total non-transport indirect spend that is addressable through vendor consolidation.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'indirect_addressable_share_input_v1',
  outputSchemaVersion: 'indirect_addressable_share_output_v1',
  expectedContextKind: INDIRECT_PROCUREMENT_CONTEXT_KIND,
  approvedSourceSystemRefs: INDIRECT_PROCUREMENT_APPROVED_SOURCE_REFS,
  implementationDigestInputs: INDIRECT_PROCUREMENT_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'indirect_addressable_share:ok:v1',
      input: Object.freeze({
        addressableSpendUsd: 120000000,
        totalIndirectSpendUsd: 200000000,
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ addressableSharePct: 60 }),
      }),
    }),
    Object.freeze({
      traceId: 'indirect_addressable_share:blocked:zero-total:v1',
      input: Object.freeze({ addressableSpendUsd: 120000000, totalIndirectSpendUsd: 0 }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'totalIndirectSpendUsd must be greater than zero',
      }),
    }),
  ]),
  execute: executeIndirectAddressableShare,
});

const indirectProcurementOpportunityDefinition: ApprovedFormulaDefinition<
  IndirectProcurementOpportunityInput,
  IndirectProcurementOpportunityOutput
> = Object.freeze({
  formulaId: 'indirect_procurement_opportunity',
  version: '1.0.0',
  displayName: 'Indirect procurement opportunity',
  description:
    'Converts approved non-transport indirect spend at the approved savings rate and recovery factor into an operating-income contribution net of program cost.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'indirect_procurement_opportunity_input_v1',
  outputSchemaVersion: 'indirect_procurement_opportunity_output_v1',
  expectedContextKind: INDIRECT_PROCUREMENT_CONTEXT_KIND,
  approvedSourceSystemRefs: INDIRECT_PROCUREMENT_APPROVED_SOURCE_REFS,
  implementationDigestInputs: INDIRECT_PROCUREMENT_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'indirect_procurement_opportunity:ok:v1',
      input: Object.freeze({
        totalIndirectSpendUsd: 200000000,
        savingsRatePct: 8,
        recoveryFactorPct: 80,
        programCostUsd: 800000,
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          grossSavingsUsd: 12800000,
          indirectProcurementOpportunityUsd: 12000000,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'indirect_procurement_opportunity:blocked:bad-recovery:v1',
      input: Object.freeze({
        totalIndirectSpendUsd: 200000000,
        savingsRatePct: 8,
        recoveryFactorPct: 0,
        programCostUsd: 800000,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'recoveryFactorPct must be greater than zero and at most 100',
      }),
    }),
  ]),
  execute: executeIndirectProcurementOpportunity,
});

const indirectProcurementWarningDefinition: ApprovedFormulaDefinition<
  IndirectProcurementWarningInput,
  IndirectProcurementWarningOutput
> = Object.freeze({
  formulaId: 'indirect_procurement_warning_level',
  version: '1.0.0',
  displayName: 'Indirect procurement warning level',
  description:
    'Classifies vendor-consolidation opportunity from the addressable-spend share against externalized floors; a high addressable share is the consolidation-opportunity detector.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'indirect_procurement_warning_input_v1',
  outputSchemaVersion: 'indirect_procurement_warning_output_v1',
  expectedContextKind: INDIRECT_PROCUREMENT_CONTEXT_KIND,
  approvedSourceSystemRefs: INDIRECT_PROCUREMENT_APPROVED_SOURCE_REFS,
  implementationDigestInputs: INDIRECT_PROCUREMENT_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'indirect_procurement_warning_level:ok:watch:v1',
      input: Object.freeze({ addressableSharePct: 60 }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ warningLevel: 'decision_required' as const }),
      }),
    }),
    Object.freeze({
      traceId: 'indirect_procurement_warning_level:blocked:floors:v1',
      input: Object.freeze({
        addressableSharePct: 60,
        watchFloorPct: 70,
        decisionRequiredFloorPct: 30,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'warning floors must satisfy 0 <= watch <= decision_required',
      }),
    }),
  ]),
  execute: executeIndirectProcurementWarningLevel,
});

// ---------------------------------------------------------------------------
// Generic SG&A-spend formula core (canonical layer; shared by SG&A spend levers)
// One audited math contract instantiated into per-lever families below. Each
// family carries its own formulaId, context kind, source system, and golden
// trace, so every lever is independently receipted while the arithmetic is
// reviewed once. Input contract is canonical across SG&A spend levers:
//   addressableSpendUsd, totalSpendUsd, savingsRatePct, recoveryFactorPct,
//   programCostUsd.
// ---------------------------------------------------------------------------

export type SgaAddressableShareInput = Readonly<{
  addressableSpendUsd: number;
  totalSpendUsd: number;
  reportingCutAsOf?: string;
}>;
export type SgaAddressableShareOutput = Readonly<{ addressableSharePct: number }>;

function executeSgaAddressableShare(
  input: SgaAddressableShareInput,
): ApprovedFormulaResult<SgaAddressableShareOutput> {
  const record = asRecord(input);
  if (!record) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'Formula input must be an object' };
  }
  const addressable = record.addressableSpendUsd;
  const total = record.totalSpendUsd;
  if (!isFiniteNumber(addressable) || !isFiniteNumber(total)) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'addressableSpendUsd and totalSpendUsd are required' };
  }
  if (total <= 0) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'totalSpendUsd must be greater than zero' };
  }
  if (addressable < 0 || addressable > total) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'addressableSpendUsd must be between zero and totalSpendUsd' };
  }
  return { status: 'ok', output: Object.freeze({ addressableSharePct: roundTo((addressable / total) * 100, 4) }) };
}

export type SgaSpendOpportunityInput = Readonly<{
  totalSpendUsd: number;
  savingsRatePct: number;
  recoveryFactorPct: number;
  programCostUsd: number;
  reportingCutAsOf?: string;
}>;
export type SgaSpendOpportunityOutput = Readonly<{
  sgaSpendOpportunityUsd: number;
  grossSavingsUsd: number;
}>;

export type SgaSpendOpportunityScenarioInput = Readonly<{
  totalSpendUsd: number;
  savingsRatePct: number;
  recoveryFactorPct: number;
  programCostUsd: number;
  scenarioRealizationFactorPct: number;
  expectedBaselineOpportunityUsd: number;
  reportingCutAsOf?: string;
}>;

export type SgaSpendOpportunityScenarioOutput = Readonly<{
  scenarioRealizationFactorPct: number;
  baselineOpportunityUsd: number;
  baselineOpportunityVarianceUsd: number;
  scenarioSavingsRatePct: number;
  scenarioGrossSavingsUsd: number;
  scenarioOpportunityUsd: number;
  opportunityDeltaUsd: number;
}>;

function executeSgaSpendOpportunity(
  input: SgaSpendOpportunityInput,
): ApprovedFormulaResult<SgaSpendOpportunityOutput> {
  const record = asRecord(input);
  if (!record) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'Formula input must be an object' };
  }
  const total = record.totalSpendUsd;
  const savings = record.savingsRatePct;
  const recovery = record.recoveryFactorPct;
  const programCost = record.programCostUsd;
  if (!isFiniteNumber(total) || !isFiniteNumber(savings) || !isFiniteNumber(recovery) || !isFiniteNumber(programCost)) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'totalSpendUsd, savingsRatePct, recoveryFactorPct, and programCostUsd are required' };
  }
  if (total < 0) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'totalSpendUsd must be non-negative' };
  }
  if (savings < 0 || savings > 100) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'savingsRatePct must be between 0 and 100' };
  }
  if (recovery <= 0 || recovery > 100) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'recoveryFactorPct must be greater than zero and at most 100' };
  }
  if (programCost < 0) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'programCostUsd must be non-negative' };
  }
  const grossSavingsUsd = roundTo((total * savings * recovery) / 10000, 2);
  return { status: 'ok', output: Object.freeze({ grossSavingsUsd, sgaSpendOpportunityUsd: roundTo(grossSavingsUsd - programCost, 2) }) };
}

function executeSgaSpendOpportunityScenario(
  input: SgaSpendOpportunityScenarioInput,
): ApprovedFormulaResult<SgaSpendOpportunityScenarioOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const scenarioRealizationFactorPct = record.scenarioRealizationFactorPct;
  const expectedBaselineOpportunityUsd = record.expectedBaselineOpportunityUsd;
  if (
    !isFiniteNumber(scenarioRealizationFactorPct) ||
    !isFiniteNumber(expectedBaselineOpportunityUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'scenarioRealizationFactorPct and expectedBaselineOpportunityUsd are required',
    };
  }
  if (scenarioRealizationFactorPct <= 0 || scenarioRealizationFactorPct > 200) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'scenarioRealizationFactorPct must be greater than zero and at most 200',
    };
  }
  if (expectedBaselineOpportunityUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'expectedBaselineOpportunityUsd must be non-negative',
    };
  }

  const baseline = executeSgaSpendOpportunity(input);
  if (baseline.status === 'blocked') return baseline;
  const scenarioSavingsRatePct = roundTo(
    (input.savingsRatePct * scenarioRealizationFactorPct) / 100,
    4,
  );
  const scenario = executeSgaSpendOpportunity({
    ...input,
    savingsRatePct: scenarioSavingsRatePct,
  });
  if (scenario.status === 'blocked') return scenario;

  return {
    status: 'ok',
    output: Object.freeze({
      scenarioRealizationFactorPct,
      baselineOpportunityUsd: baseline.output.sgaSpendOpportunityUsd,
      baselineOpportunityVarianceUsd: roundTo(
        Math.abs(
          baseline.output.sgaSpendOpportunityUsd -
            expectedBaselineOpportunityUsd,
        ),
        2,
      ),
      scenarioSavingsRatePct,
      scenarioGrossSavingsUsd: scenario.output.grossSavingsUsd,
      scenarioOpportunityUsd: scenario.output.sgaSpendOpportunityUsd,
      opportunityDeltaUsd: roundTo(
        scenario.output.sgaSpendOpportunityUsd -
          baseline.output.sgaSpendOpportunityUsd,
        2,
      ),
    }),
  };
}

export type SgaSpendWarningInput = Readonly<{
  addressableSharePct: number;
  watchFloorPct?: number;
  decisionRequiredFloorPct?: number;
}>;
export type SgaSpendWarningOutput = Readonly<{ warningLevel: 'none' | 'watch' | 'decision_required' }>;

function executeSgaSpendWarningLevel(
  input: SgaSpendWarningInput,
): ApprovedFormulaResult<SgaSpendWarningOutput> {
  const record = asRecord(input);
  if (!record) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'Formula input must be an object' };
  }
  const sharePct = record.addressableSharePct;
  if (!isFiniteNumber(sharePct) || sharePct < 0) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'addressableSharePct is required and must be non-negative' };
  }
  const watchFloor = isFiniteNumber(record.watchFloorPct) ? record.watchFloorPct : 40;
  const decisionFloor = isFiniteNumber(record.decisionRequiredFloorPct) ? record.decisionRequiredFloorPct : 60;
  if (watchFloor < 0 || decisionFloor < watchFloor) {
    return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'warning floors must satisfy 0 <= watch <= decision_required' };
  }
  const warningLevel = sharePct >= decisionFloor ? 'decision_required' : sharePct >= watchFloor ? 'watch' : 'none';
  return { status: 'ok', output: Object.freeze({ warningLevel }) };
}

const SGA_SPEND_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeSgaAddressableShare', executeSgaAddressableShare],
  ['executeSgaSpendOpportunity', executeSgaSpendOpportunity],
  ['executeSgaSpendWarningLevel', executeSgaSpendWarningLevel],
]);

// Keep the active 1.0.0 definition digests stable. Scenario-capable 1.1.0
// definitions own a separate digest so modeled cases cannot create code drift
// in an already-activated monitoring formula.
const SGA_SPEND_SCENARIO_IMPLEMENTATION_DIGEST_INPUTS =
  implementationDigestInputs([
    ['executeSgaSpendOpportunityScenario', executeSgaSpendOpportunityScenario],
    ['executeSgaSpendOpportunity', executeSgaSpendOpportunity],
    [
      'validateSgaSpendOpportunityScenarioReconciliation',
      validateSgaSpendOpportunityScenarioReconciliation,
    ],
    ['roundTo', roundTo],
  ]);

function validateSgaSpendOpportunityScenarioReconciliation(
  input: Readonly<SgaSpendOpportunityScenarioInput>,
  output: Readonly<SgaSpendOpportunityScenarioOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const recomputed = executeSgaSpendOpportunityScenario(input);
  if (recomputed.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: recomputed.message,
    };
  }
  if (recomputed.output.baselineOpportunityVarianceUsd > 0.01) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'The modeled SG&A opportunity baseline does not reconcile to the approved observed opportunity.',
    };
  }
  const mismatchedReportingCut = artifacts.find(
    (artifact) =>
      input.reportingCutAsOf &&
      artifact.reportingCutAsOf &&
      artifact.reportingCutAsOf !== input.reportingCutAsOf,
  );
  if (mismatchedReportingCut) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'The modeled SG&A opportunity reporting cut does not match its governed evidence.',
    };
  }
  if (JSON.stringify(recomputed.output) !== JSON.stringify(output)) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'The modeled SG&A opportunity output failed deterministic reconciliation.',
    };
  }
  return null;
}

type SgaSpendLeverFormulaSpec = Readonly<{
  prefix: string;
  contextKind: string;
  sourceRef: string;
  displayLabel: string;
  // The mechanism by which spend becomes addressable — "vendor consolidation and
  // contract discipline" for SG&A categories, "network design and carrier
  // discipline" for purchased-transportation levers, etc. Keeps the formula
  // math identical (addressable spend x savings x recovery - program) while the
  // narrative stays category-accurate.
  mechanismNoun?: string;
  // The outcome the opportunity lands on. Defaults to "operating-income
  // contribution"; cash-track levers (working capital, capex) pass "cash release"
  // so the canonical formula never describes P&L it does not produce.
  outcomeNoun?: string;
  goldenAddressable: number;
  goldenTotal: number;
  goldenAddressableSharePct: number;
  goldenSavingsPct: number;
  goldenRecoveryPct: number;
  goldenProgramCost: number;
  goldenGrossSavings: number;
  goldenOpportunity: number;
  goldenWarningLevel: 'none' | 'watch' | 'decision_required';
}>;

function buildSgaSpendLeverFormulaDefinitions(
  spec: SgaSpendLeverFormulaSpec,
): readonly ApprovedFormulaDefinition[] {
  const sourceRefs = Object.freeze([spec.sourceRef]);
  const mechanism = spec.mechanismNoun ?? 'vendor consolidation and contract discipline';
  const outcome = spec.outcomeNoun ?? 'an operating-income contribution';
  const addressableShare: ApprovedFormulaDefinition<SgaAddressableShareInput, SgaAddressableShareOutput> = Object.freeze({
    formulaId: `${spec.prefix}_addressable_share`,
    version: '1.0.0',
    displayName: `${spec.displayLabel} addressable share`,
    description: `Computes the addressable share of ${spec.displayLabel.toLowerCase()} spend through ${mechanism}.`,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${spec.prefix}_addressable_share_input_v1`,
    outputSchemaVersion: `${spec.prefix}_addressable_share_output_v1`,
    expectedContextKind: spec.contextKind,
    approvedSourceSystemRefs: sourceRefs,
    implementationDigestInputs: SGA_SPEND_IMPLEMENTATION_DIGEST_INPUTS,
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: `${spec.prefix}_addressable_share:ok:v1`,
        input: Object.freeze({ addressableSpendUsd: spec.goldenAddressable, totalSpendUsd: spec.goldenTotal, reportingCutAsOf: '2026-06-08' }),
        expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ addressableSharePct: spec.goldenAddressableSharePct }) }),
      }),
      Object.freeze({
        traceId: `${spec.prefix}_addressable_share:blocked:zero-total:v1`,
        input: Object.freeze({ addressableSpendUsd: spec.goldenAddressable, totalSpendUsd: 0 }),
        expected: Object.freeze({ status: 'blocked' as const, reasonCode: 'invalid_formula_input' as const, message: 'totalSpendUsd must be greater than zero' }),
      }),
    ]),
    execute: executeSgaAddressableShare,
  });
  const opportunity: ApprovedFormulaDefinition<SgaSpendOpportunityInput, SgaSpendOpportunityOutput> = Object.freeze({
    formulaId: `${spec.prefix}_opportunity`,
    version: '1.0.0',
    displayName: `${spec.displayLabel} opportunity`,
    description: `Converts approved ${spec.displayLabel.toLowerCase()} spend at the approved savings rate and recovery factor into ${outcome} net of program cost.`,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${spec.prefix}_opportunity_input_v1`,
    outputSchemaVersion: `${spec.prefix}_opportunity_output_v1`,
    expectedContextKind: spec.contextKind,
    approvedSourceSystemRefs: sourceRefs,
    implementationDigestInputs: SGA_SPEND_IMPLEMENTATION_DIGEST_INPUTS,
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: `${spec.prefix}_opportunity:ok:v1`,
        input: Object.freeze({ totalSpendUsd: spec.goldenTotal, savingsRatePct: spec.goldenSavingsPct, recoveryFactorPct: spec.goldenRecoveryPct, programCostUsd: spec.goldenProgramCost, reportingCutAsOf: '2026-06-08' }),
        expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ grossSavingsUsd: spec.goldenGrossSavings, sgaSpendOpportunityUsd: spec.goldenOpportunity }) }),
      }),
      Object.freeze({
        traceId: `${spec.prefix}_opportunity:blocked:bad-recovery:v1`,
        input: Object.freeze({ totalSpendUsd: spec.goldenTotal, savingsRatePct: spec.goldenSavingsPct, recoveryFactorPct: 0, programCostUsd: spec.goldenProgramCost }),
        expected: Object.freeze({ status: 'blocked' as const, reasonCode: 'invalid_formula_input' as const, message: 'recoveryFactorPct must be greater than zero and at most 100' }),
      }),
    ]),
    execute: executeSgaSpendOpportunity,
  });
  const conservativeScenarioGrossSavings = roundTo(
    (spec.goldenGrossSavings * 75) / 100,
    2,
  );
  const scenarioOpportunity: ApprovedFormulaDefinition<
    SgaSpendOpportunityScenarioInput,
    SgaSpendOpportunityScenarioOutput
  > = Object.freeze({
    formulaId: `${spec.prefix}_opportunity`,
    version: '1.1.0',
    displayName: `${spec.displayLabel} modeled realization scenario`,
    description:
      `Reconciles the approved ${spec.displayLabel.toLowerCase()} baseline, then applies one labeled realization posture to the approved savings rate and returns the modeled ${outcome}.`,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${spec.prefix}_opportunity_input_v1_1`,
    outputSchemaVersion: `${spec.prefix}_opportunity_output_v1_1`,
    expectedContextKind: spec.contextKind,
    approvedSourceSystemRefs: sourceRefs,
    implementationDigestInputs: SGA_SPEND_SCENARIO_IMPLEMENTATION_DIGEST_INPUTS,
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: `${spec.prefix}_opportunity:scenario:conservative:v1_1`,
        input: Object.freeze({
          totalSpendUsd: spec.goldenTotal,
          savingsRatePct: spec.goldenSavingsPct,
          recoveryFactorPct: spec.goldenRecoveryPct,
          programCostUsd: spec.goldenProgramCost,
          scenarioRealizationFactorPct: 75,
          expectedBaselineOpportunityUsd: spec.goldenOpportunity,
          reportingCutAsOf: '2026-06-08',
        }),
        expected: Object.freeze({
          status: 'ok' as const,
          output: Object.freeze({
            scenarioRealizationFactorPct: 75,
            baselineOpportunityUsd: spec.goldenOpportunity,
            baselineOpportunityVarianceUsd: 0,
            scenarioSavingsRatePct: roundTo(spec.goldenSavingsPct * 0.75, 4),
            scenarioGrossSavingsUsd: conservativeScenarioGrossSavings,
            scenarioOpportunityUsd: roundTo(
              conservativeScenarioGrossSavings - spec.goldenProgramCost,
              2,
            ),
            opportunityDeltaUsd: roundTo(
              conservativeScenarioGrossSavings -
                spec.goldenProgramCost -
                spec.goldenOpportunity,
              2,
            ),
          }),
        }),
      }),
      Object.freeze({
        traceId: `${spec.prefix}_opportunity:scenario:blocked:zero-factor:v1_1`,
        input: Object.freeze({
          totalSpendUsd: spec.goldenTotal,
          savingsRatePct: spec.goldenSavingsPct,
          recoveryFactorPct: spec.goldenRecoveryPct,
          programCostUsd: spec.goldenProgramCost,
          scenarioRealizationFactorPct: 0,
          expectedBaselineOpportunityUsd: spec.goldenOpportunity,
        }),
        expected: Object.freeze({
          status: 'blocked' as const,
          reasonCode: 'invalid_formula_input' as const,
          message:
            'scenarioRealizationFactorPct must be greater than zero and at most 200',
        }),
      }),
    ]),
    reconciliationRule: Object.freeze({
      kind: 'input_output_evidence_reconciliation',
      ruleId: `${spec.prefix}_opportunity_scenario_reconciliation_v1_1`,
      description:
        'Recompute the modeled SG&A opportunity, reconcile its baseline to the observed approved opportunity, and require the same governed reporting cut before publication.',
      validate: validateSgaSpendOpportunityScenarioReconciliation,
    }),
    execute: executeSgaSpendOpportunityScenario,
  });
  const warning: ApprovedFormulaDefinition<SgaSpendWarningInput, SgaSpendWarningOutput> = Object.freeze({
    formulaId: `${spec.prefix}_warning_level`,
    version: '1.0.0',
    displayName: `${spec.displayLabel} warning level`,
    description: `Classifies ${spec.displayLabel.toLowerCase()} consolidation opportunity from the addressable-spend share against externalized floors.`,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${spec.prefix}_warning_input_v1`,
    outputSchemaVersion: `${spec.prefix}_warning_output_v1`,
    expectedContextKind: spec.contextKind,
    approvedSourceSystemRefs: sourceRefs,
    implementationDigestInputs: SGA_SPEND_IMPLEMENTATION_DIGEST_INPUTS,
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: `${spec.prefix}_warning_level:ok:v1`,
        input: Object.freeze({ addressableSharePct: spec.goldenAddressableSharePct }),
        expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ warningLevel: spec.goldenWarningLevel }) }),
      }),
      Object.freeze({
        traceId: `${spec.prefix}_warning_level:blocked:floors:v1`,
        input: Object.freeze({ addressableSharePct: spec.goldenAddressableSharePct, watchFloorPct: 70, decisionRequiredFloorPct: 30 }),
        expected: Object.freeze({ status: 'blocked' as const, reasonCode: 'invalid_formula_input' as const, message: 'warning floors must satisfy 0 <= watch <= decision_required' }),
      }),
    ]),
    execute: executeSgaSpendWarningLevel,
  });
  return Object.freeze([
    addressableShare,
    opportunity,
    scenarioOpportunity,
    warning,
  ]);
}

const SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS = Object.freeze([
  'ask_helix_memory:professional_fees_governed_snapshot_v1',
  'ask_helix_memory:outside_contractors_governed_snapshot_v1',
  'ask_helix_memory:indirect_procurement_governed_snapshot_v1',
  'ask_helix_memory:travel_expense_governed_snapshot_v1',
  'ask_helix_memory:shared_services_governed_snapshot_v1',
  'ask_helix_memory:tech_software_governed_snapshot_v1',
  'ask_helix_memory:real_estate_footprint_governed_snapshot_v1',
  'ask_helix_memory:marketing_spend_governed_snapshot_v1',
]);

export type SgaSpendAddressableSharePlanLeverInput = Readonly<{
  leverId: string;
  totalSpendUsd: number;
  addressableSpendUsd: number;
}>;

export type SgaSpendAddressableSharePlanInput = Readonly<{
  levers: readonly SgaSpendAddressableSharePlanLeverInput[];
  reportingCutAsOf?: string;
}>;

export type SgaSpendAddressableSharePlanOutput = Readonly<{
  totalSpendUsd: number;
  totalAddressableSpendUsd: number;
  levers: ReadonlyArray<Readonly<{
    leverId: string;
    totalSpendUsd: number;
    addressableSpendUsd: number;
    addressableSharePct: number;
  }>>;
}>;

function executeSgaSpendAddressableSharePlan(
  input: SgaSpendAddressableSharePlanInput,
): ApprovedFormulaResult<SgaSpendAddressableSharePlanOutput> {
  const record = asRecord(input);
  if (!record || !Array.isArray(record.levers) || record.levers.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'levers must contain at least one approved SG&A lever',
    };
  }
  const leverIds = new Set<string>();
  const rows: Array<SgaSpendAddressableSharePlanOutput['levers'][number]> = [];
  for (const value of record.levers) {
    const lever = asRecord(value);
    const leverId = typeof lever?.leverId === 'string' ? lever.leverId.trim() : '';
    const totalSpendUsd = lever?.totalSpendUsd;
    const addressableSpendUsd = lever?.addressableSpendUsd;
    if (!leverId) {
      return {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'each addressability lever requires leverId',
      };
    }
    if (leverIds.has(leverId)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `addressability plan contains duplicate leverId: ${leverId}`,
      };
    }
    leverIds.add(leverId);
    if (!isFiniteNumber(totalSpendUsd) || !isFiniteNumber(addressableSpendUsd)) {
      return {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'each addressability lever requires totalSpendUsd and addressableSpendUsd',
      };
    }
    if (totalSpendUsd <= 0 || addressableSpendUsd < 0 || addressableSpendUsd > totalSpendUsd) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each addressability lever requires positive total spend and addressable spend between zero and total spend',
      };
    }
    rows.push(
      Object.freeze({
        leverId,
        totalSpendUsd: roundTo(totalSpendUsd, 2),
        addressableSpendUsd: roundTo(addressableSpendUsd, 2),
        addressableSharePct: roundTo((addressableSpendUsd / totalSpendUsd) * 100, 4),
      }),
    );
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalSpendUsd: roundTo(
        rows.reduce((total, lever) => total + lever.totalSpendUsd, 0),
        2,
      ),
      totalAddressableSpendUsd: roundTo(
        rows.reduce((total, lever) => total + lever.addressableSpendUsd, 0),
        2,
      ),
      levers: Object.freeze(
        [...rows].sort(
          (left, right) =>
            right.addressableSharePct - left.addressableSharePct ||
            right.addressableSpendUsd - left.addressableSpendUsd ||
            left.leverId.localeCompare(right.leverId),
        ),
      ),
    }),
  };
}

const sgaSpendAddressableSharePlanDefinition: ApprovedFormulaDefinition<
  SgaSpendAddressableSharePlanInput,
  SgaSpendAddressableSharePlanOutput
> = Object.freeze({
  formulaId: 'sga_spend_diagnose_addressable_share_plan_v1',
  version: '1.0.0',
  displayName: 'SG&A addressability ranking',
  description:
    'Ranks approved SG&A levers by addressable share, addressable spend, and stable lever identifier without using opportunity rank as a substitute.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'sga_spend_addressable_share_plan_input_v1',
  outputSchemaVersion: 'sga_spend_addressable_share_plan_output_v1',
  approvedSourceSystemRefs: SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeSgaSpendAddressableSharePlan', executeSgaSpendAddressableSharePlan],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'sga_spend_addressable_share_plan:ok:v1',
      input: Object.freeze({
        levers: Object.freeze([
          Object.freeze({
            leverId: 'professional_fees',
            totalSpendUsd: 100,
            addressableSpendUsd: 70,
          }),
          Object.freeze({
            leverId: 'indirect_procurement',
            totalSpendUsd: 200,
            addressableSpendUsd: 120,
          }),
        ]),
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          totalSpendUsd: 300,
          totalAddressableSpendUsd: 190,
          levers: Object.freeze([
            Object.freeze({
              leverId: 'professional_fees',
              totalSpendUsd: 100,
              addressableSpendUsd: 70,
              addressableSharePct: 70,
            }),
            Object.freeze({
              leverId: 'indirect_procurement',
              totalSpendUsd: 200,
              addressableSpendUsd: 120,
              addressableSharePct: 60,
            }),
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'sga_spend_addressable_share_plan:blocked:invalid-denominator:v1',
      input: Object.freeze({
        levers: Object.freeze([
          Object.freeze({
            leverId: 'professional_fees',
            totalSpendUsd: 0,
            addressableSpendUsd: 0,
          }),
        ]),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'each addressability lever requires positive total spend and addressable spend between zero and total spend',
      }),
    }),
  ]),
  execute: executeSgaSpendAddressableSharePlan,
});

export type SgaSpendRecoveryFactorScenarioLeverInput = Readonly<{
  leverId: string;
  addressableSpendUsd: number;
  savingsRatePct: number;
  baselineRecoveryFactorPct: number;
  calculationMethod: 'rate_on_addressable_base';
}>;

export type SgaSpendRecoveryFactorScenarioInput = Readonly<{
  levers: readonly SgaSpendRecoveryFactorScenarioLeverInput[];
  scenarioRecoveryFactorPct: number;
  reportingCutAsOf?: string;
}>;

export type SgaSpendRecoveryFactorScenarioOutput = Readonly<{
  baselineAddressableSpendUsd: number;
  baselineRealizableSavingsUsd: number;
  scenarioRealizableSavingsUsd: number;
  realizableSavingsDeltaUsd: number;
  levers: ReadonlyArray<Readonly<{
    leverId: string;
    addressableSpendUsd: number;
    grossIdentifiedSavingsUsd: number;
    baselineRealizableSavingsUsd: number;
    scenarioRealizableSavingsUsd: number;
  }>>;
}>;

function executeSgaSpendRecoveryFactorScenario(
  input: SgaSpendRecoveryFactorScenarioInput,
): ApprovedFormulaResult<SgaSpendRecoveryFactorScenarioOutput> {
  const record = asRecord(input);
  if (!record || !Array.isArray(record.levers) || record.levers.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'levers must contain at least one approved SG&A lever',
    };
  }
  const scenarioRecoveryFactorPct = record.scenarioRecoveryFactorPct;
  if (!isFiniteNumber(scenarioRecoveryFactorPct)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'scenarioRecoveryFactorPct is required',
    };
  }
  if (scenarioRecoveryFactorPct <= 0 || scenarioRecoveryFactorPct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'scenarioRecoveryFactorPct must be greater than zero and at most 100',
    };
  }

  const leverIds = new Set<string>();
  const rows: Array<SgaSpendRecoveryFactorScenarioOutput['levers'][number]> = [];
  for (const value of record.levers) {
    const lever = asRecord(value);
    const leverId = typeof lever?.leverId === 'string' ? lever.leverId.trim() : '';
    const addressableSpendUsd = lever?.addressableSpendUsd;
    const savingsRatePct = lever?.savingsRatePct;
    const baselineRecoveryFactorPct = lever?.baselineRecoveryFactorPct;
    const calculationMethod = lever?.calculationMethod;
    if (!leverId) {
      return {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'each recovery scenario lever requires leverId',
      };
    }
    if (leverIds.has(leverId)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `recovery scenario contains duplicate leverId: ${leverId}`,
      };
    }
    leverIds.add(leverId);
    if (
      !isFiniteNumber(addressableSpendUsd) ||
      !isFiniteNumber(savingsRatePct) ||
      !isFiniteNumber(baselineRecoveryFactorPct) ||
      typeof calculationMethod !== 'string'
    ) {
      return {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'each recovery scenario lever requires addressableSpendUsd, savingsRatePct, baselineRecoveryFactorPct, and calculationMethod',
      };
    }
    if (addressableSpendUsd < 0 || savingsRatePct < 0 || savingsRatePct > 100) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'recovery scenario lever addressable spend must be non-negative and savings rate must be between 0 and 100',
      };
    }
    if (baselineRecoveryFactorPct <= 0 || baselineRecoveryFactorPct > 100) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'baselineRecoveryFactorPct must be greater than zero and at most 100',
      };
    }
    if (calculationMethod !== 'rate_on_addressable_base') {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each recovery scenario lever calculationMethod must be rate_on_addressable_base',
      };
    }
    const grossIdentifiedSavingsUsd = roundTo((addressableSpendUsd * savingsRatePct) / 100, 2);
    const baselineRealizableSavingsUsd = roundTo(
      (grossIdentifiedSavingsUsd * baselineRecoveryFactorPct) / 100,
      2,
    );
    const scenarioRealizableSavingsUsd = roundTo(
      (grossIdentifiedSavingsUsd * scenarioRecoveryFactorPct) / 100,
      2,
    );
    rows.push(
      Object.freeze({
        leverId,
        addressableSpendUsd,
        grossIdentifiedSavingsUsd,
        baselineRealizableSavingsUsd,
        scenarioRealizableSavingsUsd,
      }),
    );
  }

  const levers = Object.freeze([...rows].sort((left, right) => left.leverId.localeCompare(right.leverId)));
  return {
    status: 'ok',
    output: Object.freeze({
      baselineAddressableSpendUsd: roundTo(
        levers.reduce((total, lever) => total + lever.addressableSpendUsd, 0),
        2,
      ),
      baselineRealizableSavingsUsd: roundTo(
        levers.reduce((total, lever) => total + lever.baselineRealizableSavingsUsd, 0),
        2,
      ),
      scenarioRealizableSavingsUsd: roundTo(
        levers.reduce((total, lever) => total + lever.scenarioRealizableSavingsUsd, 0),
        2,
      ),
      realizableSavingsDeltaUsd: roundTo(
        levers.reduce(
          (total, lever) =>
            total + lever.scenarioRealizableSavingsUsd - lever.baselineRealizableSavingsUsd,
          0,
        ),
        2,
      ),
      levers,
    }),
  };
}

const sgaSpendRecoveryFactorScenarioDefinition: ApprovedFormulaDefinition<
  SgaSpendRecoveryFactorScenarioInput,
  SgaSpendRecoveryFactorScenarioOutput
> = Object.freeze({
  formulaId: 'sga_spend_recovery_factor_scenario_v1',
  version: '1.0.0',
  displayName: 'SG&A recovery-factor scenario',
  description:
    'Recomputes realizable savings for one approved recovery-factor override on an approved addressable base; timing and recurring-cost classification remain outside this calculation.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'sga_spend_recovery_factor_scenario_input_v1',
  outputSchemaVersion: 'sga_spend_recovery_factor_scenario_output_v1',
  approvedSourceSystemRefs: SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeSgaSpendRecoveryFactorScenario', executeSgaSpendRecoveryFactorScenario],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'sga_spend_recovery_factor_scenario:ok:v1',
      input: Object.freeze({
        levers: Object.freeze([
          Object.freeze({
            leverId: 'professional_fees',
            addressableSpendUsd: 100,
            savingsRatePct: 10,
            baselineRecoveryFactorPct: 80,
            calculationMethod: 'rate_on_addressable_base' as const,
          }),
        ]),
        scenarioRecoveryFactorPct: 50,
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          baselineAddressableSpendUsd: 100,
          baselineRealizableSavingsUsd: 8,
          scenarioRealizableSavingsUsd: 5,
          realizableSavingsDeltaUsd: -3,
          levers: Object.freeze([
            Object.freeze({
              leverId: 'professional_fees',
              addressableSpendUsd: 100,
              grossIdentifiedSavingsUsd: 10,
              baselineRealizableSavingsUsd: 8,
              scenarioRealizableSavingsUsd: 5,
            }),
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'sga_spend_recovery_factor_scenario:blocked:invalid-factor:v1',
      input: Object.freeze({
        levers: Object.freeze([]),
        scenarioRecoveryFactorPct: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'levers must contain at least one approved SG&A lever',
      }),
    }),
  ]),
  execute: executeSgaSpendRecoveryFactorScenario,
});

export type SgaSpendRecoveryFactorScenarioV110LeverInput = Readonly<{
  leverId: string;
  baselineFormulaId: string;
  baselineRecoveredSavingsUsd: number;
  baselineRecoveryFactorPct: number;
  programCostUsd: number;
  calculationMethod: 'rescale_approved_recovery';
}>;

export type SgaSpendRecoveryFactorScenarioV110Input = Readonly<{
  levers: readonly SgaSpendRecoveryFactorScenarioV110LeverInput[];
  scenarioRecoveryFactorPct: number;
  expectedBaselineOperatingIncomeUsd: number;
  reportingCutAsOf?: string;
}>;

export type SgaSpendRecoveryFactorScenarioV110Output = Readonly<{
  scenarioRecoveryFactorPct: number;
  baselineOperatingIncomeUsd: number;
  baselineOperatingIncomeVarianceUsd: number;
  scenarioOperatingIncomeUsd: number;
  operatingIncomeDeltaUsd: number;
  levers: ReadonlyArray<Readonly<{
    leverId: string;
    baselineFormulaId: string;
    baselineRecoveredSavingsUsd: number;
    baselineRecoveryFactorPct: number;
    programCostUsd: number;
    scenarioRecoveredSavingsUsd: number;
    baselineOperatingIncomeUsd: number;
    scenarioOperatingIncomeUsd: number;
  }>>;
}>;

function executeSgaSpendRecoveryFactorScenarioV110(
  input: SgaSpendRecoveryFactorScenarioV110Input,
): ApprovedFormulaResult<SgaSpendRecoveryFactorScenarioV110Output> {
  const record = asRecord(input);
  if (!record || !Array.isArray(record.levers) || record.levers.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'levers must contain at least one receipted SG&A lever',
    };
  }
  const scenarioRecoveryFactorPct = record.scenarioRecoveryFactorPct;
  if (!isFiniteNumber(scenarioRecoveryFactorPct)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'scenarioRecoveryFactorPct is required',
    };
  }
  if (scenarioRecoveryFactorPct <= 0 || scenarioRecoveryFactorPct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'scenarioRecoveryFactorPct must be greater than zero and at most 100',
    };
  }
  const expectedBaselineOperatingIncomeUsd = record.expectedBaselineOperatingIncomeUsd;
  if (!isFiniteNumber(expectedBaselineOperatingIncomeUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'expectedBaselineOperatingIncomeUsd is required',
    };
  }
  if (expectedBaselineOperatingIncomeUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'expectedBaselineOperatingIncomeUsd must be non-negative',
    };
  }

  const leverIds = new Set<string>();
  const rows: Array<SgaSpendRecoveryFactorScenarioV110Output['levers'][number]> = [];
  for (const value of record.levers) {
    const lever = asRecord(value);
    const leverId = typeof lever?.leverId === 'string' ? lever.leverId.trim() : '';
    const baselineFormulaId =
      typeof lever?.baselineFormulaId === 'string' ? lever.baselineFormulaId.trim() : '';
    const baselineRecoveredSavingsUsd = lever?.baselineRecoveredSavingsUsd;
    const baselineRecoveryFactorPct = lever?.baselineRecoveryFactorPct;
    const programCostUsd = lever?.programCostUsd;
    const calculationMethod = lever?.calculationMethod;
    if (!leverId || !baselineFormulaId) {
      return {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message: 'each recovery posture lever requires leverId and baselineFormulaId',
      };
    }
    if (leverIds.has(leverId)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `recovery posture contains duplicate leverId: ${leverId}`,
      };
    }
    leverIds.add(leverId);
    if (
      !isFiniteNumber(baselineRecoveredSavingsUsd) ||
      !isFiniteNumber(baselineRecoveryFactorPct) ||
      !isFiniteNumber(programCostUsd) ||
      calculationMethod !== 'rescale_approved_recovery'
    ) {
      return {
        status: 'blocked',
        reasonCode: 'missing_formula_input',
        message:
          'each recovery posture lever requires baseline recovered savings, recovery factor, program cost, and rescale_approved_recovery calculation method',
      };
    }
    if (
      baselineRecoveredSavingsUsd < 0 ||
      programCostUsd < 0 ||
      baselineRecoveryFactorPct <= 0 ||
      baselineRecoveryFactorPct > 100
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'baseline recovered savings and program cost must be non-negative and baseline recovery factor must be greater than zero and at most 100',
      };
    }
    const grossIdentifiedSavingsUsd = roundTo(
      baselineRecoveredSavingsUsd / (baselineRecoveryFactorPct / 100),
      2,
    );
    const scenarioRecoveredSavingsUsd = roundTo(
      grossIdentifiedSavingsUsd * (scenarioRecoveryFactorPct / 100),
      2,
    );
    rows.push(
      Object.freeze({
        leverId,
        baselineFormulaId,
        baselineRecoveredSavingsUsd,
        baselineRecoveryFactorPct,
        programCostUsd,
        scenarioRecoveredSavingsUsd,
        baselineOperatingIncomeUsd: roundTo(
          baselineRecoveredSavingsUsd - programCostUsd,
          2,
        ),
        scenarioOperatingIncomeUsd: roundTo(
          scenarioRecoveredSavingsUsd - programCostUsd,
          2,
        ),
      }),
    );
  }

  const levers = Object.freeze(
    [...rows].sort((left, right) => left.leverId.localeCompare(right.leverId)),
  );
  const baselineOperatingIncomeUsd = roundTo(
    levers.reduce((total, lever) => total + lever.baselineOperatingIncomeUsd, 0),
    2,
  );
  const scenarioOperatingIncomeUsd = roundTo(
    levers.reduce((total, lever) => total + lever.scenarioOperatingIncomeUsd, 0),
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      scenarioRecoveryFactorPct,
      baselineOperatingIncomeUsd,
      baselineOperatingIncomeVarianceUsd: roundTo(
        Math.abs(baselineOperatingIncomeUsd - expectedBaselineOperatingIncomeUsd),
        2,
      ),
      scenarioOperatingIncomeUsd,
      operatingIncomeDeltaUsd: roundTo(
        scenarioOperatingIncomeUsd - baselineOperatingIncomeUsd,
        2,
      ),
      levers,
    }),
  };
}

const sgaSpendRecoveryFactorScenarioV110Definition: ApprovedFormulaDefinition<
  SgaSpendRecoveryFactorScenarioV110Input,
  SgaSpendRecoveryFactorScenarioV110Output
> = Object.freeze({
  formulaId: 'sga_spend_recovery_factor_scenario_v1',
  version: '1.1.0',
  displayName: 'SG&A recovery-factor operating-income posture',
  description:
    'Rescales each formula-receipted SG&A recovered-savings baseline to one governed portfolio recovery posture, retains approved program cost, and returns the net operating-income contribution.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'sga_spend_recovery_factor_scenario_input_v1_1',
  outputSchemaVersion: 'sga_spend_recovery_factor_scenario_output_v1_1',
  approvedSourceSystemRefs: SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeSgaSpendRecoveryFactorScenarioV110', executeSgaSpendRecoveryFactorScenarioV110],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'sga_spend_recovery_factor_scenario:net-oi:ok:v1_1',
      input: Object.freeze({
        levers: Object.freeze([
          Object.freeze({
            leverId: 'professional_fees',
            baselineFormulaId: 'professional_fees_opportunity',
            baselineRecoveredSavingsUsd: 8,
            baselineRecoveryFactorPct: 80,
            programCostUsd: 1,
            calculationMethod: 'rescale_approved_recovery' as const,
          }),
        ]),
        scenarioRecoveryFactorPct: 50,
        expectedBaselineOperatingIncomeUsd: 7,
        reportingCutAsOf: '2026-06-08',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          scenarioRecoveryFactorPct: 50,
          baselineOperatingIncomeUsd: 7,
          baselineOperatingIncomeVarianceUsd: 0,
          scenarioOperatingIncomeUsd: 4,
          operatingIncomeDeltaUsd: -3,
          levers: Object.freeze([
            Object.freeze({
              leverId: 'professional_fees',
              baselineFormulaId: 'professional_fees_opportunity',
              baselineRecoveredSavingsUsd: 8,
              baselineRecoveryFactorPct: 80,
              programCostUsd: 1,
              scenarioRecoveredSavingsUsd: 5,
              baselineOperatingIncomeUsd: 7,
              scenarioOperatingIncomeUsd: 4,
            }),
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'sga_spend_recovery_factor_scenario:net-oi:blocked:v1_1',
      input: Object.freeze({
        levers: Object.freeze([]),
        scenarioRecoveryFactorPct: 50,
        expectedBaselineOperatingIncomeUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'levers must contain at least one receipted SG&A lever',
      }),
    }),
  ]),
  execute: executeSgaSpendRecoveryFactorScenarioV110,
});

type SgaSpendTypedPlanGateInput = Readonly<{
  intentId: string;
  evidenceKeys: readonly string[];
}>;

type SgaSpendTypedPlanGateOutput = Readonly<Record<string, never>>;

function executeSgaSpendTypedPlanGate(
  input: SgaSpendTypedPlanGateInput,
): ApprovedFormulaResult<SgaSpendTypedPlanGateOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'intentId is required for the SG&A typed calculation-plan gate',
    };
  }
  const intentId = typeof record.intentId === 'string' ? record.intentId.trim() : '';
  if (!intentId) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'intentId is required for the SG&A typed calculation-plan gate',
    };
  }
  if (!Array.isArray(record.evidenceKeys) || record.evidenceKeys.some((key) => typeof key !== 'string')) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'evidenceKeys must list the approved evidence references presented to the SG&A plan',
    };
  }
  return {
    status: 'blocked',
    reasonCode: 'missing_formula_input',
    message:
      `${intentId} requires its declared typed calculation rows; an evidence reference alone cannot be converted into a ranked opportunity payload.`,
  };
}

function sgaSpendTypedPlanGateDefinition(input: {
  formulaId: string;
  intentId: string;
  displayName: string;
}): ApprovedFormulaDefinition<SgaSpendTypedPlanGateInput, SgaSpendTypedPlanGateOutput> {
  const missingTypedRowsMessage =
    `${input.intentId} requires its declared typed calculation rows; an evidence reference alone cannot be converted into a ranked opportunity payload.`;
  return Object.freeze({
    formulaId: input.formulaId,
    version: '1.0.0',
    displayName: input.displayName,
    description:
      'Fail-closed SG&A calculation-plan gate. It refuses a plan until its charter-declared typed rows are available through governed evidence.',
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${input.formulaId}_input_v1`,
    outputSchemaVersion: `${input.formulaId}_output_v1`,
    approvedSourceSystemRefs: SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs([
      ['executeSgaSpendTypedPlanGate', executeSgaSpendTypedPlanGate],
    ]),
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: `${input.formulaId}:blocked:typed-rows-required:v1`,
        input: Object.freeze({ intentId: input.intentId, evidenceKeys: Object.freeze([]) }),
        expected: Object.freeze({
          status: 'blocked' as const,
          reasonCode: 'missing_formula_input' as const,
          message: missingTypedRowsMessage,
        }),
      }),
    ]),
    execute: executeSgaSpendTypedPlanGate,
  });
}

const sgaSpendTypedPlanGateDefinitions: readonly ApprovedFormulaDefinition[] = Object.freeze([
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_diagnose_policy_leakage_plan_v1',
    intentId: 'sga_spend_diagnose_policy_leakage',
    displayName: 'SG&A policy-leakage calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_compare_lever_options_plan_v1',
    intentId: 'sga_spend_compare_lever_options',
    displayName: 'SG&A lever-options calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_compare_owner_tradeoffs_plan_v1',
    intentId: 'sga_spend_compare_owner_tradeoffs',
    displayName: 'SG&A owner-tradeoff calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_run_program_cost_scenario_plan_v1',
    intentId: 'sga_spend_run_program_cost_scenario',
    displayName: 'SG&A program-cost scenario calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_quantify_run_rate_vs_one_time_plan_v1',
    intentId: 'sga_spend_quantify_run_rate_vs_one_time',
    displayName: 'SG&A value-class calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_expose_execution_risk_plan_v1',
    intentId: 'sga_spend_expose_execution_risk',
    displayName: 'SG&A execution-risk calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_support_control_plan_plan_v1',
    intentId: 'sga_spend_support_control_plan',
    displayName: 'SG&A control-plan calculation plan',
  }),
  sgaSpendTypedPlanGateDefinition({
    formulaId: 'sga_spend_monitor_drift_to_target_plan_v1',
    intentId: 'sga_spend_monitor_drift_to_target',
    displayName: 'SG&A drift-monitoring calculation plan',
  }),
]);

type SgaSpendHonestReusePlanInput = Readonly<{ intentId: string }>;
type SgaSpendHonestReusePlanOutput = Readonly<{ reuseIntentId: string }>;

function executeSgaSpendHonestReusePlan(
  input: SgaSpendHonestReusePlanInput,
): ApprovedFormulaResult<SgaSpendHonestReusePlanOutput> {
  const record = asRecord(input);
  const intentId = typeof record?.intentId === 'string' ? record.intentId.trim() : '';
  if (!intentId) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'intentId is required for the SG&A honest-reuse plan',
    };
  }
  return { status: 'ok', output: Object.freeze({ reuseIntentId: intentId }) };
}

function sgaSpendHonestReusePlanDefinition(input: {
  formulaId: string;
  intentId: string;
  displayName: string;
}): ApprovedFormulaDefinition<SgaSpendHonestReusePlanInput, SgaSpendHonestReusePlanOutput> {
  return Object.freeze({
    formulaId: input.formulaId,
    version: '1.0.0',
    displayName: input.displayName,
    description:
      'Records a charter-approved SG&A reuse of certified lever calculations without introducing additional arithmetic.',
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${input.formulaId}_input_v1`,
    outputSchemaVersion: `${input.formulaId}_output_v1`,
    approvedSourceSystemRefs: SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs([
      ['executeSgaSpendHonestReusePlan', executeSgaSpendHonestReusePlan],
    ]),
    goldenTraces: Object.freeze([
      Object.freeze({
        traceId: `${input.formulaId}:ok:v1`,
        input: Object.freeze({ intentId: input.intentId }),
        expected: Object.freeze({
          status: 'ok' as const,
          output: Object.freeze({ reuseIntentId: input.intentId }),
        }),
      }),
    ]),
    execute: executeSgaSpendHonestReusePlan,
  });
}

const sgaSpendHonestReusePlanDefinitions: readonly ApprovedFormulaDefinition[] = Object.freeze([
  sgaSpendHonestReusePlanDefinition({
    formulaId: 'sga_spend_diagnose_driver_mix_reuse_v1',
    intentId: 'sga_spend_diagnose_driver_mix',
    displayName: 'SG&A driver-mix honest reuse plan',
  }),
  sgaSpendHonestReusePlanDefinition({
    formulaId: 'sga_spend_quantify_total_value_bridge_reuse_v1',
    intentId: 'sga_spend_quantify_total_value_bridge',
    displayName: 'SG&A total-value-bridge honest reuse plan',
  }),
  sgaSpendHonestReusePlanDefinition({
    formulaId: 'sga_spend_support_activation_package_reuse_v1',
    intentId: 'sga_spend_support_activation_package',
    displayName: 'SG&A activation-package honest reuse plan',
  }),
]);

type SgaSpendPoolFootprintAllocationRow = Readonly<{
  rowId: string;
  opportunityUsd: number;
  costPoolIds: readonly string[];
}>;

type SgaSpendPoolFootprintAllocationInput = Readonly<{
  rows: readonly SgaSpendPoolFootprintAllocationRow[];
}>;

type SgaSpendPoolFootprintAllocationOutput = Readonly<{
  allocationAtomCount: number;
  allocations: readonly Readonly<{
    rowId: string;
    costPoolId: string;
    drawnUsd: number;
  }>[];
}>;

function executeSgaSpendPoolFootprintAllocation(
  input: SgaSpendPoolFootprintAllocationInput,
): ApprovedFormulaResult<SgaSpendPoolFootprintAllocationOutput> {
  if (!Array.isArray(input?.rows) || input.rows.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'rows must contain at least one approved SG&A footprint row',
    };
  }

  const allocations: Array<{
    rowId: string;
    costPoolId: string;
    drawnUsd: number;
  }> = [];
  const rowIds = new Set<string>();
  for (const row of input.rows) {
    const rowId = typeof row?.rowId === 'string' ? row.rowId.trim() : '';
    const opportunityUsd = row?.opportunityUsd;
    const costPoolIds = Array.isArray(row?.costPoolIds)
      ? row.costPoolIds.map((costPoolId: unknown) =>
          typeof costPoolId === 'string' ? costPoolId.trim() : '',
        )
      : [];
    if (
      !rowId ||
      rowIds.has(rowId) ||
      !Number.isFinite(opportunityUsd) ||
      costPoolIds.length === 0 ||
      costPoolIds.some((costPoolId: string) => !costPoolId) ||
      new Set(costPoolIds).size !== costPoolIds.length
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'Each SG&A footprint row requires a unique id, finite opportunity, and unique non-empty cost-pool members.',
      };
    }
    rowIds.add(rowId);
    const sortedCostPoolIds = [...costPoolIds].sort((left, right) => left.localeCompare(right));
    const totalCents = Math.round(opportunityUsd * 100);
    const sign = Math.sign(totalCents) || 1;
    const absoluteCents = Math.abs(totalCents);
    const baseCents = Math.floor(absoluteCents / sortedCostPoolIds.length);
    const remainderCents = absoluteCents % sortedCostPoolIds.length;
    for (const [index, costPoolId] of sortedCostPoolIds.entries()) {
      allocations.push({
        rowId,
        costPoolId,
        drawnUsd: (sign * (baseCents + (index < remainderCents ? 1 : 0))) / 100,
      });
    }
  }

  return {
    status: 'ok',
    output: Object.freeze({
      allocationAtomCount: allocations.length,
      allocations: Object.freeze(allocations.map((allocation) => Object.freeze(allocation))),
    }),
  };
}

const sgaSpendPoolFootprintAllocationDefinition: ApprovedFormulaDefinition<
  SgaSpendPoolFootprintAllocationInput,
  SgaSpendPoolFootprintAllocationOutput
> = Object.freeze({
  formulaId: 'sga_spend_allocate_pool_footprint_v1',
  version: '1.0.0',
  displayName: 'SG&A cost-pool largest-remainder allocation',
  description:
    'Allocates every approved SG&A row across its cost-pool members in cents, assigning remainder cents by ascending cost-pool id so the resulting footprint is deterministic and exactly reconciled.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'sga_spend_pool_footprint_allocation_input_v1',
  outputSchemaVersion: 'sga_spend_pool_footprint_allocation_output_v1',
  approvedSourceSystemRefs: SGA_SPEND_INTENT_PLAN_APPROVED_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeSgaSpendPoolFootprintAllocation', executeSgaSpendPoolFootprintAllocation],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'sga_spend_pool_footprint_allocation:largest-remainder:v1',
      input: Object.freeze({
        rows: Object.freeze([
          Object.freeze({
            rowId: 'professional_fees',
            opportunityUsd: 1.01,
            costPoolIds: Object.freeze(['cost_pool:zeta', 'cost_pool:alpha']),
          }),
        ]),
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          allocationAtomCount: 2,
          allocations: Object.freeze([
            Object.freeze({
              rowId: 'professional_fees',
              costPoolId: 'cost_pool:alpha',
              drawnUsd: 0.51,
            }),
            Object.freeze({
              rowId: 'professional_fees',
              costPoolId: 'cost_pool:zeta',
              drawnUsd: 0.5,
            }),
          ]),
        }),
      }),
    }),
  ]),
  execute: executeSgaSpendPoolFootprintAllocation,
});

// professional-fees: total 120M, addressable 84M (70%), savings 10%, recovery
// 80%, program 600K -> 120M*0.10*0.80 = 9,600,000 - 600,000 = 9,000,000.
const professionalFeesFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'professional_fees',
  contextKind: 'decision_agent:professional_fees:governed_snapshot',
  sourceRef: 'ask_helix_memory:professional_fees_governed_snapshot_v1',
  displayLabel: 'Professional fees',
  goldenAddressable: 84000000, goldenTotal: 120000000, goldenAddressableSharePct: 70,
  goldenSavingsPct: 10, goldenRecoveryPct: 80, goldenProgramCost: 600000,
  goldenGrossSavings: 9600000, goldenOpportunity: 9000000, goldenWarningLevel: 'decision_required',
});

// tech-software: total 90M, addressable 58.5M (65%), savings 7.5%, recovery
// 80%, program 400K -> 90M*0.075*0.80 = 5,400,000 - 400,000 = 5,000,000.
const techSoftwareFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'tech_software',
  contextKind: 'decision_agent:tech_software:governed_snapshot',
  sourceRef: 'ask_helix_memory:tech_software_governed_snapshot_v1',
  displayLabel: 'Technology and software',
  goldenAddressable: 58500000, goldenTotal: 90000000, goldenAddressableSharePct: 65,
  goldenSavingsPct: 7.5, goldenRecoveryPct: 80, goldenProgramCost: 400000,
  goldenGrossSavings: 5400000, goldenOpportunity: 5000000, goldenWarningLevel: 'decision_required',
});

// real-estate-footprint: total 50M, addressable 30M (60%), savings 13%,
// recovery 75%, program 375K -> 50M*0.13*0.75 = 4,875,000 - 375,000 = 4,500,000.
const realEstateFootprintFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'real_estate_footprint',
  contextKind: 'decision_agent:real_estate_footprint:governed_snapshot',
  sourceRef: 'ask_helix_memory:real_estate_footprint_governed_snapshot_v1',
  displayLabel: 'Real estate and facilities',
  goldenAddressable: 30000000, goldenTotal: 50000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 13, goldenRecoveryPct: 75, goldenProgramCost: 375000,
  goldenGrossSavings: 4875000, goldenOpportunity: 4500000, goldenWarningLevel: 'decision_required',
});

// shared-services: total 70M, addressable 42M (60%), savings 9%, recovery 80%,
// program 540K -> 70M*0.09*0.80 = 5,040,000 - 540,000 = 4,500,000.
const sharedServicesFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'shared_services',
  contextKind: 'decision_agent:shared_services:governed_snapshot',
  sourceRef: 'ask_helix_memory:shared_services_governed_snapshot_v1',
  displayLabel: 'Shared services',
  goldenAddressable: 42000000, goldenTotal: 70000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 9, goldenRecoveryPct: 80, goldenProgramCost: 540000,
  goldenGrossSavings: 5040000, goldenOpportunity: 4500000, goldenWarningLevel: 'decision_required',
});

// marketing-spend: total 40M, addressable 24M (60%), savings 10%, recovery 80%,
// program 200K -> 40M*0.10*0.80 = 3,200,000 - 200,000 = 3,000,000. Demand-gen,
// agency, media, events, and martech vendor spend — distinct from professional
// fees (advisory), tech-software (licenses), and the other SG&A categories.
const marketingSpendFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'marketing_spend',
  contextKind: 'decision_agent:marketing_spend:governed_snapshot',
  sourceRef: 'ask_helix_memory:marketing_spend_governed_snapshot_v1',
  displayLabel: 'Marketing and demand generation',
  goldenAddressable: 24000000, goldenTotal: 40000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 10, goldenRecoveryPct: 80, goldenProgramCost: 200000,
  goldenGrossSavings: 3200000, goldenOpportunity: 3000000, goldenWarningLevel: 'decision_required',
});

// ---------------------------------------------------------------------------
// Purchased-transportation COGS levers. Same addressable-spend math, applied to
// SUBSETS of the ~$13B purchased-transportation pool. These overlap the carrier
// renegotiation lever AND each other (all attack the same buy-side spend), so
// the overlap registry gates them out of the additive bridge — each is a full
// governed module that answers standalone, but they are never summed together.
// ---------------------------------------------------------------------------

// network-optimization: redesignable lane spend pool 2.0B, addressable 1.2B
// (60%), savings 4%, recovery 60%, program 8M -> 2.0B*0.04*0.60 = 48,000,000 -
// 8,000,000 = 40,000,000.
const networkOptimizationFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'network_optimization',
  contextKind: 'decision_agent:network_optimization:governed_snapshot',
  sourceRef: 'ask_helix_memory:network_optimization_governed_snapshot_v1',
  displayLabel: 'Network design',
  mechanismNoun: 'lane and network redesign',
  goldenAddressable: 1200000000, goldenTotal: 2000000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 4, goldenRecoveryPct: 60, goldenProgramCost: 8000000,
  goldenGrossSavings: 48000000, goldenOpportunity: 40000000, goldenWarningLevel: 'decision_required',
});

// capacity-utilization: backhaul-relevant spend pool 1.5B, addressable 0.9B
// (60%), savings 3%, recovery 60%, program 6M -> 1.5B*0.03*0.60 = 27,000,000 -
// 6,000,000 = 21,000,000.
const capacityUtilizationFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'capacity_utilization',
  contextKind: 'decision_agent:capacity_utilization:governed_snapshot',
  sourceRef: 'ask_helix_memory:capacity_utilization_governed_snapshot_v1',
  displayLabel: 'Capacity utilization',
  mechanismNoun: 'backhaul and load-factor improvement',
  goldenAddressable: 900000000, goldenTotal: 1500000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 3, goldenRecoveryPct: 60, goldenProgramCost: 6000000,
  goldenGrossSavings: 27000000, goldenOpportunity: 21000000, goldenWarningLevel: 'decision_required',
});


// fuel-efficiency: fuel and accessorial spend pool 0.9B, addressable 0.54B
// (60%), savings 5%, recovery 50%, program 3M -> 0.9B*0.05*0.50 = 22,500,000 -
// 3,000,000 = 19,500,000.
const fuelEfficiencyFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'fuel_efficiency',
  contextKind: 'decision_agent:fuel_efficiency:governed_snapshot',
  sourceRef: 'ask_helix_memory:fuel_efficiency_governed_snapshot_v1',
  displayLabel: 'Fuel and accessorial',
  mechanismNoun: 'fuel surcharge and accessorial recovery',
  goldenAddressable: 540000000, goldenTotal: 900000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 5, goldenRecoveryPct: 50, goldenProgramCost: 3000000,
  goldenGrossSavings: 22500000, goldenOpportunity: 19500000, goldenWarningLevel: 'decision_required',
});

// outside-contractors: contingent-labor + outsourced-services spend pool 60M,
// addressable 36M (60%), savings 10%, recovery 70%, program 200K ->
// 60M*0.10*0.70 = 4,200,000 - 200,000 = 4,000,000. Distinct SG&A vendor category
// (disjoint), adds to the bridge.
const outsideContractorsFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'outside_contractors',
  contextKind: 'decision_agent:outside_contractors:governed_snapshot',
  sourceRef: 'ask_helix_memory:outside_contractors_governed_snapshot_v1',
  displayLabel: 'Outside contractors',
  mechanismNoun: 'contingent-labor conversion and outsourced-services consolidation',
  goldenAddressable: 36000000, goldenTotal: 60000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 10, goldenRecoveryPct: 70, goldenProgramCost: 200000,
  goldenGrossSavings: 4200000, goldenOpportunity: 4000000, goldenWarningLevel: 'decision_required',
});

// labor-arbitrage: relocatable back-office labor pool 400M, addressable 240M
// (60%), savings 15%, recovery 60%, program 5M -> 400M*0.15*0.60 = 36,000,000 -
// 5,000,000 = 31,000,000. Reduces INTERNAL labor cost, so it overlaps the labor
// cluster (org/workforce/productivity) and is withheld from the additive bridge.
const laborArbitrageFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'labor_arbitrage',
  contextKind: 'decision_agent:labor_arbitrage:governed_snapshot',
  sourceRef: 'ask_helix_memory:labor_arbitrage_governed_snapshot_v1',
  displayLabel: 'Labor arbitrage',
  mechanismNoun: 'role relocation and shoring optimization',
  goldenAddressable: 240000000, goldenTotal: 400000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 15, goldenRecoveryPct: 60, goldenProgramCost: 5000000,
  goldenGrossSavings: 36000000, goldenOpportunity: 31000000, goldenWarningLevel: 'decision_required',
});

// ---------------------------------------------------------------------------
// Revenue-side levers. Same addressable-base math, applied to a REVENUE base
// (the "spend" signal carries the revenue pool, "savings" the realization/uplift
// rate, "recovery" the flow-through to operating income). These overlap the
// pricing lever and each other (same customer revenue line), so the registry
// gates them out of the additive bridge — each is a full module answering
// standalone, summed only with a de-confliction receipt. They also sit on a
// DIFFERENT objective track (revenue realization/growth) from the no-growth
// cost bridge.
// ---------------------------------------------------------------------------

// NOTE: discount_leakage is intentionally NOT a separate module — it is already
// served by the pricing-realization module (quote-to-invoice leakage is part of
// sell-rate realization), so giving it its own module would double-count the
// pricing lever.



// churn-reduction: at-risk revenue 200M, addressable 120M (60%), retention
// 7%, flow-through 90%, program 300K -> 200M*0.07*0.90 = 12,600,000 - 300,000 =
// 12,300,000.
const churnReductionFormulaDefinitions = buildSgaSpendLeverFormulaDefinitions({
  prefix: 'churn_reduction',
  contextKind: 'decision_agent:churn_reduction:governed_snapshot',
  sourceRef: 'ask_helix_memory:churn_reduction_governed_snapshot_v1',
  displayLabel: 'Churn reduction',
  mechanismNoun: 'at-risk revenue retention',
  goldenAddressable: 120000000, goldenTotal: 200000000, goldenAddressableSharePct: 60,
  goldenSavingsPct: 7, goldenRecoveryPct: 90, goldenProgramCost: 300000,
  goldenGrossSavings: 12600000, goldenOpportunity: 12300000, goldenWarningLevel: 'decision_required',
});

const daysSalesOutstandingDefinition: ApprovedFormulaDefinition<
  DaysSalesOutstandingInput,
  DaysSalesOutstandingOutput
> = Object.freeze({
  formulaId: 'dso_days_v1',
  version: '1.0.0',
  displayName: 'Days sales outstanding',
  description:
    'Computes DSO as accounts receivable divided by revenue times 365 from approved company COA mapped evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'dso_days_input_v1',
  outputSchemaVersion: 'dso_days_output_v1',
  expectedContextKind: CAPITAL_CASH_CONTEXT_KIND,
  approvedSourceSystemRefs: CAPITAL_CASH_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeDaysSalesOutstanding', executeDaysSalesOutstanding],
    ['workingCapitalDaysStatusBand', workingCapitalDaysStatusBand],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'dso_days:ok:ar100-revenue1000:v1',
      input: Object.freeze({
        accountsReceivableUsd: 100,
        revenueUsd: 1000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          accountsReceivableUsd: 100,
          revenueUsd: 1000,
          dsoDays: 36.5,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'dso_days:blocked:zero-revenue:v1',
      input: Object.freeze({
        accountsReceivableUsd: 100,
        revenueUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'Accounts receivable must be non-negative and revenue must be positive.',
      }),
    }),
  ]),
  execute: executeDaysSalesOutstanding,
});

// Renamed from daysInventoryOutstandingDefinition/executeDaysInventoryOutstanding after the
// origin/main union merge collided with the island's inventory-turns copy of the same
// identifiers (formulaId 'days_inventory_outstanding', different input/output shapes).
// CTO ruling 2026-07-08: rename MAIN's dio_days_v1 side only; keep formulaId stable.
const daysInventoryOutstandingDioDaysDefinition: ApprovedFormulaDefinition<
  DaysInventoryOutstandingDioDaysInput,
  DaysInventoryOutstandingDioDaysOutput
> = Object.freeze({
  formulaId: 'dio_days_v1',
  version: '1.0.0',
  displayName: 'Days inventory outstanding',
  description:
    'Computes DIO as inventory divided by COGS times 365 from approved company COA mapped evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'dio_days_input_v1',
  outputSchemaVersion: 'dio_days_output_v1',
  expectedContextKind: CAPITAL_CASH_CONTEXT_KIND,
  approvedSourceSystemRefs: CAPITAL_CASH_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeDaysInventoryOutstandingDioDays', executeDaysInventoryOutstandingDioDays],
    ['workingCapitalDaysStatusBand', workingCapitalDaysStatusBand],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'dio_days:ok:inventory25-cogs500:v1',
      input: Object.freeze({
        inventoryUsd: 25,
        costOfGoodsSoldUsd: 500,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          inventoryUsd: 25,
          costOfGoodsSoldUsd: 500,
          dioDays: 18.25,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'dio_days:blocked:missing-inventory:v1',
      input: Object.freeze({
        inventoryUsd: Number.NaN,
        costOfGoodsSoldUsd: 500,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'Inventory and COGS are required for DIO.',
      }),
    }),
  ]),
  execute: executeDaysInventoryOutstandingDioDays,
});

const daysPayablesOutstandingDefinition: ApprovedFormulaDefinition<
  DaysPayablesOutstandingInput,
  DaysPayablesOutstandingOutput
> = Object.freeze({
  formulaId: 'dpo_days_v1',
  version: '1.0.0',
  displayName: 'Days payables outstanding',
  description:
    'Computes DPO as accounts payable divided by COGS times 365 from approved company COA mapped evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'dpo_days_input_v1',
  outputSchemaVersion: 'dpo_days_output_v1',
  expectedContextKind: CAPITAL_CASH_CONTEXT_KIND,
  approvedSourceSystemRefs: CAPITAL_CASH_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeDaysPayablesOutstanding', executeDaysPayablesOutstanding],
    ['workingCapitalDaysStatusBand', workingCapitalDaysStatusBand],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'dpo_days:ok:ap50-cogs500:v1',
      input: Object.freeze({
        accountsPayableUsd: 50,
        costOfGoodsSoldUsd: 500,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          accountsPayableUsd: 50,
          costOfGoodsSoldUsd: 500,
          dpoDays: 36.5,
          statusBand: 'constrained' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'dpo_days:blocked:negative-ap:v1',
      input: Object.freeze({
        accountsPayableUsd: -1,
        costOfGoodsSoldUsd: 500,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'Accounts payable must be non-negative and COGS must be positive.',
      }),
    }),
  ]),
  execute: executeDaysPayablesOutstanding,
});

const workingCapitalDeltaCashDefinition: ApprovedFormulaDefinition<
  WorkingCapitalDeltaCashInput,
  WorkingCapitalDeltaCashOutput
> = Object.freeze({
  formulaId: 'working_capital_delta_cash_v1',
  version: '1.0.0',
  displayName: 'Working-capital cash impact',
  description:
    'Computes prior and current net working capital from AR, inventory, and AP, then converts the NWC change into cash impact.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'working_capital_delta_cash_input_v1',
  outputSchemaVersion: 'working_capital_delta_cash_output_v1',
  expectedContextKind: CAPITAL_CASH_CONTEXT_KIND,
  approvedSourceSystemRefs: CAPITAL_CASH_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeWorkingCapitalDeltaCash', executeWorkingCapitalDeltaCash],
    ['workingCapitalCashImpactStatusBand', workingCapitalCashImpactStatusBand],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'working_capital_delta_cash:ok:increase-consumes-cash:v1',
      input: Object.freeze({
        priorAccountsReceivableUsd: 90,
        priorInventoryUsd: 20,
        priorAccountsPayableUsd: 40,
        currentAccountsReceivableUsd: 100,
        currentInventoryUsd: 25,
        currentAccountsPayableUsd: 50,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          priorNetWorkingCapitalUsd: 70,
          currentNetWorkingCapitalUsd: 75,
          netWorkingCapitalDeltaUsd: 5,
          cashImpactUsd: -5,
          statusBand: 'critical' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'working_capital_delta_cash:blocked:missing-current-ap:v1',
      input: Object.freeze({
        priorAccountsReceivableUsd: 90,
        priorInventoryUsd: 20,
        priorAccountsPayableUsd: 40,
        currentAccountsReceivableUsd: 100,
        currentInventoryUsd: 25,
        currentAccountsPayableUsd: Number.NaN,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'Prior and current AR, inventory, and AP are required for NWC cash impact.',
      }),
    }),
  ]),
  execute: executeWorkingCapitalDeltaCash,
});

const cashConversionCycleDaysDefinition: ApprovedFormulaDefinition<
  CashConversionCycleDaysInput,
  CashConversionCycleDaysOutput
> = Object.freeze({
  formulaId: 'cash_conversion_cycle_days_v1',
  version: '1.0.0',
  displayName: 'Cash conversion cycle',
  description:
    'Computes cash conversion cycle days as DSO plus DIO less DPO from approved filing-grounded working-capital component evidence.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'cash_conversion_cycle_days_input_v1',
  outputSchemaVersion: 'cash_conversion_cycle_days_output_v1',
  expectedContextKind: CAPITAL_CASH_CONTEXT_KIND,
  approvedSourceSystemRefs: CAPITAL_CASH_SOURCE_REFS,
  implementationDigestInputs: implementationDigestInputs([
    ['executeCashConversionCycleDays', executeCashConversionCycleDays],
    ['cashConversionCycleStatusBand', cashConversionCycleStatusBand],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'cash_conversion_cycle_days:ok:v1',
      input: Object.freeze({
        daysSalesOutstandingDays: 45,
        daysInventoryOutstandingDays: 12,
        daysPayablesOutstandingDays: 30,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          daysSalesOutstandingDays: 45,
          daysInventoryOutstandingDays: 12,
          daysPayablesOutstandingDays: 30,
          cashConversionCycleDays: 27,
          statusBand: 'sufficient' as const,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'cash_conversion_cycle_days:blocked:missing-dio:v1',
      input: Object.freeze({
        daysSalesOutstandingDays: 45,
        daysInventoryOutstandingDays: Number.NaN,
        daysPayablesOutstandingDays: 30,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'DSO, DIO, and DPO are required day-count inputs.',
      }),
    }),
    Object.freeze({
      traceId: 'cash_conversion_cycle_days:blocked:negative-dpo:v1',
      input: Object.freeze({
        daysSalesOutstandingDays: 45,
        daysInventoryOutstandingDays: 12,
        daysPayablesOutstandingDays: -1,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'DSO, DIO, and DPO must be non-negative day counts.',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateCashConversionCycleInputAgainstEvidence,
  reconciliationRule: Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: 'cash_conversion_cycle_days_reconciles_to_working_capital_components_v1',
    description:
      'DSO, DIO, and DPO inputs must come from an approved capital-cash balance-sheet snapshot for the same reporting cut; CCC output is pure DSO + DIO - DPO.',
    validate: validateCashConversionCycleReconciliation,
  }),
  execute: executeCashConversionCycleDays,
});

// ---------------------------------------------------------------------------
// Capital / cash-returns levers. Same addressable-base math, but the outcome is
// a CASH release, not operating income — they never enter the OI bridge (excluded
// by the cash_returns grade) and answer on their own cash track.
// ---------------------------------------------------------------------------



// ---------------------------------------------------------------------------
// Segment margin cleanup formulas (canonical layer; module segment-margin-cleanup)
// ---------------------------------------------------------------------------

const SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS = Object.freeze([
  'ask_helix_memory:segment_margin_cleanup_governed_snapshot_v1',
]);
// IMPORTED, NEVER RETYPED (#4754 amendment 8, finding 2 / Law 5). A context kind
// is what a governed read matches on: a divergent spelling here would not fail
// loudly, it would admit nothing while the loader kept emitting artifacts under
// the real one.
const SEGMENT_MARGIN_CLEANUP_CONTEXT_KIND =
  SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS.governedSnapshot;

// #2863 margin-fact decomposition (customer x SKU x channel x segment, labeled
// modeled). Already registered as an approved source system; the attribution
// formula below reads it and nothing else, so its admission is single-sourced
// from that registration rather than restated.
const SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_APPROVED_SOURCE_REFS = Object.freeze([
  SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_SOURCE_SYSTEM_REF,
]);
const SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_CONTEXT_KIND =
  SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS.marginFactDecomposition;

// #4754 amendment 3 (Codex P1, finding 1). The attribution execution presents TWO
// governed authorities on ONE invocation: the modeled margin-fact rows the channel
// cells come from, and the approved segment snapshot the cent tie-out is decided
// against. The runtime validates EVERY evidence item against the allowlists below
// (approved-formula-runtime.service.ts `validateEvidenceSourceSystems`), so a
// definition admitting only the margin-fact authority refuses the second one with
// `unapproved_source_system` BEFORE `execute` ever runs. Both sets are composed
// from the registrations already declared above — no ref and no context kind is
// retyped here.
const SEGMENT_MARGIN_ATTRIBUTION_APPROVED_SOURCE_REFS = Object.freeze([
  ...SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_APPROVED_SOURCE_REFS,
  ...SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS,
]);
const SEGMENT_MARGIN_ATTRIBUTION_ADDITIONAL_CONTEXT_KINDS = Object.freeze([
  SEGMENT_MARGIN_CLEANUP_CONTEXT_KIND,
]);

/**
 * Which loaded artifact is which authority, judged on the schema version the DB
 * ROW carries (the runtime copies `row.schemaVersion` onto the artifact; only
 * `sourceSystemRef` and `reportingCutAsOf` come from the caller). The admitted
 * versions are read from the ONE source-system registry rather than restated, so
 * admitting a future `_v3` snapshot there cannot leave this validator judging a
 * stale set (#4754 amendment 4, finding 1).
 */
function segmentMarginAttributionSchemaVersions(
  sourceSystemRef: string,
): ReadonlySet<string> {
  const definition = getApprovedSourceSystemDefinition(sourceSystemRef);
  return new Set([
    definition.evidenceSchemaVersion,
    ...(definition.allowedEvidenceSchemaVersions ?? []),
  ]);
}
const SEGMENT_MARGIN_ATTRIBUTION_MARGIN_FACT_SCHEMA_VERSIONS =
  segmentMarginAttributionSchemaVersions(SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_SOURCE_SYSTEM_REF);
const SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_SCHEMA_VERSIONS =
  segmentMarginAttributionSchemaVersions(SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS[0]);

export type SegmentMarginCleanupSegmentInput = Readonly<{
  segmentId: string;
  netRevenueUsd: number;
  operatingMarginPct: number;
}>;

export type SegmentCleanupOpportunityInput = Readonly<{
  segments: readonly SegmentMarginCleanupSegmentInput[];
  marginFloorPct: number;
  recoveryFactorPct: number;
  reportingCutAsOf?: string;
}>;

export type SegmentCleanupOpportunityOutput = Readonly<{
  cleanupOpportunityUsd: number;
  reviewedNetRevenueUsd: number;
  breachingSegmentIds: readonly string[];
}>;

export type SegmentCleanupDepthSelector =
  | 'half_approved'
  | 'approved'
  | 'full_floor_gap';

export type SegmentCleanupPostureInput = Readonly<{
  segments: readonly SegmentMarginCleanupSegmentInput[];
  marginFloorPct: number;
  approvedRecoveryFactorPct: number;
  cleanupDepthSelector: SegmentCleanupDepthSelector;
  expectedBaselineCleanupOpportunityUsd: number;
  reportingCutAsOf?: string;
}>;

export type SegmentCleanupPostureOutput = Readonly<{
  cleanupDepthSelector: SegmentCleanupDepthSelector;
  marginFloorPct: number;
  approvedRecoveryFactorPct: number;
  scenarioRecoveryFactorPct: number;
  baselineCleanupOpportunityUsd: number;
  baselineCleanupOpportunityVarianceUsd: number;
  scenarioCleanupOpportunityUsd: number;
  cleanupOpportunityDeltaUsd: number;
  reviewedNetRevenueUsd: number;
  breachingSegmentIds: readonly string[];
}>;

function parseSegmentRows(
  value: unknown,
): SegmentMarginCleanupSegmentInput[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const segments: SegmentMarginCleanupSegmentInput[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const record = asRecord(item);
    if (!record) return null;
    const segmentId = typeof record.segmentId === 'string' ? record.segmentId.trim() : '';
    if (!segmentId || seen.has(segmentId)) return null;
    seen.add(segmentId);
    if (
      !isFiniteNumber(record.netRevenueUsd) ||
      !isFiniteNumber(record.operatingMarginPct) ||
      record.netRevenueUsd < 0
    ) {
      return null;
    }
    segments.push({
      segmentId,
      netRevenueUsd: record.netRevenueUsd,
      operatingMarginPct: record.operatingMarginPct,
    });
  }
  return segments;
}

function executeSegmentCleanupOpportunity(
  input: SegmentCleanupOpportunityInput,
): ApprovedFormulaResult<SegmentCleanupOpportunityOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const segments = parseSegmentRows(record.segments);
  if (!segments) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'segments must be a non-empty list of unique {segmentId, netRevenueUsd, operatingMarginPct} rows',
    };
  }
  const floor = record.marginFloorPct;
  const recovery = record.recoveryFactorPct;
  if (!isFiniteNumber(floor) || !isFiniteNumber(recovery)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'marginFloorPct and recoveryFactorPct are required',
    };
  }
  if (floor < 0 || floor > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'marginFloorPct must be between 0 and 100',
    };
  }
  if (recovery <= 0 || recovery > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'recoveryFactorPct must be greater than zero and at most 100',
    };
  }
  const breachingSegmentIds: string[] = [];
  let gapUsd = 0;
  let reviewedNetRevenueUsd = 0;
  for (const segment of segments) {
    reviewedNetRevenueUsd += segment.netRevenueUsd;
    if (segment.operatingMarginPct >= floor) continue;
    breachingSegmentIds.push(segment.segmentId);
    gapUsd += (segment.netRevenueUsd * (floor - segment.operatingMarginPct)) / 100;
  }
  return {
    status: 'ok',
    output: Object.freeze({
      cleanupOpportunityUsd: roundTo((gapUsd * recovery) / 100, 2),
      reviewedNetRevenueUsd: roundTo(reviewedNetRevenueUsd, 2),
      breachingSegmentIds: Object.freeze([...breachingSegmentIds]),
    }),
  };
}

function executeSegmentCleanupPosture(
  input: SegmentCleanupPostureInput,
): ApprovedFormulaResult<SegmentCleanupPostureOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const segments = parseSegmentRows(record.segments);
  if (!segments) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'segments must be a non-empty list of unique {segmentId, netRevenueUsd, operatingMarginPct} rows',
    };
  }
  const floor = record.marginFloorPct;
  const approvedRecovery = record.approvedRecoveryFactorPct;
  const expectedBaseline = record.expectedBaselineCleanupOpportunityUsd;
  if (
    !isFiniteNumber(floor) ||
    !isFiniteNumber(approvedRecovery) ||
    !isFiniteNumber(expectedBaseline)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'marginFloorPct, approvedRecoveryFactorPct, and expectedBaselineCleanupOpportunityUsd are required',
    };
  }
  if (floor < 0 || floor > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'marginFloorPct must be between 0 and 100',
    };
  }
  if (approvedRecovery <= 0 || approvedRecovery > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'approvedRecoveryFactorPct must be greater than zero and at most 100',
    };
  }
  if (expectedBaseline < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'expectedBaselineCleanupOpportunityUsd must be non-negative',
    };
  }
  const selector = record.cleanupDepthSelector;
  if (
    selector !== 'half_approved' &&
    selector !== 'approved' &&
    selector !== 'full_floor_gap'
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'cleanupDepthSelector must be half_approved, approved, or full_floor_gap',
    };
  }

  const scenarioRecovery =
    selector === 'half_approved'
      ? roundTo(approvedRecovery / 2, 6)
      : selector === 'approved'
        ? approvedRecovery
        : 100;
  const breachingSegmentIds: string[] = [];
  let gapUsd = 0;
  let reviewedNetRevenueUsd = 0;
  for (const segment of segments) {
    reviewedNetRevenueUsd += segment.netRevenueUsd;
    if (segment.operatingMarginPct >= floor) continue;
    breachingSegmentIds.push(segment.segmentId);
    gapUsd += (segment.netRevenueUsd * (floor - segment.operatingMarginPct)) / 100;
  }
  const baselineCleanupOpportunityUsd = roundTo(
    (gapUsd * approvedRecovery) / 100,
    2,
  );
  const scenarioCleanupOpportunityUsd = roundTo(
    (gapUsd * scenarioRecovery) / 100,
    2,
  );

  return {
    status: 'ok',
    output: Object.freeze({
      cleanupDepthSelector: selector,
      marginFloorPct: floor,
      approvedRecoveryFactorPct: approvedRecovery,
      scenarioRecoveryFactorPct: scenarioRecovery,
      baselineCleanupOpportunityUsd,
      baselineCleanupOpportunityVarianceUsd: roundTo(
        Math.abs(baselineCleanupOpportunityUsd - expectedBaseline),
        2,
      ),
      scenarioCleanupOpportunityUsd,
      cleanupOpportunityDeltaUsd: roundTo(
        scenarioCleanupOpportunityUsd - baselineCleanupOpportunityUsd,
        2,
      ),
      reviewedNetRevenueUsd: roundTo(reviewedNetRevenueUsd, 2),
      breachingSegmentIds: Object.freeze([...breachingSegmentIds]),
    }),
  };
}

export type SegmentMarginFloorBreachesInput = Readonly<{
  segments: readonly SegmentMarginCleanupSegmentInput[];
  marginFloorPct: number;
  reportingCutAsOf?: string;
}>;

export type SegmentMarginFloorBreachesOutput = Readonly<{
  breachCount: number;
  breachingSegmentIds: readonly string[];
}>;

function executeSegmentMarginFloorBreaches(
  input: SegmentMarginFloorBreachesInput,
): ApprovedFormulaResult<SegmentMarginFloorBreachesOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const segments = parseSegmentRows(record.segments);
  if (!segments) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'segments must be a non-empty list of unique {segmentId, netRevenueUsd, operatingMarginPct} rows',
    };
  }
  const floor = record.marginFloorPct;
  if (!isFiniteNumber(floor) || floor < 0 || floor > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'marginFloorPct must be between 0 and 100',
    };
  }
  const breachingSegmentIds = segments
    .filter((segment) => segment.operatingMarginPct < floor)
    .map((segment) => segment.segmentId);
  return {
    status: 'ok',
    output: Object.freeze({
      breachCount: breachingSegmentIds.length,
      breachingSegmentIds: Object.freeze([...breachingSegmentIds]),
    }),
  };
}

export type SegmentCleanupWarningInput = Readonly<{
  cleanupOpportunityUsd: number;
  reviewedNetRevenueUsd: number;
  watchFloorPct?: number;
  decisionRequiredFloorPct?: number;
}>;

export type SegmentCleanupWarningOutput = Readonly<{
  warningLevel: 'none' | 'watch' | 'decision_required';
  opportunitySharePct: number;
}>;

function executeSegmentCleanupWarningLevel(
  input: SegmentCleanupWarningInput,
): ApprovedFormulaResult<SegmentCleanupWarningOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const opportunity = record.cleanupOpportunityUsd;
  const reviewedRevenue = record.reviewedNetRevenueUsd;
  if (!isFiniteNumber(opportunity) || !isFiniteNumber(reviewedRevenue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'cleanupOpportunityUsd and reviewedNetRevenueUsd are required',
    };
  }
  if (opportunity < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'cleanupOpportunityUsd must be non-negative',
    };
  }
  if (reviewedRevenue <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'reviewedNetRevenueUsd must be greater than zero',
    };
  }
  const watchFloor = isFiniteNumber(record.watchFloorPct) ? record.watchFloorPct : 0.5;
  const decisionFloor = isFiniteNumber(record.decisionRequiredFloorPct)
    ? record.decisionRequiredFloorPct
    : 1.5;
  if (watchFloor < 0 || decisionFloor < watchFloor) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'warning floors must satisfy 0 <= watch <= decision_required',
    };
  }
  const opportunitySharePct = roundTo((opportunity / reviewedRevenue) * 100, 4);
  const warningLevel =
    opportunitySharePct >= decisionFloor
      ? 'decision_required'
      : opportunitySharePct >= watchFloor
        ? 'watch'
        : 'none';
  return {
    status: 'ok',
    output: Object.freeze({ warningLevel, opportunitySharePct }),
  };
}

const SEGMENT_MARGIN_CLEANUP_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['parseSegmentRows', parseSegmentRows],
  ['executeSegmentCleanupOpportunity', executeSegmentCleanupOpportunity],
  ['executeSegmentMarginFloorBreaches', executeSegmentMarginFloorBreaches],
  ['executeSegmentCleanupWarningLevel', executeSegmentCleanupWarningLevel],
]);

// The definition digest canonicalizer rejects shared object references, so
// every golden trace must receive its own frozen copy of the segment rows.
function segmentCleanupGoldenSegments(): readonly SegmentMarginCleanupSegmentInput[] {
  return Object.freeze([
    Object.freeze({ segmentId: 'segment_a', netRevenueUsd: 400000000, operatingMarginPct: 5 }),
    Object.freeze({ segmentId: 'segment_b', netRevenueUsd: 200000000, operatingMarginPct: 4 }),
    Object.freeze({ segmentId: 'segment_c', netRevenueUsd: 900000000, operatingMarginPct: 12 }),
  ]);
}

const segmentCleanupOpportunityDefinition: ApprovedFormulaDefinition<
  SegmentCleanupOpportunityInput,
  SegmentCleanupOpportunityOutput
> = Object.freeze({
  formulaId: 'segment_cleanup_opportunity',
  version: '1.0.0',
  displayName: 'Segment cleanup opportunity',
  description:
    'Sizes the recoverable operating-income opportunity from segments operating below the approved margin floor at the governed recovery factor.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'segment_cleanup_opportunity_input_v1',
  outputSchemaVersion: 'segment_cleanup_opportunity_output_v1',
  expectedContextKind: SEGMENT_MARGIN_CLEANUP_CONTEXT_KIND,
  approvedSourceSystemRefs: SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS,
  implementationDigestInputs: SEGMENT_MARGIN_CLEANUP_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'segment_cleanup_opportunity:ok:v1',
      input: Object.freeze({
        segments: segmentCleanupGoldenSegments(),
        marginFloorPct: 8,
        recoveryFactorPct: 50,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          cleanupOpportunityUsd: 10000000,
          reviewedNetRevenueUsd: 1500000000,
          breachingSegmentIds: Object.freeze(['segment_a', 'segment_b']),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'segment_cleanup_opportunity:blocked:zero-recovery:v1',
      input: Object.freeze({
        segments: segmentCleanupGoldenSegments(),
        marginFloorPct: 8,
        recoveryFactorPct: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'recoveryFactorPct must be greater than zero and at most 100',
      }),
    }),
  ]),
  execute: executeSegmentCleanupOpportunity,
});

const SEGMENT_CLEANUP_POSTURE_IMPLEMENTATION_DIGEST_INPUTS =
  implementationDigestInputs([
    ['parseSegmentRows', parseSegmentRows],
    ['executeSegmentCleanupPosture', executeSegmentCleanupPosture],
  ]);

const segmentCleanupPostureDefinition: ApprovedFormulaDefinition<
  SegmentCleanupPostureInput,
  SegmentCleanupPostureOutput
> = Object.freeze({
  formulaId: 'segment_cleanup_opportunity',
  version: '1.1.0',
  displayName: 'Segment cleanup posture opportunity',
  description:
    'Sizes conservative, approved, and full-gap segment cleanup depths while reconciling the approved-depth baseline receipt.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'segment_cleanup_posture_input_v1_1',
  outputSchemaVersion: 'segment_cleanup_posture_output_v1_1',
  expectedContextKind: SEGMENT_MARGIN_CLEANUP_CONTEXT_KIND,
  approvedSourceSystemRefs: SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS,
  implementationDigestInputs: SEGMENT_CLEANUP_POSTURE_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'segment_cleanup_opportunity:ok:half-approved:v1_1',
      input: Object.freeze({
        segments: segmentCleanupGoldenSegments(),
        marginFloorPct: 8,
        approvedRecoveryFactorPct: 50,
        cleanupDepthSelector: 'half_approved' as const,
        expectedBaselineCleanupOpportunityUsd: 10000000,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          cleanupDepthSelector: 'half_approved' as const,
          marginFloorPct: 8,
          approvedRecoveryFactorPct: 50,
          scenarioRecoveryFactorPct: 25,
          baselineCleanupOpportunityUsd: 10000000,
          baselineCleanupOpportunityVarianceUsd: 0,
          scenarioCleanupOpportunityUsd: 5000000,
          cleanupOpportunityDeltaUsd: -5000000,
          reviewedNetRevenueUsd: 1500000000,
          breachingSegmentIds: Object.freeze(['segment_a', 'segment_b']),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'segment_cleanup_opportunity:blocked:selector:v1_1',
      input: Object.freeze({
        segments: segmentCleanupGoldenSegments(),
        marginFloorPct: 8,
        approvedRecoveryFactorPct: 50,
        cleanupDepthSelector: 'unsupported' as SegmentCleanupDepthSelector,
        expectedBaselineCleanupOpportunityUsd: 10000000,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message:
          'cleanupDepthSelector must be half_approved, approved, or full_floor_gap',
      }),
    }),
  ]),
  execute: executeSegmentCleanupPosture,
});

const segmentMarginFloorBreachesDefinition: ApprovedFormulaDefinition<
  SegmentMarginFloorBreachesInput,
  SegmentMarginFloorBreachesOutput
> = Object.freeze({
  formulaId: 'segment_margin_floor_breaches',
  version: '1.0.0',
  displayName: 'Segment margin floor breaches',
  description:
    'Identifies governed segments operating below the approved margin floor for cleanup review.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'segment_margin_floor_breaches_input_v1',
  outputSchemaVersion: 'segment_margin_floor_breaches_output_v1',
  expectedContextKind: SEGMENT_MARGIN_CLEANUP_CONTEXT_KIND,
  approvedSourceSystemRefs: SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS,
  implementationDigestInputs: SEGMENT_MARGIN_CLEANUP_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'segment_margin_floor_breaches:ok:v1',
      input: Object.freeze({
        segments: segmentCleanupGoldenSegments(),
        marginFloorPct: 8,
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          breachCount: 2,
          breachingSegmentIds: Object.freeze(['segment_a', 'segment_b']),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'segment_margin_floor_breaches:blocked:empty:v1',
      input: Object.freeze({ segments: Object.freeze([]), marginFloorPct: 8 }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message:
          'segments must be a non-empty list of unique {segmentId, netRevenueUsd, operatingMarginPct} rows',
      }),
    }),
  ]),
  execute: executeSegmentMarginFloorBreaches,
});

const segmentCleanupWarningDefinition: ApprovedFormulaDefinition<
  SegmentCleanupWarningInput,
  SegmentCleanupWarningOutput
> = Object.freeze({
  formulaId: 'segment_cleanup_warning_level',
  version: '1.0.0',
  displayName: 'Segment cleanup warning level',
  description:
    'Classifies portfolio hygiene from the cleanup opportunity share of reviewed segment revenue using externalized floors.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'segment_cleanup_warning_input_v1',
  outputSchemaVersion: 'segment_cleanup_warning_output_v1',
  expectedContextKind: SEGMENT_MARGIN_CLEANUP_CONTEXT_KIND,
  approvedSourceSystemRefs: SEGMENT_MARGIN_CLEANUP_APPROVED_SOURCE_REFS,
  implementationDigestInputs: SEGMENT_MARGIN_CLEANUP_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'segment_cleanup_warning_level:ok:decision-required:v1',
      input: Object.freeze({
        cleanupOpportunityUsd: 10000000,
        reviewedNetRevenueUsd: 600000000,
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          warningLevel: 'decision_required' as const,
          opportunitySharePct: 1.6667,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'segment_cleanup_warning_level:blocked:zero-revenue:v1',
      input: Object.freeze({
        cleanupOpportunityUsd: 10000000,
        reviewedNetRevenueUsd: 0,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'reviewedNetRevenueUsd must be greater than zero',
      }),
    }),
  ]),
  execute: executeSegmentCleanupWarningLevel,
});

// ---------------------------------------------------------------------------
// Segment margin ATTRIBUTION (#4602 SEGMENT-FIX). The four formulas above size
// the CLEANUP decision — recoverable operating income below an approved floor.
// This one answers the different decision the same evidence already supports:
// where the margin the business earns actually COMES FROM, by channel and by
// segment. It reads the approved margin-fact decomposition and is fail-closed
// against the approved segment P&L: every channel x segment cell must tie, per
// segment, to the approved net revenue and margin at integer-cent precision, or
// the formula refuses rather than publishing a split that restates approved
// segment totals. Channel margin can be negative — a margin-destroying channel
// is a finding, not an error — so only the TOTAL margin must be positive for
// shares to mean anything.
// ---------------------------------------------------------------------------

export type SegmentMarginAttributionCellInput = Readonly<{
  /** Unique identity of the source row (customer x SKU x channel x segment). */
  cellKey: string;
  channelKey: string;
  segmentId: string;
  netRevenueUsd: number;
  marginUsd: number;
}>;

export type SegmentMarginAttributionAnchorInput = Readonly<{
  segmentId: string;
  netRevenueUsd: number;
  operatingMarginPct: number;
}>;

/**
 * A margin-fact row that carries no SKU is a service line, and its identity says
 * so explicitly rather than leaving a blank the key could collide on.
 */
export const SEGMENT_MARGIN_ATTRIBUTION_SERVICE_LINE_SKU_KEY = '__service_line__';

/**
 * THE cell identity rule (#4754 amendment 5, finding 1). One spelling, owned by
 * the formula whose input schema it defines, and read by every party that has to
 * agree on it: the loader that de-duplicates governed rows, the planner that
 * composes the invocation, and the evidence validator that reconciles the
 * submitted cells back to the governed artifact. Three copies of a join rule is
 * three chances for a caller's cell to be judged against the wrong governed row.
 */
export function segmentMarginAttributionCellKey(row: {
  customerId: string;
  skuId?: string | null;
  channelKey: string;
  segmentId: string;
}): string {
  return [
    row.customerId,
    row.skuId ?? SEGMENT_MARGIN_ATTRIBUTION_SERVICE_LINE_SKU_KEY,
    row.channelKey,
    row.segmentId,
  ].join('|');
}

export type SegmentMarginAttributionInput = Readonly<{
  cells: readonly SegmentMarginAttributionCellInput[];
  segmentAnchors: readonly SegmentMarginAttributionAnchorInput[];
  reportingCutAsOf?: string;
}>;

export type SegmentMarginAttributionChannelRow = Readonly<{
  channelKey: string;
  netRevenueUsd: number;
  marginUsd: number;
  marginPct: number;
  shareOfMarginPct: number;
}>;

export type SegmentMarginAttributionSegmentRow = Readonly<{
  segmentId: string;
  netRevenueUsd: number;
  marginUsd: number;
  shareOfMarginPct: number;
}>;

export type SegmentMarginAttributionOutput = Readonly<{
  channels: readonly SegmentMarginAttributionChannelRow[];
  segments: readonly SegmentMarginAttributionSegmentRow[];
  totalNetRevenueUsd: number;
  totalMarginUsd: number;
  totalMarginPct: number;
  marginDestroyingChannelKeys: readonly string[];
  marginDestroyingMarginUsd: number;
}>;

function parseSegmentMarginAttributionCells(
  value: unknown,
): SegmentMarginAttributionCellInput[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const cells: SegmentMarginAttributionCellInput[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const record = asRecord(item);
    if (!record) return null;
    const cellKey =
      typeof record.cellKey === 'string' ? record.cellKey.trim() : '';
    const channelKey =
      typeof record.channelKey === 'string' ? record.channelKey.trim() : '';
    const segmentId =
      typeof record.segmentId === 'string' ? record.segmentId.trim() : '';
    if (!cellKey || !channelKey || !segmentId) return null;
    if (
      !isFiniteNumber(record.netRevenueUsd) ||
      !isFiniteNumber(record.marginUsd) ||
      record.netRevenueUsd <= 0
    ) {
      return null;
    }
    if (seen.has(cellKey)) return null;
    seen.add(cellKey);
    cells.push({
      cellKey,
      channelKey,
      segmentId,
      netRevenueUsd: record.netRevenueUsd,
      marginUsd: record.marginUsd,
    });
  }
  return cells;
}

function parseSegmentMarginAttributionAnchors(
  value: unknown,
): SegmentMarginAttributionAnchorInput[] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const anchors: SegmentMarginAttributionAnchorInput[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const record = asRecord(item);
    if (!record) return null;
    const segmentId =
      typeof record.segmentId === 'string' ? record.segmentId.trim() : '';
    if (!segmentId || seen.has(segmentId)) return null;
    seen.add(segmentId);
    if (
      !isFiniteNumber(record.netRevenueUsd) ||
      !isFiniteNumber(record.operatingMarginPct) ||
      record.netRevenueUsd <= 0
    ) {
      return null;
    }
    anchors.push({
      segmentId,
      netRevenueUsd: record.netRevenueUsd,
      operatingMarginPct: record.operatingMarginPct,
    });
  }
  return anchors;
}

/** Integer cents — the whole reconciliation runs at this precision. */
function segmentMarginAttributionCents(value: number): number {
  return Math.round(value * 100);
}

function executeSegmentMarginAttribution(
  input: SegmentMarginAttributionInput,
): ApprovedFormulaResult<SegmentMarginAttributionOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const cells = parseSegmentMarginAttributionCells(record.cells);
  if (!cells) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'cells must be a non-empty list of unique {cellKey, channelKey, segmentId, netRevenueUsd, marginUsd} rows with positive net revenue',
    };
  }
  const anchors = parseSegmentMarginAttributionAnchors(record.segmentAnchors);
  if (!anchors) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'segmentAnchors must be a non-empty list of unique {segmentId, netRevenueUsd, operatingMarginPct} rows with positive net revenue',
    };
  }

  // FAILURE MESSAGES ARE READER COPY, NOT DEBUG OUTPUT (#4754 amendment 14,
  // finding 2). A blocked result's `message` travels through the module's
  // `formulaRuntimeBlock` into the bridge's answerMarkdown, bypassing the
  // display-label authority the answered path runs every id through. This layer
  // cannot reach approved display labels, so the honest shape is NEUTRAL
  // wording — counts and 1-based positions in the submitted arrays. Raw
  // segmentIds stay in the structured input/output fields, which no reader
  // surface renders.
  type Accumulator = { revenueCents: number; marginCents: number };
  const perSegment = new Map<string, Accumulator>(
    anchors.map((anchor) => [anchor.segmentId, { revenueCents: 0, marginCents: 0 }]),
  );
  const perChannel = new Map<string, Accumulator>();
  const channelOrder: string[] = [];
  for (const [cellIndex, cell] of cells.entries()) {
    const segment = perSegment.get(cell.segmentId);
    if (!segment) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          `cell ${cellIndex + 1} of ${cells.length} names a segment that is not ` +
          `among the ${anchors.length} anchored segments`,
      };
    }
    const revenueCents = segmentMarginAttributionCents(cell.netRevenueUsd);
    const marginCents = segmentMarginAttributionCents(cell.marginUsd);
    segment.revenueCents += revenueCents;
    segment.marginCents += marginCents;
    let channel = perChannel.get(cell.channelKey);
    if (!channel) {
      channel = { revenueCents: 0, marginCents: 0 };
      perChannel.set(cell.channelKey, channel);
      channelOrder.push(cell.channelKey);
    }
    channel.revenueCents += revenueCents;
    channel.marginCents += marginCents;
  }

  let totalRevenueCents = 0;
  let totalMarginCents = 0;
  const segmentRows: Array<{
    segmentId: string;
    revenueCents: number;
    marginCents: number;
  }> = [];
  for (const [anchorIndex, anchor] of anchors.entries()) {
    // Neutral position, not the id: 1-based place in the submitted anchor list,
    // which restates the governed segment rows in their governed order.
    const anchorPosition = `anchored segment ${anchorIndex + 1} of ${anchors.length}`;
    const accumulated = perSegment.get(anchor.segmentId);
    if (!accumulated || accumulated.revenueCents === 0) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: `${anchorPosition} carries no channel rows to attribute`,
      };
    }
    const anchorRevenueCents = segmentMarginAttributionCents(anchor.netRevenueUsd);
    if (accumulated.revenueCents !== anchorRevenueCents) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message:
          `${anchorPosition} channel net revenue does not tie to the approved ` +
          `segment net revenue to the cent`,
      };
    }
    // usd x pct == cents: the same identity the approved segment anchors use.
    const anchorMarginCents = Math.round(
      anchor.netRevenueUsd * anchor.operatingMarginPct,
    );
    if (accumulated.marginCents !== anchorMarginCents) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message:
          `${anchorPosition} channel margin does not tie to the approved ` +
          `segment margin to the cent`,
      };
    }
    totalRevenueCents += accumulated.revenueCents;
    totalMarginCents += accumulated.marginCents;
    segmentRows.push({
      segmentId: anchor.segmentId,
      revenueCents: accumulated.revenueCents,
      marginCents: accumulated.marginCents,
    });
  }
  if (totalMarginCents <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'totalMarginUsd must be greater than zero; a non-positive total leaves every share of margin undefined',
    };
  }

  const channels = channelOrder
    .map((channelKey) => {
      const accumulated = perChannel.get(channelKey) as Accumulator;
      return {
        channelKey,
        netRevenueUsd: roundTo(accumulated.revenueCents / 100, 2),
        marginUsd: roundTo(accumulated.marginCents / 100, 2),
        marginPct: roundTo(
          (accumulated.marginCents / accumulated.revenueCents) * 100,
          4,
        ),
        shareOfMarginPct: roundTo(
          (accumulated.marginCents / totalMarginCents) * 100,
          4,
        ),
      };
    })
    .sort((left, right) =>
      right.marginUsd === left.marginUsd
        ? left.channelKey.localeCompare(right.channelKey)
        : right.marginUsd - left.marginUsd,
    );
  const segments = segmentRows
    .map((row) => ({
      segmentId: row.segmentId,
      netRevenueUsd: roundTo(row.revenueCents / 100, 2),
      marginUsd: roundTo(row.marginCents / 100, 2),
      shareOfMarginPct: roundTo((row.marginCents / totalMarginCents) * 100, 4),
    }))
    .sort((left, right) =>
      right.marginUsd === left.marginUsd
        ? left.segmentId.localeCompare(right.segmentId)
        : right.marginUsd - left.marginUsd,
    );
  const marginDestroying = channels.filter((channel) => channel.marginUsd < 0);

  return {
    status: 'ok',
    output: Object.freeze({
      channels: Object.freeze(channels.map((channel) => Object.freeze(channel))),
      segments: Object.freeze(segments.map((segment) => Object.freeze(segment))),
      totalNetRevenueUsd: roundTo(totalRevenueCents / 100, 2),
      totalMarginUsd: roundTo(totalMarginCents / 100, 2),
      totalMarginPct: roundTo((totalMarginCents / totalRevenueCents) * 100, 4),
      marginDestroyingChannelKeys: Object.freeze(
        marginDestroying.map((channel) => channel.channelKey),
      ),
      marginDestroyingMarginUsd: roundTo(
        marginDestroying.reduce(
          (sum, channel) => sum + segmentMarginAttributionCents(channel.marginUsd),
          0,
        ) / 100,
        2,
      ),
    }),
  };
}

/**
 * The TWO refusals that mean "these authorities describe different cuts", named
 * so a caller can recognise them without matching prose (#4754 amendment 5,
 * finding 3). Only these two may be retold to a reader as "prepared against an
 * earlier cut"; every other reconciliation refusal is a data-integrity problem on
 * the CURRENT cut and must keep its own reason, because telling that reader to
 * refresh the feed would send them to fix something that is not broken.
 *
 * Declared here — above the implementation-digest-input list — because the
 * validator's refusal messages are digested VALUES (#4754 amendment 17) and
 * must exist before that list evaluates.
 */
export const SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISMATCH_MESSAGE =
  'Approved channel margin-fact evidence is not on the same reporting cut as the ' +
  'approved segment margin snapshot.';
export const SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISMATCH_MESSAGE =
  'Formula reporting cut does not reconcile to the approved segment margin snapshot.';
export const SEGMENT_MARGIN_ATTRIBUTION_CUT_MISMATCH_MESSAGES: readonly string[] =
  Object.freeze([
    SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISMATCH_MESSAGE,
    SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISMATCH_MESSAGE,
  ]);

/**
 * An authority that cannot state its own cut is not admitted (#4754 amendment 8,
 * finding 1). These are ADMISSION refusals, not cut mismatches: they carry their
 * own reason codes and are deliberately absent from the mismatch list above, so
 * the emission never retells "evidence has no cut" as "the cuts disagree".
 */
export const SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISSING_MESSAGE =
  'Approved channel margin-fact evidence does not record a valid reporting cut.';
export const SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_CUT_MISSING_MESSAGE =
  'Approved segment margin snapshot evidence does not record a valid reporting cut.';
export const SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISSING_MESSAGE =
  'Segment margin attribution requires the reporting cut the invocation is claimed ' +
  'against.';

/**
 * Governed rows that agree on a channel KEY and disagree on its NAME (#4754
 * amendment 12, finding 2). An admission refusal like the cut ones above, and
 * likewise absent from the cut-mismatch list — it is not a freshness problem and
 * must never be retold as one.
 */
export const SEGMENT_MARGIN_ATTRIBUTION_CHANNEL_LABEL_CONFLICT_MESSAGE =
  'Approved channel margin-fact evidence gives one channel key more than one channel ' +
  'name, so the rows aggregated under it do not describe a single channel.';

// THE WHOLE TRANSITIVE VALIDATION CLOSURE IS SEALED (#4754 amendment 16,
// finding 1). The definition digest hashes the validator body and `execute`
// directly, plus the sources listed here — and NOTHING else. The helpers that
// decide what earns a PASS citation used to be callees only: the governed-row
// parsers, the cell/anchor reconciliation, the cell-key rule, the cut
// normalization and the cents rule could all change without moving the digest,
// so an existing activation stayed valid across a change to what it certifies.
// Every function reachable from `validateSegmentMarginAttributionInputAgainstEvidence`
// or `executeSegmentMarginAttribution` inside this module is therefore listed,
// down to the leaves, plus the admission-set builder the validator's schema
// filters are computed from. The guard-the-guard spec
// (segment-margin-cleanup.margin-attribution.spec.ts) identifier-scans the
// digested sources and fails if a referenced module function is missing here.
//
// AND SO ARE THE VALUES THOSE SOURCES READ (#4754 amendment 17, Codex P1 —
// the a16 residual boundary, closed). A captured source contains only the
// IDENTIFIER of a constant it reads, so the tolerance, the service-line
// sentinel key, the registry-derived evidence-admission sets and the named
// refusal messages could all change value while the stored digest stayed
// valid — an ACTIVE activation kept issuing PASS citations under changed
// reconciliation and admission rules. Each such constant is now appended as a
// `value:` seal (canonical type-tagged serialization — see valueDigestInput).
// Sealing the DERIVED admission sets — the exact sets the validator filters
// on — also closes the registry-data half of the a16 disclosure: an
// admission-changing edit to the source-system registry moves the sealed
// serialization, and with it the definition digest. The companion
// guard-the-guard scan demands a `value:` seal for every SCREAMING_SNAKE
// module constant a digested source references, so a future constant cannot
// slip back to identifier-only hashing.
const SEGMENT_MARGIN_ATTRIBUTION_IMPLEMENTATION_DIGEST_INPUTS = Object.freeze([
  ...implementationDigestInputs([
    ['parseSegmentMarginAttributionCells', parseSegmentMarginAttributionCells],
    [
      'parseSegmentMarginAttributionAnchors',
      parseSegmentMarginAttributionAnchors,
    ],
    ['executeSegmentMarginAttribution', executeSegmentMarginAttribution],
    // Validator closure (#4754 amendment 16, finding 1): evidence parsers and
    // reconciliation.
    [
      'parseSegmentMarginAttributionSnapshotEvidence',
      parseSegmentMarginAttributionSnapshotEvidence,
    ],
    [
      'parseSegmentMarginAttributionMarginFactRows',
      parseSegmentMarginAttributionMarginFactRows,
    ],
    [
      'reconcileSegmentMarginAttributionAnchors',
      reconcileSegmentMarginAttributionAnchors,
    ],
    [
      'reconcileSegmentMarginAttributionCells',
      reconcileSegmentMarginAttributionCells,
    ],
    ['segmentMarginAttributionCellKey', segmentMarginAttributionCellKey],
    ['isoCutDayOrNull', isoCutDayOrNull],
    [
      'segmentMarginAttributionSchemaVersions',
      segmentMarginAttributionSchemaVersions,
    ],
    // Execute closure: the cents rule and rounding.
    ['segmentMarginAttributionCents', segmentMarginAttributionCents],
    ['roundTo', roundTo],
    // Shared leaves both closures rest on.
    ['asRecord', asRecord],
    ['stringOrNull', stringOrNull],
    ['finiteNumberOrNull', finiteNumberOrNull],
    ['sameNumber', sameNumber],
    ['isFiniteNumber', isFiniteNumber],
  ]),
  // Reconciliation VALUES the digested sources read (#4754 amendment 17):
  // the tolerance `sameNumber` compares under, the sentinel SKU key the
  // cell-key rule emits, the registry-derived admission sets the validator
  // filters evidence on, and the named refusal messages it emits.
  valueDigestInput('NUMBER_TOLERANCE', NUMBER_TOLERANCE),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_SERVICE_LINE_SKU_KEY',
    SEGMENT_MARGIN_ATTRIBUTION_SERVICE_LINE_SKU_KEY,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_MARGIN_FACT_SCHEMA_VERSIONS',
    SEGMENT_MARGIN_ATTRIBUTION_MARGIN_FACT_SCHEMA_VERSIONS,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_SCHEMA_VERSIONS',
    SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_SCHEMA_VERSIONS,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISMATCH_MESSAGE',
    SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISMATCH_MESSAGE,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISMATCH_MESSAGE',
    SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISMATCH_MESSAGE,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISSING_MESSAGE',
    SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISSING_MESSAGE,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_CUT_MISSING_MESSAGE',
    SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_CUT_MISSING_MESSAGE,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISSING_MESSAGE',
    SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISSING_MESSAGE,
  ),
  valueDigestInput(
    'SEGMENT_MARGIN_ATTRIBUTION_CHANNEL_LABEL_CONFLICT_MESSAGE',
    SEGMENT_MARGIN_ATTRIBUTION_CHANNEL_LABEL_CONFLICT_MESSAGE,
  ),
]);

// The definition digest canonicalizer rejects shared object references, so each
// golden trace receives its own frozen copies of the cells and anchors.
function segmentMarginAttributionGoldenCells(): readonly SegmentMarginAttributionCellInput[] {
  return Object.freeze([
    Object.freeze({
      cellKey: 'cust-1|sku-1|intermodal|segment_a',
      channelKey: 'intermodal',
      segmentId: 'segment_a',
      netRevenueUsd: 300000000,
      marginUsd: 42000000,
    }),
    Object.freeze({
      cellKey: 'cust-1|sku-2|less_than_truckload|segment_a',
      channelKey: 'less_than_truckload',
      segmentId: 'segment_a',
      netRevenueUsd: 100000000,
      marginUsd: 6000000,
    }),
    Object.freeze({
      cellKey: 'cust-2|sku-1|intermodal|segment_b',
      channelKey: 'intermodal',
      segmentId: 'segment_b',
      netRevenueUsd: 150000000,
      marginUsd: 12000000,
    }),
    Object.freeze({
      cellKey: 'cust-2|service-line|produce_retail|segment_b',
      channelKey: 'produce_retail',
      segmentId: 'segment_b',
      netRevenueUsd: 50000000,
      marginUsd: -4000000,
    }),
  ]);
}

function segmentMarginAttributionGoldenAnchors(): readonly SegmentMarginAttributionAnchorInput[] {
  return Object.freeze([
    Object.freeze({
      segmentId: 'segment_a',
      netRevenueUsd: 400000000,
      operatingMarginPct: 12,
    }),
    Object.freeze({
      segmentId: 'segment_b',
      netRevenueUsd: 200000000,
      operatingMarginPct: 4,
    }),
  ]);
}

const segmentMarginAttributionDefinition: ApprovedFormulaDefinition<
  SegmentMarginAttributionInput,
  SegmentMarginAttributionOutput
> = Object.freeze({
  formulaId: 'segment_margin_attribution',
  version: '1.0.0',
  displayName: 'Segment margin attribution by channel',
  description:
    'Decomposes approved segment margin by channel and by segment, ranking each channel by the margin it contributes and its share of the total, and naming the channels that destroy margin. Refuses unless every per-segment sum ties to the approved segment net revenue and margin to the cent.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'segment_margin_attribution_input_v1',
  outputSchemaVersion: 'segment_margin_attribution_output_v1',
  expectedContextKind: SEGMENT_MARGIN_CLEANUP_MARGIN_FACT_CONTEXT_KIND,
  // The approved segment snapshot is the SECOND authority on this invocation, so
  // its context kind is admitted beside the margin-fact one (#4754 amendment 3).
  additionalExpectedContextKinds: SEGMENT_MARGIN_ATTRIBUTION_ADDITIONAL_CONTEXT_KINDS,
  approvedSourceSystemRefs: SEGMENT_MARGIN_ATTRIBUTION_APPROVED_SOURCE_REFS,
  // Admitting two authorities also admits either one alone, so the invocation is
  // required to present BOTH and to restate the approved snapshot in its anchors
  // (#4754 amendment 4, finding 1). Without this, a modeled-artifact-only call
  // with caller-authored anchors executes and earns a PASS citation.
  validateInputAgainstEvidence: validateSegmentMarginAttributionInputAgainstEvidence,
  implementationDigestInputs: SEGMENT_MARGIN_ATTRIBUTION_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'segment_margin_attribution:ok:channel-mix:v1',
      input: Object.freeze({
        cells: segmentMarginAttributionGoldenCells(),
        segmentAnchors: segmentMarginAttributionGoldenAnchors(),
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          channels: Object.freeze([
            Object.freeze({
              channelKey: 'intermodal',
              netRevenueUsd: 450000000,
              marginUsd: 54000000,
              marginPct: 12,
              shareOfMarginPct: 96.4286,
            }),
            Object.freeze({
              channelKey: 'less_than_truckload',
              netRevenueUsd: 100000000,
              marginUsd: 6000000,
              marginPct: 6,
              shareOfMarginPct: 10.7143,
            }),
            Object.freeze({
              channelKey: 'produce_retail',
              netRevenueUsd: 50000000,
              marginUsd: -4000000,
              marginPct: -8,
              shareOfMarginPct: -7.1429,
            }),
          ]),
          segments: Object.freeze([
            Object.freeze({
              segmentId: 'segment_a',
              netRevenueUsd: 400000000,
              marginUsd: 48000000,
              shareOfMarginPct: 85.7143,
            }),
            Object.freeze({
              segmentId: 'segment_b',
              netRevenueUsd: 200000000,
              marginUsd: 8000000,
              shareOfMarginPct: 14.2857,
            }),
          ]),
          totalNetRevenueUsd: 600000000,
          totalMarginUsd: 56000000,
          totalMarginPct: 9.3333,
          marginDestroyingChannelKeys: Object.freeze(['produce_retail']),
          marginDestroyingMarginUsd: -4000000,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'segment_margin_attribution:blocked:anchor-drift:v1',
      input: Object.freeze({
        cells: Object.freeze([
          Object.freeze({
            cellKey: 'cust-1|sku-1|intermodal|segment_a',
            channelKey: 'intermodal',
            segmentId: 'segment_a',
            netRevenueUsd: 300000000,
            marginUsd: 42000000,
          }),
          Object.freeze({
            cellKey: 'cust-1|sku-2|less_than_truckload|segment_a',
            channelKey: 'less_than_truckload',
            segmentId: 'segment_a',
            netRevenueUsd: 100000000,
            marginUsd: 6000000.01,
          }),
        ]),
        segmentAnchors: Object.freeze([
          Object.freeze({
            segmentId: 'segment_a',
            netRevenueUsd: 400000000,
            operatingMarginPct: 12,
          }),
        ]),
        reportingCutAsOf: '2026-05-31',
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'formula_reconciliation_failure' as const,
        // Neutral position wording (#4754 amendment 14, finding 2): a blocked
        // message reaches readers through answerMarkdown, so it never carries a
        // raw segmentId.
        message:
          'anchored segment 1 of 1 channel margin does not tie to the approved ' +
          'segment margin to the cent',
      }),
    }),
  ]),
  execute: executeSegmentMarginAttribution,
});

// ---------------------------------------------------------------------------
// Finance trend formulas (canonical layer; multi-period public-filing series)
// ---------------------------------------------------------------------------

export type FinanceYoyDeltaInput = Readonly<{
  metricKey: string;
  priorValue: number;
  currentValue: number;
  reportingCutAsOf?: string;
}>;

export type FinanceYoyDeltaOutput = Readonly<{
  deltaUsd: number;
  deltaPct: number;
}>;

function executeFinanceYoyDelta(
  input: FinanceYoyDeltaInput,
): ApprovedFormulaResult<FinanceYoyDeltaOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  if (!isFiniteNumber(record.priorValue) || !isFiniteNumber(record.currentValue)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'priorValue and currentValue are required',
    };
  }
  if (record.priorValue === 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'priorValue must be non-zero for a year-over-year percentage',
    };
  }
  const deltaUsd = roundTo(record.currentValue - record.priorValue, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      deltaUsd,
      deltaPct: roundTo((deltaUsd / Math.abs(record.priorValue)) * 100, 4),
    }),
  };
}

export type FinanceTrendPointInput = Readonly<{
  fiscalYear: number;
  value: number;
}>;

export type FinanceTrendInput = Readonly<{
  metricKey: string;
  points: readonly FinanceTrendPointInput[];
  reportingCutAsOf?: string;
}>;

export type FinanceTrendOutput = Readonly<{
  startFiscalYear: number;
  endFiscalYear: number;
  startValue: number;
  endValue: number;
  totalDeltaUsd: number;
  totalDeltaPct: number;
  direction: 'up' | 'down' | 'flat' | 'mixed';
  troughFiscalYear: number;
  troughValue: number;
  peakFiscalYear: number;
  peakValue: number;
}>;

function executeFinanceTrend(
  input: FinanceTrendInput,
): ApprovedFormulaResult<FinanceTrendOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const raw = Array.isArray(record.points) ? record.points : null;
  if (!raw || raw.length < 2) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'points must contain at least two fiscal-year values',
    };
  }
  const points: Array<{ fiscalYear: number; value: number }> = [];
  const seen = new Set<number>();
  for (const item of raw) {
    const point = asRecord(item);
    if (
      !point ||
      !isFiniteNumber(point.fiscalYear) ||
      !isFiniteNumber(point.value) ||
      seen.has(point.fiscalYear)
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'points must be unique {fiscalYear, value} pairs',
      };
    }
    seen.add(point.fiscalYear);
    points.push({ fiscalYear: point.fiscalYear, value: point.value });
  }
  points.sort((a, b) => a.fiscalYear - b.fiscalYear);
  const start = points[0];
  const end = points[points.length - 1];
  if (start.value === 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'start value must be non-zero for a trend percentage',
    };
  }
  let trough = points[0];
  let peak = points[0];
  let ups = 0;
  let downs = 0;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (current.value > previous.value) ups += 1;
    if (current.value < previous.value) downs += 1;
    if (current.value < trough.value) trough = current;
    if (current.value > peak.value) peak = current;
  }
  const totalDeltaUsd = roundTo(end.value - start.value, 2);
  const direction =
    ups > 0 && downs > 0
      ? 'mixed'
      : ups > 0
        ? 'up'
        : downs > 0
          ? 'down'
          : 'flat';
  return {
    status: 'ok',
    output: Object.freeze({
      startFiscalYear: start.fiscalYear,
      endFiscalYear: end.fiscalYear,
      startValue: start.value,
      endValue: end.value,
      totalDeltaUsd,
      totalDeltaPct: roundTo((totalDeltaUsd / Math.abs(start.value)) * 100, 4),
      direction,
      troughFiscalYear: trough.fiscalYear,
      troughValue: trough.value,
      peakFiscalYear: peak.fiscalYear,
      peakValue: peak.value,
    }),
  };
}

export type FinanceCagrInput = Readonly<{
  metricKey: string;
  startValue: number;
  endValue: number;
  years: number;
  reportingCutAsOf?: string;
}>;

export type FinanceCagrOutput = Readonly<{
  cagrPct: number;
}>;

function executeFinanceCagr(
  input: FinanceCagrInput,
): ApprovedFormulaResult<FinanceCagrOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  if (
    !isFiniteNumber(record.startValue) ||
    !isFiniteNumber(record.endValue) ||
    !isFiniteNumber(record.years)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'startValue, endValue, and years are required',
    };
  }
  if (record.startValue <= 0 || record.endValue <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'CAGR requires positive start and end values',
    };
  }
  if (!Number.isInteger(record.years) || record.years <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'years must be a positive integer',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      cagrPct: roundTo(
        (Math.pow(record.endValue / record.startValue, 1 / record.years) - 1) * 100,
        4,
      ),
    }),
  };
}

export type FinanceMomentumProjectionInput = Readonly<{
  metricKey: string;
  points: readonly FinanceTrendPointInput[];
  horizonYears: number;
  reportingCutAsOf?: string;
}>;

export type FinanceMomentumProjectionPointOutput = Readonly<{
  fiscalYear: number;
  value: number;
  valueType: 'model_derived';
  formulaCitation: 'approved-formula:finance_momentum_projection@1.0.0';
}>;

export type FinanceMomentumProjectionOutput = Readonly<{
  metricKey: string;
  method: 'cagr';
  methodDescription: string;
  baseFiscalYear: number;
  baseValue: number;
  horizonYears: number;
  annualGrowthPct: number;
  points: readonly FinanceMomentumProjectionPointOutput[];
}>;

const FINANCE_MOMENTUM_PROJECTION_CITATION =
  'approved-formula:finance_momentum_projection@1.0.0';

function executeFinanceMomentumProjection(
  input: FinanceMomentumProjectionInput,
): ApprovedFormulaResult<FinanceMomentumProjectionOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const raw = Array.isArray(record.points) ? record.points : null;
  if (!raw || raw.length < 3) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'points must contain at least three fiscal-year values',
    };
  }
  if (!isFiniteNumber(record.horizonYears)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'horizonYears is required',
    };
  }
  if (!Number.isInteger(record.horizonYears) || record.horizonYears <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'horizonYears must be a positive integer',
    };
  }

  const points: Array<{ fiscalYear: number; value: number }> = [];
  const seen = new Set<number>();
  for (const item of raw) {
    const point = asRecord(item);
    if (
      !point ||
      !isFiniteNumber(point.fiscalYear) ||
      !Number.isInteger(point.fiscalYear) ||
      !isFiniteNumber(point.value) ||
      seen.has(point.fiscalYear)
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'points must be unique {fiscalYear, value} pairs',
      };
    }
    seen.add(point.fiscalYear);
    points.push({ fiscalYear: point.fiscalYear, value: point.value });
  }
  points.sort((a, b) => a.fiscalYear - b.fiscalYear);
  const start = points[0];
  const end = points[points.length - 1];
  const yearSpan = end.fiscalYear - start.fiscalYear;
  if (yearSpan <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'points must span at least one fiscal year',
    };
  }
  if (start.value <= 0 || end.value <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'momentum projection requires positive start and end values',
    };
  }

  const annualGrowthRate = Math.pow(end.value / start.value, 1 / yearSpan) - 1;
  const projectionPoints = Object.freeze(
    Array.from({ length: record.horizonYears }, (_, index) =>
      Object.freeze({
        fiscalYear: end.fiscalYear + index + 1,
        value: roundTo(end.value * Math.pow(1 + annualGrowthRate, index + 1), 2),
        valueType: 'model_derived' as const,
        formulaCitation: FINANCE_MOMENTUM_PROJECTION_CITATION,
      }),
    ),
  );

  return {
    status: 'ok',
    output: Object.freeze({
      metricKey: typeof record.metricKey === 'string' ? record.metricKey : '',
      method: 'cagr',
      methodDescription:
        'CAGR over governed annual FinanceMetricTrend history; each future fiscal year is projected from the latest observed value without strategy ambition or lever effects.',
      baseFiscalYear: end.fiscalYear,
      baseValue: end.value,
      horizonYears: record.horizonYears,
      annualGrowthPct: roundTo(annualGrowthRate * 100, 4),
      points: projectionPoints,
    }),
  };
}

const FINANCE_TREND_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeFinanceYoyDelta', executeFinanceYoyDelta],
  ['executeFinanceTrend', executeFinanceTrend],
  ['executeFinanceCagr', executeFinanceCagr],
  ['executeFinanceMomentumProjection', executeFinanceMomentumProjection],
]);

const financeYoyDeltaDefinition: ApprovedFormulaDefinition<
  FinanceYoyDeltaInput,
  FinanceYoyDeltaOutput
> = Object.freeze({
  formulaId: 'finance_yoy_delta',
  version: '1.0.0',
  displayName: 'Finance year-over-year delta',
  description:
    'Computes the year-over-year dollar and percentage change of a governed finance line.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_yoy_delta_input_v1',
  outputSchemaVersion: 'finance_yoy_delta_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_TREND_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_yoy_delta:ok:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        priorValue: 669141000,
        currentValue: 794961000,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ deltaUsd: 125820000, deltaPct: 18.8032 }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_yoy_delta:blocked:zero-prior:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        priorValue: 0,
        currentValue: 794961000,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'priorValue must be non-zero for a year-over-year percentage',
      }),
    }),
  ]),
  execute: executeFinanceYoyDelta,
});

const financeTrendDefinition: ApprovedFormulaDefinition<
  FinanceTrendInput,
  FinanceTrendOutput
> = Object.freeze({
  formulaId: 'finance_trend',
  version: '1.0.0',
  displayName: 'Finance multi-period trend',
  description:
    'Summarizes a governed finance line across fiscal years: direction, total change, trough, and peak.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_trend_input_v1',
  outputSchemaVersion: 'finance_trend_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_TREND_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_trend:ok:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        points: Object.freeze([
          Object.freeze({ fiscalYear: 2021, value: 1082108000 }),
          Object.freeze({ fiscalYear: 2022, value: 1266782000 }),
          Object.freeze({ fiscalYear: 2023, value: 514607000 }),
          Object.freeze({ fiscalYear: 2024, value: 669141000 }),
          Object.freeze({ fiscalYear: 2025, value: 794961000 }),
        ]),
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          startFiscalYear: 2021,
          endFiscalYear: 2025,
          startValue: 1082108000,
          endValue: 794961000,
          totalDeltaUsd: -287147000,
          totalDeltaPct: -26.5359,
          direction: 'mixed' as const,
          troughFiscalYear: 2023,
          troughValue: 514607000,
          peakFiscalYear: 2022,
          peakValue: 1266782000,
        }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_trend:blocked:single-point:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        points: Object.freeze([Object.freeze({ fiscalYear: 2025, value: 794961000 })]),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'points must contain at least two fiscal-year values',
      }),
    }),
  ]),
  execute: executeFinanceTrend,
});

const financeCagrDefinition: ApprovedFormulaDefinition<
  FinanceCagrInput,
  FinanceCagrOutput
> = Object.freeze({
  formulaId: 'finance_cagr',
  version: '1.0.0',
  displayName: 'Finance compound annual growth rate',
  description:
    'Computes the compound annual growth rate of a governed finance line between two fiscal years.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_cagr_input_v1',
  outputSchemaVersion: 'finance_cagr_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_TREND_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_cagr:ok:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        startValue: 514607000,
        endValue: 794961000,
        years: 2,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({ cagrPct: 24.2897 }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_cagr:blocked:negative:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        startValue: -1,
        endValue: 794961000,
        years: 2,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'invalid_formula_input' as const,
        message: 'CAGR requires positive start and end values',
      }),
    }),
  ]),
  execute: executeFinanceCagr,
});

const financeMomentumProjectionDefinition: ApprovedFormulaDefinition<
  FinanceMomentumProjectionInput,
  FinanceMomentumProjectionOutput
> = Object.freeze({
  formulaId: 'finance_momentum_projection',
  version: '1.0.0',
  displayName: 'Finance momentum projection',
  description:
    'Projects governed finance metric momentum from multi-year public-filing history using CAGR, with every projected point labeled model_derived.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'finance_momentum_projection_input_v1',
  outputSchemaVersion: 'finance_momentum_projection_output_v1',
  expectedContextKind: FINANCE_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: FINANCE_TREND_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'finance_momentum_projection:ok:three-year-cagr:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        points: Object.freeze([
          Object.freeze({ fiscalYear: 2023, value: 514607000 }),
          Object.freeze({ fiscalYear: 2024, value: 669141000 }),
          Object.freeze({ fiscalYear: 2025, value: 794961000 }),
        ]),
        horizonYears: 3,
        reportingCutAsOf: '2025-12-31',
      }),
      expected: Object.freeze({
        status: 'ok' as const,
        output: Object.freeze({
          metricKey: 'income_from_operations_usd',
          method: 'cagr' as const,
          methodDescription:
            'CAGR over governed annual FinanceMetricTrend history; each future fiscal year is projected from the latest observed value without strategy ambition or lever effects.',
          baseFiscalYear: 2025,
          baseValue: 794961000,
          horizonYears: 3,
          annualGrowthPct: 24.2897,
          points: Object.freeze([
            Object.freeze({
              fiscalYear: 2026,
              value: 988054476.11,
              valueType: 'model_derived' as const,
              formulaCitation: FINANCE_MOMENTUM_PROJECTION_CITATION,
            }),
            Object.freeze({
              fiscalYear: 2027,
              value: 1228049738,
              valueType: 'model_derived' as const,
              formulaCitation: FINANCE_MOMENTUM_PROJECTION_CITATION,
            }),
            Object.freeze({
              fiscalYear: 2028,
              value: 1526339078.92,
              valueType: 'model_derived' as const,
              formulaCitation: FINANCE_MOMENTUM_PROJECTION_CITATION,
            }),
          ]),
        }),
      }),
    }),
    Object.freeze({
      traceId: 'finance_momentum_projection:blocked:two-point-history:v1',
      input: Object.freeze({
        metricKey: 'income_from_operations_usd',
        points: Object.freeze([
          Object.freeze({ fiscalYear: 2024, value: 669141000 }),
          Object.freeze({ fiscalYear: 2025, value: 794961000 }),
        ]),
        horizonYears: 3,
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'points must contain at least three fiscal-year values',
      }),
    }),
  ]),
  execute: executeFinanceMomentumProjection,
});

// ---------------------------------------------------------------------------
// Long-range planning — driver-coherent P&L projection formula.
// ---------------------------------------------------------------------------
const LRP_CONTEXT_KIND = 'decision_agent:long_range_plan';
const LRP_MOMENTUM_PROJECTION_CITATION =
  'approved-formula:lrp_momentum_projection_v1@1.0.0';

export type LrpMomentumProjectionActualInput = Readonly<{
  fiscalYear: number;
  grossRevenueUsd: number;
  netRevenueUsd: number;
  operatingIncomeUsd: number;
  asOf?: string;
}>;

export type LrpMomentumProjectionScenarioAssumptionInput = Readonly<{
  scenarioId: string;
  label: string;
  assumptionSetId: string;
  grossRevenueCagrAdjustmentPct: number;
  netRevenueShareTrendMultiplier: number;
  netRevenueShareTerminalPct: number;
  netOperatingMarginTrendMultiplier: number;
  netOperatingMarginDampingFactor: number;
  netOperatingMarginTerminalPct: number;
}>;

export type LrpMomentumProjectionInput = Readonly<{
  actuals: readonly LrpMomentumProjectionActualInput[];
  horizonStartFiscalYear: number;
  horizonEndFiscalYear: number;
  scenarios: readonly LrpMomentumProjectionScenarioAssumptionInput[];
  targetOperatingIncomeUsd?: number;
  reportingCutAsOf?: string;
}>;

export type LrpMomentumProjectionHistoricalDrivers = Readonly<{
  grossRevenueCagrPct: number;
  netRevenueShareStartPct: number;
  netRevenueShareEndPct: number;
  netRevenueShareAnnualDeltaPct: number;
  netOperatingMarginStartPct: number;
  netOperatingMarginEndPct: number;
  netOperatingMarginAnnualDeltaPct: number;
}>;

export type LrpMomentumProjectionPointOutput = Readonly<{
  fiscalYear: number;
  grossRevenueUsd: number;
  netRevenueUsd: number;
  operatingIncomeUsd: number;
  netRevenueSharePct: number;
  netOperatingMarginPct: number;
  operatingIncomeMarginPct: number;
  valueType: 'model_derived';
  formulaCitation: typeof LRP_MOMENTUM_PROJECTION_CITATION;
  assumptionSetId: string;
  assumptionSet: LrpMomentumProjectionScenarioAssumptionInput;
}>;

export type LrpMomentumProjectionTargetComparison = Readonly<{
  targetOperatingIncomeUsd: number;
  targetReachedFiscalYear: number | null;
  finalFiscalYear: number;
  finalOperatingIncomeUsd: number;
  remainingGapUsd: number;
  overshootUsd: number;
  regime: 'gap_exists' | 'momentum_reaches_target';
}>;

export type LrpMomentumProjectionScenarioOutput = Readonly<{
  scenarioId: string;
  label: string;
  assumptionSetId: string;
  assumptionSet: LrpMomentumProjectionScenarioAssumptionInput;
  points: readonly LrpMomentumProjectionPointOutput[];
  targetComparison: LrpMomentumProjectionTargetComparison | null;
}>;

export type LrpMomentumProjectionDriverParameterKey = Exclude<
  keyof LrpMomentumProjectionScenarioAssumptionInput,
  'scenarioId' | 'label' | 'assumptionSetId'
>;

export type LrpMomentumProjectionAssumptionLedgerEntry = Readonly<{
  scenarioId: string;
  scenarioLabel: string;
  assumptionSetId: string;
  parameterKey: LrpMomentumProjectionDriverParameterKey;
  parameterLabel: string;
  value: number;
  unit: 'percent' | 'multiplier';
  assumptionClass: 'planning_assumption';
}>;

export type LrpMomentumProjectionOutput = Readonly<{
  formulaCitation: typeof LRP_MOMENTUM_PROJECTION_CITATION;
  method: 'driver_coherent_pnl_system';
  methodDescription: string;
  baseFiscalYear: number;
  baseGrossRevenueUsd: number;
  baseNetRevenueUsd: number;
  baseOperatingIncomeUsd: number;
  horizonStartFiscalYear: number;
  horizonEndFiscalYear: number;
  historicalDrivers: LrpMomentumProjectionHistoricalDrivers;
  assumptionsLedger: readonly LrpMomentumProjectionAssumptionLedgerEntry[];
  scenarios: readonly LrpMomentumProjectionScenarioOutput[];
}>;

type NormalizedLrpActual = Readonly<{
  fiscalYear: number;
  grossRevenueUsd: number;
  netRevenueUsd: number;
  operatingIncomeUsd: number;
}>;

type LrpScenarioProjectionContext = Readonly<{
  scenario: LrpMomentumProjectionScenarioAssumptionInput;
  baseActual: NormalizedLrpActual;
  historicalDrivers: LrpMomentumProjectionHistoricalDrivers;
  horizonStartFiscalYear: number;
  horizonEndFiscalYear: number;
  targetOperatingIncomeUsd: number | null;
}>;

const LRP_ASSUMPTION_PARAMETER_LABELS: Readonly<Record<
  LrpMomentumProjectionDriverParameterKey,
  { label: string; unit: 'percent' | 'multiplier' }
>> = Object.freeze({
  grossRevenueCagrAdjustmentPct: {
    label: 'Gross revenue CAGR adjustment',
    unit: 'percent',
  },
  netRevenueShareTrendMultiplier: {
    label: 'Net revenue share trend multiplier',
    unit: 'multiplier',
  },
  netRevenueShareTerminalPct: {
    label: 'Net revenue share terminal bound',
    unit: 'percent',
  },
  netOperatingMarginTrendMultiplier: {
    label: 'Net operating margin trend multiplier',
    unit: 'multiplier',
  },
  netOperatingMarginDampingFactor: {
    label: 'Net operating margin damping factor',
    unit: 'multiplier',
  },
  netOperatingMarginTerminalPct: {
    label: 'Net operating margin terminal bound',
    unit: 'percent',
  },
});

const LRP_PLANNING_ASSUMPTION_KEYS: readonly LrpMomentumProjectionDriverParameterKey[] =
  Object.freeze([
    'grossRevenueCagrAdjustmentPct',
    'netRevenueShareTrendMultiplier',
    'netRevenueShareTerminalPct',
    'netOperatingMarginTrendMultiplier',
    'netOperatingMarginDampingFactor',
    'netOperatingMarginTerminalPct',
  ]);

function executeLrpMomentumProjection(
  input: LrpMomentumProjectionInput,
): ApprovedFormulaResult<LrpMomentumProjectionOutput> {
  const record = asRecord(input);
  if (!record) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }

  const actuals = normalizeLrpActuals(record.actuals);
  if (actuals.status === 'blocked') return actuals;

  if (
    !isFiniteNumber(record.horizonStartFiscalYear) ||
    !Number.isInteger(record.horizonStartFiscalYear) ||
    !isFiniteNumber(record.horizonEndFiscalYear) ||
    !Number.isInteger(record.horizonEndFiscalYear)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'horizonStartFiscalYear and horizonEndFiscalYear are required',
    };
  }
  const horizonStartFiscalYear = Number(record.horizonStartFiscalYear);
  const horizonEndFiscalYear = Number(record.horizonEndFiscalYear);
  if (horizonEndFiscalYear < horizonStartFiscalYear) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'horizonEndFiscalYear must be greater than or equal to horizonStartFiscalYear',
    };
  }

  const scenarios = normalizeLrpScenarios(record.scenarios);
  if (scenarios.status === 'blocked') return scenarios;

  const sortedActuals = actuals.output;
  const first = sortedActuals[0];
  const last = sortedActuals[sortedActuals.length - 1];
  const yearSpan = last.fiscalYear - first.fiscalYear;
  const historicalDrivers = deriveLrpHistoricalDrivers(first, last, yearSpan);
  const targetOperatingIncomeUsd = isFiniteNumber(record.targetOperatingIncomeUsd)
    ? record.targetOperatingIncomeUsd
    : null;

  const scenarioOutputs = Object.freeze(
    scenarios.output.map((scenario) =>
      projectLrpScenario({
        scenario,
        baseActual: last,
        historicalDrivers,
        horizonStartFiscalYear,
        horizonEndFiscalYear,
        targetOperatingIncomeUsd,
      }),
    ),
  );

  return {
    status: 'ok',
    output: Object.freeze({
      formulaCitation: LRP_MOMENTUM_PROJECTION_CITATION,
      method: 'driver_coherent_pnl_system',
      methodDescription:
        'Projects gross revenue momentum, derives net revenue as a bounded share of gross revenue, and derives operating income from net revenue times a damped bounded margin trajectory. Scenario parameters are labeled planning assumptions.',
      baseFiscalYear: last.fiscalYear,
      baseGrossRevenueUsd: roundTo(last.grossRevenueUsd, 2),
      baseNetRevenueUsd: roundTo(last.netRevenueUsd, 2),
      baseOperatingIncomeUsd: roundTo(last.operatingIncomeUsd, 2),
      horizonStartFiscalYear,
      horizonEndFiscalYear,
      historicalDrivers,
      assumptionsLedger: buildLrpAssumptionsLedger(scenarios.output),
      scenarios: scenarioOutputs,
    }),
  };
}

function normalizeLrpActuals(value: unknown):
  | ApprovedFormulaOkResult<readonly NormalizedLrpActual[]>
  | ApprovedFormulaBlockedResult {
  const raw = Array.isArray(value) ? value : null;
  if (!raw || raw.length < 3) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'actuals must contain at least three fiscal-year P&L points',
    };
  }

  const points: NormalizedLrpActual[] = [];
  const seen = new Set<number>();
  for (const item of raw) {
    const point = asRecord(item);
    if (
      !point ||
      !isFiniteNumber(point.fiscalYear) ||
      !Number.isInteger(point.fiscalYear) ||
      !isFiniteNumber(point.grossRevenueUsd) ||
      !isFiniteNumber(point.netRevenueUsd) ||
      !isFiniteNumber(point.operatingIncomeUsd) ||
      seen.has(point.fiscalYear)
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'actuals must be unique {fiscalYear, grossRevenueUsd, netRevenueUsd, operatingIncomeUsd} points',
      };
    }
    if (
      point.grossRevenueUsd <= 0 ||
      point.netRevenueUsd <= 0 ||
      point.operatingIncomeUsd <= 0 ||
      point.netRevenueUsd > point.grossRevenueUsd ||
      point.operatingIncomeUsd > point.netRevenueUsd
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'actuals require positive reconciled P&L values with operating income <= net revenue <= gross revenue',
      };
    }
    seen.add(point.fiscalYear);
    points.push(Object.freeze({
      fiscalYear: point.fiscalYear,
      grossRevenueUsd: point.grossRevenueUsd,
      netRevenueUsd: point.netRevenueUsd,
      operatingIncomeUsd: point.operatingIncomeUsd,
    }));
  }
  points.sort((a, b) => a.fiscalYear - b.fiscalYear);
  const yearSpan = points[points.length - 1].fiscalYear - points[0].fiscalYear;
  if (yearSpan < 2) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'actuals must span at least three fiscal years',
    };
  }
  return { status: 'ok', output: Object.freeze(points) };
}

function normalizeLrpScenarios(value: unknown):
  | ApprovedFormulaOkResult<readonly LrpMomentumProjectionScenarioAssumptionInput[]>
  | ApprovedFormulaBlockedResult {
  const raw = Array.isArray(value) ? value : null;
  if (!raw || raw.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'scenarios must contain at least one assumption set',
    };
  }

  const output: LrpMomentumProjectionScenarioAssumptionInput[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const scenario = asRecord(item);
    const scenarioId = stringOrNull(scenario?.scenarioId);
    const label = stringOrNull(scenario?.label);
    const assumptionSetId = stringOrNull(scenario?.assumptionSetId);
    if (!scenario || !scenarioId || !label || !assumptionSetId || seen.has(scenarioId)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'each scenario requires unique scenarioId, label, and assumptionSetId',
      };
    }
    const grossRevenueCagrAdjustmentPct = scenario.grossRevenueCagrAdjustmentPct;
    const netRevenueShareTrendMultiplier = scenario.netRevenueShareTrendMultiplier;
    const netRevenueShareTerminalPct = scenario.netRevenueShareTerminalPct;
    const netOperatingMarginTrendMultiplier = scenario.netOperatingMarginTrendMultiplier;
    const netOperatingMarginDampingFactor = scenario.netOperatingMarginDampingFactor;
    const netOperatingMarginTerminalPct = scenario.netOperatingMarginTerminalPct;
    const numericFields = {
      grossRevenueCagrAdjustmentPct,
      netRevenueShareTrendMultiplier,
      netRevenueShareTerminalPct,
      netOperatingMarginTrendMultiplier,
      netOperatingMarginDampingFactor,
      netOperatingMarginTerminalPct,
    };
    for (const [key, value] of Object.entries(numericFields)) {
      if (!isFiniteNumber(value)) {
        return {
          status: 'blocked',
          reasonCode: 'missing_formula_input',
          message: `scenario ${scenarioId} is missing ${key}`,
        };
      }
    }
    const grossRevenueCagrAdjustmentPctNumber = Number(grossRevenueCagrAdjustmentPct);
    const netRevenueShareTrendMultiplierNumber = Number(netRevenueShareTrendMultiplier);
    const netRevenueShareTerminalPctNumber = Number(netRevenueShareTerminalPct);
    const netOperatingMarginTrendMultiplierNumber = Number(netOperatingMarginTrendMultiplier);
    const netOperatingMarginDampingFactorNumber = Number(netOperatingMarginDampingFactor);
    const netOperatingMarginTerminalPctNumber = Number(netOperatingMarginTerminalPct);
    if (
      netRevenueShareTerminalPctNumber <= 0 ||
      netRevenueShareTerminalPctNumber > 100 ||
      netOperatingMarginTerminalPctNumber <= 0 ||
      netOperatingMarginTerminalPctNumber > 100 ||
      netOperatingMarginDampingFactorNumber < 0 ||
      netOperatingMarginDampingFactorNumber >= 1
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'scenario terminal bounds must be percentages in (0,100] and margin damping must be in [0,1)',
      };
    }
    seen.add(scenarioId);
    output.push(Object.freeze({
      scenarioId,
      label,
      assumptionSetId,
      grossRevenueCagrAdjustmentPct: grossRevenueCagrAdjustmentPctNumber,
      netRevenueShareTrendMultiplier: netRevenueShareTrendMultiplierNumber,
      netRevenueShareTerminalPct: netRevenueShareTerminalPctNumber,
      netOperatingMarginTrendMultiplier: netOperatingMarginTrendMultiplierNumber,
      netOperatingMarginDampingFactor: netOperatingMarginDampingFactorNumber,
      netOperatingMarginTerminalPct: netOperatingMarginTerminalPctNumber,
    }));
  }
  return { status: 'ok', output: Object.freeze(output) };
}

function deriveLrpHistoricalDrivers(
  first: NormalizedLrpActual,
  last: NormalizedLrpActual,
  yearSpan: number,
): LrpMomentumProjectionHistoricalDrivers {
  const grossRevenueCagr = Math.pow(last.grossRevenueUsd / first.grossRevenueUsd, 1 / yearSpan) - 1;
  const netRevenueShareStartPct = (first.netRevenueUsd / first.grossRevenueUsd) * 100;
  const netRevenueShareEndPct = (last.netRevenueUsd / last.grossRevenueUsd) * 100;
  const netOperatingMarginStartPct = (first.operatingIncomeUsd / first.netRevenueUsd) * 100;
  const netOperatingMarginEndPct = (last.operatingIncomeUsd / last.netRevenueUsd) * 100;
  return Object.freeze({
    grossRevenueCagrPct: roundTo(grossRevenueCagr * 100, 4),
    netRevenueShareStartPct: roundTo(netRevenueShareStartPct, 4),
    netRevenueShareEndPct: roundTo(netRevenueShareEndPct, 4),
    netRevenueShareAnnualDeltaPct: roundTo(
      (netRevenueShareEndPct - netRevenueShareStartPct) / yearSpan,
      4,
    ),
    netOperatingMarginStartPct: roundTo(netOperatingMarginStartPct, 4),
    netOperatingMarginEndPct: roundTo(netOperatingMarginEndPct, 4),
    netOperatingMarginAnnualDeltaPct: roundTo(
      (netOperatingMarginEndPct - netOperatingMarginStartPct) / yearSpan,
      4,
    ),
  });
}

function projectLrpScenario(
  context: LrpScenarioProjectionContext,
): LrpMomentumProjectionScenarioOutput {
  const points: LrpMomentumProjectionPointOutput[] = [];
  const grossRevenueGrowthRate =
    (context.historicalDrivers.grossRevenueCagrPct +
      context.scenario.grossRevenueCagrAdjustmentPct) /
    100;
  const latestNetRevenueSharePct = context.historicalDrivers.netRevenueShareEndPct;
  const netRevenueShareDirection = context.historicalDrivers.netRevenueShareAnnualDeltaPct;
  const latestNetOperatingMarginPct = context.historicalDrivers.netOperatingMarginEndPct;
  const netOperatingMarginDirection = context.historicalDrivers.netOperatingMarginAnnualDeltaPct;

  for (
    let fiscalYear = context.horizonStartFiscalYear;
    fiscalYear <= context.horizonEndFiscalYear;
    fiscalYear += 1
  ) {
    const years = fiscalYear - context.baseActual.fiscalYear;
    const grossRevenueUsd = roundTo(
      context.baseActual.grossRevenueUsd * Math.pow(1 + grossRevenueGrowthRate, years),
      2,
    );
    const netRevenueSharePct = roundTo(
      boundTowardTerminalPct(
        latestNetRevenueSharePct +
          netRevenueShareDirection * context.scenario.netRevenueShareTrendMultiplier * years,
        context.scenario.netRevenueShareTerminalPct,
        netRevenueShareDirection,
      ),
      4,
    );
    const netOperatingMarginPct = roundTo(
      boundTowardTerminalPct(
        dampedMarginPct(
          latestNetOperatingMarginPct,
          netOperatingMarginDirection,
          context.scenario.netOperatingMarginTrendMultiplier,
          context.scenario.netOperatingMarginDampingFactor,
          years,
        ),
        context.scenario.netOperatingMarginTerminalPct,
        netOperatingMarginDirection,
      ),
      4,
    );
    const netRevenueUsd = roundTo(grossRevenueUsd * (netRevenueSharePct / 100), 2);
    const operatingIncomeUsd = roundTo(netRevenueUsd * (netOperatingMarginPct / 100), 2);
    points.push(Object.freeze({
      fiscalYear,
      grossRevenueUsd,
      netRevenueUsd,
      operatingIncomeUsd,
      netRevenueSharePct,
      netOperatingMarginPct,
      operatingIncomeMarginPct: roundTo((operatingIncomeUsd / grossRevenueUsd) * 100, 4),
      valueType: 'model_derived',
      formulaCitation: LRP_MOMENTUM_PROJECTION_CITATION,
      assumptionSetId: context.scenario.assumptionSetId,
      assumptionSet: context.scenario,
    }));
  }

  return Object.freeze({
    scenarioId: context.scenario.scenarioId,
    label: context.scenario.label,
    assumptionSetId: context.scenario.assumptionSetId,
    assumptionSet: context.scenario,
    points: Object.freeze(points),
    targetComparison: buildLrpTargetComparison(points, context.targetOperatingIncomeUsd),
  });
}

function dampedMarginPct(
  startingMarginPct: number,
  annualDeltaPct: number,
  trendMultiplier: number,
  dampingFactor: number,
  years: number,
): number {
  let marginPct = startingMarginPct;
  for (let year = 1; year <= years; year += 1) {
    marginPct += annualDeltaPct * trendMultiplier * Math.pow(dampingFactor, year - 1);
  }
  return marginPct;
}

function boundTowardTerminalPct(
  valuePct: number,
  terminalPct: number,
  directionPct: number,
): number {
  if (directionPct > 0) return Math.min(valuePct, terminalPct);
  if (directionPct < 0) return Math.max(valuePct, terminalPct);
  return Math.min(valuePct, terminalPct);
}

function buildLrpTargetComparison(
  points: readonly LrpMomentumProjectionPointOutput[],
  targetOperatingIncomeUsd: number | null,
): LrpMomentumProjectionTargetComparison | null {
  if (!isFiniteNumber(targetOperatingIncomeUsd) || points.length === 0) return null;
  const reached = points.find(
    (point) => point.operatingIncomeUsd >= targetOperatingIncomeUsd,
  );
  const final = points[points.length - 1];
  return Object.freeze({
    targetOperatingIncomeUsd: roundTo(targetOperatingIncomeUsd, 2),
    targetReachedFiscalYear: reached?.fiscalYear ?? null,
    finalFiscalYear: final.fiscalYear,
    finalOperatingIncomeUsd: final.operatingIncomeUsd,
    remainingGapUsd: roundTo(
      Math.max(targetOperatingIncomeUsd - final.operatingIncomeUsd, 0),
      2,
    ),
    overshootUsd: roundTo(Math.max(final.operatingIncomeUsd - targetOperatingIncomeUsd, 0), 2),
    regime: reached ? 'momentum_reaches_target' : 'gap_exists',
  });
}

function buildLrpAssumptionsLedger(
  scenarios: readonly LrpMomentumProjectionScenarioAssumptionInput[],
): readonly LrpMomentumProjectionAssumptionLedgerEntry[] {
  return Object.freeze(
    scenarios.flatMap((scenario) =>
      LRP_PLANNING_ASSUMPTION_KEYS.map((parameterKey) => {
        const meta = LRP_ASSUMPTION_PARAMETER_LABELS[parameterKey];
        return Object.freeze({
          scenarioId: scenario.scenarioId,
          scenarioLabel: scenario.label,
          assumptionSetId: scenario.assumptionSetId,
          parameterKey,
          parameterLabel: meta.label,
          value: scenario[parameterKey],
          unit: meta.unit,
          assumptionClass: 'planning_assumption' as const,
        });
      }),
    ),
  );
}

const LRP_MOMENTUM_PROJECTION_GOLDEN_INPUT: LrpMomentumProjectionInput = Object.freeze({
  actuals: Object.freeze([
    Object.freeze({
      fiscalYear: 2023,
      grossRevenueUsd: 17597000000,
      netRevenueUsd: 2605000000,
      operatingIncomeUsd: 514607000,
      asOf: '2023-12-31',
    }),
    Object.freeze({
      fiscalYear: 2024,
      grossRevenueUsd: 16924000000,
      netRevenueUsd: 2681000000,
      operatingIncomeUsd: 669141000,
      asOf: '2024-12-31',
    }),
    Object.freeze({
      fiscalYear: 2025,
      grossRevenueUsd: 16232763000,
      netRevenueUsd: 2729410000,
      operatingIncomeUsd: 794961000,
      asOf: '2025-12-31',
    }),
  ]),
  horizonStartFiscalYear: 2026,
  horizonEndFiscalYear: 2029,
  targetOperatingIncomeUsd: 1002500000,
  reportingCutAsOf: '2026-07-08',
  scenarios: Object.freeze([
    Object.freeze({
      scenarioId: 'conservative',
      label: 'Conservative',
      assumptionSetId: 'lrp_assumption_conservative_v1',
      grossRevenueCagrAdjustmentPct: -1,
      netRevenueShareTrendMultiplier: 0.5,
      netRevenueShareTerminalPct: 17.25,
      netOperatingMarginTrendMultiplier: 0.35,
      netOperatingMarginDampingFactor: 0.5,
      netOperatingMarginTerminalPct: 32,
    }),
    Object.freeze({
      scenarioId: 'base',
      label: 'Base',
      assumptionSetId: 'lrp_assumption_base_v1',
      grossRevenueCagrAdjustmentPct: 0,
      netRevenueShareTrendMultiplier: 0.75,
      netRevenueShareTerminalPct: 17.75,
      netOperatingMarginTrendMultiplier: 0.45,
      netOperatingMarginDampingFactor: 0.45,
      netOperatingMarginTerminalPct: 34,
    }),
    Object.freeze({
      scenarioId: 'stretch',
      label: 'Stretch',
      assumptionSetId: 'lrp_assumption_stretch_v1',
      grossRevenueCagrAdjustmentPct: 1,
      netRevenueShareTrendMultiplier: 1,
      netRevenueShareTerminalPct: 18.5,
      netOperatingMarginTrendMultiplier: 0.6,
      netOperatingMarginDampingFactor: 0.4,
      netOperatingMarginTerminalPct: 36,
    }),
  ]),
});

const LRP_MOMENTUM_PROJECTION_IMPLEMENTATION_DIGEST_INPUTS = implementationDigestInputs([
  ['executeLrpMomentumProjection', executeLrpMomentumProjection],
  ['projectLrpScenario', projectLrpScenario],
  ['buildLrpAssumptionsLedger', buildLrpAssumptionsLedger],
  ['dampedMarginPct', dampedMarginPct],
  ['boundTowardTerminalPct', boundTowardTerminalPct],
]);

const lrpMomentumProjectionDefinition: ApprovedFormulaDefinition<
  LrpMomentumProjectionInput,
  LrpMomentumProjectionOutput
> = Object.freeze({
  formulaId: 'lrp_momentum_projection_v1',
  version: '1.0.0',
  displayName: 'LRP momentum projection',
  description:
    'Projects a long-range-plan P&L as one driver-coherent system: gross revenue momentum, bounded net-revenue share, and damped bounded net-operating-margin trajectory.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'lrp_momentum_projection_input_v1',
  outputSchemaVersion: 'lrp_momentum_projection_output_v1',
  expectedContextKind: LRP_CONTEXT_KIND,
  approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
  implementationDigestInputs: LRP_MOMENTUM_PROJECTION_IMPLEMENTATION_DIGEST_INPUTS,
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'lrp_momentum_projection_v1:chrw:three-scenario-driver-system:v1',
      input: LRP_MOMENTUM_PROJECTION_GOLDEN_INPUT,
      expected: executeLrpMomentumProjection(LRP_MOMENTUM_PROJECTION_GOLDEN_INPUT),
    }),
    Object.freeze({
      traceId: 'lrp_momentum_projection_v1:blocked:missing-three-year-pnl:v1',
      input: Object.freeze({
        ...LRP_MOMENTUM_PROJECTION_GOLDEN_INPUT,
        actuals: Object.freeze([
          Object.freeze({
            fiscalYear: 2024,
            grossRevenueUsd: 16924000000,
            netRevenueUsd: 2681000000,
            operatingIncomeUsd: 669141000,
          }),
          Object.freeze({
            fiscalYear: 2025,
            grossRevenueUsd: 16232763000,
            netRevenueUsd: 2729410000,
            operatingIncomeUsd: 794961000,
          }),
        ]),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'actuals must contain at least three fiscal-year P&L points',
      }),
    }),
  ]),
  execute: executeLrpMomentumProjection,
});
// ---------------------------------------------------------------------------
// Commercial effectiveness — conversion, productivity, retention, and growth
// formulas. Industry benchmarks and tenant values remain governed evidence;
// these functions own only the canonical arithmetic from charter v2.
// ---------------------------------------------------------------------------
const COMMERCIAL_EFFECTIVENESS_PIPELINE_CONTEXT_KIND =
  'decision_agent:commercial_effectiveness:pipeline_snapshot';
const COMMERCIAL_EFFECTIVENESS_ROSTER_CONTEXT_KIND =
  'decision_agent:commercial_effectiveness:sales_cost_roster';
const COMMERCIAL_EFFECTIVENESS_RETENTION_CONTEXT_KIND =
  'decision_agent:commercial_effectiveness:churn_retention';
const COMMERCIAL_EFFECTIVENESS_ATTACH_CONTEXT_KIND =
  'decision_agent:commercial_effectiveness:account_attach';
const COMMERCIAL_EFFECTIVENESS_SOURCE_REFS = Object.freeze([
  COMMERCIAL_EFFECTIVENESS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);

export type CommercialWinRateInput = Readonly<{
  wonCount: number;
  lostCount: number;
  wonValueUsd: number;
  lostValueUsd: number;
  reportingCutAsOf?: string;
}>;
export type CommercialWinRateOutput = Readonly<{
  countWinRatePct: number;
  valueWinRatePct: number;
  countDenominator: number;
  valueDenominatorUsd: number;
}>;

function executeCommercialWinRate(
  input: CommercialWinRateInput
): ApprovedFormulaResult<CommercialWinRateOutput> {
  const values = [input?.wonCount, input?.lostCount, input?.wonValueUsd, input?.lostValueUsd];
  if (values.some((value) => !isFiniteNumber(value) || value < 0)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'won/lost counts and values must be finite non-negative numbers',
    };
  }
  const countDenominator = input.wonCount + input.lostCount;
  const valueDenominatorUsd = input.wonValueUsd + input.lostValueUsd;
  if (countDenominator <= 0 || valueDenominatorUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'won plus lost denominators must be greater than zero',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      countWinRatePct: roundTo((input.wonCount / countDenominator) * 100, 4),
      valueWinRatePct: roundTo((input.wonValueUsd / valueDenominatorUsd) * 100, 4),
      countDenominator,
      valueDenominatorUsd: roundTo(valueDenominatorUsd, 2),
    }),
  };
}

export type CommercialPipelineCoverageInput = Readonly<{
  openPipelineUsd: number;
  weightedPipelineUsd: number;
  quotaUsd: number;
  closedWonToDateUsd: number;
  winRatePct: number;
  reportingCutAsOf?: string;
}>;
export type CommercialPipelineCoverageOutput = Readonly<{
  quotaGapUsd: number;
  coverageRatio: number | null;
  weightedCoverageRatio: number | null;
  targetCoverageRatio: number | null;
  gapClosed: boolean;
}>;

function executeCommercialPipelineCoverage(
  input: CommercialPipelineCoverageInput
): ApprovedFormulaResult<CommercialPipelineCoverageOutput> {
  const values = [
    input?.openPipelineUsd,
    input?.weightedPipelineUsd,
    input?.quotaUsd,
    input?.closedWonToDateUsd,
    input?.winRatePct,
  ];
  if (values.some((value) => !isFiniteNumber(value) || value < 0) || input.winRatePct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'pipeline, quota, closed-won, and win-rate inputs must be finite and in range',
    };
  }
  const quotaGapUsd = roundTo(input.quotaUsd - input.closedWonToDateUsd, 2);
  if (quotaGapUsd <= 0) {
    return {
      status: 'ok',
      output: Object.freeze({
        quotaGapUsd: 0,
        coverageRatio: null,
        weightedCoverageRatio: null,
        targetCoverageRatio: input.winRatePct > 0 ? roundTo(100 / input.winRatePct, 4) : null,
        gapClosed: true,
      }),
    };
  }
  if (input.winRatePct <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'winRatePct must be greater than zero while a quota gap remains',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      quotaGapUsd,
      coverageRatio: roundTo(input.openPipelineUsd / quotaGapUsd, 4),
      weightedCoverageRatio: roundTo(input.weightedPipelineUsd / quotaGapUsd, 4),
      targetCoverageRatio: roundTo(100 / input.winRatePct, 4),
      gapClosed: false,
    }),
  };
}

export type CommercialSalesProductivityInput = Readonly<{
  netNewRevenueUsd: number;
  rampedSellerCapacity: number;
  loadedSalesCostUsd: number;
  reportingCutAsOf?: string;
}>;
export type CommercialSalesProductivityOutput = Readonly<{
  revenuePerRampedSellerUsd: number;
  salesEfficiencyRatio: number;
}>;

function executeCommercialSalesProductivity(
  input: CommercialSalesProductivityInput
): ApprovedFormulaResult<CommercialSalesProductivityOutput> {
  if (
    !isFiniteNumber(input?.netNewRevenueUsd) ||
    !isFiniteNumber(input?.rampedSellerCapacity) ||
    !isFiniteNumber(input?.loadedSalesCostUsd) ||
    input.netNewRevenueUsd < 0 ||
    input.rampedSellerCapacity <= 0 ||
    input.loadedSalesCostUsd <= 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'productivity requires non-negative revenue and positive ramped capacity and loaded cost',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      revenuePerRampedSellerUsd: roundTo(input.netNewRevenueUsd / input.rampedSellerCapacity, 2),
      salesEfficiencyRatio: roundTo(input.netNewRevenueUsd / input.loadedSalesCostUsd, 4),
    }),
  };
}

export type CommercialFunnelConversionInput = Readonly<{
  enteredStageCount: number;
  enteredNextStageCount: number;
  reportingCutAsOf?: string;
}>;
export type CommercialFunnelConversionOutput = Readonly<{
  stageConversionPct: number;
  falloutCount: number;
}>;

function executeCommercialFunnelConversion(
  input: CommercialFunnelConversionInput
): ApprovedFormulaResult<CommercialFunnelConversionOutput> {
  if (
    !isFiniteNumber(input?.enteredStageCount) ||
    !isFiniteNumber(input?.enteredNextStageCount) ||
    input.enteredStageCount <= 0 ||
    input.enteredNextStageCount < 0 ||
    input.enteredNextStageCount > input.enteredStageCount
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'cohort stage counts require a positive base and a next-stage count within the base',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      stageConversionPct: roundTo((input.enteredNextStageCount / input.enteredStageCount) * 100, 4),
      falloutCount: input.enteredStageCount - input.enteredNextStageCount,
    }),
  };
}

export type CommercialCycleTimeInput = Readonly<{
  stageDurationsDays: readonly number[];
  salesCycleDurationsDays: readonly number[];
  reportingCutAsOf?: string;
}>;
export type CommercialCycleTimeOutput = Readonly<{
  averageDaysInStage: number;
  averageSalesCycleDays: number;
}>;

function executeCommercialCycleTime(
  input: CommercialCycleTimeInput
): ApprovedFormulaResult<CommercialCycleTimeOutput> {
  const stage = Array.isArray(input?.stageDurationsDays) ? input.stageDurationsDays : [];
  const cycle = Array.isArray(input?.salesCycleDurationsDays) ? input.salesCycleDurationsDays : [];
  if (
    stage.length === 0 ||
    cycle.length === 0 ||
    [...stage, ...cycle].some((value) => !isFiniteNumber(value) || value < 0)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'cycle-time formulas require non-empty finite non-negative duration arrays',
    };
  }
  const average = (values: readonly number[]) =>
    roundTo(values.reduce((sum, value) => sum + value, 0) / values.length, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      averageDaysInStage: average(stage),
      averageSalesCycleDays: average(cycle),
    }),
  };
}

export type CommercialRetentionInput = Readonly<{
  beginningRevenueUsd: number;
  expansionRevenueUsd: number;
  contractionRevenueUsd: number;
  churnRevenueUsd: number;
  endingRevenueUsd: number;
  reportingCutAsOf?: string;
}>;
export type CommercialNetRevenueRetentionOutput = Readonly<{
  netRevenueRetentionPct: number;
  endingRevenueUsd: number;
  reconciliationResidualUsd: number;
}>;
export type CommercialGrossRevenueRetentionOutput = Readonly<{
  grossRevenueRetentionPct: number;
  endingRevenueUsd: number;
  reconciliationResidualUsd: number;
}>;

function validateCommercialRetentionInput(
  input: CommercialRetentionInput
): ApprovedFormulaBlockedResult | null {
  const values = [
    input?.beginningRevenueUsd,
    input?.expansionRevenueUsd,
    input?.contractionRevenueUsd,
    input?.churnRevenueUsd,
    input?.endingRevenueUsd,
  ];
  if (values.some((value) => !isFiniteNumber(value) || value < 0)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'retention bridge inputs must be finite non-negative USD values',
    };
  }
  if (input.beginningRevenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'beginningRevenueUsd must be greater than zero',
    };
  }
  const expectedEnding =
    input.beginningRevenueUsd +
    input.expansionRevenueUsd -
    input.contractionRevenueUsd -
    input.churnRevenueUsd;
  if (Math.abs(input.endingRevenueUsd - expectedEnding) > 0.005) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'endingRevenueUsd must reconcile to beginning plus expansion less contraction and churn',
    };
  }
  return null;
}

function executeCommercialNetRevenueRetention(
  input: CommercialRetentionInput
): ApprovedFormulaResult<CommercialNetRevenueRetentionOutput> {
  const block = validateCommercialRetentionInput(input);
  if (block) return block;
  const numerator =
    input.beginningRevenueUsd +
    input.expansionRevenueUsd -
    input.contractionRevenueUsd -
    input.churnRevenueUsd;
  return {
    status: 'ok',
    output: Object.freeze({
      netRevenueRetentionPct: roundTo((numerator / input.beginningRevenueUsd) * 100, 4),
      endingRevenueUsd: roundTo(input.endingRevenueUsd, 2),
      reconciliationResidualUsd: 0,
    }),
  };
}

function executeCommercialGrossRevenueRetention(
  input: CommercialRetentionInput
): ApprovedFormulaResult<CommercialGrossRevenueRetentionOutput> {
  const block = validateCommercialRetentionInput(input);
  if (block) return block;
  const numerator = input.beginningRevenueUsd - input.contractionRevenueUsd - input.churnRevenueUsd;
  return {
    status: 'ok',
    output: Object.freeze({
      grossRevenueRetentionPct: roundTo((numerator / input.beginningRevenueUsd) * 100, 4),
      endingRevenueUsd: roundTo(input.endingRevenueUsd, 2),
      reconciliationResidualUsd: 0,
    }),
  };
}

export type CommercialWhitespaceRow = Readonly<{
  attached: boolean;
  benchmarkAttachAcvUsd: number;
}>;
export type CommercialWhitespaceInput = Readonly<{
  rows: readonly CommercialWhitespaceRow[];
  reportingCutAsOf?: string;
}>;
export type CommercialWhitespaceOutput = Readonly<{
  whitespaceLineCount: number;
  whitespaceUsd: number;
}>;

function executeCommercialWhitespace(
  input: CommercialWhitespaceInput
): ApprovedFormulaResult<CommercialWhitespaceOutput> {
  if (!Array.isArray(input?.rows) || input.rows.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'whitespace requires at least one account service-line row',
    };
  }
  if (
    input.rows.some(
      (row) =>
        typeof row?.attached !== 'boolean' ||
        !isFiniteNumber(row?.benchmarkAttachAcvUsd) ||
        row.benchmarkAttachAcvUsd < 0
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'whitespace rows require attached and non-negative benchmarkAttachAcvUsd',
    };
  }
  const unattached = input.rows.filter((row) => !row.attached);
  return {
    status: 'ok',
    output: Object.freeze({
      whitespaceLineCount: unattached.length,
      whitespaceUsd: roundTo(
        unattached.reduce((sum, row) => sum + row.benchmarkAttachAcvUsd, 0),
        2
      ),
    }),
  };
}

export type CommercialQuotaAttainmentRow = Readonly<{
  repId: string;
  actualBookingsUsd: number;
  quotaUsd: number;
}>;
export type CommercialQuotaAttainmentInput = Readonly<{
  reps: readonly CommercialQuotaAttainmentRow[];
  reportingCutAsOf?: string;
}>;
export type CommercialQuotaAttainmentOutput = Readonly<{
  attainmentByRep: readonly Readonly<{ repId: string; attainmentPct: number }>[];
  pctRepsAtQuota: number;
  attainmentP50Pct: number;
  attainmentP90Pct: number;
}>;

function commercialPercentile(sorted: readonly number[], percentile: number): number {
  const index = (sorted.length - 1) * percentile;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

function executeCommercialQuotaAttainment(
  input: CommercialQuotaAttainmentInput
): ApprovedFormulaResult<CommercialQuotaAttainmentOutput> {
  if (!Array.isArray(input?.reps) || input.reps.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'quota attainment requires at least one seller row',
    };
  }
  if (
    input.reps.some(
      (rep) =>
        !nonEmptyText(rep?.repId) ||
        !isFiniteNumber(rep?.actualBookingsUsd) ||
        rep.actualBookingsUsd < 0 ||
        !isFiniteNumber(rep?.quotaUsd) ||
        rep.quotaUsd <= 0
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'quota attainment rows require repId, non-negative bookings, and positive quota',
    };
  }
  const attainmentByRep = input.reps.map((rep) =>
    Object.freeze({
      repId: rep.repId,
      attainmentPct: roundTo((rep.actualBookingsUsd / rep.quotaUsd) * 100, 4),
    })
  );
  const sorted = attainmentByRep.map((row) => row.attainmentPct).sort((a, b) => a - b);
  return {
    status: 'ok',
    output: Object.freeze({
      attainmentByRep: Object.freeze(attainmentByRep),
      pctRepsAtQuota: roundTo(
        (attainmentByRep.filter((row) => row.attainmentPct >= 100).length /
          attainmentByRep.length) *
          100,
        4
      ),
      attainmentP50Pct: roundTo(commercialPercentile(sorted, 0.5), 4),
      attainmentP90Pct: roundTo(commercialPercentile(sorted, 0.9), 4),
    }),
  };
}

export type CommercialCompPlanRoiRep = Readonly<{
  repId: string;
  totalCompUsd: number;
  bookingsUsd: number;
  quotaUsd: number;
  onTargetEarningsUsd: number;
}>;
export type CommercialCompPlanRoiInput = Readonly<{
  reps: readonly CommercialCompPlanRoiRep[];
  incrementalCompUsd?: number;
  incrementalBookingsUsd?: number;
  reportingCutAsOf?: string;
}>;
export type CommercialCompPlanRoiOutput = Readonly<{
  costOfSalePct: number;
  quotaOteMultiple: number;
  planCostOfGrowthPct: number | null;
}>;

function executeCommercialCompPlanRoi(
  input: CommercialCompPlanRoiInput
): ApprovedFormulaResult<CommercialCompPlanRoiOutput> {
  if (
    !Array.isArray(input?.reps) ||
    input.reps.length === 0 ||
    input.reps.some(
      (rep) =>
        !nonEmptyText(rep?.repId) ||
        !isFiniteNumber(rep?.totalCompUsd) ||
        rep.totalCompUsd < 0 ||
        !isFiniteNumber(rep?.bookingsUsd) ||
        rep.bookingsUsd < 0 ||
        !isFiniteNumber(rep?.quotaUsd) ||
        rep.quotaUsd < 0 ||
        !isFiniteNumber(rep?.onTargetEarningsUsd) ||
        rep.onTargetEarningsUsd <= 0
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'comp-plan ROI requires seller rows with non-negative comp, bookings, and quota plus positive OTE',
    };
  }
  const totalCompUsd = input.reps.reduce((sum, rep) => sum + rep.totalCompUsd, 0);
  const bookingsUsd = input.reps.reduce((sum, rep) => sum + rep.bookingsUsd, 0);
  if (bookingsUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'comp-plan ROI requires positive total bookings',
    };
  }
  const hasGrowthInputs =
    input.incrementalCompUsd !== undefined || input.incrementalBookingsUsd !== undefined;
  const incrementalCompUsd = isFiniteNumber(input.incrementalCompUsd)
    ? input.incrementalCompUsd
    : null;
  const incrementalBookingsUsd = isFiniteNumber(input.incrementalBookingsUsd)
    ? input.incrementalBookingsUsd
    : null;
  if (
    hasGrowthInputs &&
    (incrementalCompUsd === null ||
      incrementalCompUsd < 0 ||
      incrementalBookingsUsd === null ||
      incrementalBookingsUsd <= 0)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'plan cost of growth requires non-negative incremental comp and positive incremental bookings',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      costOfSalePct: roundTo((totalCompUsd / bookingsUsd) * 100, 4),
      quotaOteMultiple: roundTo(
        input.reps.reduce(
          (sum, rep) => sum + rep.quotaUsd / rep.onTargetEarningsUsd,
          0
        ) / input.reps.length,
        4
      ),
      planCostOfGrowthPct:
        hasGrowthInputs && incrementalCompUsd !== null && incrementalBookingsUsd !== null
          ? roundTo((incrementalCompUsd / incrementalBookingsUsd) * 100, 4)
          : null,
    }),
  };
}

export type CommercialWinRateUpliftInput = Readonly<{
  currentWinRatePct: number;
  winRateDeltaPts: number;
  openPipelineOpportunityCount: number;
  averageAcvUsd: number;
  contributionMarginPct: number;
  reportingCutAsOf?: string;
}>;
export type CommercialWinRateUpliftOutput = Readonly<{
  winRateDeltaPts: number;
  winRateUpliftRevenueUsd: number;
  winRateUpliftOperatingIncomeUsd: number;
  leverKind: 'growth';
  flatRevenueEligible: false;
}>;

function executeCommercialWinRateUplift(
  input: CommercialWinRateUpliftInput
): ApprovedFormulaResult<CommercialWinRateUpliftOutput> {
  const values = [
    input?.currentWinRatePct,
    input?.winRateDeltaPts,
    input?.openPipelineOpportunityCount,
    input?.averageAcvUsd,
    input?.contributionMarginPct,
  ];
  if (
    values.some((value) => !isFiniteNumber(value) || value < 0) ||
    input.currentWinRatePct > 100 ||
    input.winRateDeltaPts > 100 ||
    input.currentWinRatePct + input.winRateDeltaPts > 100 ||
    input.contributionMarginPct > 100 ||
    input.winRateDeltaPts <= 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'win-rate uplift requires a positive bounded delta that keeps resulting win rate at or below 100 percent',
    };
  }
  const winRateDeltaPts = roundTo(input.winRateDeltaPts, 4);
  const revenue = roundTo(
    (winRateDeltaPts / 100) * input.openPipelineOpportunityCount * input.averageAcvUsd,
    2
  );
  return {
    status: 'ok',
    output: Object.freeze({
      winRateDeltaPts,
      winRateUpliftRevenueUsd: revenue,
      winRateUpliftOperatingIncomeUsd: roundTo(revenue * (input.contributionMarginPct / 100), 2),
      leverKind: 'growth',
      flatRevenueEligible: false,
    }),
  };
}

export type CommercialCoverageRebalanceInput = Readonly<{
  capacityShift: number;
  sourceAvailableCapacity: number;
  sourceProductivityDensityUsd: number;
  targetProductivityDensityUsd: number;
  reportingCutAsOf?: string;
}>;
export type CommercialCoverageRebalanceOutput = Readonly<{
  capacityShift: number;
  productivityDensityDeltaUsd: number;
  expectedRevenueDeltaUsd: number;
}>;

function executeCommercialCoverageRebalance(
  input: CommercialCoverageRebalanceInput
): ApprovedFormulaResult<CommercialCoverageRebalanceOutput> {
  if (
    !isFiniteNumber(input?.capacityShift) ||
    !isFiniteNumber(input?.sourceAvailableCapacity) ||
    !isFiniteNumber(input?.sourceProductivityDensityUsd) ||
    !isFiniteNumber(input?.targetProductivityDensityUsd) ||
    input.capacityShift <= 0 ||
    input.sourceAvailableCapacity <= 0 ||
    input.capacityShift > input.sourceAvailableCapacity ||
    input.sourceProductivityDensityUsd < 0 ||
    input.targetProductivityDensityUsd < input.sourceProductivityDensityUsd
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'coverage rebalance requires positive capacity within the governed source capacity and a target density at or above source',
    };
  }
  const densityDelta = roundTo(
    input.targetProductivityDensityUsd - input.sourceProductivityDensityUsd,
    2
  );
  return {
    status: 'ok',
    output: Object.freeze({
      capacityShift: input.capacityShift,
      productivityDensityDeltaUsd: densityDelta,
      expectedRevenueDeltaUsd: roundTo(input.capacityShift * densityDelta, 2),
    }),
  };
}

function commercialEffectivenessFormulaDefinition<TInput, TOutput>(spec: {
  formulaId: string;
  displayName: string;
  description: string;
  contextKind: string;
  additionalContextKinds?: readonly string[];
  execute: (input: TInput) => ApprovedFormulaResult<TOutput>;
  digestName: string;
  goldenTraces: readonly ApprovedFormulaGoldenTrace<TInput, TOutput>[];
}): ApprovedFormulaDefinition<TInput, TOutput> {
  const reconciliationRule: ApprovedFormulaReconciliationRule<TInput, TOutput> = Object.freeze({
    kind: 'input_output_evidence_reconciliation',
    ruleId: `${spec.formulaId}_reconciliation_v1`,
    description:
      'Recompute the canonical commercial-effectiveness formula and require exact output agreement before publication.',
    validate(input: Readonly<TInput>, output: Readonly<TOutput>) {
      const recomputed = spec.execute(input as TInput);
      if (recomputed.status === 'blocked') {
        return {
          reasonCode: 'invalid_formula_input',
          message: recomputed.message,
        };
      }
      if (JSON.stringify(recomputed.output) !== JSON.stringify(output)) {
        return {
          reasonCode: 'formula_reconciliation_failure',
          message: `${spec.formulaId} output failed deterministic reconciliation`,
        };
      }
      return null;
    },
  });
  return Object.freeze({
    formulaId: spec.formulaId,
    version: '1.0.0',
    displayName: spec.displayName,
    description: spec.description,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: `${spec.formulaId}_input_v1`,
    outputSchemaVersion: `${spec.formulaId}_output_v1`,
    expectedContextKind: spec.contextKind,
    ...(spec.additionalContextKinds
      ? { additionalExpectedContextKinds: spec.additionalContextKinds }
      : {}),
    approvedSourceSystemRefs: COMMERCIAL_EFFECTIVENESS_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs([[spec.digestName, spec.execute]]),
    goldenTraces: Object.freeze(spec.goldenTraces),
    reconciliationRule,
    execute: spec.execute,
  });
}

const commercialEffectivenessFormulaDefinitions: readonly ApprovedFormulaDefinition[] =
  Object.freeze([
    commercialEffectivenessFormulaDefinition({
      formulaId: 'win_rate_v1',
      displayName: 'Commercial opportunity win rate',
      description:
        'Won divided by won plus lost, on count and value bases; no-decision is excluded.',
      contextKind: COMMERCIAL_EFFECTIVENESS_PIPELINE_CONTEXT_KIND,
      execute: executeCommercialWinRate,
      digestName: 'executeCommercialWinRate',
      goldenTraces: [
        {
          traceId: 'win_rate_v1:count-and-value:v1',
          input: { wonCount: 2, lostCount: 6, wonValueUsd: 400, lostValueUsd: 600 },
          expected: {
            status: 'ok',
            output: {
              countWinRatePct: 25,
              valueWinRatePct: 40,
              countDenominator: 8,
              valueDenominatorUsd: 1000,
            },
          },
        },
        {
          traceId: 'win_rate_v1:blocked:zero-denominator:v1',
          input: { wonCount: 0, lostCount: 0, wonValueUsd: 0, lostValueUsd: 0 },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message: 'won plus lost denominators must be greater than zero',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'pipeline_coverage_v1',
      displayName: 'Pipeline coverage against quota gap',
      description:
        'Open and probability-weighted pipeline divided by the remaining quota gap, with target coverage from win rate.',
      contextKind: COMMERCIAL_EFFECTIVENESS_PIPELINE_CONTEXT_KIND,
      additionalContextKinds: [COMMERCIAL_EFFECTIVENESS_ROSTER_CONTEXT_KIND],
      execute: executeCommercialPipelineCoverage,
      digestName: 'executeCommercialPipelineCoverage',
      goldenTraces: [
        {
          traceId: 'pipeline_coverage_v1:open-gap:v1',
          input: {
            openPipelineUsd: 300,
            weightedPipelineUsd: 100,
            quotaUsd: 150,
            closedWonToDateUsd: 50,
            winRatePct: 25,
          },
          expected: {
            status: 'ok',
            output: {
              quotaGapUsd: 100,
              coverageRatio: 3,
              weightedCoverageRatio: 1,
              targetCoverageRatio: 4,
              gapClosed: false,
            },
          },
        },
        {
          traceId: 'pipeline_coverage_v1:gap-closed:v1',
          input: {
            openPipelineUsd: 0,
            weightedPipelineUsd: 0,
            quotaUsd: 100,
            closedWonToDateUsd: 120,
            winRatePct: 25,
          },
          expected: {
            status: 'ok',
            output: {
              quotaGapUsd: 0,
              coverageRatio: null,
              weightedCoverageRatio: null,
              targetCoverageRatio: 4,
              gapClosed: true,
            },
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'sales_productivity_v1',
      displayName: 'Ramp-adjusted sales productivity',
      description: 'Net-new revenue per ramp-adjusted seller and per loaded sales-cost dollar.',
      contextKind: COMMERCIAL_EFFECTIVENESS_ROSTER_CONTEXT_KIND,
      execute: executeCommercialSalesProductivity,
      digestName: 'executeCommercialSalesProductivity',
      goldenTraces: [
        {
          traceId: 'sales_productivity_v1:ramped:v1',
          input: { netNewRevenueUsd: 1_000, rampedSellerCapacity: 2.5, loadedSalesCostUsd: 500 },
          expected: {
            status: 'ok',
            output: { revenuePerRampedSellerUsd: 400, salesEfficiencyRatio: 2 },
          },
        },
        {
          traceId: 'sales_productivity_v1:blocked:zero-capacity:v1',
          input: { netNewRevenueUsd: 1_000, rampedSellerCapacity: 0, loadedSalesCostUsd: 500 },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message:
              'productivity requires non-negative revenue and positive ramped capacity and loaded cost',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'funnel_conversion_v1',
      displayName: 'Cohort stage conversion',
      description: 'Next-stage cohort entries divided by current-stage cohort entries.',
      contextKind: COMMERCIAL_EFFECTIVENESS_PIPELINE_CONTEXT_KIND,
      execute: executeCommercialFunnelConversion,
      digestName: 'executeCommercialFunnelConversion',
      goldenTraces: [
        {
          traceId: 'funnel_conversion_v1:stage:v1',
          input: { enteredStageCount: 100, enteredNextStageCount: 60 },
          expected: { status: 'ok', output: { stageConversionPct: 60, falloutCount: 40 } },
        },
        {
          traceId: 'funnel_conversion_v1:blocked:zero-base:v1',
          input: { enteredStageCount: 0, enteredNextStageCount: 0 },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message:
              'cohort stage counts require a positive base and a next-stage count within the base',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'cycle_time_v1',
      displayName: 'Commercial cycle time',
      description: 'Average days in stage and average created-to-closed sales-cycle days.',
      contextKind: COMMERCIAL_EFFECTIVENESS_PIPELINE_CONTEXT_KIND,
      execute: executeCommercialCycleTime,
      digestName: 'executeCommercialCycleTime',
      goldenTraces: [
        {
          traceId: 'cycle_time_v1:average:v1',
          input: { stageDurationsDays: [10, 20], salesCycleDurationsDays: [30, 60] },
          expected: { status: 'ok', output: { averageDaysInStage: 15, averageSalesCycleDays: 45 } },
        },
        {
          traceId: 'cycle_time_v1:blocked:empty:v1',
          input: { stageDurationsDays: [], salesCycleDurationsDays: [] },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message: 'cycle-time formulas require non-empty finite non-negative duration arrays',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'net_revenue_retention_v1',
      displayName: 'Net revenue retention',
      description:
        'Beginning revenue plus expansion less contraction and churn, divided by beginning revenue.',
      contextKind: COMMERCIAL_EFFECTIVENESS_RETENTION_CONTEXT_KIND,
      execute: executeCommercialNetRevenueRetention,
      digestName: 'executeCommercialNetRevenueRetention',
      goldenTraces: [
        {
          traceId: 'net_revenue_retention_v1:bridge:v1',
          input: {
            beginningRevenueUsd: 1_000,
            expansionRevenueUsd: 200,
            contractionRevenueUsd: 100,
            churnRevenueUsd: 50,
            endingRevenueUsd: 1_050,
          },
          expected: {
            status: 'ok',
            output: {
              netRevenueRetentionPct: 105,
              endingRevenueUsd: 1_050,
              reconciliationResidualUsd: 0,
            },
          },
        },
        {
          traceId: 'net_revenue_retention_v1:blocked:zero-beginning:v1',
          input: {
            beginningRevenueUsd: 0,
            expansionRevenueUsd: 0,
            contractionRevenueUsd: 0,
            churnRevenueUsd: 0,
            endingRevenueUsd: 0,
          },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message: 'beginningRevenueUsd must be greater than zero',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'gross_revenue_retention_v1',
      displayName: 'Gross revenue retention',
      description:
        'Beginning revenue less contraction and churn, divided by beginning revenue; expansion is excluded.',
      contextKind: COMMERCIAL_EFFECTIVENESS_RETENTION_CONTEXT_KIND,
      execute: executeCommercialGrossRevenueRetention,
      digestName: 'executeCommercialGrossRevenueRetention',
      goldenTraces: [
        {
          traceId: 'gross_revenue_retention_v1:bridge:v1',
          input: {
            beginningRevenueUsd: 1_000,
            expansionRevenueUsd: 200,
            contractionRevenueUsd: 100,
            churnRevenueUsd: 50,
            endingRevenueUsd: 1_050,
          },
          expected: {
            status: 'ok',
            output: {
              grossRevenueRetentionPct: 85,
              endingRevenueUsd: 1_050,
              reconciliationResidualUsd: 0,
            },
          },
        },
        {
          traceId: 'gross_revenue_retention_v1:blocked:residual:v1',
          input: {
            beginningRevenueUsd: 1_000,
            expansionRevenueUsd: 200,
            contractionRevenueUsd: 100,
            churnRevenueUsd: 50,
            endingRevenueUsd: 900,
          },
          expected: {
            status: 'blocked',
            reasonCode: 'formula_reconciliation_failure',
            message:
              'endingRevenueUsd must reconcile to beginning plus expansion less contraction and churn',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'whitespace_v1',
      displayName: 'Account service-line whitespace',
      description: 'Counts unattached service lines and sums their governed benchmark attach ACV.',
      contextKind: COMMERCIAL_EFFECTIVENESS_ATTACH_CONTEXT_KIND,
      execute: executeCommercialWhitespace,
      digestName: 'executeCommercialWhitespace',
      goldenTraces: [
        {
          traceId: 'whitespace_v1:unattached:v1',
          input: {
            rows: [
              { attached: true, benchmarkAttachAcvUsd: 100 },
              { attached: false, benchmarkAttachAcvUsd: 200 },
              { attached: false, benchmarkAttachAcvUsd: 300 },
            ],
          },
          expected: { status: 'ok', output: { whitespaceLineCount: 2, whitespaceUsd: 500 } },
        },
        {
          traceId: 'whitespace_v1:blocked:empty:v1',
          input: { rows: [] },
          expected: {
            status: 'blocked',
            reasonCode: 'missing_formula_input',
            message: 'whitespace requires at least one account service-line row',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'quota_attainment_v1',
      displayName: 'Quota attainment distribution',
      description: 'Rep bookings divided by quota, with share at quota and p50/p90 distribution.',
      contextKind: COMMERCIAL_EFFECTIVENESS_ROSTER_CONTEXT_KIND,
      execute: executeCommercialQuotaAttainment,
      digestName: 'executeCommercialQuotaAttainment',
      goldenTraces: [
        {
          traceId: 'quota_attainment_v1:distribution:v1',
          input: {
            reps: [
              { repId: 'r1', actualBookingsUsd: 50, quotaUsd: 100 },
              { repId: 'r2', actualBookingsUsd: 100, quotaUsd: 100 },
              { repId: 'r3', actualBookingsUsd: 150, quotaUsd: 100 },
            ],
          },
          expected: {
            status: 'ok',
            output: {
              attainmentByRep: [
                { repId: 'r1', attainmentPct: 50 },
                { repId: 'r2', attainmentPct: 100 },
                { repId: 'r3', attainmentPct: 150 },
              ],
              pctRepsAtQuota: 66.6667,
              attainmentP50Pct: 100,
              attainmentP90Pct: 140,
            },
          },
        },
        {
          traceId: 'quota_attainment_v1:blocked:zero-quota:v1',
          input: { reps: [{ repId: 'r1', actualBookingsUsd: 50, quotaUsd: 0 }] },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message:
              'quota attainment rows require repId, non-negative bookings, and positive quota',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'comp_plan_roi_v1',
      displayName: 'Commercial compensation plan ROI',
      description: 'Cost of sale, quota-to-OTE leverage, and incremental plan cost of growth.',
      contextKind: COMMERCIAL_EFFECTIVENESS_ROSTER_CONTEXT_KIND,
      execute: executeCommercialCompPlanRoi,
      digestName: 'executeCommercialCompPlanRoi',
      goldenTraces: [
        {
          traceId: 'comp_plan_roi_v1:plan:v1',
          input: {
            reps: [
              {
                repId: 'r1',
                totalCompUsd: 100,
                bookingsUsd: 1_000,
                quotaUsd: 500,
                onTargetEarningsUsd: 100,
              },
            ],
            incrementalCompUsd: 20,
            incrementalBookingsUsd: 200,
          },
          expected: {
            status: 'ok',
            output: { costOfSalePct: 10, quotaOteMultiple: 5, planCostOfGrowthPct: 10 },
          },
        },
        {
          traceId: 'comp_plan_roi_v1:blocked:zero-bookings:v1',
          input: {
            reps: [
              {
                repId: 'r1',
                totalCompUsd: 100,
                bookingsUsd: 0,
                quotaUsd: 500,
                onTargetEarningsUsd: 100,
              },
            ],
          },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message: 'comp-plan ROI requires positive total bookings',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'winrate_uplift_v1',
      displayName: 'Win-rate growth uplift',
      description:
        'Win-rate delta times open opportunity count times average ACV, with finance-owned contribution margin applied for OI effect.',
      contextKind: COMMERCIAL_EFFECTIVENESS_PIPELINE_CONTEXT_KIND,
      execute: executeCommercialWinRateUplift,
      digestName: 'executeCommercialWinRateUplift',
      goldenTraces: [
        {
          traceId: 'winrate_uplift_v1:growth:v1',
          input: {
            currentWinRatePct: 20,
            winRateDeltaPts: 5,
            openPipelineOpportunityCount: 10,
            averageAcvUsd: 100_000,
            contributionMarginPct: 40,
          },
          expected: {
            status: 'ok',
            output: {
              winRateDeltaPts: 5,
              winRateUpliftRevenueUsd: 50_000,
              winRateUpliftOperatingIncomeUsd: 20_000,
              leverKind: 'growth',
              flatRevenueEligible: false,
            },
          },
        },
        {
          traceId: 'winrate_uplift_v1:blocked:delta-exceeds-bound:v1',
          input: {
            currentWinRatePct: 95,
            winRateDeltaPts: 10,
            openPipelineOpportunityCount: 10,
            averageAcvUsd: 100_000,
            contributionMarginPct: 40,
          },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message:
              'win-rate uplift requires a positive bounded delta that keeps resulting win rate at or below 100 percent',
          },
        },
      ],
    }),
    commercialEffectivenessFormulaDefinition({
      formulaId: 'coverage_rebalance_v1',
      displayName: 'Seller coverage rebalance',
      description:
        'Shifted ramp-adjusted capacity times the productivity-density difference between target and source segments.',
      contextKind: COMMERCIAL_EFFECTIVENESS_ROSTER_CONTEXT_KIND,
      execute: executeCommercialCoverageRebalance,
      digestName: 'executeCommercialCoverageRebalance',
      goldenTraces: [
        {
          traceId: 'coverage_rebalance_v1:density:v1',
          input: {
            capacityShift: 2,
            sourceAvailableCapacity: 3,
            sourceProductivityDensityUsd: 100,
            targetProductivityDensityUsd: 250,
          },
          expected: {
            status: 'ok',
            output: {
              capacityShift: 2,
              productivityDensityDeltaUsd: 150,
              expectedRevenueDeltaUsd: 300,
            },
          },
        },
        {
          traceId: 'coverage_rebalance_v1:blocked:negative-density-delta:v1',
          input: {
            capacityShift: 2,
            sourceAvailableCapacity: 3,
            sourceProductivityDensityUsd: 250,
            targetProductivityDensityUsd: 100,
          },
          expected: {
            status: 'blocked',
            reasonCode: 'invalid_formula_input',
            message:
              'coverage rebalance requires positive capacity within the governed source capacity and a target density at or above source',
          },
        },
      ],
    }),
  ]);

// ---------------------------------------------------------------------------
// Risk & claims (module #16) — cargo-claim and contract-liability formulas.
// Canonical, industry-agnostic math; the industry/jurisdiction facts (liability
// regime, benchmark band, leakage rate) live in risk-claims.profile.ts, never
// here. Every governed risk-claims number flows through one of these formulas.
// ---------------------------------------------------------------------------
const RISK_CLAIMS_CONTEXT_KIND = 'decision_agent:risk_claims:governed_snapshot';
const RISK_CLAIMS_FORMULA_SOURCE_REFS = Object.freeze([
  RISK_CLAIMS_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
]);

function claimsLossRatioStatusBand(lossRatioPct: number): ApprovedFormulaStatusBand {
  if (lossRatioPct <= 1.5) return 'sufficient';
  if (lossRatioPct <= 3) return 'constrained';
  return 'critical';
}

function subrogationRecoveryStatusBand(recoveryPct: number): ApprovedFormulaStatusBand {
  if (recoveryPct >= 50) return 'sufficient';
  if (recoveryPct >= 30) return 'constrained';
  return 'critical';
}

function reserveAdequacyStatusBand(adequacyPct: number): ApprovedFormulaStatusBand {
  if (adequacyPct >= 100) return 'sufficient';
  if (adequacyPct >= 90) return 'constrained';
  return 'critical';
}

function nonEmptyText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export type RiskClaimsLossBasis = 'incurred' | 'paid';

export type RiskClaimsLossRatioInput = Readonly<{
  claimsAmountUsd: number;
  freightRevenueUsd: number;
  basis: RiskClaimsLossBasis;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsLossRatioOutput = Readonly<{
  lossRatioPct: number;
  basis: RiskClaimsLossBasis;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsLossRatio(
  input: RiskClaimsLossRatioInput,
): ApprovedFormulaResult<RiskClaimsLossRatioOutput> {
  if (!isFiniteNumber(input?.claimsAmountUsd) || !isFiniteNumber(input?.freightRevenueUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'claimsAmountUsd and freightRevenueUsd must be finite USD values',
    };
  }
  if (input.basis !== 'incurred' && input.basis !== 'paid') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'basis must be incurred or paid',
    };
  }
  if (input.freightRevenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'freightRevenueUsd must be greater than zero',
    };
  }
  const lossRatioPct = roundTo((input.claimsAmountUsd / input.freightRevenueUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      lossRatioPct,
      basis: input.basis,
      statusBand: claimsLossRatioStatusBand(lossRatioPct),
    }),
  };
}

export type RiskClaimsIncurredClaimsInput = Readonly<{
  claimsPaidUsd: number;
  caseReservesUsd: number;
  ibnrUsd: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsIncurredClaimsOutput = Readonly<{
  incurredClaimsUsd: number;
  claimsPaidUsd: number;
  caseReservesUsd: number;
  ibnrUsd: number;
}>;

function executeRiskClaimsIncurredClaims(
  input: RiskClaimsIncurredClaimsInput,
): ApprovedFormulaResult<RiskClaimsIncurredClaimsOutput> {
  if (
    !isFiniteNumber(input?.claimsPaidUsd) ||
    !isFiniteNumber(input?.caseReservesUsd) ||
    !isFiniteNumber(input?.ibnrUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'claimsPaidUsd, caseReservesUsd, and ibnrUsd must be finite USD values',
    };
  }
  if (input.claimsPaidUsd < 0 || input.caseReservesUsd < 0 || input.ibnrUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'claimsPaidUsd, caseReservesUsd, and ibnrUsd must not be negative',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      incurredClaimsUsd: roundTo(
        input.claimsPaidUsd + input.caseReservesUsd + input.ibnrUsd,
        2,
      ),
      claimsPaidUsd: roundTo(input.claimsPaidUsd, 2),
      caseReservesUsd: roundTo(input.caseReservesUsd, 2),
      ibnrUsd: roundTo(input.ibnrUsd, 2),
    }),
  };
}

export type RiskClaimsLossRatioTrendInput = Readonly<{
  priorLossRatioPct: number;
  currentLossRatioPct: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsLossRatioTrendOutput = Readonly<{
  deltaPts: number;
  deltaPctOfPrior: number;
  direction: 'improving' | 'worsening' | 'flat';
}>;

function executeRiskClaimsLossRatioTrend(
  input: RiskClaimsLossRatioTrendInput,
): ApprovedFormulaResult<RiskClaimsLossRatioTrendOutput> {
  if (
    !isFiniteNumber(input?.priorLossRatioPct) ||
    !isFiniteNumber(input?.currentLossRatioPct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'priorLossRatioPct and currentLossRatioPct must be finite values',
    };
  }
  if (input.priorLossRatioPct <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'priorLossRatioPct must be greater than zero',
    };
  }
  const deltaPts = roundTo(input.currentLossRatioPct - input.priorLossRatioPct, 4);
  const deltaPctOfPrior = roundTo(
    ((input.currentLossRatioPct - input.priorLossRatioPct) / input.priorLossRatioPct) * 100,
    4,
  );
  const direction =
    input.currentLossRatioPct < input.priorLossRatioPct
      ? 'improving'
      : input.currentLossRatioPct > input.priorLossRatioPct
        ? 'worsening'
        : 'flat';
  return {
    status: 'ok',
    output: Object.freeze({ deltaPts, deltaPctOfPrior, direction }),
  };
}

export type RiskClaimsParetoItem = Readonly<{ key: string; valueUsd: number }>;
export type RiskClaimsParetoInput = Readonly<{
  items: readonly RiskClaimsParetoItem[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsParetoRow = Readonly<{
  key: string;
  valueUsd: number;
  sharePct: number;
  cumulativeSharePct: number;
}>;
export type RiskClaimsParetoOutput = Readonly<{
  ranked: readonly RiskClaimsParetoRow[];
  totalUsd: number;
  topKey: string;
  topValueUsd: number;
  paretoKeyCount: number;
}>;

function executeRiskClaimsPareto(
  input: RiskClaimsParetoInput,
): ApprovedFormulaResult<RiskClaimsParetoOutput> {
  if (!Array.isArray(input?.items) || input.items.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'claims pareto requires at least one item',
    };
  }
  for (const item of input.items) {
    if (!nonEmptyText(item?.key) || !isFiniteNumber(item?.valueUsd) || item.valueUsd < 0) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'each pareto item requires a key and a non-negative valueUsd',
      };
    }
  }
  const total = input.items.reduce((sum, item) => sum + item.valueUsd, 0);
  if (total <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'pareto total value must be greater than zero',
    };
  }
  const sorted = [...input.items].sort(
    (a, b) => b.valueUsd - a.valueUsd || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );
  let cumulative = 0;
  let paretoKeyCount = 0;
  let reached = false;
  const ranked = sorted.map((item) => {
    cumulative += item.valueUsd;
    if (!reached) {
      paretoKeyCount += 1;
      if (cumulative / total >= 0.8) reached = true;
    }
    return Object.freeze({
      key: item.key,
      valueUsd: roundTo(item.valueUsd, 2),
      sharePct: roundTo((item.valueUsd / total) * 100, 4),
      cumulativeSharePct: roundTo((cumulative / total) * 100, 4),
    });
  });
  return {
    status: 'ok',
    output: Object.freeze({
      ranked: Object.freeze(ranked),
      totalUsd: roundTo(total, 2),
      topKey: ranked[0].key,
      topValueUsd: ranked[0].valueUsd,
      paretoKeyCount,
    }),
  };
}

export type RiskClaimsAttributionEntity = Readonly<{
  key: string;
  claimsUsd: number;
  freightRevenueUsd: number;
}>;
export type RiskClaimsAttributionInput = Readonly<{
  entities: readonly RiskClaimsAttributionEntity[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsAttributionRow = Readonly<{
  key: string;
  claimsUsd: number;
  freightRevenueUsd: number;
  lossRatioPct: number;
  sharePct: number;
}>;
export type RiskClaimsAttributionOutput = Readonly<{
  ranked: readonly RiskClaimsAttributionRow[];
  totalClaimsUsd: number;
  topKey: string;
  topClaimsUsd: number;
}>;

function executeRiskClaimsAttribution(
  input: RiskClaimsAttributionInput,
): ApprovedFormulaResult<RiskClaimsAttributionOutput> {
  if (!Array.isArray(input?.entities) || input.entities.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'claims attribution requires at least one entity',
    };
  }
  for (const entity of input.entities) {
    if (
      !nonEmptyText(entity?.key) ||
      !isFiniteNumber(entity?.claimsUsd) ||
      entity.claimsUsd < 0 ||
      !isFiniteNumber(entity?.freightRevenueUsd) ||
      entity.freightRevenueUsd < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each attribution entity requires a key, non-negative claimsUsd, and non-negative freightRevenueUsd',
      };
    }
  }
  const totalClaims = input.entities.reduce((sum, entity) => sum + entity.claimsUsd, 0);
  if (totalClaims <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'attribution total claims must be greater than zero',
    };
  }
  const sorted = [...input.entities].sort(
    (a, b) => b.claimsUsd - a.claimsUsd || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );
  const ranked = sorted.map((entity) =>
    Object.freeze({
      key: entity.key,
      claimsUsd: roundTo(entity.claimsUsd, 2),
      freightRevenueUsd: roundTo(entity.freightRevenueUsd, 2),
      lossRatioPct:
        entity.freightRevenueUsd > 0
          ? roundTo((entity.claimsUsd / entity.freightRevenueUsd) * 100, 4)
          : 0,
      sharePct: roundTo((entity.claimsUsd / totalClaims) * 100, 4),
    }),
  );
  return {
    status: 'ok',
    output: Object.freeze({
      ranked: Object.freeze(ranked),
      totalClaimsUsd: roundTo(totalClaims, 2),
      topKey: ranked[0].key,
      topClaimsUsd: ranked[0].claimsUsd,
    }),
  };
}

export type RiskClaimsOpenExposureBand = Readonly<{ bandKey: string; openReserveUsd: number }>;
export type RiskClaimsOpenExposureInput = Readonly<{
  bands: readonly RiskClaimsOpenExposureBand[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsOpenExposureRow = Readonly<{
  bandKey: string;
  openReserveUsd: number;
  sharePct: number;
}>;
export type RiskClaimsOpenExposureOutput = Readonly<{
  totalOpenExposureUsd: number;
  byBand: readonly RiskClaimsOpenExposureRow[];
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsOpenExposure(
  input: RiskClaimsOpenExposureInput,
): ApprovedFormulaResult<RiskClaimsOpenExposureOutput> {
  if (!Array.isArray(input?.bands) || input.bands.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'open exposure requires at least one age band',
    };
  }
  for (const band of input.bands) {
    if (!nonEmptyText(band?.bandKey) || !isFiniteNumber(band?.openReserveUsd) || band.openReserveUsd < 0) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'each open-exposure band requires a bandKey and a non-negative openReserveUsd',
      };
    }
  }
  const total = input.bands.reduce((sum, band) => sum + band.openReserveUsd, 0);
  if (total <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'open-exposure total reserve must be greater than zero',
    };
  }
  const sorted = [...input.bands].sort(
    (a, b) =>
      b.openReserveUsd - a.openReserveUsd ||
      (a.bandKey < b.bandKey ? -1 : a.bandKey > b.bandKey ? 1 : 0),
  );
  const byBand = sorted.map((band) =>
    Object.freeze({
      bandKey: band.bandKey,
      openReserveUsd: roundTo(band.openReserveUsd, 2),
      sharePct: roundTo((band.openReserveUsd / total) * 100, 4),
    }),
  );
  return {
    status: 'ok',
    output: Object.freeze({
      totalOpenExposureUsd: roundTo(total, 2),
      byBand: Object.freeze(byBand),
      statusBand: positiveUsdStatusBand(total),
    }),
  };
}

export type RiskClaimsContract = Readonly<{
  contractId: string;
  hasLiabilityCap: boolean;
  hasIndemnityClause: boolean;
  exposureUsd: number;
}>;
export type RiskClaimsContractLiabilityFlagInput = Readonly<{
  contracts: readonly RiskClaimsContract[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsFlaggedContract = Readonly<{
  contractId: string;
  exposureUsd: number;
  reasons: readonly string[];
}>;
export type RiskClaimsContractLiabilityFlagOutput = Readonly<{
  flagged: readonly RiskClaimsFlaggedContract[];
  uncappedExposureUsd: number;
  flaggedCount: number;
  totalContracts: number;
}>;

function executeRiskClaimsContractLiabilityFlag(
  input: RiskClaimsContractLiabilityFlagInput,
): ApprovedFormulaResult<RiskClaimsContractLiabilityFlagOutput> {
  if (!Array.isArray(input?.contracts) || input.contracts.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'contract liability diagnostic requires at least one contract',
    };
  }
  for (const contract of input.contracts) {
    if (
      !nonEmptyText(contract?.contractId) ||
      typeof contract?.hasLiabilityCap !== 'boolean' ||
      typeof contract?.hasIndemnityClause !== 'boolean' ||
      !isFiniteNumber(contract?.exposureUsd) ||
      contract.exposureUsd < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each contract requires a contractId, cap/indemnity flags, and a non-negative exposureUsd',
      };
    }
  }
  const flaggedRaw = input.contracts
    .filter((contract) => !contract.hasLiabilityCap || !contract.hasIndemnityClause)
    .map((contract) => {
      const reasons: string[] = [];
      if (!contract.hasLiabilityCap) reasons.push('no_liability_cap');
      if (!contract.hasIndemnityClause) reasons.push('indemnity_gap');
      return { contractId: contract.contractId, exposureUsd: contract.exposureUsd, reasons };
    })
    .sort(
      (a, b) =>
        b.exposureUsd - a.exposureUsd ||
        (a.contractId < b.contractId ? -1 : a.contractId > b.contractId ? 1 : 0),
    );
  const uncappedExposureUsd = roundTo(
    input.contracts
      .filter((contract) => !contract.hasLiabilityCap)
      .reduce((sum, contract) => sum + contract.exposureUsd, 0),
    2,
  );
  const flagged = flaggedRaw.map((entry) =>
    Object.freeze({
      contractId: entry.contractId,
      exposureUsd: roundTo(entry.exposureUsd, 2),
      reasons: Object.freeze([...entry.reasons]),
    }),
  );
  return {
    status: 'ok',
    output: Object.freeze({
      flagged: Object.freeze(flagged),
      uncappedExposureUsd,
      flaggedCount: flagged.length,
      totalContracts: input.contracts.length,
    }),
  };
}

export type RiskClaimsSubrogationInput = Readonly<{
  recoveredUsd: number;
  recoverableUsd: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsSubrogationOutput = Readonly<{
  subrogationRecoveryPct: number;
  unrecoveredUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsSubrogationRecovery(
  input: RiskClaimsSubrogationInput,
): ApprovedFormulaResult<RiskClaimsSubrogationOutput> {
  if (!isFiniteNumber(input?.recoveredUsd) || !isFiniteNumber(input?.recoverableUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'recoveredUsd and recoverableUsd must be finite USD values',
    };
  }
  if (input.recoverableUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'recoverableUsd must be greater than zero',
    };
  }
  const subrogationRecoveryPct = roundTo((input.recoveredUsd / input.recoverableUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      subrogationRecoveryPct,
      unrecoveredUsd: roundTo(input.recoverableUsd - input.recoveredUsd, 2),
      statusBand: subrogationRecoveryStatusBand(subrogationRecoveryPct),
    }),
  };
}

export type RiskClaimsProvenanceInput = Readonly<{
  evidenceRefIds: readonly string[];
  sourceClass: string;
  sourceRef: string;
  asOf: string;
  carrierIds: readonly string[];
  laneIds: readonly string[];
  glAccountIds: readonly string[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsProvenanceOutput = Readonly<{
  evidenceRefCount: number;
  sourceClass: string;
  asOf: string;
  carrierScopeCount: number;
  laneScopeCount: number;
  glAccountIds: readonly string[];
  distinctFromCarrierCost: boolean;
}>;

function executeRiskClaimsProvenance(
  input: RiskClaimsProvenanceInput,
): ApprovedFormulaResult<RiskClaimsProvenanceOutput> {
  if (!Array.isArray(input?.evidenceRefIds) || input.evidenceRefIds.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'claims provenance requires at least one evidence reference',
    };
  }
  if (!nonEmptyText(input?.sourceClass) || !nonEmptyText(input?.sourceRef) || !nonEmptyText(input?.asOf)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'claims provenance requires sourceClass, sourceRef, and asOf',
    };
  }
  const carrierIds = Array.isArray(input.carrierIds) ? input.carrierIds : [];
  const laneIds = Array.isArray(input.laneIds) ? input.laneIds : [];
  const glAccountIds = Array.isArray(input.glAccountIds) ? input.glAccountIds : [];
  return {
    status: 'ok',
    output: Object.freeze({
      evidenceRefCount: input.evidenceRefIds.length,
      sourceClass: input.sourceClass,
      asOf: input.asOf,
      carrierScopeCount: carrierIds.length,
      laneScopeCount: laneIds.length,
      glAccountIds: Object.freeze([...glAccountIds]),
      distinctFromCarrierCost: glAccountIds.length > 0,
    }),
  };
}

export type RiskClaimsReductionOpportunityInput = Readonly<{
  currentLossRatioPct: number;
  targetLossRatioPct: number;
  freightRevenueUsd: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsReductionOpportunityOutput = Readonly<{
  reductionOpportunityUsd: number;
  lossRatioReductionPts: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsReductionOpportunity(
  input: RiskClaimsReductionOpportunityInput,
): ApprovedFormulaResult<RiskClaimsReductionOpportunityOutput> {
  if (
    !isFiniteNumber(input?.currentLossRatioPct) ||
    !isFiniteNumber(input?.targetLossRatioPct) ||
    !isFiniteNumber(input?.freightRevenueUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'currentLossRatioPct, targetLossRatioPct, and freightRevenueUsd must be finite values',
    };
  }
  if (input.freightRevenueUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'freightRevenueUsd must be greater than zero',
    };
  }
  if (input.targetLossRatioPct < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'targetLossRatioPct must be zero or greater',
    };
  }
  const reductionPts = Math.max(input.currentLossRatioPct - input.targetLossRatioPct, 0);
  const reductionOpportunityUsd = roundTo((reductionPts / 100) * input.freightRevenueUsd, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      reductionOpportunityUsd,
      lossRatioReductionPts: roundTo(reductionPts, 4),
      statusBand: opportunityStatusBand(reductionOpportunityUsd),
    }),
  };
}

export type RiskClaimsRecoveryOption = Readonly<{
  optionId: string;
  label: string;
  grossRecoveryUsd: number;
  implementationCostUsd: number;
  sharedScopeOverlapUsd: number;
}>;
export type RiskClaimsRecoveryOptionsInput = Readonly<{
  options: readonly RiskClaimsRecoveryOption[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsRecoveryOptionRow = Readonly<{
  optionId: string;
  label: string;
  netRecoveryUsd: number;
}>;
export type RiskClaimsRecoveryOptionsOutput = Readonly<{
  ranked: readonly RiskClaimsRecoveryOptionRow[];
  topOptionId: string;
  topNetRecoveryUsd: number;
  optionCount: number;
}>;

function executeRiskClaimsRecoveryOptions(
  input: RiskClaimsRecoveryOptionsInput,
): ApprovedFormulaResult<RiskClaimsRecoveryOptionsOutput> {
  if (!Array.isArray(input?.options) || input.options.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'recovery options compare requires at least one option',
    };
  }
  for (const option of input.options) {
    if (
      !nonEmptyText(option?.optionId) ||
      !nonEmptyText(option?.label) ||
      !isFiniteNumber(option?.grossRecoveryUsd) ||
      option.grossRecoveryUsd < 0 ||
      !isFiniteNumber(option?.implementationCostUsd) ||
      option.implementationCostUsd < 0 ||
      !isFiniteNumber(option?.sharedScopeOverlapUsd) ||
      option.sharedScopeOverlapUsd < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each recovery option requires ids, label, and non-negative gross/implementation/overlap USD values',
      };
    }
  }
  const ranked = input.options
    .map((option) => ({
      optionId: option.optionId,
      label: option.label,
      netRecoveryUsd: roundTo(
        option.grossRecoveryUsd - option.implementationCostUsd - option.sharedScopeOverlapUsd,
        2,
      ),
    }))
    .sort(
      (a, b) =>
        b.netRecoveryUsd - a.netRecoveryUsd ||
        (a.optionId < b.optionId ? -1 : a.optionId > b.optionId ? 1 : 0),
    )
    .map((row) => Object.freeze(row));
  return {
    status: 'ok',
    output: Object.freeze({
      ranked: Object.freeze(ranked),
      topOptionId: ranked[0].optionId,
      topNetRecoveryUsd: ranked[0].netRecoveryUsd,
      optionCount: ranked.length,
    }),
  };
}

export type RiskClaimsCarrierRequalificationInput = Readonly<{
  removedCarrierClaimsUsd: number;
  rerouteCostUsd: number;
  sharedScopeOverlapUsd: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsCarrierRequalificationOutput = Readonly<{
  netRequalificationValueUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsCarrierRequalification(
  input: RiskClaimsCarrierRequalificationInput,
): ApprovedFormulaResult<RiskClaimsCarrierRequalificationOutput> {
  if (
    !isFiniteNumber(input?.removedCarrierClaimsUsd) ||
    !isFiniteNumber(input?.rerouteCostUsd) ||
    !isFiniteNumber(input?.sharedScopeOverlapUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'removedCarrierClaimsUsd, rerouteCostUsd, and sharedScopeOverlapUsd must be finite USD values',
    };
  }
  const net = roundTo(
    input.removedCarrierClaimsUsd - input.rerouteCostUsd - input.sharedScopeOverlapUsd,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      netRequalificationValueUsd: net,
      statusBand: ebitdaOpportunityStatusBand(net),
    }),
  };
}

export type RiskClaimsLiabilityCapValueInput = Readonly<{
  uncappedExposureUsd: number;
  capAdoptionRatePct: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsLiabilityCapValueOutput = Readonly<{
  liabilityCapValueUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsLiabilityCapValue(
  input: RiskClaimsLiabilityCapValueInput,
): ApprovedFormulaResult<RiskClaimsLiabilityCapValueOutput> {
  if (!isFiniteNumber(input?.uncappedExposureUsd) || !isFiniteNumber(input?.capAdoptionRatePct)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'uncappedExposureUsd and capAdoptionRatePct must be finite values',
    };
  }
  if (input.uncappedExposureUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'uncappedExposureUsd must be zero or greater',
    };
  }
  if (input.capAdoptionRatePct < 0 || input.capAdoptionRatePct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'capAdoptionRatePct must be between 0 and 100',
    };
  }
  const liabilityCapValueUsd = roundTo(
    (input.uncappedExposureUsd * input.capAdoptionRatePct) / 100,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      liabilityCapValueUsd,
      statusBand: opportunityStatusBand(liabilityCapValueUsd),
    }),
  };
}

export type RiskClaimsLeakageInput = Readonly<{
  actualPaidUsd: number;
  bestPracticeRecoverableSharePct: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsLeakageOutput = Readonly<{
  leakageUsd: number;
  shouldPayUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsLeakage(
  input: RiskClaimsLeakageInput,
): ApprovedFormulaResult<RiskClaimsLeakageOutput> {
  if (
    !isFiniteNumber(input?.actualPaidUsd) ||
    !isFiniteNumber(input?.bestPracticeRecoverableSharePct)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'actualPaidUsd and bestPracticeRecoverableSharePct must be finite values',
    };
  }
  if (input.actualPaidUsd < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'actualPaidUsd must be zero or greater',
    };
  }
  if (input.bestPracticeRecoverableSharePct < 0 || input.bestPracticeRecoverableSharePct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'bestPracticeRecoverableSharePct must be between 0 and 100',
    };
  }
  const leakageUsd = roundTo(
    (input.actualPaidUsd * input.bestPracticeRecoverableSharePct) / 100,
    2,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      leakageUsd,
      shouldPayUsd: roundTo(input.actualPaidUsd - leakageUsd, 2),
      statusBand: opportunityStatusBand(leakageUsd),
    }),
  };
}

export type RiskClaimsTcorInput = Readonly<{
  riskTransferUsd: number;
  retainedLossesUsd: number;
  riskControlUsd: number;
  adminUsd: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsTcorOutput = Readonly<{
  totalCostOfRiskUsd: number;
  riskTransferSharePct: number;
  retainedLossesSharePct: number;
  riskControlSharePct: number;
  adminSharePct: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsTcor(
  input: RiskClaimsTcorInput,
): ApprovedFormulaResult<RiskClaimsTcorOutput> {
  if (
    !isFiniteNumber(input?.riskTransferUsd) ||
    !isFiniteNumber(input?.retainedLossesUsd) ||
    !isFiniteNumber(input?.riskControlUsd) ||
    !isFiniteNumber(input?.adminUsd)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'all TCOR components must be finite USD values',
    };
  }
  if (
    input.riskTransferUsd < 0 ||
    input.retainedLossesUsd < 0 ||
    input.riskControlUsd < 0 ||
    input.adminUsd < 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'TCOR components must be zero or greater',
    };
  }
  const total =
    input.riskTransferUsd + input.retainedLossesUsd + input.riskControlUsd + input.adminUsd;
  if (total <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'total cost of risk must be greater than zero',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      totalCostOfRiskUsd: roundTo(total, 2),
      riskTransferSharePct: roundTo((input.riskTransferUsd / total) * 100, 4),
      retainedLossesSharePct: roundTo((input.retainedLossesUsd / total) * 100, 4),
      riskControlSharePct: roundTo((input.riskControlUsd / total) * 100, 4),
      adminSharePct: roundTo((input.adminUsd / total) * 100, 4),
      statusBand: positiveUsdStatusBand(total),
    }),
  };
}

export type RiskClaimsFrequencySeverityInput = Readonly<{
  incurredClaimsUsd: number;
  claimCount: number;
  exposureUnits: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsFrequencySeverityOutput = Readonly<{
  severityUsd: number;
  frequencyPer100kExposure: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsFrequencySeverity(
  input: RiskClaimsFrequencySeverityInput,
): ApprovedFormulaResult<RiskClaimsFrequencySeverityOutput> {
  if (
    !isFiniteNumber(input?.incurredClaimsUsd) ||
    !isFiniteNumber(input?.claimCount) ||
    !isFiniteNumber(input?.exposureUnits)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'incurredClaimsUsd, claimCount, and exposureUnits must be finite values',
    };
  }
  if (input.claimCount <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'claimCount must be greater than zero',
    };
  }
  if (input.exposureUnits <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'exposureUnits must be greater than zero',
    };
  }
  const severityUsd = roundTo(input.incurredClaimsUsd / input.claimCount, 2);
  return {
    status: 'ok',
    output: Object.freeze({
      severityUsd,
      frequencyPer100kExposure: roundTo((input.claimCount / input.exposureUnits) * 100000, 4),
      statusBand: positiveUsdStatusBand(severityUsd),
    }),
  };
}

export type RiskClaimsReserveAdequacyInput = Readonly<{
  heldReservesUsd: number;
  estimatedUltimateUsd: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsReserveAdequacyOutput = Readonly<{
  reserveAdequacyPct: number;
  reserveSurplusDeficitUsd: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsReserveAdequacy(
  input: RiskClaimsReserveAdequacyInput,
): ApprovedFormulaResult<RiskClaimsReserveAdequacyOutput> {
  if (!isFiniteNumber(input?.heldReservesUsd) || !isFiniteNumber(input?.estimatedUltimateUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'heldReservesUsd and estimatedUltimateUsd must be finite USD values',
    };
  }
  if (input.estimatedUltimateUsd <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'estimatedUltimateUsd must be greater than zero',
    };
  }
  const reserveAdequacyPct = roundTo((input.heldReservesUsd / input.estimatedUltimateUsd) * 100, 4);
  return {
    status: 'ok',
    output: Object.freeze({
      reserveAdequacyPct,
      reserveSurplusDeficitUsd: roundTo(input.heldReservesUsd - input.estimatedUltimateUsd, 2),
      statusBand: reserveAdequacyStatusBand(reserveAdequacyPct),
    }),
  };
}

export type RiskClaimsPaidDevelopmentObservation = Readonly<{
  cohortId: string;
  developmentAgeMonths: number;
  valuationAsOf: string;
  cumulativePaidUsd: number;
  cohortStatus: 'open' | 'closed';
  sourceField: 'cumulative_paid_usd';
  sourceObjectId: string;
  evidenceRefId: string;
  proofRefId: string;
}>;
export type RiskClaimsReserveAdequacyV2Input = Readonly<{
  heldReservesUsd: number;
  basis: 'paid';
  observations: readonly RiskClaimsPaidDevelopmentObservation[];
  reportingCutAsOf: string;
}>;
export type RiskClaimsReserveAdequacyV2Output = Readonly<{
  reserveAdequacyPct: number;
  reserveSurplusDeficitUsd: number;
  currentCumulativePaidUsd: number;
  estimatedUltimateUsd: number;
  estimatedOutstandingLiabilityUsd: number;
  weightedPaidDevelopmentFactor: number;
  statusBand: ApprovedFormulaStatusBand;
}>;

function executeRiskClaimsReserveAdequacyV2(
  input: RiskClaimsReserveAdequacyV2Input,
): ApprovedFormulaResult<RiskClaimsReserveAdequacyV2Output> {
  const rawInput =
    input && typeof input === 'object'
      ? (input as unknown as Record<string, unknown>)
      : null;
  if (
    rawInput &&
    ('estimatedUltimateUsd' in rawInput ||
      'estimatedOutstandingLiabilityUsd' in rawInput)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message:
        'no independent ultimate basis: caller-supplied ultimate values cannot satisfy paid-development evidence',
    };
  }
  if (
    !rawInput ||
    !isFiniteNumber(input.heldReservesUsd) ||
    input.heldReservesUsd < 0 ||
    input.basis !== 'paid' ||
    !nonEmptyText(input.reportingCutAsOf) ||
    Number.isNaN(Date.parse(input.reportingCutAsOf)) ||
    !Array.isArray(input.observations)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'reserve_adequacy_v2 requires non-negative held reserves, a reporting cut, and paid cohort development observations',
    };
  }
  if (input.observations.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message:
        'no independent ultimate basis: paid cohort development history is missing',
    };
  }

  const byCohort = new Map<string, RiskClaimsPaidDevelopmentObservation[]>();
  for (const observation of input.observations) {
    const rawObservation =
      observation && typeof observation === 'object'
        ? (observation as unknown as Record<string, unknown>)
        : null;
    if (
      !rawObservation ||
      observation.sourceField !== 'cumulative_paid_usd' ||
      !nonEmptyText(observation.sourceObjectId) ||
      !nonEmptyText(observation.evidenceRefId) ||
      !nonEmptyText(observation.proofRefId) ||
      'caseReservesUsd' in rawObservation ||
      'ibnrUsd' in rawObservation ||
      'openClaimReserveUsd' in rawObservation ||
      'case_reserves_usd' in rawObservation ||
      'ibnr_usd' in rawObservation ||
      'open_claim_reserve_usd' in rawObservation
    ) {
      return {
        status: 'blocked',
        reasonCode: 'missing_governed_evidence',
        message:
          'no independent ultimate basis: each development observation requires a governed cumulative-paid source receipt and cannot carry reserve fields',
      };
    }
    if (
      !nonEmptyText(observation?.cohortId) ||
      !Number.isInteger(observation.developmentAgeMonths) ||
      observation.developmentAgeMonths < 0 ||
      !nonEmptyText(observation.valuationAsOf) ||
      Number.isNaN(Date.parse(observation.valuationAsOf)) ||
      Date.parse(observation.valuationAsOf) > Date.parse(input.reportingCutAsOf) ||
      !isFiniteNumber(observation.cumulativePaidUsd) ||
      observation.cumulativePaidUsd < 0 ||
      (observation.cohortStatus !== 'open' && observation.cohortStatus !== 'closed')
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'paid development observations require cohort id, non-negative integer age, valuation as-of no later than the reporting cut, cumulative paid USD, and open/closed status',
      };
    }
    const cohortId = observation.cohortId.trim();
    const cohort = byCohort.get(cohortId) ?? [];
    cohort.push({ ...observation, cohortId });
    byCohort.set(cohortId, cohort);
  }

  const openCohorts: RiskClaimsPaidDevelopmentObservation[][] = [];
  const closedCohorts: RiskClaimsPaidDevelopmentObservation[][] = [];
  for (const observations of byCohort.values()) {
    observations.sort(
      (left, right) => left.developmentAgeMonths - right.developmentAgeMonths,
    );
    for (let index = 0; index < observations.length; index += 1) {
      const observation = observations[index];
      const prior = index > 0 ? observations[index - 1] : null;
      if (
        prior &&
        (prior.developmentAgeMonths === observation.developmentAgeMonths ||
          Date.parse(observation.valuationAsOf) < Date.parse(prior.valuationAsOf) ||
          observation.cumulativePaidUsd < prior.cumulativePaidUsd)
      ) {
        return {
          status: 'blocked',
          reasonCode: 'formula_reconciliation_failure',
          message:
            'no independent ultimate basis: paid development ages and valuation dates must advance while cumulative paid remains monotonic by cohort',
        };
      }
      if (
        observation.cohortStatus === 'closed' &&
        index !== observations.length - 1
      ) {
        return {
          status: 'blocked',
          reasonCode: 'formula_reconciliation_failure',
          message:
            'no independent ultimate basis: a closed cohort cannot carry later development observations',
        };
      }
    }
    const latest = observations[observations.length - 1];
    if (latest.cohortStatus === 'closed') {
      closedCohorts.push(observations);
    } else {
      openCohorts.push(observations);
    }
  }

  if (openCohorts.length === 0 || closedCohorts.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message:
        'no independent ultimate basis: paid development requires both current open cohorts and mature closed historical cohorts',
    };
  }

  let currentCumulativePaidUsd = 0;
  let estimatedUltimateUsd = 0;
  for (const openCohort of openCohorts) {
    const current = openCohort[openCohort.length - 1];
    let matchedAgePaidUsd = 0;
    let matchedUltimatePaidUsd = 0;
    for (const closedCohort of closedCohorts) {
      const atAge = closedCohort.find(
        (observation) =>
          observation.developmentAgeMonths === current.developmentAgeMonths,
      );
      if (!atAge) continue;
      const mature = closedCohort[closedCohort.length - 1];
      if (atAge.cumulativePaidUsd <= 0) continue;
      matchedAgePaidUsd += atAge.cumulativePaidUsd;
      matchedUltimatePaidUsd += mature.cumulativePaidUsd;
    }
    if (
      matchedAgePaidUsd <= 0 ||
      matchedUltimatePaidUsd < matchedAgePaidUsd
    ) {
      return {
        status: 'blocked',
        reasonCode: 'missing_governed_evidence',
        message: `no independent ultimate basis: no mature paid-development match exists at ${current.developmentAgeMonths} months`,
      };
    }
    const developmentFactor = matchedUltimatePaidUsd / matchedAgePaidUsd;
    currentCumulativePaidUsd += current.cumulativePaidUsd;
    estimatedUltimateUsd += current.cumulativePaidUsd * developmentFactor;
  }

  const estimatedOutstandingLiabilityUsd =
    estimatedUltimateUsd - currentCumulativePaidUsd;
  if (
    currentCumulativePaidUsd <= 0 ||
    estimatedOutstandingLiabilityUsd <= 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message:
        'no independent ultimate basis: paid development does not produce a positive outstanding liability',
    };
  }

  const roundedOutstandingLiabilityUsd = roundTo(
    estimatedOutstandingLiabilityUsd,
    2,
  );
  const reserveAdequacyPct = roundTo(
    (input.heldReservesUsd / roundedOutstandingLiabilityUsd) * 100,
    4,
  );
  return {
    status: 'ok',
    output: Object.freeze({
      reserveAdequacyPct,
      reserveSurplusDeficitUsd: roundTo(
        input.heldReservesUsd - roundedOutstandingLiabilityUsd,
        2,
      ),
      currentCumulativePaidUsd: roundTo(currentCumulativePaidUsd, 2),
      estimatedUltimateUsd: roundTo(estimatedUltimateUsd, 2),
      estimatedOutstandingLiabilityUsd: roundedOutstandingLiabilityUsd,
      weightedPaidDevelopmentFactor: roundTo(
        estimatedUltimateUsd / currentCumulativePaidUsd,
        6,
      ),
      statusBand: reserveAdequacyStatusBand(reserveAdequacyPct),
    }),
  };
}

export type RiskClaimsBrokerCarrier = Readonly<{
  carrierId: string;
  safetyScore: number;
  controlRedFlags: number;
  venueRiskMultiplier: number;
  exposureUsd: number;
}>;
export type RiskClaimsBrokerLiabilityInput = Readonly<{
  carriers: readonly RiskClaimsBrokerCarrier[];
  safetyScoreFloor: number;
  reportingCutAsOf?: string;
}>;
export type RiskClaimsBrokerFlaggedCarrier = Readonly<{
  carrierId: string;
  weightedExposureUsd: number;
  reasons: readonly string[];
}>;
export type RiskClaimsBrokerLiabilityOutput = Readonly<{
  flagged: readonly RiskClaimsBrokerFlaggedCarrier[];
  brokerLiabilityExposureUsd: number;
  flaggedCount: number;
  totalCarriers: number;
}>;

function executeRiskClaimsBrokerLiability(
  input: RiskClaimsBrokerLiabilityInput,
): ApprovedFormulaResult<RiskClaimsBrokerLiabilityOutput> {
  if (!Array.isArray(input?.carriers) || input.carriers.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'broker liability exposure requires at least one carrier',
    };
  }
  if (!isFiniteNumber(input?.safetyScoreFloor)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'safetyScoreFloor must be a finite value',
    };
  }
  for (const carrier of input.carriers) {
    if (
      !nonEmptyText(carrier?.carrierId) ||
      !isFiniteNumber(carrier?.safetyScore) ||
      !isFiniteNumber(carrier?.controlRedFlags) ||
      !isFiniteNumber(carrier?.venueRiskMultiplier) ||
      carrier.venueRiskMultiplier < 0 ||
      !isFiniteNumber(carrier?.exposureUsd) ||
      carrier.exposureUsd < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each carrier requires a carrierId, finite safety/flags/venue values, and a non-negative exposureUsd',
      };
    }
  }
  const flaggedRaw = input.carriers
    .filter(
      (carrier) => carrier.safetyScore < input.safetyScoreFloor || carrier.controlRedFlags > 0,
    )
    .map((carrier) => {
      const reasons: string[] = [];
      if (carrier.safetyScore < input.safetyScoreFloor) reasons.push('safety_score_below_floor');
      if (carrier.controlRedFlags > 0) reasons.push('control_red_flag');
      return {
        carrierId: carrier.carrierId,
        weightedExposureUsd: roundTo(carrier.exposureUsd * carrier.venueRiskMultiplier, 2),
        reasons,
      };
    })
    .sort(
      (a, b) =>
        b.weightedExposureUsd - a.weightedExposureUsd ||
        (a.carrierId < b.carrierId ? -1 : a.carrierId > b.carrierId ? 1 : 0),
    );
  const flagged = flaggedRaw.map((entry) =>
    Object.freeze({
      carrierId: entry.carrierId,
      weightedExposureUsd: entry.weightedExposureUsd,
      reasons: Object.freeze([...entry.reasons]),
    }),
  );
  return {
    status: 'ok',
    output: Object.freeze({
      flagged: Object.freeze(flagged),
      brokerLiabilityExposureUsd: roundTo(
        flagged.reduce((sum, entry) => sum + entry.weightedExposureUsd, 0),
        2,
      ),
      flaggedCount: flagged.length,
      totalCarriers: input.carriers.length,
    }),
  };
}

export type RiskClaimsInsuranceShipmentBand = Readonly<{
  valueBandKey: string;
  cargoValueUsd: number;
  carrierCargoLimitUsd: number;
  shipmentCount: number;
}>;
export type RiskClaimsInsuranceCoverageGapInput = Readonly<{
  shipments: readonly RiskClaimsInsuranceShipmentBand[];
  reportingCutAsOf?: string;
}>;
export type RiskClaimsInsuranceCoverageGapRow = Readonly<{
  valueBandKey: string;
  perShipmentGapUsd: number;
  shipmentCount: number;
  bandGapUsd: number;
}>;
export type RiskClaimsInsuranceCoverageGapOutput = Readonly<{
  coverageGapUsd: number;
  byBand: readonly RiskClaimsInsuranceCoverageGapRow[];
  gapBandCount: number;
}>;

function executeRiskClaimsInsuranceCoverageGap(
  input: RiskClaimsInsuranceCoverageGapInput,
): ApprovedFormulaResult<RiskClaimsInsuranceCoverageGapOutput> {
  if (!Array.isArray(input?.shipments) || input.shipments.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'insurance coverage gap requires at least one shipment value band',
    };
  }
  for (const shipment of input.shipments) {
    if (
      !nonEmptyText(shipment?.valueBandKey) ||
      !isFiniteNumber(shipment?.cargoValueUsd) ||
      shipment.cargoValueUsd < 0 ||
      !isFiniteNumber(shipment?.carrierCargoLimitUsd) ||
      shipment.carrierCargoLimitUsd < 0 ||
      !isFiniteNumber(shipment?.shipmentCount) ||
      shipment.shipmentCount < 0
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'each shipment band requires a valueBandKey, non-negative cargo value and carrier limit, and a non-negative shipmentCount',
      };
    }
  }
  const byBand = input.shipments
    .map((shipment) => {
      const perShipmentGapUsd = roundTo(
        Math.max(shipment.cargoValueUsd - shipment.carrierCargoLimitUsd, 0),
        2,
      );
      return {
        valueBandKey: shipment.valueBandKey,
        perShipmentGapUsd,
        shipmentCount: shipment.shipmentCount,
        bandGapUsd: roundTo(perShipmentGapUsd * shipment.shipmentCount, 2),
      };
    })
    .sort(
      (a, b) =>
        b.bandGapUsd - a.bandGapUsd ||
        (a.valueBandKey < b.valueBandKey ? -1 : a.valueBandKey > b.valueBandKey ? 1 : 0),
    )
    .map((row) => Object.freeze(row));
  return {
    status: 'ok',
    output: Object.freeze({
      coverageGapUsd: roundTo(
        byBand.reduce((sum, row) => sum + row.bandGapUsd, 0),
        2,
      ),
      byBand: Object.freeze(byBand),
      gapBandCount: byBand.filter((row) => row.bandGapUsd > 0).length,
    }),
  };
}

function riskClaimsFormulaDefinition<TInput, TOutput>(spec: {
  formulaId: string;
  displayName: string;
  description: string;
  inputSchemaVersion: string;
  outputSchemaVersion: string;
  execute: (input: TInput) => ApprovedFormulaResult<TOutput>;
  digestName: string;
  goldenTraces: readonly ApprovedFormulaGoldenTrace<TInput, TOutput>[];
}): ApprovedFormulaDefinition<TInput, TOutput> {
  return Object.freeze({
    formulaId: spec.formulaId,
    version: '1.0.0',
    displayName: spec.displayName,
    description: spec.description,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: spec.inputSchemaVersion,
    outputSchemaVersion: spec.outputSchemaVersion,
    expectedContextKind: RISK_CLAIMS_CONTEXT_KIND,
    approvedSourceSystemRefs: RISK_CLAIMS_FORMULA_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs([[spec.digestName, spec.execute]]),
    goldenTraces: Object.freeze(spec.goldenTraces),
    execute: spec.execute,
  });
}

const riskClaimsFormulaDefinitions: readonly ApprovedFormulaDefinition[] = Object.freeze([
  riskClaimsFormulaDefinition({
    formulaId: 'claims_loss_ratio_v1',
    displayName: 'Cargo-claim loss ratio',
    description: 'Loss ratio = claims (incurred or paid) divided by freight revenue, in percent.',
    inputSchemaVersion: 'risk_claims_loss_ratio_input_v1',
    outputSchemaVersion: 'risk_claims_loss_ratio_output_v1',
    execute: executeRiskClaimsLossRatio,
    digestName: 'executeRiskClaimsLossRatio',
    goldenTraces: [
      {
        traceId: 'claims_loss_ratio:incurred:ok:v1',
        input: { claimsAmountUsd: 2000000, freightRevenueUsd: 100000000, basis: 'incurred', reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { lossRatioPct: 2, basis: 'incurred', statusBand: 'constrained' } },
      },
      {
        traceId: 'claims_loss_ratio:blocked:zero-revenue:v1',
        input: { claimsAmountUsd: 2000000, freightRevenueUsd: 0, basis: 'incurred' },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'freightRevenueUsd must be greater than zero' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'incurred_claims_usd_v1',
    displayName: 'Incurred cargo claims',
    description:
      'Incurred claims = paid claims plus case reserves plus IBNR from the same approved claims register cut.',
    inputSchemaVersion: 'risk_claims_incurred_claims_input_v1',
    outputSchemaVersion: 'risk_claims_incurred_claims_output_v1',
    execute: executeRiskClaimsIncurredClaims,
    digestName: 'executeRiskClaimsIncurredClaims',
    goldenTraces: [
      {
        traceId: 'incurred_claims_usd:ok:v1',
        input: { claimsPaidUsd: 1000000, caseReservesUsd: 400000, ibnrUsd: 250000, reportingCutAsOf: '2025-12-31' },
        expected: {
          status: 'ok',
          output: { incurredClaimsUsd: 1650000, claimsPaidUsd: 1000000, caseReservesUsd: 400000, ibnrUsd: 250000 },
        },
      },
      {
        traceId: 'incurred_claims_usd:blocked:negative:v1',
        input: { claimsPaidUsd: 1000000, caseReservesUsd: -1, ibnrUsd: 250000 },
        expected: {
          status: 'blocked',
          reasonCode: 'invalid_formula_input',
          message: 'claimsPaidUsd, caseReservesUsd, and ibnrUsd must not be negative',
        },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_loss_ratio_trend_v1',
    displayName: 'Cargo-claim loss ratio trend',
    description: 'Period-over-period loss-ratio change in percentage points and as a percent of the prior ratio.',
    inputSchemaVersion: 'risk_claims_loss_ratio_trend_input_v1',
    outputSchemaVersion: 'risk_claims_loss_ratio_trend_output_v1',
    execute: executeRiskClaimsLossRatioTrend,
    digestName: 'executeRiskClaimsLossRatioTrend',
    goldenTraces: [
      {
        traceId: 'claims_loss_ratio_trend:improving:ok:v1',
        input: { priorLossRatioPct: 2, currentLossRatioPct: 1.5, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { deltaPts: -0.5, deltaPctOfPrior: -25, direction: 'improving' } },
      },
      {
        traceId: 'claims_loss_ratio_trend:blocked:zero-prior:v1',
        input: { priorLossRatioPct: 0, currentLossRatioPct: 1.5 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'priorLossRatioPct must be greater than zero' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_pareto_v1',
    displayName: 'Cargo-claim Pareto by cause',
    description: 'Ranks claim value by group (e.g. cause), with share, cumulative share, and the count to reach 80%.',
    inputSchemaVersion: 'risk_claims_pareto_input_v1',
    outputSchemaVersion: 'risk_claims_pareto_output_v1',
    execute: executeRiskClaimsPareto,
    digestName: 'executeRiskClaimsPareto',
    goldenTraces: [
      {
        traceId: 'claims_pareto:by-cause:ok:v1',
        input: { items: [{ key: 'damage', valueUsd: 60 }, { key: 'theft', valueUsd: 30 }, { key: 'loss', valueUsd: 10 }] },
        expected: {
          status: 'ok',
          output: {
            ranked: [
              { key: 'damage', valueUsd: 60, sharePct: 60, cumulativeSharePct: 60 },
              { key: 'theft', valueUsd: 30, sharePct: 30, cumulativeSharePct: 90 },
              { key: 'loss', valueUsd: 10, sharePct: 10, cumulativeSharePct: 100 },
            ],
            totalUsd: 100,
            topKey: 'damage',
            topValueUsd: 60,
            paretoKeyCount: 2,
          },
        },
      },
      {
        traceId: 'claims_pareto:blocked:empty:v1',
        input: { items: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'claims pareto requires at least one item' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_attribution_v1',
    displayName: 'Cargo-claim attribution by carrier and lane',
    description: 'Ranks claims by carrier/lane with per-entity loss ratio and share of total claims.',
    inputSchemaVersion: 'risk_claims_attribution_input_v1',
    outputSchemaVersion: 'risk_claims_attribution_output_v1',
    execute: executeRiskClaimsAttribution,
    digestName: 'executeRiskClaimsAttribution',
    goldenTraces: [
      {
        traceId: 'claims_attribution:by-carrier:ok:v1',
        input: { entities: [{ key: 'Carrier A', claimsUsd: 600, freightRevenueUsd: 30000 }, { key: 'Carrier B', claimsUsd: 400, freightRevenueUsd: 40000 }] },
        expected: {
          status: 'ok',
          output: {
            ranked: [
              { key: 'Carrier A', claimsUsd: 600, freightRevenueUsd: 30000, lossRatioPct: 2, sharePct: 60 },
              { key: 'Carrier B', claimsUsd: 400, freightRevenueUsd: 40000, lossRatioPct: 1, sharePct: 40 },
            ],
            totalClaimsUsd: 1000,
            topKey: 'Carrier A',
            topClaimsUsd: 600,
          },
        },
      },
      {
        traceId: 'claims_attribution:blocked:empty:v1',
        input: { entities: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'claims attribution requires at least one entity' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'open_exposure_v1',
    displayName: 'Open-claim exposure',
    description: 'Sums open-claim reserve by age band and computes each band share of total open exposure.',
    inputSchemaVersion: 'risk_claims_open_exposure_input_v1',
    outputSchemaVersion: 'risk_claims_open_exposure_output_v1',
    execute: executeRiskClaimsOpenExposure,
    digestName: 'executeRiskClaimsOpenExposure',
    goldenTraces: [
      {
        traceId: 'open_exposure:by-band:ok:v1',
        input: { bands: [{ bandKey: '0_30', openReserveUsd: 70 }, { bandKey: '31_90', openReserveUsd: 30 }] },
        expected: {
          status: 'ok',
          output: {
            totalOpenExposureUsd: 100,
            byBand: [
              { bandKey: '0_30', openReserveUsd: 70, sharePct: 70 },
              { bandKey: '31_90', openReserveUsd: 30, sharePct: 30 },
            ],
            statusBand: 'sufficient',
          },
        },
      },
      {
        traceId: 'open_exposure:blocked:empty:v1',
        input: { bands: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'open exposure requires at least one age band' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'contract_liability_flag_v1',
    displayName: 'Contract liability diagnostic',
    description: 'Flags contracts with no liability cap or an indemnity gap and totals uncapped exposure.',
    inputSchemaVersion: 'risk_claims_contract_liability_flag_input_v1',
    outputSchemaVersion: 'risk_claims_contract_liability_flag_output_v1',
    execute: executeRiskClaimsContractLiabilityFlag,
    digestName: 'executeRiskClaimsContractLiabilityFlag',
    goldenTraces: [
      {
        traceId: 'contract_liability_flag:uncapped:ok:v1',
        input: { contracts: [{ contractId: 'C1', hasLiabilityCap: false, hasIndemnityClause: true, exposureUsd: 500000 }, { contractId: 'C2', hasLiabilityCap: true, hasIndemnityClause: true, exposureUsd: 300000 }] },
        expected: {
          status: 'ok',
          output: {
            flagged: [{ contractId: 'C1', exposureUsd: 500000, reasons: ['no_liability_cap'] }],
            uncappedExposureUsd: 500000,
            flaggedCount: 1,
            totalContracts: 2,
          },
        },
      },
      {
        traceId: 'contract_liability_flag:blocked:empty:v1',
        input: { contracts: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'contract liability diagnostic requires at least one contract' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'subrogation_recovery_v1',
    displayName: 'Subrogation recovery',
    description: 'Recovered divided by recoverable claims, in percent, with unrecovered remainder.',
    inputSchemaVersion: 'risk_claims_subrogation_recovery_input_v1',
    outputSchemaVersion: 'risk_claims_subrogation_recovery_output_v1',
    execute: executeRiskClaimsSubrogationRecovery,
    digestName: 'executeRiskClaimsSubrogationRecovery',
    goldenTraces: [
      {
        traceId: 'subrogation_recovery:ok:v1',
        input: { recoveredUsd: 57000, recoverableUsd: 114000, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { subrogationRecoveryPct: 50, unrecoveredUsd: 57000, statusBand: 'sufficient' } },
      },
      {
        traceId: 'subrogation_recovery:blocked:zero-recoverable:v1',
        input: { recoveredUsd: 57000, recoverableUsd: 0 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'recoverableUsd must be greater than zero' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_provenance_v1',
    displayName: 'Cargo-claim provenance and de-confliction boundary',
    description: 'Summarizes evidence lineage and the carrier/lane/GL de-confliction boundary distinct from carrier cost.',
    inputSchemaVersion: 'risk_claims_provenance_input_v1',
    outputSchemaVersion: 'risk_claims_provenance_output_v1',
    execute: executeRiskClaimsProvenance,
    digestName: 'executeRiskClaimsProvenance',
    goldenTraces: [
      {
        traceId: 'claims_provenance:ok:v1',
        input: { evidenceRefIds: ['e1', 'e2'], sourceClass: 'extrapolated', sourceRef: 'risk-claims/extrapolated/sha256:abc', asOf: '2025-12-31', carrierIds: ['Carrier A'], laneIds: ['CHI_ATL'], glAccountIds: ['5300_claims_expense'] },
        expected: {
          status: 'ok',
          output: {
            evidenceRefCount: 2,
            sourceClass: 'extrapolated',
            asOf: '2025-12-31',
            carrierScopeCount: 1,
            laneScopeCount: 1,
            glAccountIds: ['5300_claims_expense'],
            distinctFromCarrierCost: true,
          },
        },
      },
      {
        traceId: 'claims_provenance:blocked:no-evidence:v1',
        input: { evidenceRefIds: [], sourceClass: 'extrapolated', sourceRef: 'r', asOf: '2025-12-31', carrierIds: [], laneIds: [], glAccountIds: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'claims provenance requires at least one evidence reference' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_reduction_opportunity_v1',
    displayName: 'Cargo-claim loss-reduction opportunity',
    description: 'Operating-income recovery from cutting the loss ratio to target = (current - target) pts x freight revenue.',
    inputSchemaVersion: 'risk_claims_reduction_opportunity_input_v1',
    outputSchemaVersion: 'risk_claims_reduction_opportunity_output_v1',
    execute: executeRiskClaimsReductionOpportunity,
    digestName: 'executeRiskClaimsReductionOpportunity',
    goldenTraces: [
      {
        traceId: 'claims_reduction_opportunity:ok:v1',
        input: { currentLossRatioPct: 2, targetLossRatioPct: 1, freightRevenueUsd: 1000000000, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { reductionOpportunityUsd: 10000000, lossRatioReductionPts: 1, statusBand: 'sufficient' } },
      },
      {
        traceId: 'claims_reduction_opportunity:blocked:zero-revenue:v1',
        input: { currentLossRatioPct: 2, targetLossRatioPct: 1, freightRevenueUsd: 0 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'freightRevenueUsd must be greater than zero' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_recovery_options_v1',
    displayName: 'Cargo-claim recovery options compare',
    description: 'Ranks recovery options by net recovery = gross recovery less implementation cost and shared-scope overlap.',
    inputSchemaVersion: 'risk_claims_recovery_options_input_v1',
    outputSchemaVersion: 'risk_claims_recovery_options_output_v1',
    execute: executeRiskClaimsRecoveryOptions,
    digestName: 'executeRiskClaimsRecoveryOptions',
    goldenTraces: [
      {
        traceId: 'claims_recovery_options:ok:v1',
        input: { options: [{ optionId: 'requalify', label: 'Requalify carriers', grossRecoveryUsd: 5000000, implementationCostUsd: 1000000, sharedScopeOverlapUsd: 500000 }, { optionId: 'caps', label: 'Enforce caps', grossRecoveryUsd: 3000000, implementationCostUsd: 200000, sharedScopeOverlapUsd: 0 }] },
        expected: {
          status: 'ok',
          output: {
            ranked: [
              { optionId: 'requalify', label: 'Requalify carriers', netRecoveryUsd: 3500000 },
              { optionId: 'caps', label: 'Enforce caps', netRecoveryUsd: 2800000 },
            ],
            topOptionId: 'requalify',
            topNetRecoveryUsd: 3500000,
            optionCount: 2,
          },
        },
      },
      {
        traceId: 'claims_recovery_options:blocked:empty:v1',
        input: { options: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'recovery options compare requires at least one option' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'carrier_requalification_v1',
    displayName: 'Carrier requalification value',
    description: 'Net value of requalifying worst-claim carriers = removed claims less reroute cost and shared-scope overlap.',
    inputSchemaVersion: 'risk_claims_carrier_requalification_input_v1',
    outputSchemaVersion: 'risk_claims_carrier_requalification_output_v1',
    execute: executeRiskClaimsCarrierRequalification,
    digestName: 'executeRiskClaimsCarrierRequalification',
    goldenTraces: [
      {
        traceId: 'carrier_requalification:ok:v1',
        input: { removedCarrierClaimsUsd: 4000000, rerouteCostUsd: 1500000, sharedScopeOverlapUsd: 500000, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { netRequalificationValueUsd: 2000000, statusBand: 'sufficient' } },
      },
      {
        traceId: 'carrier_requalification:blocked:non-finite:v1',
        input: { removedCarrierClaimsUsd: Number.POSITIVE_INFINITY, rerouteCostUsd: 1500000, sharedScopeOverlapUsd: 500000 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'removedCarrierClaimsUsd, rerouteCostUsd, and sharedScopeOverlapUsd must be finite USD values' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'liability_cap_value_v1',
    displayName: 'Liability-cap enforcement value',
    description: 'Exposure cut from enforcing liability caps = uncapped exposure x cap-adoption rate.',
    inputSchemaVersion: 'risk_claims_liability_cap_value_input_v1',
    outputSchemaVersion: 'risk_claims_liability_cap_value_output_v1',
    execute: executeRiskClaimsLiabilityCapValue,
    digestName: 'executeRiskClaimsLiabilityCapValue',
    goldenTraces: [
      {
        traceId: 'liability_cap_value:ok:v1',
        input: { uncappedExposureUsd: 10000000, capAdoptionRatePct: 50, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { liabilityCapValueUsd: 5000000, statusBand: 'sufficient' } },
      },
      {
        traceId: 'liability_cap_value:blocked:rate-out-of-range:v1',
        input: { uncappedExposureUsd: 10000000, capAdoptionRatePct: 150 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'capAdoptionRatePct must be between 0 and 100' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_leakage_v1',
    displayName: 'Cargo-claim leakage',
    description: 'Leakage = actual paid x best-practice recoverable share; should-pay = actual paid less leakage.',
    inputSchemaVersion: 'risk_claims_leakage_input_v1',
    outputSchemaVersion: 'risk_claims_leakage_output_v1',
    execute: executeRiskClaimsLeakage,
    digestName: 'executeRiskClaimsLeakage',
    goldenTraces: [
      {
        traceId: 'claims_leakage:ok:v1',
        input: { actualPaidUsd: 1000000, bestPracticeRecoverableSharePct: 7, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { leakageUsd: 70000, shouldPayUsd: 930000, statusBand: 'sufficient' } },
      },
      {
        traceId: 'claims_leakage:blocked:share-out-of-range:v1',
        input: { actualPaidUsd: 1000000, bestPracticeRecoverableSharePct: 120 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'bestPracticeRecoverableSharePct must be between 0 and 100' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'tcor_v1',
    displayName: 'Total cost of risk (TCOR)',
    description: 'TCOR = risk transfer + retained losses + risk control + administration, with component shares.',
    inputSchemaVersion: 'risk_claims_tcor_input_v1',
    outputSchemaVersion: 'risk_claims_tcor_output_v1',
    execute: executeRiskClaimsTcor,
    digestName: 'executeRiskClaimsTcor',
    goldenTraces: [
      {
        traceId: 'tcor:ok:v1',
        input: { riskTransferUsd: 4000000, retainedLossesUsd: 5000000, riskControlUsd: 500000, adminUsd: 500000, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { totalCostOfRiskUsd: 10000000, riskTransferSharePct: 40, retainedLossesSharePct: 50, riskControlSharePct: 5, adminSharePct: 5, statusBand: 'sufficient' } },
      },
      {
        traceId: 'tcor:blocked:negative-component:v1',
        input: { riskTransferUsd: -1, retainedLossesUsd: 5000000, riskControlUsd: 500000, adminUsd: 500000 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'TCOR components must be zero or greater' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'claims_frequency_severity_v1',
    displayName: 'Cargo-claim frequency and severity',
    description: 'Severity = incurred claims / claim count; frequency = claim count / exposure per 100k shipments.',
    inputSchemaVersion: 'risk_claims_frequency_severity_input_v1',
    outputSchemaVersion: 'risk_claims_frequency_severity_output_v1',
    execute: executeRiskClaimsFrequencySeverity,
    digestName: 'executeRiskClaimsFrequencySeverity',
    goldenTraces: [
      {
        traceId: 'claims_frequency_severity:ok:v1',
        input: { incurredClaimsUsd: 1000000, claimCount: 200, exposureUnits: 100000, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { severityUsd: 5000, frequencyPer100kExposure: 200, statusBand: 'sufficient' } },
      },
      {
        traceId: 'claims_frequency_severity:blocked:zero-count:v1',
        input: { incurredClaimsUsd: 1000000, claimCount: 0, exposureUnits: 100000 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'claimCount must be greater than zero' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'reserve_adequacy_v1',
    displayName: 'Reserve adequacy',
    description: 'Held reserves (case + IBNR) divided by estimated ultimate, in percent, with surplus/deficit.',
    inputSchemaVersion: 'risk_claims_reserve_adequacy_input_v1',
    outputSchemaVersion: 'risk_claims_reserve_adequacy_output_v1',
    execute: executeRiskClaimsReserveAdequacy,
    digestName: 'executeRiskClaimsReserveAdequacy',
    goldenTraces: [
      {
        traceId: 'reserve_adequacy:ok:v1',
        input: { heldReservesUsd: 1000000, estimatedUltimateUsd: 1000000, reportingCutAsOf: '2025-12-31' },
        expected: { status: 'ok', output: { reserveAdequacyPct: 100, reserveSurplusDeficitUsd: 0, statusBand: 'sufficient' } },
      },
      {
        traceId: 'reserve_adequacy:blocked:zero-ultimate:v1',
        input: { heldReservesUsd: 1000000, estimatedUltimateUsd: 0 },
        expected: { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'estimatedUltimateUsd must be greater than zero' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'reserve_adequacy_v2',
    displayName: 'Reserve adequacy from paid development',
    description:
      'Held case and IBNR reserves divided by independently estimated outstanding liability from matched-age paid cohort development with governed cumulative-paid source receipts.',
    inputSchemaVersion: 'risk_claims_reserve_adequacy_paid_development_input_v2',
    outputSchemaVersion: 'risk_claims_reserve_adequacy_paid_development_output_v2',
    execute: executeRiskClaimsReserveAdequacyV2,
    digestName: 'executeRiskClaimsReserveAdequacyV2',
    goldenTraces: [
      {
        traceId: 'reserve_adequacy:under_reserved:paid_development:v2',
        input: {
          heldReservesUsd: 400000,
          basis: 'paid',
          observations: [
            {
              cohortId: 'historical-2024-01',
              developmentAgeMonths: 0,
              valuationAsOf: '2024-01-31T00:00:00.000Z',
              cumulativePaidUsd: 200000,
              cohortStatus: 'open',
              sourceField: 'cumulative_paid_usd',
              sourceObjectId: 'paid-development:historical-2024-01:0',
              evidenceRefId: 'risk-claims:paid-development:golden',
              proofRefId: 'risk-claims:proof:paid-development:golden',
            },
            {
              cohortId: 'historical-2024-01',
              developmentAgeMonths: 12,
              valuationAsOf: '2025-01-31T00:00:00.000Z',
              cumulativePaidUsd: 1000000,
              cohortStatus: 'closed',
              sourceField: 'cumulative_paid_usd',
              sourceObjectId: 'paid-development:historical-2024-01:12',
              evidenceRefId: 'risk-claims:paid-development:golden',
              proofRefId: 'risk-claims:proof:paid-development:golden',
            },
            {
              cohortId: 'current-2025-12',
              developmentAgeMonths: 0,
              valuationAsOf: '2025-12-31T00:00:00.000Z',
              cumulativePaidUsd: 200000,
              cohortStatus: 'open',
              sourceField: 'cumulative_paid_usd',
              sourceObjectId: 'paid-development:current-2025-12:0',
              evidenceRefId: 'risk-claims:paid-development:golden',
              proofRefId: 'risk-claims:proof:paid-development:golden',
            },
          ],
          reportingCutAsOf: '2025-12-31T00:00:00.000Z',
        },
        expected: {
          status: 'ok',
          output: {
            reserveAdequacyPct: 50,
            reserveSurplusDeficitUsd: -400000,
            currentCumulativePaidUsd: 200000,
            estimatedUltimateUsd: 1000000,
            estimatedOutstandingLiabilityUsd: 800000,
            weightedPaidDevelopmentFactor: 5,
            statusBand: 'critical',
          },
        },
      },
      {
        traceId: 'reserve_adequacy:blocked:no_independent_basis:v2',
        input: {
          heldReservesUsd: 400000,
          basis: 'paid',
          observations: [],
          reportingCutAsOf: '2025-12-31T00:00:00.000Z',
        },
        expected: {
          status: 'blocked',
          reasonCode: 'missing_governed_evidence',
          message:
            'no independent ultimate basis: paid cohort development history is missing',
        },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'broker_liability_exposure_v1',
    displayName: 'Broker liability exposure',
    description: 'Flags carriers below the safety-score floor or with control red flags and weights exposure by venue risk.',
    inputSchemaVersion: 'risk_claims_broker_liability_exposure_input_v1',
    outputSchemaVersion: 'risk_claims_broker_liability_exposure_output_v1',
    execute: executeRiskClaimsBrokerLiability,
    digestName: 'executeRiskClaimsBrokerLiability',
    goldenTraces: [
      {
        traceId: 'broker_liability_exposure:ok:v1',
        input: { carriers: [{ carrierId: 'Carrier A', safetyScore: 70, controlRedFlags: 1, venueRiskMultiplier: 1.5, exposureUsd: 2000000 }, { carrierId: 'Carrier B', safetyScore: 95, controlRedFlags: 0, venueRiskMultiplier: 1, exposureUsd: 1000000 }], safetyScoreFloor: 80 },
        expected: {
          status: 'ok',
          output: {
            flagged: [{ carrierId: 'Carrier A', weightedExposureUsd: 3000000, reasons: ['safety_score_below_floor', 'control_red_flag'] }],
            brokerLiabilityExposureUsd: 3000000,
            flaggedCount: 1,
            totalCarriers: 2,
          },
        },
      },
      {
        traceId: 'broker_liability_exposure:blocked:empty:v1',
        input: { carriers: [], safetyScoreFloor: 80 },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'broker liability exposure requires at least one carrier' },
      },
    ],
  }),
  riskClaimsFormulaDefinition({
    formulaId: 'insurance_coverage_gap_v1',
    displayName: 'Insurance coverage gap',
    description: 'Uninsured gap = sum over value bands of max(cargo value - carrier cargo limit, 0) x shipment count.',
    inputSchemaVersion: 'risk_claims_insurance_coverage_gap_input_v1',
    outputSchemaVersion: 'risk_claims_insurance_coverage_gap_output_v1',
    execute: executeRiskClaimsInsuranceCoverageGap,
    digestName: 'executeRiskClaimsInsuranceCoverageGap',
    goldenTraces: [
      {
        traceId: 'insurance_coverage_gap:ok:v1',
        input: { shipments: [{ valueBandKey: 'over_500k', cargoValueUsd: 600000, carrierCargoLimitUsd: 100000, shipmentCount: 10 }, { valueBandKey: 'under_100k', cargoValueUsd: 80000, carrierCargoLimitUsd: 100000, shipmentCount: 50 }] },
        expected: {
          status: 'ok',
          output: {
            coverageGapUsd: 5000000,
            byBand: [
              { valueBandKey: 'over_500k', perShipmentGapUsd: 500000, shipmentCount: 10, bandGapUsd: 5000000 },
              { valueBandKey: 'under_100k', perShipmentGapUsd: 0, shipmentCount: 50, bandGapUsd: 0 },
            ],
            gapBandCount: 1,
          },
        },
      },
      {
        traceId: 'insurance_coverage_gap:blocked:empty:v1',
        input: { shipments: [] },
        expected: { status: 'blocked', reasonCode: 'missing_formula_input', message: 'insurance coverage gap requires at least one shipment value band' },
      },
    ],
  }),
]);

function portfolioFormulaInputBlock(
  input: unknown,
): ApprovedFormulaBlockedResult | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object',
    };
  }
  const sourceAsOf = stringOrNull((input as { sourceAsOf?: unknown }).sourceAsOf);
  if (!sourceAsOf) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'sourceAsOf is required',
    };
  }
  return null;
}

export type AttributionDeconflictionReceipt = Readonly<{
  receiptId: string;
  resolverVersion: string;
  resolverImplementationDigest: string;
  derivedSetDigest: string;
  status: 'complete' | 'partial' | 'blocked';
  canPublishFullCompositeTotal: boolean;
  includedClaimIds: readonly string[];
  unresolvedPairs: readonly string[];
  evidenceRefIds: readonly string[];
  asOf: string;
}>;

export type AllocationLensExclusionReceiptInput = Readonly<{
  receiptId: string;
  tenantId: string;
  scopeKind: 'enterprise';
  scopeId: string;
  pairIdentity: Readonly<{
    leftLeverFamilyId: string;
    rightLeverFamilyId: string;
    coveredClaimIds: readonly [string, string];
  }>;
  reportingCutIdentity: string;
  reportingCutAsOf: string;
  timeframe: string;
  sourceAsOf: string;
  comparisonDimension: 'atomic_p_and_l_action';
  comparisonRuleId: string;
  comparisonRuleVersion: string;
  leftMemberIds: readonly string[];
  rightMemberIds: readonly string[];
  memberUniverseCompleteness: Readonly<{
    left: 'complete' | 'incomplete';
    right: 'complete' | 'incomplete';
  }>;
  memberUniverseProvenance: Readonly<{
    left: AllocationLensExclusionMemberUniverseProvenance;
    right: AllocationLensExclusionMemberUniverseProvenance;
  }>;
  coveredClaimSourceValueHashes: readonly Readonly<{
    claimId: string;
    sourceValueHash: string;
  }>[];
  evidenceRefIds: readonly string[];
}>;

export type AllocationLensExclusionReceiptOutput = Readonly<{
  schemaVersion: 'allocation_lens_exclusion_receipt_v1';
  formulaId: 'allocation_lens_exclusion_receipt_v1';
  formulaVersion: '1.0.0';
  receiptId: string;
  tenantId: string;
  scopeKind: 'enterprise';
  scopeId: string;
  pairIdentity: Readonly<{
    leftLeverFamilyId: string;
    rightLeverFamilyId: string;
  }>;
  coveredClaimIds: readonly [string, string];
  reportingCutIdentity: string;
  reportingCutAsOf: string;
  timeframe: string;
  sourceAsOf: string;
  comparisonDimension: 'atomic_p_and_l_action';
  comparisonRuleId: string;
  comparisonRuleVersion: string;
  comparedMemberUniverses: Readonly<{
    left: Readonly<{
      completeness: 'complete';
      memberCount: number;
      memberDigest: string;
      provenance: AllocationLensExclusionMemberUniverseProvenance;
    }>;
    right: Readonly<{
      completeness: 'complete';
      memberCount: number;
      memberDigest: string;
      provenance: AllocationLensExclusionMemberUniverseProvenance;
    }>;
  }>;
  overlapMemberCount: 0;
  overlapMemberDigest: string;
  verdict: 'zero_overlap';
  overlapAmountUsd: 0;
  coveredClaimSourceValueHashes: readonly Readonly<{
    claimId: string;
    sourceValueHash: string;
  }>[];
  evidenceRefIds: readonly string[];
}>;

function canonicalAllocationLensMemberIds(
  memberIds: readonly string[],
): readonly string[] {
  return Object.freeze(
    [...new Set(memberIds.map((value) => value.trim()).filter(Boolean))].sort(),
  );
}

export function allocationLensMemberDigest(memberIds: readonly string[]): string {
  return createHash('sha256')
    .update(JSON.stringify(canonicalAllocationLensMemberIds(memberIds)), 'utf8')
    .digest('hex');
}

function allocationLensExclusionInputString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function canonicalAllocationLensMemberUniverseProvenance(
  value: AllocationLensExclusionMemberUniverseProvenance | undefined,
): AllocationLensExclusionMemberUniverseProvenance | null {
  if (!value || typeof value !== 'object') return null;
  const sourceClass = allocationLensExclusionInputString(value.sourceClass);
  const sourceRef = allocationLensExclusionInputString(value.sourceRef);
  const artifactId = allocationLensExclusionInputString(value.artifactId);
  const ingestJobId = allocationLensExclusionInputString(value.ingestJobId);
  const sourceSystemRef = allocationLensExclusionInputString(
    value.sourceSystemRef,
  );
  const evidenceSchemaVersion = allocationLensExclusionInputString(
    value.evidenceSchemaVersion,
  );
  const contextKind = allocationLensExclusionInputString(value.contextKind);
  const sourceAsOf = allocationLensExclusionInputString(value.sourceAsOf);
  const contentDigest = allocationLensExclusionInputString(value.contentDigest);
  const evidenceRefIds = canonicalAllocationLensMemberIds(
    value.evidenceRefIds ?? [],
  );
  if (
    !sourceClass ||
    !sourceRef ||
    !artifactId ||
    !ingestJobId ||
    !sourceSystemRef ||
    !evidenceSchemaVersion ||
    !contextKind ||
    !sourceAsOf ||
    !contentDigest ||
    !/^[a-f0-9]{64}$/.test(contentDigest) ||
    evidenceRefIds.length === 0 ||
    !Object.values(DECISION_AGENT_SOURCE_CLASSES).includes(
      sourceClass as DecisionAgentSourceClass,
    )
  ) {
    return null;
  }
  let sourceDefinition;
  try {
    sourceDefinition = getApprovedSourceSystemDefinition(sourceSystemRef);
  } catch {
    return null;
  }
  if (
    sourceSystemRef !== ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF ||
    evidenceSchemaVersion !== ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION ||
    contextKind !== ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND ||
    !sourceDefinition.allowedSourceClasses.includes(sourceClass) ||
    !sourceDefinition.allowedContextKinds?.includes(contextKind)
  ) {
    return null;
  }
  return Object.freeze({
    sourceClass: sourceClass as DecisionAgentSourceClass,
    evidenceRefIds,
    sourceRef,
    artifactId,
    ingestJobId,
    sourceSystemRef,
    evidenceSchemaVersion,
    contextKind,
    sourceAsOf,
    contentDigest,
  });
}

function allocationLensEvidenceContentDigest(contentJson: unknown): string {
  return createHash('sha256')
    .update(JSON.stringify(canonicalizeJson(contentJson)), 'utf8')
    .digest('hex');
}

function sameAllocationLensInstant(left: string, right: string): boolean {
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  return (
    Number.isFinite(leftTime) &&
    Number.isFinite(rightTime) &&
    leftTime === rightTime
  );
}

type AllocationLensMemberUniverseEvidenceExpectation = Readonly<{
  side: 'left' | 'right';
  tenantId: string;
  scopeKind: 'enterprise';
  scopeId: string;
  leverFamilyId: string;
  comparisonDimension: 'atomic_p_and_l_action';
  sourceAsOf: string;
  reportingCutAsOf: string;
  timeframe: string;
  provenance: AllocationLensExclusionMemberUniverseProvenance;
  evidenceRefIds: readonly string[];
  memberIds?: readonly string[];
  memberCount?: number;
  memberDigest?: string;
}>;

function validateAllocationLensMemberUniverseEvidence(
  expectation: AllocationLensMemberUniverseEvidenceExpectation,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const provenance = canonicalAllocationLensMemberUniverseProvenance(
    expectation.provenance,
  );
  if (!provenance) {
    return {
      reasonCode: 'unapproved_source_system',
      message: `${expectation.side} member universe does not name a compiled governed source contract.`,
    };
  }
  const artifact = artifacts.find(
    (candidate) => candidate.artifactId === provenance.artifactId,
  );
  if (!artifact) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: `${expectation.side} member-universe artifact is missing from formula evidence.`,
    };
  }
  let sourceDefinition: ReturnType<typeof getApprovedSourceSystemDefinition>;
  try {
    sourceDefinition = getApprovedSourceSystemDefinition(
      provenance.sourceSystemRef,
    );
  } catch {
    return {
      reasonCode: 'unapproved_source_system',
      message: `${expectation.side} member universe cites an uncompiled source system.`,
    };
  }
  if (
    provenance.sourceSystemRef !==
      ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF ||
    provenance.contextKind !== ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND ||
    provenance.evidenceSchemaVersion !==
      ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION ||
    artifact.sourceSystemRef !== provenance.sourceSystemRef ||
    artifact.contextKind !== provenance.contextKind ||
    artifact.schemaVersion !== provenance.evidenceSchemaVersion ||
    artifact.sourceClass !== provenance.sourceClass ||
    artifact.sourceRef !== provenance.sourceRef ||
    artifact.ingestJobId !== provenance.ingestJobId ||
    !sourceDefinition.allowedSourceClasses.includes(artifact.sourceClass) ||
    !sourceDefinition.allowedContextKinds?.includes(provenance.contextKind)
  ) {
    return {
      reasonCode: 'unapproved_source_system',
      message: `${expectation.side} member-universe artifact does not match compiled source governance.`,
    };
  }
  const computedContentDigest = allocationLensEvidenceContentDigest(
    artifact.contentJson,
  );
  if (
    artifact.contentDigest !== computedContentDigest ||
    provenance.contentDigest !== computedContentDigest
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: `${expectation.side} member-universe content digest does not reconcile to the approved artifact.`,
    };
  }
  const content = asRecord(artifact.contentJson);
  const source = asRecord(content?.source);
  const memberUniverse = asRecord(content?.memberUniverse);
  if (!content || !source || !memberUniverse) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: `${expectation.side} artifact does not carry a governed member universe.`,
    };
  }
  const artifactMemberIds = canonicalAllocationLensMemberIds(
    Array.isArray(memberUniverse.memberIds)
      ? memberUniverse.memberIds.filter(
          (value): value is string => typeof value === 'string',
        )
      : [],
  );
  const evidenceRefIds = canonicalAllocationLensMemberIds(
    Array.isArray(memberUniverse.evidenceRefIds)
      ? memberUniverse.evidenceRefIds.filter(
          (value): value is string => typeof value === 'string',
        )
      : [],
  );
  const expectedMemberIds = expectation.memberIds
    ? canonicalAllocationLensMemberIds(expectation.memberIds)
    : null;
  if (
    content.schemaVersion !== artifact.schemaVersion ||
    source.sourceClass !== artifact.sourceClass ||
    source.sourceRef !== artifact.sourceRef ||
    source.ingestJobId !== artifact.ingestJobId ||
    source.sourceSystemRef !== artifact.sourceSystemRef ||
    memberUniverse.schemaVersion !==
      ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION ||
    memberUniverse.tenantId !== expectation.tenantId ||
    artifact.tenantId !== expectation.tenantId ||
    artifact.scopeRootNodeId !== null ||
    memberUniverse.scopeKind !== expectation.scopeKind ||
    memberUniverse.scopeId !== expectation.scopeId ||
    memberUniverse.leverFamilyId !== expectation.leverFamilyId.trim() ||
    memberUniverse.comparisonDimension !== expectation.comparisonDimension ||
    memberUniverse.memberKey !== 'gl_account' ||
    memberUniverse.completeness !== 'complete' ||
    memberUniverse.sourceAsOf !== provenance.sourceAsOf ||
    !sameAllocationLensInstant(provenance.sourceAsOf, expectation.sourceAsOf) ||
    !artifact.reportingCutAsOf ||
    !sameAllocationLensInstant(
      artifact.reportingCutAsOf,
      provenance.sourceAsOf,
    ) ||
    memberUniverse.reportingCutAsOf !== expectation.reportingCutAsOf ||
    memberUniverse.timeframe !== expectation.timeframe ||
    artifactMemberIds.length === 0 ||
    (expectedMemberIds !== null &&
      !sameCanonicalJson(artifactMemberIds, expectedMemberIds)) ||
    (expectation.memberCount !== undefined &&
      artifactMemberIds.length !== expectation.memberCount) ||
    memberUniverse.memberDigest !==
      allocationLensMemberDigest(artifactMemberIds) ||
    (expectation.memberDigest !== undefined &&
      memberUniverse.memberDigest !== expectation.memberDigest) ||
    !sameCanonicalJson(evidenceRefIds, provenance.evidenceRefIds) ||
    evidenceRefIds.some(
      (evidenceRefId) => !expectation.evidenceRefIds.includes(evidenceRefId),
    )
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: `${expectation.side} member universe does not reconcile to tenant, scope, cut, or artifact-derived members.`,
    };
  }
  return null;
}

function validateAllocationLensExclusionInputAgainstEvidence(
  input: Readonly<AllocationLensExclusionReceiptInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const sides = [
    {
      side: 'left',
      leverFamilyId: input.pairIdentity?.leftLeverFamilyId,
      memberIds: input.leftMemberIds,
      provenance: input.memberUniverseProvenance?.left,
    },
    {
      side: 'right',
      leverFamilyId: input.pairIdentity?.rightLeverFamilyId,
      memberIds: input.rightMemberIds,
      provenance: input.memberUniverseProvenance?.right,
    },
  ] as const;

  for (const side of sides) {
    const block = validateAllocationLensMemberUniverseEvidence(
      {
        side: side.side,
        tenantId: input.tenantId,
        scopeKind: input.scopeKind,
        scopeId: input.scopeId,
        leverFamilyId: side.leverFamilyId,
        comparisonDimension: input.comparisonDimension,
        sourceAsOf: input.sourceAsOf,
        reportingCutAsOf: input.reportingCutAsOf,
        timeframe: input.timeframe,
        provenance: side.provenance,
        evidenceRefIds: input.evidenceRefIds,
        memberIds: side.memberIds,
      },
      artifacts,
    );
    if (block) return block;
  }

  if (
    input.memberUniverseProvenance.left.artifactId ===
    input.memberUniverseProvenance.right.artifactId
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Exclusion receipt sides must derive from distinct member-universe artifacts.',
    };
  }
  return null;
}

type AllocationLensExclusionReceiptEvidenceView = Readonly<{
  tenantId: string;
  scopeKind: 'enterprise';
  scopeId: string;
  pairIdentity: Readonly<{
    leftLeverFamilyId: string;
    rightLeverFamilyId: string;
  }>;
  reportingCutAsOf: string;
  timeframe: string;
  sourceAsOf: string;
  comparisonDimension: 'atomic_p_and_l_action';
  comparedMemberUniverses: Readonly<{
    left: Readonly<{
      completeness: 'complete' | 'incomplete';
      memberCount: number;
      memberDigest: string;
      provenance: AllocationLensExclusionMemberUniverseProvenance;
    }>;
    right: Readonly<{
      completeness: 'complete' | 'incomplete';
      memberCount: number;
      memberDigest: string;
      provenance: AllocationLensExclusionMemberUniverseProvenance;
    }>;
  }>;
  evidenceRefIds: readonly string[];
}>;

export function validateAllocationLensExclusionReceiptAgainstEvidence(
  receipt: AllocationLensExclusionReceiptEvidenceView,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const sides = [
    {
      side: 'left',
      leverFamilyId: receipt.pairIdentity.leftLeverFamilyId,
      universe: receipt.comparedMemberUniverses.left,
    },
    {
      side: 'right',
      leverFamilyId: receipt.pairIdentity.rightLeverFamilyId,
      universe: receipt.comparedMemberUniverses.right,
    },
  ] as const;
  for (const side of sides) {
    if (side.universe.completeness !== 'complete') {
      return {
        reasonCode: 'missing_governed_evidence',
        message: `${side.side} member universe is incomplete.`,
      };
    }
    const block = validateAllocationLensMemberUniverseEvidence(
      {
        side: side.side,
        tenantId: receipt.tenantId,
        scopeKind: receipt.scopeKind,
        scopeId: receipt.scopeId,
        leverFamilyId: side.leverFamilyId,
        comparisonDimension: receipt.comparisonDimension,
        sourceAsOf: receipt.sourceAsOf,
        reportingCutAsOf: receipt.reportingCutAsOf,
        timeframe: receipt.timeframe,
        provenance: side.universe.provenance,
        evidenceRefIds: receipt.evidenceRefIds,
        memberCount: side.universe.memberCount,
        memberDigest: side.universe.memberDigest,
      },
      artifacts,
    );
    if (block) return block;
  }
  return receipt.comparedMemberUniverses.left.provenance.artifactId ===
    receipt.comparedMemberUniverses.right.provenance.artifactId
    ? {
        reasonCode: 'formula_reconciliation_failure',
        message: 'Exclusion receipt sides must derive from distinct member-universe artifacts.',
      }
    : null;
}

export function executeAllocationLensExclusionReceipt(
  input: AllocationLensExclusionReceiptInput,
): ApprovedFormulaResult<AllocationLensExclusionReceiptOutput> {
  if (!input || typeof input !== 'object') {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'allocation-lens exclusion receipt input is required',
    };
  }
  const requiredStrings = [
    input.receiptId,
    input.tenantId,
    input.scopeKind,
    input.scopeId,
    input.pairIdentity?.leftLeverFamilyId,
    input.pairIdentity?.rightLeverFamilyId,
    input.reportingCutIdentity,
    input.reportingCutAsOf,
    input.timeframe,
    input.sourceAsOf,
    input.comparisonRuleId,
    input.comparisonRuleVersion,
  ];
  if (requiredStrings.some((value) => !allocationLensExclusionInputString(value))) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message:
        'allocation-lens exclusion receipt requires pair, scope, cut, source, and rule identity',
    };
  }
  const leftLeverFamilyId = input.pairIdentity.leftLeverFamilyId.trim();
  const rightLeverFamilyId = input.pairIdentity.rightLeverFamilyId.trim();
  if (
    input.comparisonDimension !== 'atomic_p_and_l_action' ||
    input.comparisonRuleId !==
      'allocation_lens_atomic_action_non_intersection' ||
    input.comparisonRuleVersion !== '1.0.0' ||
    input.scopeKind !== 'enterprise' ||
    input.scopeId.trim() !== input.tenantId.trim() ||
    leftLeverFamilyId === rightLeverFamilyId
  ) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message:
        'allocation-lens exclusion receipt requires two distinct lever families on the atomic P&L action dimension',
    };
  }
  if (
    input.memberUniverseCompleteness?.left !== 'complete' ||
    input.memberUniverseCompleteness?.right !== 'complete'
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message: 'allocation-lens exclusion receipt requires complete member universes',
    };
  }
  const leftMemberIds = canonicalAllocationLensMemberIds(input.leftMemberIds ?? []);
  const rightMemberIds = canonicalAllocationLensMemberIds(input.rightMemberIds ?? []);
  if (leftMemberIds.length === 0 || rightMemberIds.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message:
        'allocation-lens exclusion receipt requires non-empty compared member universes',
    };
  }
  const coveredClaimIds = (input.pairIdentity.coveredClaimIds ?? []).map(
    (claimId) => claimId.trim(),
  );
  if (
    coveredClaimIds.length !== 2 ||
    new Set(coveredClaimIds).size !== 2 ||
    coveredClaimIds.some((claimId) => !allocationLensExclusionInputString(claimId))
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'allocation-lens exclusion receipt requires exactly two distinct covered claim ids',
    };
  }
  const sourceHashes = (input.coveredClaimSourceValueHashes ?? [])
    .map((item) => ({
      claimId: item.claimId.trim(),
      sourceValueHash: item.sourceValueHash.trim(),
    }))
    .sort((left, right) => left.claimId.localeCompare(right.claimId));
  if (
    sourceHashes.length !== 2 ||
    new Set(sourceHashes.map((item) => item.claimId)).size !== 2 ||
    sourceHashes.some(
      (item) =>
        !coveredClaimIds.includes(item.claimId) ||
        !allocationLensExclusionInputString(item.sourceValueHash),
    )
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'allocation-lens exclusion receipt source hashes must bind the exact covered claims',
    };
  }
  const evidenceRefIds = canonicalAllocationLensMemberIds(input.evidenceRefIds ?? []);
  const leftProvenance = canonicalAllocationLensMemberUniverseProvenance(
    input.memberUniverseProvenance?.left,
  );
  const rightProvenance = canonicalAllocationLensMemberUniverseProvenance(
    input.memberUniverseProvenance?.right,
  );
  const provenanceEvidenceRefIds = canonicalAllocationLensMemberIds([
    ...(leftProvenance?.evidenceRefIds ?? []),
    ...(rightProvenance?.evidenceRefIds ?? []),
  ]);
  const sourceAsOf = input.sourceAsOf.trim();
  const reportingCutAsOf = input.reportingCutAsOf.trim();
  if (
    evidenceRefIds.length === 0 ||
    !leftProvenance ||
    !rightProvenance ||
    evidenceRefIds.length !== provenanceEvidenceRefIds.length ||
    evidenceRefIds.some(
      (evidenceRefId) => !provenanceEvidenceRefIds.includes(evidenceRefId),
    ) ||
    !/^\d{4}-\d{2}-\d{2}$/.test(reportingCutAsOf) ||
    !Number.isFinite(Date.parse(sourceAsOf))
  ) {
    return {
      status: 'blocked',
      reasonCode: 'missing_governed_evidence',
      message: 'allocation-lens exclusion receipt requires dated governed evidence',
    };
  }
  if (
    sourceAsOf.slice(0, 10) !== reportingCutAsOf ||
    !input.reportingCutIdentity.includes(reportingCutAsOf)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'allocation-lens exclusion receipt source as-of must reconcile to the reporting cut',
    };
  }
  const rightMembers = new Set(rightMemberIds);
  const overlapMemberIds = leftMemberIds.filter((memberId) => rightMembers.has(memberId));
  if (overlapMemberIds.length > 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message:
        'allocation-lens exclusion receipt cannot certify a non-empty member intersection',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      schemaVersion: 'allocation_lens_exclusion_receipt_v1',
      formulaId: 'allocation_lens_exclusion_receipt_v1',
      formulaVersion: '1.0.0',
      receiptId: input.receiptId.trim(),
      tenantId: input.tenantId.trim(),
      scopeKind: 'enterprise',
      scopeId: input.scopeId.trim(),
      pairIdentity: Object.freeze({
        leftLeverFamilyId,
        rightLeverFamilyId,
      }),
      coveredClaimIds: Object.freeze([
        coveredClaimIds[0].trim(),
        coveredClaimIds[1].trim(),
      ]) as readonly [string, string],
      reportingCutIdentity: input.reportingCutIdentity.trim(),
      reportingCutAsOf,
      timeframe: input.timeframe.trim(),
      sourceAsOf: input.sourceAsOf.trim(),
      comparisonDimension: 'atomic_p_and_l_action',
      comparisonRuleId: input.comparisonRuleId.trim(),
      comparisonRuleVersion: input.comparisonRuleVersion.trim(),
      comparedMemberUniverses: Object.freeze({
        left: Object.freeze({
          completeness: 'complete' as const,
          memberCount: leftMemberIds.length,
          memberDigest: allocationLensMemberDigest(leftMemberIds),
          provenance: leftProvenance,
        }),
        right: Object.freeze({
          completeness: 'complete' as const,
          memberCount: rightMemberIds.length,
          memberDigest: allocationLensMemberDigest(rightMemberIds),
          provenance: rightProvenance,
        }),
      }),
      overlapMemberCount: 0,
      overlapMemberDigest: allocationLensMemberDigest(overlapMemberIds),
      verdict: 'zero_overlap',
      overlapAmountUsd: 0,
      coveredClaimSourceValueHashes: Object.freeze(
        sourceHashes.map((item) =>
          Object.freeze({
            claimId: item.claimId.trim(),
            sourceValueHash: item.sourceValueHash.trim(),
          }),
        ),
      ),
      evidenceRefIds,
    }),
  };
}

export type AttributionNeverSumDerivedClaim = Readonly<{
  impactClaim: ImpactClaim;
  ledgerEntry: AttributionLedgerEntry;
  metricFamily: TargetMetricFamily;
  overlapDimensionKeys: readonly ApprovedOverlapDimensionKey[];
  governedSeriesId: string;
}>;

export type AttributionNeverSumRollupInput = Readonly<{
  derivedClaims: readonly AttributionNeverSumDerivedClaim[];
  deconflictionReceipt: AttributionDeconflictionReceipt;
  reportingCutAsOf?: string;
}>;

const ATTRIBUTION_DECONFLICTION_RESOLVER_VERSION =
  'ask_helix_pairwise_overlap_resolver_v1' as const;

function canonicalDerivedClaimSet(
  derivedClaims: readonly AttributionNeverSumDerivedClaim[],
): readonly AttributionNeverSumDerivedClaim[] {
  return [...derivedClaims].sort((left, right) =>
    left.impactClaim.claimId.localeCompare(right.impactClaim.claimId),
  );
}

function computeAttributionDerivedSetDigest(
  derivedClaims: readonly AttributionNeverSumDerivedClaim[],
): string {
  return createHash('sha256')
    .update(JSON.stringify(canonicalizeJson(canonicalDerivedClaimSet(derivedClaims))), 'utf8')
    .digest('hex');
}

function attributedFootprintAtomKeys(claim: AttributionNeverSumDerivedClaim): readonly string[] {
  const footprint = claim.impactClaim.poolFootprint;
  if (!footprint || footprint.footprintStatus !== 'attributed') return Object.freeze([]);
  return Object.freeze(
    footprint.atoms.map((atom) =>
      [atom.atomKind, atom.atomId, atom.scope, atom.timeWindow].join('|'),
    ),
  );
}

function findUnresolvedAttributionPairs(
  derivedClaims: readonly AttributionNeverSumDerivedClaim[],
): readonly string[] {
  const ordered = canonicalDerivedClaimSet(derivedClaims);
  const unresolved: string[] = [];
  for (const [leftIndex, left] of ordered.entries()) {
    const leftAtoms = new Set(attributedFootprintAtomKeys(left));
    for (const right of ordered.slice(leftIndex + 1)) {
      if (attributedFootprintAtomKeys(right).some((atom) => leftAtoms.has(atom))) {
        unresolved.push(`${left.impactClaim.claimId}:${right.impactClaim.claimId}`);
      }
    }
  }
  return Object.freeze(unresolved);
}

const ATTRIBUTION_DECONFLICTION_RESOLVER_IMPLEMENTATION_DIGEST = governedSourceDigest(
  'approved_formula_evaluator',
);

export function resolveAttributionDeconfliction(
  derivedClaims: readonly AttributionNeverSumDerivedClaim[],
  evidenceRefIds: readonly string[],
  asOf: string,
  receiptId = 'generated:attribution-deconfliction',
): AttributionDeconflictionReceipt {
  const includedClaimIds = canonicalDerivedClaimSet(derivedClaims).map(
    (item) => item.impactClaim.claimId,
  );
  const unresolvedPairs = findUnresolvedAttributionPairs(derivedClaims);
  const complete =
    derivedClaims.length > 0 &&
    new Set(includedClaimIds).size === includedClaimIds.length &&
    evidenceRefIds.length > 0 &&
    unresolvedPairs.length === 0;
  return Object.freeze({
    receiptId,
    resolverVersion: ATTRIBUTION_DECONFLICTION_RESOLVER_VERSION,
    resolverImplementationDigest: ATTRIBUTION_DECONFLICTION_RESOLVER_IMPLEMENTATION_DIGEST,
    derivedSetDigest: computeAttributionDerivedSetDigest(derivedClaims),
    status: complete ? 'complete' : unresolvedPairs.length > 0 ? 'partial' : 'blocked',
    canPublishFullCompositeTotal: complete,
    includedClaimIds: Object.freeze(includedClaimIds),
    unresolvedPairs,
    evidenceRefIds: Object.freeze([...evidenceRefIds]),
    asOf,
  });
}

function executeAttributionCounterfactual(
  input: ComputeCounterfactualAttributionInput,
): ApprovedFormulaResult<AttributedOutcome> {
  const result = computeCounterfactualAttribution(input);
  if (result.status === 'ok') {
    return { status: 'ok', output: result.outcome };
  }
  return {
    status: 'blocked',
    reasonCode:
      result.reasonCode === 'missing_counterfactual_basis'
        ? 'missing_formula_input'
        : 'invalid_formula_input',
    message: result.message,
  };
}

function executeAttributionNeverSumRollup(
  input: AttributionNeverSumRollupInput,
): ApprovedFormulaResult<AttributionRollup> {
  if (!Array.isArray(input?.derivedClaims) || input.derivedClaims.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'attribution never-sum rollup requires at least one derived ImpactClaim',
    };
  }
  const receipt = input.deconflictionReceipt;
  if (
    !receipt ||
    receipt.status !== 'complete' ||
    receipt.canPublishFullCompositeTotal !== true ||
    receipt.unresolvedPairs.length > 0 ||
    receipt.evidenceRefIds.length === 0
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'attribution rollup requires a complete de-confliction receipt with no unresolved pairs',
    };
  }

  const claimIds = input.derivedClaims.map((item) => item.impactClaim.claimId).sort();
  const includedClaimIds = [...receipt.includedClaimIds].sort();
  if (
    new Set(claimIds).size !== claimIds.length ||
    JSON.stringify(claimIds) !== JSON.stringify(includedClaimIds)
  ) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'de-confliction receipt claim ids do not match the derived set',
    };
  }

  for (const item of input.derivedClaims) {
    const validation = validateOverlapDimensionKeys(
      item.metricFamily,
      item.overlapDimensionKeys,
    );
    if (validation.status === 'blocked') {
      return {
        status: 'blocked',
        reasonCode: 'dimension_not_declared',
        message: `ImpactClaim overlap keys are not approved for ${item.metricFamily}: ${validation.invalidKeys.join(', ')}`,
      };
    }
    if (
      item.impactClaim.poolFootprint?.footprintStatus !== 'attributed' ||
      item.impactClaim.poolFootprint.atoms.length === 0 ||
      !item.impactClaim.valueBindingId
    ) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'every rolled-up ImpactClaim requires an attributed pool footprint and value binding',
      };
    }
    if (!sameCanonicalJson(item.overlapDimensionKeys, item.impactClaim.overlapDimensionKeys)) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'ImpactClaim overlap keys do not match the de-confliction input',
      };
    }
  }

  const resolvedReceipt = resolveAttributionDeconfliction(
    input.derivedClaims,
    receipt.evidenceRefIds,
    receipt.asOf,
    receipt.receiptId,
  );
  if (!sameCanonicalJson(resolvedReceipt, receipt)) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'de-confliction receipt is not bound to the complete derived claim set',
    };
  }

  if (new Set(input.derivedClaims.map((item) => item.governedSeriesId)).size !== 1) {
    return {
      status: 'blocked',
      reasonCode: 'never_blend_violation',
      message: 'attribution rollup cannot blend distinct governed series',
    };
  }

  return {
    status: 'ok',
    output: composeAttributionRollup(input.derivedClaims.map((item) => item.ledgerEntry)),
  };
}

const ATTRIBUTION_COUNTERFACTUAL_GOLDEN_INPUT: ComputeCounterfactualAttributionInput =
  Object.freeze({
    commitmentId: 'commitment-oracle-1',
    metricKey: 'purchased_transport_cost_usd',
    unit: 'usd',
    direction: 'decrease' as const,
    commitmentDate: '2026-01-15',
    valuePerUnitUsd: 1,
    annualizationFactor: 1,
    actual: Object.freeze({
      value: 80,
      asOf: '2026-06-30',
      evidenceRefIds: Object.freeze(['evidence:actual:oracle-1']),
    }),
    counterfactual: Object.freeze({
      method: 'momentum_projection' as const,
      formulaId: 'finance_momentum_projection',
      formulaVersion: '1.0.0',
      formulaRef: 'approved-formula:finance_momentum_projection@1.0.0',
      pinnedAsOf: '2026-01-15',
      horizonAsOf: '2026-06-30',
      projected: Object.freeze({
        conservativeValue: 90,
        baseValue: 100,
        stretchValue: 110,
      }),
      bandBasis: 'hand-walked approved momentum band',
      provenance: Object.freeze({
        sourceClass: 'connector-derived',
        evidenceRefIds: Object.freeze(['evidence:counterfactual:oracle-1']),
        sourceArtifactId: 'artifact:counterfactual:oracle-1',
      }),
    }),
    commitmentEvidenceRefIds: Object.freeze(['evidence:commitment:oracle-1']),
  });

const ATTRIBUTION_COUNTERFACTUAL_GOLDEN_EXPECTED: ApprovedFormulaResult<AttributedOutcome> =
  Object.freeze({
    status: 'ok' as const,
    output: Object.freeze({
      schemaVersion: 'attribution_counterfactual_v1' as const,
      commitmentId: 'commitment-oracle-1',
      metricKey: 'purchased_transport_cost_usd',
      unit: 'usd',
      direction: 'decrease' as const,
      commitmentDate: '2026-01-15',
      asOf: '2026-06-30',
      attributionWindow: Object.freeze({ from: '2026-01-15', to: '2026-06-30' }),
      actualValue: 80,
      valuePerUnitUsd: 1,
      annualizationFactor: 1,
      counterfactual: Object.freeze({
        method: 'momentum_projection' as const,
        formulaId: 'finance_momentum_projection',
        formulaVersion: '1.0.0',
        formulaRef: 'approved-formula:finance_momentum_projection@1.0.0',
        pinnedAsOf: '2026-01-15',
        horizonAsOf: '2026-06-30',
        projected: Object.freeze({ conservativeValue: 90, baseValue: 100, stretchValue: 110 }),
        bandBasis: 'hand-walked approved momentum band',
        provenance: Object.freeze({
          sourceClass: 'connector-derived',
          evidenceRefIds: Object.freeze(['evidence:counterfactual:oracle-1']),
          sourceArtifactId: 'artifact:counterfactual:oracle-1',
        }),
      }),
      attributedImpact: Object.freeze({ conservativeUsd: 10, baseUsd: 20, stretchUsd: 30 }),
      attributionBandBasis:
        "Attributed impact = actual − counterfactual, where the counterfactual is the do-nothing projection (approved-formula:finance_momentum_projection@1.0.0) pinned as-of 2026-01-15. The counterfactual's Conservative/Base/Stretch uncertainty is carried as the attribution honesty band.",
      reconciliation: Object.freeze({
        actualValue: 80,
        counterfactualBaseValue: 100,
        attributedBaseDeltaUnits: 20,
        reconstructedActual: 80,
        residual: 0,
        tolerance: 0.01,
        reconciles: true,
      }),
      confounds: Object.freeze([]),
      cleanWindow: Object.freeze({ from: '2026-01-15', to: '2026-06-30' }),
      attributionStatus: 'attributed' as const,
      provenance: Object.freeze({
        commitmentEvidenceRefIds: Object.freeze(['evidence:commitment:oracle-1']),
        actualsEvidenceRefIds: Object.freeze(['evidence:actual:oracle-1']),
        counterfactualEvidenceRefIds: Object.freeze(['evidence:counterfactual:oracle-1']),
      }),
      verdictSentence:
        'Improvement attributable to the action: $10 to $30 (base $20), scenario-labeled — actual 80 vs do-nothing 90–110 [decrease metric].',
    }),
  });

const ATTRIBUTION_NEVER_SUM_GOLDEN_LEDGER: readonly AttributionLedgerEntry[] = Object.freeze([
  Object.freeze({
    schemaVersion: 'attribution_ledger_entry_v1' as const,
    entryId: 'attribution-entry-oracle-1',
    createdAt: '2026-06-30T00:00:00.000Z',
    outcome: (ATTRIBUTION_COUNTERFACTUAL_GOLDEN_EXPECTED as ApprovedFormulaOkResult<AttributedOutcome>).output,
    commitmentRef: Object.freeze({
      decisionId: 'decision-oracle-1',
      commitmentId: 'commitment-oracle-1',
      curveVersion: '1.0.0',
      finalizationPackageId: 'package-oracle-1',
      gateReceiptKind: 'decision_ledger_activation',
    }),
    actualsRef: Object.freeze({
      sourceArtifactId: 'artifact:actual:oracle-1',
      asOf: '2026-06-30',
      sourceClass: 'connector-derived',
      evidenceRefIds: Object.freeze(['evidence:actual:oracle-1']),
    }),
    entryStatus: 'attributed' as const,
  }),
]);

const ATTRIBUTION_NEVER_SUM_GOLDEN_DERIVED_CLAIMS = Object.freeze([
  Object.freeze({
    impactClaim: Object.freeze({
      claimId: 'claim-oracle-1',
      sourceCapabilityId: 'ask_helix_symbolic_inference',
      metricKey: 'Cost',
      valueBindingId: 'binding-oracle-1',
      costPoolIds: Object.freeze(['cost-pool-ocean']),
      overlapDimensionKeys: Object.freeze(['costPoolIds']),
      timeWindow: '2026-H1',
      claimType: 'p_and_l' as const,
      poolFootprint: Object.freeze({
        footprintStatus: 'attributed' as const,
        atoms: Object.freeze([
          Object.freeze({
            atomKind: 'cost_pool' as const,
            atomId: 'cost-pool-ocean',
            scope: 'global-forwarding',
            timeWindow: '2026-H1',
            drawnUsd: 20,
          }),
        ]),
      }),
    }),
    ledgerEntry: ATTRIBUTION_NEVER_SUM_GOLDEN_LEDGER[0],
    metricFamily: 'cost' as const,
    overlapDimensionKeys: Object.freeze(['costPoolIds'] as const),
    governedSeriesId: 'derived_ebitda_from_operating_income',
  }),
] satisfies readonly AttributionNeverSumDerivedClaim[]);

const ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT: AttributionNeverSumRollupInput = Object.freeze({
  derivedClaims: ATTRIBUTION_NEVER_SUM_GOLDEN_DERIVED_CLAIMS,
  deconflictionReceipt: resolveAttributionDeconfliction(
    ATTRIBUTION_NEVER_SUM_GOLDEN_DERIVED_CLAIMS,
    ['evidence:deconfliction:oracle-1'],
    '2026-06-30',
    'deconfliction-receipt-oracle-1',
  ),
  reportingCutAsOf: '2026-06-30',
});

const ATTRIBUTION_NEVER_SUM_GOLDEN_EXPECTED: ApprovedFormulaResult<AttributionRollup> =
  Object.freeze({
    status: 'ok' as const,
    output: Object.freeze({
      schemaVersion: 'attribution_rollup_v1' as const,
      asOfRange: Object.freeze({ from: '2026-06-30', to: '2026-06-30' }),
      cleanAttributed: Object.freeze({
        count: 1,
        bandUsd: Object.freeze({ conservativeUsd: 10, baseUsd: 20, stretchUsd: 30 }),
        rows: Object.freeze([
          Object.freeze({
            entryId: 'attribution-entry-oracle-1',
            commitmentId: 'commitment-oracle-1',
            decisionId: 'decision-oracle-1',
            metricKey: 'purchased_transport_cost_usd',
            actualsSourceArtifactId: 'artifact:actual:oracle-1',
            asOf: '2026-06-30',
            attributedBaseUsd: 20,
            attributedBandUsd: Object.freeze({
              conservativeUsd: 10,
              baseUsd: 20,
              stretchUsd: 30,
            }),
            status: 'attributed' as const,
          }),
        ]),
      }),
      confoundedDeclared: Object.freeze({
        count: 0,
        bandUsd: Object.freeze({ conservativeUsd: 0, baseUsd: 0, stretchUsd: 0 }),
        rows: Object.freeze([]),
      }),
      totalRealizedBaseUsd: 20,
      attributionBandUsd: Object.freeze({ conservativeUsd: 10, baseUsd: 20, stretchUsd: 30 }),
      commitmentCount: 1,
      confoundedCount: 0,
      renewalAnswer:
        'Helix-governed action delivered $20 across 1 commitment ($10–$30 attribution band, governed); 0 confounded outcomes.',
    }),
  });

function validateAttributionCounterfactualInputAgainstEvidence(
  input: Readonly<ComputeCounterfactualAttributionInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const artifact = artifacts.find(
    (candidate) =>
      candidate.schemaVersion === 'attribution_counterfactual_evidence_v1' &&
      candidate.sourceSystemRef === ATTRIBUTION_COUNTERFACTUAL_EVIDENCE_SOURCE_SYSTEM_REF,
  );
  const content = asRecord(artifact?.contentJson);
  if (!artifact || !content) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Counterfactual attribution requires a governed commitment/actual/projection evidence artifact.',
    };
  }
  if (!sameCanonicalJson(content.counterfactualAttributionInput, input)) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Counterfactual attribution input does not reconcile to the governed evidence artifact.',
    };
  }
  return null;
}

function isPortfolioFormulaBlockedResult(
  value: unknown,
): value is ApprovedFormulaBlockedResult {
  const record = asRecord(value);
  return record?.status === 'blocked';
}

function portfolioFiniteNonNegative(
  value: unknown,
  field: string,
): number | ApprovedFormulaBlockedResult {
  if (!isFiniteNumber(value)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `${field} is required`,
    };
  }
  if (value < 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `${field} must be zero or greater`,
    };
  }
  return value;
}

function portfolioInteger(
  value: unknown,
  field: string,
): number | ApprovedFormulaBlockedResult {
  const parsed = portfolioFiniteNonNegative(value, field);
  if (typeof parsed !== 'number') return parsed;
  if (!Number.isInteger(parsed)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `${field} must be an integer`,
    };
  }
  return parsed;
}

function portfolioStringList(
  value: unknown,
  field: string,
  options: Readonly<{ allowEmpty: boolean }>,
): readonly string[] | ApprovedFormulaBlockedResult {
  if (!Array.isArray(value)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: `${field} is required`,
    };
  }
  const ids = value.map((item) => stringOrNull(item));
  if (ids.some((item) => item === null)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `${field} must contain non-empty ids`,
    };
  }
  const normalized = ids.filter((item): item is string => item !== null);
  if (!options.allowEmpty && normalized.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `${field} must not be empty`,
    };
  }
  if (new Set(normalized).size !== normalized.length) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `${field} must not contain duplicate ids`,
    };
  }
  return Object.freeze([...normalized]);
}

function executePortfolioCapacityBalance(
  input: PortfolioCapacityBalanceInput,
): ApprovedFormulaResult<PortfolioCapacityBalanceOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const capabilityId = stringOrNull(input.capabilityId);
  if (!capabilityId) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'capabilityId is required' };
  }
  const committedFte = portfolioFiniteNonNegative(input.committedFte, 'committedFte');
  if (typeof committedFte !== 'number') return committedFte;
  const availableFte = portfolioFiniteNonNegative(input.availableFte, 'availableFte');
  if (typeof availableFte !== 'number') return availableFte;
  return {
    status: 'ok',
    output: Object.freeze({
      capabilityId,
      committedFte,
      availableFte,
      capacityGapFte: roundTo(committedFte - availableFte, 6),
      utilizationPct:
        availableFte === 0 ? null : roundTo((committedFte / availableFte) * 100, 6),
    }),
  };
}

function executePortfolioValueDensity(
  input: PortfolioValueDensityInput,
): ApprovedFormulaResult<PortfolioValueDensityOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const initiativeId = stringOrNull(input.initiativeId);
  if (!initiativeId) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'initiativeId is required' };
  }
  const expectedValueUsd = portfolioFiniteNonNegative(input.expectedValueUsd, 'expectedValueUsd');
  if (typeof expectedValueUsd !== 'number') return expectedValueUsd;
  const requiredFte = portfolioFiniteNonNegative(input.requiredFte, 'requiredFte');
  if (typeof requiredFte !== 'number') return requiredFte;
  if (requiredFte === 0) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'requiredFte must be greater than zero',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      initiativeId,
      expectedValueUsd,
      requiredFte,
      valueDensityUsdPerFte: roundTo(expectedValueUsd / requiredFte, 6),
    }),
  };
}

function executePortfolioDependencyBlockScore(
  input: PortfolioDependencyBlockScoreInput,
): ApprovedFormulaResult<PortfolioDependencyBlockScoreOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const initiativeId = stringOrNull(input.initiativeId);
  if (!initiativeId) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'initiativeId is required' };
  }
  const unmetDependencyCount = portfolioInteger(input.unmetDependencyCount, 'unmetDependencyCount');
  if (typeof unmetDependencyCount !== 'number') return unmetDependencyCount;
  const totalDependencyCount = portfolioInteger(input.totalDependencyCount, 'totalDependencyCount');
  if (typeof totalDependencyCount !== 'number') return totalDependencyCount;
  if (unmetDependencyCount > totalDependencyCount) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'unmetDependencyCount cannot exceed totalDependencyCount',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      initiativeId,
      unmetDependencyCount,
      totalDependencyCount,
      dependencyBlocked: unmetDependencyCount > 0,
    }),
  };
}

function executePortfolioConstraintBinding(
  input: PortfolioConstraintBindingInput,
): ApprovedFormulaResult<PortfolioConstraintBindingOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const constraintId = stringOrNull(input.constraintId);
  if (!constraintId) {
    return { status: 'blocked', reasonCode: 'missing_formula_input', message: 'constraintId is required' };
  }
  const demandHours = portfolioFiniteNonNegative(input.demandHours, 'demandHours');
  if (typeof demandHours !== 'number') return demandHours;
  const supplyHours = portfolioFiniteNonNegative(input.supplyHours, 'supplyHours');
  if (typeof supplyHours !== 'number') return supplyHours;
  const policyAllocatedPct = portfolioFiniteNonNegative(input.policyAllocatedPct, 'policyAllocatedPct');
  if (typeof policyAllocatedPct !== 'number') return policyAllocatedPct;
  const policyCapPct = portfolioFiniteNonNegative(input.policyCapPct, 'policyCapPct');
  if (typeof policyCapPct !== 'number') return policyCapPct;
  const kernelScarcityPriceUsdPerHour = portfolioFiniteNonNegative(
    input.kernelScarcityPriceUsdPerHour,
    'kernelScarcityPriceUsdPerHour',
  );
  if (typeof kernelScarcityPriceUsdPerHour !== 'number') return kernelScarcityPriceUsdPerHour;
  if (policyAllocatedPct > 100 || policyCapPct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'policy percentages must be at most 100',
    };
  }
  const capacityShortfallHours = roundTo(Math.max(demandHours - supplyHours, 0), 6);
  const capacityBinding = capacityShortfallHours > 0;
  const policyBinding = policyAllocatedPct > policyCapPct;
  if (capacityBinding && kernelScarcityPriceUsdPerHour <= 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'a binding capacity bucket requires a positive kernel scarcity price',
    };
  }
  if (!capacityBinding && kernelScarcityPriceUsdPerHour !== 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'a non-binding capacity bucket must have zero kernel scarcity price',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      constraintId,
      capacityShortfallHours,
      capacityBinding,
      policyBinding,
      binding: capacityBinding || policyBinding,
      kernelScarcityPriceUsdPerHour,
    }),
  };
}

function executePortfolioPlanActualsReconcile(
  input: PortfolioPlanActualsReconcileInput,
): ApprovedFormulaResult<PortfolioPlanActualsReconcileOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const portfolioScopeId = stringOrNull(input.portfolioScopeId);
  const financeScopeId = stringOrNull(input.financeScopeId);
  if (!portfolioScopeId || !financeScopeId) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'portfolioScopeId and financeScopeId are required',
    };
  }
  if (portfolioScopeId !== financeScopeId) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'portfolio and finance scopes must match',
    };
  }
  const planSpendUsd = portfolioFiniteNonNegative(input.planSpendUsd, 'planSpendUsd');
  if (typeof planSpendUsd !== 'number') return planSpendUsd;
  const financeActualUsd = portfolioFiniteNonNegative(input.financeActualUsd, 'financeActualUsd');
  if (typeof financeActualUsd !== 'number') return financeActualUsd;
  if (!isFiniteNumber(input.reconciliationControlGapUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'reconciliationControlGapUsd is required',
    };
  }
  const mappedCostPoolCount = portfolioInteger(input.mappedCostPoolCount, 'mappedCostPoolCount');
  if (typeof mappedCostPoolCount !== 'number') return mappedCostPoolCount;
  const unmappedCostPoolCount = portfolioInteger(input.unmappedCostPoolCount, 'unmappedCostPoolCount');
  if (typeof unmappedCostPoolCount !== 'number') return unmappedCostPoolCount;
  if (unmappedCostPoolCount > 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'all portfolio cost pools must be mapped to finance actuals',
    };
  }
  const planActualsGapUsd = roundTo(planSpendUsd - financeActualUsd, 6);
  return {
    status: 'ok',
    output: Object.freeze({
      portfolioScopeId,
      planSpendUsd,
      financeActualUsd,
      planActualsGapUsd,
      residualAfterReconciliationUsd: roundTo(
        planActualsGapUsd - input.reconciliationControlGapUsd,
        6,
      ),
      mappedCostPoolCount,
    }),
  };
}

function executePortfolioReallocationValueDelta(
  input: PortfolioReallocationValueDeltaInput,
): ApprovedFormulaResult<PortfolioReallocationValueDeltaOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const baselineCapturedValueUsd = portfolioFiniteNonNegative(
    input.baselineCapturedValueUsd,
    'baselineCapturedValueUsd',
  );
  if (typeof baselineCapturedValueUsd !== 'number') return baselineCapturedValueUsd;
  const scenarioCapturedValueUsd = portfolioFiniteNonNegative(
    input.scenarioCapturedValueUsd,
    'scenarioCapturedValueUsd',
  );
  if (typeof scenarioCapturedValueUsd !== 'number') return scenarioCapturedValueUsd;
  if (!isFiniteNumber(input.firstOrderValueDeltaUsd)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'firstOrderValueDeltaUsd is required',
    };
  }
  const tolerancePct = portfolioFiniteNonNegative(input.tolerancePct, 'tolerancePct');
  if (typeof tolerancePct !== 'number') return tolerancePct;
  if (tolerancePct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'tolerancePct must be at most 100',
    };
  }
  if (typeof input.targetBucketBinding !== 'boolean') {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'targetBucketBinding is required',
    };
  }
  const addedInitiativeIds = portfolioStringList(input.addedInitiativeIds, 'addedInitiativeIds', {
    allowEmpty: true,
  });
  if (isPortfolioFormulaBlockedResult(addedInitiativeIds)) return addedInitiativeIds;
  const removedInitiativeIds = portfolioStringList(
    input.removedInitiativeIds,
    'removedInitiativeIds',
    { allowEmpty: true },
  );
  if (isPortfolioFormulaBlockedResult(removedInitiativeIds)) return removedInitiativeIds;
  const capturedValueDeltaUsd = roundTo(
    scenarioCapturedValueUsd - baselineCapturedValueUsd,
    6,
  );
  if (!input.targetBucketBinding && capturedValueDeltaUsd > 0) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'a non-binding target bucket cannot claim positive reallocation value',
    };
  }
  const reconciliationResidualUsd = roundTo(
    capturedValueDeltaUsd - input.firstOrderValueDeltaUsd,
    6,
  );
  const toleranceUsd =
    Math.max(Math.abs(input.firstOrderValueDeltaUsd), 1) * (tolerancePct / 100);
  const withinTolerance = Math.abs(reconciliationResidualUsd) <= toleranceUsd + NUMBER_TOLERANCE;
  if (!withinTolerance) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'reallocation value delta does not reconcile to the independent bound',
    };
  }
  return {
    status: 'ok',
    output: Object.freeze({
      capturedValueDeltaUsd,
      firstOrderValueDeltaUsd: input.firstOrderValueDeltaUsd,
      reconciliationResidualUsd,
      withinTolerance,
      targetBucketBinding: input.targetBucketBinding,
      addedInitiativeIds: Object.freeze([...addedInitiativeIds]),
      removedInitiativeIds: Object.freeze([...removedInitiativeIds]),
    }),
  };
}

function parsePortfolioCapacityCutRows(
  value: unknown,
): readonly PortfolioCapacityCutRow[] | ApprovedFormulaBlockedResult {
  if (!Array.isArray(value) || value.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'baselineSelected is required',
    };
  }
  const rows: PortfolioCapacityCutRow[] = [];
  const ids = new Set<string>();
  for (const raw of value) {
    const record = asRecord(raw);
    const initiativeId = stringOrNull(record?.initiativeId);
    const valueUsd = finiteNumberOrNull(record?.valueUsd);
    const valueDensityUsdPerFte = finiteNumberOrNull(record?.valueDensityUsdPerFte);
    if (!initiativeId || valueUsd === null || valueUsd < 0 || valueDensityUsdPerFte === null) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'baselineSelected rows require id, non-negative value, and finite density',
      };
    }
    if (ids.has(initiativeId)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'baselineSelected must not contain duplicate initiatives',
      };
    }
    ids.add(initiativeId);
    rows.push(Object.freeze({ initiativeId, valueUsd, valueDensityUsdPerFte }));
  }
  return Object.freeze(rows);
}

function executePortfolioCapacityCutRanking(
  input: PortfolioCapacityCutRankingInput,
): ApprovedFormulaResult<PortfolioCapacityCutRankingOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const cutPct = portfolioFiniteNonNegative(input.cutPct, 'cutPct');
  if (typeof cutPct !== 'number') return cutPct;
  if (cutPct <= 0 || cutPct > 100) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'cutPct must be greater than zero and at most 100',
    };
  }
  const baselineSelected = parsePortfolioCapacityCutRows(input.baselineSelected);
  if (isPortfolioFormulaBlockedResult(baselineSelected)) return baselineSelected;
  const scenarioSelectedInitiativeIds = portfolioStringList(
    input.scenarioSelectedInitiativeIds,
    'scenarioSelectedInitiativeIds',
    { allowEmpty: true },
  );
  if (isPortfolioFormulaBlockedResult(scenarioSelectedInitiativeIds)) {
    return scenarioSelectedInitiativeIds;
  }
  const baselineIds = new Set(baselineSelected.map((row) => row.initiativeId));
  if (scenarioSelectedInitiativeIds.some((initiativeId) => !baselineIds.has(initiativeId))) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'capacity-cut scenario selection must be a subset of the baseline selection',
    };
  }
  const scenarioIds = new Set(scenarioSelectedInitiativeIds);
  const belowLine = baselineSelected
    .filter((row) => !scenarioIds.has(row.initiativeId))
    .sort(
      (left, right) =>
        right.valueDensityUsdPerFte - left.valueDensityUsdPerFte ||
        left.initiativeId.localeCompare(right.initiativeId),
    );
  return {
    status: 'ok',
    output: Object.freeze({
      cutPct,
      belowLine: Object.freeze(belowLine),
      valueAtRiskUsd: roundTo(
        belowLine.reduce((total, row) => total + row.valueUsd, 0),
        6,
      ),
    }),
  };
}

function dependencyGraphHasCycle(
  initiativeIds: readonly string[],
  edges: readonly PortfolioDependencyEdge[],
): boolean {
  const adjacency = new Map<string, string[]>(initiativeIds.map((id) => [id, []]));
  for (const edge of edges) adjacency.get(edge.fromInitiativeId)?.push(edge.toInitiativeId);
  const visiting = new Set<string>();
  const visited = new Set<string>();
  function visit(initiativeId: string): boolean {
    if (visiting.has(initiativeId)) return true;
    if (visited.has(initiativeId)) return false;
    visiting.add(initiativeId);
    for (const successorId of adjacency.get(initiativeId) ?? []) {
      if (visit(successorId)) return true;
    }
    visiting.delete(initiativeId);
    visited.add(initiativeId);
    return false;
  }
  return initiativeIds.some((initiativeId) => visit(initiativeId));
}

function executePortfolioDependencySequencing(
  input: PortfolioDependencySequencingInput,
): ApprovedFormulaResult<PortfolioDependencySequencingOutput> {
  const inputBlock = portfolioFormulaInputBlock(input);
  if (inputBlock) return inputBlock;
  const requestedInitiativeIds = portfolioStringList(
    input.requestedInitiativeIds,
    'requestedInitiativeIds',
    {
    allowEmpty: false,
    },
  );
  if (isPortfolioFormulaBlockedResult(requestedInitiativeIds)) return requestedInitiativeIds;
  if (new Set(requestedInitiativeIds).size !== requestedInitiativeIds.length) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'requestedInitiativeIds must not contain duplicates',
    };
  }
  if (!Array.isArray(input.dependencyEdges) || !Array.isArray(input.kernelForecastRows)) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'dependencyEdges and kernelForecastRows are required',
    };
  }
  const requestedIds = new Set(requestedInitiativeIds);
  const forecastRows: PortfolioKernelForecastRow[] = [];
  const forecastIds = new Set<string>();
  for (const raw of input.kernelForecastRows) {
    const record = asRecord(raw);
    const initiativeId = stringOrNull(record?.initiativeId);
    const p50CompletionWeek = stringOrNull(record?.p50CompletionWeek);
    const p80CompletionWeek = stringOrNull(record?.p80CompletionWeek);
    const selected = record?.selected;
    if (
      !initiativeId ||
      !p50CompletionWeek ||
      !p80CompletionWeek ||
      typeof selected !== 'boolean'
    ) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message:
          'kernelForecastRows require initiativeId, p50CompletionWeek, p80CompletionWeek, and selected',
      };
    }
    if (!requestedIds.has(initiativeId) || forecastIds.has(initiativeId)) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'kernel forecast rows must map one-to-one to requested initiatives',
      };
    }
    forecastIds.add(initiativeId);
    forecastRows.push(
      Object.freeze({ initiativeId, p50CompletionWeek, p80CompletionWeek, selected }),
    );
  }
  if (forecastRows.length !== requestedInitiativeIds.length) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'kernel forecast rows must cover every requested initiative',
    };
  }
  const orderedInitiativeIds = forecastRows.map((row) => row.initiativeId);
  const orderIndex = new Map(
    orderedInitiativeIds.map((initiativeId, index) => [initiativeId, index]),
  );
  const dependencyEdges: PortfolioDependencyEdge[] = [];
  for (const raw of input.dependencyEdges) {
    const record = asRecord(raw);
    const fromInitiativeId = stringOrNull(record?.fromInitiativeId);
    const toInitiativeId = stringOrNull(record?.toInitiativeId);
    if (!fromInitiativeId || !toInitiativeId) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'dependency edges require fromInitiativeId and toInitiativeId',
      };
    }
    if (!orderIndex.has(fromInitiativeId) || !orderIndex.has(toInitiativeId)) {
      return {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'dependency edges must reference initiatives in the proposed order',
      };
    }
    dependencyEdges.push(Object.freeze({ fromInitiativeId, toInitiativeId }));
  }
  if (dependencyGraphHasCycle(orderedInitiativeIds, dependencyEdges)) {
    return {
      status: 'blocked',
      reasonCode: 'formula_reconciliation_failure',
      message: 'dependency graph contains a cycle',
    };
  }
  let dependencyViolationCount = 0;
  for (const edge of dependencyEdges) {
    if ((orderIndex.get(edge.fromInitiativeId) ?? -1) > (orderIndex.get(edge.toInitiativeId) ?? -1)) {
      dependencyViolationCount += 1;
    }
  }
  return {
    status: 'ok',
    output: Object.freeze({
      orderedInitiativeIds: Object.freeze(orderedInitiativeIds),
      forecastRows: Object.freeze(forecastRows),
      dependencyViolationCount,
      capacityViolationCount: 0,
      publishable: dependencyViolationCount === 0,
    }),
  };
}

function validatePortfolioFormulaInputAgainstEvidence(
  input: Readonly<{ sourceAsOf: string }>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  if (!Array.isArray(artifacts) || artifacts.length === 0) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Portfolio formula requires governed evidence artifacts.',
    };
  }
  const mismatched = artifacts.find(
    (artifact) =>
      !!artifact.reportingCutAsOf && artifact.reportingCutAsOf !== input.sourceAsOf,
  );
  if (mismatched) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Formula sourceAsOf must match every governed evidence reporting cut.',
    };
  }
  return null;
}

function validatePortfolioFormulaReconciliation<
  TInput extends { sourceAsOf: string },
  TOutput,
>(
  input: Readonly<TInput>,
  _output: Readonly<TOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  return validatePortfolioFormulaInputAgainstEvidence(input, artifacts);
}

function portfolioFormulaDefinition<TInput extends { sourceAsOf: string }, TOutput>(
  input: Readonly<{
    formulaId: string;
    displayName: string;
    description: string;
    inputSchemaVersion: string;
    outputSchemaVersion: string;
    contextKinds: readonly string[];
    execute: (input: TInput) => ApprovedFormulaResult<TOutput>;
    goldenTraces: readonly ApprovedFormulaGoldenTrace<TInput, TOutput>[];
    digestInputs: readonly (readonly [string, unknown])[];
  }>,
): ApprovedFormulaDefinition<TInput, TOutput> {
  const [expectedContextKind, ...additionalExpectedContextKinds] = input.contextKinds;
  if (!expectedContextKind) {
    throw new Error(`Portfolio formula ${input.formulaId} requires at least one context kind`);
  }
  return Object.freeze({
    formulaId: input.formulaId,
    version: '1.0.0',
    displayName: input.displayName,
    description: input.description,
    lifecycle: 'code_defined',
    formulaRuntimeVersion: 'approved_formula_runtime_v1',
    canonicalizerVersion: 'approved_formula_canonicalizer_v1',
    inputSchemaVersion: input.inputSchemaVersion,
    outputSchemaVersion: input.outputSchemaVersion,
    expectedContextKind,
    additionalExpectedContextKinds: Object.freeze(additionalExpectedContextKinds),
    approvedSourceSystemRefs: PORTFOLIO_CAPACITY_ALLOCATION_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs(input.digestInputs),
    goldenTraces: Object.freeze(input.goldenTraces.map((trace) => Object.freeze(trace))),
    validateInputAgainstEvidence: validatePortfolioFormulaInputAgainstEvidence,
    reconciliationRule: Object.freeze({
      kind: 'input_output_evidence_reconciliation',
      ruleId: `${input.formulaId}_reconciles_to_governed_evidence_v1`,
      description:
        'Reconcile the deterministic formula output to same-cut governed evidence before publication.',
      validate: validatePortfolioFormulaReconciliation,
    }),
    execute: input.execute,
  });
}

const portfolioCapacityBalanceDefinition = portfolioFormulaDefinition<
  PortfolioCapacityBalanceInput,
  PortfolioCapacityBalanceOutput
>({
  formulaId: 'portfolio_capacity_balance_v1',
  displayName: 'Portfolio capacity balance',
  description:
    'Projects committed and available delivery FTE into a same-scope capacity gap and utilization readout.',
  inputSchemaVersion: 'portfolio_capacity_balance_input_v1',
  outputSchemaVersion: 'portfolio_capacity_balance_output_v1',
  contextKinds: [
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[0],
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[1],
  ],
  execute: executePortfolioCapacityBalance,
  digestInputs: [
    ['executePortfolioCapacityBalance', executePortfolioCapacityBalance],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
    ['roundTo', roundTo],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_capacity_balance:overcommitted:v1',
      input: {
        capabilityId: 'data-engineering',
        committedFte: 12,
        availableFte: 10,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          capabilityId: 'data-engineering',
          committedFte: 12,
          availableFte: 10,
          capacityGapFte: 2,
          utilizationPct: 120,
        },
      },
    },
    {
      traceId: 'portfolio_capacity_balance:zero-supply:v1',
      input: {
        capabilityId: 'data-engineering',
        committedFte: 3,
        availableFte: 0,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          capabilityId: 'data-engineering',
          committedFte: 3,
          availableFte: 0,
          capacityGapFte: 3,
          utilizationPct: null,
        },
      },
    },
  ],
});

const portfolioValueDensityDefinition = portfolioFormulaDefinition<
  PortfolioValueDensityInput,
  PortfolioValueDensityOutput
>({
  formulaId: 'value_density_v1',
  displayName: 'Portfolio value density',
  description:
    'Projects governed initiative value and required delivery FTE into value per FTE without scoring unbound initiatives.',
  inputSchemaVersion: 'portfolio_value_density_input_v1',
  outputSchemaVersion: 'portfolio_value_density_output_v1',
  contextKinds: [
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[0],
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[3],
  ],
  execute: executePortfolioValueDensity,
  digestInputs: [
    ['executePortfolioValueDensity', executePortfolioValueDensity],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
    ['roundTo', roundTo],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_value_density:bound:v1',
      input: {
        initiativeId: 'initiative-a',
        expectedValueUsd: 1000000,
        requiredFte: 4,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          initiativeId: 'initiative-a',
          expectedValueUsd: 1000000,
          requiredFte: 4,
          valueDensityUsdPerFte: 250000,
        },
      },
    },
    {
      traceId: 'portfolio_value_density:blocked-zero-demand:v1',
      input: {
        initiativeId: 'initiative-a',
        expectedValueUsd: 100,
        requiredFte: 0,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'requiredFte must be greater than zero',
      },
    },
  ],
});

const portfolioDependencyBlockScoreDefinition = portfolioFormulaDefinition<
  PortfolioDependencyBlockScoreInput,
  PortfolioDependencyBlockScoreOutput
>({
  formulaId: 'dependency_block_score_v1',
  displayName: 'Portfolio dependency block score',
  description:
    'Projects the governed unmet dependency count for an initiative and blocks inconsistent totals.',
  inputSchemaVersion: 'portfolio_dependency_block_score_input_v1',
  outputSchemaVersion: 'portfolio_dependency_block_score_output_v1',
  contextKinds: [PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[2]],
  execute: executePortfolioDependencyBlockScore,
  digestInputs: [
    ['executePortfolioDependencyBlockScore', executePortfolioDependencyBlockScore],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioInteger', portfolioInteger],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_dependency_block_score:blocked:v1',
      input: {
        initiativeId: 'initiative-b',
        unmetDependencyCount: 2,
        totalDependencyCount: 3,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          initiativeId: 'initiative-b',
          unmetDependencyCount: 2,
          totalDependencyCount: 3,
          dependencyBlocked: true,
        },
      },
    },
    {
      traceId: 'portfolio_dependency_block_score:reconciliation-block:v1',
      input: {
        initiativeId: 'initiative-b',
        unmetDependencyCount: 4,
        totalDependencyCount: 3,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'unmetDependencyCount cannot exceed totalDependencyCount',
      },
    },
  ],
});

const portfolioConstraintBindingDefinition = portfolioFormulaDefinition<
  PortfolioConstraintBindingInput,
  PortfolioConstraintBindingOutput
>({
  formulaId: 'constraint_binding_v1',
  displayName: 'Portfolio constraint binding',
  description:
    'Identifies binding capacity and policy constraints from governed demand, supply, and caps while retaining the kernel scarcity price as a separate pressure signal.',
  inputSchemaVersion: 'portfolio_constraint_binding_input_v1',
  outputSchemaVersion: 'portfolio_constraint_binding_output_v1',
  contextKinds: [
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[0],
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[1],
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[2],
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[3],
  ],
  execute: executePortfolioConstraintBinding,
  digestInputs: [
    ['executePortfolioConstraintBinding', executePortfolioConstraintBinding],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_constraint_binding:capacity-and-policy:v1',
      input: {
        constraintId: 'data-engineering:2026-W28',
        demandHours: 120,
        supplyHours: 100,
        policyAllocatedPct: 80,
        policyCapPct: 75,
        kernelScarcityPriceUsdPerHour: 120,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          constraintId: 'data-engineering:2026-W28',
          capacityShortfallHours: 20,
          capacityBinding: true,
          policyBinding: true,
          binding: true,
          kernelScarcityPriceUsdPerHour: 120,
        },
      },
    },
    {
      traceId: 'portfolio_constraint_binding:non-binding-positive-price:v1',
      input: {
        constraintId: 'ops-pmo:2026-W28',
        demandHours: 80,
        supplyHours: 100,
        policyAllocatedPct: 50,
        policyCapPct: 75,
        kernelScarcityPriceUsdPerHour: 80,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'a non-binding capacity bucket must have zero kernel scarcity price',
      },
    },
  ],
});

const portfolioPlanActualsReconcileDefinition = portfolioFormulaDefinition<
  PortfolioPlanActualsReconcileInput,
  PortfolioPlanActualsReconcileOutput
>({
  formulaId: 'plan_actuals_reconcile_v1',
  displayName: 'Portfolio plan-to-actuals reconciliation',
  description:
    'Reconciles same-scope portfolio plan funding to finance-approved actuals and exposes the residual.',
  inputSchemaVersion: 'portfolio_plan_actuals_reconcile_input_v1',
  outputSchemaVersion: 'portfolio_plan_actuals_reconcile_output_v1',
  contextKinds: [
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[0],
    PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS[4],
  ],
  execute: executePortfolioPlanActualsReconcile,
  digestInputs: [
    ['executePortfolioPlanActualsReconcile', executePortfolioPlanActualsReconcile],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
    ['portfolioInteger', portfolioInteger],
    ['roundTo', roundTo],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_plan_actuals_reconcile:balanced-control:v1',
      input: {
        portfolioScopeId: 'portfolio-1',
        financeScopeId: 'portfolio-1',
        planSpendUsd: 120,
        financeActualUsd: 100,
        reconciliationControlGapUsd: 20,
        mappedCostPoolCount: 2,
        unmappedCostPoolCount: 0,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          portfolioScopeId: 'portfolio-1',
          planSpendUsd: 120,
          financeActualUsd: 100,
          planActualsGapUsd: 20,
          residualAfterReconciliationUsd: 0,
          mappedCostPoolCount: 2,
        },
      },
    },
    {
      traceId: 'portfolio_plan_actuals_reconcile:blocked-scope:v1',
      input: {
        portfolioScopeId: 'portfolio-1',
        financeScopeId: 'portfolio-2',
        planSpendUsd: 120,
        financeActualUsd: 100,
        reconciliationControlGapUsd: 20,
        mappedCostPoolCount: 2,
        unmappedCostPoolCount: 0,
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'portfolio and finance scopes must match',
      },
    },
  ],
});

const portfolioReallocationValueDeltaDefinition = portfolioFormulaDefinition<
  PortfolioReallocationValueDeltaInput,
  PortfolioReallocationValueDeltaOutput
>({
  formulaId: 'reallocation_value_delta_v1',
  displayName: 'Portfolio reallocation value delta',
  description:
    'Projects baseline and scenario kernel captured value into a reconciled delta with an explicit non-binding guard.',
  inputSchemaVersion: 'portfolio_reallocation_value_delta_input_v1',
  outputSchemaVersion: 'portfolio_reallocation_value_delta_output_v1',
  contextKinds: PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS,
  execute: executePortfolioReallocationValueDelta,
  digestInputs: [
    ['executePortfolioReallocationValueDelta', executePortfolioReallocationValueDelta],
    ['isPortfolioFormulaBlockedResult', isPortfolioFormulaBlockedResult],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
    ['portfolioStringList', portfolioStringList],
    ['roundTo', roundTo],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_reallocation_value_delta:reconciled:v1',
      input: {
        baselineCapturedValueUsd: 100,
        scenarioCapturedValueUsd: 130,
        firstOrderValueDeltaUsd: 30,
        targetBucketBinding: true,
        tolerancePct: 5,
        addedInitiativeIds: ['initiative-b'],
        removedInitiativeIds: ['initiative-a'],
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          capturedValueDeltaUsd: 30,
          firstOrderValueDeltaUsd: 30,
          reconciliationResidualUsd: 0,
          withinTolerance: true,
          targetBucketBinding: true,
          addedInitiativeIds: ['initiative-b'],
          removedInitiativeIds: ['initiative-a'],
        },
      },
    },
    {
      traceId: 'portfolio_reallocation_value_delta:blocked-non-binding:v1',
      input: {
        baselineCapturedValueUsd: 100,
        scenarioCapturedValueUsd: 110,
        firstOrderValueDeltaUsd: 10,
        targetBucketBinding: false,
        tolerancePct: 5,
        addedInitiativeIds: [],
        removedInitiativeIds: [],
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'formula_reconciliation_failure',
        message: 'a non-binding target bucket cannot claim positive reallocation value',
      },
    },
  ],
});

const portfolioCapacityCutRankingDefinition = portfolioFormulaDefinition<
  PortfolioCapacityCutRankingInput,
  PortfolioCapacityCutRankingOutput
>({
  formulaId: 'capacity_cut_ranking_v1',
  displayName: 'Portfolio capacity-cut ranking',
  description:
    'Projects the baseline-to-reduced-supply kernel selection diff into a value-density-ranked below-line set.',
  inputSchemaVersion: 'portfolio_capacity_cut_ranking_input_v1',
  outputSchemaVersion: 'portfolio_capacity_cut_ranking_output_v1',
  contextKinds: PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS,
  execute: executePortfolioCapacityCutRanking,
  digestInputs: [
    ['executePortfolioCapacityCutRanking', executePortfolioCapacityCutRanking],
    ['parsePortfolioCapacityCutRows', parsePortfolioCapacityCutRows],
    ['isPortfolioFormulaBlockedResult', isPortfolioFormulaBlockedResult],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioFiniteNonNegative', portfolioFiniteNonNegative],
    ['portfolioStringList', portfolioStringList],
    ['asRecord', asRecord],
    ['finiteNumberOrNull', finiteNumberOrNull],
    ['roundTo', roundTo],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_capacity_cut_ranking:below-line:v1',
      input: {
        cutPct: 15,
        baselineSelected: [
          { initiativeId: 'initiative-a', valueUsd: 100, valueDensityUsdPerFte: 50 },
          { initiativeId: 'initiative-b', valueUsd: 50, valueDensityUsdPerFte: 10 },
        ],
        scenarioSelectedInitiativeIds: ['initiative-a'],
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          cutPct: 15,
          belowLine: [
            { initiativeId: 'initiative-b', valueUsd: 50, valueDensityUsdPerFte: 10 },
          ],
          valueAtRiskUsd: 50,
        },
      },
    },
    {
      traceId: 'portfolio_capacity_cut_ranking:blocked-zero-cut:v1',
      input: {
        cutPct: 0,
        baselineSelected: [
          { initiativeId: 'initiative-a', valueUsd: 100, valueDensityUsdPerFte: 50 },
        ],
        scenarioSelectedInitiativeIds: ['initiative-a'],
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: 'cutPct must be greater than zero and at most 100',
      },
    },
  ],
});

const portfolioDependencySequencingDefinition = portfolioFormulaDefinition<
  PortfolioDependencySequencingInput,
  PortfolioDependencySequencingOutput
>({
  formulaId: 'dependency_sequencing_v1',
  displayName: 'Portfolio dependency sequencing validation',
  description:
    'Projects the Portfolio Planner kernel forecast and validates that its initiative order respects governed dependencies.',
  inputSchemaVersion: 'portfolio_dependency_sequencing_input_v1',
  outputSchemaVersion: 'portfolio_dependency_sequencing_output_v1',
  contextKinds: PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS,
  execute: executePortfolioDependencySequencing,
  digestInputs: [
    ['executePortfolioDependencySequencing', executePortfolioDependencySequencing],
    ['dependencyGraphHasCycle', dependencyGraphHasCycle],
    ['isPortfolioFormulaBlockedResult', isPortfolioFormulaBlockedResult],
    ['portfolioFormulaInputBlock', portfolioFormulaInputBlock],
    ['portfolioStringList', portfolioStringList],
    ['asRecord', asRecord],
    ['finiteNumberOrNull', finiteNumberOrNull],
  ],
  goldenTraces: [
    {
      traceId: 'portfolio_dependency_sequencing:publishable:v1',
      input: {
        requestedInitiativeIds: ['initiative-a', 'initiative-b'],
        kernelForecastRows: [
          {
            initiativeId: 'initiative-a',
            p50CompletionWeek: '2026-W28',
            p80CompletionWeek: '2026-W29',
            selected: true,
          },
          {
            initiativeId: 'initiative-b',
            p50CompletionWeek: '2026-W30',
            p80CompletionWeek: '2026-W31',
            selected: true,
          },
        ],
        dependencyEdges: [
          { fromInitiativeId: 'initiative-a', toInitiativeId: 'initiative-b' },
        ],
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          orderedInitiativeIds: ['initiative-a', 'initiative-b'],
          forecastRows: [
            {
              initiativeId: 'initiative-a',
              p50CompletionWeek: '2026-W28',
              p80CompletionWeek: '2026-W29',
              selected: true,
            },
            {
              initiativeId: 'initiative-b',
              p50CompletionWeek: '2026-W30',
              p80CompletionWeek: '2026-W31',
              selected: true,
            },
          ],
          dependencyViolationCount: 0,
          capacityViolationCount: 0,
          publishable: true,
        },
      },
    },
    {
      traceId: 'portfolio_dependency_sequencing:not-publishable:v1',
      input: {
        requestedInitiativeIds: ['initiative-a', 'initiative-b'],
        kernelForecastRows: [
          {
            initiativeId: 'initiative-b',
            p50CompletionWeek: '2026-W28',
            p80CompletionWeek: '2026-W29',
            selected: true,
          },
          {
            initiativeId: 'initiative-a',
            p50CompletionWeek: '2026-W30',
            p80CompletionWeek: '2026-W31',
            selected: true,
          },
        ],
        dependencyEdges: [
          { fromInitiativeId: 'initiative-a', toInitiativeId: 'initiative-b' },
        ],
        sourceAsOf: '2026-07-10T00:00:00.000Z',
      },
      expected: {
        status: 'ok',
        output: {
          orderedInitiativeIds: ['initiative-b', 'initiative-a'],
          forecastRows: [
            {
              initiativeId: 'initiative-b',
              p50CompletionWeek: '2026-W28',
              p80CompletionWeek: '2026-W29',
              selected: true,
            },
            {
              initiativeId: 'initiative-a',
              p50CompletionWeek: '2026-W30',
              p80CompletionWeek: '2026-W31',
              selected: true,
            },
          ],
          dependencyViolationCount: 1,
          capacityViolationCount: 0,
          publishable: false,
        },
      },
    },
  ],
});

function validateAttributionNeverSumInputAgainstEvidence(
  input: Readonly<AttributionNeverSumRollupInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const artifact = artifacts.find(
    (candidate) =>
      candidate.schemaVersion === 'attribution_deconfliction_receipt_v1' &&
      candidate.sourceSystemRef === ATTRIBUTION_DECONFLICTION_RECEIPT_SOURCE_SYSTEM_REF,
  );
  const content = asRecord(artifact?.contentJson);
  if (!artifact || !content) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Attribution rollup requires a governed de-confliction receipt artifact.',
    };
  }
  if (!sameCanonicalJson(content.neverSumRollupInput, input)) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Attribution rollup input does not reconcile to governed evidence.',
    };
  }
  return null;
}

function sameCanonicalJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(canonicalizeJson(left)) === JSON.stringify(canonicalizeJson(right));
}

function canonicalizeJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalizeJson);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, canonicalizeJson(nested)]),
    );
  }
  return value;
}

const attributionCounterfactualDefinition: ApprovedFormulaDefinition<
  ComputeCounterfactualAttributionInput,
  AttributedOutcome
> = Object.freeze({
  formulaId: 'attribution_counterfactual_v1',
  version: '1.0.0',
  displayName: 'Counterfactual attribution',
  description:
    'Computes a direction-aware attributed impact band from a counterfactual pinned at the commitment date.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'attribution_counterfactual_v1',
  outputSchemaVersion: 'attribution_counterfactual_v1',
  expectedContextKind: 'decision_ledger:attribution_counterfactual',
  approvedSourceSystemRefs: Object.freeze([
    ATTRIBUTION_COUNTERFACTUAL_EVIDENCE_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: Object.freeze([
    implementationDigestInput('executeAttributionCounterfactual', executeAttributionCounterfactual),
    ...getAttributionCounterfactualImplementationDigestInputs(),
    implementationDigestInput(
      'validateAttributionCounterfactualInputAgainstEvidence',
      validateAttributionCounterfactualInputAgainstEvidence,
    ),
    implementationDigestInput('sameCanonicalJson', sameCanonicalJson),
    implementationDigestInput('canonicalizeJson', canonicalizeJson),
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'attribution_counterfactual_v1:decrease:hand-walked:v1',
      input: ATTRIBUTION_COUNTERFACTUAL_GOLDEN_INPUT,
      expected: ATTRIBUTION_COUNTERFACTUAL_GOLDEN_EXPECTED,
    }),
    Object.freeze({
      traceId: 'attribution_counterfactual_v1:blocked:missing-basis:v1',
      input: Object.freeze({
        ...(JSON.parse(
          JSON.stringify(ATTRIBUTION_COUNTERFACTUAL_GOLDEN_INPUT),
        ) as ComputeCounterfactualAttributionInput),
        counterfactual: undefined,
      }) as unknown as ComputeCounterfactualAttributionInput,
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message:
          'Attribution requires a counterfactual (do-nothing) projection pinned as-of the commitment date.',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateAttributionCounterfactualInputAgainstEvidence,
  execute: executeAttributionCounterfactual,
});

function allocationLensExclusionGoldenInput(
  rightMemberIds: readonly string[],
): AllocationLensExclusionReceiptInput {
  return Object.freeze({
    receiptId: 'exclusion:carrier-segment:fy2025',
    tenantId: 'helix_retail',
    scopeKind: 'enterprise',
    scopeId: 'helix_retail',
    pairIdentity: Object.freeze({
      leftLeverFamilyId: 'carrier_renegotiation',
      rightLeverFamilyId: 'segment_margin_cleanup',
      coveredClaimIds: Object.freeze([
        'carrier_renegotiation:carrier-transportation-cost:carrier_cost_opportunity_usd',
        'segment_margin_cleanup:segment-margin-cleanup:segment_cleanup_operating_income_contribution_usd',
      ]) as readonly [string, string],
    }),
    reportingCutIdentity: 'helix_retail|FY2025|2025-12-31',
    reportingCutAsOf: '2025-12-31',
    timeframe: 'FY2025',
    sourceAsOf: '2025-12-31T00:00:00.000Z',
    comparisonDimension: 'atomic_p_and_l_action',
    comparisonRuleId: 'allocation_lens_atomic_action_non_intersection',
    comparisonRuleVersion: '1.0.0',
    leftMemberIds: Object.freeze(['carrier_buy_rate:lane-1:carrier-a']),
    rightMemberIds: Object.freeze([...rightMemberIds]),
    memberUniverseCompleteness: Object.freeze({
      left: 'complete',
      right: 'complete',
    }),
    memberUniverseProvenance: Object.freeze({
      left: Object.freeze({
        sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
        evidenceRefIds: Object.freeze(['ev-carrier']),
        sourceRef: 'carrier-member-universe:FY2025',
        artifactId: 'artifact-carrier-members',
        ingestJobId: 'ingest-carrier-members',
        sourceSystemRef: ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
        evidenceSchemaVersion: ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION,
        contextKind: ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
        sourceAsOf: '2025-12-31T00:00:00.000Z',
        contentDigest: 'a'.repeat(64),
      }),
      right: Object.freeze({
        sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
        evidenceRefIds: Object.freeze(['ev-segment']),
        sourceRef: 'segment-member-universe:FY2025',
        artifactId: 'artifact-segment-members',
        ingestJobId: 'ingest-segment-members',
        sourceSystemRef: ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
        evidenceSchemaVersion: ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION,
        contextKind: ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
        sourceAsOf: '2025-12-31T00:00:00.000Z',
        contentDigest: 'b'.repeat(64),
      }),
    }),
    coveredClaimSourceValueHashes: Object.freeze([
      Object.freeze({
        claimId:
          'carrier_renegotiation:carrier-transportation-cost:carrier_cost_opportunity_usd',
        sourceValueHash: 'carrier-source-hash',
      }),
      Object.freeze({
        claimId:
          'segment_margin_cleanup:segment-margin-cleanup:segment_cleanup_operating_income_contribution_usd',
        sourceValueHash: 'segment-source-hash',
      }),
    ]),
    evidenceRefIds: Object.freeze(['ev-carrier', 'ev-segment']),
  });
}

const ALLOCATION_LENS_EXCLUSION_GOLDEN_INPUT = allocationLensExclusionGoldenInput(
  Object.freeze(['segment_cleanup:all_other_and_corporate']),
);

const ALLOCATION_LENS_EXCLUSION_GOLDEN_EXPECTED: ApprovedFormulaResult<
  AllocationLensExclusionReceiptOutput
> = Object.freeze({
  status: 'ok' as const,
  output: Object.freeze({
    schemaVersion: 'allocation_lens_exclusion_receipt_v1' as const,
    formulaId: 'allocation_lens_exclusion_receipt_v1' as const,
    formulaVersion: '1.0.0' as const,
    receiptId: 'exclusion:carrier-segment:fy2025',
    tenantId: 'helix_retail',
    scopeKind: 'enterprise',
    scopeId: 'helix_retail',
    pairIdentity: Object.freeze({
      leftLeverFamilyId: 'carrier_renegotiation',
      rightLeverFamilyId: 'segment_margin_cleanup',
    }),
    coveredClaimIds: Object.freeze([
      'carrier_renegotiation:carrier-transportation-cost:carrier_cost_opportunity_usd',
      'segment_margin_cleanup:segment-margin-cleanup:segment_cleanup_operating_income_contribution_usd',
    ]) as readonly [string, string],
    reportingCutIdentity: 'helix_retail|FY2025|2025-12-31',
    reportingCutAsOf: '2025-12-31',
    timeframe: 'FY2025',
    sourceAsOf: '2025-12-31T00:00:00.000Z',
    comparisonDimension: 'atomic_p_and_l_action' as const,
    comparisonRuleId: 'allocation_lens_atomic_action_non_intersection',
    comparisonRuleVersion: '1.0.0',
    comparedMemberUniverses: Object.freeze({
      left: Object.freeze({
        completeness: 'complete' as const,
        memberCount: 1,
        memberDigest:
          '05a340cd5308f752ecea0d4b127214214bb5d798b4860449ff840f810e435f01',
        provenance: Object.freeze({
          sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
          evidenceRefIds: Object.freeze(['ev-carrier']),
          sourceRef: 'carrier-member-universe:FY2025',
          artifactId: 'artifact-carrier-members',
          ingestJobId: 'ingest-carrier-members',
          sourceSystemRef: ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
          evidenceSchemaVersion: ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION,
          contextKind: ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
          sourceAsOf: '2025-12-31T00:00:00.000Z',
          contentDigest: 'a'.repeat(64),
        }),
      }),
      right: Object.freeze({
        completeness: 'complete' as const,
        memberCount: 1,
        memberDigest:
          '94125f289b00273c4a6d13fcc23c1584904dfa3d40815b4a9854082a50c6aae0',
        provenance: Object.freeze({
          sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
          evidenceRefIds: Object.freeze(['ev-segment']),
          sourceRef: 'segment-member-universe:FY2025',
          artifactId: 'artifact-segment-members',
          ingestJobId: 'ingest-segment-members',
          sourceSystemRef: ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
          evidenceSchemaVersion: ALLOCATION_LENS_MEMBER_UNIVERSE_SCHEMA_VERSION,
          contextKind: ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
          sourceAsOf: '2025-12-31T00:00:00.000Z',
          contentDigest: 'b'.repeat(64),
        }),
      }),
    }),
    overlapMemberCount: 0 as const,
    overlapMemberDigest:
      '4f53cda18c2baa0c0354bb5f9a3ecbe5ed12ab4d8e11ba873c2f11161202b945',
    verdict: 'zero_overlap' as const,
    overlapAmountUsd: 0 as const,
    coveredClaimSourceValueHashes: Object.freeze([
      Object.freeze({
        claimId:
          'carrier_renegotiation:carrier-transportation-cost:carrier_cost_opportunity_usd',
        sourceValueHash: 'carrier-source-hash',
      }),
      Object.freeze({
        claimId:
          'segment_margin_cleanup:segment-margin-cleanup:segment_cleanup_operating_income_contribution_usd',
        sourceValueHash: 'segment-source-hash',
      }),
    ]),
    evidenceRefIds: Object.freeze(['ev-carrier', 'ev-segment']),
  }),
});

const allocationLensExclusionReceiptDefinition: ApprovedFormulaDefinition<
  AllocationLensExclusionReceiptInput,
  AllocationLensExclusionReceiptOutput
> = Object.freeze({
  formulaId: 'allocation_lens_exclusion_receipt_v1',
  version: '1.0.0',
  displayName: 'Allocation-lens zero-overlap exclusion receipt',
  description:
    'Certifies an exact pair as zero-overlap only when complete member universes on a common governed atomic P&L action lineage have an empty intersection.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'allocation_lens_exclusion_receipt_input_v1',
  outputSchemaVersion: 'allocation_lens_exclusion_receipt_v1',
  expectedContextKind: ALLOCATION_LENS_MEMBER_UNIVERSE_CONTEXT_KIND,
  approvedSourceSystemRefs: Object.freeze([
    ALLOCATION_LENS_MEMBER_UNIVERSE_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: Object.freeze([
    implementationDigestInput('executeAllocationLensExclusionReceipt', executeAllocationLensExclusionReceipt),
    implementationDigestInput('canonicalAllocationLensMemberIds', canonicalAllocationLensMemberIds),
    implementationDigestInput('allocationLensMemberDigest', allocationLensMemberDigest),
    implementationDigestInput('allocationLensExclusionInputString', allocationLensExclusionInputString),
    implementationDigestInput(
      'canonicalAllocationLensMemberUniverseProvenance',
      canonicalAllocationLensMemberUniverseProvenance,
    ),
    implementationDigestInput('allocationLensEvidenceContentDigest', allocationLensEvidenceContentDigest),
    implementationDigestInput('sameAllocationLensInstant', sameAllocationLensInstant),
    implementationDigestInput('sameCanonicalJson', sameCanonicalJson),
    implementationDigestInput('asRecord', asRecord),
    implementationDigestInput(
      'validateAllocationLensMemberUniverseEvidence',
      validateAllocationLensMemberUniverseEvidence,
    ),
    implementationDigestInput(
      'validateAllocationLensExclusionInputAgainstEvidence',
      validateAllocationLensExclusionInputAgainstEvidence,
    ),
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'allocation_lens_exclusion_receipt_v1:zero-overlap:v1',
      input: ALLOCATION_LENS_EXCLUSION_GOLDEN_INPUT,
      expected: ALLOCATION_LENS_EXCLUSION_GOLDEN_EXPECTED,
    }),
    Object.freeze({
      traceId: 'allocation_lens_exclusion_receipt_v1:blocked:intersection:v1',
      input: allocationLensExclusionGoldenInput(
        Object.freeze([
          'carrier_buy_rate:lane-1:carrier-a',
          'segment_cleanup:all_other_and_corporate',
        ]),
      ),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'formula_reconciliation_failure' as const,
        message:
          'allocation-lens exclusion receipt cannot certify a non-empty member intersection',
      }),
    }),
  ]),
  validateInputAgainstEvidence:
    validateAllocationLensExclusionInputAgainstEvidence,
  execute: executeAllocationLensExclusionReceipt,
});

const attributionNeverSumRollupDefinition: ApprovedFormulaDefinition<
  AttributionNeverSumRollupInput,
  AttributionRollup
> = Object.freeze({
  formulaId: 'attribution_never_sum_rollup_v1',
  version: '1.0.0',
  displayName: 'Attribution never-sum rollup',
  description:
    'Sums only clean, already de-conflicted attribution rows and holds confounded rows on a separate non-realized line.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'attribution_never_sum_rollup_input_v1',
  outputSchemaVersion: 'attribution_rollup_v1',
  expectedContextKind: 'decision_ledger:attribution_rollup',
  approvedSourceSystemRefs: Object.freeze([
    ATTRIBUTION_DECONFLICTION_RECEIPT_SOURCE_SYSTEM_REF,
  ]),
  implementationDigestInputs: Object.freeze([
    implementationDigestInput('executeAttributionNeverSumRollup', executeAttributionNeverSumRollup),
    ...getAttributionRollupImplementationDigestInputs(),
    implementationDigestInput('resolveAttributionDeconfliction', resolveAttributionDeconfliction),
    implementationDigestInput('computeAttributionDerivedSetDigest', computeAttributionDerivedSetDigest),
    implementationDigestInput('findUnresolvedAttributionPairs', findUnresolvedAttributionPairs),
    implementationDigestInput('attributedFootprintAtomKeys', attributedFootprintAtomKeys),
    implementationDigestInput('validateOverlapDimensionKeys', validateOverlapDimensionKeys),
    implementationDigestInput(
      'validateAttributionNeverSumInputAgainstEvidence',
      validateAttributionNeverSumInputAgainstEvidence,
    ),
    implementationDigestInput('sameCanonicalJson', sameCanonicalJson),
    implementationDigestInput('canonicalizeJson', canonicalizeJson),
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'attribution_never_sum_rollup_v1:clean:hand-walked:v1',
      input: ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT,
      expected: ATTRIBUTION_NEVER_SUM_GOLDEN_EXPECTED,
    }),
    Object.freeze({
      traceId: 'attribution_never_sum_rollup_v1:blocked:invented-key:v1',
      input: Object.freeze({
        ...(JSON.parse(JSON.stringify(ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT)) as AttributionNeverSumRollupInput),
        derivedClaims: Object.freeze([
          Object.freeze({
            ...(JSON.parse(JSON.stringify(ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT.derivedClaims[0])) as AttributionNeverSumDerivedClaim),
            overlapDimensionKeys: Object.freeze(['orgNodeIds'] as unknown as ApprovedOverlapDimensionKey[]),
          }),
        ]),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'dimension_not_declared' as const,
        message: 'ImpactClaim overlap keys are not approved for cost: orgNodeIds',
      }),
    }),
    Object.freeze({
      traceId: 'attribution_never_sum_rollup_v1:blocked:unresolved-pair:v1',
      input: Object.freeze({
        ...(JSON.parse(JSON.stringify(ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT)) as AttributionNeverSumRollupInput),
        deconflictionReceipt: Object.freeze({
          ...JSON.parse(JSON.stringify(ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT.deconflictionReceipt)),
          status: 'partial' as const,
          canPublishFullCompositeTotal: false,
          unresolvedPairs: Object.freeze(['claim-oracle-1:claim-oracle-2']),
        }),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'formula_reconciliation_failure' as const,
        message:
          'attribution rollup requires a complete de-confliction receipt with no unresolved pairs',
      }),
    }),
    Object.freeze({
      traceId: 'attribution_never_sum_rollup_v1:blocked:empty:v1',
      input: Object.freeze({
        derivedClaims: Object.freeze([]),
        deconflictionReceipt: JSON.parse(
          JSON.stringify(ATTRIBUTION_NEVER_SUM_GOLDEN_INPUT.deconflictionReceipt),
        ),
      }),
      expected: Object.freeze({
        status: 'blocked' as const,
        reasonCode: 'missing_formula_input' as const,
        message: 'attribution never-sum rollup requires at least one derived ImpactClaim',
      }),
    }),
  ]),
  validateInputAgainstEvidence: validateAttributionNeverSumInputAgainstEvidence,
  execute: executeAttributionNeverSumRollup,
});

type PortfolioProjectionInput = Readonly<Record<string, unknown> & { sourceAsOf: string }>;

function portfolioProjectionNumbers(
  input: unknown,
  fields: readonly string[],
): ApprovedFormulaResult<Readonly<Record<string, number>>> {
  const block = portfolioFormulaInputBlock(input);
  if (block) return block;
  const record = input as Record<string, unknown>;
  const values: Record<string, number> = {};
  for (const field of fields) {
    const value = record[field];
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return {
        status: 'blocked',
        reasonCode: 'invalid_formula_input',
        message: `${field} must be a finite number`,
      };
    }
    values[field] = value;
  }
  return { status: 'ok', output: values };
}

function portfolioProjectionNumberArray(
  input: unknown,
  field: string,
): ApprovedFormulaResult<readonly number[]> {
  const block = portfolioFormulaInputBlock(input);
  if (block) return block;
  const value = (input as Record<string, unknown>)[field];
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'number' || !Number.isFinite(item))) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: `${field} must be a finite number array`,
    };
  }
  return { status: 'ok', output: value };
}

function portfolioProjectionDefinition<TOutput>(input: Readonly<{
  formulaId: string;
  displayName: string;
  execute: (input: PortfolioProjectionInput) => ApprovedFormulaResult<TOutput>;
  goldenInput: PortfolioProjectionInput;
  goldenOutput: TOutput;
  digestInputs: readonly (readonly [string, unknown])[];
}>): ApprovedFormulaDefinition<PortfolioProjectionInput, TOutput> {
  return portfolioFormulaDefinition({
    formulaId: input.formulaId,
    displayName: input.displayName,
    description: `Deterministic ${input.displayName.toLowerCase()} projection over governed Portfolio Planner kernel inputs.`,
    inputSchemaVersion: `${input.formulaId}_input_v1`,
    outputSchemaVersion: `${input.formulaId}_output_v1`,
    contextKinds: PORTFOLIO_CAPACITY_ALLOCATION_CONTEXT_KINDS,
    execute: input.execute,
    digestInputs: input.digestInputs,
    goldenTraces: [{
      traceId: `${input.formulaId}:ok:v1`,
      input: input.goldenInput,
      expected: { status: 'ok', output: input.goldenOutput },
    }],
  });
}

const portfolioRequiredFteProjectionDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_required_fte_projection_v1',
  displayName: 'Portfolio required FTE projection',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['totalHours', 'hoursPerFte']);
    if (values.status === 'blocked') return values;
    if (values.output.hoursPerFte <= 0) return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'hoursPerFte must be positive' };
    return { status: 'ok', output: { requiredFte: roundTo(values.output.totalHours / values.output.hoursPerFte, 6) } };
  },
  goldenInput: { totalHours: 80, hoursPerFte: 40, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { requiredFte: 2 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioReallocationFirstOrderDeltaDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_reallocation_first_order_delta_v1',
  displayName: 'Portfolio reallocation first-order delta',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['toValueUsd', 'toRequiredFte', 'fromValueUsd', 'fromRequiredFte', 'shiftedFte']);
    if (values.status === 'blocked') return values;
    if (values.output.toRequiredFte <= 0 || values.output.fromRequiredFte <= 0) return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'required FTE inputs must be positive' };
    return { status: 'ok', output: { firstOrderValueDeltaUsd: roundTo((values.output.toValueUsd / values.output.toRequiredFte - values.output.fromValueUsd / values.output.fromRequiredFte) * values.output.shiftedFte, 6) } };
  },
  goldenInput: { toValueUsd: 200, toRequiredFte: 2, fromValueUsd: 100, fromRequiredFte: 2, shiftedFte: 1, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { firstOrderValueDeltaUsd: 50 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioCapacityShiftHoursDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_capacity_shift_hours_v1',
  displayName: 'Portfolio capacity shift hours',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['shiftedFte', 'hoursPerFte']);
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { hoursPerWeek: roundTo(values.output.shiftedFte * values.output.hoursPerFte, 6) } };
  },
  goldenInput: { shiftedFte: 2, hoursPerFte: 40, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { hoursPerWeek: 80 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioCapacityCutDeltaDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_capacity_cut_delta_v1',
  displayName: 'Portfolio capacity cut delta',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['supplyHours', 'cutPct']);
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { deltaHours: roundTo(-values.output.supplyHours * (values.output.cutPct / 100), 6) } };
  },
  goldenInput: { supplyHours: 80, cutPct: 25, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { deltaHours: -20 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioPositionWeeklyHoursDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_position_weekly_hours_v1',
  displayName: 'Portfolio position weekly hours',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['fte', 'defaultHoursPerFte']);
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { weeklyHours: values.output.fte * values.output.defaultHoursPerFte } };
  },
  goldenInput: { fte: 1.5, defaultHoursPerFte: 40, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { weeklyHours: 60 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers]],
});

const portfolioCommitmentRollupDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_commitment_rollup_v1',
  displayName: 'Portfolio commitment rollup',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['currentCommittedFte', 'committedFte']);
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { committedFte: roundTo(values.output.currentCommittedFte + values.output.committedFte, 6) } };
  },
  goldenInput: { currentCommittedFte: 1.25, committedFte: 0.75, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { committedFte: 2 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioAvailableFteProjectionDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_available_fte_projection_v1',
  displayName: 'Portfolio available FTE projection',
  execute: (input) => {
    const values = portfolioProjectionNumberArray(input, 'weeklyHours');
    if (values.status === 'blocked') return values;
    const divisor = portfolioProjectionNumbers(input, ['hoursPerFte']);
    if (divisor.status === 'blocked') return divisor;
    if (values.output.length === 0 || divisor.output.hoursPerFte <= 0) return { status: 'blocked', reasonCode: 'invalid_formula_input', message: 'weeklyHours and hoursPerFte must be usable' };
    return { status: 'ok', output: { availableFte: roundTo(values.output.reduce((total, value) => total + value, 0) / values.output.length / divisor.output.hoursPerFte, 6) } };
  },
  goldenInput: { weeklyHours: [40, 80], hoursPerFte: 40, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { availableFte: 1.5 },
  digestInputs: [['portfolioProjectionNumberArray', portfolioProjectionNumberArray], ['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioFinanceProjectionDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_finance_projection_v1',
  displayName: 'Portfolio finance projection',
  execute: (input) => {
    const funding = portfolioProjectionNumberArray(input, 'fundingRequiredUsd');
    if (funding.status === 'blocked') return funding;
    const actuals = portfolioProjectionNumberArray(input, 'costPoolActualUsd');
    if (actuals.status === 'blocked') return actuals;
    const control = portfolioProjectionNumbers(input, ['transformationSpendActualUsd']);
    if (control.status === 'blocked') return control;
    const planSpendUsd = roundTo(funding.output.reduce((total, value) => total + value, 0), 6);
    const financeActualUsd = roundTo(actuals.output.reduce((total, value) => total + value, 0), 6);
    return { status: 'ok', output: { planSpendUsd, financeActualUsd, reconciled: Math.abs(financeActualUsd - control.output.transformationSpendActualUsd) <= 0.000001 } };
  },
  goldenInput: { fundingRequiredUsd: [40, 60], costPoolActualUsd: [50, 50], transformationSpendActualUsd: 100, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { planSpendUsd: 100, financeActualUsd: 100, reconciled: true },
  digestInputs: [['portfolioProjectionNumberArray', portfolioProjectionNumberArray], ['portfolioProjectionNumbers', portfolioProjectionNumbers], ['roundTo', roundTo]],
});

const portfolioCapturedValueTotalDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_captured_value_total_v1',
  displayName: 'Portfolio captured value total',
  execute: (input) => {
    const values = portfolioProjectionNumberArray(input, 'valueUsd');
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { totalValueUsd: roundTo(values.output.reduce((total, value) => total + value, 0), 6) } };
  },
  goldenInput: { valueUsd: [40, 60], sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { totalValueUsd: 100 },
  digestInputs: [['portfolioProjectionNumberArray', portfolioProjectionNumberArray], ['roundTo', roundTo]],
});

const portfolioAbsoluteValueDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_absolute_value_v1',
  displayName: 'Portfolio absolute value',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['value']);
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { absoluteValue: Math.abs(values.output.value) } };
  },
  goldenInput: { value: -10, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { absoluteValue: 10 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers]],
});

const portfolioMetricTotalDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_metric_total_v1',
  displayName: 'Portfolio metric total',
  execute: (input) => {
    const values = portfolioProjectionNumberArray(input, 'values');
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { total: roundTo(values.output.reduce((total, value) => total + value, 0), 6) } };
  },
  goldenInput: { values: [1, 2], sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { total: 3 },
  digestInputs: [['portfolioProjectionNumberArray', portfolioProjectionNumberArray], ['roundTo', roundTo]],
});

const portfolioSequenceViolationTotalDefinition = portfolioProjectionDefinition({
  formulaId: 'portfolio_sequence_violation_total_v1',
  displayName: 'Portfolio sequence violation total',
  execute: (input) => {
    const values = portfolioProjectionNumbers(input, ['dependencyViolationCount', 'capacityViolationCount']);
    if (values.status === 'blocked') return values;
    return { status: 'ok', output: { violationCount: values.output.dependencyViolationCount + values.output.capacityViolationCount } };
  },
  goldenInput: { dependencyViolationCount: 2, capacityViolationCount: 1, sourceAsOf: '2026-07-10T00:00:00.000Z' },
  goldenOutput: { violationCount: 3 },
  digestInputs: [['portfolioProjectionNumbers', portfolioProjectionNumbers]],
});

export type FreightEmissionsGlecLeg = Readonly<{
  tonneKm: number;
  factorGco2ePerTonneKm: number;
  rfiUpliftFactor?: number | null;
}>;

export type FreightEmissionsGlecInput = Readonly<{ legs: readonly FreightEmissionsGlecLeg[] }>;
export type FreightEmissionsGlecOutput = Readonly<{
  freightEmissionsTco2e: number;
  totalTonneKm: number;
}>;

export type EmissionsAllocationLeg = FreightEmissionsGlecLeg & Readonly<{
  customerId?: string | null;
  scope: 'scope1' | 'scope3';
}>;
export type EmissionsAllocationInput = Readonly<{ legs: readonly EmissionsAllocationLeg[] }>;
export type EmissionsAllocationOutput = Readonly<{
  allocations: readonly Readonly<{ customerId: string; emissionsTco2e: number }>[];
  unallocatedResidualTco2e: number;
  scope3EmissionsTco2e: number;
}>;

export type EmissionIntensityInput = Readonly<{
  freightEmissionsTco2e: number;
  totalTonneKm: number;
  benchmarkLowGco2ePerTonneKm: number;
  benchmarkHighGco2ePerTonneKm: number;
}>;
export type EmissionIntensityOutput = Readonly<{
  emissionIntensityGco2ePerTonneKm: number;
  benchmarkBand: 'below' | 'within' | 'above';
}>;

export type EmissionTargetDriftInput = Readonly<{
  actualTco2e: number;
  targetPathTco2e: number;
}>;
export type EmissionTargetDriftOutput = Readonly<{ emissionTargetDriftPct: number }>;

export type AbatementLeverFormulaInput = Readonly<{
  leverId: string;
  annualAbatementTonnes: number;
  annualizedCapexUsd: number;
  deltaOpexUsd: number;
  deltaFuelOrCostSavingUsd: number;
}>;
export type MarginalAbatementCostInput = Readonly<{
  levers: readonly AbatementLeverFormulaInput[];
}>;
export type MarginalAbatementCostOutput = Readonly<{
  levers: readonly Readonly<{
    leverId: string;
    annualAbatementTonnes: number;
    marginalCostUsdPerTonne: number;
  }>[];
}>;

export type AbatementPotentialInput = MarginalAbatementCostInput & Readonly<{
  costCeilingUsdPerTonne: number;
}>;
export type AbatementPotentialOutput = Readonly<{
  abatementPotentialTco2e: number;
  qualifiedLeverIds: readonly string[];
}>;

export type ModeShiftEmissionsInput = Readonly<{
  movedTonneKm: number;
  sourceFactorGco2ePerTonneKm: number;
  targetFactorGco2ePerTonneKm: number;
  shiftPct: number;
}>;
export type ModeShiftEmissionsOutput = Readonly<{
  modeShiftAbatementTco2e: number;
  shiftedTonneKm: number;
}>;

export type FleetRenewalEmissionsInput = Readonly<{
  affectedTonneKm: number;
  oldFactorGco2ePerTonneKm: number;
  newFactorGco2ePerTonneKm: number;
  annualizedCapexUsd: number;
}>;
export type FleetRenewalEmissionsOutput = Readonly<{
  fleetRenewalAbatementTco2e: number;
  annualizedCapexUsd: number;
}>;

function invalidEsgFormulaInput(message: string): ApprovedFormulaBlockedResult {
  return { status: 'blocked', reasonCode: 'invalid_formula_input', message };
}

function missingEsgFormulaInput(message: string): ApprovedFormulaBlockedResult {
  return { status: 'blocked', reasonCode: 'missing_formula_input', message };
}

function emissionTonnes(leg: FreightEmissionsGlecLeg): number | null {
  if (
    !isFiniteNumber(leg.tonneKm) ||
    !isFiniteNumber(leg.factorGco2ePerTonneKm) ||
    leg.tonneKm < 0 ||
    leg.factorGco2ePerTonneKm < 0
  ) {
    return null;
  }
  const rfi = leg.rfiUpliftFactor ?? 1;
  if (!isFiniteNumber(rfi) || rfi <= 0) return null;
  return (leg.tonneKm * leg.factorGco2ePerTonneKm * rfi) / 1_000_000;
}

function executeFreightEmissionsGlec(
  input: FreightEmissionsGlecInput,
): ApprovedFormulaResult<FreightEmissionsGlecOutput> {
  if (!Array.isArray(input?.legs) || input.legs.length === 0) {
    return missingEsgFormulaInput('freight emissions requires at least one governed tonne-kilometre leg');
  }
  let tonnes = 0;
  let tonneKm = 0;
  for (const leg of input.legs) {
    const emissions = emissionTonnes(leg);
    if (emissions === null) {
      return invalidEsgFormulaInput('each freight leg requires non-negative tonne-kilometres, factor, and optional positive RFI uplift');
    }
    tonnes += emissions;
    tonneKm += leg.tonneKm;
  }
  return {
    status: 'ok',
    output: Object.freeze({
      freightEmissionsTco2e: roundTo(tonnes, 6),
      totalTonneKm: roundTo(tonneKm, 6),
    }),
  };
}

function executeEmissionsAllocation(
  input: EmissionsAllocationInput,
): ApprovedFormulaResult<EmissionsAllocationOutput> {
  if (!Array.isArray(input?.legs) || input.legs.length === 0) {
    return missingEsgFormulaInput('emissions allocation requires at least one governed Scope 3 freight leg');
  }
  const byCustomer = new Map<string, number>();
  let residual = 0;
  let total = 0;
  for (const leg of input.legs) {
    if (leg.scope !== 'scope3') continue;
    const emissions = emissionTonnes(leg);
    if (emissions === null) return invalidEsgFormulaInput('Scope 3 allocation requires valid governed freight legs');
    total += emissions;
    const customerId = typeof leg.customerId === 'string' ? leg.customerId.trim() : '';
    if (!customerId) {
      residual += emissions;
      continue;
    }
    byCustomer.set(customerId, (byCustomer.get(customerId) ?? 0) + emissions);
  }
  const allocations = [...byCustomer.entries()]
    .map(([customerId, emissionsTco2e]) => ({ customerId, emissionsTco2e: roundTo(emissionsTco2e, 6) }))
    .sort((left, right) => right.emissionsTco2e - left.emissionsTco2e || left.customerId.localeCompare(right.customerId));
  return {
    status: 'ok',
    output: Object.freeze({
      allocations: Object.freeze(allocations),
      unallocatedResidualTco2e: roundTo(residual, 6),
      scope3EmissionsTco2e: roundTo(total, 6),
    }),
  };
}

function executeEmissionIntensity(
  input: EmissionIntensityInput,
): ApprovedFormulaResult<EmissionIntensityOutput> {
  if (
    !isFiniteNumber(input?.freightEmissionsTco2e) ||
    !isFiniteNumber(input?.totalTonneKm) ||
    !isFiniteNumber(input?.benchmarkLowGco2ePerTonneKm) ||
    !isFiniteNumber(input?.benchmarkHighGco2ePerTonneKm)
  ) return invalidEsgFormulaInput('emission intensity requires finite emissions, tonne-kilometres, and benchmark band');
  if (
    input.freightEmissionsTco2e < 0 ||
    input.totalTonneKm <= 0 ||
    input.benchmarkLowGco2ePerTonneKm < 0 ||
    input.benchmarkHighGco2ePerTonneKm < input.benchmarkLowGco2ePerTonneKm
  ) return invalidEsgFormulaInput('emission intensity requires non-negative emissions, a positive denominator, and an ordered benchmark band');
  const intensity = (input.freightEmissionsTco2e * 1_000_000) / input.totalTonneKm;
  return {
    status: 'ok',
    output: Object.freeze({
      emissionIntensityGco2ePerTonneKm: roundTo(intensity, 6),
      benchmarkBand: intensity < input.benchmarkLowGco2ePerTonneKm ? 'below' : intensity > input.benchmarkHighGco2ePerTonneKm ? 'above' : 'within',
    }),
  };
}

function executeEmissionTargetDrift(
  input: EmissionTargetDriftInput,
): ApprovedFormulaResult<EmissionTargetDriftOutput> {
  if (!isFiniteNumber(input?.actualTco2e) || !isFiniteNumber(input?.targetPathTco2e)) {
    return invalidEsgFormulaInput('emission target drift requires finite actual and target-path emissions');
  }
  if (input.actualTco2e < 0 || input.targetPathTco2e <= 0) {
    return invalidEsgFormulaInput('emission target drift requires non-negative actual emissions and a positive target path');
  }
  return { status: 'ok', output: Object.freeze({ emissionTargetDriftPct: roundTo(((input.actualTco2e - input.targetPathTco2e) / input.targetPathTco2e) * 100, 6) }) };
}

function validateAbatementLevers(
  levers: readonly AbatementLeverFormulaInput[],
): ApprovedFormulaBlockedResult | null {
  if (levers.length === 0) return missingEsgFormulaInput('abatement formulas require at least one governed lever');
  for (const lever of levers) {
    if (
      !lever.leverId.trim() ||
      !isFiniteNumber(lever.annualAbatementTonnes) ||
      !isFiniteNumber(lever.annualizedCapexUsd) ||
      !isFiniteNumber(lever.deltaOpexUsd) ||
      !isFiniteNumber(lever.deltaFuelOrCostSavingUsd) ||
      lever.annualAbatementTonnes <= 0
    ) return invalidEsgFormulaInput('each abatement lever requires an id, positive abatement tonnes, and finite cost components');
  }
  return null;
}

function marginalCost(lever: AbatementLeverFormulaInput): number {
  return (lever.annualizedCapexUsd + lever.deltaOpexUsd - lever.deltaFuelOrCostSavingUsd) / lever.annualAbatementTonnes;
}

function executeMarginalAbatementCost(
  input: MarginalAbatementCostInput,
): ApprovedFormulaResult<MarginalAbatementCostOutput> {
  if (!Array.isArray(input?.levers)) return missingEsgFormulaInput('marginal abatement cost requires governed lever rows');
  const invalid = validateAbatementLevers(input.levers);
  if (invalid) return invalid;
  const levers = input.levers
    .map((lever) => ({ leverId: lever.leverId, annualAbatementTonnes: lever.annualAbatementTonnes, marginalCostUsdPerTonne: roundTo(marginalCost(lever), 6) }))
    .sort((left, right) => left.marginalCostUsdPerTonne - right.marginalCostUsdPerTonne || left.leverId.localeCompare(right.leverId));
  return { status: 'ok', output: Object.freeze({ levers: Object.freeze(levers) }) };
}

function executeAbatementPotential(
  input: AbatementPotentialInput,
): ApprovedFormulaResult<AbatementPotentialOutput> {
  if (!Array.isArray(input?.levers) || !isFiniteNumber(input?.costCeilingUsdPerTonne)) {
    return invalidEsgFormulaInput('abatement potential requires governed lever rows and a finite cost ceiling');
  }
  const invalid = validateAbatementLevers(input.levers);
  if (invalid) return invalid;
  const qualifying = input.levers.filter((lever) => marginalCost(lever) <= input.costCeilingUsdPerTonne);
  return {
    status: 'ok',
    output: Object.freeze({
      abatementPotentialTco2e: roundTo(qualifying.reduce((sum, lever) => sum + lever.annualAbatementTonnes, 0), 6),
      qualifiedLeverIds: Object.freeze(qualifying.map((lever) => lever.leverId).sort()),
    }),
  };
}

function executeModeShiftEmissions(
  input: ModeShiftEmissionsInput,
): ApprovedFormulaResult<ModeShiftEmissionsOutput> {
  if (
    !isFiniteNumber(input?.movedTonneKm) ||
    !isFiniteNumber(input?.sourceFactorGco2ePerTonneKm) ||
    !isFiniteNumber(input?.targetFactorGco2ePerTonneKm) ||
    !isFiniteNumber(input?.shiftPct)
  ) return invalidEsgFormulaInput('mode shift requires finite tonne-kilometres, source factor, target factor, and shift percentage');
  if (
    input.movedTonneKm < 0 ||
    input.sourceFactorGco2ePerTonneKm < 0 ||
    input.targetFactorGco2ePerTonneKm < 0 ||
    input.targetFactorGco2ePerTonneKm >= input.sourceFactorGco2ePerTonneKm ||
    input.shiftPct < 0 || input.shiftPct > 1
  ) return invalidEsgFormulaInput('mode shift requires non-negative inputs, a lower target factor, and shiftPct in [0,1]');
  const shiftedTonneKm = input.movedTonneKm * input.shiftPct;
  return {
    status: 'ok',
    output: Object.freeze({
      shiftedTonneKm: roundTo(shiftedTonneKm, 6),
      modeShiftAbatementTco2e: roundTo((shiftedTonneKm * (input.sourceFactorGco2ePerTonneKm - input.targetFactorGco2ePerTonneKm)) / 1_000_000, 6),
    }),
  };
}

function executeFleetRenewalEmissions(
  input: FleetRenewalEmissionsInput,
): ApprovedFormulaResult<FleetRenewalEmissionsOutput> {
  if (
    !isFiniteNumber(input?.affectedTonneKm) ||
    !isFiniteNumber(input?.oldFactorGco2ePerTonneKm) ||
    !isFiniteNumber(input?.newFactorGco2ePerTonneKm) ||
    !isFiniteNumber(input?.annualizedCapexUsd)
  ) return invalidEsgFormulaInput('fleet renewal requires finite affected tonne-kilometres, factors, and governed capex');
  if (
    input.affectedTonneKm < 0 ||
    input.oldFactorGco2ePerTonneKm < 0 ||
    input.newFactorGco2ePerTonneKm < 0 ||
    input.newFactorGco2ePerTonneKm >= input.oldFactorGco2ePerTonneKm ||
    input.annualizedCapexUsd < 0
  ) return invalidEsgFormulaInput('fleet renewal requires non-negative values and a lower replacement factor');
  return {
    status: 'ok',
    output: Object.freeze({
      fleetRenewalAbatementTco2e: roundTo((input.affectedTonneKm * (input.oldFactorGco2ePerTonneKm - input.newFactorGco2ePerTonneKm)) / 1_000_000, 6),
      annualizedCapexUsd: roundTo(input.annualizedCapexUsd, 2),
    }),
  };
}

const freightEmissionsGlecDefinition: ApprovedFormulaDefinition<FreightEmissionsGlecInput, FreightEmissionsGlecOutput> = Object.freeze({
  formulaId: 'freight_emissions_glec_v1', version: '1.0.0', displayName: 'Freight emissions GLEC rollup', description: 'Computes well-to-wheel freight CO2e from governed tonne-kilometres and approved GLEC/ISO 14083 factors.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'freight_emissions_glec_input_v1', outputSchemaVersion: 'freight_emissions_glec_output_v1', expectedContextKind: 'decision_agent:esg_emissions:shipment_tonne_km_snapshot', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeFreightEmissionsGlec', executeFreightEmissionsGlec], ['emissionTonnes', emissionTonnes]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'freight_emissions_glec:base:v1', input: Object.freeze({ legs: Object.freeze([{ tonneKm: 1000, factorGco2ePerTonneKm: 100 }, { tonneKm: 2000, factorGco2ePerTonneKm: 20 }]) }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ freightEmissionsTco2e: 0.14, totalTonneKm: 3000 }) }) })]), execute: executeFreightEmissionsGlec,
});

const emissionsAllocationDefinition: ApprovedFormulaDefinition<EmissionsAllocationInput, EmissionsAllocationOutput> = Object.freeze({
  formulaId: 'emissions_allocation_v1', version: '1.0.0', displayName: 'Freight emissions allocation', description: 'Allocates governed Scope 3 freight CO2e by customer and preserves unmapped residuals.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'emissions_allocation_input_v1', outputSchemaVersion: 'emissions_allocation_output_v1', expectedContextKind: 'decision_agent:esg_emissions:shipment_tonne_km_snapshot', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeEmissionsAllocation', executeEmissionsAllocation]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'emissions_allocation:base:v1', input: Object.freeze({ legs: Object.freeze([{ tonneKm: 1000, factorGco2ePerTonneKm: 100, scope: 'scope3' as const, customerId: 'customer-a' }, { tonneKm: 500, factorGco2ePerTonneKm: 100, scope: 'scope3' as const }]) }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ allocations: Object.freeze([{ customerId: 'customer-a', emissionsTco2e: 0.1 }]), unallocatedResidualTco2e: 0.05, scope3EmissionsTco2e: 0.15 }) }) })]), execute: executeEmissionsAllocation,
});

const emissionIntensityDefinition: ApprovedFormulaDefinition<EmissionIntensityInput, EmissionIntensityOutput> = Object.freeze({
  formulaId: 'emission_intensity_v1', version: '1.0.0', displayName: 'Freight emission intensity', description: 'Computes freight gCO2e per tonne-kilometre and classifies it against a profile-declared band.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'emission_intensity_input_v1', outputSchemaVersion: 'emission_intensity_output_v1', expectedContextKind: 'decision_agent:esg_emissions:shipment_tonne_km_snapshot', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeEmissionIntensity', executeEmissionIntensity]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'emission_intensity:base:v1', input: Object.freeze({ freightEmissionsTco2e: 0.14, totalTonneKm: 3000, benchmarkLowGco2ePerTonneKm: 40, benchmarkHighGco2ePerTonneKm: 50 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ emissionIntensityGco2ePerTonneKm: 46.666667, benchmarkBand: 'within' as const }) }) })]), execute: executeEmissionIntensity,
});

const emissionTargetDriftDefinition: ApprovedFormulaDefinition<EmissionTargetDriftInput, EmissionTargetDriftOutput> = Object.freeze({
  formulaId: 'emission_target_drift_v1', version: '1.0.0', displayName: 'Freight emission target drift', description: 'Computes actual freight emissions versus the approved target-path value.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'emission_target_drift_input_v1', outputSchemaVersion: 'emission_target_drift_output_v1', expectedContextKind: 'decision_agent:esg_emissions:emission_reduction_target', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeEmissionTargetDrift', executeEmissionTargetDrift]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'emission_target_drift:base:v1', input: Object.freeze({ actualTco2e: 90, targetPathTco2e: 100 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ emissionTargetDriftPct: -10 }) }) })]), execute: executeEmissionTargetDrift,
});

const marginalAbatementCostDefinition: ApprovedFormulaDefinition<MarginalAbatementCostInput, MarginalAbatementCostOutput> = Object.freeze({
  formulaId: 'marginal_abatement_cost_v1', version: '1.0.0', displayName: 'Marginal abatement cost', description: 'Ranks approved freight-abatement levers by recomputed net annualized cost per tonne.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'marginal_abatement_cost_input_v1', outputSchemaVersion: 'marginal_abatement_cost_output_v1', expectedContextKind: 'decision_agent:esg_emissions:abatement_lever_cost_table', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeMarginalAbatementCost', executeMarginalAbatementCost], ['marginalCost', marginalCost]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'marginal_abatement_cost:base:v1', input: Object.freeze({ levers: Object.freeze([{ leverId: 'rail', annualAbatementTonnes: 10, annualizedCapexUsd: 0, deltaOpexUsd: 0, deltaFuelOrCostSavingUsd: 100 }, { leverId: 'fleet', annualAbatementTonnes: 10, annualizedCapexUsd: 200, deltaOpexUsd: 0, deltaFuelOrCostSavingUsd: 0 }]) }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ levers: Object.freeze([{ leverId: 'rail', annualAbatementTonnes: 10, marginalCostUsdPerTonne: -10 }, { leverId: 'fleet', annualAbatementTonnes: 10, marginalCostUsdPerTonne: 20 }]) }) }) })]), execute: executeMarginalAbatementCost,
});

const abatementPotentialDefinition: ApprovedFormulaDefinition<AbatementPotentialInput, AbatementPotentialOutput> = Object.freeze({
  formulaId: 'abatement_potential_v1', version: '1.0.0', displayName: 'Abatement potential at cost ceiling', description: 'Sums governed annual abatement tonnes at or below a declared marginal-cost ceiling.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'abatement_potential_input_v1', outputSchemaVersion: 'abatement_potential_output_v1', expectedContextKind: 'decision_agent:esg_emissions:abatement_lever_cost_table', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeAbatementPotential', executeAbatementPotential], ['marginalCost', marginalCost]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'abatement_potential:base:v1', input: Object.freeze({ levers: Object.freeze([{ leverId: 'rail', annualAbatementTonnes: 10, annualizedCapexUsd: 0, deltaOpexUsd: 0, deltaFuelOrCostSavingUsd: 100 }, { leverId: 'fleet', annualAbatementTonnes: 10, annualizedCapexUsd: 200, deltaOpexUsd: 0, deltaFuelOrCostSavingUsd: 0 }]), costCeilingUsdPerTonne: 0 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ abatementPotentialTco2e: 10, qualifiedLeverIds: Object.freeze(['rail']) }) }) })]), execute: executeAbatementPotential,
});

const modeShiftEmissionsDefinition: ApprovedFormulaDefinition<ModeShiftEmissionsInput, ModeShiftEmissionsOutput> = Object.freeze({
  formulaId: 'mode_shift_emissions_v1', version: '1.0.0', displayName: 'Road-to-rail mode-shift emissions', description: 'Computes carbon abatement from governed road tonne-kilometres shifted to a lower-factor target mode.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'mode_shift_emissions_input_v1', outputSchemaVersion: 'mode_shift_emissions_output_v1', expectedContextKind: 'decision_agent:esg_emissions:shipment_tonne_km_snapshot', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeModeShiftEmissions', executeModeShiftEmissions]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'mode_shift_emissions:base:v1', input: Object.freeze({ movedTonneKm: 1000, sourceFactorGco2ePerTonneKm: 100, targetFactorGco2ePerTonneKm: 20, shiftPct: 0.25 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ shiftedTonneKm: 250, modeShiftAbatementTco2e: 0.02 }) }) })]), execute: executeModeShiftEmissions,
});

const fleetRenewalEmissionsDefinition: ApprovedFormulaDefinition<FleetRenewalEmissionsInput, FleetRenewalEmissionsOutput> = Object.freeze({
  formulaId: 'fleet_renewal_emissions_v1', version: '1.0.0', displayName: 'Fleet-renewal emissions', description: 'Computes freight-carbon abatement from a governed factor improvement and reads governed annualized capex.', lifecycle: 'code_defined', formulaRuntimeVersion: 'approved_formula_runtime_v1', canonicalizerVersion: 'approved_formula_canonicalizer_v1', inputSchemaVersion: 'fleet_renewal_emissions_input_v1', outputSchemaVersion: 'fleet_renewal_emissions_output_v1', expectedContextKind: 'decision_agent:esg_emissions:abatement_lever_cost_table', approvedSourceSystemRefs: ESG_EMISSIONS_SOURCE_REFS, implementationDigestInputs: implementationDigestInputs([['executeFleetRenewalEmissions', executeFleetRenewalEmissions]]), goldenTraces: Object.freeze([Object.freeze({ traceId: 'fleet_renewal_emissions:base:v1', input: Object.freeze({ affectedTonneKm: 1000, oldFactorGco2ePerTonneKm: 100, newFactorGco2ePerTonneKm: 20, annualizedCapexUsd: 1500 }), expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ fleetRenewalAbatementTco2e: 0.08, annualizedCapexUsd: 1500 }) }) })]), execute: executeFleetRenewalEmissions,
});

type LeverDeliberationFormulaOption = Readonly<{
  optionId: string;
  certaintyRank: number;
  impactUsd: number;
  explicitCostInputsUsd: readonly number[];
  riskRank: number;
  declaredCalibration: boolean;
}>;

function executeLeverDeliberationOptionSelection(
  input: Readonly<{ options: readonly LeverDeliberationFormulaOption[] }> | null | undefined,
): ApprovedFormulaResult<Readonly<{
  options: readonly Readonly<{ optionId: string; explicitCostUsd: number }>[];
  recommendedOptionId: string;
}>> {
  if (!input || !Array.isArray(input.options) || input.options.length === 0) {
    return {
      status: 'blocked',
      reasonCode: 'missing_formula_input',
      message: 'Lever deliberation selection requires evidence-bound scenario options.',
    };
  }
  const options = input.options.map((option) => {
    if (
      !option.optionId.trim() ||
      !isFiniteNumber(option.certaintyRank) ||
      !isFiniteNumber(option.impactUsd) ||
      !isFiniteNumber(option.riskRank) ||
      !Array.isArray(option.explicitCostInputsUsd) ||
      option.explicitCostInputsUsd.some(
        (value: number) => !isFiniteNumber(value) || value < 0,
      )
    ) {
      return null;
    }
    return Object.freeze({
      optionId: option.optionId,
      certaintyRank: option.certaintyRank,
      impactUsd: option.impactUsd,
      explicitCostUsd: roundTo(
        option.explicitCostInputsUsd.reduce(
          (sum: number, value: number) => sum + value,
          0,
        ),
        2,
      ),
      riskRank: option.riskRank,
      declaredCalibration: option.declaredCalibration,
    });
  });
  if (options.some((option) => option === null)) {
    return {
      status: 'blocked',
      reasonCode: 'invalid_formula_input',
      message: 'Each lever deliberation option requires finite evidence-bound ranking metrics and non-negative explicit costs.',
    };
  }
  const ranked = options as readonly NonNullable<(typeof options)[number]>[];
  const riskEligible = ranked.filter((option) => option.riskRank >= 2);
  const selectionPool = riskEligible.length > 0 ? riskEligible : ranked;
  const selected = [...selectionPool].sort((left, right) =>
    Number(right.declaredCalibration) - Number(left.declaredCalibration) ||
    right.certaintyRank - left.certaintyRank ||
    right.impactUsd - left.impactUsd ||
    left.explicitCostUsd - right.explicitCostUsd ||
    right.riskRank - left.riskRank ||
    left.optionId.localeCompare(right.optionId),
  )[0];
  return {
    status: 'ok',
    output: Object.freeze({
      options: Object.freeze(ranked.map(({ optionId, explicitCostUsd }) => Object.freeze({ optionId, explicitCostUsd }))),
      recommendedOptionId: selected.optionId,
    }),
  };
}

const leverDeliberationOptionSelectionDefinition: ApprovedFormulaDefinition = Object.freeze({
  formulaId: 'lever_deliberation_option_selection_v1',
  version: '1.0.0',
  displayName: 'Lever deliberation option selection',
  description: 'Aggregates explicit evidence-bound option costs and selects one scenario option using the approved certainty, impact, cost, risk, and calibration order.',
  lifecycle: 'code_defined',
  formulaRuntimeVersion: 'approved_formula_runtime_v1',
  canonicalizerVersion: 'approved_formula_canonicalizer_v1',
  inputSchemaVersion: 'lever_deliberation_option_selection_input_v1',
  outputSchemaVersion: 'lever_deliberation_option_selection_output_v1',
  approvedSourceSystemRefs: [
    CARRIER_TRANSPORTATION_COST_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    ORG_COST_OPTIMIZATION_GOVERNED_SNAPSHOT_SOURCE_SYSTEM_REF,
    FINANCE_PNL_PUBLIC_FILING_SOURCE_SYSTEM_REF,
    WORKFORCE_REDUCTION_GOVERNED_EVIDENCE_PACK_SOURCE_SYSTEM_REF,
  ],
  implementationDigestInputs: implementationDigestInputs([
    ['executeLeverDeliberationOptionSelection', executeLeverDeliberationOptionSelection],
  ]),
  goldenTraces: Object.freeze([
    Object.freeze({
      traceId: 'lever_deliberation_option_selection:declared-calibration:v1',
      input: Object.freeze({ options: Object.freeze([
        Object.freeze({ optionId: 'base', certaintyRank: 2, impactUsd: 300, explicitCostInputsUsd: Object.freeze([0]), riskRank: 2, declaredCalibration: true }),
        Object.freeze({ optionId: 'stretch', certaintyRank: 2, impactUsd: 500, explicitCostInputsUsd: Object.freeze([300]), riskRank: 1, declaredCalibration: false }),
      ]) }),
      expected: Object.freeze({ status: 'ok' as const, output: Object.freeze({ options: Object.freeze([{ optionId: 'base', explicitCostUsd: 0 }, { optionId: 'stretch', explicitCostUsd: 300 }]), recommendedOptionId: 'base' }) }),
    }),
  ]),
  execute: executeLeverDeliberationOptionSelection,
});

const approvedFormulaDefinitions: readonly ApprovedFormulaDefinition[] = Object.freeze([
  leverDeliberationOptionSelectionDefinition,
  allocationLensExclusionReceiptDefinition,
  attributionCounterfactualDefinition,
  attributionNeverSumRollupDefinition,
  portfolioCapacityBalanceDefinition,
  portfolioValueDensityDefinition,
  portfolioDependencyBlockScoreDefinition,
  portfolioConstraintBindingDefinition,
  portfolioPlanActualsReconcileDefinition,
  portfolioReallocationValueDeltaDefinition,
  portfolioCapacityCutRankingDefinition,
  portfolioDependencySequencingDefinition,
  portfolioRequiredFteProjectionDefinition,
  portfolioReallocationFirstOrderDeltaDefinition,
  portfolioCapacityShiftHoursDefinition,
  portfolioCapacityCutDeltaDefinition,
  portfolioPositionWeeklyHoursDefinition,
  portfolioCommitmentRollupDefinition,
  portfolioAvailableFteProjectionDefinition,
  portfolioFinanceProjectionDefinition,
  portfolioCapturedValueTotalDefinition,
  portfolioAbsoluteValueDefinition,
  portfolioMetricTotalDefinition,
  portfolioSequenceViolationTotalDefinition,
  strategicExecutionCapacityUtilizationDefinition,
  strategicExecutionCapacityBaselineTotalsDefinition,
  strategicExecutionCapacityRiskScoreDefinition,
  strategicExecutionCapacityWarningDefinition,
  strategicExecutionCapacityDriftDefinition,
  realizationProjectedMissDefinition,
  realizationPaceReceiptDefinition,
  realizationProportionDefinition,
  maDealGrossSynergyByBandDefinition,
  ...ORG_ANALYSIS_APPROVED_FORMULA_DEFINITIONS,
  ...ORG_PATTERN_COST_APPROVED_FORMULA_DEFINITIONS,
  GL_SCOPED_REDUCTION_APPROVED_FORMULA_DEFINITION,
  workforceReductionNetEbitdaOpportunityDefinition,
  delayeringNetEbitdaOpportunityDefinition,
  carrierTransportationCostOpportunityDefinition,
  carrierTransportationCostOpportunityScenarioDefinition,
  // SPEC-1..4 of the #4008 CHW park: the four carrier scenario operations the
  // manifest cited as formulaRefs, now real approved formulas.
  ...CARRIER_TRANSPORTATION_APPROVED_FORMULA_DEFINITIONS,
  Object.freeze({ ...freightEmissionsGlecDefinition, additionalExpectedContextKinds: Object.freeze(['decision_agent:esg_emissions:approved_emission_factor_table']) }),
  Object.freeze({ ...emissionsAllocationDefinition, additionalExpectedContextKinds: Object.freeze(['decision_agent:esg_emissions:approved_emission_factor_table']) }),
  Object.freeze({ ...emissionIntensityDefinition, additionalExpectedContextKinds: Object.freeze(['decision_agent:esg_emissions:approved_emission_factor_table']) }),
  Object.freeze({ ...emissionTargetDriftDefinition, additionalExpectedContextKinds: Object.freeze(['decision_agent:esg_emissions:shipment_tonne_km_snapshot', 'decision_agent:esg_emissions:approved_emission_factor_table']) }),
  marginalAbatementCostDefinition,
  abatementPotentialDefinition,
  Object.freeze({ ...modeShiftEmissionsDefinition, additionalExpectedContextKinds: Object.freeze(['decision_agent:esg_emissions:approved_emission_factor_table', 'decision_agent:esg_emissions:abatement_lever_cost_table']) }),
  Object.freeze({ ...fleetRenewalEmissionsDefinition, additionalExpectedContextKinds: Object.freeze(['decision_agent:esg_emissions:shipment_tonne_km_snapshot', 'decision_agent:esg_emissions:approved_emission_factor_table']) }),
  delayeringOrgHealthWarningDefinition,
  financeOperatingIncomeDeriveDefinition,
  financeEbitdaFromOperatingIncomeDefinition,
  financeNetRevenueMarginDefinition,
  freightCostRepricingDefinition,
  demandRevenueRepricingDefinition,
  operationalRevenuePerLoadDefinition,
  operationalCostPerLoadDefinition,
  financeOperatingMarginDefinition,
  financeEbitdaDeriveDefinition,
  financeGrossMarginDefinition,
  financeEbitdaMarginDefinition,
  financeOperatingExpensePerHeadDefinition,
  financePublicFilingEbitdaMarginDefinition,
  financePublicFilingOperatingExpensePerHeadDefinition,
  ...CURRENT_FY_ESTIMATE_APPROVED_FORMULA_DEFINITIONS,
  financeEbitdaTargetResolveDefinition,
  createFinanceIncomeMarginTargetDefinition({ expectedContextKind: FINANCE_CONTEXT_KIND,
    approvedSourceSystemRefs: FINANCE_PUBLIC_FILING_PNL_SOURCE_REFS,
    implementationDigestInputs: implementationDigestInputs([['executeFinanceIncomeMarginTarget', executeFinanceIncomeMarginTarget]]) }),
  financeEbitdaGapDefinition,
  financeObjectiveValueGapDefinition,
  benchmarkGapToMedianDollarizedDefinition,
  scenarioInvestmentRoiDefinition,
  pricingRealizationNetRevenueDeltaDefinition,
  pricingRealizationElasticityGuardDefinition,
  pricingRealizationAgpContributionDefinition,
  pricingRealizationOiContributionDefinition,
  pricingQuoteInvoiceLeakageDefinition,
  costToServeMarginDefinition,
  costToServeActivityAllocationDefinition,
  costToServeServiceTierCostDefinition,
  costToServeConcentrationHhiDefinition,
  costToServeTopNRevenueShareDefinition,
  costToServeBenchmarkDefinition,
  costToServeRecoveryOpportunityDefinition,
  costToServeRepriceDeltaDefinition,
  costToServeServiceRedesignDefinition,
  pricingDiscountLeakageDefinition,
  realizationRateDefinition,
  priceVolumeMixBridgeDefinition,
  pricingStructureMixShiftMarginContributionDefinition,
  varianceDecompositionDefinition,
  pvmDecompositionDefinition,
  costRateVolumeDefinition,
  fxIsolationDefinition,
  oneoffIsolationDefinition,
  oiBridgeComposeDefinition,
  varianceTracebackDefinition,
  pricingIncreaseElasticityScenarioDefinition,
  productivityThroughputDeltaDefinition,
  productivityOiConversionDefinition,
  productivityOiConversionV110Definition,
  productivityRealizationWarningDefinition,
  segmentCleanupOpportunityDefinition,
  segmentCleanupPostureDefinition,
  segmentMarginFloorBreachesDefinition,
  segmentCleanupWarningDefinition,
  segmentMarginAttributionDefinition,
  tnePolicyExceptionRateDefinition,
  tneLeakageOpportunityDefinition,
  tneWarningDefinition,
  daysSalesOutstandingDefinition,
  daysInventoryOutstandingDioDaysDefinition,
  daysPayablesOutstandingDefinition,
  cashConversionCycleDaysDefinition,
  workingCapitalDeltaCashDefinition,
  indirectAddressableShareDefinition,
  indirectProcurementOpportunityDefinition,
  indirectProcurementWarningDefinition,
  sgaSpendAddressableSharePlanDefinition,
  sgaSpendRecoveryFactorScenarioDefinition,
  sgaSpendRecoveryFactorScenarioV110Definition,
  sgaSpendPoolFootprintAllocationDefinition,
  ...sgaSpendTypedPlanGateDefinitions,
  ...sgaSpendHonestReusePlanDefinitions,
  ...professionalFeesFormulaDefinitions,
  ...techSoftwareFormulaDefinitions,
  ...realEstateFootprintFormulaDefinitions,
  ...sharedServicesFormulaDefinitions,
  ...marketingSpendFormulaDefinitions,
  ...networkOptimizationFormulaDefinitions,
  ...capacityUtilizationFormulaDefinitions,
  ...fuelEfficiencyFormulaDefinitions,
  ...outsideContractorsFormulaDefinitions,
  ...laborArbitrageFormulaDefinitions,
  ...churnReductionFormulaDefinitions,
  ...capitalAllocationFormulaDefinitions,
  ...commercialEffectivenessFormulaDefinitions,
  ...riskClaimsFormulaDefinitions,
  financeYoyDeltaDefinition,
  financeTrendDefinition,
  financeCagrDefinition,
  financeMomentumProjectionDefinition,
  lrpMomentumProjectionDefinition,
]);

const approvedFormulaRegistry = createApprovedFormulaRegistry(approvedFormulaDefinitions);

export function listApprovedFormulaDefinitions(): readonly ApprovedFormulaDefinition[] {
  return approvedFormulaRegistry.list();
}

export function getApprovedFormulaDefinition(
  formulaId: string,
  version: string,
): ApprovedFormulaDefinition {
  return approvedFormulaRegistry.get(formulaId, version);
}

export function hasApprovedFormulaDefinition(formulaId: string, version: string): boolean {
  return approvedFormulaRegistry.has(formulaId, version);
}

function validateStrategicExecutionCapacityUtilizationInputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityUtilizationInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const record = asRecord(input);
  if (!record) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object.',
    };
  }

  const requiredCapacityFte = finiteNumberOrNull(record.requiredCapacityFte);
  const availableCapacityFte = finiteNumberOrNull(record.availableCapacityFte);
  if (requiredCapacityFte === null || availableCapacityFte === null) {
    return null;
  }

  const summaries: StrategicExecutionCapacityEvidenceSummary[] = [];
  for (const artifact of artifacts) {
    const summary = parseStrategicExecutionCapacityEvidenceSummary(artifact.contentJson);
    if (!summary) {
      return {
        reasonCode: 'missing_governed_evidence',
        message: 'Approved strategic capacity evidence summary is missing required numbers.',
      };
    }
    summaries.push(summary);

    if (
      artifact.reportingCutAsOf &&
      (!summary.reportingCutAsOf || !sameIsoDate(summary.reportingCutAsOf, artifact.reportingCutAsOf))
    ) {
      return {
        reasonCode: 'missing_governed_evidence',
        message: 'Runtime reporting cut does not match approved evidence reporting cut.',
      };
    }
  }

  const expectedRequired = roundTo(
    summaries.reduce((total, summary) => total + summary.requiredCapacityFte, 0),
    6,
  );
  const expectedAvailable = roundTo(
    summaries.reduce((total, summary) => total + summary.availableCapacityFte, 0),
    6,
  );

  if (
    !sameNumber(requiredCapacityFte, expectedRequired) ||
    !sameNumber(availableCapacityFte, expectedAvailable)
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Formula input does not reconcile to approved governed evidence.',
    };
  }

  return null;
}

function validateStrategicExecutionCapacityUtilizationOutputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityUtilizationInput>,
  output: Readonly<StrategicExecutionCapacityUtilizationOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateStrategicExecutionCapacityUtilizationInputAgainstEvidence(
    input,
    artifacts,
  );
  if (inputBlock) return inputBlock;

  const record = asRecord(input);
  if (!record) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object.',
    };
  }

  const requiredCapacityFte = finiteNumberOrNull(record.requiredCapacityFte);
  const availableCapacityFte = finiteNumberOrNull(record.availableCapacityFte);
  if (
    requiredCapacityFte === null ||
    availableCapacityFte === null ||
    !sameNumber(output.requiredCapacityFte, requiredCapacityFte) ||
    !sameNumber(output.availableCapacityFte, availableCapacityFte)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Formula output does not reconcile to its governed input.',
    };
  }

  const expectedGap = roundTo(Math.max(requiredCapacityFte - availableCapacityFte, 0), 2);
  const expectedRatio = roundTo(availableCapacityFte / requiredCapacityFte, 4);
  const expectedPct = roundTo(expectedRatio * 100, 2);
  if (
    !sameNumber(output.capacityGapFte, expectedGap) ||
    !sameNumber(output.utilizationRatio, expectedRatio) ||
    !sameNumber(output.utilizationPct, expectedPct) ||
    output.statusBand !== capacityStatusBand(expectedRatio)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Formula output failed approved reconciliation checks.',
    };
  }

  return null;
}

type StrategicExecutionCapacityEvidenceSummary = Readonly<{
  rowCount: number | null;
  requiredCapacityFte: number;
  availableCapacityFte: number;
  reportingCutAsOf: string | null;
}>;

function validateStrategicExecutionCapacityRecordsAgainstEvidence(
  input: Readonly<
    | StrategicExecutionCapacityBaselineTotalsInput
    | StrategicExecutionCapacityRiskScoreInput
    | StrategicExecutionCapacityWarningInput
  >,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') {
    return {
      reasonCode:
        recordsResult.reasonCode === 'missing_formula_input'
          ? 'missing_governed_evidence'
          : 'invalid_formula_input',
      message: recordsResult.message,
    };
  }

  const records = recordsResult.records;
  const totalRequired = roundTo(
    records.reduce((sum, record) => sum + record.capacityRequiredFte, 0),
    6,
  );
  const totalAvailable = roundTo(
    records.reduce((sum, record) => sum + record.capacityAvailableFte, 0),
    6,
  );

  const summaries: StrategicExecutionCapacityEvidenceSummary[] = [];
  for (const artifact of artifacts) {
    const summary = parseStrategicExecutionCapacityEvidenceSummary(artifact.contentJson);
    if (!summary) {
      return {
        reasonCode: 'missing_governed_evidence',
        message: 'Approved strategic capacity evidence summary is missing required numbers.',
      };
    }
    summaries.push(summary);

    if (
      artifact.reportingCutAsOf &&
      (!summary.reportingCutAsOf || !sameIsoDate(summary.reportingCutAsOf, artifact.reportingCutAsOf))
    ) {
      return {
        reasonCode: 'missing_governed_evidence',
        message: 'Runtime reporting cut does not match approved evidence reporting cut.',
      };
    }
  }

  const expectedRows = summaries.reduce((total, summary) => total + (summary.rowCount ?? 0), 0);
  const expectedRequired = roundTo(
    summaries.reduce((total, summary) => total + summary.requiredCapacityFte, 0),
    6,
  );
  const expectedAvailable = roundTo(
    summaries.reduce((total, summary) => total + summary.availableCapacityFte, 0),
    6,
  );

  if (
    expectedRows !== records.length ||
    !sameNumber(totalRequired, expectedRequired) ||
    !sameNumber(totalAvailable, expectedAvailable)
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Formula records do not reconcile to approved governed evidence.',
    };
  }

  return null;
}

function validateStrategicExecutionCapacityRiskInputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityRiskScoreInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const baseBlock = validateStrategicExecutionCapacityRecordsAgainstEvidence(input, artifacts);
  if (baseBlock) return baseBlock;
  if (!isFiniteNumber(input.capacityGapPct) || input.capacityGapPct < 0) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'capacityGapPct must be a finite non-negative number',
    };
  }

  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: recordsResult.message,
    };
  }

  const detailsBlock = validateStrategicExecutionCapacityRecordDetailsAgainstEvidence(
    recordsResult.records,
    artifacts,
  );
  if (detailsBlock) return detailsBlock;

  const expectedBaseline = deriveStrategicExecutionCapacityBaselineTotals(recordsResult.records);
  if (!sameNumber(input.capacityGapPct, expectedBaseline.capacityGapPct)) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Risk score capacity gap percent does not reconcile to approved evidence.',
    };
  }

  return null;
}

function validateStrategicExecutionCapacityWarningInputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityWarningInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const recordsBlock = validateStrategicExecutionCapacityRecordsAgainstEvidence(input, artifacts);
  if (recordsBlock) return recordsBlock;
  const numericBlock = validateStrategicExecutionCapacityWarningNumbers(input);
  if (numericBlock) return numericBlock;

  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: recordsResult.message,
    };
  }

  const detailsBlock = validateStrategicExecutionCapacityRecordDetailsAgainstEvidence(
    recordsResult.records,
    artifacts,
  );
  if (detailsBlock) return detailsBlock;

  const expectedBaseline = deriveStrategicExecutionCapacityBaselineTotals(recordsResult.records);
  const expectedCapacityGapFte = roundTo(
    Math.max(
      expectedBaseline.totalCapacityRequiredFte - expectedBaseline.totalCapacityAvailableFte,
      0,
    ),
    2,
  );
  const expectedRisk = deriveStrategicExecutionCapacityRiskScore({
    records: recordsResult.records,
    capacityGapPct: expectedBaseline.capacityGapPct,
    reportingCutAsOf: input.reportingCutAsOf,
  });

  if (
    !sameNumber(input.capacityGapFte, expectedCapacityGapFte) ||
    !sameNumber(input.capacityGapPct, expectedBaseline.capacityGapPct) ||
    input.blockedInitiativeCount !== expectedRisk.blockedInitiativeCount ||
    !sameNumber(
      input.strategicExecutionCapacityRiskScore,
      expectedRisk.strategicExecutionCapacityRiskScore,
    )
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Warning formula inputs do not reconcile to approved formula evidence.',
    };
  }

  return null;
}

function validateStrategicExecutionCapacityBaselineTotalsOutputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityBaselineTotalsInput>,
  output: Readonly<StrategicExecutionCapacityBaselineTotalsOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateStrategicExecutionCapacityRecordsAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;
  const recordsResult = parseStrategicExecutionCapacityFormulaRecords(input);
  if (recordsResult.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: recordsResult.message,
    };
  }
  const expected = deriveStrategicExecutionCapacityBaselineTotals(recordsResult.records);
  if (
    output.initiativeCount !== expected.initiativeCount ||
    !sameNumber(output.totalCapacityRequiredFte, expected.totalCapacityRequiredFte) ||
    !sameNumber(output.totalCapacityAvailableFte, expected.totalCapacityAvailableFte) ||
    !sameNumber(output.capacityGapFte, expected.capacityGapFte) ||
    !sameNumber(output.capacityGapPct, expected.capacityGapPct)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Baseline totals output does not reconcile to approved records.',
    };
  }
  return null;
}

function validateStrategicExecutionCapacityRiskOutputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityRiskScoreInput>,
  output: Readonly<StrategicExecutionCapacityRiskScoreOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateStrategicExecutionCapacityRiskInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;
  const expected = deriveStrategicExecutionCapacityRiskScore(input);
  if (
    output.initiativesAtRiskCount !== expected.initiativesAtRiskCount ||
    output.blockedInitiativeCount !== expected.blockedInitiativeCount ||
    !sameNumber(
      output.strategicExecutionCapacityRiskScore,
      expected.strategicExecutionCapacityRiskScore,
    )
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Risk score output does not reconcile to approved records.',
    };
  }
  return null;
}

function validateStrategicExecutionCapacityWarningOutputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityWarningInput>,
  output: Readonly<StrategicExecutionCapacityWarningOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateStrategicExecutionCapacityWarningInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;
  const expected = deriveStrategicExecutionCapacityWarning(input);
  if (
    output.warningLevel !== expected.warningLevel ||
    !sameStringArray(output.triggeredRuleIds, expected.triggeredRuleIds)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Warning level output does not reconcile to approved inputs.',
    };
  }
  return null;
}

function validateStrategicExecutionCapacityDriftInputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityDriftDetectionInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const record = asRecord(input);
  if (!record) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Formula input must be an object.',
    };
  }

  const metricKey = stringOrNull(record.metricKey);
  const actualValue = finiteNumberOrNull(record.actualValue);
  if (!metricKey || actualValue === null) return null;

  const evidenceValue = strategicExecutionCapacityEvidenceMetricValue(metricKey, artifacts);
  if (evidenceValue === null) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Drift formula actual value has no approved governed evidence metric.',
    };
  }

  if (!sameNumber(actualValue, evidenceValue)) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Drift formula actual value does not reconcile to approved evidence.',
    };
  }

  return null;
}

function validateStrategicExecutionCapacityDriftOutputAgainstEvidence(
  input: Readonly<StrategicExecutionCapacityDriftDetectionInput>,
  output: Readonly<StrategicExecutionCapacityDriftDetectionOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateStrategicExecutionCapacityDriftInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;

  const expected = executeStrategicExecutionCapacityDriftDetection(input);
  if (expected.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: expected.message,
    };
  }

  const expectedOutput = expected.output;
  if (
    output.metricKey !== expectedOutput.metricKey ||
    !sameNumber(output.baselineValue, expectedOutput.baselineValue) ||
    !sameNumber(output.targetValue, expectedOutput.targetValue) ||
    !sameNumber(output.actualValue, expectedOutput.actualValue) ||
    !sameNumber(output.expectedValue, expectedOutput.expectedValue) ||
    !sameNumber(output.deltaFromExpected, expectedOutput.deltaFromExpected) ||
    !sameNumber(output.behindRatio, expectedOutput.behindRatio) ||
    output.driftStatus !== expectedOutput.driftStatus ||
    output.driftStatusCode !== expectedOutput.driftStatusCode ||
    output.triggeredRuleId !== expectedOutput.triggeredRuleId
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Drift formula output does not reconcile to approved target math.',
    };
  }

  return null;
}

type DelayeringEvidenceSummary = Readonly<{
  baselineRunRateCostUsd: number | null;
  scenarioRunRateCostUsd: number | null;
  oneTimeSeveranceUsd: number | null;
  capacityCostAtRiskUsd: number | null;
  maxDepth: number | null;
  targetMaxDepth: number | null;
  averageManagerSpan: number | null;
  targetMinimumManagerSpan: number | null;
  thinSpanManagerCount: number | null;
  managerCount: number | null;
  maxThinSpanManagerRatio: number | null;
  reportingCutAsOf: string | null;
}>;

type DelayeringEvidenceSummaryResolution =
  | Readonly<{ summary: DelayeringEvidenceSummary }>
  | ApprovedFormulaEvidenceValidationBlock;

export function parseWorkforceReductionFormulaScenarioRunEvidence(
  raw: unknown,
): WorkforceReductionFormulaScenarioRunEvidence | null {
  const content = asRecord(raw);
  const scenarioRun = asRecord(content?.workforceScenarioRun);
  if (
    scenarioRun?.schemaVersion !==
    WORKFORCE_REDUCTION_FORMULA_SCENARIO_RUN_SCHEMA_VERSION
  ) {
    return null;
  }
  const sourceRunId = stringOrNull(scenarioRun.sourceRunId);
  const reportingCutAsOf = stringOrNull(scenarioRun.reportingCutAsOf);
  if (
    !sourceRunId ||
    !reportingCutAsOf ||
    !Number.isFinite(Date.parse(reportingCutAsOf)) ||
    !Array.isArray(scenarioRun.cases) ||
    scenarioRun.cases.length === 0
  ) {
    return null;
  }

  const cases: WorkforceReductionFormulaScenarioCase[] = [];
  const caseIds = new Set<string>();
  for (const rawCase of scenarioRun.cases) {
    const scenarioCase = asRecord(rawCase);
    const scenarioCaseId = stringOrNull(scenarioCase?.scenarioCaseId);
    const grossRunRateSavingsUsd = finiteNumberOrNull(
      scenarioCase?.grossRunRateSavingsUsd,
    );
    const oneTimeSeveranceUsd = finiteNumberOrNull(
      scenarioCase?.oneTimeSeveranceUsd,
    );
    const capacityCostAtRiskUsd = finiteNumberOrNull(
      scenarioCase?.capacityCostAtRiskUsd,
    );
    const workforceReductionNetEbitdaOpportunityUsd = finiteNumberOrNull(
      scenarioCase?.workforceReductionNetEbitdaOpportunityUsd,
    );
    if (
      !scenarioCaseId ||
      caseIds.has(scenarioCaseId) ||
      grossRunRateSavingsUsd === null ||
      oneTimeSeveranceUsd === null ||
      capacityCostAtRiskUsd === null ||
      workforceReductionNetEbitdaOpportunityUsd === null ||
      grossRunRateSavingsUsd < 0 ||
      oneTimeSeveranceUsd < 0 ||
      capacityCostAtRiskUsd < 0
    ) {
      return null;
    }
    caseIds.add(scenarioCaseId);
    cases.push({
      scenarioCaseId,
      grossRunRateSavingsUsd,
      oneTimeSeveranceUsd,
      capacityCostAtRiskUsd,
      workforceReductionNetEbitdaOpportunityUsd,
    });
  }

  return {
    schemaVersion: WORKFORCE_REDUCTION_FORMULA_SCENARIO_RUN_SCHEMA_VERSION,
    sourceRunId,
    reportingCutAsOf,
    cases,
  };
}

function validateWorkforceReductionNetEbitdaInputAgainstEvidence(
  input: Readonly<WorkforceReductionNetEbitdaOpportunityInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  if (artifacts.length !== 1) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Workforce net EBITDA requires exactly one approved scenario-run evidence artifact.',
    };
  }
  const scenarioRun = parseWorkforceReductionFormulaScenarioRunEvidence(
    artifacts[0].contentJson,
  );
  if (!scenarioRun) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Approved workforce evidence is missing its versioned scenario-run and case bindings.',
    };
  }
  const scenarioCase = scenarioRun.cases.find(
    (candidate) => candidate.scenarioCaseId === input.scenarioCaseId,
  );
  if (
    scenarioRun.sourceRunId !== input.sourceRunId ||
    !scenarioCase ||
    Date.parse(scenarioRun.reportingCutAsOf) !==
      Date.parse(input.reportingCutAsOf) ||
    !sameNumber(
      scenarioCase.grossRunRateSavingsUsd,
      input.grossRunRateSavingsUsd,
    ) ||
    !sameNumber(
      scenarioCase.oneTimeSeveranceUsd,
      input.oneTimeSeveranceUsd,
    ) ||
    !sameNumber(
      scenarioCase.capacityCostAtRiskUsd,
      input.capacityCostAtRiskUsd,
    )
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Workforce formula input does not reconcile to the approved source run and scenario case.',
    };
  }
  return null;
}

function validateDelayeringNetEbitdaOpportunityInputAgainstEvidence(
  input: Readonly<DelayeringNetEbitdaOpportunityInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const executable = executeDelayeringNetEbitdaOpportunity(input);
  if (executable.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: executable.message,
    };
  }

  const summaryResult = resolveDelayeringEvidenceSummary(artifacts);
  if ('reasonCode' in summaryResult) return summaryResult;
  const { summary } = summaryResult;

  if (
    summary.baselineRunRateCostUsd === null ||
    summary.scenarioRunRateCostUsd === null ||
    summary.oneTimeSeveranceUsd === null ||
    summary.capacityCostAtRiskUsd === null
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Approved org-cost evidence is missing baseline cost, scenario cost, severance, or capacity-at-risk values.',
    };
  }

  if (
    !sameNumber(input.baselineRunRateCostUsd, summary.baselineRunRateCostUsd) ||
    !sameNumber(input.scenarioRunRateCostUsd, summary.scenarioRunRateCostUsd) ||
    !sameNumber(input.oneTimeSeveranceUsd, summary.oneTimeSeveranceUsd) ||
    !sameNumber(input.capacityCostAtRiskUsd, summary.capacityCostAtRiskUsd)
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Org Analysis Optimization formula input does not reconcile to approved org-cost evidence.',
    };
  }

  return validateDelayeringReportingCut(input.reportingCutAsOf, summary.reportingCutAsOf);
}

function validateDelayeringNetEbitdaOpportunityOutputAgainstEvidence(
  input: Readonly<DelayeringNetEbitdaOpportunityInput>,
  output: Readonly<DelayeringNetEbitdaOpportunityOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateDelayeringNetEbitdaOpportunityInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;

  const expected = executeDelayeringNetEbitdaOpportunity(input);
  if (expected.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: expected.message,
    };
  }

  if (
    !sameNumber(output.grossRunRateSavingsUsd, expected.output.grossRunRateSavingsUsd) ||
    !sameNumber(output.oneTimeSeveranceUsd, expected.output.oneTimeSeveranceUsd) ||
    !sameNumber(output.capacityCostAtRiskUsd, expected.output.capacityCostAtRiskUsd) ||
    !sameNumber(
      output.delayeringNetEbitdaOpportunityUsd,
      expected.output.delayeringNetEbitdaOpportunityUsd,
    ) ||
    output.statusBand !== expected.output.statusBand
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Org Analysis Optimization formula output failed approved reconciliation checks.',
    };
  }

  return null;
}

type CarrierTransportationCostFormulaShipment = Readonly<{
  shipmentId: string;
  laneId: string;
  carrier: string;
  shipDate: string;
  revenueUsd: number;
  carrierCostUsd: number;
  contractedCarrierCostUsd: number;
  marginFloorPct: number;
  contractEffectiveStart: string | null;
  contractEffectiveEnd: string | null;
}>;

type CarrierTransportationCostEvidenceSummary = Readonly<{
  baselineGrossMarginUsd: number;
  belowFloorRecoveryUsd: number;
  contractRateRecoveryUsd: number;
  carrierRateDeltaRecoveryUsd: number;
  reportingCutAsOf: string | null;
}>;

type CarrierTransportationCostScenarioEvidenceSummary = Readonly<{
  baseline: CarrierTransportationCostEvidenceSummary;
  carrierRateDeltaBaseSpendUsd: number;
}>;

type CarrierTransportationCostEvidenceSummaryResolution =
  | Readonly<{ summary: CarrierTransportationCostEvidenceSummary }>
  | ApprovedFormulaEvidenceValidationBlock;

function validateCarrierTransportationCostOpportunityInputAgainstEvidence(
  input: Readonly<CarrierTransportationCostOpportunityInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const executable = executeCarrierTransportationCostOpportunity(input);
  if (executable.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: executable.message,
    };
  }

  const summaryResult = resolveCarrierTransportationCostEvidenceSummary(input, artifacts);
  if ('reasonCode' in summaryResult) return summaryResult;
  const { summary } = summaryResult;

  if (
    !sameNumber(input.baselineGrossMarginUsd, summary.baselineGrossMarginUsd) ||
    exceedsEvidenceBound(input.belowFloorRecoveryUsd, summary.belowFloorRecoveryUsd) ||
    exceedsEvidenceBound(input.contractRateRecoveryUsd, summary.contractRateRecoveryUsd) ||
    exceedsEvidenceBound(input.carrierRateDeltaRecoveryUsd, summary.carrierRateDeltaRecoveryUsd)
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Carrier Transportation Cost formula input exceeds approved shipment, rate-card, contract, or margin-floor evidence.',
    };
  }

  return validateCarrierTransportationCostReportingCut(
    input.reportingCutAsOf,
    summary.reportingCutAsOf,
  );
}

function validateCarrierTransportationCostOpportunityOutputAgainstEvidence(
  input: Readonly<CarrierTransportationCostOpportunityInput>,
  output: Readonly<CarrierTransportationCostOpportunityOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateCarrierTransportationCostOpportunityInputAgainstEvidence(
    input,
    artifacts,
  );
  if (inputBlock) return inputBlock;

  const expected = executeCarrierTransportationCostOpportunity(input);
  if (expected.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: expected.message,
    };
  }

  if (
    !sameNumber(output.baselineGrossMarginUsd, expected.output.baselineGrossMarginUsd) ||
    !sameNumber(output.belowFloorRecoveryUsd, expected.output.belowFloorRecoveryUsd) ||
    !sameNumber(output.contractRateRecoveryUsd, expected.output.contractRateRecoveryUsd) ||
    !sameNumber(output.carrierRateDeltaRecoveryUsd, expected.output.carrierRateDeltaRecoveryUsd) ||
    !sameNumber(output.carrierCostOpportunityUsd, expected.output.carrierCostOpportunityUsd) ||
    output.statusBand !== expected.output.statusBand
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Carrier Transportation Cost formula output failed approved reconciliation checks.',
    };
  }

  return null;
}

function validateCarrierTransportationCostOpportunityScenarioInputAgainstEvidence(
  input: Readonly<CarrierTransportationCostOpportunityScenarioInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const executable = executeCarrierTransportationCostOpportunityScenario(input);
  if (executable.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: executable.message,
    };
  }

  const summaryResult =
    resolveCarrierTransportationCostScenarioEvidenceSummary(input, artifacts);
  if ('reasonCode' in summaryResult) return summaryResult;
  const { baseline, carrierRateDeltaBaseSpendUsd } = summaryResult.summary;
  const expectedBaselineOpportunityUsd = roundTo(
    baseline.belowFloorRecoveryUsd + baseline.contractRateRecoveryUsd,
    2,
  );
  if (
    !sameNumber(input.baselineGrossMarginUsd, baseline.baselineGrossMarginUsd) ||
    !sameNumber(input.belowFloorRecoveryUsd, baseline.belowFloorRecoveryUsd) ||
    !sameNumber(input.contractRateRecoveryUsd, baseline.contractRateRecoveryUsd) ||
    !sameNumber(
      input.carrierRateDeltaBaseSpendUsd,
      carrierRateDeltaBaseSpendUsd,
    ) ||
    !sameNumber(input.expectedBaselineOpportunityUsd, expectedBaselineOpportunityUsd)
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Carrier scenario baseline or carrier-spend driver does not reconcile to approved transportation evidence.',
    };
  }
  return validateCarrierTransportationCostReportingCut(
    input.reportingCutAsOf,
    baseline.reportingCutAsOf,
  );
}

function resolveCarrierTransportationCostScenarioEvidenceSummary(
  input: Readonly<CarrierTransportationCostOpportunityScenarioInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
):
  | Readonly<{ summary: CarrierTransportationCostScenarioEvidenceSummary }>
  | ApprovedFormulaEvidenceValidationBlock {
  const baselineResult = resolveCarrierTransportationCostEvidenceSummary(
    {
      baselineGrossMarginUsd: input.baselineGrossMarginUsd,
      belowFloorRecoveryUsd: input.belowFloorRecoveryUsd,
      contractRateRecoveryUsd: input.contractRateRecoveryUsd,
      carrierRateDeltaRecoveryUsd: 0,
      carrier: input.carrier,
      rateDeltaPct: input.scenarioRateDeltaPct,
      reportingCutAsOf: input.reportingCutAsOf,
    },
    artifacts,
  );
  if ('reasonCode' in baselineResult) return baselineResult;
  const artifact = artifacts[0];
  if (!artifact) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Carrier Transportation Cost scenarios require exactly one approved governed transportation snapshot.',
    };
  }
  const shipments = latestPeriodTransportationShipments(
    parseCarrierTransportationCostEvidenceShipments(artifact.contentJson),
  );
  return {
    summary: {
      baseline: baselineResult.summary,
      carrierRateDeltaBaseSpendUsd: roundTo(
        carrierScenarioRateDeltaBaseSpend(input.carrier, shipments),
        2,
      ),
    },
  };
}

function validateCarrierTransportationCostOpportunityScenarioOutputAgainstEvidence(
  input: Readonly<CarrierTransportationCostOpportunityScenarioInput>,
  output: Readonly<CarrierTransportationCostOpportunityScenarioOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock =
    validateCarrierTransportationCostOpportunityScenarioInputAgainstEvidence(
      input,
      artifacts,
    );
  if (inputBlock) return inputBlock;
  const expected = executeCarrierTransportationCostOpportunityScenario(input);
  if (expected.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: expected.message,
    };
  }
  const keys: readonly (keyof CarrierTransportationCostOpportunityScenarioOutput)[] = [
    'baselineGrossMarginUsd',
    'belowFloorRecoveryUsd',
    'contractRateRecoveryUsd',
    'carrierRateDeltaBaseSpendUsd',
    'scenarioRateDeltaPct',
    'baselineCarrierCostOpportunityUsd',
    'baselineOpportunityVarianceUsd',
    'scenarioCarrierRateDeltaRecoveryUsd',
    'scenarioCarrierCostOpportunityUsd',
    'operatingIncomeDeltaUsd',
  ];
  if (
    keys.some((key) => !sameNumber(output[key] as number, expected.output[key] as number)) ||
    output.statusBand !== expected.output.statusBand
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Carrier Transportation Cost scenario output failed approved reconciliation checks.',
    };
  }
  return null;
}

function validateDelayeringOrgHealthWarningInputAgainstEvidence(
  input: Readonly<DelayeringOrgHealthWarningInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const executable = executeDelayeringOrgHealthWarningLevel(input);
  if (executable.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: executable.message,
    };
  }

  const summaryResult = resolveDelayeringEvidenceSummary(artifacts);
  if ('reasonCode' in summaryResult) return summaryResult;
  const { summary } = summaryResult;

  if (
    summary.maxDepth === null ||
    summary.targetMaxDepth === null ||
    summary.averageManagerSpan === null ||
    summary.targetMinimumManagerSpan === null ||
    summary.thinSpanManagerCount === null ||
    summary.managerCount === null ||
    summary.maxThinSpanManagerRatio === null
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Approved org-health evidence is missing span, layer, or threshold values.',
    };
  }

  if (
    !sameNumber(input.maxDepth, summary.maxDepth) ||
    !sameNumber(input.targetMaxDepth, summary.targetMaxDepth) ||
    !sameNumber(input.averageManagerSpan, summary.averageManagerSpan) ||
    !sameNumber(input.targetMinimumManagerSpan, summary.targetMinimumManagerSpan) ||
    !sameNumber(input.thinSpanManagerCount, summary.thinSpanManagerCount) ||
    !sameNumber(input.managerCount, summary.managerCount) ||
    !sameNumber(input.maxThinSpanManagerRatio, summary.maxThinSpanManagerRatio)
  ) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Org-health warning formula input does not reconcile to approved evidence.',
    };
  }

  return validateDelayeringReportingCut(input.reportingCutAsOf, summary.reportingCutAsOf);
}

function validateDelayeringOrgHealthWarningOutputAgainstEvidence(
  input: Readonly<DelayeringOrgHealthWarningInput>,
  output: Readonly<DelayeringOrgHealthWarningOutput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const inputBlock = validateDelayeringOrgHealthWarningInputAgainstEvidence(input, artifacts);
  if (inputBlock) return inputBlock;

  const expected = executeDelayeringOrgHealthWarningLevel(input);
  if (expected.status === 'blocked') {
    return {
      reasonCode: 'invalid_formula_input',
      message: expected.message,
    };
  }

  if (
    output.warningLevel !== expected.output.warningLevel ||
    output.warningLevelCode !== expected.output.warningLevelCode ||
    !sameStringArray(output.triggeredRuleIds, expected.output.triggeredRuleIds)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Org-health warning formula output failed approved reconciliation checks.',
    };
  }

  return null;
}

function resolveDelayeringEvidenceSummary(
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): DelayeringEvidenceSummaryResolution {
  const summaries: DelayeringEvidenceSummary[] = [];
  for (const artifact of artifacts) {
    const isOrgCostSnapshot =
      artifact.schemaVersion === 'org_analysis_optimization_governed_snapshot_v1' ||
      asRecord(artifact.contentJson)?.schemaVersion ===
        'org_analysis_optimization_governed_snapshot_v1';
    if (!isOrgCostSnapshot) continue;

    const summary = parseDelayeringEvidenceSummary(artifact.contentJson);
    if (!summary) {
      return {
        reasonCode: 'missing_governed_evidence',
        message: 'Approved org-cost governed snapshot evidence is missing its summary.',
      };
    }
    summaries.push(summary);
  }

  if (summaries.length === 0) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Approved org-cost governed snapshot evidence is required.',
    };
  }

  if (summaries.length > 1) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Delayering formulas require exactly one approved org-cost governed snapshot.',
    };
  }

  return { summary: summaries[0] };
}

function validateDelayeringReportingCut(
  inputReportingCutAsOf: string | undefined,
  evidenceReportingCutAsOf: string | null,
): ApprovedFormulaEvidenceValidationBlock | null {
  if (
    inputReportingCutAsOf &&
    evidenceReportingCutAsOf &&
    !sameIsoDate(inputReportingCutAsOf, evidenceReportingCutAsOf)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Formula reporting cut does not reconcile to approved org-cost evidence.',
    };
  }
  return null;
}

function validateCarrierTransportationCostReportingCut(
  inputReportingCutAsOf: string | undefined,
  evidenceReportingCutAsOf: string | null,
): ApprovedFormulaEvidenceValidationBlock | null {
  if (
    inputReportingCutAsOf &&
    evidenceReportingCutAsOf &&
    !sameIsoDate(inputReportingCutAsOf, evidenceReportingCutAsOf)
  ) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Formula reporting cut does not reconcile to approved transportation cost evidence.',
    };
  }
  return null;
}

function exceedsEvidenceBound(inputValue: number, evidenceValue: number): boolean {
  return inputValue - evidenceValue > NUMBER_TOLERANCE;
}

function resolveCarrierTransportationCostEvidenceSummary(
  input: Readonly<CarrierTransportationCostOpportunityInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): CarrierTransportationCostEvidenceSummaryResolution {
  if (artifacts.length !== 1) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Carrier Transportation Cost formulas require exactly one approved governed transportation snapshot.',
    };
  }

  const artifact = artifacts[0];
  const shipments = parseCarrierTransportationCostEvidenceShipments(artifact.contentJson);
  if (shipments.length === 0) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: 'Approved transportation evidence is missing shipment lane spend rows.',
    };
  }

  const outOfWindow = shipments.find((shipment) =>
    !transportationContractWindowMatchesShipment(shipment),
  );
  if (outOfWindow) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Approved transportation evidence contains a shipment outside its effective contract window.',
    };
  }

  // Approved snapshots may carry multi-period lane history; opportunity
  // evidence reconciles against the latest governed period only, matching the
  // module engine's point-in-time cut. History periods feed drift/trend reads.
  return {
    summary: deriveCarrierTransportationCostOpportunitySummary(
      input,
      latestPeriodTransportationShipments(shipments),
      artifact,
    ),
  };
}

function latestPeriodTransportationShipments(
  shipments: readonly CarrierTransportationCostFormulaShipment[],
): CarrierTransportationCostFormulaShipment[] {
  const periodKeys = Array.from(
    new Set(shipments.map((shipment) => shipment.shipDate.slice(0, 7))),
  ).sort();
  if (periodKeys.length <= 1) return [...shipments];
  const currentPeriodKey = periodKeys[periodKeys.length - 1];
  return shipments.filter(
    (shipment) => shipment.shipDate.slice(0, 7) === currentPeriodKey,
  );
}

function deriveCarrierTransportationCostOpportunitySummary(
  input: Readonly<CarrierTransportationCostOpportunityInput>,
  shipments: readonly CarrierTransportationCostFormulaShipment[],
  artifact: ApprovedFormulaEvidenceArtifact,
): CarrierTransportationCostEvidenceSummary {
  const baselineGrossMarginUsd = roundTo(
    shipments.reduce(
      (sum, shipment) => sum + shipment.revenueUsd - shipment.carrierCostUsd,
      0,
    ),
    2,
  );
  const contractRateRecoveryUsd = roundTo(
    shipments.reduce(
      (sum, shipment) =>
        sum + Math.max(0, shipment.carrierCostUsd - shipment.contractedCarrierCostUsd),
      0,
    ),
    2,
  );
  const belowFloorRecoveryUsd = roundTo(
    Array.from(groupTransportationShipmentsByLane(shipments).values()).reduce(
      (sum, laneShipments) => sum + belowFloorRecoveryForLane(laneShipments),
      0,
    ),
    2,
  );
  const carrierRateDeltaRecoveryUsd = roundTo(
    carrierRateDeltaRecovery(input, shipments),
    2,
  );
  return {
    baselineGrossMarginUsd,
    belowFloorRecoveryUsd,
    contractRateRecoveryUsd,
    carrierRateDeltaRecoveryUsd,
    reportingCutAsOf:
      stringOrNull(artifact.reportingCutAsOf) ??
      transportationEvidenceReportingCut(artifact.contentJson),
  };
}

function parseCarrierTransportationCostEvidenceShipments(
  raw: unknown,
): CarrierTransportationCostFormulaShipment[] {
  const artifact = asRecord(raw);
  const state = asRecord(artifact?.state);
  const shipments = Array.isArray(state?.shipments) ? state.shipments : [];
  return shipments
    .map((item) => {
      const record = asRecord(item);
      if (!record) return null;
      const shipmentId = stringOrNull(record.shipmentId);
      const laneId = stringOrNull(record.laneId);
      const carrier = stringOrNull(record.carrier);
      const shipDate = stringOrNull(record.shipDate);
      const revenueUsd = finiteNumberOrNull(record.revenueUsd);
      const carrierCostUsd = finiteNumberOrNull(record.carrierCostUsd);
      const contractedCarrierCostUsd = finiteNumberOrNull(record.contractedCarrierCostUsd);
      const marginFloorPct = finiteNumberOrNull(record.marginFloorPct);
      if (
        !shipmentId ||
        !laneId ||
        !carrier ||
        !shipDate ||
        revenueUsd === null ||
        carrierCostUsd === null ||
        contractedCarrierCostUsd === null ||
        marginFloorPct === null ||
        revenueUsd < 0 ||
        carrierCostUsd < 0 ||
        contractedCarrierCostUsd < 0 ||
        marginFloorPct < 0
      ) {
        return null;
      }
      return {
        shipmentId,
        laneId,
        carrier,
        shipDate,
        revenueUsd,
        carrierCostUsd,
        contractedCarrierCostUsd,
        marginFloorPct,
        contractEffectiveStart: stringOrNull(record.contractEffectiveStart),
        contractEffectiveEnd: stringOrNull(record.contractEffectiveEnd),
      };
    })
    .filter((item): item is CarrierTransportationCostFormulaShipment => item !== null);
}

function groupTransportationShipmentsByLane(
  shipments: readonly CarrierTransportationCostFormulaShipment[],
): Map<string, CarrierTransportationCostFormulaShipment[]> {
  const groups = new Map<string, CarrierTransportationCostFormulaShipment[]>();
  for (const shipment of shipments) {
    const rows = groups.get(shipment.laneId) ?? [];
    rows.push(shipment);
    groups.set(shipment.laneId, rows);
  }
  return groups;
}

function belowFloorRecoveryForLane(
  shipments: readonly CarrierTransportationCostFormulaShipment[],
): number {
  const lane = shipments.reduce(
    (acc, shipment) => {
      acc.revenueUsd += shipment.revenueUsd;
      acc.carrierCostUsd += shipment.carrierCostUsd;
      acc.targetMarginUsd += shipment.revenueUsd * shipment.marginFloorPct;
      acc.contractRecoveryUsd += Math.max(
        0,
        shipment.carrierCostUsd - shipment.contractedCarrierCostUsd,
      );
      return acc;
    },
    {
      revenueUsd: 0,
      carrierCostUsd: 0,
      targetMarginUsd: 0,
      contractRecoveryUsd: 0,
    },
  );
  const currentMarginAfterContractRecovery =
    lane.revenueUsd - lane.carrierCostUsd + lane.contractRecoveryUsd;
  return Math.max(0, lane.targetMarginUsd - currentMarginAfterContractRecovery);
}

function carrierRateDeltaRecovery(
  input: Readonly<CarrierTransportationCostOpportunityInput>,
  shipments: readonly CarrierTransportationCostFormulaShipment[],
): number {
  const carrier = stringOrNull(input.carrier);
  const rateDeltaPct = finiteNumberOrNull(input.rateDeltaPct);
  if (!carrier || rateDeltaPct === null || rateDeltaPct <= 0) return 0;
  const normalizedDelta = rateDeltaPct > 1 ? rateDeltaPct / 100 : rateDeltaPct;
  if (normalizedDelta <= 0 || normalizedDelta > 1) return 0;
  return shipments
    .filter((shipment) => shipment.carrier === carrier)
    .reduce((sum, shipment) => sum + shipment.carrierCostUsd * normalizedDelta, 0);
}

function carrierScenarioRateDeltaBaseSpend(
  carrierInput: string,
  shipments: readonly CarrierTransportationCostFormulaShipment[],
): number {
  const carrier = stringOrNull(carrierInput);
  if (!carrier) return 0;
  return shipments
    .filter((shipment) => shipment.carrier === carrier)
    .reduce((sum, shipment) => sum + shipment.carrierCostUsd, 0);
}

function transportationEvidenceReportingCut(raw: unknown): string | null {
  const artifact = asRecord(raw);
  const source = asRecord(artifact?.source);
  const summary = asRecord(artifact?.summary);
  return (
    stringOrNull(source?.sourceAsOfMax) ??
    stringOrNull(summary?.sourceAsOfMax) ??
    null
  );
}

function transportationContractWindowMatchesShipment(
  shipment: CarrierTransportationCostFormulaShipment,
): boolean {
  const ship = dateOnlyTimestamp(shipment.shipDate);
  if (ship === null) return false;
  const start = shipment.contractEffectiveStart
    ? dateOnlyTimestamp(shipment.contractEffectiveStart)
    : null;
  const end = shipment.contractEffectiveEnd
    ? dateOnlyTimestamp(shipment.contractEffectiveEnd)
    : null;
  if (shipment.contractEffectiveStart && start === null) return false;
  if (shipment.contractEffectiveEnd && end === null) return false;
  if (start !== null && ship < start) return false;
  if (end !== null && ship > end) return false;
  return true;
}

function dateOnlyTimestamp(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const parsed = new Date(timestamp);
  return parsed.getUTCFullYear() === Number(match[1]) &&
    parsed.getUTCMonth() + 1 === Number(match[2]) &&
    parsed.getUTCDate() === Number(match[3])
    ? timestamp
    : null;
}

function strategicExecutionCapacityEvidenceMetricValue(
  metricKey: string,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): number | null {
  const records: StrategicExecutionCapacityFormulaRecord[] = [];
  for (const artifact of artifacts) {
    const parsedRecords = parseStrategicExecutionCapacityEvidenceRecords(artifact.contentJson);
    if (parsedRecords) records.push(...parsedRecords);
  }
  if (records.length === 0) return null;

  const baseline = deriveStrategicExecutionCapacityBaselineTotals(records);
  const risk = deriveStrategicExecutionCapacityRiskScore({
    records,
    capacityGapPct: baseline.capacityGapPct,
  });
  if (metricKey === 'initiative_count') return baseline.initiativeCount;
  if (metricKey === 'total_capacity_required_fte') return baseline.totalCapacityRequiredFte;
  if (metricKey === 'total_capacity_available_fte') return baseline.totalCapacityAvailableFte;
  if (metricKey === 'capacity_gap_fte') return baseline.capacityGapFte;
  if (metricKey === 'capacity_gap_pct') return baseline.capacityGapPct;
  if (metricKey === 'initiatives_at_risk_count') return risk.initiativesAtRiskCount;
  if (metricKey === 'blocked_initiative_count') return risk.blockedInitiativeCount;
  if (metricKey === 'strategic_execution_capacity_risk_score') {
    return risk.strategicExecutionCapacityRiskScore;
  }
  return null;
}

function validateStrategicExecutionCapacityRecordDetailsAgainstEvidence(
  records: readonly StrategicExecutionCapacityFormulaRecord[],
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const evidenceRecords: StrategicExecutionCapacityFormulaRecord[] = [];
  for (const artifact of artifacts) {
    const parsedRecords = parseStrategicExecutionCapacityEvidenceRecords(artifact.contentJson);
    if (!parsedRecords) {
      return {
        reasonCode: 'missing_governed_evidence',
        message: 'Approved strategic capacity evidence records are required for derived formulas.',
      };
    }
    evidenceRecords.push(...parsedRecords);
  }

  if (evidenceRecords.length !== records.length) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: 'Formula record count does not reconcile to approved evidence records.',
    };
  }

  const evidenceByInitiative = new Map<string, StrategicExecutionCapacityFormulaRecord>();
  for (const evidenceRecord of evidenceRecords) {
    if (evidenceByInitiative.has(evidenceRecord.initiativeId)) {
      return {
        reasonCode: 'formula_reconciliation_failure',
        message: 'Approved evidence contains duplicate strategic execution initiatives.',
      };
    }
    evidenceByInitiative.set(evidenceRecord.initiativeId, evidenceRecord);
  }

  for (const record of records) {
    const evidenceRecord = evidenceByInitiative.get(record.initiativeId);
    if (
      !evidenceRecord ||
      !sameNumber(record.capacityRequiredFte, evidenceRecord.capacityRequiredFte) ||
      !sameNumber(record.capacityAvailableFte, evidenceRecord.capacityAvailableFte) ||
      normalizeStatus(record.currentStatus) !== normalizeStatus(evidenceRecord.currentStatus) ||
      normalizeOptionalText(record.blockedReason) !== normalizeOptionalText(evidenceRecord.blockedReason)
    ) {
      return {
        reasonCode: 'formula_reconciliation_failure',
        message: 'Formula records do not reconcile to approved evidence records.',
      };
    }
  }

  return null;
}

function validateStrategicExecutionCapacityWarningNumbers(
  input: Readonly<StrategicExecutionCapacityWarningInput>,
): ApprovedFormulaEvidenceValidationBlock | null {
  const requiredNumbers = [
    input.capacityGapFte,
    input.capacityGapPct,
    input.blockedInitiativeCount,
    input.strategicExecutionCapacityRiskScore,
  ];
  if (requiredNumbers.some((value) => !Number.isFinite(value) || value < 0)) {
    return {
      reasonCode: 'invalid_formula_input',
      message: 'Warning inputs must be finite non-negative numbers',
    };
  }
  return null;
}

function parseStrategicExecutionCapacityEvidenceSummary(
  raw: unknown,
): StrategicExecutionCapacityEvidenceSummary | null {
  const content = asRecord(raw);
  const summary = asRecord(content?.summary);
  if (!summary) return null;

  const requiredCapacityFte = finiteNumberOrNull(summary.capacityRequiredFte);
  const availableCapacityFte = finiteNumberOrNull(summary.capacityAvailableFte);
  if (requiredCapacityFte === null || availableCapacityFte === null) return null;

  const source = asRecord(content?.source);
  return {
    rowCount: finiteNumberOrNull(summary.rowCount),
    requiredCapacityFte,
    availableCapacityFte,
    reportingCutAsOf: stringOrNull(summary.sourceAsOfMax) ?? stringOrNull(source?.sourceAsOfMax),
  };
}

function parseStrategicExecutionCapacityEvidenceRecords(
  raw: unknown,
): readonly StrategicExecutionCapacityFormulaRecord[] | null {
  const content = asRecord(raw);
  const rows = Array.isArray(content?.records) ? content.records : null;
  if (!rows) return null;

  const records: StrategicExecutionCapacityFormulaRecord[] = [];
  const seen = new Set<string>();
  for (const item of rows) {
    const row = asRecord(item);
    if (!row) return null;
    const initiativeId = stringOrNull(row.initiativeId);
    const capacityRequiredFte = finiteNumberOrNull(row.capacityRequiredFte);
    const capacityAvailableFte = finiteNumberOrNull(row.capacityAvailableFte);
    if (
      !initiativeId ||
      seen.has(initiativeId) ||
      capacityRequiredFte === null ||
      capacityAvailableFte === null ||
      capacityRequiredFte < 0 ||
      capacityAvailableFte < 0
    ) {
      return null;
    }
    seen.add(initiativeId);
    records.push(
      formulaRecord(
        initiativeId,
        capacityRequiredFte,
        capacityAvailableFte,
        stringOrNull(row.currentStatus),
        stringOrNull(row.blockedReason),
      ),
    );
  }

  return records;
}

function parseDelayeringEvidenceSummary(raw: unknown): DelayeringEvidenceSummary | null {
  const content = asRecord(raw);
  if (!content) return null;
  const summary = asRecord(content.summary);
  if (!summary) return null;
  const source = asRecord(content.source);

  return {
    baselineRunRateCostUsd: finiteNumberOrNull(summary.baselineRunRateCostUsd),
    scenarioRunRateCostUsd: finiteNumberOrNull(summary.scenarioRunRateCostUsd),
    oneTimeSeveranceUsd: finiteNumberOrNull(summary.oneTimeSeveranceUsd),
    capacityCostAtRiskUsd: finiteNumberOrNull(summary.capacityCostAtRiskUsd),
    maxDepth: finiteNumberOrNull(summary.maxDepth),
    targetMaxDepth: finiteNumberOrNull(summary.targetMaxDepth),
    averageManagerSpan: finiteNumberOrNull(summary.averageManagerSpan),
    targetMinimumManagerSpan: finiteNumberOrNull(summary.targetMinimumManagerSpan),
    thinSpanManagerCount: finiteNumberOrNull(summary.thinSpanManagerCount),
    managerCount: finiteNumberOrNull(summary.managerCount),
    maxThinSpanManagerRatio: finiteNumberOrNull(summary.maxThinSpanManagerRatio),
    reportingCutAsOf:
      stringOrNull(summary.reportingCutAsOf) ??
      stringOrNull(summary.sourceAsOfMax) ??
      stringOrNull(source?.sourceAsOfMax),
  };
}

// ---------------------------------------------------------------------------
// segment_margin_attribution — dual-authority evidence validation
// (#4754 amendment 4, finding 1).
//
// Admitting TWO source systems also admits EITHER ONE ALONE. The runtime's
// `validateEvidenceSourceSystems` judges only the evidence items a caller
// actually supplies, so with no input-vs-evidence check a caller could present
// the MODELED margin-fact artifact by itself, hand in whatever `segmentAnchors`
// it liked, and `execute` would tie the modeled channel cells to those
// caller-authored anchors to the cent and return a PASS citation — approved
// totals that no approved snapshot ever established.
//
// This validator runs BEFORE `execute` and closes that door: exactly one artifact
// of each authority, every anchor a restatement of the governed snapshot's own
// content, and both authorities on one reporting cut. The anchors stop being an
// input the caller asserts and become the approved segment P&L the runtime loaded.
// ---------------------------------------------------------------------------

type SegmentMarginAttributionSnapshotEvidence = Readonly<{
  anchors: readonly SegmentMarginAttributionAnchorInput[];
  asOf: string | null;
}>;

// The named refusal messages this validator emits are declared ABOVE the
// implementation-digest-input list (beside the value seals that hash them) —
// they are digested VALUES now, so they must exist before that list evaluates
// (#4754 amendment 17).

function validateSegmentMarginAttributionInputAgainstEvidence(
  input: Readonly<SegmentMarginAttributionInput>,
  artifacts: readonly ApprovedFormulaEvidenceArtifact[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const marginFacts = artifacts.filter((artifact) =>
    SEGMENT_MARGIN_ATTRIBUTION_MARGIN_FACT_SCHEMA_VERSIONS.has(artifact.schemaVersion),
  );
  const snapshots = artifacts.filter((artifact) =>
    SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_SCHEMA_VERSIONS.has(artifact.schemaVersion),
  );

  if (marginFacts.length === 0) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Segment margin attribution requires the approved channel margin-fact decomposition.',
    };
  }
  if (snapshots.length === 0) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Segment margin attribution requires the approved segment margin snapshot its ' +
        'anchors restate.',
    };
  }
  if (marginFacts.length > 1 || snapshots.length > 1) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message:
        'Segment margin attribution requires exactly one approved margin-fact ' +
        'decomposition and exactly one approved segment margin snapshot.',
    };
  }

  const snapshot = parseSegmentMarginAttributionSnapshotEvidence(snapshots[0].contentJson);
  if (!snapshot) {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Approved segment margin snapshot evidence is missing its per-segment net revenue ' +
        'and operating margin rows.',
    };
  }

  const anchorBlock = reconcileSegmentMarginAttributionAnchors(
    input.segmentAnchors ?? [],
    snapshot.anchors,
  );
  if (anchorBlock) return anchorBlock;

  // THE CELLS ARE EVIDENCE TOO (#4754 amendment 5, finding 1). Reconciling the
  // anchors alone leaves the ALLOCATION unchecked: many different channel splits
  // preserve the same approved segment totals, so a caller could keep every
  // anchor honest and still invent which channel earned the margin — and the
  // cent tie-out inside `execute` would pass it, because it only checks the
  // per-segment sums. The published channel shares are the whole point of this
  // intent, so they are reconciled to the governed margin-fact rows here, by
  // identity AND by value, before `execute` ever runs.
  const marginFactRows = parseSegmentMarginAttributionMarginFactRows(
    marginFacts[0].contentJson,
  );
  if (marginFactRows.status === 'malformed') {
    return {
      reasonCode: 'missing_governed_evidence',
      message:
        'Approved channel margin-fact evidence is missing its customer, SKU, channel and ' +
        'segment rows.',
    };
  }
  // Its own named refusal, deliberately NOT a cut message: the emission must
  // never retell "these rows disagree about what a channel is called" as a
  // freshness problem. It surfaces with its true reason and the formula's own
  // words (#4754 amendment 12, finding 2).
  if (marginFactRows.status === 'conflicting_channel_label') {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: SEGMENT_MARGIN_ATTRIBUTION_CHANNEL_LABEL_CONFLICT_MESSAGE,
    };
  }
  const cellBlock = reconcileSegmentMarginAttributionCells(
    input.cells ?? [],
    marginFactRows.cells,
  );
  if (cellBlock) return cellBlock;

  // ONE CUT, BOTH AUTHORITIES — AND A MISSING CUT IS A REFUSAL, NOT A SKIP
  // (#4754 amendment 8, finding 1). The channel detail and the snapshot must
  // describe the same cut, or a refreshed snapshot that preserved segment totals
  // would let a stale channel allocation publish as the current attribution.
  //
  // These comparisons used to be CONDITIONAL on the values being present, so an
  // authority whose content omitted `asOf` simply skipped its own check — and
  // with both omitting it, any `reportingCutAsOf` a caller cared to state earned
  // a PASS citation for a cut nothing governed had established. The generic
  // runtime validator checks schema and source metadata but does not require the
  // field, so nothing else was holding this.
  //
  // Law 15 does not soften this: it is admission/execute-side gating, not render
  // suppression. A PASS citation asserts a governed computation over admitted
  // evidence, so evidence that cannot state its own cut is not admitted.
  const marginFactCutDay = isoCutDayOrNull(
    stringOrNull(asRecord(marginFacts[0].contentJson)?.asOf),
  );
  if (!marginFactCutDay) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISSING_MESSAGE,
    };
  }
  const snapshotCutDay = isoCutDayOrNull(snapshot.asOf);
  if (!snapshotCutDay) {
    return {
      reasonCode: 'missing_governed_evidence',
      message: SEGMENT_MARGIN_ATTRIBUTION_SNAPSHOT_CUT_MISSING_MESSAGE,
    };
  }
  // The invocation has to say which cut it claims, or "both reconcile to the
  // input" is a comparison against nothing.
  const inputCutDay = isoCutDayOrNull(input.reportingCutAsOf ?? null);
  if (!inputCutDay) {
    return {
      reasonCode: 'invalid_formula_input',
      message: SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISSING_MESSAGE,
    };
  }
  if (marginFactCutDay !== snapshotCutDay) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: SEGMENT_MARGIN_ATTRIBUTION_EVIDENCE_CUT_MISMATCH_MESSAGE,
    };
  }
  if (inputCutDay !== snapshotCutDay) {
    return {
      reasonCode: 'formula_reconciliation_failure',
      message: SEGMENT_MARGIN_ATTRIBUTION_INPUT_CUT_MISMATCH_MESSAGE,
    };
  }

  return null;
}

/**
 * The dated day a reporting cut falls on, or null when the value is absent or is
 * not a date at all. Unlike `sameIsoDate`, an unparseable value is NEVER compared
 * verbatim here: two authorities agreeing on the string `"latest"` do not agree
 * on a cut, and treating that as a match is the skip this fence exists to close.
 */
function isoCutDayOrNull(value: string | null | undefined): string | null {
  const normalized = stringOrNull(value ?? null);
  if (!normalized) return null;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
}

/**
 * Every anchor the input carries must BE an approved snapshot row: same segment
 * set, same net revenue, same operating margin. One shared refusal message —
 * which segment drifted is a lineage question for the receipt, not a hint to
 * hand a caller probing the fence.
 */
function reconcileSegmentMarginAttributionAnchors(
  inputAnchors: readonly SegmentMarginAttributionAnchorInput[],
  approvedAnchors: readonly SegmentMarginAttributionAnchorInput[],
): ApprovedFormulaEvidenceValidationBlock | null {
  const block: ApprovedFormulaEvidenceValidationBlock = {
    reasonCode: 'formula_reconciliation_failure',
    message:
      'Segment attribution anchors do not reconcile to the approved segment margin ' +
      'snapshot.',
  };
  if (inputAnchors.length !== approvedAnchors.length) return block;

  const seen = new Set<string>();
  for (const anchor of inputAnchors) {
    if (seen.has(anchor.segmentId)) return block;
    seen.add(anchor.segmentId);
    const approved = approvedAnchors.find(
      (candidate) => candidate.segmentId === anchor.segmentId,
    );
    if (
      !approved ||
      !sameNumber(anchor.netRevenueUsd, approved.netRevenueUsd) ||
      !sameNumber(anchor.operatingMarginPct, approved.operatingMarginPct)
    ) {
      return block;
    }
  }
  return null;
}

/**
 * Every submitted cell must BE a governed margin-fact row: the same set of cell
 * identities, and the same channel, segment, net revenue and margin on each. One
 * shared refusal message — which cell drifted is a lineage question for the
 * receipt, not a hint to hand a caller probing the fence.
 */
function reconcileSegmentMarginAttributionCells(
  inputCells: readonly SegmentMarginAttributionCellInput[],
  approvedCells: ReadonlyMap<string, SegmentMarginAttributionCellInput>,
): ApprovedFormulaEvidenceValidationBlock | null {
  const block: ApprovedFormulaEvidenceValidationBlock = {
    reasonCode: 'formula_reconciliation_failure',
    message:
      'Segment attribution channel cells do not reconcile to the approved margin-fact ' +
      'rows.',
  };
  if (inputCells.length !== approvedCells.size) return block;

  const seen = new Set<string>();
  for (const cell of inputCells) {
    if (seen.has(cell.cellKey)) return block;
    seen.add(cell.cellKey);
    const approved = approvedCells.get(cell.cellKey);
    if (
      !approved ||
      approved.channelKey !== cell.channelKey ||
      approved.segmentId !== cell.segmentId ||
      !sameNumber(cell.netRevenueUsd, approved.netRevenueUsd) ||
      !sameNumber(cell.marginUsd, approved.marginUsd)
    ) {
      return block;
    }
  }
  return null;
}

/**
 * Fail-closed on the whole margin-fact artifact, keyed by the ONE cell identity
 * rule the formula owns. A malformed or duplicated row makes the artifact
 * unusable as the allocation authority, and silently dropping one would shrink
 * the set the submitted cells are reconciled against — which is the same as not
 * checking that cell at all.
 */
type SegmentMarginAttributionMarginFactRows =
  | Readonly<{
      status: 'ok';
      cells: ReadonlyMap<string, SegmentMarginAttributionCellInput>;
    }>
  | Readonly<{ status: 'malformed' }>
  | Readonly<{ status: 'conflicting_channel_label' }>;

function parseSegmentMarginAttributionMarginFactRows(
  raw: unknown,
): SegmentMarginAttributionMarginFactRows {
  const content = asRecord(raw);
  const rows = asRecord(content?.signals)?.rows;
  if (!Array.isArray(rows) || rows.length === 0) return { status: 'malformed' };
  const cells = new Map<string, SegmentMarginAttributionCellInput>();
  // ONE CHANNEL KEY, ONE NAME (#4754 amendment 12, finding 2). The formula
  // aggregates every row sharing a channelKey into one published channel, so two
  // rows that agree on the key and disagree on the label describe one channel
  // under two names. Nothing downstream can reconcile that: the display map keeps
  // whichever label the iteration reached first, which makes the NAME on a
  // governed figure depend on row order in the artifact.
  const labelsByChannelKey = new Map<string, string>();
  for (const candidate of rows) {
    const record = asRecord(candidate);
    const customerId = stringOrNull(record?.customerId);
    const channelKey = stringOrNull(record?.channelKey);
    const channelLabel = stringOrNull(record?.channelLabel);
    const segmentId = stringOrNull(record?.segmentId);
    const netRevenueUsd = finiteNumberOrNull(record?.netRevenueUsd);
    const marginUsd = finiteNumberOrNull(record?.marginUsd);
    // Explicit null-SKU semantics, matching the governed loader: a present-but-
    // null skuId is a service line; a missing or non-string, non-null skuId is
    // not an admissible identity.
    const skuIdRaw = record?.skuId;
    const skuId = skuIdRaw === null ? null : stringOrNull(skuIdRaw);
    if (
      !customerId ||
      !channelKey ||
      !channelLabel ||
      !segmentId ||
      (skuIdRaw !== null && !skuId) ||
      netRevenueUsd === null ||
      marginUsd === null
    ) {
      return { status: 'malformed' };
    }
    const knownLabel = labelsByChannelKey.get(channelKey);
    // Same label repeated is lawful and stays lawful — that is simply the same
    // channel appearing on several rows, which is the normal shape of this grain.
    if (knownLabel !== undefined && knownLabel !== channelLabel) {
      return { status: 'conflicting_channel_label' };
    }
    labelsByChannelKey.set(channelKey, channelLabel);
    const cellKey = segmentMarginAttributionCellKey({
      customerId,
      skuId,
      channelKey,
      segmentId,
    });
    if (cells.has(cellKey)) return { status: 'malformed' };
    cells.set(cellKey, { cellKey, channelKey, segmentId, netRevenueUsd, marginUsd });
  }
  return { status: 'ok', cells };
}

/**
 * Fail-closed on the whole snapshot: a malformed or duplicated segment row makes
 * the artifact unusable as the anchor authority, and silently dropping it would
 * shrink the set the anchors are reconciled against.
 */
function parseSegmentMarginAttributionSnapshotEvidence(
  raw: unknown,
): SegmentMarginAttributionSnapshotEvidence | null {
  const content = asRecord(raw);
  if (!content || !Array.isArray(content.segments) || content.segments.length === 0) {
    return null;
  }
  const anchors: SegmentMarginAttributionAnchorInput[] = [];
  const seen = new Set<string>();
  for (const candidate of content.segments) {
    const record = asRecord(candidate);
    const segmentId = stringOrNull(record?.segmentId);
    const netRevenueUsd = finiteNumberOrNull(record?.netRevenueUsd);
    const operatingMarginPct = finiteNumberOrNull(record?.operatingMarginPct);
    if (
      !segmentId ||
      seen.has(segmentId) ||
      netRevenueUsd === null ||
      operatingMarginPct === null
    ) {
      return null;
    }
    seen.add(segmentId);
    anchors.push({ segmentId, netRevenueUsd, operatingMarginPct });
  }
  return { anchors, asOf: stringOrNull(content.asOf) };
}

function formulaRecord(
  initiativeId: string,
  capacityRequiredFte: number,
  capacityAvailableFte: number,
  currentStatus: string | null,
  blockedReason: string | null,
): StrategicExecutionCapacityFormulaRecord {
  return Object.freeze({
    initiativeId,
    capacityRequiredFte,
    capacityAvailableFte,
    currentStatus,
    blockedReason,
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized ? normalized : null;
}

function sameNumber(left: number, right: number): boolean {
  return Math.abs(left - right) <= NUMBER_TOLERANCE;
}

function sameIsoDate(left: string, right: string): boolean {
  return toIsoDate(left) === toIsoDate(right);
}

function sameStringArray(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function normalizeStatus(value: string | null | undefined): string {
  return normalizeOptionalText(value).toLowerCase();
}

function normalizeOptionalText(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function toIsoDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toISOString().slice(0, 10);
}
